import { useState, useRef, useEffect, useMemo } from "react";
import { SignedIn, SignedOut, SignIn, useUser } from "@clerk/clerk-react";
import { supabase, logSecurityEvent } from './supabase';
import { DrawSignature, generateSignedPdf } from './agreementSigning';
import { buildAgreementText, DEFAULT_AGREEMENT_FORM, CURRENCIES } from './agreementTemplate';

// Server-side Anthropic proxy. Frontend never sees the API key. Caller's Clerk JWT
// authenticates them at the Edge Function. Pass an Anthropic Messages API payload
// (model, max_tokens, messages, system, etc.) and you get the Anthropic response back.
async function callClaude(payload) {
  const token = await window.Clerk?.session?.getToken();
  const resp = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/claude-proxy`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return await resp.json();
}

function useLocalStorage(key, initial) {
  const [state, setState] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : initial; } catch { return initial; }
  });
  // Re-read from storage when key changes (brand switch)
  const prevKey = useRef(key);
  useEffect(() => {
    if(prevKey.current !== key) {
      prevKey.current = key;
      try { const s = localStorage.getItem(key); setState(s ? JSON.parse(s) : initial); } catch { setState(initial); }
    }
  // eslint-disable-next-line
  }, [key]);
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(state)); } catch {} }, [key, state]);
  // Cross-tab sync. Browsers fire a `storage` event in OTHER tabs (not the
  // tab that wrote it) whenever localStorage changes. Without this listener,
  // tab A's edits to brand X don't reflect in tab B until B refreshes — the
  // operator sees stale data and can write conflicting edits. With it, every
  // tab observing the same key stays in sync within ~10ms of any write.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== key || e.newValue === null) return;
      try { setState(JSON.parse(e.newValue)); } catch { /* ignore malformed */ }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key]);
  return [state, setState];
}
function toast(msg, type="success") {
  window.dispatchEvent(new CustomEvent("ios-toast",{detail:{msg,type,id:Date.now()}}));
}

// confirmDialog — promise-based replacement for window.confirm().
// Resolves true/false. Renders via GlobalConfirmHost (mounted in App).
// Usage:  if(!(await confirmDialog({title,body,destructive:true}))) return;
function confirmDialog(opts){
  return new Promise(resolve => {
    const id = Date.now() + Math.random();
    window.dispatchEvent(new CustomEvent("ios-confirm", { detail: { id, ...opts, resolve } }));
  });
}
// Global host for promise-based confirms. Listens to "ios-confirm" events
// (dispatched by confirmDialog()) and renders ConfirmModal. Single-instance
// queue — if multiple confirms fire simultaneously they show in order.
function GlobalConfirmHost() {
  const [queue, setQueue] = useState([]);
  useEffect(()=>{
    const h = (e) => setQueue(q => [...q, e.detail]);
    window.addEventListener("ios-confirm", h);
    return () => window.removeEventListener("ios-confirm", h);
  },[]);
  if(queue.length === 0) return null;
  const cur = queue[0];
  const finish = (value) => {
    cur.resolve(value);
    setQueue(q => q.slice(1));
  };
  return <ConfirmModal
    title={cur.title || "Confirm"}
    body={cur.body || "Are you sure?"}
    confirmLabel={cur.confirmLabel || "Confirm"}
    destructive={!!cur.destructive}
    onCancel={()=>finish(false)}
    onConfirm={()=>finish(true)}/>;
}

// promptDialog — promise-based replacement for window.prompt().
// Resolves to the entered string on submit, null on cancel.
// Usage:  const url = await promptDialog({title:"Insert link", label:"URL"});
function promptDialog(opts){
  return new Promise(resolve => {
    const id = Date.now() + Math.random();
    window.dispatchEvent(new CustomEvent("ios-prompt", { detail: { id, ...opts, resolve } }));
  });
}
function GlobalPromptHost() {
  const [queue, setQueue] = useState([]);
  useEffect(()=>{
    const h = (e) => setQueue(q => [...q, e.detail]);
    window.addEventListener("ios-prompt", h);
    return () => window.removeEventListener("ios-prompt", h);
  },[]);
  if(queue.length === 0) return null;
  const cur = queue[0];
  const finish = (value) => {
    cur.resolve(value);
    setQueue(q => q.slice(1));
  };
  return <PromptModal
    title={cur.title || "Enter value"}
    subtitle={cur.subtitle}
    label={cur.label || "Value"}
    initial={cur.initial || ""}
    placeholder={cur.placeholder || ""}
    confirmLabel={cur.confirmLabel || "Save"}
    onCancel={()=>finish(null)}
    onSubmit={(v)=>finish(v)}/>;
}

function ToastContainer() {
  const [toasts,setToasts]=useState([]);
  useEffect(()=>{
    const h=e=>{
      setToasts(t=>[...t,e.detail]);
      // Tiered timeouts: errors stick longer (user needs to read), success/info dismiss fast
      // so they don't visually linger over the page after the action's done.
      const ttl = e.detail.type === 'error' ? 4500 : 1700;
      setTimeout(()=>setToasts(t=>t.filter(x=>x.id!==e.detail.id)), ttl);
    };
    window.addEventListener("ios-toast",h);return()=>window.removeEventListener("ios-toast",h);
  },[]);
  const col={success:"#00c97a",error:"#EF4444",info:"#3B82F6"};
  const dismiss = (id)=>setToasts(t=>t.filter(x=>x.id!==id));
  return(<div style={{position:"fixed",top:20,right:20,zIndex:9999,display:"flex",flexDirection:"column",gap:8}}>{toasts.map(t=>(
    <div key={t.id} onClick={()=>dismiss(t.id)} title="Click to dismiss"
      style={{background:"#0C0D0E",color:"#fff",padding:"11px 18px",borderRadius:10,fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:10,boxShadow:"0 4px 20px rgba(0,0,0,0.3)",borderLeft:`3px solid ${col[t.type]||"#00c97a"}`,minWidth:220,animation:"slideIn .2s ease",cursor:"pointer"}}>
      <span style={{color:col[t.type]||"#00c97a"}}>{t.type==="success"?"✓":t.type==="error"?"✕":"ℹ"}</span>{t.msg}
    </div>))}<style>{"@keyframes slideIn{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:translateX(0)}}"}</style></div>);
}

const G="#00c97a",DARK="#0C0D0E",MUTED="#8B9299",BORDER="#E5E7EB",PAGE_BG="#F5F5F6";
const CARD="0 0 0 1px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)";

const NAV=[
  {id:"setup",label:"Setup",icon:"M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"},
  {id:"finder",label:"Influencer Finder",icon:"M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"},
  {id:"dashboard",label:"Dashboard",icon:"M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"},
  {id:"creators",label:"Creators",icon:"M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"},
  {id:"campaigns",label:"Campaigns",icon:"M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"},
  {id:"flows",label:"Email Flows",icon:"M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"},
  {id:"queue",label:"Email Queue",icon:"M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"},
  {id:"receipts",label:"Content Wall",icon:"M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z"},
  {id:"content",label:"Content Analysis",icon:"M15 10l4.553-2.069A1 1 0 0121 8.869v6.262a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"},
  {id:"agreements",label:"Agreements",icon:"M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"},
  {id:"integrations",label:"Integrations",icon:"M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"},
  {id:"settings",label:"Settings",icon:"M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z"},
];

// Agency workspace nav. Rendered when `active` starts with "agency-". Visible
// only when isAgencyOwner. Each id maps to a page component in the routing
// dispatcher below. The whole sidebar swaps so the user knows they're in a
// different mode (cross-brand ops + outreach), not a brand context.
const AGENCY_NAV=[
  {id:"agency-dashboard",label:"Dashboard",icon:"M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"},
  {id:"agency-brands",label:"Brands",icon:"M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"},
  {id:"agency-revenue",label:"Revenue",icon:"M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"},
  {id:"agency-team",label:"Team",icon:"M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"},
  {id:"agency-outreach",label:"Outreach",icon:"M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"},
  {id:"agency-activity",label:"Activity",icon:"M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"},
  {id:"agency-drive",label:"Drive",icon:"M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"},
  {id:"agency-suppress",label:"Suppress list",icon:"M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"},
  {id:"agency-changes",label:"Changes",icon:"M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"},
  {id:"agency-agreements",label:"Agreements",icon:"M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"},
];

// (Mock CREATORS array removed — was dead seed data never referenced after
// the multi-tenant switch. All creators now come from the brand_id-scoped
// creators table.)

// Used only as a last-resort fallback when a kanban entry references a campaign
// id that's no longer in the loaded `campaigns` state. Empty — real campaigns
// come from the `campaigns` table (RLS-scoped by brand_id).
const CAMPAIGNS=[];

const STAGES=[
  {id:"To Outreach",color:"#A78BFA",light:"#F5F3FF"},
  {id:"Outreached",color:"#6B7280",light:"#F3F4F6"},
  {id:"Negotiation",color:"#3B82F6",light:"#EFF6FF"},
  {id:"Paid Only",color:"#EF4444",light:"#FEF2F2"},
  {id:"Awaiting Post",color:"#F59E0B",light:"#FFFBEB"},
  {id:"Posted",color:"#10B981",light:"#ECFDF5"},
];

// (CAMPAIGN_CREATORS_INIT removed — was decorative fake-kanban seed data with
// the Cooper Smith / themamajoger / etc. mocks. Campaign cards now render only
// real kanban_items rows. See migration 0025 for the real source.)

// Brand-neutral seed flows. Inserted only on first run for a new brand
// (real flows live in the `flows` table and are edited in Settings → Flows).
// Use {{brand_name}}, {{firstname}}, {{discount_code}}, {{sender_name}}
// placeholders so this works for any brand without bleeding ES copy.
// FLOWS_INIT — seeded into a new brand's `flows` table on first load.
// IMPORTANT: one flow per type. Worker picks ONE flow per creator per type
// (weighted pick by split_weight) — so multiple flows of the same type are
// A/B variants, NOT sequential stages. Sequential = multiple STEPS in one flow.
// Default intervals follow Kristijan's spec (Loom 2026-05-20):
//   Outreach: 3 days between each follow-up
//   Fulfillment: order conf day 0, then arrived check at day 3 (same flow, 2 steps)
//   Post-content: brief 7d after delivery, then follow-ups 5d → 3d → 3d
const FLOWS_INIT=[
  {id:1,name:"Fulfillment",type:"Fulfillment",steps:[
    {id:1,subject:"Your order is confirmed!",body:"Hi {{firstname}},\n\nYour order is in — tracking link will follow in 1–2 days once it ships.\n\nIf we set you up with an affiliate code, you'll receive it separately.\n\nThanks,\n{{brand_name}}",days:0},
    {id:2,subject:"How's the {{brand_name}} working out?",body:"Hi {{firstname}},\n\nYour package should have landed by now — how's it going?\n\nAny first impressions or questions, happy to help.\n\nThanks,\n{{brand_name}}",days:3},
  ],used:0,reply:"—"},
  {id:2,name:"Content brief + follow-ups",type:"Post-content",steps:[
    {id:1,subject:"Your {{brand_name}} content brief",body:"Hi {{firstname}},\n\nYour {{brand_name}} package has had a few days to settle in — here's the brief for when you're ready to post.\n\n• Tag us in your caption / story\n• Use your affiliate code\n• A dedicated post or reel is ideal\n• Disclose as #gifted or #ad per platform rules\n\nHonest feedback either way — that's what we want.\n\nThanks,\n{{brand_name}}",days:7},
    {id:2,subject:"RE: Your {{brand_name}} content brief",body:"Hi {{firstname}},\n\nAnything you need from us to help with the post? Happy to clarify the brief or send extra photos.\n\nThanks,\n{{brand_name}}",days:5},
    {id:3,subject:"RE: Your {{brand_name}} content brief",body:"Hi {{firstname}},\n\nStill keen to see your post when the timing's right.\n\nThanks,\n{{brand_name}}",days:3},
    {id:4,subject:"Last check on the {{brand_name}} brief",body:"Hi {{firstname}},\n\nLast nudge from me — if posting isn't on the cards right now, just let me know so I can close this out my end.\n\nThanks either way.\n\n{{sender_name}}",days:3},
  ],used:0,reply:"—"},
  {id:3,name:"Outreach",type:"Outreach",steps:[
    {id:1,subject:"Collaboration opportunity",body:"Hi {{firstname}},\n\n[ADD 1 PERSONALISED LINE HERE]\n\nWe're {{brand_name}}, and we'd love to send you our product to try. No strings attached — honest feedback and content if you love it.\n\nInterested? Just reply with your full name + shipping address.\n\nThanks,\n{{sender_name}}\n{{brand_name}}",days:0},
    {id:2,subject:"RE: Collaboration opportunity",body:"Hi {{firstname}},\n\nJust following up on my previous message in case it slipped through.\n\nThanks,\n{{sender_name}}",days:3},
    {id:3,subject:"RE: Collaboration opportunity",body:"Hi {{firstname}},\n\nFinal follow-up. Completely understand if now isn't a good time.\n\nThanks,\n{{sender_name}}",days:3},
  ],used:0,reply:"—"},
];

// Unified 6-stage taxonomy — matches the campaign kanban columns exactly.
// "Inactive" sits outside the kanban (for declined / ghosted creators) but
// remains a valid status for filtering. See migration 0052.
const STATUS_COLORS={"To Outreach":"#A78BFA","Outreached":"#6B7280","Negotiation":"#3B82F6","Paid Only":"#EF4444","Awaiting Post":"#F59E0B","Posted":"#10B981","Inactive":"#9CA3AF"};

// Clickable Instagram handle → opens the creator's IG profile in a new tab.
// stopPropagation so clicking it inside a clickable row/card doesn't also fire
// the row's onClick (which opens the creator detail panel).
function HandleLink({handle,size=11,color="#9ab8a6"}){
  if(!handle) return null;
  const h=String(handle).replace(/^@/,"");
  return <a href={`https://instagram.com/${h}`} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{fontSize:size,color,textDecoration:"none",cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"} onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>@{h}</a>;
}

// Collapsible status guide for the top of the campaign board. Explains every
// column (lifecycle stage) and every card label (sub-status). Native <details>
// so it needs no React state. Copy is kept in sync with STAGES / STATUS_TO_FLOW_TYPE
// and the boardSub sub-status labels.
function StatusLegend(){
  const cols=[
    ["To Outreach","#A78BFA","Added, not emailed yet — the worker sends the first email within ~5 min, then moves them to Outreached."],
    ["Outreached","#6B7280","First email sent, follow-up sequence running. Waiting on their reply."],
    ["Negotiation","#3B82F6","They accepted but product isn't shipped yet. Set when you click 'Send Product'. No automated emails."],
    ["Paid Only","#EF4444","They asked for payment — gifted flow stopped. Handle manually."],
    ["Awaiting Post","#F59E0B","Product ordered / on the way. Content-reminder emails run until they post."],
    ["Posted","#10B981","Their post tagging the brand is live. Complete."],
    ["Inactive","#9CA3AF","Declined, bounced, or went quiet. Sits off the board."],
  ];
  const subs=[
    ["Overdue","#EF4444","They replied more than 3 days ago and you haven't answered. Reply now."],
    ["To Do","#F59E0B","They replied recently — needs your reply."],
    ["Review","#8B5CF6","An AI reply is drafted, waiting for you to approve or edit before it sends."],
    ["AI replying","#3B82F6","An approved AI reply is queued to send. No action needed."],
    ["Follow-up","#F97316","You emailed more than 3 days ago, no reply — a nudge is due (usually auto-sent)."],
    ["Waiting","#10B981","You emailed recently, waiting on them. Nothing to do yet."],
    ["No Task","#9CA3AF","Nothing needed right now."],
  ];
  const row=([n,c,d])=>(<div key={n} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"3px 0"}}><span style={{width:9,height:9,borderRadius:3,background:c,marginTop:4,flexShrink:0}}/><div style={{lineHeight:1.35}}><span style={{fontWeight:700,fontSize:12,color:"#374151"}}>{n}</span> <span style={{fontSize:12,color:"#6B7280"}}>— {d}</span></div></div>);
  return(<details style={{background:"#F8FAF9",border:"1px solid #E5EFE9",borderRadius:12,padding:"10px 14px"}}><summary style={{cursor:"pointer",fontSize:12,fontWeight:700,color:"#6b9e80"}}>ℹ️ Status guide — what each column and label means</summary><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24,marginTop:12}}><div><div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",color:"#9CA3AF",marginBottom:6}}>Columns — creator stage</div>{cols.map(row)}</div><div><div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",color:"#9CA3AF",marginBottom:6}}>Card labels — what to do</div>{subs.map(row)}</div></div></details>);
}

// Default empty-brand template. Used as the localStorage seed for a brand's
// UI-only fields (signature, daily email timing config, team list, etc.).
// Real per-brand data (name, domain, send_mode, settings JSONB) loads from
// the `brands` table via brand_id. Nothing here should be brand-specific —
// otherwise a fresh user inherits the previous brand's defaults.
function emptyBrandData(name="New Brand"){
  return {
    name,domain:"",market:"",
    instagram:"",youtube:"",tiktok:"",
    senderName:"",fromEmail:"",commissionRate:"15%",discountPct:"15%",
    shopifyUrl:"",signature:"",logo:null,
    // Drive folder IDs are auto-populated by the Setup wizard's "Drive folders"
    // card now — these manual fields are kept for back-compat but operators
    // shouldn't need to touch them.
    driveRootId:"",driveAgreementsId:"",driveContentId:"",driveBriefId:"",
    commission:"15%",codeFormat:"FIRSTNAME + 15",
    timezone:"",currency:"",
    email:{orderConfirm:0,arriving:7,brief:12,fu1:17,fu2:22,fu3:27,oFu1:3,oFu2:8,oFu3:22},
    integrations:{},
    team:[],
  };
}
const INITIAL_BRAND_DATA={ "New Brand": emptyBrandData("New Brand") };

// ── Canonical email template interpolation ──────────────────────────────────
// SINGLE source of truth for the frontend. This MUST stay in lock-step with the
// server-side `interpolate` + vars in supabase/functions/email-worker and
// send-creator-email — same keys, same collapse-to-empty behaviour. Previously
// there were 3 divergent ad-hoc interpolators here (one hardcoded "NanoClear"
// for every brand, one handling only 3 vars), which is how literal {{vars}} and
// wrong-brand text leaked into real sends.
const EMAIL_TEMPLATE_VARS = [
  "firstname","name","handle","influencer_handle","discount_code","brand_name",
  "sender_name","sender_signature","from_email","brand_instagram","brand_youtube",
  "commission_rate","discount_pct","personalized_sentence",
];
function buildEmailVars(creator={}, brand={}){
  const name = creator.name || creator.handle || "";
  const ig = brand.instagram || "";
  const yt = brand.youtube || "";
  return {
    firstname: name ? name.split(/\s+/)[0] : "there",
    name,
    handle: creator.handle || "",
    influencer_handle: creator.handle || "",
    discount_code: creator.discountCode || "",   // never invent a code (matches server)
    brand_name: brand.name || "",
    sender_name: brand.senderName || brand.name || "",
    sender_signature: brand.signature || brand.senderName || "",
    from_email: brand.fromEmail || "",
    brand_instagram: ig ? `@${ig}` : "",
    brand_youtube: yt ? `@${yt}` : "",
    commission_rate: brand.commissionRate || "15%",
    discount_pct: brand.discountPct || "15%",
    personalized_sentence: creator.personalized_sentence || "",
  };
}
function interpolateTemplate(text, vars){
  const withDefaults = (text||"").replace(/\{\{(\w+)\|default\((\w+)\)\}\}/g, (_,key,dk)=>{
    const v = vars[key];
    if (v && v.trim()) return v;
    return vars[dk] ?? "";
  });
  const collapsed = withDefaults
    .replace(/<p[^>]*>\s*\{\{personalized_sentence\}\}\s*<\/p>/gi, vars.personalized_sentence?`<p style="margin:0 0 12px 0">${vars.personalized_sentence}</p>`:"")
    .replace(/\{\{personalized_sentence\}\}\n\n/gi, vars.personalized_sentence?`${vars.personalized_sentence}\n\n`:"")
    .replace(/\{\{personalized_sentence\}\}/gi, vars.personalized_sentence||"");
  return collapsed.replace(/\{\{(\w+)(?:\|([^}]+))?\}\}/g, (_,key,fallback)=>{
    const v = vars[key];
    if (v !== undefined && v !== null && v !== "") return v;
    return fallback ?? ""; // never leak literal braces to a recipient
  });
}

// Universal flow templates — brand-agnostic copy with placeholders so any brand
// can pick a template and just tweak. Organised by the three flow types that
// drive auto-assignment (Outreach / Fulfillment / Post-content).
// Universal email flow templates. Tone is friendly-professional (lowercase
// openers, no "Dear", no "Best regards,") to work across brands without
// sounding like cold corporate spam — the biggest single reason cold creator
// outreach gets ignored. Each brand can adapt voice via the (future) per-brand
// voice profile feature; these are the baseline starter packs.
//
// Design principles applied:
//   • Short subject lines, lowercase, no "Collaboration opportunity" type phrasing
//   • First-line personalisation slot for AI auto-fill ([ADD 1 PERSONALISED LINE HERE])
//   • 4-6 sentence max per email — creators skim, not read
//   • "No pressure either way" framing on cold asks — proven to lift reply rates
//   • Drop "in exchange for a post" transactional language on initial outreach
//   • Affiliate math (commission_rate, discount_pct) deferred from email #1 — too
//     overwhelming up front; surfaces in the post-delivery brief instead
//   • Sign-off is just {{sender_name}} (or {{brand_name}} on fulfillment emails) —
//     no "Best,/Cheers,/Regards," fluff
const TEMPLATE_LIBRARY=[
  // ─── OUTREACH ─────────────────────────────────────────────────────────────
  {id:"outreach-seeding-3step",name:"Gifted seeding — 3 steps",category:"Outreach",description:"Standard gifted outreach for most product categories. Initial + 2 follow-ups, 3 days between.",steps:3,color:"#3B82F6",
    steps_data:[
      {subject:"{{firstname}}, quick send from {{brand_name}}",body:"hey {{firstname}},\n\n[ADD 1 PERSONALISED LINE HERE]\n\ni'm {{sender_name}} from {{brand_name}}. wanted to send you something from us to try — no strings, just our product on you to use however you want.\n\nif you end up posting, you'd get a {{discount_pct}} affiliate code your audience can use too. but no pressure either way.\n\nkeen? reply with your shipping address and we'll get it out today.\n\n{{sender_name}}",days:0},
      {subject:"re: {{firstname}}, quick send from {{brand_name}}",body:"hey {{firstname|default(influencer_handle)}} — bumping this in case it got buried.\n\nstill happy to send {{brand_name}} your way if you're up for it.\n\n{{sender_name}}",days:3},
      {subject:"last one",body:"hey {{firstname|default(influencer_handle)}} — promise this is the last note.\n\nif {{brand_name}} isn't a fit right now, all good. happy to circle back another time.\n\n{{sender_name}}",days:6},
    ]},
  {id:"outreach-vip",name:"VIP / high-follower outreach",category:"Outreach",description:"Slower, more personal pace for 50k+ follower creators. Opens with a paid component to respect their tier.",steps:2,color:"#8B5CF6",
    steps_data:[
      {subject:"{{firstname}} × {{brand_name}}",body:"hi {{firstname}},\n\n[ADD 1 PERSONALISED LINE — reference a specific recent post or theme they cover]\n\ni'm {{sender_name}}, head of partnerships at {{brand_name}}. we've been following your work and would love to talk about a collab.\n\nwe can do gifted product + a paid component on top. what's your usual structure for paid partnerships?\n\nhappy to be flexible.\n\n{{sender_name}}",days:0},
      {subject:"re: {{firstname}} × {{brand_name}}",body:"hi {{firstname}},\n\nno pressure if it's not the right fit — happy to revisit any time.\n\nwanted to make sure my note landed.\n\n{{sender_name}}",days:7},
    ]},
  {id:"outreach-tiktok",name:"TikTok creator outreach",category:"Outreach",description:"Punchier copy + faster cadence for TikTok creators (post-publish faster than IG, lower-friction reply expectations).",steps:3,color:"#EC4899",
    steps_data:[
      {subject:"gifted collab? @{{influencer_handle}}",body:"hey {{firstname}}!\n\n[ADD 1 PERSONALISED LINE about their recent TikTok content]\n\n{{brand_name}} would love to send you our product (free, gifted) to use in a TikTok if you're keen. plus your own affiliate code your audience can use.\n\nreply with name + address and i'll ship today.\n\n{{sender_name}}",days:0},
      {subject:"re: gifted collab? @{{influencer_handle}}",body:"hey {{firstname}} — bumping this. still keen to send if you are.\n\n{{sender_name}}",days:2},
      {subject:"last try",body:"hey {{firstname}}, last from me — say the word and i'll get the product out today.\n\n{{sender_name}}",days:5},
    ]},
  {id:"outreach-affiliate-only",name:"Affiliate-only invite",category:"Outreach",description:"For creators we won't ship product to but want as affiliate partners. No gifting overhead.",steps:2,color:"#06B6D4",
    steps_data:[
      {subject:"{{brand_name}} affiliate program",body:"hi {{firstname}},\n\n[ADD 1 PERSONALISED LINE]\n\nwe're opening up the {{brand_name}} affiliate program to creators we love. {{commission_rate}} commission on every sale you drive, plus your audience gets {{discount_pct}} off.\n\nno gifting on this one — pure affiliate setup.\n\nreply if you're in and i'll send your unique link.\n\n{{sender_name}}",days:0},
      {subject:"re: {{brand_name}} affiliate program",body:"hi {{firstname}}, easy yes/no when you have a sec.\n\n{{sender_name}}",days:5},
    ]},

  // ─── FULFILLMENT ──────────────────────────────────────────────────────────
  {id:"fulfillment-confirm-track",name:"Order confirmation + package arrived",category:"Fulfillment",description:"Confirmation right after order placed, then a check-in 3 days after the package is delivered.",steps:2,color:"#10B981",
    steps_data:[
      {subject:"your {{brand_name}} order is in",body:"hi {{firstname}},\n\nyour order's been placed — we'll ship within 1-2 days and send tracking the moment it goes out.\n\nif we set you up with an affiliate code, you'll see it in a separate email shortly.\n\ncheers,\n{{brand_name}}",days:0},
      {subject:"how's the {{brand_name}} working for you?",body:"hi {{firstname}},\n\nyour package should've landed by now — how's it going?\n\nany first impressions, questions, anything? happy to help.\n\n{{sender_name}}",days:3},
    ]},
  {id:"fulfillment-replacement",name:"Replacement / lost package",category:"Fulfillment",description:"One email when something went wrong with the original ship — damage, lost in transit, wrong item. Apology + immediate fix.",steps:1,color:"#F97316",
    steps_data:[
      {subject:"sorry — sending a replacement",body:"hi {{firstname}},\n\nsorry about the issue with your {{brand_name}} order. that's on us, not on you.\n\nwe're sending out a replacement today. tracking will come through within 24 hours.\n\nlet me know if there's anything else you need.\n\n{{sender_name}}",days:0},
    ]},

  // ─── POST-CONTENT ─────────────────────────────────────────────────────────
  {id:"post-delivery-3step",name:"Content brief + 3 follow-ups",category:"Post-content",description:"Sent 7 days after delivery. Clear brief with tag/affiliate-code instructions + 3 follow-ups (5d, 3d, 3d apart) for late posters.",steps:4,color:"#F59E0B",
    steps_data:[
      {subject:"your {{brand_name}} content brief",body:"hey {{firstname|default(influencer_handle)}},\n\nyour {{brand_name}} package has had a few days to settle in — here's the brief for when you're ready to post.\n\nquick guidelines:\n• tag {{brand_instagram}}\n• use your affiliate code in the caption or bio\n• ideally a dedicated post (not a haul) within 14 days\n• disclose as #gifted or #ad per platform rules\n\nyour honest take is what we want — good or bad. that's the whole point.\n\n{{sender_name}}",days:7},
      {subject:"re: your {{brand_name}} content brief",body:"hi {{firstname|default(influencer_handle)}},\n\nchecking in — anything you need from us to help with the post? happy to clarify the brief or send extra angle photos.\n\n{{sender_name}}",days:5},
      {subject:"re: your {{brand_name}} content brief",body:"hey {{firstname|default(influencer_handle)}},\n\nstill keen to see your post when the timing's right. remember to tag {{brand_instagram}}.\n\n{{sender_name}}",days:3},
      {subject:"last check on the {{brand_name}} brief",body:"hi {{firstname|default(influencer_handle)}},\n\nlast nudge from me — if posting isn't on the cards right now, that's fine, just let me know so i can close this out my end.\n\nthanks either way.\n\n{{sender_name}}",days:3},
    ]},
  {id:"reengagement",name:"Re-engagement (dormant creators)",category:"Post-content",description:"For creators who got product months ago but never posted. One graceful ask, no guilt-trip.",steps:1,color:"#6366F1",
    steps_data:[
      {subject:"been a while, {{firstname}}",body:"hi {{firstname}},\n\nit's been a while since we sent you {{brand_name}}. no pressure at all — but if a post is still on your radar, we'd love to see it.\n\nif the timing was off or you'd rather pass, totally fine — just let me know.\n\n{{sender_name}}",days:0},
    ]},
  {id:"thank-you-after-post",name:"Thank-you after content posted",category:"Post-content",description:"Sent after creator publishes. Closes the loop + opens door for next round.",steps:1,color:"#0EA5E9",
    steps_data:[
      {subject:"loved your post!",body:"hi {{firstname}},\n\njust saw your post — it's brilliant. thanks for the genuine work, it means a lot.\n\nwe'd love to keep working with you. if there's anything else from our catalog you'd like to try, just reply.\n\n{{sender_name}}\n{{brand_name}}",days:0},
    ]},
  {id:"usage-rights-request",name:"Usage rights request",category:"Post-content",description:"Sent after a creator's post is performing well. Asks for repurpose/whitelisting rights for paid social.",steps:1,color:"#7C3AED",
    steps_data:[
      {subject:"quick ask about your {{brand_name}} post",body:"hi {{firstname}},\n\nyour recent post for us is performing well — would love to ask: would you be open to us using it for our own ads/social as well?\n\nwhat we'd need:\n• repurpose / whitelist / both — your call\n• which platforms (instagram, facebook, tiktok)\n• how long (typically 6 or 12 months)\n\nhappy to compensate if that's part of your usual setup — just let me know your rate. or if it's a friendly extension of the collab, we'd be grateful for that too.\n\n{{sender_name}}",days:0},
    ]},
];

// ─── FLOW LIFECYCLE MAPPING ──────────────────────────────────────────────────
// Maps a creator's lifecycle status to the type of email flow they should be on.
// The system uses this to auto-assign flows — no manual "pick a flow" step.
//   Outreached            → Outreach flow (cold outreach + follow-ups)
//   Ordered / In transit  → Fulfillment flow (order confirmation, arriving soon)
//   Delivered / Brief sent → Post-content flow (awaiting post, content reminders)
//   Content posted        → no flow (lifecycle complete)
//   Paid Inquiry          → no flow (handled manually by account manager)
const STATUS_TO_FLOW_TYPE = {
  // "To Outreach" maps to Outreach so the worker assigns a flow the moment the
  // creator lands in a campaign's To Outreach column. The first email sends,
  // then the worker flips status to "Outreached" — operator never has to drag.
  "To Outreach": "Outreach",
  "Outreached": "Outreach",
  // Negotiation = "they accepted but we haven't shipped product yet". AI
  // auto-replies handle the back-and-forth (address ask, product clarification,
  // out-of-stock alternatives). No scheduled drip — fires when product moves.
  "Negotiation": null,
  // Awaiting Post covers everything post-shipment: product en route, delivered,
  // brief sent, content reminders. One flow type per status keeps the worker
  // logic simple — brand puts all fulfillment + content steps in one flow.
  "Awaiting Post": "Post-content",
  "Posted": null,                  // lifecycle complete
  "Paid Only": null,               // operator handles negotiation
  "Inactive": null,                // declined or stale-no-response — no further flow
};

// Usage-rights expiry parser. Takes the operator-typed free text duration
// ("6 months", "1 year", "30 days", "perpetual") + the date the creator
// signed the agreement, returns a structured result the UI uses to render
// a colour-coded badge. Lazy + best-effort — unknown formats fall through
// to {urgency: 'unknown'} so the operator still sees the raw text.
function computeAgreementExpiry(durationText, creatorSignedAtIso) {
  if (!creatorSignedAtIso) return { urgency: "unsigned", label: null };
  const raw = (durationText || "").trim().toLowerCase();
  if (!raw) return { urgency: "unknown", label: null };
  if (/^(perpetual|indefinite|forever|unlimited|lifetime|in perpetuity)/i.test(raw)) {
    return { urgency: "perpetual", label: "Perpetual" };
  }
  const m = raw.match(/(\d+)\s*(day|week|month|year)s?/i);
  if (!m) return { urgency: "unknown", label: durationText };
  const n = parseInt(m[1], 10);
  const unit = m[2].toLowerCase();
  const signedAt = new Date(creatorSignedAtIso);
  const expiresAt = new Date(signedAt);
  if (unit === "day")   expiresAt.setDate(expiresAt.getDate() + n);
  if (unit === "week")  expiresAt.setDate(expiresAt.getDate() + n * 7);
  if (unit === "month") expiresAt.setMonth(expiresAt.getMonth() + n);
  if (unit === "year")  expiresAt.setFullYear(expiresAt.getFullYear() + n);
  const daysLeft = Math.floor((expiresAt.getTime() - Date.now()) / 86400000);
  const niceDate = expiresAt.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" });
  if (daysLeft < 0) return { urgency: "expired", label: `Expired ${Math.abs(daysLeft)}d ago`, expiresAt, daysLeft };
  if (daysLeft <= 14) return { urgency: "warn", label: `Expires in ${daysLeft}d`, expiresAt, daysLeft };
  return { urgency: "ok", label: `Expires ${niceDate}`, expiresAt, daysLeft };
}

// Cheap, stable string hash (FNV-1a 32-bit). Used for deterministic per-creator
// bucketing — same creator always lands in the same A/B branch / weighted flow.
function hashHandleToFloat(s){
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = (h * 0x01000193) >>> 0; }
  return (h >>> 0) / 0x100000000;
}

function pickFlowForStatus(status, flows, creatorHandle=""){
  const type = STATUS_TO_FLOW_TYPE[status];
  if(!type || !flows) return null;
  // Filter to active flows of the matching type. is_active=undefined (legacy rows
  // pre-migration 0020) counts as active.
  const eligible = flows.filter(f => f.type === type && f.is_active !== false);
  if (eligible.length === 0) return null;
  if (eligible.length === 1) return eligible[0];
  // Weighted pick: deterministic per creator handle.
  const weights = eligible.map(f => Math.max(1, f.split_weight || 100));
  const total = weights.reduce((a,b) => a+b, 0);
  const bucket = hashHandleToFloat((creatorHandle || "") + "::" + type) * total;
  let acc = 0;
  for (let i = 0; i < eligible.length; i++) {
    acc += weights[i];
    if (bucket < acc) return eligible[i];
  }
  return eligible[eligible.length - 1];
}

// ─── FOLLOW-UP SCHEDULER ─────────────────────────────────────────────────────
// For each creator, checks activity log + assigned flow to determine if a
// follow-up email is due. Returns null if nothing is due.
function getFollowUpStatus(handle, activityLog=[], flows=[]){
  const log=[...activityLog].filter(e=>e.handle===handle);
  // Find the most recent email_sent entry that has a flowId
  const lastSent=log.find(e=>e.type==="email_sent");
  if(!lastSent) return null;

  // Check if creator replied since last sent
  const lastSentTs=new Date(lastSent.ts).getTime();
  const repliedAfter=log.find(e=>e.type==="email_received"&&new Date(e.ts).getTime()>lastSentTs);
  if(repliedAfter) return null; // they replied, no follow-up needed

  // Find how many emails have been sent (= which step we're on)
  const sentCount=log.filter(e=>e.type==="email_sent").length;
  const flowId=lastSent.flowId;
  const flow=flowId?flows.find(f=>f.id===flowId):flows[0];
  if(!flow||!flow.steps) return null;

  // Next step to send
  const nextStep=flow.steps[sentCount]; // 0-indexed: sentCount=1 means step at index 1
  if(!nextStep) return null; // all steps sent

  // Check if enough days have passed
  const daysSinceLast=Math.floor((Date.now()-lastSentTs)/(1000*60*60*24));
  const daysRequired=nextStep.days||3;
  const daysOverdue=daysSinceLast-daysRequired;

  if(daysOverdue<0) return {status:"upcoming",daysUntil:-daysOverdue,nextStep,sentCount,flow};
  return {status:"due",daysOverdue,nextStep,sentCount,flow};
}

// Quick check — should we ALERT the operator about a follow-up?
// The email worker auto-sends every scheduled step within ~5 min of it coming
// due, so a normally-due follow-up is NOT an operator task — alerting on it just
// cries wolf (and tempts a manual send that double-mails the creator). We only
// raise it once it's ≥2 days overdue, which means the worker ISN'T sending it
// (brand paused, flow cancelled, Gmail disconnected) — a real anomaly worth a look.
function isFollowUpDue(handle, activityLog, flows){
  const s=getFollowUpStatus(handle, activityLog, flows);
  return s?.status==="due" && (s.daysOverdue ?? 0) >= 2;
}

// ─── KANBAN SUB-STATUS ───────────────────────────────────────────────────────
// Computes the per-card sub-status pill from email activity. Spec:
//   - No Task  → pre-outreach (To Outreach), terminal (Posted), Paid Only, Inactive,
//                or no email activity yet on the card.
//   - Overdue  → they replied >3d ago, we still haven't responded. Ball in our court.
//   - To Do    → they replied ≤3d ago, action needed. Ball in our court.
//   - Follow-up→ we sent >3d ago, no reply. Time to nudge.
//   - Waiting  → we sent recently (≤3d), still in window.
const SUB_STATUS_OVERDUE_DAYS = 3;
function getKanbanSubStatus(creator, stageId, activityLog=[]){
  if(creator?.status==="Inactive") return "No Task";
  if(stageId==="To Outreach"||stageId==="Posted"||stageId==="Paid Only") return "No Task";
  const handle=creator?.handle;
  if(!handle) return "No Task";
  const log=activityLog.filter(e=>e.handle===handle);
  const lastSent=log.find(e=>e.type==="email_sent");
  const lastReceived=log.find(e=>e.type==="email_received");
  // A genuine REPLY from us = an auto-reply (step -1) or a manual one-off send
  // (manual flag). A cold/follow-up flow step (step>=0) is automated outreach,
  // NOT a response to their message — so it must NOT flip an unanswered inbound
  // from "To Do" to "Waiting". Without this, a flow send landing after a reply
  // (e.g. a re-enrollment, or the 2026-06-10 duplicate-cold-send bug) made the
  // board read "Waiting" and silently hid a reply that needed a human.
  const lastReply=log.find(e=>e.type==="email_sent" && ((typeof e.step==="number" && e.step<0) || e.manual===true));
  const sentTs=lastSent?new Date(lastSent.ts).getTime():0;
  const recvTs=lastReceived?new Date(lastReceived.ts).getTime():0;
  const replyTs=lastReply?new Date(lastReply.ts).getTime():0;
  if(!sentTs&&!recvTs) return "No Task";
  const now=Date.now();
  // They replied and we haven't genuinely responded since → ball in our court.
  if(recvTs>0 && recvTs>replyTs){
    const days=Math.floor((now-recvTs)/(1000*60*60*24));
    return days>SUB_STATUS_OVERDUE_DAYS?"Overdue":"To Do";
  }
  const days=Math.floor((now-sentTs)/(1000*60*60*24));
  return days>SUB_STATUS_OVERDUE_DAYS?"Follow-up":"Waiting";
}
// Which outreach step a card is parked on, for the muted line under the pill.
// Reads extra.step (0 = initial outreach, 1 = follow-up 1, …) off the latest
// flow send. Order-confirmation / manual emails carry no step, so they're
// ignored. Returns null once the creator has replied (the ball's then in our
// court — the To Do / Overdue pill already says so) or when there's no outreach.
function getOutreachStageDetail(creator, stageId, activityLog=[]){
  if(stageId==="To Outreach"||stageId==="Posted"||stageId==="Paid Only") return null;
  const handle=creator?.handle;
  if(!handle) return null;
  const log=activityLog.filter(e=>e.handle===handle); // newest-first
  const flowSends=log.filter(e=>e.type==="email_sent"&&typeof e.step==="number");
  if(!flowSends.length) return null;
  const latest=flowSends[0];
  const latestTs=new Date(latest.ts).getTime();
  if(log.some(e=>e.type==="email_received"&&new Date(e.ts).getTime()>latestTs)) return null;
  const maxStep=Math.max(...flowSends.filter(e=>e.flowId===latest.flowId).map(e=>e.step));
  return maxStep===0?"Outreach sent":`Follow-up ${maxStep} sent`;
}
// ─────────────────────────────────────────────────────────────────────────────return(<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">{d.split(" M").map((seg,i)=><path key={i} d={i===0?seg:"M"+seg}/>)}</svg>);}
function Ico({d,size=16,color="currentColor"}){return(<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">{d.split(" M").map((seg,i)=><path key={i} d={i===0?seg:"M"+seg}/>)}</svg>);}
// Country list grouped by region. Top-seeded markets surface first; rest grouped by
// continent. Used by CountryMultiSelect (campaign markets) and CountrySelect
// (agreement jurisdiction). ISO 3166-1 alpha-2 codes for storage; labels for display.
const COUNTRIES_BY_REGION = [
  { region: "Top markets", countries: [
    { code: "AU", name: "Australia" },
    { code: "US", name: "United States" },
    { code: "GB", name: "United Kingdom" },
    { code: "NZ", name: "New Zealand" },
    { code: "CA", name: "Canada" },
  ]},
  { region: "Europe", countries: [
    { code: "IE", name: "Ireland" }, { code: "FR", name: "France" },
    { code: "DE", name: "Germany" }, { code: "NL", name: "Netherlands" },
    { code: "ES", name: "Spain" }, { code: "IT", name: "Italy" },
    { code: "PT", name: "Portugal" }, { code: "BE", name: "Belgium" },
    { code: "CH", name: "Switzerland" }, { code: "AT", name: "Austria" },
    { code: "SE", name: "Sweden" }, { code: "NO", name: "Norway" },
    { code: "DK", name: "Denmark" }, { code: "FI", name: "Finland" },
    { code: "PL", name: "Poland" }, { code: "CZ", name: "Czech Republic" },
    { code: "HU", name: "Hungary" }, { code: "GR", name: "Greece" },
    { code: "HR", name: "Croatia" }, { code: "RO", name: "Romania" },
  ]},
  { region: "Americas", countries: [
    { code: "MX", name: "Mexico" }, { code: "BR", name: "Brazil" },
    { code: "AR", name: "Argentina" }, { code: "CL", name: "Chile" },
    { code: "CO", name: "Colombia" }, { code: "PE", name: "Peru" },
  ]},
  { region: "Asia & Pacific", countries: [
    { code: "JP", name: "Japan" }, { code: "KR", name: "South Korea" },
    { code: "SG", name: "Singapore" }, { code: "HK", name: "Hong Kong" },
    { code: "TW", name: "Taiwan" }, { code: "MY", name: "Malaysia" },
    { code: "TH", name: "Thailand" }, { code: "ID", name: "Indonesia" },
    { code: "PH", name: "Philippines" }, { code: "VN", name: "Vietnam" },
    { code: "IN", name: "India" }, { code: "CN", name: "China" },
  ]},
  { region: "Middle East & Africa", countries: [
    { code: "AE", name: "United Arab Emirates" }, { code: "SA", name: "Saudi Arabia" },
    { code: "IL", name: "Israel" }, { code: "TR", name: "Turkey" },
    { code: "ZA", name: "South Africa" }, { code: "EG", name: "Egypt" },
    { code: "MA", name: "Morocco" }, { code: "NG", name: "Nigeria" },
    { code: "KE", name: "Kenya" },
  ]},
];
const ALL_COUNTRIES = COUNTRIES_BY_REGION.flatMap(r => r.countries);

// Multi-select country picker with chip display + searchable popover.
// Used in: campaign creation (target markets).
function CountryMultiSelect({value=[],onChange}){
  const [open,setOpen]=useState(false);
  const [search,setSearch]=useState("");
  const ref=useRef(null);
  useEffect(()=>{
    const h=(e)=>{ if(open && ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown',h);
    return()=>document.removeEventListener('mousedown',h);
  },[open]);
  const selected=new Set((value||[]).map(v=>(v||"").toUpperCase()));
  const toggle=(code)=>{
    const next=new Set(selected);
    if(next.has(code)) next.delete(code); else next.add(code);
    onChange([...next]);
  };
  const labelFor=(code)=>(ALL_COUNTRIES.find(c=>c.code===code)?.name)||code;

  return(<div ref={ref} style={{position:"relative"}}>
    <div onClick={()=>setOpen(o=>!o)} style={{minHeight:38,padding:"6px 10px",border:`1px solid ${BORDER}`,borderRadius:8,background:"#fff",cursor:"pointer",display:"flex",flexWrap:"wrap",gap:5,alignItems:"center"}}>
      {selected.size===0&&<span style={{color:MUTED,fontSize:13}}>Pick countries…</span>}
      {[...selected].map(code=>(<span key={code} style={{background:G+"18",color:"#166534",border:`1px solid ${G}40`,borderRadius:5,padding:"2px 8px",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",gap:5}}>
        {labelFor(code)}
        <span onClick={e=>{e.stopPropagation();toggle(code);}} style={{cursor:"pointer",opacity:0.6,fontSize:13,lineHeight:1}}>×</span>
      </span>))}
      <span style={{marginLeft:"auto",color:MUTED,fontSize:11}}>{open?"▴":"▾"}</span>
    </div>
    {open&&(<div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"#fff",border:`1px solid ${BORDER}`,borderRadius:8,boxShadow:"0 12px 32px rgba(0,0,0,0.12)",zIndex:50,maxHeight:340,display:"flex",flexDirection:"column"}}>
      <div style={{padding:8,borderBottom:`1px solid ${BORDER}`}}>
        <input autoFocus value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search countries…"
          style={{width:"100%",padding:"6px 10px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,outline:"none",boxSizing:"border-box"}}/>
      </div>
      <div style={{flex:1,overflowY:"auto"}}>
        {COUNTRIES_BY_REGION.map(group=>{
          const filtered=group.countries.filter(c=>!search||c.name.toLowerCase().includes(search.toLowerCase())||c.code.toLowerCase().includes(search.toLowerCase()));
          if(filtered.length===0)return null;
          return(<div key={group.region}>
            <div style={{padding:"6px 12px 3px",fontSize:10,fontWeight:700,color:MUTED,textTransform:"uppercase",letterSpacing:".05em",background:"#fafcfb"}}>{group.region}</div>
            {filtered.map(c=>{
              const isSelected=selected.has(c.code);
              return(<div key={c.code} onClick={()=>toggle(c.code)} style={{padding:"7px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:12,background:isSelected?G+"08":"transparent"}}
                onMouseEnter={e=>e.currentTarget.style.background=isSelected?G+"12":"#f8faf9"}
                onMouseLeave={e=>e.currentTarget.style.background=isSelected?G+"08":"transparent"}>
                <input type="checkbox" checked={isSelected} readOnly style={{accentColor:G,cursor:"pointer"}}/>
                <span style={{color:DARK,fontWeight:isSelected?600:400,flex:1}}>{c.name}</span>
                <span style={{fontSize:10,color:MUTED,fontFamily:"DM Mono, monospace"}}>{c.code}</span>
              </div>);
            })}
          </div>);
        })}
      </div>
    </div>)}
  </div>);
}

// Single-country dropdown (jurisdiction) — same data source, single value.
function CountrySelect({value,onChange,placeholder="Select country…"}){
  return(<select value={value||""} onChange={e=>onChange(e.target.value)}
    style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",background:"#fff",boxSizing:"border-box"}}>
    <option value="">{placeholder}</option>
    {COUNTRIES_BY_REGION.map(group=>(<optgroup key={group.region} label={group.region}>
      {group.countries.map(c=>(<option key={c.code} value={c.name}>{c.name}</option>))}
    </optgroup>))}
  </select>);
}

// Common business timezones — curated so user doesn't have to scroll through 400.
// Format: { value: IANA, label: human-readable }. Worker's TZ-aware sending uses
// the IANA value when matching creator country → can be cross-referenced.
const TIMEZONE_OPTIONS = [
  { value: "Australia/Sydney", label: "Australia/Sydney (AEST/AEDT)" },
  { value: "Australia/Melbourne", label: "Australia/Melbourne (AEST/AEDT)" },
  { value: "Australia/Brisbane", label: "Australia/Brisbane (AEST)" },
  { value: "Australia/Perth", label: "Australia/Perth (AWST)" },
  { value: "Pacific/Auckland", label: "Pacific/Auckland (NZST/NZDT)" },
  { value: "America/New_York", label: "America/New York (EST/EDT)" },
  { value: "America/Chicago", label: "America/Chicago (CST/CDT)" },
  { value: "America/Denver", label: "America/Denver (MST/MDT)" },
  { value: "America/Los_Angeles", label: "America/Los Angeles (PST/PDT)" },
  { value: "America/Toronto", label: "America/Toronto (EST/EDT)" },
  { value: "America/Mexico_City", label: "America/Mexico City (CST)" },
  { value: "America/Sao_Paulo", label: "America/São Paulo (BRT)" },
  { value: "Europe/London", label: "Europe/London (GMT/BST)" },
  { value: "Europe/Dublin", label: "Europe/Dublin (GMT/IST)" },
  { value: "Europe/Paris", label: "Europe/Paris (CET/CEST)" },
  { value: "Europe/Berlin", label: "Europe/Berlin (CET/CEST)" },
  { value: "Europe/Madrid", label: "Europe/Madrid (CET/CEST)" },
  { value: "Europe/Rome", label: "Europe/Rome (CET/CEST)" },
  { value: "Europe/Amsterdam", label: "Europe/Amsterdam (CET/CEST)" },
  { value: "Europe/Stockholm", label: "Europe/Stockholm (CET/CEST)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GST)" },
  { value: "Asia/Singapore", label: "Asia/Singapore (SGT)" },
  { value: "Asia/Hong_Kong", label: "Asia/Hong Kong (HKT)" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo (JST)" },
  { value: "Asia/Seoul", label: "Asia/Seoul (KST)" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata (IST)" },
  { value: "Africa/Johannesburg", label: "Africa/Johannesburg (SAST)" },
];

// Common currencies — major markets first, with ISO code + symbol for display.
const CURRENCY_OPTIONS = [
  { value: "AUD", label: "AUD · A$ · Australian Dollar" },
  { value: "USD", label: "USD · $ · US Dollar" },
  { value: "EUR", label: "EUR · € · Euro" },
  { value: "GBP", label: "GBP · £ · British Pound" },
  { value: "CAD", label: "CAD · C$ · Canadian Dollar" },
  { value: "NZD", label: "NZD · NZ$ · New Zealand Dollar" },
  { value: "JPY", label: "JPY · ¥ · Japanese Yen" },
  { value: "CHF", label: "CHF · CHF · Swiss Franc" },
  { value: "SEK", label: "SEK · kr · Swedish Krona" },
  { value: "NOK", label: "NOK · kr · Norwegian Krone" },
  { value: "DKK", label: "DKK · kr · Danish Krone" },
  { value: "ZAR", label: "ZAR · R · South African Rand" },
  { value: "SGD", label: "SGD · S$ · Singapore Dollar" },
  { value: "HKD", label: "HKD · HK$ · Hong Kong Dollar" },
  { value: "AED", label: "AED · د.إ · UAE Dirham" },
  { value: "BRL", label: "BRL · R$ · Brazilian Real" },
  { value: "MXN", label: "MXN · MX$ · Mexican Peso" },
  { value: "INR", label: "INR · ₹ · Indian Rupee" },
];

// Inline dropdown editor for brand-settings fields. Same edit/save UX as FieldRow
// but with a constrained option list instead of free text. Auto-saves on change
// (no separate Save button — selecting from a fixed list is a deliberate action).
function DropdownField({label, value, fieldKey, options, onSave}){
  const current = options.find(o => o.value === value) || (value ? { value, label: value } : null);
  return(<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 0",borderBottom:"1px solid #f8faf9"}}>
    <div style={{fontSize:13,color:"#9ab8a6",fontWeight:500,minWidth:160}}>{label}</div>
    <div style={{display:"flex",gap:8,alignItems:"center"}}>
      <select value={value || ""} onChange={e => onSave(fieldKey, e.target.value)}
        style={{padding:"6px 11px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",background:"#fff",color:current?DARK:MUTED,fontWeight:current?600:400,maxWidth:320}}>
        <option value="">Select…</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  </div>);
}

// User profile menu — top-right avatar dropdown. Persists name + avatar to Clerk
// (the auth source of truth) rather than to local state. Email is read-only here;
// Clerk's hosted profile page is the canonical place to change email.
function UserProfileMenu({user,userProfile,setUserProfile,onClose}){
  const [first,setFirst] = useState(userProfile.firstName);
  const [last,setLast] = useState(userProfile.lastName);
  const [saving,setSaving] = useState(false);
  const [uploading,setUploading] = useState(false);
  const dirty = first !== userProfile.firstName || last !== userProfile.lastName;
  const initials = (`${first||"?"} ${last||""}`).split(" ").map(w=>w[0]).filter(Boolean).slice(0,2).join("").toUpperCase();

  const saveName = async()=>{
    if(!user||!dirty)return;
    setSaving(true);
    try{
      await user.update({firstName:first||"",lastName:last||""});
      setUserProfile(p=>({...p,firstName:first,lastName:last}));
      toast("Profile saved");
    }catch(e){toast(`Save failed: ${e.errors?.[0]?.message||e.message}`,'error');}
    finally{setSaving(false);}
  };
  const uploadAvatar = async(file)=>{
    if(!user||!file)return;
    setUploading(true);
    try{
      await user.setProfileImage({file});
      const newUrl=user.imageUrl;
      setUserProfile(p=>({...p,avatar:newUrl}));
      toast("Profile photo updated");
    }catch(e){toast(`Upload failed: ${e.errors?.[0]?.message||e.message}`,'error');}
    finally{setUploading(false);}
  };
  const signOut = async()=>{
    if(!(await confirmDialog({title:"Sign out", body:"Sign out of influencer-os?", confirmLabel:"Sign out"}))) return;
    try{ await window.Clerk?.signOut(); window.location.reload(); }
    catch(e){toast(`Sign out failed: ${e.message}`,'error');}
  };

  return(<div style={{position:"absolute",top:"calc(100% + 6px)",right:0,background:"#fff",borderRadius:12,boxShadow:"0 0 0 1px rgba(0,0,0,0.08),0 16px 48px rgba(0,0,0,0.12)",zIndex:200,width:300,overflow:"hidden"}}>
    <div style={{padding:"14px",borderBottom:`1px solid ${BORDER}`,display:"flex",gap:11,alignItems:"center"}}>
      <label style={{cursor:uploading?"wait":"pointer",flexShrink:0,position:"relative",opacity:uploading?0.5:1}}>
        <div style={{width:42,height:42,borderRadius:"50%",overflow:"hidden",border:`1px solid ${BORDER}`}}>
          {userProfile.avatar?<img src={userProfile.avatar} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<div style={{width:"100%",height:"100%",background:`linear-gradient(135deg,${G},#00a862)`,display:"flex",alignItems:"center",justifyContent:"center",color:"#000",fontWeight:800,fontSize:13}}>{initials||"?"}</div>}
        </div>
        <div title="Upload new photo" style={{position:"absolute",bottom:-1,right:-1,width:16,height:16,borderRadius:"50%",background:"#fff",border:`1px solid ${BORDER}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:MUTED}}>✎</div>
        <input type="file" accept="image/*" disabled={uploading} style={{display:"none"}} onChange={e=>uploadAvatar(e.target.files[0])}/>
      </label>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontWeight:600,color:DARK,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{userProfile.firstName} {userProfile.lastName}</div>
        <div style={{fontSize:11,color:MUTED,marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{userProfile.email}</div>
      </div>
    </div>
    <div style={{padding:"10px 12px"}}>
      <div style={{display:"flex",gap:6,marginBottom:8}}>
        <div style={{flex:1}}>
          <div style={{fontSize:10,color:MUTED,fontWeight:600,textTransform:"uppercase",letterSpacing:".04em",marginBottom:3}}>First name</div>
          <input value={first} onChange={e=>setFirst(e.target.value)} disabled={saving}
            style={{width:"100%",padding:"6px 9px",border:`1px solid ${dirty?G+"60":BORDER}`,borderRadius:6,fontSize:12,outline:"none",color:DARK,boxSizing:"border-box"}}/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:10,color:MUTED,fontWeight:600,textTransform:"uppercase",letterSpacing:".04em",marginBottom:3}}>Last name</div>
          <input value={last} onChange={e=>setLast(e.target.value)} disabled={saving}
            style={{width:"100%",padding:"6px 9px",border:`1px solid ${dirty?G+"60":BORDER}`,borderRadius:6,fontSize:12,outline:"none",color:DARK,boxSizing:"border-box"}}/>
        </div>
      </div>
      <div style={{marginBottom:8}}>
        <div style={{fontSize:10,color:MUTED,fontWeight:600,textTransform:"uppercase",letterSpacing:".04em",marginBottom:3}}>Email <span style={{textTransform:"none",fontWeight:400,opacity:0.6,marginLeft:4}} title="Change your email in your Clerk account at clerk.com — we don't manage it here">· change in Clerk</span></div>
        <input value={userProfile.email} disabled title="To change your email, update it in Clerk (your auth provider). We can't edit it from inside the app." style={{width:"100%",padding:"6px 9px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,outline:"none",color:MUTED,boxSizing:"border-box",background:"#f8faf9"}}/>
      </div>
      {dirty&&<button onClick={saveName} disabled={saving} style={{width:"100%",background:DARK,color:G,border:"none",borderRadius:7,padding:"7px 12px",fontWeight:700,fontSize:12,cursor:saving?"wait":"pointer",marginBottom:4}}>{saving?"Saving…":"Save changes"}</button>}
    </div>
    <div style={{padding:"4px 6px 6px",borderTop:`1px solid ${BORDER}`}}>
      <button onClick={signOut} style={{width:"100%",display:"flex",alignItems:"center",gap:8,padding:"7px 9px",border:"none",background:"none",cursor:"pointer",fontSize:13,color:"#DC2626",borderRadius:6,fontWeight:500}} onMouseEnter={e=>e.currentTarget.style.background="#FEF2F2"} onMouseLeave={e=>e.currentTarget.style.background="none"}>
        <span style={{width:18,height:18,borderRadius:4,background:"#FEF2F2",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11}}>→</span>Sign out
      </button>
    </div>
  </div>);
}

function Avatar({name,color,size=36}){const i=name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();return<div style={{width:size,height:size,borderRadius:"50%",background:color+"30",border:`2px solid ${color}50`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.32,fontWeight:700,color,flexShrink:0}}>{i}</div>;}
function Badge({label,color="#e8f0eb",text="#1a7a45"}){return<span style={{background:color,color:text,borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>{label}</span>;}
function Card({children,style={},hover=true}){const[h,setH]=useState(false);return<div style={{background:"#fff",borderRadius:10,border:`1px solid ${BORDER}`,boxShadow:h&&hover?"0 4px 16px rgba(0,0,0,0.06)":"none",transition:"box-shadow .15s,transform .15s",transform:h&&hover?"translateY(-1px)":"none",...style}} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}>{children}</div>;}
function Modal({title,subtitle,children,onClose,width=480}){return(<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",backdropFilter:"blur(3px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}><div style={{background:"#fff",borderRadius:14,padding:28,width,boxShadow:"0 0 0 1px rgba(0,0,0,0.08),0 24px 80px rgba(0,0,0,0.18)",maxHeight:"90vh",overflowY:"auto"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}><div><div style={{fontWeight:600,fontSize:16,color:DARK}}>{title}</div>{subtitle&&<div style={{fontSize:12,color:MUTED,marginTop:3}}>{subtitle}</div>}</div><button onClick={onClose} style={{border:"none",background:PAGE_BG,borderRadius:6,width:28,height:28,cursor:"pointer",color:MUTED,fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button></div>{children}</div></div>);}

// Replaces window.prompt() with a styled in-app modal. Used for renames.
// Usage: <PromptModal title="Rename campaign" initial={c.name} onCancel={...} onSubmit={(value)=>...}/>
function PromptModal({title, subtitle, label="Name", initial="", placeholder="", confirmLabel="Save", onCancel, onSubmit}){
  const [val, setVal] = useState(initial);
  const [busy, setBusy] = useState(false);
  const submit = async() => {
    const v = val.trim();
    if(!v || busy) return;
    setBusy(true);
    try{ await onSubmit(v); }
    finally{ setBusy(false); }
  };
  return(<Modal title={title} subtitle={subtitle} onClose={onCancel} width={440}>
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div>
        <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:5}}>{label}</label>
        <input autoFocus value={val} onChange={e=>setVal(e.target.value)} placeholder={placeholder} disabled={busy}
          onKeyDown={e=>{if(e.key==="Enter")submit(); if(e.key==="Escape")onCancel();}}
          style={{width:"100%",padding:"10px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
      </div>
      <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
        <button onClick={onCancel} disabled={busy} style={{background:"transparent",border:`1px solid ${BORDER}`,color:MUTED,borderRadius:8,padding:"9px 16px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
        <button onClick={submit} disabled={!val.trim()||busy} style={{background:val.trim()&&!busy?DARK:"#f0f0f0",color:val.trim()&&!busy?G:"#9ab8a6",border:"none",borderRadius:8,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:val.trim()&&!busy?"pointer":"not-allowed"}}>{busy?"Saving…":confirmLabel}</button>
      </div>
    </div>
  </Modal>);
}

// Replaces window.confirm() for destructive ops. Visual treatment makes
// the destructive nature obvious (red confirm button + warning copy).
function ConfirmModal({title, body, confirmLabel="Confirm", destructive=false, onCancel, onConfirm}){
  const [busy, setBusy] = useState(false);
  const submit = async() => {
    if(busy) return;
    setBusy(true);
    try{ await onConfirm(); }
    finally{ setBusy(false); }
  };
  const confirmStyle = destructive
    ? {bg:"#DC2626",fg:"#fff",hover:"#B91C1C"}
    : {bg:DARK,fg:G,hover:"#000"};
  return(<Modal title={title} onClose={onCancel} width={420}>
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{fontSize:13,color:DARK,lineHeight:1.55}}>{body}</div>
      <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
        <button onClick={onCancel} disabled={busy} style={{background:"transparent",border:`1px solid ${BORDER}`,color:MUTED,borderRadius:8,padding:"9px 16px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
        <button onClick={submit} disabled={busy}
          style={{background:confirmStyle.bg,color:confirmStyle.fg,border:"none",borderRadius:8,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:busy?"wait":"pointer",opacity:busy?0.7:1}}>
          {busy?"Working…":confirmLabel}
        </button>
      </div>
    </div>
  </Modal>);
}

function BrandSwitcher({brandsList=[],brandId,onSwitch,onAdd,brandLogos={}}){
  const[open,setOpen]=useState(false);const[,setAdding]=useState(false);const[newBrand,setNewBrand]=useState("");
  const[busy,setBusy]=useState(false);
  const[search,setSearch]=useState("");
  const BC=["#00c97a","#60A5FA","#F59E0B","#E879F9","#F87171","#34D399"];
  const bc=n=>BC[Math.abs((n||"").split("").reduce((a,c)=>a+c.charCodeAt(0),0))%BC.length];
  const ini=n=>(n||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
  const active=brandsList.find(b=>b.id===brandId);
  const activeName=active?.name||"Loading…";
  // Sort A→Z; filter by search term (case-insensitive contains).
  const sortedFiltered=[...brandsList]
    .sort((a,b)=>(a.name||"").localeCompare(b.name||"",undefined,{sensitivity:"base"}))
    .filter(b=>!search||((b.name||"").toLowerCase().includes(search.toLowerCase())));

  // eslint-disable-next-line no-unused-vars
  const handleAdd=async()=>{
    if(!newBrand.trim()||busy)return;
    setBusy(true);
    const ok=await onAdd(newBrand.trim());
    setBusy(false);
    if(ok){setNewBrand("");setAdding(false);setOpen(false);setSearch("");}
  };
  const renderLogo=(name,size)=>{
    const logo=brandLogos[name];
    if(logo)return(<img src={logo} alt="" style={{width:size,height:size,borderRadius:size>26?7:6,objectFit:"cover",flexShrink:0,border:"1px solid rgba(255,255,255,0.08)"}}/>);
    return(<div style={{width:size,height:size,borderRadius:size>26?7:6,background:bc(name)+"30",border:`1.5px solid ${bc(name)}60`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size>26?11:10,fontWeight:800,color:bc(name),flexShrink:0}}>{ini(name)}</div>);
  };

  return(<div style={{padding:"14px 16px",borderTop:"1px solid #1a3028",position:"relative"}}>
    {/* "Active brand" label + chunky trigger ONLY render when the dropdown
        is closed. When open, the dropdown's own header takes over so the
        operator doesn't see the same brand twice (which read as a UI bug
        — team member surfaced 2026-05-25). When open, we render just a
        thin "Close" strip in the trigger's spot so the operator has a
        clear way out without picking a brand. */}
    {!open && <>
      <div style={{fontSize:9,color:"#3d6b50",textTransform:"uppercase",letterSpacing:".1em",fontWeight:700,marginBottom:10}}>Active Brand</div>
      <div onClick={()=>setOpen(true)} style={{background:"#1a3028",borderRadius:10,padding:"10px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,border:"1px solid #2a4a38"}} onMouseEnter={e=>e.currentTarget.style.background="#223d2e"} onMouseLeave={e=>e.currentTarget.style.background="#1a3028"}>
        {renderLogo(activeName,28)}
        <div style={{flex:1,minWidth:0}}><div style={{color:"#e0f0e8",fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{activeName}</div><div style={{color:"#4a7a5e",fontSize:10,marginTop:1}}>{brandsList.length} brand{brandsList.length!==1?"s":""}</div></div>
        <span style={{color:"#4a7a5e",fontSize:10}}>▾</span>
      </div>
    </>}
    {open && (
      <div onClick={()=>{setOpen(false);setSearch("");}} title="Close brand switcher" style={{background:"transparent",borderRadius:10,padding:"10px 12px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,border:"1px dashed #2a4a38",color:"#4a7a5e",fontSize:11,fontWeight:600,letterSpacing:".05em",textTransform:"uppercase"}}
        onMouseEnter={e=>{e.currentTarget.style.background="#1a3028";e.currentTarget.style.color="#a8d5be";}}
        onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color="#4a7a5e";}}>
        ▴ Close
      </div>
    )}
    {open&&(<div style={{position:"absolute",bottom:"100%",left:16,right:16,background:"#162b22",borderRadius:12,border:"1px solid #2a4a38",overflow:"hidden",marginBottom:6,boxShadow:"0 -12px 32px rgba(0,0,0,0.4)",maxHeight:480,display:"flex",flexDirection:"column"}}>
      <div style={{padding:"10px 12px 6px",fontSize:9,color:"#3d6b50",textTransform:"uppercase",letterSpacing:".1em",fontWeight:700,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span>Switch brand · {brandsList.length} total</span>
        <button onClick={()=>{setOpen(false);setSearch("");}} title="Close" style={{border:"none",background:"transparent",color:"#4a7a5e",fontSize:14,cursor:"pointer",padding:"0 4px",lineHeight:1}}>✕</button>
      </div>
      {brandsList.length>4&&(<div style={{padding:"4px 8px 4px"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search brands…" autoFocus
          style={{width:"100%",background:"#0d1f1a",border:"1px solid #2a4a38",borderRadius:7,padding:"6px 10px",color:"#a8d5be",fontSize:12,outline:"none",boxSizing:"border-box"}}/>
      </div>)}
      <div style={{flex:1,overflowY:"auto"}}>
        {brandsList.length===0&&<div style={{padding:"12px",fontSize:12,color:"#4a7a5e",fontStyle:"italic"}}>No brands yet.</div>}
        {sortedFiltered.length===0&&search&&<div style={{padding:"12px",fontSize:12,color:"#4a7a5e",fontStyle:"italic"}}>No matches for "{search}"</div>}
        {sortedFiltered.map(b=>(<div key={b.id} onClick={()=>{onSwitch(b.id);setOpen(false);setSearch("");}} style={{padding:"9px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,background:b.id===brandId?"#1a3028":"transparent",borderRadius:8,margin:"2px 4px"}} onMouseEnter={e=>e.currentTarget.style.background="#223d2e"} onMouseLeave={e=>e.currentTarget.style.background=b.id===brandId?"#1a3028":"transparent"}>
          {renderLogo(b.name,24)}
          <span style={{fontSize:13,color:b.id===brandId?G:"#a8d5be",fontWeight:b.id===brandId?700:400,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.name}</span>
          {b.id===brandId&&<span style={{color:G,fontSize:12}}>✓</span>}
        </div>))}
      </div>
      {/* Brand creation moved to Agency workspace → Brands per Kristijan's
          feedback. The inline "Add new brands…" footer text was removed
          2026-05-25 because it added clutter and operators already know
          where to manage brands. */}
    </div>)}
  </div>);
}

// Status ↔ Kanban stage mapping
// After migration 0052 the status taxonomy IS the kanban-stage taxonomy.
// These maps are now identity for the 6 active stages — kept so legacy
// callers that pass through them keep working without code changes.
const STATUS_TO_STAGE={"To Outreach":"To Outreach","Outreached":"Outreached","Negotiation":"Negotiation","Paid Only":"Paid Only","Awaiting Post":"Awaiting Post","Posted":"Posted"};
const STAGE_TO_STATUS={"To Outreach":"To Outreach","Outreached":"Outreached","Negotiation":"Negotiation","Paid Only":"Paid Only","Awaiting Post":"Awaiting Post","Posted":"Posted"};

// ─── Public agreement signing page ───────────────────────────────────────
// Standalone, no-auth route at /sign/:token. Renders BEFORE the Clerk gate
// in the main App component, so creators (who don't have a kolabs account)
// can sign without signing up.
//
// Security model: the signing_token IS the auth credential. SECURITY DEFINER
// RPCs in the DB (get_agreement_for_signing + submit_creator_signature) gate
// access by checking the token matches a real agreement row. Token is 24-char
// base62, generated cryptographically — guessing isn't feasible.
function PublicSignPage({ token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signedAt, setSignedAt] = useState(null);
  const [sigMode, setSigMode] = useState("type"); // "type" | "draw"
  const [sigImage, setSigImage] = useState(null); // base64 data URL when drawn

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: rpcErr } = await supabase.rpc('get_agreement_for_signing', { p_token: token });
        if (cancelled) return;
        if (rpcErr) {
          setError(rpcErr.message.includes('agreement_not_found')
            ? "This signing link is invalid or has been revoked."
            : rpcErr.message.includes('invalid_token')
              ? "This signing link is malformed."
              : `Couldn't load agreement: ${rpcErr.message}`);
          setLoading(false);
          return;
        }
        const row = (data || [])[0];
        if (!row) {
          setError("This agreement no longer exists.");
          setLoading(false);
          return;
        }
        setAgreement(row);
        if (row.creator_signed_at) setSignedAt(row.creator_signed_at);
        setLoading(false);
      } catch (e) {
        if (!cancelled) { setError(e.message); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    if (!name.trim() || name.trim().length < 2) { alert("Please enter your full name."); return; }
    if (!email.includes("@")) { alert("Please enter a valid email."); return; }
    if (!agreed) { alert("Please tick the agreement checkbox."); return; }
    setSubmitting(true);
    // Best-effort capture of the public IP for audit. ipify is free + no key.
    let publicIp = null;
    try {
      const ipResp = await fetch("https://api.ipify.org?format=json");
      if (ipResp.ok) { const j = await ipResp.json(); publicIp = j.ip || null; }
    } catch {}
    const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : "").slice(0, 500);
    const { data, error: rpcErr } = await supabase.rpc('submit_creator_signature', {
      p_token: token, p_name: name.trim(), p_email: email.trim(), p_ip: publicIp, p_ua: ua,
      p_image: sigMode === "draw" ? sigImage : null,
    });
    setSubmitting(false);
    if (rpcErr) {
      alert(`Couldn't record signature: ${rpcErr.message}`);
      return;
    }
    setSignedAt(data || new Date().toISOString());
  };

  // ─── Render states ────────────────────────────────────────────────────
  const containerStyle = { minHeight: "100vh", background: "#f7f8f7", fontFamily: "'DM Sans',system-ui,sans-serif", color: "#0C0D0E", padding: "24px 16px" };
  const cardStyle = { maxWidth: 760, margin: "0 auto", background: "#fff", borderRadius: 16, padding: "40px 36px", boxShadow: "0 1px 3px rgba(0,0,0,0.04),0 8px 32px rgba(12,13,14,0.08)", border: "1px solid rgba(0,0,0,0.04)" };

  if (loading) {
    return (<div style={containerStyle}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
      <div style={{ ...cardStyle, textAlign: "center", padding: "60px 36px" }}>
        <div style={{ fontSize: 14, color: "#6B7280" }}>Loading agreement…</div>
      </div>
    </div>);
  }
  if (error) {
    return (<div style={containerStyle}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
      <div style={{ ...cardStyle, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Unable to load this agreement</div>
        <div style={{ fontSize: 14, color: "#6B7280", lineHeight: 1.6 }}>{error}</div>
        <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 20 }}>Contact the brand that sent this link if you think it's a mistake.</div>
      </div>
    </div>);
  }
  if (!agreement) return null;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" }) : "—";

  // ─── Signed (read-only confirmation) ──────────────────────────────────
  if (signedAt) {
    return (<div style={containerStyle}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
      <div style={{ ...cardStyle, textAlign: "center" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#00c97a", color: "#fff", margin: "0 auto 24px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36 }}>✓</div>
        <div style={{ fontWeight: 800, fontSize: 24, marginBottom: 6, letterSpacing: "-.02em" }}>Agreement signed</div>
        <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 28 }}>Signed on {fmtDate(signedAt)}</div>
        <div style={{ background: "#f8faf9", border: "1px solid #e5e7eb", borderRadius: 10, padding: "16px 20px", textAlign: "left", marginBottom: 24 }}>
          <div style={{ fontSize: 12, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, marginBottom: 8 }}>Document reference</div>
          <div style={{ fontFamily: "Menlo,Monaco,monospace", fontSize: 13, color: "#0C0D0E", marginBottom: 14 }}>{agreement.agreement_ref || token.slice(0, 16)}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, fontSize: 12 }}>
            <div>
              <div style={{ color: "#9CA3AF", fontWeight: 700, marginBottom: 3 }}>Signed by you</div>
              <div style={{ color: "#0C0D0E", fontWeight: 600 }}>{agreement.creator_signer_name || name}</div>
            </div>
            <div>
              <div style={{ color: "#9CA3AF", fontWeight: 700, marginBottom: 3 }}>Signed by {agreement.brand_name}</div>
              <div style={{ color: "#0C0D0E", fontWeight: 600 }}>{agreement.brand_signer_name}</div>
            </div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: "#9CA3AF", lineHeight: 1.6 }}>
          A copy of this signed agreement will be saved with the brand.<br/>You can close this page.
        </div>
      </div>
    </div>);
  }

  // ─── Sign UI ──────────────────────────────────────────────────────────
  const collabTypeLabel = agreement.collab_type || "Gifted";
  return (<div style={containerStyle}>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>

    {/* Header */}
    <div style={{ maxWidth: 760, margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 4px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {agreement.brand_logo
          ? <img src={agreement.brand_logo} alt={agreement.brand_name} style={{ width: 36, height: 36, borderRadius: 9, objectFit: "cover" }}/>
          : <div style={{ width: 36, height: 36, borderRadius: 9, background: "#0C0D0E", color: "#00c97a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18 }}>{(agreement.brand_name || "B").charAt(0)}</div>
        }
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0C0D0E", letterSpacing: "-.01em" }}>{agreement.brand_name}</div>
          <div style={{ fontSize: 11, color: "#6B7280" }}>via kolabs.agency</div>
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Menlo,Monaco,monospace", letterSpacing: ".02em" }}>Ref: {agreement.agreement_ref || token.slice(0, 16)}</div>
    </div>

    {/* Agreement card */}
    <div style={cardStyle}>
      <div style={{ marginBottom: 24, paddingBottom: 18, borderBottom: "1px solid #e5e7eb" }}>
        <div style={{ fontWeight: 800, fontSize: 26, color: "#0C0D0E", letterSpacing: "-.025em", marginBottom: 6 }}>{collabTypeLabel} Collaboration Agreement</div>
        <div style={{ fontSize: 14, color: "#6B7280" }}>Between <strong>{agreement.brand_name}</strong> and <strong>@{agreement.creator_handle}</strong></div>
      </div>

      {/* Rendered agreement content */}
      <div style={{ fontSize: 14, lineHeight: 1.75, color: "#1f2937", whiteSpace: "pre-wrap", marginBottom: 28 }}>
        {agreement.content_snapshot || "(agreement content was not snapshotted — contact the brand to resend)"}
      </div>

      {/* Brand signature (already signed) — show image if drawn, else typed in handwriting font */}
      <div style={{ background: "#f0fdf4", border: "1px solid rgba(0,201,122,0.3)", borderRadius: 10, padding: "14px 18px", marginBottom: 32, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: "#166534", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 }}>✓ Signed by brand</div>
          {agreement.brand_signer_image
            ? <img src={agreement.brand_signer_image} alt="Brand signature" style={{ maxHeight: 50, maxWidth: "100%", objectFit: "contain", background: "#fff", padding: 4, borderRadius: 6 }}/>
            : <div style={{ fontSize: 22, color: "#0C0D0E", fontWeight: 700, fontFamily: "'Caveat',cursive,sans-serif", fontStyle: "italic", lineHeight: 1.2 }}>{agreement.brand_signer_name}</div>
          }
          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4 }}><strong>{agreement.brand_signer_name}</strong></div>
        </div>
        <div style={{ fontSize: 11, color: "#6B7280", textAlign: "right", flexShrink: 0 }}>
          <div>{fmtDate(agreement.brand_signed_at)}</div>
        </div>
      </div>

      {/* Sign-as-creator section */}
      <div style={{ background: "#fafafa", borderRadius: 10, padding: "20px 22px", border: "2px dashed #d1d5db" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0C0D0E", marginBottom: 6 }}>Your signature</div>
        <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 16, lineHeight: 1.5 }}>By signing below, you accept the terms of this agreement. Your signature, email, timestamp, and IP address will be recorded for legal validity.</div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, color: "#0C0D0E", fontWeight: 700, marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>Your full name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Type your full legal name" style={{ width: "100%", padding: "11px 13px", border: "1px solid #d1d5db", borderRadius: 9, fontSize: 14, outline: "none", boxSizing: "border-box", color: "#0C0D0E" }} />
        </div>

        {/* Signature mode toggle: Type (handwriting font auto-render) vs Draw (canvas pad) */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, color: "#0C0D0E", fontWeight: 700, marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>Your signature</label>
          <div style={{ display: "flex", gap: 4, background: "#fff", borderRadius: 8, padding: 3, marginBottom: 8, border: "1px solid #d1d5db" }}>
            {[{k:"type",label:"⌨ Type"},{k:"draw",label:"✍ Draw"}].map(m=>(
              <button key={m.k} onClick={()=>setSigMode(m.k)} type="button" style={{flex:1,padding:"7px 10px",border:"none",background:sigMode===m.k?"#0C0D0E":"transparent",color:sigMode===m.k?"#00c97a":"#6B7280",borderRadius:6,fontSize:12,fontWeight:600,cursor:"pointer"}}>{m.label}</button>
            ))}
          </div>
          {sigMode === "type"
            ? <div style={{ padding: "14px 16px", background: "#fff", border: "1px dashed #d1d5db", borderRadius: 9, fontFamily: "'Caveat',cursive,sans-serif", fontStyle: "italic", fontWeight: 700, fontSize: 26, color: "#0C0D0E", lineHeight: 1.2, minHeight: 60, display: "flex", alignItems: "center" }}>{name || <span style={{ color: "#c0d4c8", fontSize: 13, fontFamily: "inherit", fontStyle: "normal", fontWeight: 400 }}>(your name typed above will appear here)</span>}</div>
            : <DrawSignature value={sigImage} typedFallback={name} onChange={setSigImage} height={110}/>
          }
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 11, color: "#0C0D0E", fontWeight: 700, marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>Your email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="creator@example.com" style={{ width: "100%", padding: "11px 13px", border: "1px solid #d1d5db", borderRadius: 9, fontSize: 14, outline: "none", boxSizing: "border-box" }} />
        </div>

        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 18, cursor: "pointer", fontSize: 13, color: "#1f2937", lineHeight: 1.5 }}>
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 2, accentColor: "#00c97a", width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}/>
          <span>I have read and agree to the terms of this {collabTypeLabel.toLowerCase()} collaboration agreement.</span>
        </label>

        {(() => {
          const drawIncomplete = sigMode === "draw" && !sigImage;
          const disabled = !name.trim() || !email.includes("@") || !agreed || submitting || drawIncomplete;
          return (<button onClick={submit} disabled={disabled} style={{
            width: "100%", padding: "14px 18px", background: disabled ? "#d1d5db" : "#00c97a",
            color: disabled ? "#9CA3AF" : "#0C0D0E",
            border: "none", borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: disabled ? "not-allowed" : "pointer", letterSpacing: "-.01em"
          }}>{submitting ? "Recording signature…" : drawIncomplete ? "Draw your signature above to continue" : "✍️ Sign agreement"}</button>);
        })()}
      </div>

      <div style={{ fontSize: 11, color: "#9CA3AF", textAlign: "center", marginTop: 18, lineHeight: 1.5 }}>
        Document reference: <span style={{ fontFamily: "Menlo,Monaco,monospace" }}>{agreement.agreement_ref || token.slice(0, 16)}</span>
      </div>
    </div>

    <div style={{ textAlign: "center", fontSize: 11, color: "#9CA3AF", marginTop: 18 }}>
      Powered by <a href="https://kolabs.agency" style={{ color: "#9CA3AF", textDecoration: "none", fontWeight: 600 }}>kolabs.agency</a>
    </div>

    {/* Caveat handwriting font for signatures — loaded lazily, fails gracefully */}
    <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&display=swap" rel="stylesheet"/>
  </div>);
}

// Map between sidebar nav state and URL path. Two-way:
//   ACTIVE_TO_PATH['receipts']      → '/content-wall'
//   PATH_TO_ACTIVE['/content-wall'] → 'receipts'
// Keeps the existing `active` state-machine intact (every screen still
// dispatches off `active===...`) — we just shadow it onto the URL so
// refresh, back/forward, deep-link from email all work.
const ACTIVE_TO_PATH = {
  dashboard:           '/dashboard',
  setup:               '/onboarding',
  finder:              '/finder',
  creators:            '/creators',
  campaigns:           '/campaigns',
  flows:               '/flows',
  queue:               '/queue',
  receipts:            '/content-wall',
  content:             '/content-analysis',
  agreements:          '/agreements',
  integrations:        '/integrations',
  settings:            '/settings',
  'agency-dashboard':  '/agency',
  'agency-brands':     '/agency/brands',
  'agency-revenue':    '/agency/revenue',
  'agency-team':       '/agency/team',
  'agency-outreach':   '/agency/outreach',
  'agency-activity':   '/agency/activity',
  'agency-drive':      '/agency/drive',
  'agency-suppress':   '/agency/suppress',
  'agency-changes':    '/agency/changes',
  'agency-agreements': '/agency/agreements',
};
const PATH_TO_ACTIVE = Object.entries(ACTIVE_TO_PATH).reduce((acc,[a,p])=>{acc[p]=a;return acc;},{});
// Root path aliases to dashboard — keeps old bookmarks / shared links working
// while the URL bar shows the explicit /dashboard whenever you're on it.
PATH_TO_ACTIVE['/'] = 'dashboard';
function activeFromPath(pathname){
  // Trim trailing slash (except root) so '/flows/' and '/flows' map the same.
  const norm = pathname.length>1 && pathname.endsWith('/') ? pathname.slice(0,-1) : pathname;
  return PATH_TO_ACTIVE[norm] || 'dashboard';
}

export default function App(){
  // Initial active read from URL so refreshing on /flows stays on /flows.
  const[active,setActive]=useState(()=>activeFromPath(typeof window!=='undefined'?window.location.pathname:'/'));
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const[creatorProfile,setCreatorProfile]=useState(null);
  // Which campaign the Campaigns screen has open. Lives here (not in Campaigns)
  // because opening a creator profile unmounts <Campaigns/> — if this state
  // lived there, Back from the profile would dump you on the campaign list
  // instead of the campaign you were browsing.
  const[campaignSelectedId,setCampaignSelectedId]=useState(null);
  // One-shot filter hint from Dashboard banners → Creators list. Creators clears it on mount.
  const[creatorsFilterHint,setCreatorsFilterHint]=useState(null);
  const navigateWithFilter=(tab,hint=null)=>{setCreatorsFilterHint(hint);setActive(tab);};

  // active → URL (pushState). Preserves existing search params so OAuth
  // callbacks (?gmail=...&email=...) don't get clobbered when the existing
  // useEffect setActive('integrations') fires.
  useEffect(()=>{
    const desired = ACTIVE_TO_PATH[active] || '/';
    if(typeof window==='undefined') return;
    if(window.location.pathname === desired) return;
    window.history.pushState({active}, '', desired + window.location.search);
  },[active]);

  // URL → active (browser back/forward, manual edit). popstate fires on the
  // browser's nav buttons; we re-derive active so the sidebar highlight + the
  // rendered screen match what the address bar says.
  useEffect(()=>{
    const onPop = ()=>setActive(activeFromPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return ()=>window.removeEventListener('popstate', onPop);
  },[]);

  // Handle OAuth redirect from Google (?gmail=...) or Shopify (?shopify=...). Runs once on mount.
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const gmail=params.get('gmail');
    const shopify=params.get('shopify');
    const agencyDrive=params.get('agency_drive');
    if(!gmail&&!shopify&&!agencyDrive)return;
    if(gmail==='connected'){
      const email=params.get('email');
      toast(email?`Gmail connected: ${email}`:"Gmail connected");
      setActive('integrations');
    }else if(gmail==='error'){
      toast(`Gmail connect failed: ${params.get('reason')||'unknown'}`,'error');
    }
    if(shopify==='connected'){
      const shop=params.get('shop');
      toast(shop?`Shopify connected: ${shop}`:"Shopify connected");
      setActive('integrations');
    }else if(shopify==='error'){
      toast(`Shopify connect failed: ${params.get('reason')||'unknown'}`,'error');
    }
    if(agencyDrive==='connected'){
      const email=params.get('email');
      toast(email?`Agency Drive connected: ${email}`:"Agency Drive connected");
      setActive('settings');
    }else if(agencyDrive==='error'){
      toast(`Agency Drive connect failed: ${params.get('reason')||'unknown'}`,'error');
    }
    const agencyGmail=params.get('agency_gmail');
    if(agencyGmail==='connected'){
      const email=params.get('email');
      toast(email?`Agency Gmail connected: ${email}`:"Agency Gmail connected");
      setActive('agency');
    }else if(agencyGmail==='error'){
      toast(`Agency Gmail connect failed: ${params.get('reason')||'unknown'}`,'error');
    }
    // Strip the query so we don't re-toast on reload.
    const clean=window.location.pathname+window.location.hash;
    window.history.replaceState({},'',clean);
  },[]);

  // Capture invite token AND brand-restore token at mount BEFORE the strip-query effect runs.
  // Both wait for Clerk user to be loaded before firing.
  const [pendingInviteToken, setPendingInviteToken] = useState(()=>{
    if (typeof window === 'undefined') return null;
    const p = new URLSearchParams(window.location.search);
    return p.get('invite');
  });
  const [pendingRestoreToken, setPendingRestoreToken] = useState(()=>{
    if (typeof window === 'undefined') return null;
    const p = new URLSearchParams(window.location.search);
    return p.get('restore_brand');
  });
  const { user: clerkUser } = useUser();
  useEffect(()=>{
    if (!pendingInviteToken || !clerkUser) return;
    const email = clerkUser.primaryEmailAddress?.emailAddress || clerkUser.emailAddresses?.[0]?.emailAddress || null;
    (async()=>{
      const { data: newBrandId, error } = await supabase.rpc('claim_brand_invite', {
        p_token: pendingInviteToken,
        p_email: email,
      });
      setPendingInviteToken(null);
      if (error) {
        toast(`Invite couldn't be claimed: ${error.message}`, 'error');
        return;
      }
      toast('✓ Welcome — you now have access to this brand');
      // Force reload of brands list + switch to the joined brand.
      const { data: updated } = await supabase.from('brands').select('id, name, slug').is('deleted_at', null).order('created_at');
      if (updated) {
        setBrandsList(updated);
        const target = updated.find(b => b.id === newBrandId);
        if (target) {
          setBrandId(target.id);
          setActiveBrandIdLS(target.id);
          setActiveBrand(target.name);
        }
      }
      setActive('dashboard');
    })();
  // eslint-disable-next-line
  }, [pendingInviteToken, clerkUser]);

  // Belt-and-suspenders: every time a Clerk user signs in, also try to claim
  // any pending brand_members invites that match their VERIFIED Clerk emails.
  // Catches the cases the token-based claim above misses: email typo in the
  // invite, Clerk-rewritten redirect URL that strips ?invite, returning user
  // with no URL token. Safe to call repeatedly — the RPC only claims rows
  // that match a verified email AND haven't been claimed yet.
  useEffect(()=>{
    if (!clerkUser) return;
    const verifiedEmails = (clerkUser.emailAddresses || [])
      .filter(e => e?.verification?.status === 'verified' && e?.emailAddress)
      .map(e => e.emailAddress);
    if (verifiedEmails.length === 0) return;
    (async()=>{
      const { data: claimedBrandIds, error } = await supabase.rpc('claim_pending_invites_by_email', {
        p_emails: verifiedEmails,
      });
      if (error) { console.warn('auto-claim invites failed', error); return; }
      if (!claimedBrandIds || claimedBrandIds.length === 0) return;
      toast(`✓ Joined ${claimedBrandIds.length} brand${claimedBrandIds.length===1?"":"s"}`);
      // Refresh brand list + switch to the most recently joined brand.
      const { data: updated } = await supabase.from('brands').select('id, name, slug').is('deleted_at', null).order('created_at');
      if (updated) {
        setBrandsList(updated);
        const target = updated.find(b => b.id === claimedBrandIds[claimedBrandIds.length-1]);
        if (target) {
          setBrandId(target.id);
          setActiveBrandIdLS(target.id);
          setActiveBrand(target.name);
        }
      }
    })();
  // eslint-disable-next-line
  }, [clerkUser?.id]);

  // Brand-restore claim flow. Owner clicks the recovery link from the deletion-notice email,
  // we call the RPC, brand un-deletes, sidebar refreshes.
  useEffect(()=>{
    if (!pendingRestoreToken || !clerkUser) return;
    (async()=>{
      const { data: restoredBrandId, error } = await supabase.rpc('restore_brand_by_token', { p_token: pendingRestoreToken });
      setPendingRestoreToken(null);
      if (error) { toast(`Couldn't restore brand: ${error.message}`, 'error'); return; }
      logSecurityEvent(restoredBrandId,'brand.restore','brand',restoredBrandId,{via:'recovery_email_link'});
      toast('✓ Brand restored — all your data is back');
      // Refresh brand list + jump to the restored brand.
      const { data: updated } = await supabase.from('brands').select('id, name, slug').is('deleted_at', null).order('created_at');
      if (updated) {
        setBrandsList(updated);
        const target = updated.find(b => b.id === restoredBrandId);
        if (target) {
          setBrandId(target.id);
          setActiveBrandIdLS(target.id);
          setActiveBrand(target.name);
        }
      }
      setActive('dashboard');
    })();
  // eslint-disable-next-line
  }, [pendingRestoreToken, clerkUser]);

  const[brandData,setBrandData]=useLocalStorage("ios_brandData",INITIAL_BRAND_DATA);
  const[activeBrand,setActiveBrand]=useLocalStorage("ios_activeBrand","New Brand");
  const brand=brandData[activeBrand]||Object.values(brandData)[0];

  // Brand-scoped key prefix — all data isolated per brand
  const bk=activeBrand.toLowerCase().replace(/[^a-z0-9]/g,"_");
  const { user } = useUser();
  const userId = user?.id || 'anonymous';
  // Owner-only flag drives the Agency workspace + a few other gated surfaces.
  // Source of truth: user_owns_any_brand() RPC (checks brands.owner_clerk_user_id).
  const [isAgencyOwner, setIsAgencyOwner] = useState(false);
  useEffect(()=>{
    if(!userId||userId==='anonymous'){ setIsAgencyOwner(false); return; }
    (async()=>{
      const{data}=await supabase.rpc('user_owns_any_brand');
      setIsAgencyOwner(!!data);
    })();
  },[userId]);
  // Active workspace mode — derived from the active page id. Any "agency-*"
  // value means we're in the cross-brand owner workspace (different sidebar,
  // brand switcher hidden). The default brand-ops workspace covers everything else.
  const inAgencyMode = typeof active === "string" && active.startsWith("agency-");
  const [allCreators, setAllCreators] = useState([]);
  const [, setCreatorsLoaded] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  // ---------- Brand bootstrap (Supabase, multi-brand) ----------
  // Operator (Clerk user) can own multiple brand rows. Loads all brands they own,
  // remembers which one is active across reloads, lets them switch + add new.
  // brandId is the active brand's uuid — every creator/flow/email/campaign effect keys off it.
  const [brandId, setBrandId] = useState(null);
  const [brandsList, setBrandsList] = useState([]); // [{id, name, slug}]
  const [activeBrandIdLS, setActiveBrandIdLS] = useLocalStorage('ios_activeBrandId', null);

  useEffect(() => {
    if (!userId || userId === 'anonymous') return;
    let cancelled = false;
    (async () => {
      const { data: existing, error } = await supabase.from('brands').select('id, name, slug').is('deleted_at', null).order('created_at');
      if (cancelled) return;
      if (error) { console.error('brands load', error); return; }
      if (existing && existing.length) {
        setBrandsList(existing);
        // Resume on the previously-active brand if it's still in the list, otherwise first.
        const resume = (activeBrandIdLS && existing.find(b => b.id === activeBrandIdLS)) || existing[0];
        setBrandId(resume.id);
        // Mirror name into activeBrand so the localStorage UI cache stays in sync (signature, codeFormat, etc.).
        if (resume.name && resume.name !== activeBrand) setActiveBrand(resume.name);
        return;
      }
      // No brands accessible to this user. Previously we auto-created a brand
      // literally named "New Brand" so first-time users had something to land
      // on — but this also fired for invited teammates whose Clerk invite-claim
      // hadn't completed yet, creating phantom brands they didn't ask for and
      // then dumping real creator data into them (incident 2026-05-25). Now we
      // leave brandId null + brandsList empty; the App-level render branches
      // to a "No access yet — contact your agency owner" empty state instead
      // of the dashboard.
      //
      // The auto-claim-by-verified-email path (migration 0048) still runs in
      // parallel — when it completes, the user's invite acceptance triggers a
      // brands-list reload and they land on the intended brand.
      setBrandsList([]);
      setBrandId(null);
    })();
    return () => { cancelled = true; };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cross-device settings hydration — pulls brands.settings JSONB into the
  // localStorage brandData cache whenever brandId changes. Without this, the
  // logo + other DB-persisted fields would be invisible on a fresh device or
  // after the user clears localStorage. We only OVERWRITE fields that exist
  // in DB settings — local fields not in DB stay untouched.
  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.from('brands').select('settings, name').eq('id', brandId).maybeSingle();
      if (cancelled || error || !data) return;
      const dbSettings = data.settings || {};
      const brandKey = data.name || activeBrand;
      // Merge — DB settings take precedence for fields that exist there.
      setBrandData(prev => {
        const cur = prev[brandKey] || emptyBrandData(brandKey);
        const merged = { ...cur };
        for (const k of Object.keys(dbSettings)) {
          if (dbSettings[k] != null) merged[k] = dbSettings[k];
        }
        if (JSON.stringify(merged) === JSON.stringify(cur)) return prev;
        return { ...prev, [brandKey]: merged };
      });
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // Re-fetch brandsList from DB. Called after a brand is added or renamed so the
  // sidebar switcher reflects the latest state without a hard refresh.
  const reloadBrands = async () => {
    const { data } = await supabase.from('brands').select('id, name, slug').is('deleted_at', null).order('created_at');
    if (data) setBrandsList(data);
  };

  // Switch the active brand. Updates brandId + localStorage cache + the legacy activeBrand-name
  // string (used as a key for various localStorage UI caches — kanban, agreements, brandData).
  const switchBrand = async (newBrandId) => {
    const target = brandsList.find(b => b.id === newBrandId);
    if (!target || target.id === brandId) return;
    const currentName = activeBrand || (brandsList.find(b => b.id === brandId)?.name) || "current brand";
    // Confirmation modal on every brand switch. Real-world multi-brand
    // operators routinely think they're in brand A when they're actually
    // editing brand B — the modal is a deliberate brake. Also lists the
    // visible side effects (open creator profile closes; you land on
    // dashboard) so the operator knows what they're losing.
    const proceed = await confirmDialog({
      title: `Switch to ${target.name}?`,
      body: `You're leaving ${currentName} and switching to ${target.name}. Any open creator profile or modal closes, and you'll land on the ${target.name} dashboard. Make sure your current edits are saved.`,
      confirmLabel: `Switch to ${target.name}`,
    });
    if (!proceed) return;
    setBrandId(target.id);
    setActiveBrandIdLS(target.id);
    setActiveBrand(target.name);
    setActive('dashboard');
    setCreatorProfile(null);
    setCampaignSelectedId(null);
    toast(`Switched to ${target.name}`, 'info');
  };

  // Create a new brand owned by the current Clerk user (RLS handles ownership). Switches to it.
  const addBrand = async (rawName) => {
    const name = (rawName || '').trim();
    if (!name) { toast('Name required', 'error'); return false; }
    if (brandsList.some(b => b.name.toLowerCase() === name.toLowerCase())) {
      toast(`Brand "${name}" already exists`, 'error'); return false;
    }
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '-' + Math.random().toString(36).slice(2, 6);
    const { data: created, error } = await supabase.from('brands')
      .insert({ name, slug, settings: emptyBrandData(name) }).select('id, name, slug').single();
    if (error) { toast(`Couldn't create brand: ${error.message}`, 'error'); return false; }
    // Creator becomes the owner of this brand.
    const userEmail = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || null;
    await supabase.from('brand_members').insert({
      brand_id: created.id, clerk_user_id: userId, email: userEmail,
      role: 'owner', accepted_at: new Date().toISOString(),
    });
    // Seed localStorage UI cache for the new brand so the brand object isn't undefined while loading.
    setBrandData(prev => ({ ...prev, [name]: emptyBrandData(name) }));
    setBrandsList(prev => [...prev, created]);
    setBrandId(created.id);
    setActiveBrandIdLS(created.id);
    setActiveBrand(name);
    setActive('setup');
    setCreatorProfile(null);
    setCampaignSelectedId(null);
    toast(`Brand "${name}" created — running Setup wizard`);
    // brand_id: null is intentional — brand.create is an agency-level action.
    // Logging against the previously-active brand would leak the new brand's
    // existence to that brand's team members via operator_actions_team_select.
    logOperatorAction('brand.create', {brandId: null, targetType:'brand', targetId:created.id, details:{name}});
    return true;
  };

  // Creators + campaigns are brand-scoped (RLS by brand_id). We still send user_id
  // on writes for backward compatibility with the legacy column.
  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    // Supabase default caps .select() at 1000 rows. Real brands at agency scale
    // hit thousands of creators (especially with imports). Paginate via .range()
    // until we've drained the table, hard-stopping at 50k as a safety bound.
    (async () => {
      const PAGE = 1000;
      const SAFETY_MAX = 50000;
      let all = [];
      let from = 0;
      while (from < SAFETY_MAX) {
        const { data, error } = await supabase.from('creators').select('*')
          .eq('brand_id', brandId)
          .order('created_at', { ascending: false })
          .range(from, from + PAGE - 1);
        if (cancelled) return;
        if (error) { console.error('creators load', error); break; }
        if (!data || data.length === 0) break;
        all = all.concat(data);
        if (data.length < PAGE) break; // last page (partial fill)
        from += PAGE;
      }
      if (cancelled) return;
      // Hydrate derived `market` field — creators table has no dedicated market
      // column, country lives in `niche`. UI reads c.market everywhere; we
      // surface niche as market so display works without touching render sites.
      // Also surface DB column `discount_code` (snake) as React-side
      // `discountCode` (camel) so existing render code keeps working without
      // a sweep across 7 lookup sites.
      const hydrated = all.map(c => ({
        ...c,
        market: c.market || c.niche || null,
        discountCode: c.discount_code || c.discountCode || null,
        // Surface DB columns under the camelCase keys the CreatorProfile edits use,
        // so saved values actually load back (previously these were UI-only).
        followersF: c.followers ?? c.followersF ?? null,
        eng: c.engagement_rate ?? c.eng ?? null,
        order: c.shopify_order_id ?? c.order ?? null,
        usageRights: c.usage_rights ?? c.usageRights ?? null,
        broll: c.broll ?? null,
        // shipping_info jsonb → granular shipping fields
        addressStreet: c.shipping_info?.address1 ?? c.addressStreet ?? "",
        addressCity: c.shipping_info?.city ?? c.addressCity ?? "",
        addressState: c.shipping_info?.state ?? c.addressState ?? "",
        addressPostcode: c.shipping_info?.zip ?? c.addressPostcode ?? "",
        addressCountry: c.shipping_info?.country ?? c.addressCountry ?? "",
      }));
      setAllCreators(hydrated);
      setCreatorsLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [brandId]);

  // Whitelist of columns we're allowed to write through targeted updates below.
  // Anything not on this list (followersF, market, color, score, eng — UI-only
  // derived/display fields) gets dropped instead of erroring out the whole update.
  // Whitelist of creator fields that can be updated via persistCreatorPatch.
  // Intentionally excludes 'handle': it's the identifier used as a foreign-key
  // across activity_log, email_queue, kanban_items, flow_assignments. Renaming
  // it orphans all that history. If an operator needs to "fix a typo in the
  // handle", they should archive the old creator and add a fresh one — that
  // path preserves history under the old handle for audit and starts clean.
  const CREATOR_WRITABLE = useMemo(()=>new Set([
    'name','email','platform','followers','niche','status','notes','address',
    'products_sent','review_reason','shopify_order_id','shopify_order_url','shipping_info',
    'tracking_number','tracking_url','tracking_company','fulfilled_at','shipped_reply_sent_at',
    'archived','discount_code','engagement_rate','instagram','tiktok','youtube',
    'usage_rights','broll',
  ]),[]);

  // React side uses camelCase; DB uses snake_case for some fields. The map below
  // covers the ones where the two diverge — anything not in the map passes
  // through unchanged.
  const CREATOR_CAMEL_TO_SNAKE = useMemo(()=>({
    discountCode: 'discount_code',
    followersF: 'followers',
    eng: 'engagement_rate',
    order: 'shopify_order_id',
    usageRights: 'usage_rights',
  }),[]);

  // The 5 granular shipping fields the UI edits → keys inside the shipping_info
  // jsonb column (which the gifting/agreed-sheet flow reads).
  const ADDRESS_TO_SHIPPING = useMemo(()=>({
    addressStreet:'address1', addressCity:'city', addressState:'state',
    addressPostcode:'zip', addressCountry:'country',
  }),[]);

  // Helper for per-handler targeted updates. Updates local state optimistically,
  // then persists only the whitelisted fields. Errors surface as a toast so
  // silent data loss can't happen. Match key is uuid id when available, else handle.
  const persistCreatorPatch = async (creator, patch) => {
    setAllCreators(cs => cs.map(c =>
      ((creator.id && c.id===creator.id) || (c.handle===creator.handle))
        ? {...c, ...patch} : c
    ));
    if (!brandId) return;
    const dbPatch = {};
    // Any shipping edit rebuilds the shipping_info jsonb from the creator's
    // current granular fields merged with this patch (preserves phone etc.).
    if (Object.keys(patch).some(k => ADDRESS_TO_SHIPPING[k])) {
      const merged = { ...creator, ...patch };
      dbPatch.shipping_info = {
        ...(creator.shipping_info || {}),
        address1: merged.addressStreet || "",
        city: merged.addressCity || "",
        state: merged.addressState || "",
        zip: merged.addressPostcode || "",
        country: merged.addressCountry || "",
      };
    }
    Object.keys(patch).forEach(k => {
      if (ADDRESS_TO_SHIPPING[k]) return; // folded into shipping_info above
      const dbKey = CREATOR_CAMEL_TO_SNAKE[k] || k;
      if (CREATOR_WRITABLE.has(dbKey)) dbPatch[dbKey] = patch[k];
    });
    if (Object.keys(dbPatch).length === 0) return;
    const q = supabase.from('creators').update(dbPatch).eq('brand_id', brandId);
    const { error } = await (creator.id ? q.eq('id', creator.id) : q.eq('handle', creator.handle));
    if (error) { console.error('creator update failed', error, patch); toast(`Save failed: ${error.message}`, 'error'); }
  };

  useEffect(() => {
    if (!brandId) return;
    supabase.from('campaigns').select('*').eq('brand_id', brandId)
      .then(({ data, error }) => {
        if (!error && data) setCampaigns(data);
      });
  }, [brandId]);
  // Was localStorage, now DB-backed (migration 0025 / 0026). Local state is the working
  // copy + write-through to Supabase on every change so kanban + agreements survive
  // browser/device switches per brand.
  const [kanbanData, setKanbanData] = useState({});
  const [agreements, setAgreements] = useState([]);

  // Load kanban_items for the current brand → group by campaign_id → shape into
  // the {[campaignId]: [{name, handle, stage, ...}]} dict the existing UI expects.
  useEffect(() => {
    if (!brandId) { setKanbanData({}); return; }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.from('kanban_items')
        .select('*').eq('brand_id', brandId).order('position', { ascending: true });
      if (cancelled || error) return;
      // Enrich each item with creator details (name, color) from allCreators if available.
      const creatorByHandle = new Map(allCreators.map(c => [c.handle, c]));
      const grouped = {};
      for (const item of (data || [])) {
        const c = creatorByHandle.get(item.creator_handle) || {};
        if (!grouped[item.campaign_id]) grouped[item.campaign_id] = [];
        grouped[item.campaign_id].push({
          id: item.id,
          name: c.name || item.creator_handle,
          handle: item.creator_handle,
          followers: c.followersF || c.followers || '?',
          stage: item.stage,
          color: c.color || '#60A5FA',
          email: c.email,
          market: c.niche,
        });
      }
      setKanbanData(grouped);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [brandId, allCreators.length]);

  // Load agreements similarly. UI shape preserves the existing object structure.
  useEffect(() => {
    if (!brandId) { setAgreements([]); return; }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.from('agreements')
        .select('*').eq('brand_id', brandId).order('created_at', { ascending: false });
      if (cancelled || error) return;
      const creatorByHandle = new Map(allCreators.map(c => [c.handle, c]));
      const mapped = (data || []).map(r => ({
        id: r.id,
        creator: { name: creatorByHandle.get(r.creator_handle)?.name || r.creator_handle, handle: r.creator_handle, email: creatorByHandle.get(r.creator_handle)?.email || null },
        brand: activeBrand,
        collabType: r.collab_type,
        status: r.status,
        content: r.content_snapshot,
        compensation: r.compensation,
        commissionRate: r.commission_rate,
        sparkAds: r.spark_ads,
        postingDeadline: r.posting_deadline,
        usageRights: r.usage_rights,
        usageDuration: r.usage_duration,
        jurisdiction: r.jurisdiction,
        driveId: r.drive_file_id,
        driveUrl: r.drive_url,
        sentAt: r.sent_at,
        signedAt: r.signed_at,
        // Signing workflow fields (added migration 0036)
        signingToken: r.signing_token,
        agreementRef: r.agreement_ref,
        brandSignerName: r.brand_signer_name,
        brandSignerEmail: r.brand_signer_email,
        brandSignedAt: r.brand_signed_at,
        creatorSignerName: r.creator_signer_name,
        creatorSignerEmail: r.creator_signer_email,
        creatorSignedAt: r.creator_signed_at,
        creatorViewedAt: r.creator_viewed_at,
        creatorViewCount: r.creator_view_count,
        creatorSignerIp: r.creator_signer_ip,
        // Signed-PDF + drawn-signature images (persisted by google-agency-drive on
        // sign). Without these in the load map, a reload reverted "Open signed PDF"
        // back to "Save to Drive" and drawn signatures rendered as typed names.
        signedPdfDriveId: r.signed_pdf_drive_id,
        // No signed_pdf_url column exists — reconstruct the Drive view link from the
        // file id so the "Open signed PDF" button works after a reload (otherwise
        // pdfUploaded was true but the link had no URL to point at).
        signedPdfDriveUrl: r.signed_pdf_drive_id ? `https://drive.google.com/file/d/${r.signed_pdf_drive_id}/view` : null,
        brandSignerImage: r.brand_signer_image,
        creatorSignerImage: r.creator_signer_image,
        created: r.created_at ? new Date(r.created_at).toLocaleDateString('en-AU') : '',
      }));
      setAgreements(mapped);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [brandId, allCreators.length, activeBrand]);

  // Kanban write helpers. These mirror local state updates with DB writes so any
  // drag / add / remove persists across devices.
  const persistKanbanItem = async (campaignId, creatorHandle, patch) => {
    if (!brandId) return;
    // Upsert by (campaign_id, creator_handle) — unique constraint enforces dedup.
    const { error } = await supabase.from('kanban_items')
      .upsert({
        brand_id: brandId, campaign_id: campaignId, creator_handle: creatorHandle,
        ...patch,
      }, { onConflict: 'campaign_id,creator_handle' });
    if (error) console.error('kanban upsert failed', error);
  };
  const removeKanbanItem = async (campaignId, creatorHandle) => {
    if (!brandId) return;
    await supabase.from('kanban_items').delete()
      .eq('brand_id', brandId).eq('campaign_id', campaignId).eq('creator_handle', creatorHandle);
  };

  // Agreement write helpers.
  const persistAgreement = async (agreement, patch = {}) => {
    if (!brandId) return null;
    const handle = agreement.creator?.handle || agreement.handle;
    if (!handle) return null;
    const row = {
      brand_id: brandId,
      creator_handle: handle,
      collab_type: agreement.collabType || 'Gifted',
      status: agreement.status || 'Draft',
      content_snapshot: agreement.content || null,
      compensation: agreement.compensation || null,
      commission_rate: agreement.commissionRate || null,
      spark_ads: !!agreement.sparkAds,
      posting_deadline: agreement.postingDeadline || null,
      usage_rights: agreement.usageRights || null,
      usage_duration: agreement.usageDuration || null,
      jurisdiction: agreement.jurisdiction || null,
      drive_file_id: agreement.driveId || null,
      drive_url: agreement.driveUrl || null,
      ...patch,
    };
    if (agreement.id) {
      const { error } = await supabase.from('agreements').update(row).eq('id', agreement.id);
      // These are signed legal documents — a swallowed RLS/network failure here
      // meant "Saved" toasts fired while the DB kept the old row. Return null so
      // callers can surface the failure instead of showing false success.
      if (error) { console.error('agreement update failed', error); return null; }
      return agreement.id;
    } else {
      const { data, error } = await supabase.from('agreements').insert(row).select('id').single();
      if (error) { console.error('agreement insert failed', error); return null; }
      return data.id;
    }
  };
  const removeAgreement = async (id) => {
    if (!id) return false;
    const { error } = await supabase.from('agreements').delete().eq('id', id);
    if (error) { console.error('agreement delete failed', error); return false; }
    return true;
  };
  // Pull from Clerk — never hardcode a specific operator. Falls back gracefully
  // if Clerk hasn't returned user metadata yet (e.g. anonymous bootstrap state).
  const[userProfile,setUserProfile]=useState({firstName:"",lastName:"",email:"",avatar:null});
  useEffect(()=>{
    if(!user)return;
    setUserProfile({
      firstName:user.firstName||"",
      lastName:user.lastName||"",
      email:user.primaryEmailAddress?.emailAddress||user.emailAddresses?.[0]?.emailAddress||"",
      avatar:user.imageUrl||null,
    });
  },[user]);
  const[showUserMenu,setShowUserMenu]=useState(false);

  // ---------- Flows (Supabase-backed) ----------
  const [flows, setFlows] = useState([]);
  const [flowsReady, setFlowsReady] = useState(false);
  const lastSavedFlowsRef = useRef([]);
  // Surfaced to <Flows> so the editor can show "Saving…" / "Saved · 3s ago" / "Save error"
  // instead of the misleading "Save changes" button (everything auto-saves via debounce).
  const [flowSaveState, setFlowSaveState] = useState('idle'); // 'idle' | 'pending' | 'saving' | 'error'
  const [flowSavedAt, setFlowSavedAt] = useState(null);

  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    (async () => {
      setFlowsReady(false);
      const { data, error } = await supabase.from('flows').select('*').eq('brand_id', brandId).order('created_at');
      if (cancelled) return;
      if (error) { console.error('flows load', error); return; }
      if (data && data.length) {
        setFlows(data);
        lastSavedFlowsRef.current = data;
        setFlowsReady(true);
        return;
      }
      // First run for this brand → migrate localStorage or seed defaults
      const lsKey = `ios_flows_${bk}`;
      let seed = FLOWS_INIT;
      try {
        const s = localStorage.getItem(lsKey);
        const parsed = s ? JSON.parse(s) : null;
        if (Array.isArray(parsed) && parsed.length) seed = parsed;
      } catch {}
      const rows = seed.map(f => ({
        id: (typeof crypto!=='undefined' && crypto.randomUUID) ? crypto.randomUUID() : undefined,
        brand_id: brandId,
        name: f.name,
        type: f.type,
        steps: f.steps || [],
        used: f.used || 0,
        reply: f.reply || '—',
        is_active: true,
        split_weight: 100,
        smart_pause_days: 5,
      }));
      const { data: inserted, error: e2 } = await supabase.from('flows').insert(rows).select();
      if (cancelled) return;
      if (e2) { console.error('flows seed', e2); return; }
      try { localStorage.removeItem(lsKey); } catch {}
      setFlows(inserted || []);
      lastSavedFlowsRef.current = inserted || [];
      setFlowsReady(true);
    })();
    return () => { cancelled = true; };
  }, [brandId, bk]);

  // Debounced diff-and-sync for flow edits.
  useEffect(() => {
    if (!flowsReady || !brandId) return;
    // Detect if there's anything to save before announcing pending state (avoids
    // flashing "Saving…" on the initial load when the ref is just being seeded).
    const prev = lastSavedFlowsRef.current;
    const dirty = flows.length !== prev.length
      || flows.some(f => JSON.stringify(f) !== JSON.stringify(prev.find(p => p.id === f.id)));
    if (!dirty) return;
    setFlowSaveState('pending');
    const timer = setTimeout(async () => {
      setFlowSaveState('saving');
      const prevById = new Map(prev.map(f => [f.id, f]));
      const currIds = new Set(flows.map(f => f.id));
      const upserts = flows.filter(f => {
        const old = prevById.get(f.id);
        return !old || JSON.stringify(old) !== JSON.stringify(f);
      });
      const deletes = prev.filter(f => !currIds.has(f.id)).map(f => f.id);
      try {
        if (upserts.length) {
          const payload = upserts.map(f => ({
            id: f.id, brand_id: brandId,
            name: f.name, type: f.type, steps: f.steps,
            used: f.used ?? 0, reply: f.reply ?? '—',
            is_active: f.is_active !== false,
            split_weight: typeof f.split_weight === 'number' ? f.split_weight : 100,
            smart_pause_days: (typeof f.smart_pause_days === 'number' && f.smart_pause_days >= 1 && f.smart_pause_days <= 30) ? f.smart_pause_days : 5,
            attach_pdf_drive_file_id: f.attach_pdf_drive_file_id || null,
            attach_pdf_filename: f.attach_pdf_filename || null,
          }));
          const { error } = await supabase.from('flows').upsert(payload);
          if (error) throw error;
        }
        if (deletes.length) {
          const { error } = await supabase.from('flows').delete().in('id', deletes);
          if (error) throw error;
        }
        lastSavedFlowsRef.current = flows;
        setFlowSaveState('idle');
        setFlowSavedAt(Date.now());
      } catch (err) {
        console.error('flows sync', err);
        setFlowSaveState('error');
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [flows, flowsReady, brandId]);

  // ---------- Activity log (Supabase-backed) ----------
  const [activityLog, setActivityLog] = useState([]);

  // ---------- Operator activity tracking (session + actions) ----------
  // Two clocks running per session:
  //   last_active_at  — session still alive (heartbeat tick, tab-visible only)
  //   active_seconds  — accumulating "real work" counter, only ticks when the
  //                     operator has touched mouse/keyboard within last 5 min
  // The latter is what we trust for owner-facing hours-worked dashboards.
  const sessionIdRef = useRef(null);
  const heartbeatRef = useRef(null);
  const lastInputRef = useRef(Date.now());
  const IDLE_THRESHOLD_MS = 5 * 60 * 1000; // 5 min
  const HEARTBEAT_MS = 30000;

  useEffect(() => {
    if (!userId || userId === 'anonymous') return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.from('operator_sessions').insert({
        clerk_user_id: userId,
        brand_id: brandId,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : null,
        last_input_at: new Date().toISOString(),
      }).select('id').single();
      if (cancelled || error) return;
      sessionIdRef.current = data.id;
    })();

    // Mouse/keyboard activity → update lastInputRef. Passive listeners, very cheap.
    const markActive = () => { lastInputRef.current = Date.now(); };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(ev => window.addEventListener(ev, markActive, { passive: true }));

    // Heartbeat every 30s.
    //   - Idle (no input in 5 min): just bump last_active_at to keep session alive
    //   - Active: atomic RPC increments active_seconds and touches last_input_at
    // RPC avoids read-modify-write races when an operator has multiple tabs open.
    heartbeatRef.current = setInterval(async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (!sessionIdRef.current) return;
      const idle = Date.now() - lastInputRef.current > IDLE_THRESHOLD_MS;
      if (idle) {
        await supabase.from('operator_sessions')
          .update({ last_active_at: new Date().toISOString(), brand_id: brandId })
          .eq('id', sessionIdRef.current);
      } else {
        await supabase.rpc('bump_operator_active_seconds', {
          p_session_id: sessionIdRef.current,
          p_secs: Math.round(HEARTBEAT_MS / 1000),
          p_brand_id: brandId || null,
        });
      }
    }, HEARTBEAT_MS);

    return () => {
      cancelled = true;
      events.forEach(ev => window.removeEventListener(ev, markActive));
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  // eslint-disable-next-line
  }, [userId]); // re-establish session if userId changes (rare)

  // Update session's brand_id when active brand switches (cheap update).
  useEffect(() => {
    if (!sessionIdRef.current || !brandId) return;
    supabase.from('operator_sessions').update({ brand_id: brandId }).eq('id', sessionIdRef.current);
  }, [brandId]);

  // Lightweight action logger. Call from handlers that mutate operator-visible
  // state we want to audit: status changes, flow edits, send_mode flips,
  // brand creation, integration toggles, etc. Best-effort, fire-and-forget.
  const logOperatorAction = (action, opts = {}) => {
    if (!userId || userId === 'anonymous') return;
    supabase.from('operator_actions').insert({
      clerk_user_id: userId,
      brand_id: opts.brandId ?? brandId,
      action, // string like 'creator.status_change'
      target_type: opts.targetType || null,
      target_id: opts.targetId || null,
      details: opts.details || {},
    }).then(({ error }) => { if (error) console.error('action log failed', error); });
  };

  // Per-brand operator notifications (cap reached, reply rate dropped, etc.).
  // Polled every 60s; surfaces as a Dashboard banner + dismissable per item.
  const [brandNotifications, setBrandNotifications] = useState([]);
  // Integration-health count surfaces on the Settings nav badge so the operator
  // sees "something needs attention" without having to open Settings first.
  // Counts only unacknowledged webhook_failures + scopes to current brand.
  const [settingsAlertCount, setSettingsAlertCount] = useState(0);
  const loadBrandNotifications = async () => {
    if (!brandId) return;
    const { data } = await supabase.from('brand_notifications')
      .select('*').eq('brand_id', brandId)
      .is('dismissed_at', null)
      .order('created_at', { ascending: false }).limit(300);
    setBrandNotifications(data || []);
    // Unacked webhook failures for the badge. Includes NULL brand_id rows
    // (e.g. pre-bind auth failures) so global webhook issues still surface.
    const { count } = await supabase.from('webhook_failures')
      .select('*', { count: 'exact', head: true })
      .is('acknowledged_at', null)
      .or(`brand_id.eq.${brandId},brand_id.is.null`);
    setSettingsAlertCount(count || 0);
  };
  useEffect(() => {
    if (!brandId) { setBrandNotifications([]); setSettingsAlertCount(0); return; }
    loadBrandNotifications();
    const t = setInterval(loadBrandNotifications, 60000);
    return () => clearInterval(t);
  // eslint-disable-next-line
  }, [brandId]);
  // Re-fetch the badge count when operator navigates away from Settings (might
  // have acknowledged items while there). Cheap query, head:true, count-only.
  useEffect(() => {
    if (active !== 'settings' && brandId) loadBrandNotifications();
  // eslint-disable-next-line
  }, [active]);
  const dismissBrandNotification = async (id) => {
    const { error } = await supabase.from('brand_notifications').update({ dismissed_at: new Date().toISOString() }).eq('id', id);
    if (error) { toast(`Couldn't dismiss: ${error.message}`, 'error'); return; }
    setBrandNotifications(prev => prev.filter(n => n.id !== id));
  };

  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.from('activity_log')
        .select('*').eq('brand_id', brandId).order('ts', { ascending: false }).limit(500);
      if (cancelled) return;
      if (error) { console.error('activity load', error); return; }
      // Map DB columns -> existing in-memory shape (creator_handle -> handle, ...extra spread)
      const mapped = (data || []).map(r => ({
        id: r.id, handle: r.creator_handle, type: r.type, text: r.text, ts: r.ts, ...(r.extra || {}),
      }));
      // One-time migration from localStorage if Supabase is empty
      if (mapped.length === 0) {
        const lsKey = `ios_activity_${bk}`;
        try {
          const s = localStorage.getItem(lsKey);
          const parsed = s ? JSON.parse(s) : null;
          if (Array.isArray(parsed) && parsed.length) {
            const rows = parsed.slice(0, 500).map(e => {
              const { id, handle, type: t, text: tx, ts, ...rest } = e;
              return {
                id: (typeof crypto!=='undefined' && crypto.randomUUID) ? crypto.randomUUID() : undefined,
                brand_id: brandId, creator_handle: handle, type: t, text: tx,
                extra: rest, ts: ts || new Date().toISOString(),
              };
            });
            const { data: inserted, error: e2 } = await supabase.from('activity_log').insert(rows).select();
            if (!e2 && inserted) {
              try { localStorage.removeItem(lsKey); } catch {}
              const reMapped = inserted
                .sort((a,b) => new Date(b.ts) - new Date(a.ts))
                .map(r => ({ id: r.id, handle: r.creator_handle, type: r.type, text: r.text, ts: r.ts, ...(r.extra || {}) }));
              setActivityLog(reMapped);
              return;
            }
          }
        } catch {}
      }
      setActivityLog(mapped);
    })();
    return () => { cancelled = true; };
  }, [brandId, bk]);

  // Poll for server-written activity (gmail-reply-webhook, email-worker) so
  // inbound replies show up without a manual page refresh. Full snapshot
  // replace, NOT an incremental merge: optimistic rows from logActivity use a
  // different id than the authoritative row when _optimisticOnly (e.g.
  // send-creator-email), so a merge would duplicate the timeline — a replace
  // converges on server truth instead. Skips hidden tabs.
  useEffect(() => {
    if (!brandId) return;
    const tick = async () => {
      if (document.hidden) return;
      const { data, error } = await supabase.from('activity_log')
        .select('*').eq('brand_id', brandId).order('ts', { ascending: false }).limit(500);
      if (error || !data) return;
      setActivityLog(data.map(r => ({
        id: r.id, handle: r.creator_handle, type: r.type, text: r.text, ts: r.ts, ...(r.extra || {}),
      })));
    };
    const t = setInterval(tick, 20000);
    return () => clearInterval(t);
  }, [brandId]);

  const logActivity = (handle, type, text, extra = {}) => {
    if (!brandId) return;
    const id = (typeof crypto!=='undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());
    const ts = new Date().toISOString();
    // _optimisticOnly: a server function already inserted the authoritative row
    // (e.g. send-creator-email). We only prepend locally for instant feedback and
    // skip the DB insert so the timeline doesn't get a duplicate.
    const { _optimisticOnly, ...cleanExtra } = extra;
    // Optimistic local prepend
    setActivityLog(prev => [{ id, handle, type, text, ts, ...cleanExtra }, ...prev].slice(0, 500));
    if (_optimisticOnly) return;
    // Fire-and-forget insert
    supabase.from('activity_log').insert({
      id, brand_id: brandId, creator_handle: handle, type, text, extra: cleanExtra, ts,
    }).then(({ error }) => { if (error) console.error('activity insert', error); });
  };

  const handleRemoveCreator=(creator)=>{
    persistCreatorPatch(creator, {archived: true});
    logActivity(creator.handle,"archive","Archived");
    setCreatorProfile(null);
    toast(`${creator.name} archived`,"info");
  };

  // Hard delete — the opposite of archive. Removes the creator AND every trace of
  // them (flow assignments, queued/sent emails, activity, board cards) so nothing
  // orphans or re-fires. Brand-scoped (never touches another brand's creator) and
  // gated behind an explicit "Delete forever" confirm in the UI. Creator row goes
  // first — if RLS blocks that, we bail before sweeping children so we never
  // orphan them.
  const handleDeleteCreator=async(creator)=>{
    if(!brandId||!creator?.handle){toast("Can't delete — brand not loaded","error");return;}
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-creator`,{
        method:'POST',
        headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},
        body:JSON.stringify({brand_id:brandId,creator_handle:creator.handle}),
      });
      const result=await resp.json().catch(()=>({}));
      if(!resp.ok||!result.ok){toast(`Delete failed: ${result.message||result.error||resp.status}`,"error");return;}
      setAllCreators(cs=>cs.filter(c=>c.handle!==creator.handle));
      setActivityLog(prev=>prev.filter(e=>e.handle!==creator.handle));
      setCreatorProfile(null);
      toast(`${creator.name} permanently deleted`,"info");
    }catch(e){console.error('handleDeleteCreator',e);toast(`Delete failed: ${e.message||'network error'}`,"error");}
  };

  const handleUpdateCreator=(creator,fields)=>{
    persistCreatorPatch(creator, fields);
    setCreatorProfile(p=>p?{...p,...fields}:p);
    const labels={order:"Order #",discountCode:"Discount code",email:"Email",address:"Shipping address",instagram:"Instagram",tiktok:"TikTok",youtube:"YouTube"};
    Object.keys(fields).forEach(k=>{
      if(k==="notes") logActivity(creator.handle,"note","Note updated");
      else if(labels[k]) logActivity(creator.handle,"edit",`${labels[k]} → ${fields[k]}`);
    });
  };

  // Bring a creator's flow assignment in line with their current status. Idempotent —
  // safe to call repeatedly. Used from any status-change path (profile, kanban, list).
  const reconcileFlowAssignment=async(handle,status)=>{
    if(!brandId||!handle)return;
    const desired=pickFlowForStatus(status,flows,handle);
    const desiredFlowId=desired?.id||null;
    try{
      // Look at ALL live assignments (active + paused), not just active. A positive
      // reply smart-pauses the assignment while status often stays flowable; if we
      // only checked 'active' we'd treat a paused creator as un-enrolled and insert
      // a duplicate — re-sending the cold step 0 to someone who already replied.
      // (Same bug class as the worker's assignMissingFlows.) If a live assignment
      // already exists on the desired flow, leave it — paused ones auto-resume.
      const{data:live}=await supabase.from('flow_assignments')
        .select('id, flow_id, status').eq('brand_id',brandId).eq('creator_handle',handle).in('status',['active','paused']);
      if((live||[]).some(a=>a.flow_id===desiredFlowId))return;
      const current=(live||[]).find(a=>a.status==='active');
      if(current){
        await supabase.from('flow_assignments').update({status:'completed',completed_at:new Date().toISOString()}).eq('id',current.id);
      }
      if(desiredFlowId){
        await supabase.from('flow_assignments').insert({brand_id:brandId,creator_handle:handle,flow_id:desiredFlowId,status:'active'});
      }
    }catch(e){console.error('reconcileFlowAssignment',e);}
  };

  const handleStatusChange=(creator,newStatus)=>{
    persistCreatorPatch(creator, {status: newStatus});
    setCreatorProfile(p=>p?{...p,status:newStatus}:p);
    logActivity(creator.handle,"status",`Status → ${newStatus}`);
    logOperatorAction('creator.status_change', {targetType:'creator', targetId:creator.handle, details:{from:creator.status,to:newStatus,name:creator.name}});
    const newStage=STATUS_TO_STAGE[newStatus];
    if(newStage){
      setKanbanData(prev=>{
        const updated={...prev};
        Object.keys(updated).forEach(campId=>{
          updated[campId]=(updated[campId]||[]).map(c=>c.name===creator.name?{...c,stage:newStage}:c);
        });
        return updated;
      });
    }
    reconcileFlowAssignment(creator.handle,newStatus);
    toast(`Status → ${newStatus}`);
  };
  // Sync button removed — was theatre (setTimeout that toasted "Synced successfully" without
  // doing anything real). Data already loads from Supabase on mount + auto-refreshes via
  // intervals (notifications/health) + RLS reflects DB state instantly. Nothing to "sync."

  // Compute merged creator list on-the-fly — never writes duplicates to localStorage
  const COLORS_SEED=["#E879F9","#60A5FA","#34D399","#FBBF24","#F87171","#818CF8","#06B6D4","#F59E0B"];
  const mergedCreators=useMemo(()=>{
    // First deduplicate allCreators itself (handle localStorage dupes)
    const seen=new Set();
    const deduped=allCreators.filter(c=>{
      const key=(c.handle||c.name).toLowerCase().replace(/^@/,"");
      if(seen.has(key))return false;
      seen.add(key);return true;
    });
    // Then add kanban-only creators not already present
    const names=new Set(deduped.map(c=>c.name.toLowerCase()));
    const handles=new Set(deduped.map(c=>(c.handle||"").toLowerCase()));
    const extras=[];
    let idx=0;
    Object.values(kanbanData).forEach(creators=>{
      (creators||[]).forEach(c=>{
        const n=c.name.toLowerCase();
        const h=(c.handle||"").toLowerCase().replace(/^@/,"");
        if(!names.has(n)&&!handles.has(h)){
          names.add(n); handles.add(h);
          extras.push({name:c.name,handle:h||n.replace(/\s/g,"_"),followersF:c.followers||"—",followers:parseFollowers(c.followers||0),eng:"—",engN:0,status:STAGE_TO_STATUS[c.stage]||"Outreached",market:c.market||"AU",pocket:"—",color:c.color||COLORS_SEED[idx++%COLORS_SEED.length],id:`k_${h}`,_kanbanOnly:true});
        }
      });
    });
    return [...deduped,...extras];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[allCreators,kanbanData]);
  // Public agreement signing route — /sign/:token. Renders BEFORE the Clerk
  // SignedIn/SignedOut gate because the signer (creator) doesn't have a kolabs
  // account. Token-based auth via SECURITY DEFINER RPCs (get_agreement_for_signing
  // + submit_creator_signature) — bypasses RLS safely because the token IS the
  // auth credential.
  const publicSignMatch = (typeof window !== 'undefined') && window.location.pathname.match(/^\/sign\/([A-Za-z0-9_-]{16,})$/);
  if (publicSignMatch) {
    return <PublicSignPage token={publicSignMatch[1]} />;
  }

  // A merchant (or App-Review reviewer) who just finished a Shopify install with
  // no brand context lands here logged-out. Show a clean "connected" confirmation
  // above the sign-in form so the destination is functional + on-brand.
  const _sp = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const shopifyInstalledPending = _sp.get('shopify') === 'installed_pending';
  const shopifyInstalledShop = (shopifyInstalledPending ? (_sp.get('shop') || '') : '').replace(/[<>"']/g, '');

  return(<>
    <SignedOut>
      {/* Split-layout sign-in: brand panel left, themed Clerk form right.
          flexWrap: wrap-reverse → on narrow screens the form stacks ABOVE the
          brand panel (action-first on mobile, brand-first on desktop). */}
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
      <div style={{display:"flex",minHeight:"100vh",flexWrap:"wrap-reverse",fontFamily:"'DM Sans',system-ui,sans-serif"}}>

        {/* LEFT — brand panel */}
        <div style={{flex:"1 1 500px",minHeight:380,background:"linear-gradient(135deg,#0C0D0E 0%,#1a1d22 55%,#0a3424 100%)",color:"#fff",padding:"56px 56px 48px",display:"flex",flexDirection:"column",justifyContent:"space-between",position:"relative",overflow:"hidden"}}>
          {/* Decorative gradient orb — subtle visual interest without imagery. */}
          <div style={{position:"absolute",top:-120,right:-120,width:440,height:440,borderRadius:"50%",background:"radial-gradient(circle,rgba(0,201,122,0.18),transparent 70%)",pointerEvents:"none"}}/>
          <div style={{position:"absolute",bottom:-80,left:-80,width:300,height:300,borderRadius:"50%",background:"radial-gradient(circle,rgba(0,201,122,0.07),transparent 65%)",pointerEvents:"none"}}/>

          {/* Logo */}
          <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:11}}>
            <div style={{width:40,height:40,borderRadius:11,background:"#00c97a",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:22,color:"#0C0D0E",letterSpacing:"-.04em"}}>k</div>
            <div style={{fontWeight:800,fontSize:19,letterSpacing:"-.02em"}}>kolabs</div>
          </div>

          {/* Hero copy */}
          <div style={{position:"relative",zIndex:1}}>
            <div style={{fontSize:38,fontWeight:800,lineHeight:1.12,letterSpacing:"-.03em",marginBottom:18,maxWidth:520}}>
              The operating system that runs KoLabs.
            </div>
            <div style={{fontSize:15,color:"rgba(255,255,255,0.62)",lineHeight:1.6,marginBottom:38,maxWidth:460}}>
              Built in-house, for our team only — the system we use to run every brand in the KoLabs portfolio end-to-end, from cold outreach to posted content. Not a product, not for sale.
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {[
                {icon:"✉️",text:"Full email outreach pipeline — replies auto-classified, orders auto-shipped"},
                {icon:"🛒",text:"Shopify + Gmail + Drive sync, end-to-end with bounce + warmup safety nets"},
                {icon:"📊",text:"Statistical A/B testing, AI reply suggestions, predictive cap warnings"},
              ].map((b,i)=>(
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:12}}>
                  <div style={{fontSize:17,lineHeight:1,marginTop:2,flexShrink:0}}>{b.icon}</div>
                  <div style={{fontSize:13.5,color:"rgba(255,255,255,0.86)",lineHeight:1.5}}>{b.text}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer note */}
          <div style={{position:"relative",zIndex:1,display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11,color:"rgba(255,255,255,0.35)",letterSpacing:".02em"}}>
            <div>© 2026 kolabs.agency</div>
            <div>built for operators, not founders</div>
          </div>
        </div>

        {/* RIGHT — sign-in panel. Soft cream background with a centered white
            card that holds the form. Card gives the form visual presence + makes
            the dark form-button-row blend in instead of looking like a bleed. */}
        <div style={{flex:"1 1 460px",display:"flex",alignItems:"center",justifyContent:"center",background:"#f7f8f7",padding:"48px 32px",position:"relative",overflow:"hidden"}}>
          {/* Subtle background decoration — two soft green orbs in the corners to
              mirror the brand panel's decoration without competing for attention. */}
          <div style={{position:"absolute",top:-100,right:-100,width:280,height:280,borderRadius:"50%",background:"radial-gradient(circle,rgba(0,201,122,0.07),transparent 70%)",pointerEvents:"none"}}/>
          <div style={{position:"absolute",bottom:-120,left:-120,width:320,height:320,borderRadius:"50%",background:"radial-gradient(circle,rgba(12,13,14,0.04),transparent 70%)",pointerEvents:"none"}}/>

          <div style={{width:"100%",maxWidth:420,position:"relative",zIndex:1}}>
            {/* Header above the card — sets context without crowding the form. */}
            {shopifyInstalledPending ? (
              <div style={{marginBottom:24}}>
                <div style={{background:"#ECFDF5",border:"1px solid #A7F3D0",borderRadius:12,padding:"16px 18px",textAlign:"center"}}>
                  <div style={{width:40,height:40,borderRadius:"50%",background:"#0b3d2e",color:"#bff0d8",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,margin:"0 auto 10px"}}>✓</div>
                  <div style={{fontWeight:800,fontSize:17,color:"#065F46",marginBottom:4}}>Store connected</div>
                  <div style={{fontSize:13,color:"#047857",lineHeight:1.5}}>{shopifyInstalledShop?<><strong>{shopifyInstalledShop}</strong> is </>:"Your store is "}connected to KoLabs. Your account manager will link it to your brand and finish setup — no further action needed.</div>
                </div>
              </div>
            ) : (
              <div style={{marginBottom:24,textAlign:"center"}}>
                <div style={{fontWeight:800,fontSize:24,color:"#0C0D0E",marginBottom:6,letterSpacing:"-.025em"}}>Welcome back</div>
                <div style={{fontSize:13.5,color:"#6B7280"}}>Sign in to your operations cockpit</div>
              </div>
            )}

            {/* White card with shadow — gives the form weight on the cream bg
                and ensures the Clerk footer area visibly belongs to the form. */}
            <div style={{background:"#fff",borderRadius:16,padding:"32px 28px 24px",boxShadow:"0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(12,13,14,0.06)",border:"1px solid rgba(0,0,0,0.04)"}}>
              <SignIn
                routing="hash"
                appearance={{
                  variables:{
                    colorPrimary:"#00c97a",
                    colorText:"#0C0D0E",
                    colorTextSecondary:"#6B7280",
                    colorBackground:"#ffffff",
                    colorInputBackground:"#ffffff",
                    colorInputText:"#0C0D0E",
                    fontFamily:"'DM Sans',system-ui,sans-serif",
                    borderRadius:"10px",
                    spacingUnit:"1rem",
                  },
                  elements:{
                    rootBox:{width:"100%"},
                    cardBox:{boxShadow:"none",border:"none",background:"#fff",padding:0,margin:0,width:"100%"},
                    card:{boxShadow:"none",border:"none",background:"#fff",padding:0,margin:0,width:"100%"},
                    main:{background:"#fff",gap:"16px"},
                    header:{display:"none"},
                    headerTitle:{display:"none"},
                    headerSubtitle:{display:"none"},

                    // Social buttons (Google, etc.)
                    socialButtonsBlockButton:{
                      border:"1px solid #E5E7EB",
                      borderRadius:"10px",
                      fontWeight:600,
                      fontSize:"13.5px",
                      padding:"11px 14px",
                      color:"#0C0D0E",
                      background:"#fff",
                      boxShadow:"none",
                      "&:hover":{background:"#f4f6f5",borderColor:"#d1d5db",boxShadow:"none"},
                      "&:focus":{boxShadow:"0 0 0 3px rgba(0,201,122,0.18)"},
                    },
                    socialButtonsBlockButtonText:{fontWeight:600,fontSize:"13.5px"},
                    socialButtonsProviderIcon:{width:"17px",height:"17px"},

                    // Divider between social + email
                    divider:{margin:"16px 0"},
                    dividerLine:{background:"#E5E7EB",height:"1px"},
                    dividerText:{color:"#9CA3AF",fontSize:"11px",letterSpacing:".06em",textTransform:"uppercase",fontWeight:600,padding:"0 12px"},

                    // Form fields
                    formField:{marginBottom:"4px"},
                    formFieldLabel:{fontSize:"12.5px",fontWeight:600,color:"#0C0D0E",marginBottom:"6px"},
                    formFieldInput:{
                      border:"1px solid #E5E7EB",
                      borderRadius:"10px",
                      padding:"11px 13px",
                      fontSize:"14px",
                      background:"#fff",
                      color:"#0C0D0E",
                      boxShadow:"none",
                      transition:"all .15s",
                      "&:focus":{borderColor:"#00c97a",boxShadow:"0 0 0 3px rgba(0,201,122,0.18)",outline:"none"},
                      "&:hover":{borderColor:"#d1d5db"},
                    },
                    formFieldInputShowPasswordButton:{color:"#8B9299","&:hover":{color:"#0C0D0E"}},
                    formFieldAction:{color:"#00c97a",fontSize:"12px",fontWeight:600,"&:hover":{color:"#0C0D0E"}},
                    formFieldLabelRow:{justifyContent:"space-between"},

                    // Primary CTA — soft green (more inviting than the previous dark)
                    formButtonRow:{margin:"4px 0 0",padding:0,background:"#fff"},
                    formButtonPrimary:{
                      background:"#00c97a",
                      color:"#0C0D0E",
                      fontWeight:700,
                      textTransform:"none",
                      fontSize:"14px",
                      padding:"12px 16px",
                      borderRadius:"10px",
                      boxShadow:"0 1px 0 rgba(0,0,0,0.04)",
                      border:"none",
                      "&:hover":{background:"#00b56d",boxShadow:"0 1px 2px rgba(0,201,122,0.4)"},
                      "&:focus":{boxShadow:"0 0 0 3px rgba(0,201,122,0.3)"},
                      "&:active":{background:"#00a361"},
                    },

                    // Footer — explicit white background to KILL the dark bleed
                    // that the screenshot showed. Everything below the form button
                    // (Sign up link + Clerk branding) lives in here.
                    footer:{background:"#fff",margin:"16px 0 0",padding:"12px 0 0",borderTop:"1px solid #f0f2f0"},
                    footerAction:{background:"#fff",padding:0,margin:0,justifyContent:"center"},
                    footerActionText:{fontSize:"13px",color:"#6B7280"},
                    footerActionLink:{color:"#00c97a",fontWeight:700,marginLeft:"4px","&:hover":{color:"#0C0D0E"}},
                    // Hide footerPages entirely — this section contains Privacy/Terms
                    // links AND the "Secured by Clerk" badge. On Pro tier this is
                    // allowed. "Don't have an account? Sign up" lives in `footerAction`
                    // (separate element), so that stays visible.
                    footerPages:{display:"none"},
                    footerPagesLink:{display:"none"},
                    // Belt-and-suspenders for the Clerk branding badge — version-stable
                    // hide via multiple known element names. Pro tier permits this.
                    logoBox:{display:"none"},
                    badge:{display:"none"},
                    branding:{display:"none"},

                    // Identity preview (when you've signed in before)
                    identityPreview:{background:"#f8faf9",border:"1px solid #E5E7EB",borderRadius:"10px",padding:"10px 12px"},
                    identityPreviewText:{fontSize:"13px",color:"#0C0D0E"},
                    identityPreviewEditButton:{color:"#00c97a","&:hover":{color:"#0C0D0E"}},
                    formResendCodeLink:{color:"#00c97a",fontWeight:600},

                    // Alerts (error messages)
                    alert:{borderRadius:"10px",fontSize:"13px"},
                    alertText:{fontSize:"13px"},
                  },
                  layout:{
                    socialButtonsPlacement:"top",
                    socialButtonsVariant:"blockButton",
                    showOptionalFields:false,
                    logoPlacement:"none",
                  },
                }}
              />
            </div>

            {/* Small footnote below card — gentle reassurance */}
            <div style={{textAlign:"center",fontSize:11.5,color:"#9CA3AF",marginTop:18,letterSpacing:".01em"}}>
              Trouble signing in? Contact your kolabs admin.
            </div>

            {/* Legal links — required to be visible from the app for Google OAuth
                verification (privacy policy must be linked from the app itself).
                Points to the single source of truth on the marketing site. */}
            <div style={{textAlign:"center",fontSize:11.5,marginTop:10,letterSpacing:".01em"}}>
              <a href="https://kolabs.agency/privacy" target="_blank" rel="noopener noreferrer" style={{color:"#9CA3AF",textDecoration:"none"}}>Privacy</a>
              <span style={{color:"#D1D5DB",margin:"0 8px"}}>·</span>
              <a href="https://kolabs.agency/terms" target="_blank" rel="noopener noreferrer" style={{color:"#9CA3AF",textDecoration:"none"}}>Terms</a>
              <span style={{color:"#D1D5DB",margin:"0 8px"}}>·</span>
              <a href="https://kolabs.agency/data-deletion" target="_blank" rel="noopener noreferrer" style={{color:"#9CA3AF",textDecoration:"none"}}>Data Deletion</a>
            </div>
          </div>
        </div>
      </div>
    </SignedOut>
    <SignedIn>
    <div style={{display:"flex",height:"100vh",background:PAGE_BG,fontFamily:"'DM Sans',sans-serif",fontSize:14,color:DARK}} onClick={e=>{if(showUserMenu&&!e.target.closest('[data-usermenu]'))setShowUserMenu(false);}}>
    <ToastContainer/>
    <GlobalConfirmHost/>
    <GlobalPromptHost/>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet"/>
    <style>{"*{box-sizing:border-box}::-webkit-scrollbar{width:4px;height:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.15);border-radius:4px}button{font-family:'DM Sans',sans-serif}input,textarea,select{font-family:'DM Sans',sans-serif}@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}"}</style>
    <div style={{width:sidebarOpen?228:52,background:inAgencyMode?"#0a0f0d":"#0C0D0E",flexShrink:0,display:"flex",flexDirection:"column",transition:"width .22s cubic-bezier(.4,0,.2,1)",overflow:"hidden",borderRight:inAgencyMode?`1px solid ${G}30`:"1px solid rgba(255,255,255,0.05)"}}>
      <div style={{padding:sidebarOpen?"20px 14px 16px":"20px 10px 16px",borderBottom:inAgencyMode?`1px solid ${G}20`:"1px solid rgba(255,255,255,0.06)",display:"flex",alignItems:"center",gap:11}}>
        {(() => {
          const brandLogo = !inAgencyMode ? (brandData?.[activeBrand]?.logo) : null;
          if (brandLogo) {
            return <div style={{width:36,height:36,borderRadius:9,overflow:"hidden",background:"#fff",border:"1px solid rgba(255,255,255,0.12)",flexShrink:0,boxShadow:"0 1px 0 rgba(255,255,255,0.04) inset"}}>
              <img src={brandLogo} alt={activeBrand} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            </div>;
          }
          // Fallback: branded cube. Agency mode = white-on-green AG; brand mode = green IO.
          return <div style={{width:36,height:36,background:inAgencyMode?"linear-gradient(135deg,#fff,#e8fff5)":`linear-gradient(135deg,${G},#00a862)`,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:inAgencyMode?`0 0 0 1px ${G}30, 0 2px 8px ${G}25`:"0 2px 6px rgba(0,201,122,0.3)"}}>
            <span style={{color:inAgencyMode?G:"#000",fontWeight:900,fontSize:13,letterSpacing:"-.05em"}}>{inAgencyMode?"AG":"IO"}</span>
          </div>;
        })()}
        {sidebarOpen&&<div style={{minWidth:0,flex:1}}>
          <div style={{color:"#fff",fontWeight:700,fontSize:14,letterSpacing:"-.01em",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{inAgencyMode?"kolabs · Agency":activeBrand}</div>
          <div style={{color:"rgba(255,255,255,0.4)",fontSize:10,marginTop:2,textTransform:"uppercase",letterSpacing:".09em",fontWeight:600}}>{inAgencyMode?"Agency workspace":"Influencer OS"}</div>
        </div>}
      </div>
      <nav style={{flex:1,padding:"8px 5px",display:"flex",flexDirection:"column",gap:1,overflowY:"auto"}}>
        {(inAgencyMode?AGENCY_NAV:NAV).map(item=>{
          const isActive=active===item.id;
          // Per-item alert badge — currently only Settings surfaces unacked
          // webhook failures. Easy to extend (e.g. Creators "needs review" count)
          // by checking item.id and feeding the relevant count.
          const alertCount = item.id==='settings' ? settingsAlertCount : 0;
          return(<button key={item.id} onClick={()=>{setActive(item.id);setCreatorProfile(null);}} style={{display:"flex",alignItems:"center",gap:8,padding:sidebarOpen?"7px 9px":"7px",borderRadius:6,border:"none",cursor:"pointer",textAlign:"left",background:isActive?(inAgencyMode?G+"22":"rgba(255,255,255,0.09)"):"transparent",color:isActive?"#fff":"rgba(255,255,255,0.3)",fontFamily:"inherit",fontSize:12.5,fontWeight:isActive?500:400,transition:"all .1s",width:"100%",position:"relative"}} onMouseEnter={e=>{if(!isActive){e.currentTarget.style.background="rgba(255,255,255,0.05)";e.currentTarget.style.color="rgba(255,255,255,0.55)";}}} onMouseLeave={e=>{if(!isActive){e.currentTarget.style.background="transparent";e.currentTarget.style.color="rgba(255,255,255,0.3)";}}}>
            <div style={{position:"relative",flexShrink:0}}>
              <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={isActive?2.2:1.8} strokeLinecap="round" strokeLinejoin="round" style={{display:"block"}}>{item.icon.split(" M").map((d,i)=><path key={i} d={i===0?d:"M"+d}/>)}</svg>
              {/* Collapsed-sidebar badge: pulsing red dot, no number. The full
                  count shows once the sidebar is open + the label is visible. */}
              {alertCount>0&&!sidebarOpen&&<div style={{position:"absolute",top:-3,right:-3,width:8,height:8,borderRadius:"50%",background:"#EF4444",border:"1.5px solid #1a1d1f"}}/>}
            </div>
            {sidebarOpen&&<span style={{whiteSpace:"nowrap",flex:1}}>{item.label}</span>}
            {alertCount>0&&sidebarOpen&&<span style={{background:"#EF4444",color:"#fff",borderRadius:10,padding:"1px 7px",fontSize:10,fontWeight:800,flexShrink:0,minWidth:18,textAlign:"center"}}>{alertCount}</span>}
            {isActive&&sidebarOpen&&alertCount===0&&<div style={{width:4,height:4,borderRadius:"50%",background:G,flexShrink:0}}/>}
          </button>);
        })}
      </nav>
      {/* Workspace switch — visible only to agency owners. Brand mode shows
          "Agency →" + brand switcher. Agency mode shows "← Back to brand work"
          and hides the brand switcher (the user isn't picking a brand here). */}
      {inAgencyMode
        ? isAgencyOwner && <button onClick={()=>{setActive('dashboard');setCreatorProfile(null);}}
            style={{margin:"4px 8px 10px",padding:"9px 10px",borderRadius:7,border:`1px solid ${G}40`,background:G+"10",color:G,cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontFamily:"inherit",fontSize:11.5,fontWeight:700}}>
            <span style={{fontSize:13}}>←</span> {sidebarOpen && "Back to brand work"}
          </button>
        : <>
            {sidebarOpen && <BrandSwitcher brandsList={brandsList} brandId={brandId} onSwitch={switchBrand} onAdd={addBrand} brandLogos={Object.fromEntries(Object.entries(brandData||{}).map(([k,v])=>[k,v?.logo||null]))}/>}
            {isAgencyOwner && <button onClick={()=>{setActive('agency-dashboard');setCreatorProfile(null);}}
              style={{margin:"4px 8px 10px",padding:"9px 10px",borderRadius:7,border:"1px solid rgba(255,255,255,0.1)",background:"rgba(255,255,255,0.04)",color:"rgba(255,255,255,0.65)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,fontFamily:"inherit",fontSize:11.5,fontWeight:600}}
              onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,0.08)";e.currentTarget.style.color="#fff";}}
              onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,0.04)";e.currentTarget.style.color="rgba(255,255,255,0.65)";}}>
              {sidebarOpen ? <><span>Agency workspace</span><span>→</span></> : <span style={{margin:"0 auto"}}>→</span>}
            </button>}
          </>}
      <button onClick={()=>setSidebarOpen(!sidebarOpen)} style={{padding:"11px 0",border:"none",background:"transparent",color:"rgba(255,255,255,0.18)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",borderTop:"1px solid rgba(255,255,255,0.05)"}} onMouseEnter={e=>e.currentTarget.style.color="rgba(255,255,255,0.45)"} onMouseLeave={e=>e.currentTarget.style.color="rgba(255,255,255,0.18)"}><svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d={sidebarOpen?"M15 19l-7-7 7-7":"M9 5l7 7-7 7"}/></svg></button>
    </div>
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0}}>
      <div style={{background:"#fff",borderBottom:`1px solid ${BORDER}`,padding:"0 22px",height:52,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
        <div style={{fontWeight:600,fontSize:14,color:DARK}}>{creatorProfile?creatorProfile.name:(NAV.find(n=>n.id===active)?.label || AGENCY_NAV.find(n=>n.id===active)?.label)}</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{position:"relative"}} data-usermenu="true">
            <button onClick={()=>setShowUserMenu(!showUserMenu)} style={{width:30,height:30,borderRadius:"50%",border:`1.5px solid ${BORDER}`,cursor:"pointer",padding:0,overflow:"hidden"}}>
              {userProfile.avatar?<img src={userProfile.avatar} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<div style={{width:"100%",height:"100%",background:`linear-gradient(135deg,${G},#00a862)`,display:"flex",alignItems:"center",justifyContent:"center",color:"#000",fontWeight:800,fontSize:10}}>{userProfile.firstName[0]}{userProfile.lastName[0]}</div>}
            </button>
            {showUserMenu&&(<UserProfileMenu user={user} userProfile={userProfile} setUserProfile={setUserProfile} onClose={()=>setShowUserMenu(false)}/>)}
          </div>
        </div>
      </div>
      <div style={{flex:1,overflow:"auto",padding:"20px 22px"}}>
        {/* No-access empty state — replaces the auto-bootstrap "New Brand"
            anti-pattern. Fires for a signed-in user who has zero brand
            memberships (brand-mode + brandsList empty + not an agency owner).
            This is what an invited teammate sees in the brief window before
            their invite auto-claims (or permanently, if no one invited them
            yet). Tells them what to do instead of showing a blank dashboard. */}
        {!brandId && !inAgencyMode && brandsList.length === 0 ? (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"75vh"}}>
            <Card style={{padding:36,maxWidth:520,textAlign:"center"}}>
              <div style={{width:56,height:56,borderRadius:"50%",background:"#F3F4F6",color:MUTED,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 18px",fontSize:24}}>🔒</div>
              <div style={{fontWeight:700,fontSize:18,color:DARK,marginBottom:8}}>You don't have access to any brand yet</div>
              <div style={{fontSize:13,color:MUTED,lineHeight:1.6,marginBottom:18}}>
                Your account isn't linked to a brand workspace yet. If you were invited by your agency owner, the invite should auto-claim within a few seconds — try refreshing the page.<br/><br/>
                If you weren't expecting access, ask the person who runs the agency to send you an invite from <strong style={{color:DARK}}>Settings → Team</strong>.
              </div>
              <button onClick={()=>window.location.reload()} style={{background:DARK,color:G,border:"none",borderRadius:9,padding:"10px 22px",fontWeight:700,fontSize:13,cursor:"pointer"}}>↻ Refresh</button>
            </Card>
          </div>
        ) :
        creatorProfile?<CreatorProfile creator={creatorProfile} onBack={()=>setCreatorProfile(null)} onRemove={()=>handleRemoveCreator(creatorProfile)} onDelete={()=>handleDeleteCreator(creatorProfile)} onStatusChange={(s)=>handleStatusChange(creatorProfile,s)} onUpdate={(fields)=>handleUpdateCreator(creatorProfile,fields)} kanbanData={kanbanData} agreements={agreements} activityLog={activityLog.filter(e=>e.handle===creatorProfile.handle)} flows={flows} onLogActivity={(type,text,extra)=>logActivity(creatorProfile.handle,type,text,extra)} brand={brand} campaigns={campaigns} brandId={brandId} allCreators={mergedCreators}/>:
          active==="setup"?<OnboardingWizard brand={brand} activeBrand={activeBrand} brandId={brandId} flows={flows} onNavigate={setActive}/>:
          active==="finder"?<Finder/>:
          active==="dashboard"?<Dashboard allCreators={mergedCreators} kanbanData={kanbanData} flows={flows} activityLog={activityLog} onNavigate={navigateWithFilter} brand={brand} integrations={brand?.integrations||{}} brandId={brandId} onSelectCreator={setCreatorProfile} campaigns={campaigns} brandNotifications={brandNotifications} onDismissNotification={dismissBrandNotification}/>:
          active==="creators"?<Creators key={bk} onSelect={setCreatorProfile} allCreators={mergedCreators} setAllCreators={setAllCreators} onStatusChange={(creator,status)=>handleStatusChange(creator,status)} activityLog={activityLog} flows={flows} brand={brand} brandId={brandId} userId={userId} campaigns={campaigns} kanbanData={kanbanData} persistKanbanItem={persistKanbanItem} filterHint={creatorsFilterHint} clearFilterHint={()=>setCreatorsFilterHint(null)}/>:
          active==="campaigns"?<Campaigns selectedId={campaignSelectedId} onSelectCampaign={setCampaignSelectedId} onSelectCreator={setCreatorProfile} kanbanData={kanbanData} setKanbanData={setKanbanData} allCreators={mergedCreators} setAllCreators={setAllCreators} campaigns={campaigns} setCampaigns={setCampaigns} activeBrand={activeBrand} brandId={brandId} userId={userId} onStatusChange={(creator,status)=>handleStatusChange(creator,status)} persistKanbanItem={persistKanbanItem} removeKanbanItem={removeKanbanItem} flows={flows} activityLog={activityLog}/>:
          active==="flows"?<Flows flows={flows} setFlows={setFlows} saveState={flowSaveState} savedAt={flowSavedAt} brandId={brandId}/>:
          active==="queue"?<EmailQueue brandId={brandId} flows={flows}/>:
          active==="receipts"?<Receipts brandId={brandId} brand={brand} activeBrand={activeBrand} onSelectCreator={setCreatorProfile} allCreators={mergedCreators}/>:
          active==="content"?<ContentAnalysis/>:
          active==="agreements"?<Agreements brandData={brandData} activeBrand={activeBrand} agreements={agreements} setAgreements={setAgreements} allCreators={mergedCreators} persistAgreement={persistAgreement} removeAgreement={removeAgreement} brandId={brandId}/>:
          active==="integrations"?<Integrations brand={brand} activeBrand={activeBrand} setBrandData={setBrandData} brandId={brandId}/>:
          active==="agency-dashboard"?<AgencyDashboardPage brandsList={brandsList} userId={userId}/>:
          active==="agency-brands"?<AgencyPage title="Brands" subtitle="Every brand you own. Click any card to jump into that brand's workspace."><BrandsOverviewCard brandsList={brandsList} brandData={brandData} onAddBrand={addBrand} onSwitchBrand={(id)=>{switchBrand(id);setActive('dashboard');}}/></AgencyPage>:
          active==="agency-revenue"?<AgencyPage title="Revenue" subtitle="Monthly recurring revenue per brand. Includes brands outside the tool (legacy / manual clients)."><RevenueTrackerCard userId={userId} brandsList={brandsList}/></AgencyPage>:
          active==="agency-team"?<AgencyPage title="Team" subtitle="Members across every brand you own."><TeamMatrixCard brandsList={brandsList} userId={userId} userProfile={userProfile} onSwitchBrand={(id)=>{switchBrand(id);setActive('dashboard');}}/></AgencyPage>:
          active==="agency-outreach"?<AgencyPage title="Cold outreach" subtitle="B2B outreach to prospect brands from your agency Gmail."><AgencyOutreachInline userProfile={userProfile}/></AgencyPage>:
          active==="agency-activity"?<AgencyPage title="Team activity" subtitle="Active vs total time-in-app + audit trail per operator."><TeamActivityCard userProfile={userProfile} currentUserId={userId}/></AgencyPage>:
          active==="agency-drive"?<AgencyPage title="Agency Drive" subtitle="The Google account that owns kolabs's tracking sheets, agreement Docs, and content folders."><AgencyDriveCard/></AgencyPage>:
          active==="agency-suppress"?<AgencyPage title="Suppress list" subtitle="Emails we'll never contact, even on CSV imports. Auto-populated on bounces + when prospects go to Do Not Contact. Manual entries supported."><SuppressListCard userId={userId}/></AgencyPage>:
          active==="agency-changes"?<AgencyPage title="Recent changes" subtitle="Audit trail across all brands you own."><PlaceholderCard title="Cross-brand change feed" subtitle="Coming soon — already collected in operator_actions; just needs a cross-brand feed UI here."/></AgencyPage>:
          active==="agency-agreements"?<AgencyPage title="Brand agreements" subtitle="Your kolabs ↔ client contracts (different from creator agreements)."><PlaceholderCard title="kolabs ↔ client agreements" subtitle="Coming soon — one signed agreement per brand."/></AgencyPage>:
          <Settings brand={brand} activeBrand={activeBrand} setBrandData={setBrandData} brandData={brandData} setActiveBrand={setActiveBrand} brandId={brandId} reloadBrands={reloadBrands}/>}
      </div>
    </div>
  </div>
    </SignedIn>
  </>);
}

// (FINDER_CREATORS + FILTERS constants removed — fake mock data for the
// decorative Finder section. See replacement Finder component below.)

function Finder(){
  // The previous Finder was decorative — fake "Modash connected" via a localStorage
  // flip + a hardcoded creator list + a fake "12.27m influencers" count.
  // Removed 2026-05-22. Kristijan is evaluating Phyllo + HypeAuditor as the
  // real creator-DB integration. When picked, this surface gets rebuilt against
  // the chosen API.
  return(<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"65vh",flexDirection:"column",gap:14}}>
    <div style={{width:72,height:72,borderRadius:18,background:"#EEF2FF",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth={1.5} strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
    </div>
    <div style={{fontWeight:700,fontSize:18,color:DARK}}>Influencer Finder — coming soon</div>
    <div style={{fontSize:13,color:MUTED,textAlign:"center",maxWidth:420,lineHeight:1.7}}>Creator discovery surface gets built once we lock in the data provider (Phyllo or HypeAuditor — demos booked). In the meantime, import a CSV into the Creators tab or add creators manually.</div>
  </div>);
}

// ─── ONBOARDING WIZARD ───────────────────────────────────────────────────────
// 12 cards walking a new brand from blank to live. Each card derives status
// from data (no separate completion flag) so editing is just re-running the card.
function OnboardingWizard({brand,activeBrand,brandId,flows=[],onNavigate}){
  const [brandRow,setBrandRow] = useState(null); // brand row from supabase incl. settings + send_mode
  const [gmailInboxes,setGmailInboxes] = useState([]);
  const [shopifyStatus,setShopifyStatus] = useState(null);
  const [loaded,setLoaded] = useState(false);
  const [savingKey,setSavingKey] = useState(null);

  const loadAll = async()=>{
    if(!brandId)return;
    const [br,gi,ss] = await Promise.all([
      supabase.from('brands').select('*').eq('id',brandId).maybeSingle(),
      supabase.rpc('gmail_connection_status').select('*').eq('brand_id',brandId).order('connected_at'),
      supabase.rpc('shopify_connection_status').select('*').eq('brand_id',brandId).maybeSingle(),
    ]);
    setBrandRow(br.data||null);
    setGmailInboxes(gi.data||[]);
    setShopifyStatus(ss.data||null);
    // Pull Shopify catalog + locations only if connected, via the Shopify Admin proxy is overkill —
    // we don't have one. For now, products + locations only fetched when the gifting/warehouse cards expand.
    setLoaded(true);
  };
  useEffect(()=>{loadAll();/* eslint-disable-line react-hooks/exhaustive-deps */},[brandId]);

  const settings = brandRow?.settings || {};
  const sendMode = brandRow?.send_mode || 'paused';

  // Save a single setting key under brand.settings (merges with existing).
  const saveSetting = async (key,value)=>{
    if(!brandId)return;
    setSavingKey(key);
    const next = {...settings, [key]: value};
    const{error} = await supabase.from('brands').update({settings:next}).eq('id',brandId);
    setSavingKey(null);
    if(error){toast(`Save failed: ${error.message}`,'error');return false;}
    setBrandRow(b=>({...b, settings: next}));
    toast(`✓ Saved ${key}`);
    return true;
  };

  // Status helpers — every card derives from data, no separate "step done" flag.
  const status = {
    brand_basics: !!(brand?.name && (brand?.domain || brand?.instagram)),
    gmail: gmailInboxes.length>0,
    shopify: !!shopifyStatus,
    warehouse_routing: !!settings.warehouse_routing && settings.warehouse_routing.length>0,
    gifting_catalog: !!settings.gifting_catalog && settings.gifting_catalog.length>0,
    required_fields: !!settings.required_fields,
    flows: flows.length>0 && !!settings.flows_reviewed_at,
    daily_caps: gmailInboxes.length>0, // technically defaults to 100; mark complete if inbox connected
    snowball: !!brandRow?.snowball_zap_url,
    drive_folders: !!settings.drive_folders?.brand_root,
    modash: false, // future
    slack: false,  // future
    go_live: sendMode==='live',
  };
  const CRITICAL = ['brand_basics','gmail','shopify','warehouse_routing','gifting_catalog','drive_folders','go_live'];
  const completedCritical = CRITICAL.filter(k=>status[k]).length;
  const completedAll = Object.values(status).filter(Boolean).length;
  const totalCards = 13;

  if(!loaded){
    return <Card style={{padding:40,textAlign:"center",color:MUTED}}>Loading setup…</Card>;
  }

  return(<div style={{display:"flex",flexDirection:"column",gap:18,maxWidth:880}}>
    <div>
      <div style={{fontSize:11,fontWeight:700,color:MUTED,letterSpacing:".08em",textTransform:"uppercase",marginBottom:8}}>Onboarding</div>
      <h1 style={{margin:0,fontWeight:800,fontSize:24,color:DARK}}>Set up {activeBrand}</h1>
      <div style={{fontSize:13,color:MUTED,marginTop:5}}>Walk through each step. Critical steps must be ✓ before you can go Live.</div>
    </div>

    {/* Progress */}
    <Card style={{padding:18}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontWeight:700,color:DARK,fontSize:14}}>{completedAll} of {totalCards} steps complete</div>
        <div style={{fontSize:12,color:MUTED}}>{completedCritical}/{CRITICAL.length} critical</div>
      </div>
      <div style={{height:8,background:"#f0f0f0",borderRadius:4,overflow:"hidden"}}>
        <div style={{width:`${Math.round((completedAll/totalCards)*100)}%`,height:"100%",background:G,transition:"width .3s"}}/>
      </div>
    </Card>

    <SetupCard num={1} title="Brand basics" desc="Name, domain, social handles" critical complete={status.brand_basics}
      currentText={brand?.name ? `${brand.name}${brand.domain?` · ${brand.domain}`:""}` : "Not set"}
      actionLabel="Edit in Settings" onAction={()=>onNavigate&&onNavigate("settings")}/>

    <SetupCard num={2} title="Connect Gmail inbox" desc="Add at least one Gmail account to send outreach from" critical complete={status.gmail}
      currentText={gmailInboxes.length===0 ? "No inboxes connected" : gmailInboxes.length===1 ? `Connected: ${gmailInboxes[0].email_address}` : `${gmailInboxes.length} inboxes connected`}
      actionLabel={gmailInboxes.length===0?"Connect Gmail":"Manage inboxes"} onAction={()=>onNavigate&&onNavigate("settings")}/>

    <SetupCard num={3} title="Connect Shopify" desc="Auto-create gifted orders on positive replies" critical complete={status.shopify}
      currentText={shopifyStatus ? `Connected: ${shopifyStatus.shop_name||shopifyStatus.shop_domain}` : "Not connected"}
      actionLabel={shopifyStatus?"Manage in Integrations":"Connect Shopify"} onAction={()=>onNavigate&&onNavigate("integrations")}/>

    {/* Warehouse routing — inline editor */}
    <WarehouseRoutingCard num={4} status={status.warehouse_routing} routing={settings.warehouse_routing||[]} shopifyConnected={!!shopifyStatus}
      onSave={(v)=>saveSetting('warehouse_routing',v)} saving={savingKey==='warehouse_routing'}
      brandId={brandId}/>

    {/* Gifting catalog — inline editor */}
    <GiftingCatalogCard num={5} status={status.gifting_catalog} catalog={settings.gifting_catalog||[]} shopifyConnected={!!shopifyStatus}
      onSave={(v)=>saveSetting('gifting_catalog',v)} saving={savingKey==='gifting_catalog'}
      brandId={brandId}/>

    {/* Required reply fields — inline checkboxes */}
    <RequiredFieldsCard num={6} status={status.required_fields} fields={settings.required_fields||DEFAULT_REQUIRED_FIELDS}
      onSave={(v)=>saveSetting('required_fields',v)} saving={savingKey==='required_fields'}/>

    <SetupCard num={7} title="Review flow templates" desc={`${flows.length} flow${flows.length!==1?"s":""} seeded — confirm wording matches your brand voice`} complete={status.flows}
      currentText={status.flows?`Reviewed ${new Date(settings.flows_reviewed_at).toLocaleDateString()}`:flows.length===0?"No flows yet":"Not reviewed yet"}
      actionLabel="Open Flows" onAction={()=>onNavigate&&onNavigate("flows")}
      secondaryLabel={status.flows?null:flows.length>0?"Mark reviewed":null} onSecondary={()=>saveSetting('flows_reviewed_at', new Date().toISOString())}/>

    <SetupCard num={8} title="Daily send caps" desc={`Per-inbox cap (default 100/day). New inboxes auto-ramp over 4 weeks (warmup) to protect deliverability.`} complete={status.daily_caps}
      currentText={gmailInboxes.length===0?"Requires Gmail":`${gmailInboxes.length} inbox${gmailInboxes.length!==1?"es":""} × ${gmailInboxes[0]?.daily_cap||100}/day = ${gmailInboxes.reduce((s,i)=>s+(i.daily_cap||100),0)}/day total`}
      actionLabel="Edit in Settings → Email" onAction={()=>onNavigate&&onNavigate("settings")}/>

    {/* Snowball Zap — simple URL input */}
    <SnowballCard num={9} status={status.snowball} value={brandRow?.snowball_zap_url||""}
      onSave={async(v)=>{ setSavingKey('snowball'); const {error}=await supabase.from('brands').update({snowball_zap_url:v||null}).eq('id',brandId); setSavingKey(null); if(error){toast(error.message,'error');return false;} setBrandRow(b=>({...b,snowball_zap_url:v||null})); toast('✓ Saved'); return true; }}
      saving={savingKey==='snowball'}/>

    {/* Drive folders — agency-wide Google Drive integration, optional but recommended.
        Provisions per-brand folder structure under the agency's
        "KOLABS — Influencer OS" root once operator clicks Set up. */}
    <DriveFoldersCard num={10} status={status.drive_folders} folders={settings.drive_folders||null} brandId={brandId}/>

    <SetupCard num={11} title="Slack notifications" desc="Paid-inquiry pings, order alerts, weekly summaries" complete={false} optional
      currentText="Coming soon" actionLabel="Coming soon" actionDisabled/>

    {/* Go live */}
    <GoLiveCard num={12} status={status.go_live} sendMode={sendMode} criticalDone={completedCritical>=CRITICAL.length-1} brandId={brandId}
      onChange={async(mode)=>{ const prev=brandRow?.send_mode; setSavingKey('send_mode'); const {error}=await supabase.from('brands').update({send_mode:mode}).eq('id',brandId); setSavingKey(null); if(error){toast(error.message,'error');return;} setBrandRow(b=>({...b,send_mode:mode})); logSecurityEvent(brandId,'brand.send_mode_change','brand',brandId,{from:prev,to:mode}); toast(`Send mode: ${mode}`,mode==='live'?'success':'info'); }}
      saving={savingKey==='send_mode'}/>
  </div>);
}

// ----- Onboarding sub-components -----

const DEFAULT_REQUIRED_FIELDS = ["name","address_line1","city","state","postal_code","country","product"];

// Generic card frame for steps that just link out to existing pages.
function SetupCard({num,title,desc,critical,optional,complete,currentText,actionLabel,onAction,secondaryLabel,onSecondary,actionDisabled}){
  const accent = complete ? G : critical ? "#EF4444" : optional ? "#9CA3AF" : "#F59E0B";
  return(<Card style={{padding:18,border:`1px solid ${complete?G+"50":BORDER}`,background:complete?G+"06":"#fff"}}>
    <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
      <div style={{width:28,height:28,borderRadius:"50%",background:complete?G:"#f0f4f2",color:complete?"#fff":MUTED,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0,border:complete?"none":`1.5px solid ${BORDER}`}}>{complete?"✓":num}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
          <div style={{fontWeight:700,color:DARK,fontSize:14}}>{title}</div>
          {critical&&!complete&&<span style={{background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",borderRadius:4,padding:"1px 6px",fontSize:10,fontWeight:700,letterSpacing:".05em"}}>CRITICAL</span>}
          {optional&&<span style={{background:"#F3F4F6",color:MUTED,fontSize:10,fontWeight:600,padding:"1px 6px",borderRadius:4}}>OPTIONAL</span>}
        </div>
        <div style={{fontSize:12,color:MUTED,lineHeight:1.5}}>{desc}</div>
        <div style={{fontSize:12,color:complete?"#166534":accent,marginTop:7,fontWeight:600}}>{currentText}</div>
      </div>
      <div style={{display:"flex",gap:6,flexShrink:0}}>
        {secondaryLabel&&<button onClick={onSecondary} style={{border:`1px solid ${BORDER}`,background:"#fff",color:DARK,borderRadius:7,padding:"7px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>{secondaryLabel}</button>}
        <button disabled={actionDisabled} onClick={onAction} style={{background:actionDisabled?"#f0f0f0":DARK,color:actionDisabled?"#9ab8a6":G,border:"none",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:actionDisabled?"not-allowed":"pointer"}}>{actionLabel}</button>
      </div>
    </div>
  </Card>);
}

// Calls the shopify-catalog Edge Function. `path` is either "locations" or "products".
// Sends the ACTIVE brand id (from the same localStorage key the brand switcher
// persists) — the backend refuses to guess a brand for multi-brand operators.
async function fetchShopifyCatalog(path){
  const t = await window.Clerk?.session?.getToken();
  let activeBrandId=null;
  try{ activeBrandId=JSON.parse(localStorage.getItem('ios_activeBrandId')); }catch{ /* fall through */ }
  const r = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/shopify-catalog/${path}`,{
    method:"POST",
    headers:{Authorization:`Bearer ${t}`,"content-type":"application/json"},
    body:JSON.stringify(activeBrandId?{brand_id:activeBrandId}:{}),
  });
  const body = await r.json();
  if(!r.ok) throw new Error(body.error||body.message||`HTTP ${r.status}`);
  return body;
}

function WarehouseRoutingCard({num,status,routing,shopifyConnected,onSave,saving,brandId}){
  const [expanded,setExpanded] = useState(false);
  const [locations,setLocations] = useState([]);
  const [loading,setLoading] = useState(false);
  const [loadErr,setLoadErr] = useState(null);
  const [draft,setDraft] = useState(routing);
  const [dirty,setDirty] = useState(false);
  useEffect(()=>{setDraft(routing);setDirty(false);},[routing]);

  const loadLocations = async()=>{
    if(locations.length>0)return;
    setLoading(true);setLoadErr(null);
    try{
      const {locations:locs} = await fetchShopifyCatalog("locations");
      setLocations((locs||[]).filter(l=>l.active));
    }catch(e){setLoadErr(e.message);}
    finally{setLoading(false);}
  };
  const expand = ()=>{const next=!expanded;setExpanded(next);if(next)loadLocations();};

  // Split out the default rule (countries=["*"]) from country-specific rules for clearer UI.
  const defaultRule = draft.find(r=>(r.countries||[]).includes("*"));
  const specificRules = draft.filter(r=>!(r.countries||[]).includes("*"));

  const updateRule = (idx,patch)=>{
    const next=specificRules.map((r,i)=>i===idx?{...r,...patch}:r);
    setDraft(defaultRule?[...next,defaultRule]:next);setDirty(true);
  };
  const removeRule = (idx)=>{
    const next=specificRules.filter((_,i)=>i!==idx);
    setDraft(defaultRule?[...next,defaultRule]:next);setDirty(true);
  };
  const addRule = ()=>{
    const next=[...specificRules,{countries:[],location_id:locations[0]?.id||null,location_name:locations[0]?.name||""}];
    setDraft(defaultRule?[...next,defaultRule]:next);setDirty(true);
  };
  const setDefault = (locId)=>{
    const loc=locations.find(l=>String(l.id)===String(locId));
    const newDflt=locId?{countries:["*"],location_id:Number(locId),location_name:loc?.name||""}:null;
    setDraft(newDflt?[...specificRules,newDflt]:specificRules);setDirty(true);
  };
  const setRuleLocation = (idx,locId)=>{
    const loc=locations.find(l=>String(l.id)===String(locId));
    updateRule(idx,{location_id:Number(locId),location_name:loc?.name||""});
  };
  const setRuleCountries = (idx,csv)=>{
    const codes=csv.toUpperCase().split(/[,\s]+/).map(s=>s.trim()).filter(Boolean);
    updateRule(idx,{countries:codes});
  };

  const handleSave = async()=>{
    // Validate: no empty rule, no rule without a location.
    const cleaned=draft.filter(r=>r.location_id&&(r.countries||[]).length>0);
    const ok = await onSave(cleaned);
    if(ok){setDirty(false);setExpanded(false);}
  };

  if(!shopifyConnected){
    return(<SetupCard num={num} title="Warehouse routing" desc="Country → warehouse mapping (skipped — Shopify not connected)"
      critical complete={false} currentText="Connect Shopify first" actionLabel="—" actionDisabled/>);
  }

  return(<Card style={{padding:18,border:`1px solid ${status?G+"50":BORDER}`,background:status?G+"06":"#fff"}}>
    <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
      <div style={{width:28,height:28,borderRadius:"50%",background:status?G:"#f0f4f2",color:status?"#fff":MUTED,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0,border:status?"none":`1.5px solid ${BORDER}`}}>{status?"✓":num}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
          <div style={{fontWeight:700,color:DARK,fontSize:14}}>Warehouse routing</div>
          {!status&&<span style={{background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",borderRadius:4,padding:"1px 6px",fontSize:10,fontWeight:700,letterSpacing:".05em"}}>CRITICAL</span>}
        </div>
        <div style={{fontSize:12,color:MUTED,lineHeight:1.5}}>Map each country to a Shopify location so orders ship from the right warehouse. Use ISO 2-letter codes (US, CA, GB, AU).</div>
        <div style={{fontSize:12,color:status?"#166534":"#F59E0B",marginTop:7,fontWeight:600}}>
          {status?`${routing.length} rule${routing.length!==1?"s":""} configured`:"Not configured — orders won't auto-create without routing"}
        </div>
      </div>
      <button onClick={expand} style={{background:expanded?"#fff":DARK,color:expanded?DARK:G,border:expanded?`1px solid ${BORDER}`:"none",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer",flexShrink:0}}>{expanded?"Hide":status?"Edit":"Configure"}</button>
    </div>

    {expanded&&(<div style={{marginTop:14,padding:14,background:"#f8faf9",borderRadius:8}}>
      {loading?<div style={{fontSize:12,color:MUTED}}>Loading Shopify locations…</div>:
       loadErr?<div style={{fontSize:12,color:"#DC2626"}}>Could not load locations: {loadErr}</div>:
       locations.length===0?<div style={{fontSize:12,color:MUTED}}>No active Shopify locations found.</div>:(
        <div>
          <div style={{fontSize:11,fontWeight:700,color:MUTED,letterSpacing:".05em",textTransform:"uppercase",marginBottom:8}}>Country rules</div>
          {specificRules.length===0&&<div style={{fontSize:12,color:MUTED,fontStyle:"italic",marginBottom:8}}>No country-specific rules. Add one below.</div>}
          {specificRules.map((r,i)=>(
            <div key={i} style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
              <input value={(r.countries||[]).join(", ")} onChange={e=>setRuleCountries(i,e.target.value)}
                placeholder="US, CA"
                style={{flex:1,padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,fontFamily:"DM Mono, monospace",textTransform:"uppercase"}}/>
              <span style={{color:MUTED,fontSize:13}}>→</span>
              <select value={r.location_id||""} onChange={e=>setRuleLocation(i,e.target.value)}
                style={{flex:2,padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,background:"#fff"}}>
                <option value="">Select warehouse…</option>
                {locations.map(l=>(<option key={l.id} value={l.id}>{l.name} ({l.country_code})</option>))}
              </select>
              <button onClick={()=>removeRule(i)} title="Remove rule" style={{background:"#fff",border:`1px solid ${BORDER}`,borderRadius:6,padding:"6px 9px",cursor:"pointer",color:MUTED,fontSize:14,lineHeight:1}}>×</button>
            </div>
          ))}
          <button onClick={addRule} style={{background:"#fff",border:`1px dashed ${BORDER}`,borderRadius:6,padding:"7px 12px",fontSize:12,fontWeight:600,color:DARK,cursor:"pointer",marginTop:2}}>+ Add country rule</button>

          <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${BORDER}`}}>
            <div style={{fontSize:11,fontWeight:700,color:MUTED,letterSpacing:".05em",textTransform:"uppercase",marginBottom:8}}>Default warehouse</div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <span style={{fontSize:12,color:MUTED,flex:1}}>All other countries →</span>
              <select value={defaultRule?.location_id||""} onChange={e=>setDefault(e.target.value)}
                style={{flex:2,padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,background:"#fff"}}>
                <option value="">No default (orders skip if country unmatched)</option>
                {locations.map(l=>(<option key={l.id} value={l.id}>{l.name} ({l.country_code})</option>))}
              </select>
            </div>
          </div>

          <div style={{display:"flex",gap:8,marginTop:16,justifyContent:"flex-end"}}>
            <button onClick={()=>{setDraft(routing);setDirty(false);setExpanded(false);}} disabled={saving}
              style={{background:"#fff",border:`1px solid ${BORDER}`,color:DARK,borderRadius:7,padding:"8px 14px",fontWeight:600,fontSize:12,cursor:saving?"not-allowed":"pointer"}}>Cancel</button>
            <button onClick={handleSave} disabled={!dirty||saving}
              style={{background:dirty&&!saving?DARK:"#f0f0f0",color:dirty&&!saving?G:"#9ab8a6",border:"none",borderRadius:7,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:dirty&&!saving?"pointer":"not-allowed"}}>{saving?"Saving…":"Save routing"}</button>
          </div>
        </div>
      )}
    </div>)}
  </Card>);
}

function GiftingCatalogCard({num,status,catalog,shopifyConnected,onSave,saving,brandId}){
  const [expanded,setExpanded] = useState(false);
  const [products,setProducts] = useState([]);
  const [loading,setLoading] = useState(false);
  const [loadErr,setLoadErr] = useState(null);
  const [selected,setSelected] = useState(new Set(catalog.map(c=>String(c.product_id))));
  const [filter,setFilter] = useState("");
  const [dirty,setDirty] = useState(false);
  useEffect(()=>{setSelected(new Set(catalog.map(c=>String(c.product_id))));setDirty(false);},[catalog]);

  const loadProducts = async()=>{
    if(products.length>0)return;
    setLoading(true);setLoadErr(null);
    try{
      const {products:prods} = await fetchShopifyCatalog("products");
      // Show active first, then sort by title.
      prods.sort((a,b)=>{
        if((a.status==="active")!==(b.status==="active")) return a.status==="active"?-1:1;
        return a.title.localeCompare(b.title);
      });
      setProducts(prods);
    }catch(e){setLoadErr(e.message);}
    finally{setLoading(false);}
  };
  const expand = ()=>{const next=!expanded;setExpanded(next);if(next)loadProducts();};

  const toggle = (p)=>{
    const next=new Set(selected);
    const key=String(p.id);
    if(next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);setDirty(true);
  };
  const selectAllActive = ()=>{
    const next=new Set(selected);
    products.filter(p=>p.status==="active").forEach(p=>next.add(String(p.id)));
    setSelected(next);setDirty(true);
  };
  const clearAll = ()=>{ setSelected(new Set()); setDirty(true); };

  const handleSave = async()=>{
    const entries=products.filter(p=>selected.has(String(p.id))).map(p=>({product_id:p.id,title:p.title}));
    // Also preserve any selected entries whose products aren't in the currently-loaded list (paranoia for >250 catalogs).
    catalog.forEach(c=>{
      if(selected.has(String(c.product_id))&&!entries.find(e=>String(e.product_id)===String(c.product_id))){
        entries.push({product_id:c.product_id,title:c.title});
      }
    });
    const ok = await onSave(entries);
    if(ok){setDirty(false);setExpanded(false);}
  };

  const filtered = products.filter(p=>!filter||p.title.toLowerCase().includes(filter.toLowerCase()));

  if(!shopifyConnected){
    return(<SetupCard num={num} title="Gifting catalog" desc="Pick which products are available for influencer seeding (skipped — Shopify not connected)"
      critical complete={false} currentText="Connect Shopify first" actionLabel="—" actionDisabled/>);
  }

  return(<Card style={{padding:18,border:`1px solid ${status?G+"50":BORDER}`,background:status?G+"06":"#fff"}}>
    <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
      <div style={{width:28,height:28,borderRadius:"50%",background:status?G:"#f0f4f2",color:status?"#fff":MUTED,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0,border:status?"none":`1.5px solid ${BORDER}`}}>{status?"✓":num}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
          <div style={{fontWeight:700,color:DARK,fontSize:14}}>Gifting catalog</div>
          {!status&&<span style={{background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",borderRadius:4,padding:"1px 6px",fontSize:10,fontWeight:700,letterSpacing:".05em"}}>CRITICAL</span>}
        </div>
        <div style={{fontSize:12,color:MUTED,lineHeight:1.5}}>Pick which Shopify products can be sent to influencers. The matcher and the "alternatives if out of stock" suggester both restrict to this list.</div>
        <div style={{fontSize:12,color:status?"#166534":"#F59E0B",marginTop:7,fontWeight:600}}>
          {status?`${selected.size||catalog.length} product${(selected.size||catalog.length)!==1?"s":""} in gifting catalog`:"Not configured — matcher uses full Shopify catalog (risks wrong products)"}
        </div>
      </div>
      <button onClick={expand} style={{background:expanded?"#fff":DARK,color:expanded?DARK:G,border:expanded?`1px solid ${BORDER}`:"none",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer",flexShrink:0}}>{expanded?"Hide":status?"Edit":"Configure"}</button>
    </div>

    {expanded&&(<div style={{marginTop:14,padding:14,background:"#f8faf9",borderRadius:8}}>
      {loading?<div style={{fontSize:12,color:MUTED}}>Loading Shopify products…</div>:
       loadErr?<div style={{fontSize:12,color:"#DC2626"}}>Could not load products: {loadErr}</div>:
       products.length===0?<div style={{fontSize:12,color:MUTED}}>No products found in Shopify.</div>:(
        <div>
          <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:10}}>
            <input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Filter products…"
              style={{flex:1,padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12}}/>
            <button onClick={selectAllActive} style={{background:"#fff",border:`1px solid ${BORDER}`,borderRadius:6,padding:"7px 10px",fontSize:11,fontWeight:600,cursor:"pointer",color:DARK}}>Select all active</button>
            <button onClick={clearAll} style={{background:"#fff",border:`1px solid ${BORDER}`,borderRadius:6,padding:"7px 10px",fontSize:11,fontWeight:600,cursor:"pointer",color:MUTED}}>Clear</button>
          </div>
          <div style={{fontSize:11,color:MUTED,marginBottom:8}}>
            {selected.size} of {products.length} selected · showing {filtered.length}{products.length>=250?" (first 250 only)":""}
          </div>
          <div style={{maxHeight:360,overflowY:"auto",border:`1px solid ${BORDER}`,borderRadius:6,background:"#fff"}}>
            {filtered.map(p=>{
              const isSelected = selected.has(String(p.id));
              return(<label key={p.id} style={{display:"flex",gap:10,alignItems:"center",padding:"8px 10px",borderBottom:`1px solid #f0f0f0`,cursor:"pointer",background:isSelected?G+"08":"transparent"}}>
                <input type="checkbox" checked={isSelected} onChange={()=>toggle(p)} style={{cursor:"pointer",margin:0}}/>
                {p.image?<img src={p.image} alt="" style={{width:28,height:28,objectFit:"cover",borderRadius:4,flexShrink:0}}/>:
                  <div style={{width:28,height:28,background:"#f0f0f0",borderRadius:4,flexShrink:0}}/>}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:600,color:DARK,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.title}</div>
                  <div style={{fontSize:10,color:MUTED}}>
                    {p.status!=="active"&&<span style={{textTransform:"uppercase",color:"#F59E0B",fontWeight:700,marginRight:6}}>{p.status}</span>}
                    {p.variant_count} variant{p.variant_count!==1?"s":""} · {p.total_inventory} in stock
                  </div>
                </div>
              </label>);
            })}
          </div>

          <div style={{display:"flex",gap:8,marginTop:16,justifyContent:"flex-end"}}>
            <button onClick={()=>{setSelected(new Set(catalog.map(c=>String(c.product_id))));setDirty(false);setExpanded(false);}} disabled={saving}
              style={{background:"#fff",border:`1px solid ${BORDER}`,color:DARK,borderRadius:7,padding:"8px 14px",fontWeight:600,fontSize:12,cursor:saving?"not-allowed":"pointer"}}>Cancel</button>
            <button onClick={handleSave} disabled={!dirty||saving}
              style={{background:dirty&&!saving?DARK:"#f0f0f0",color:dirty&&!saving?G:"#9ab8a6",border:"none",borderRadius:7,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:dirty&&!saving?"pointer":"not-allowed"}}>{saving?"Saving…":`Save ${selected.size} product${selected.size!==1?"s":""}`}</button>
          </div>
        </div>
      )}
    </div>)}
  </Card>);
}

function FulfillmentModeEditor({brand,persistBrandField}){
  const mode = brand?.fulfillment_mode==="tiktok_shop" ? "tiktok_shop" : "shopify";
  const saved = Array.isArray(brand?.tiktok_shop?.products) ? brand.tiktok_shop.products : [];
  const [products,setProducts] = useState(saved);
  const [dirty,setDirty] = useState(false);
  useEffect(()=>{ setProducts(saved); setDirty(false); },[JSON.stringify(saved)]);
  const edit = (i,key,val)=>{ setProducts(ps=>ps.map((p,j)=>j===i?{...p,[key]:val}:p)); setDirty(true); };
  const add = ()=>{ setProducts(ps=>[...ps,{name:"",url:""}]); setDirty(true); };
  const remove = (i)=>{ setProducts(ps=>ps.filter((_,j)=>j!==i)); setDirty(true); };
  const save = async()=>{
    const clean = products.map(p=>({name:(p.name||"").trim(),url:(p.url||"").trim()})).filter(p=>p.name);
    await persistBrandField('tiktok_shop',{...(brand?.tiktok_shop||{}),products:clean});
    setDirty(false);
  };
  return(<div style={{padding:"14px 0",borderTop:`1px solid ${BORDER}`,marginTop:6}}>
    <div style={{fontSize:13,fontWeight:600,color:DARK,marginBottom:4}}>Fulfilment</div>
    <div style={{fontSize:11,color:MUTED,marginBottom:8,lineHeight:1.5}}>How the gift reaches the creator. This decides what the AI asks a creator who says yes.</div>
    <select value={mode} onChange={e=>persistBrandField('fulfillment_mode',e.target.value)}
      style={{padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,background:"#fff",color:DARK,fontFamily:"inherit"}}>
      <option value="shopify">Shopify order — ask for shipping address + product</option>
      <option value="tiktok_shop">TikTok Shop target invite — ask for TikTok handle + ONE product, never an address</option>
    </select>
    {mode==="tiktok_shop"&&(<div style={{marginTop:12,padding:12,background:"#f8faf9",borderRadius:8}}>
      <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:4}}>Products on offer (creator picks one)</div>
      <div style={{fontSize:11,color:MUTED,marginBottom:8,lineHeight:1.5}}>The AI only ever offers these, one per creator, and sends the link when asking for the pick. When you've sent the Target Collaboration invite, reply "Target invite sent" in the thread (or move the card to Awaiting Post) — the creator moves to Awaiting Post and lands on the Agreed Influencer sheet.</div>
      {products.map((p,i)=>(<div key={i} style={{display:"flex",gap:6,marginBottom:6}}>
        <input value={p.name||""} onChange={e=>edit(i,'name',e.target.value)} placeholder="Product name, e.g. Multi+"
          style={{flex:"0 0 200px",padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,fontFamily:"inherit"}}/>
        <input value={p.url||""} onChange={e=>edit(i,'url',e.target.value)} placeholder="https://… product link (sent to the creator)"
          style={{flex:1,padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,fontFamily:"DM Mono, monospace"}}/>
        <button onClick={()=>remove(i)} title="Remove" style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:6,padding:"0 10px",fontSize:12,cursor:"pointer",color:MUTED}}>✕</button>
      </div>))}
      <div style={{display:"flex",gap:6,justifyContent:"space-between",marginTop:8}}>
        <button onClick={add} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:7,padding:"6px 12px",fontSize:12,cursor:"pointer",color:DARK}}>+ Add product</button>
        <button onClick={save} disabled={!dirty} style={{background:dirty?DARK:"#f0f0f0",color:dirty?G:"#9ab8a6",border:"none",borderRadius:7,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:dirty?"pointer":"not-allowed"}}>Save products</button>
      </div>
    </div>)}
  </div>);
}

function RequiredFieldsCard({num,status,fields,onSave,saving}){
  const [expanded,setExpanded] = useState(false);
  const ALL_FIELDS = [
    {key:"name",label:"Full name",default:true},
    {key:"address_line1",label:"Street address",default:true},
    {key:"address_line2",label:"Address line 2 (apt/suite)",default:false},
    {key:"city",label:"City",default:true},
    {key:"state",label:"State / province",default:true},
    {key:"postal_code",label:"Postal / ZIP code",default:true},
    {key:"country",label:"Country",default:true},
    {key:"phone",label:"Phone",default:false},
    {key:"product",label:"Product preference",default:true},
    {key:"variant",label:"Variant (color, size)",default:false},
  ];
  const [selected,setSelected] = useState(new Set(fields));
  useEffect(()=>{setSelected(new Set(fields));},[fields]);
  const toggle = (k)=>{
    const next = new Set(selected);
    if(next.has(k))next.delete(k); else next.add(k);
    setSelected(next);
  };
  const save = async()=>{
    const ok = await onSave([...selected]);
    if(ok)setExpanded(false);
  };

  return(<Card style={{padding:18,border:`1px solid ${status?G+"50":BORDER}`,background:status?G+"06":"#fff"}}>
    <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
      <div style={{width:28,height:28,borderRadius:"50%",background:status?G:"#f0f4f2",color:status?"#fff":MUTED,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0,border:status?"none":`1.5px solid ${BORDER}`}}>{status?"✓":num}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontWeight:700,color:DARK,fontSize:14,marginBottom:3}}>Required fields for positive replies</div>
        <div style={{fontSize:12,color:MUTED,lineHeight:1.5}}>What info to ask creators for. Auto-reply checks for these — missing fields trigger a "could you also share X?" follow-up.</div>
        <div style={{fontSize:12,color:status?"#166534":MUTED,marginTop:7,fontWeight:600}}>
          {fields.length} field{fields.length!==1?"s":""} required: <span style={{fontFamily:"DM Mono, monospace",fontSize:11}}>{fields.join(", ")}</span>
        </div>
        {expanded&&(<div style={{marginTop:12,padding:12,background:"#f8faf9",borderRadius:8}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {ALL_FIELDS.map(f=>(<label key={f.key} style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:DARK,cursor:"pointer",padding:6,borderRadius:6,background:selected.has(f.key)?G+"15":"transparent"}}>
              <input type="checkbox" checked={selected.has(f.key)} onChange={()=>toggle(f.key)} style={{accentColor:G}}/>
              {f.label}
            </label>))}
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:6,marginTop:12}}>
            <button onClick={()=>setExpanded(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:7,padding:"6px 14px",fontSize:12,cursor:"pointer"}}>Cancel</button>
            <button disabled={saving} onClick={save} style={{background:DARK,color:G,border:"none",borderRadius:7,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:saving?"wait":"pointer"}}>{saving?"Saving…":"Save"}</button>
          </div>
        </div>)}
      </div>
      <button onClick={()=>setExpanded(e=>!e)} style={{background:expanded?"#fff":DARK,color:expanded?DARK:G,border:expanded?`1px solid ${BORDER}`:"none",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer",flexShrink:0}}>{expanded?"Hide":status?"Edit":"Configure"}</button>
    </div>
  </Card>);
}

function SnowballCard({num,status,value,onSave,saving}){
  const [expanded,setExpanded] = useState(false);
  const [url,setUrl] = useState(value);
  useEffect(()=>{setUrl(value);},[value]);
  return(<Card style={{padding:18,border:`1px solid ${BORDER}`}}>
    <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
      <div style={{width:28,height:28,borderRadius:"50%",background:status?G:"#f0f4f2",color:status?"#fff":MUTED,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0,border:status?"none":`1.5px solid ${BORDER}`}}>{status?"✓":num}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
          <div style={{fontWeight:700,color:DARK,fontSize:14}}>Social Snowball (via Zapier)</div>
          <span style={{background:"#F3F4F6",color:MUTED,fontSize:10,fontWeight:600,padding:"1px 6px",borderRadius:4}}>OPTIONAL</span>
        </div>
        <div style={{fontSize:12,color:MUTED,lineHeight:1.5}}>Snowball doesn't have a public API. Create a Zap that listens for our webhook, generates the affiliate code in Snowball, and posts it back. Paste the Zap's URL here.</div>
        <div style={{fontSize:12,color:status?"#166534":MUTED,marginTop:7,fontWeight:600,fontFamily:"DM Mono, monospace",wordBreak:"break-all"}}>{value||"Not configured"}</div>
        {expanded&&(<div style={{marginTop:12}}>
          <input value={url} onChange={e=>setUrl(e.target.value)} placeholder="https://hooks.zapier.com/hooks/catch/..." style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"DM Mono, monospace"}}/>
          <div style={{display:"flex",justifyContent:"flex-end",gap:6,marginTop:10}}>
            <button onClick={()=>{setUrl(value);setExpanded(false);}} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:7,padding:"6px 14px",fontSize:12,cursor:"pointer"}}>Cancel</button>
            <button disabled={saving} onClick={async()=>{const ok=await onSave(url);if(ok)setExpanded(false);}} style={{background:DARK,color:G,border:"none",borderRadius:7,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:saving?"wait":"pointer"}}>{saving?"Saving…":"Save"}</button>
          </div>
        </div>)}
      </div>
      <button onClick={()=>setExpanded(e=>!e)} style={{background:expanded?"#fff":DARK,color:expanded?DARK:G,border:expanded?`1px solid ${BORDER}`:"none",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer",flexShrink:0}}>{expanded?"Hide":status?"Edit":"Configure"}</button>
    </div>
  </Card>);
}

function DriveFoldersCard({num,status,folders,brandId}){
  const [provisioning,setProvisioning] = useState(false);
  const [agencyConnected,setAgencyConnected] = useState(null); // null=loading, true/false
  const [error,setError] = useState(null);
  const [resyncing,setResyncing] = useState(false);
  const [resyncMsg,setResyncMsg] = useState(null);
  const resyncSheet = async()=>{
    setResyncing(true);setResyncMsg(null);
    try{
      const t = await window.Clerk?.session?.getToken();
      const r = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-drive/resync-agreed`,{
        method:'POST',headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},body:JSON.stringify({brand_id:brandId}),
      });
      const b = await r.json();
      if(!r.ok||!b.ok){setResyncMsg(b.error||`HTTP ${r.status}`);toast(`Re-sync failed: ${b.error||r.status}`,'error');return;}
      setResyncMsg(`${b.appended} added · ${b.already_present} already there`);
      toast(`✓ Sheet re-synced — ${b.appended} row(s) added`);
    }catch(e){setResyncMsg(e.message);toast('Re-sync failed','error');}
    finally{setResyncing(false);}
  };
  useEffect(()=>{
    (async()=>{
      const{data}=await supabase.rpc('agency_drive_status').select('email').maybeSingle();
      setAgencyConnected(!!data?.email);
    })();
  },[]);
  const provision = async()=>{
    setProvisioning(true);setError(null);
    try{
      const t = await window.Clerk?.session?.getToken();
      const r = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-drive/provision-brand`,{
        method:'POST',headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},body:JSON.stringify({brand_id:brandId}),
      });
      const body = await r.json();
      // Drive isn't connected — this is a calm, expected state (Drive is an
      // optional agency-internal convenience), not a hard error. Show a friendly
      // inline note and a path to fix it; never surface a raw 412 / error code.
      if(body.needs_drive_connection){setError(body.message||"Connect Google Drive in Settings → Integrations to enable folder auto-creation.");setAgencyConnected(false);return;}
      // Map any remaining failure to a human message — no raw codes / HTTP statuses in the UI.
      if(!r.ok||!body.ok){
        const FRIENDLY={agency_drive_not_connected:"Google Drive isn't connected. Connect it in Settings → Integrations.",agency_root_folder_missing:"Reconnect Google Drive in Settings → Integrations — the agency root folder is missing."};
        const msg=FRIENDLY[body.error||body.code]||body.message||"Couldn't set up Drive folders. Please try again, or reconnect Google Drive in Settings → Integrations.";
        setError(msg);toast(msg,'error');return;
      }
      toast(`✓ Drive folders created for this brand`);
      // Soft reload — easiest way to refresh brand.settings.drive_folders via parent loadAll.
      setTimeout(()=>window.location.reload(),500);
    }catch(e){setError(e.message);toast('Setup failed','error');}
    finally{setProvisioning(false);}
  };

  // Only offer the active "Set up folders" action once the agency Drive is
  // actually connected. While the status is still loading (null), or when it's
  // disconnected (false), show a calm optional card with a clear next step —
  // never an active button that would fail. (If folders were already provisioned
  // we still render the full card below so the re-sync controls stay reachable.)
  if(agencyConnected!==true && !status){
    return(<SetupCard num={num} title="Drive folders" desc="Auto-create per-brand folder structure in the agency Drive (Agreements, Content, Briefs, tracking Sheet). Optional — connect Google Drive in Settings → Integrations first." optional
      complete={false} currentText={agencyConnected===false?"Google Drive not connected":"Checking Google Drive…"} actionLabel="Connect Drive first" actionDisabled/>);
  }

  return(<Card style={{padding:18,border:`1px solid ${status?G+"50":BORDER}`,background:status?G+"06":"#fff"}}>
    <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
      <div style={{width:28,height:28,borderRadius:"50%",background:status?G:"#f0f4f2",color:status?"#fff":MUTED,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0,border:status?"none":`1.5px solid ${BORDER}`}}>{status?"✓":num}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
          <div style={{fontWeight:700,color:DARK,fontSize:14}}>Drive folders</div>
          {!status&&<span style={{background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",borderRadius:4,padding:"1px 6px",fontSize:10,fontWeight:700,letterSpacing:".05em"}}>REQUIRED</span>}
        </div>
        <div style={{fontSize:12,color:MUTED,lineHeight:1.5}}>Auto-creates this brand's folders + "Creator Tracking" Sheet under the agency Drive root. Idempotent — safe to re-run.</div>
        <div style={{fontSize:12,color:status?"#166534":MUTED,marginTop:7,fontWeight:600}}>
          {status?"All folders ready":"Not set up"}
        </div>
        {status&&folders&&(<div style={{marginTop:10,display:"flex",gap:6,flexWrap:"wrap"}}>
          {[
            {label:"Brand root",f:folders.brand_root},
            {label:"Agreements",f:folders.agreements},
            {label:"Content",f:folders.content},
            {label:"Briefs",f:folders.briefs},
            {label:"📊 Tracking sheet",f:folders.creator_tracking_sheet},
          ].filter(x=>x.f?.webViewLink).map(x=>(
            <a key={x.label} href={x.f.webViewLink} target="_blank" rel="noreferrer"
              style={{fontSize:11,padding:"4px 10px",border:`1px solid ${BORDER}`,borderRadius:6,background:"#fff",color:DARK,textDecoration:"none",fontWeight:600}}>
              {x.label} ↗
            </a>
          ))}
        </div>)}
        {status&&folders&&(<div style={{marginTop:8,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <button disabled={resyncing} onClick={resyncSheet}
            title="Auto-syncs every 6 hours — this forces it now. Re-appends any creators missing from the Agreed tab (recovers writes that failed silently)."
            style={{fontSize:11,padding:"5px 11px",border:`1px solid ${BORDER}`,borderRadius:6,background:"#fff",color:DARK,fontWeight:600,cursor:resyncing?"wait":"pointer"}}>
            {resyncing?"Syncing…":"↻ Sync now"}
          </button>
          <span style={{fontSize:10.5,color:MUTED}}>Auto-syncs every 6h</span>
          {resyncMsg&&<span style={{fontSize:11,color:MUTED}}>{resyncMsg}</span>}
        </div>)}
        {error&&<div style={{marginTop:8,fontSize:11,color:"#DC2626"}}>⚠ {error}</div>}
      </div>
      <button disabled={provisioning} onClick={provision}
        style={{background:provisioning?"#f0f0f0":DARK,color:provisioning?"#9ab8a6":G,border:"none",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:provisioning?"wait":"pointer",flexShrink:0,whiteSpace:"nowrap"}}>
        {provisioning?"Setting up…":status?"Re-sync":"Set up folders"}
      </button>
    </div>
  </Card>);
}

function GoLiveCard({num,status,sendMode,criticalDone,onChange,saving,brandId}){
  const modeMeta = {
    paused:{label:"Paused",desc:"Worker skips this brand entirely. Nothing fires.",color:"#9CA3AF",bg:"#F3F4F6"},
    test:  {label:"Test",  desc:"Generates queue rows + 'sends' them but doesn't call Gmail. Safe for verifying flow logic.",color:"#F59E0B",bg:"#FFFBEB"},
    live:  {label:"Live",  desc:"Real emails go out via your connected Gmail.",color:"#10B981",bg:"#ECFDF5"},
  };
  const m = modeMeta[sendMode] || modeMeta.paused;

  // Verification modal state — gates the Test→Live transition until operator
  // confirms they received the test email (proves the send path works end-to-end).
  const [showVerify, setShowVerify] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState(null);

  const handleModeClick = (mm) => {
    if (mm === sendMode) return;
    if (mm === "live") {
      // Live requires manual verification of the Gmail send path first.
      setShowVerify(true);
      setTestEmail("");
      setSent(false);
      setSendError(null);
      return;
    }
    onChange(mm);
  };
  const sendTest = async () => {
    if (!testEmail.trim() || !brandId) return;
    setSending(true); setSendError(null);
    try {
      const t = await window.Clerk?.session?.getToken();
      const r = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-test-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${t}`, 'content-type': 'application/json' },
        body: JSON.stringify({ brand_id: brandId, to: testEmail.trim() }),
      });
      const body = await r.json();
      if (!r.ok || !body.ok) { setSendError(body.message || body.error || `HTTP ${r.status}`); return; }
      setSent(true);
    } catch (e) { setSendError(e.message); }
    finally { setSending(false); }
  };
  const confirmAndGoLive = () => {
    setShowVerify(false);
    onChange("live");
  };

  return(<>
    <Card style={{padding:18,border:`1.5px solid ${status?G:m.color}40`,background:status?G+"08":m.bg}}>
      <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
        <div style={{width:28,height:28,borderRadius:"50%",background:status?G:m.color,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0}}>{status?"✓":num}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
            <div style={{fontWeight:700,color:DARK,fontSize:14}}>Go live</div>
            {!status&&<span style={{background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",borderRadius:4,padding:"1px 6px",fontSize:10,fontWeight:700,letterSpacing:".05em"}}>CRITICAL</span>}
            <span style={{background:m.color,color:"#fff",fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:4,letterSpacing:".05em"}}>{m.label.toUpperCase()}</span>
          </div>
          <div style={{fontSize:12,color:"#4B5563",lineHeight:1.5,marginTop:3}}>{m.desc}</div>
          {!criticalDone&&sendMode!=="live"&&(<div style={{marginTop:10,fontSize:12,color:"#92400E",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"8px 12px"}}>⚠ Finish the critical setup steps above before flipping to Live.</div>)}
        </div>
        <div style={{display:"flex",gap:0,border:`1px solid ${BORDER}`,borderRadius:9,overflow:"hidden",background:"#fff",flexShrink:0}}>
          {["paused","test","live"].map((mm,i)=>(<button key={mm} disabled={saving||(mm==="live"&&!criticalDone)} onClick={()=>handleModeClick(mm)} style={{background:sendMode===mm?modeMeta[mm].color:"#fff",color:sendMode===mm?"#fff":DARK,border:"none",borderLeft:i>0?`1px solid ${BORDER}`:"none",padding:"7px 14px",fontSize:12,fontWeight:700,cursor:saving?"wait":mm==="live"&&!criticalDone?"not-allowed":"pointer",opacity:(mm==="live"&&!criticalDone)?0.4:1,textTransform:"capitalize"}}>{mm}</button>))}
        </div>
      </div>
    </Card>
    {showVerify&&(<Modal title="Verify Gmail connection before going Live" subtitle="We'll send a real test email so you can confirm the send path works end-to-end." onClose={()=>setShowVerify(false)} width={460}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{padding:"12px 14px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,fontSize:12,color:"#92400E",lineHeight:1.55}}>
          Once you confirm the test arrived, the brand flips to <strong>Live</strong> and real outreach emails start firing automatically.
        </div>
        <div>
          <div style={{fontSize:11,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:5}}>Send test to</div>
          <input type="email" value={testEmail} onChange={e=>setTestEmail(e.target.value)}
            placeholder="your-email@example.com"
            disabled={sending||sent}
            style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          <div style={{fontSize:11,color:MUTED,marginTop:5}}>Use any address you can check right now. One-off — not saved.</div>
        </div>
        {sendError&&<div style={{padding:"10px 12px",background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,fontSize:12,color:"#991B1B"}}>{sendError}</div>}
        {sent&&!sendError&&<div style={{padding:"10px 12px",background:"#F0FDF4",border:`1px solid ${G}40`,borderRadius:8,fontSize:12,color:"#166534"}}>✓ Test email sent to <strong>{testEmail}</strong>. Check your inbox (and spam folder).</div>}
        <div style={{display:"flex",justifyContent:"flex-end",gap:8,paddingTop:6,borderTop:`1px solid ${BORDER}`}}>
          <button onClick={()=>setShowVerify(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:8,padding:"9px 18px",fontSize:13,cursor:"pointer"}}>Cancel</button>
          {!sent
            ?<button onClick={sendTest} disabled={!testEmail.trim()||sending} style={{background:testEmail.trim()&&!sending?DARK:"#f0f0f0",color:testEmail.trim()&&!sending?G:"#9ab8a6",border:"none",borderRadius:8,padding:"9px 18px",fontWeight:700,fontSize:13,cursor:testEmail.trim()&&!sending?"pointer":"not-allowed"}}>{sending?"Sending…":"Send test email"}</button>
            :<button onClick={confirmAndGoLive} style={{background:"#10B981",color:"#fff",border:"none",borderRadius:8,padding:"9px 18px",fontWeight:700,fontSize:13,cursor:"pointer"}}>✓ I received it — Switch to Live</button>
          }
        </div>
      </div>
    </Modal>)}
  </>);
}

// Plain-English group labels for brand_notifications.type. Anything not listed
// falls back to the type with underscores turned into spaces.
const NOTIF_GROUPS={
  shopify_order_failed:{label:"Shopify orders that failed",icon:"🛒"},
  manual_order_needed:{label:"Orders to place by hand",icon:"📦"},
  positive_went_quiet:{label:"Said yes, then went quiet",icon:"🤫"},
  review_backlog:{label:"AI replies waiting for approval",icon:"✍️"},
  instagram_token_expired:{label:"Instagram needs reconnecting",icon:"📸"},
  oauth_failed:{label:"Inbox needs reconnecting",icon:"📪"},
  tiktok_invite_needed:{label:"TikTok Shop invites to send",icon:"🎯"},
  creator_extra_request:{label:"Creators asking for extras",icon:"➕"},
  reply_from_unknown_email:{label:"Replies from unknown senders",icon:"❓"},
  email_outside_platform_thread:{label:"Mail outside Influencer OS threads",icon:"📨"},
  reply_after_lifecycle:{label:"Replies after the collab ended",icon:"💬"},
  operator_handling_thread:{label:"Threads you're handling yourself",icon:"🙋"},
  creator_no_email:{label:"Creators without an email address",icon:"📭"},
  shared_email_multiple_creators:{label:"Creators sharing one email",icon:"👥"},
  gifting_catalog_bypassed:{label:"Gifts outside the catalog",icon:"🎁"},
  cap_reached:{label:"Daily send cap reached",icon:"🛑"},
  cap_approaching:{label:"Daily send cap almost reached",icon:"⚠️"},
  cap_pace_warning:{label:"Sending faster than the cap allows",icon:"⏱"},
  bounce_spike:{label:"Bounce spike",icon:"📮"},
};
function NotificationDigest({items=[],onDismiss,onNavigate}){
  const[openType,setOpenType]=useState(null);
  const[busy,setBusy]=useState(false);
  if(!items.length)return null;
  const groups=[];
  const byType=new Map();
  for(const n of items){
    if(!byType.has(n.type)){byType.set(n.type,{type:n.type,level:n.level,items:[]});groups.push(byType.get(n.type));}
    const g=byType.get(n.type);g.items.push(n);
    if(n.level==="critical")g.level="critical";else if(n.level==="warn"&&g.level!=="critical")g.level="warn";
  }
  const rank={critical:0,warn:1,info:2};
  groups.sort((a,b)=>(rank[a.level]??3)-(rank[b.level]??3)||b.items.length-a.items.length);
  const tone={critical:{dot:"#DC2626",text:"#991B1B",bg:"#FEF2F2"},warn:{dot:"#F59E0B",text:"#92400E",bg:"#FFFBEB"},info:{dot:"#3B82F6",text:"#1E40AF",bg:"#EFF6FF"}};
  const criticalCount=groups.filter(g=>g.level==="critical").reduce((a,g)=>a+g.items.length,0);
  const dismissAll=async(g)=>{if(busy)return;setBusy(true);try{for(const n of g.items)await onDismiss?.(n.id);}finally{setBusy(false);if(openType===g.type)setOpenType(null);}};
  return(<div style={{background:"#fff",border:`1px solid ${criticalCount?"#FECACA":BORDER}`,borderRadius:12,padding:"12px 16px"}}>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
      <span style={{fontSize:16}}>🔔</span>
      <div style={{fontWeight:700,color:DARK,fontSize:13}}>Needs attention · {items.length}</div>
      <div style={{fontSize:11,color:MUTED}}>grouped — click a line to see the items</div>
    </div>
    {groups.map(g=>{const t=tone[g.level]||tone.info;const meta=NOTIF_GROUPS[g.type]||{label:String(g.type||"other").replace(/_/g," "),icon:"🔔"};const open=openType===g.type;const latest=g.items[0];
      return(<div key={g.type} style={{borderTop:`1px solid ${BORDER}`}}>
        <div onClick={()=>setOpenType(open?null:g.type)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 2px",cursor:"pointer"}}>
          <span style={{width:8,height:8,borderRadius:4,background:t.dot,flexShrink:0}}/>
          <span style={{fontSize:14,flexShrink:0}}>{meta.icon}</span>
          <div style={{flex:1,minWidth:0,display:"flex",alignItems:"baseline",gap:8}}>
            <span style={{fontWeight:700,color:t.text,fontSize:13}}>{g.items.length}</span>
            <span style={{fontWeight:600,color:DARK,fontSize:13}}>{meta.label}</span>
            {!open&&latest&&<span style={{fontSize:11,color:MUTED,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>· latest {new Date(latest.created_at).toLocaleString("en-AU",{day:"numeric",month:"short"})}</span>}
          </div>
          {(g.type==="cap_reached"||g.type==="cap_approaching"||g.type==="cap_pace_warning"||g.type==="review_backlog"||g.type==="instagram_token_expired"||g.type==="oauth_failed")&&onNavigate&&
            <button onClick={e=>{e.stopPropagation();onNavigate(g.type==="review_backlog"?"queue":"settings");}} style={{background:"#f8faf9",color:DARK,border:`1px solid ${BORDER}`,borderRadius:7,padding:"4px 10px",fontWeight:700,fontSize:11,cursor:"pointer",whiteSpace:"nowrap"}}>{g.type==="review_backlog"?"Open queue":"Open settings"}</button>}
          <button disabled={busy} onClick={e=>{e.stopPropagation();dismissAll(g);}} title="Dismiss all of these" style={{background:"none",border:"none",color:MUTED,fontSize:11,cursor:busy?"wait":"pointer",padding:"2px 6px"}}>dismiss all</button>
          <span style={{color:MUTED,fontSize:11,width:14,textAlign:"center"}}>{open?"▾":"▸"}</span>
        </div>
        {open&&<div style={{paddingBottom:8,display:"flex",flexDirection:"column",gap:6}}>
          {g.items.map(n=>(<div key={n.id} style={{background:t.bg,borderRadius:8,padding:"8px 12px",display:"flex",gap:10,alignItems:"flex-start"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:700,color:t.text,fontSize:12}}>{n.title}</div>
              <div style={{fontSize:11,color:t.text,opacity:0.85,marginTop:2,lineHeight:1.45,whiteSpace:"pre-wrap"}}>{n.message}</div>
              <div style={{fontSize:10,color:MUTED,marginTop:3}}>{new Date(n.created_at).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})}</div>
            </div>
            <button onClick={()=>onDismiss?.(n.id)} title="Dismiss" style={{border:"none",background:"none",color:t.text,opacity:0.5,fontSize:16,cursor:"pointer",padding:"0 4px",lineHeight:1}}>×</button>
          </div>))}
        </div>}
      </div>);})}
  </div>);
}
function Dashboard({allCreators=[],kanbanData={},flows=[],activityLog=[],onNavigate,brand={},integrations={},brandId=null,onSelectCreator,campaigns=[],brandNotifications=[],onDismissNotification}){
  const[hovered,setHovered]=useState(null);
  const[range,setRange]=useState("30d");
  const[hideOnboarding,setHideOnboarding]=useLocalStorage("ios_onboarding_hidden",false);
  const byStatus=s=>allCreators.filter(c=>!c.archived&&c.status===s).length;
  // Handles whose next step already sits in email_queue as pending with a
  // scheduled_for that isn't badly past (>24h grace for daily-cap slippage).
  // Those are NOT "didn't auto-send" — the worker has them scheduled and will
  // send them (e.g. an outage backlog draining at the campaign's daily rate).
  // The banner should only name creators the worker has genuinely dropped:
  // no pending row at all, or a pending row stuck >24h past its send time.
  // null = still loading → hide the banner rather than flash a false alarm.
  const[queuedFollowUpHandles,setQueuedFollowUpHandles]=useState(null);
  useEffect(()=>{
    if(!brandId){setQueuedFollowUpHandles(new Set());return;}
    let cancelled=false;
    (async()=>{
      const{data}=await supabase.from('email_queue')
        .select('creator_handle, scheduled_for')
        .eq('brand_id',brandId).eq('status','pending').gte('step_index',0)
        .limit(3000);
      if(cancelled)return;
      const graceCutoff=Date.now()-24*3600*1000;
      const s=new Set();
      (data||[]).forEach(r=>{if(r.scheduled_for&&new Date(r.scheduled_for).getTime()>=graceCutoff)s.add(r.creator_handle);});
      setQueuedFollowUpHandles(s);
    })();
    return()=>{cancelled=true;};
  },[brandId]);
  const dueFollowUps=queuedFollowUpHandles===null?[]:allCreators.filter(c=>!c.archived&&isFollowUpDue(c.handle,activityLog,flows)&&!queuedFollowUpHandles.has(c.handle));
  // Awaiting reply: creator wrote back, ball is in our court. Overdue = >3d sitting on it.
  const awaitingReplyAll=allCreators.filter(c=>!c.archived&&{Overdue:1,"To Do":1}[getKanbanSubStatus(c,c.status,activityLog)]);
  const overdueReplies=awaitingReplyAll.filter(c=>getKanbanSubStatus(c,c.status,activityLog)==="Overdue");

  // ---------- Real-data metrics for the premium dashboard ----------
  // Pull email_queue + activity_log for accurate counts (the in-memory
  // activityLog may be capped at 500 entries). Period-over-period comparisons
  // for KPI trend arrows.
  const [metrics, setMetrics] = useState(null);
  const rangeDays = range === "7d" ? 7 : range === "30d" ? 30 : range === "90d" ? 90 : 365;

  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    (async () => {
      const now = new Date();
      const periodStart = new Date(now.getTime() - rangeDays * 86400000);
      const prevStart = new Date(now.getTime() - 2 * rangeDays * 86400000);
      // Both periods fetched in one query — split client-side.
      const { data: sent } = await supabase.from('email_queue')
        .select('id, status, sent_at, scheduled_for, flow_id, step_index, creator_handle')
        .eq('brand_id', brandId)
        .eq('status', 'sent')
        .gte('sent_at', prevStart.toISOString())
        .order('sent_at', { ascending: false })
        .limit(5000);
      const { data: replies } = await supabase.from('activity_log')
        .select('creator_handle, ts')
        .eq('brand_id', brandId)
        .eq('type', 'email_received')
        .gte('ts', prevStart.toISOString())
        .limit(5000);
      if (cancelled) return;

      const inWindow = (ts, start, end) => { const t = new Date(ts).getTime(); return t >= start.getTime() && t < end.getTime(); };
      const cur = { sent: 0, replies: 0, sentByFlow: new Map(), repliesByFlow: new Map(), creatorsByFlow: new Map() };
      const prev = { sent: 0, replies: 0 };
      // PEOPLE, not emails. "Outreach sent" counts distinct creators who got a
      // cold/follow-up flow step (step_index >= 0) — auto-replies, nudges and
      // shipping confirmations don't inflate it. Reply rate = distinct repliers
      // over distinct creators outreached.
      const curOutreached = new Set(), prevOutreached = new Set();
      const curRepliers = new Set(), prevRepliers = new Set();
      const sentCreatorByFlow = new Map(); // flow_id -> Set<handle> (current period)
      const allFlowFirstSendByHandle = new Map(); // handle -> {flow_id, ts} for reply attribution

      for (const r of (sent || [])) {
        if (inWindow(r.sent_at, periodStart, now)) {
          cur.sent++;
          if (typeof r.step_index === 'number' && r.step_index >= 0) curOutreached.add(r.creator_handle);
          if (r.flow_id) {
            cur.sentByFlow.set(r.flow_id, (cur.sentByFlow.get(r.flow_id) || 0) + 1);
            const set = sentCreatorByFlow.get(r.flow_id) || new Set();
            set.add(r.creator_handle);
            sentCreatorByFlow.set(r.flow_id, set);
            const existing = allFlowFirstSendByHandle.get(r.creator_handle);
            const ts = new Date(r.sent_at).getTime();
            if (!existing || ts < existing.ts) allFlowFirstSendByHandle.set(r.creator_handle, { flow_id: r.flow_id, ts });
          }
        } else if (inWindow(r.sent_at, prevStart, periodStart)) {
          prev.sent++;
          if (typeof r.step_index === 'number' && r.step_index >= 0) prevOutreached.add(r.creator_handle);
        }
      }
      for (const r of (replies || [])) {
        if (inWindow(r.ts, periodStart, now)) {
          cur.replies++;
          curRepliers.add(r.creator_handle);
          const first = allFlowFirstSendByHandle.get(r.creator_handle);
          if (first && new Date(r.ts).getTime() > first.ts) {
            cur.repliesByFlow.set(first.flow_id, (cur.repliesByFlow.get(first.flow_id) || 0) + 1);
          }
        } else if (inWindow(r.ts, prevStart, periodStart)) {
          prev.replies++;
          prevRepliers.add(r.creator_handle);
        }
      }
      cur.outreached = curOutreached.size; prev.outreached = prevOutreached.size;
      cur.repliers = curRepliers.size; prev.repliers = prevRepliers.size;
      const flowMetrics = (flows || []).map(f => {
        const sentN = cur.sentByFlow.get(f.id) || 0;
        const creatorsN = (sentCreatorByFlow.get(f.id) || new Set()).size;
        const repliesN = cur.repliesByFlow.get(f.id) || 0;
        return { id: f.id, name: f.name, type: f.type, is_active: f.is_active !== false, sent: sentN, creators: creatorsN, replies: repliesN, replyRate: creatorsN > 0 ? repliesN / creatorsN : 0 };
      }).filter(f => f.sent > 0).sort((a, b) => b.sent - a.sent);

      if (!cancelled) setMetrics({ cur, prev, flowMetrics });
    })();
    return () => { cancelled = true; };
  }, [brandId, rangeDays, flows]);

  // KPI cards with trend deltas. % change vs previous identical-length period.
  const replyRate = metrics?.cur.outreached > 0 ? metrics.cur.repliers / metrics.cur.outreached : 0;
  const prevReplyRate = metrics?.prev.outreached > 0 ? metrics.prev.repliers / metrics.prev.outreached : 0;
  const pctChange = (curr, prev) => prev === 0 ? (curr > 0 ? 100 : 0) : ((curr - prev) / prev) * 100;

  // Funnel: hardcoded order, percentages relative to outreach top.
  // Status taxonomy collapsed to 6 stages (migration 0052):
  //   Outreached → Negotiation (working out deal) → Awaiting Post (product en route / brief sent) → Posted
  //   Paid Only is a side branch.
  const outreachCount = byStatus("Outreached") + byStatus("Negotiation") + byStatus("Awaiting Post") + byStatus("Posted") + byStatus("Paid Only");
  const replyCount = metrics?.cur.repliers || 0; // distinct creators who replied — the funnel counts people, not emails
  const paidOnlyCount = byStatus("Paid Only");
  const negotiationCount = byStatus("Negotiation") + byStatus("Awaiting Post") + byStatus("Posted");
  const awaitingPostCount = byStatus("Awaiting Post") + byStatus("Posted");
  const postedCount = byStatus("Posted");
  const funnelStages = [
    { label: "Outreach sent", count: outreachCount, color: "#6B7280" },
    { label: "Replied", count: replyCount, color: "#3B82F6" },
    { label: "Paid only inquiries", count: paidOnlyCount, color: "#EF4444", branch: true },
    { label: "Negotiation", count: negotiationCount, color: "#F59E0B" },
    { label: "Awaiting post", count: awaitingPostCount, color: "#06B6D4" },
    { label: "Posted", count: postedCount, color: G },
  ];
  const funnelMax = Math.max(1, ...funnelStages.map(s => s.count));

  // Top performers: posted creators sorted by followers (proxy for reach). Cap at 10.
  const topCreators = useMemo(() => {
    return [...allCreators]
      .filter(c => !c.archived && (c.status === "Posted" || c.status === "Awaiting Post"))
      .sort((a, b) => {
        const fa = typeof a.followers === 'number' ? a.followers : parseFollowers(a.followersF || a.followers || '0');
        const fb = typeof b.followers === 'number' ? b.followers : parseFollowers(b.followersF || b.followers || '0');
        return fb - fa;
      })
      .slice(0, 10);
  }, [allCreators]);

  // Total estimated reach = sum of followers across creators who posted.
  const estimatedReach = useMemo(() => allCreators
    .filter(c => !c.archived && c.status === "Posted")
    .reduce((s, c) => s + (typeof c.followers === 'number' ? c.followers : parseFollowers(c.followersF || c.followers || '0')), 0), [allCreators]);
  const formatReach = (n) => n >= 1_000_000 ? `${(n/1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n/1000).toFixed(1)}K` : String(n);

  // Emails-sent-per-campaign breakdown. Each campaign's kanban (localStorage) lists creators;
  // we look up each creator's email_sent count in activityLog and sum per campaign.
  // A creator in multiple campaigns counts toward each (intentional — same email IS the
  // contribution to each campaign's effort).
  // Send volume per day, stacked by campaign. Each day = one column;
  // segments within a column = campaigns. Hover the column to see breakdown.
  // Date range filter (7/30/90/365/custom) drives both the # of columns and the
  // back-window for activityLog filtering. Mirrors Influencer Hero's layout.
  const [campaignVolumeRange, setCampaignVolumeRange] = useState('30d');
  const [customRange, setCustomRange] = useState({from:'',to:''}); // YYYY-MM-DD strings
  const campaignSendDaily = useMemo(() => {
    const palette = ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4", "#EC4899", "#84CC16", "#F97316", "#6366F1"];

    // Figure out the window
    let days, fromTs, toTs;
    if (campaignVolumeRange === 'custom' && customRange.from && customRange.to) {
      fromTs = new Date(customRange.from + 'T00:00:00').getTime();
      toTs = new Date(customRange.to + 'T23:59:59').getTime();
      days = Math.max(1, Math.ceil((toTs - fromTs) / 86400000));
    } else {
      days = campaignVolumeRange === '7d' ? 7 : campaignVolumeRange === '30d' ? 30 : campaignVolumeRange === '90d' ? 90 : 365;
      toTs = Date.now();
      fromTs = toTs - days * 86400000;
    }

    // Build creator→campaigns map (a creator can be in multiple).
    const handleToCampaigns = new Map();
    for (const camp of campaigns) {
      for (const item of (kanbanData[camp.id] || [])) {
        const set = handleToCampaigns.get(item.handle) || new Set();
        set.add(camp.id);
        handleToCampaigns.set(item.handle, set);
      }
    }

    // Day buckets — one entry per day in the range, keyed by YYYY-MM-DD.
    const buckets = [];
    for (let i = 0; i < days; i++) {
      const d = new Date(toTs - (days - 1 - i) * 86400000);
      d.setHours(0,0,0,0);
      buckets.push({ ts: d.getTime(), date: d, key: d.toISOString().slice(0,10), perCampaign: {}, total: 0 });
    }
    const bucketByDate = new Map(buckets.map(b => [b.key, b]));

    // Assign each email_sent event to its bucket(s).
    let grandTotal = 0;
    for (const e of activityLog) {
      if (e.type !== 'email_sent') continue;
      const t = new Date(e.ts).getTime();
      if (t < fromTs || t > toTs) continue;
      const key = new Date(new Date(e.ts).setHours(0,0,0,0)).toISOString().slice(0,10);
      const bucket = bucketByDate.get(key);
      if (!bucket) continue;
      bucket.total++;
      grandTotal++;
      const camps = handleToCampaigns.get(e.handle);
      if (camps && camps.size > 0) {
        for (const cid of camps) bucket.perCampaign[cid] = (bucket.perCampaign[cid] || 0) + 1;
      } else {
        bucket.perCampaign['_uncat'] = (bucket.perCampaign['_uncat'] || 0) + 1;
      }
    }

    // Which campaigns actually had ANY sends in the window (for legend + colouring).
    const usedCampIds = new Set();
    for (const b of buckets) for (const cid of Object.keys(b.perCampaign)) usedCampIds.add(cid);
    const legend = [];
    let colorIdx = 0;
    for (const camp of campaigns) {
      if (usedCampIds.has(camp.id)) {
        legend.push({ id: camp.id, name: camp.name, color: palette[colorIdx++ % palette.length] });
      }
    }
    if (usedCampIds.has('_uncat')) legend.push({ id: '_uncat', name: 'Not in a campaign', color: '#9CA3AF' });

    const max = Math.max(1, ...buckets.map(b => b.total));
    return { buckets, legend, max, grandTotal, days };
  }, [campaigns, kanbanData, activityLog, campaignVolumeRange, customRange]);
  const [hoveredCol, setHoveredCol] = useState(null);

  const pipeline=[
    {label:"To Outreach",   count:byStatus("To Outreach"),   color:"#A78BFA",sub:"Queued"},
    {label:"Outreached",    count:byStatus("Outreached"),    color:"#6B7280",sub:"Contacted"},
    {label:"Negotiation",   count:byStatus("Negotiation"),   color:"#3B82F6",sub:"Working out details"},
    {label:"Paid Only",     count:byStatus("Paid Only"),     color:"#EF4444",sub:"Need terms"},
    {label:"Awaiting Post", count:byStatus("Awaiting Post"), color:"#F59E0B",sub:"Briefed / shipped"},
    {label:"Posted",        count:byStatus("Posted"),        color:G,        sub:"Live"},
  ];

  // Build real chart data from activityLog + allCreators creation dates
  const chartData=useMemo(()=>{
    const days=range==="7d"?7:range==="30d"?30:range==="90d"?90:365;
    const now=new Date();
    const buckets=[];
    for(let i=days-1;i>=0;i--){
      const d=new Date(now);d.setDate(d.getDate()-i);d.setHours(0,0,0,0);
      const dEnd=new Date(d);dEnd.setDate(dEnd.getDate()+1);
      const label=days<=7
        ?d.toLocaleDateString("en-AU",{weekday:"short"})
        :days<=30
        ?d.toLocaleDateString("en-AU",{day:"numeric",month:"short"})
        :d.toLocaleDateString("en-AU",{day:"numeric",month:"short"});
      // Count creators added/outreached this day from activityLog
      const outreachCount=activityLog.filter(e=>{
        if(e.type!=="status")return false;
        const ts=new Date(e.ts);
        return ts>=d&&ts<dEnd&&(e.text||"").includes("Outreached");
      }).length;
      // Count emails sent
      const emailCount=activityLog.filter(e=>{
        if(e.type!=="email_sent")return false;
        const ts=new Date(e.ts);
        return ts>=d&&ts<dEnd;
      }).length;
      // Green bar = creators outreached this day (real, from activityLog). The old
      // "creators added" series counted by numeric-timestamp id, but DB creators
      // have UUID ids, so it was always 0 — removed rather than show fake bars.
      buckets.push({label,outreach:outreachCount,emails:emailCount,total:outreachCount});
    }
    return buckets;
  },[activityLog,range]);

  // If no real data, show a helpful empty state instead of flat zero bars
  const hasRealData=chartData.some(d=>d.total>0||d.emails>0);
  const maxVal=hasRealData?Math.max(1,...chartData.map(d=>Math.max(d.total,d.emails))):5;

  // Thin out labels for longer ranges
  const showLabel=(i,total)=>{
    if(total<=14)return true;
    if(total<=30)return i%5===0||i===total-1;
    return i%15===0||i===total-1;
  };

  // Onboarding checklist
  const checks=[
    {id:"brand",label:"Set up your brand profile",desc:"Add your domain, Instagram, sender name, email",done:!!(brand?.domain||brand?.instagram||brand?.senderName),nav:"settings",icon:"🏢"},
    {id:"inbox",label:"Add your first inbox",desc:"Configure Gmail account for outreach sending",done:!!(brand?.inboxes?.length>0),nav:"settings",icon:"📬"},
    {id:"flows",label:"Create an email flow",desc:"Set up your outreach sequence and templates",done:flows.length>0,nav:"flows",icon:"✉️"},
    {id:"creators",label:"Add your first creator",desc:"Import creators via CSV or add manually",done:allCreators.filter(c=>!c._isDefault).length>0||allCreators.length>0,nav:"creators",icon:"👤"},
    {id:"campaign",label:"Create a campaign",desc:"Organise creators into an outreach campaign",done:Object.keys(kanbanData).length>0,nav:"campaigns",icon:"📣"},
    {id:"shopify",label:"Connect Shopify",desc:"Auto-fill orders, track sales per creator",done:!!(brand?.integrations?.Shopify?.connected),nav:"integrations",icon:"🛍️",postDeploy:false},
    {id:"gmail",label:"Connect Gmail",desc:"Send real emails from within the app",done:!!(brand?.integrations?.Gmail?.connected),nav:"integrations",icon:"📧",postDeploy:true},
  ];
  const completedCount=checks.filter(c=>c.done).length;
  const allDone=completedCount===checks.length;

  return(<div style={{display:"flex",flexDirection:"column",gap:20}}>
    {/* Setup banner — points at the new Onboarding Wizard. The wizard is the source
        of truth for brand setup state; this banner just nudges users to go there. */}
    {!hideOnboarding&&!allDone&&(
      <div style={{background:G+"08",border:`1.5px solid ${G}40`,borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",gap:12}}>
        <span style={{fontSize:20}}>🚀</span>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,color:DARK,fontSize:14}}>Finish setting up {brand?.name||"your brand"}</div>
          <div style={{fontSize:12,color:MUTED,marginTop:2}}>Step through the onboarding to get Gmail + Shopify wired, gifting catalog defined, and go live.</div>
        </div>
        <button onClick={()=>setHideOnboarding(true)} style={{border:"none",background:"none",cursor:"pointer",color:MUTED,fontSize:11,padding:"4px 8px"}} onMouseEnter={e=>e.currentTarget.style.color=DARK} onMouseLeave={e=>e.currentTarget.style.color=MUTED}>Dismiss</button>
        <button onClick={()=>onNavigate&&onNavigate("setup")} style={{background:DARK,color:G,border:"none",borderRadius:8,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>Open Setup →</button>
      </div>
    )}
    {/* Brand operator notifications, grouped. One line per kind with a count —
        the per-message cards that used to stack here (unknown senders, outside-
        thread mail, order failures, extra requests…) buried the dashboard.
        Expand a line to read or dismiss the individual items. */}
    <NotificationDigest items={brandNotifications} onDismiss={onDismissNotification} onNavigate={onNavigate}/>
    })}
    {awaitingReplyAll.length>0&&(()=>{const isCritical=overdueReplies.length>0;const colors=isCritical?{bg:"#FEF2F2",border:"#FECACA",text:"#991B1B",accent:"#B91C1C",btn:"#DC2626"}:{bg:"#EFF6FF",border:"#BFDBFE",text:"#1E40AF",accent:"#1D4ED8",btn:"#3B82F6"};const label=isCritical?`${overdueReplies.length} reply${overdueReplies.length!==1?"s":""} overdue — sitting >3 days`:`${awaitingReplyAll.length} creator${awaitingReplyAll.length!==1?"s":""} awaiting your reply`;return(
      <div style={{background:colors.bg,border:`1px solid ${colors.border}`,borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",gap:12}}>
        <span style={{fontSize:20}}>{isCritical?"🚨":"📩"}</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,color:colors.text,fontSize:14}}>{label}</div>
          <div style={{fontSize:12,color:colors.accent,marginTop:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{awaitingReplyAll.slice(0,6).map(c=>c.name).join(", ")}{awaitingReplyAll.length>6?` +${awaitingReplyAll.length-6} more`:""}</div>
        </div>
        <button onClick={()=>onNavigate&&onNavigate("creators","awaiting-reply")} style={{background:colors.btn,color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>Open inbox →</button>
      </div>
    );})()}
    {dueFollowUps.length>0&&(
      <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",gap:12}}>
        <span style={{fontSize:20}}>⏰</span>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,color:"#92400E",fontSize:14}}>{dueFollowUps.length} overdue follow-up{dueFollowUps.length!==1?"s":""} that didn't auto-send</div>
          <div style={{fontSize:12,color:"#B45309",marginTop:3}}>Normally the system sends these automatically — these are 2+ days overdue, so the flow may be paused or the inbox disconnected: {dueFollowUps.map(c=>c.name).join(", ")}</div>
        </div>
        <button onClick={()=>onNavigate&&onNavigate("creators")} style={{background:"#92400E",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>View Creators →</button>
      </div>
    )}
    {(()=>{const paidInquiries=allCreators.filter(c=>!c.archived&&c.review_reason==="paid_only_inquiry");return paidInquiries.length>0&&(
      <div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",gap:12}}>
        <span style={{fontSize:20}}>💰</span>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,color:"#991B1B",fontSize:14}}>{paidInquiries.length} paid-only inquir{paidInquiries.length!==1?"ies":"y"} — account manager review</div>
          <div style={{fontSize:12,color:"#B91C1C",marginTop:3}}>{paidInquiries.map(c=>c.name).join(", ")}</div>
        </div>
        <button onClick={()=>onNavigate&&onNavigate("creators")} style={{background:"#DC2626",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>Review →</button>
      </div>
    );})()}
    {/* HERO KPI ROW — period-over-period comparison drives the trend arrows */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:14}}>
      {(()=>{
        const kpis = [
          { label: "Creators outreached", value: metrics?.cur.outreached ?? "—", prev: metrics?.prev.outreached, fmt: v => typeof v === 'number' ? v.toLocaleString() : v, color: DARK, accent: "#6B7280" },
          { label: "Reply rate", value: metrics ? `${(replyRate * 100).toFixed(1)}%` : "—", prev: metrics ? prevReplyRate * 100 : null, current: metrics ? replyRate * 100 : null, fmt: v => typeof v === 'number' ? `${v.toFixed(1)}pp` : v, color: "#3B82F6", accent: "#3B82F6", deltaSuffix: "pp", isAbs: true },
          { label: "In negotiation", value: negotiationCount, prev: null, fmt: v => v, color: "#F59E0B", accent: "#F59E0B" },
          { label: "Posted", value: postedCount, prev: null, fmt: v => v, color: G, accent: G },
          { label: "Est. reach", value: formatReach(estimatedReach), prev: null, fmt: v => v, color: "#8B5CF6", accent: "#8B5CF6" },
        ];
        return kpis.map((k, i) => {
          let deltaLabel = null, deltaColor = MUTED;
          if (k.prev != null && typeof k.value === 'number') {
            const change = pctChange(k.value, k.prev);
            if (Math.abs(change) > 0.5) {
              deltaLabel = `${change > 0 ? '↑' : '↓'} ${Math.abs(change).toFixed(0)}%`;
              deltaColor = change > 0 ? "#16a34a" : "#dc2626";
            } else if (k.prev > 0 && k.value > 0) {
              deltaLabel = "flat";
            }
          } else if (k.isAbs && k.prev != null) {
            const change = (k.current||0) - k.prev;
            if (Math.abs(change) > 0.1) {
              deltaLabel = `${change > 0 ? '↑' : '↓'} ${Math.abs(change).toFixed(1)}pp`;
              deltaColor = change > 0 ? "#16a34a" : "#dc2626";
            }
          }
          return (<Card key={i} style={{padding:"16px 18px",background:"#fff",border:`1px solid ${BORDER}`,position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:k.accent}}/>
            <div style={{fontSize:11,color:MUTED,textTransform:"uppercase",letterSpacing:".07em",fontWeight:600,marginBottom:6}}>{k.label}</div>
            <div style={{fontSize:30,fontWeight:800,color:DARK,lineHeight:1,letterSpacing:"-.03em"}}>{k.fmt(k.value)}</div>
            {deltaLabel ? <div style={{fontSize:11,color:deltaColor,marginTop:8,fontWeight:600}}>{deltaLabel} vs prev {range}</div>
              : <div style={{fontSize:11,color:MUTED,marginTop:8,fontWeight:500}}>—</div>}
          </Card>);
        });
      })()}
    </div>

    {/* CONVERSION FUNNEL — visual flow from outreach to content posted */}
    <Card style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:18}}>
        <div>
          <div style={{fontWeight:700,color:DARK,fontSize:15}}>Conversion funnel</div>
          <div style={{fontSize:12,color:MUTED,marginTop:2}}>From cold outreach to live content</div>
        </div>
        <div style={{fontSize:11,color:MUTED,fontWeight:500}}>End-to-end rate: <strong style={{color:DARK}}>{outreachCount > 0 ? ((postedCount/outreachCount)*100).toFixed(1) : "0.0"}%</strong></div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {funnelStages.map((s, i) => {
          const pct = funnelMax > 0 ? (s.count / funnelMax) * 100 : 0;
          // For branch stages (paid-only — comes off "Replied"), use replies as denominator.
          // For main funnel steps, use the previous non-branch stage.
          let denomLabel = null, denomVal = 0;
          if (s.branch) { denomLabel = "of replies"; denomVal = replyCount; }
          else if (i > 0) {
            // Find previous non-branch stage
            for (let j = i - 1; j >= 0; j--) {
              if (!funnelStages[j].branch) { denomLabel = "of prev"; denomVal = funnelStages[j].count; break; }
            }
          }
          const stagePct = denomVal > 0 ? (s.count / denomVal) * 100 : null;
          return(<div key={i} style={{display:"flex",alignItems:"center",gap:14,opacity:s.branch?0.85:1}}>
            <div style={{width:160,fontSize:12,fontWeight:600,color:DARK,flexShrink:0,display:"flex",alignItems:"center",gap:6}}>
              {s.branch&&<span style={{color:"#9CA3AF",fontSize:14,lineHeight:1}}>↳</span>}
              {s.label}
            </div>
            <div style={{flex:1,position:"relative",height:32,background:"#f8faf9",borderRadius:6,overflow:"hidden"}}>
              <div style={{position:"absolute",inset:0,width:`${pct}%`,background:`linear-gradient(90deg, ${s.color}26 0%, ${s.color}40 100%)`,borderRight:`3px solid ${s.color}`,display:"flex",alignItems:"center",paddingLeft:12,transition:"width .4s"}}>
                <span style={{fontSize:13,fontWeight:700,color:s.color}}>{s.count.toLocaleString()}</span>
              </div>
            </div>
            <div style={{width:110,textAlign:"right",fontSize:11,color:MUTED,fontWeight:500,flexShrink:0}}>
              {i === 0 ? "—" : stagePct != null ? `${stagePct.toFixed(1)}% ${denomLabel}` : "—"}
            </div>
          </div>);
        })}
      </div>
    </Card>

    {/* SEND VOLUME PER DAY — stacked column chart, segments coloured by campaign.
        Hover a column to see per-campaign breakdown for that day. Date range
        filter at top. Inspired by Influencer Hero's daily volume view. */}
    <Card style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontWeight:700,color:DARK,fontSize:15}}>Send volume by campaign</div>
          <div style={{fontSize:12,color:MUTED,marginTop:2}}>Daily email send volume, stacked by campaign. Hover a column for the day's breakdown.</div>
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          <div style={{display:"flex",gap:3,background:"#f8faf9",borderRadius:8,padding:3}}>
            {[{l:"7d",v:"7d"},{l:"30d",v:"30d"},{l:"90d",v:"90d"},{l:"365d",v:"365d"},{l:"Custom",v:"custom"}].map(r=>(
              <button key={r.v} onClick={()=>setCampaignVolumeRange(r.v)} style={{padding:"5px 11px",borderRadius:6,border:"none",background:campaignVolumeRange===r.v?"#fff":"transparent",color:campaignVolumeRange===r.v?DARK:MUTED,fontSize:11,cursor:"pointer",fontWeight:campaignVolumeRange===r.v?700:500,boxShadow:campaignVolumeRange===r.v?"0 1px 4px rgba(0,0,0,0.08)":"none"}}>{r.l}</button>
            ))}
          </div>
        </div>
      </div>
      {campaignVolumeRange==='custom'&&(
        <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:14,padding:"10px 12px",background:"#f8faf9",borderRadius:8}}>
          <span style={{fontSize:12,color:MUTED}}>From:</span>
          <input type="date" value={customRange.from} onChange={e=>setCustomRange(r=>({...r,from:e.target.value}))} style={{padding:"5px 9px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,outline:"none"}}/>
          <span style={{fontSize:12,color:MUTED}}>To:</span>
          <input type="date" value={customRange.to} onChange={e=>setCustomRange(r=>({...r,to:e.target.value}))} style={{padding:"5px 9px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,outline:"none"}}/>
          {(!customRange.from||!customRange.to)&&<span style={{fontSize:11,color:"#92400E",fontStyle:"italic"}}>Pick a date range</span>}
        </div>
      )}
      <div style={{fontSize:11,color:MUTED,fontWeight:500,marginBottom:10}}>Total in window: <strong style={{color:DARK}}>{campaignSendDaily.grandTotal.toLocaleString()}</strong> email{campaignSendDaily.grandTotal!==1?"s":""} sent · {campaignSendDaily.days} days</div>
      {campaignSendDaily.grandTotal===0?
        <div style={{padding:"30px 0",textAlign:"center",color:MUTED,fontSize:13}}>No emails sent in this window.</div>
        :<>
        {/* The columns — relative position so we can absolute-position the hover tooltip */}
        <div style={{position:"relative",height:180,display:"flex",alignItems:"flex-end",gap:1,padding:"0 2px"}}>
          {campaignSendDaily.buckets.map((b,i)=>{
            const pct = b.total/campaignSendDaily.max;
            const heightPx = b.total>0 ? Math.max(2, pct*170) : 2;
            return(<div key={b.key} style={{flex:1,height:"100%",display:"flex",flexDirection:"column",justifyContent:"flex-end",cursor:b.total>0?"default":"default",position:"relative"}}
              onMouseEnter={()=>setHoveredCol(i)} onMouseLeave={()=>setHoveredCol(null)}>
              {/* Stacked segments within this column */}
              <div style={{height:heightPx,display:"flex",flexDirection:"column-reverse",borderRadius:"3px 3px 0 0",overflow:"hidden",border:hoveredCol===i?"1px solid #1a1a1a":"none"}}>
                {b.total===0&&<div style={{height:2,background:"#e5e7eb"}}/>}
                {campaignSendDaily.legend.map(camp=>{
                  const v = b.perCampaign[camp.id] || 0;
                  if(v===0)return null;
                  const segHeight = (v/b.total)*heightPx;
                  return(<div key={camp.id} style={{height:segHeight,background:camp.color,transition:"height .25s"}}/>);
                })}
              </div>
              {/* Tooltip on hover */}
              {hoveredCol===i&&b.total>0&&(
                <div style={{position:"absolute",bottom:"calc(100% + 6px)",left:"50%",transform:"translateX(-50%)",background:DARK,color:"#fff",borderRadius:8,padding:"8px 11px",fontSize:11,minWidth:200,zIndex:10,boxShadow:"0 8px 24px rgba(0,0,0,0.25)",pointerEvents:"none"}}>
                  <div style={{fontWeight:700,color:G,marginBottom:5}}>{b.date.toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric",weekday:"short"})}</div>
                  <div style={{borderBottom:"1px solid rgba(255,255,255,0.15)",marginBottom:6,paddingBottom:5,fontWeight:600}}>Total: {b.total.toLocaleString()}</div>
                  {campaignSendDaily.legend.map(camp=>{
                    const v = b.perCampaign[camp.id] || 0;
                    if(v===0)return null;
                    return(<div key={camp.id} style={{display:"flex",alignItems:"center",gap:6,padding:"2px 0"}}>
                      <span style={{width:8,height:8,borderRadius:2,background:camp.color,flexShrink:0}}/>
                      <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{camp.name}</span>
                      <strong>{v}</strong>
                    </div>);
                  })}
                </div>
              )}
            </div>);
          })}
        </div>
        {/* X-axis labels */}
        <div style={{display:"flex",padding:"6px 2px 0"}}>
          {campaignSendDaily.buckets.map((b,i)=>{
            const showLabel = campaignSendDaily.days<=14 || (campaignSendDaily.days<=30 && i%5===0) || (campaignSendDaily.days<=90 && i%15===0) || (i%60===0) || i===campaignSendDaily.buckets.length-1;
            return(<div key={b.key} style={{flex:1,textAlign:"center",fontSize:9,color:MUTED,fontWeight:500,overflow:"hidden",whiteSpace:"nowrap"}}>
              {showLabel ? b.date.toLocaleDateString("en-AU",{day:"numeric",month:"short"}) : ""}
            </div>);
          })}
        </div>
        {/* Legend below — clickable for real campaigns */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:8,marginTop:14}}>
          {campaignSendDaily.legend.map(c=>{
            const total = campaignSendDaily.buckets.reduce((s,b)=>s+(b.perCampaign[c.id]||0),0);
            const isReal = c.id !== '_uncat';
            return(<div key={c.id} onClick={()=>{if(isReal&&onNavigate)onNavigate('campaigns');}}
              style={{display:"flex",alignItems:"center",gap:8,padding:"6px 9px",borderRadius:7,border:`1px solid ${BORDER}`,background:"#fff",cursor:isReal?"pointer":"default"}}
              onMouseEnter={e=>{if(isReal)e.currentTarget.style.background="#fafcfb";}}
              onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
              <div style={{width:10,height:10,borderRadius:3,background:c.color,flexShrink:0}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:DARK,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</div>
                <div style={{fontSize:10,color:MUTED}}>{total.toLocaleString()} sent</div>
              </div>
            </div>);
          })}
        </div>
        </>
      }
    </Card>

    <Card style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontWeight:700,color:DARK,fontSize:15}}>Creator activity</div>
          <div style={{fontSize:12,color:"#9ab8a6",marginTop:2}}>Creators outreached & emails sent per day</div>
        </div>
        <div style={{display:"flex",gap:4,background:"#f8faf9",borderRadius:9,padding:3}}>
          {["7d","30d","90d","365d"].map(r=>(
            <button key={r} onClick={()=>setRange(r)} style={{padding:"5px 11px",borderRadius:7,border:"none",background:range===r?"#fff":"transparent",color:range===r?DARK:"#9ab8a6",fontSize:11,cursor:"pointer",fontWeight:range===r?600:400,boxShadow:range===r?"0 1px 4px rgba(0,0,0,0.08)":"none"}}>{r}</button>
          ))}
        </div>
      </div>

      {!hasRealData
        ?<div style={{height:140,display:"flex",alignItems:"center",justifyContent:"center",color:"#9ab8a6",fontSize:13,flexDirection:"column",gap:8}}>
            <div style={{fontSize:28}}>📊</div>
            <div>No activity yet — data will appear here as you add creators and send emails</div>
          </div>
        :<>
          <div style={{display:"flex",alignItems:"flex-end",gap:2,height:140,padding:"0 2px"}}>
            {chartData.map((d,i)=>(
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",position:"relative",height:"100%",justifyContent:"flex-end"}} onMouseEnter={()=>setHovered(i)} onMouseLeave={()=>setHovered(null)}>
                {hovered===i&&(
                  <div style={{position:"absolute",bottom:"105%",left:"50%",transform:"translateX(-50%)",background:DARK,color:"#fff",borderRadius:10,padding:"8px 12px",fontSize:11,whiteSpace:"nowrap",zIndex:10,boxShadow:"0 8px 24px rgba(0,0,0,0.2)",pointerEvents:"none"}}>
                    <div style={{fontWeight:700,marginBottom:4,color:G}}>{d.label}</div>
                    {d.total>0&&<div>Added/Outreached: <strong>{d.total}</strong></div>}
                    {d.emails>0&&<div>Emails sent: <strong>{d.emails}</strong></div>}
                    {d.total===0&&d.emails===0&&<div style={{color:"#9ab8a6"}}>No activity</div>}
                    <div style={{position:"absolute",bottom:-5,left:"50%",transform:"translateX(-50%)",width:8,height:5,background:DARK,clipPath:"polygon(0 0,100% 0,50% 100%)"}}/>
                  </div>
                )}
                <div style={{width:"100%",display:"flex",gap:1,alignItems:"flex-end",height:"100%"}}>
                  {d.total>0&&<div style={{flex:1,background:hovered===i?G:"#00a862",borderRadius:"3px 3px 0 0",height:`${(d.total/maxVal)*100}%`,transition:"background .15s"}}/>}
                  {d.emails>0&&<div style={{flex:1,background:hovered===i?"#93C5FD":"#60A5FA",borderRadius:"3px 3px 0 0",height:`${(d.emails/maxVal)*100}%`,transition:"background .15s"}}/>}
                  {d.total===0&&d.emails===0&&<div style={{flex:1,background:"#f0f0f0",borderRadius:"3px 3px 0 0",height:"4px"}}/>}
                </div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",padding:"6px 2px 0"}}>
            {chartData.map((d,i)=>(
              <div key={i} style={{flex:1,textAlign:"center",fontSize:9,color:"#9ab8a6",fontWeight:500,overflow:"hidden",whiteSpace:"nowrap"}}>
                {showLabel(i,chartData.length)?d.label:""}
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:14,marginTop:10}}>
            <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"#9ab8a6"}}><span style={{width:8,height:8,borderRadius:2,background:"#00a862",display:"inline-block"}}/>Outreached</div>
            <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"#9ab8a6"}}><span style={{width:8,height:8,borderRadius:2,background:"#60A5FA",display:"inline-block"}}/>Emails sent</div>
          </div>
        </>
      }
    </Card>

    <Card style={{padding:24}}>
      <div style={{fontWeight:700,marginBottom:4,color:DARK,fontSize:15}}>Creator pipeline</div>
      <div style={{fontSize:12,color:"#9ab8a6",marginBottom:18}}>Live counts per stage</div>
      <div style={{display:"flex",gap:4,alignItems:"stretch"}}>
        {pipeline.map((s,i)=>(<div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center"}}>
          <div style={{width:"100%",background:s.color+"15",border:`1px solid ${s.color}30`,borderRadius:12,padding:"18px 8px",textAlign:"center",marginBottom:8}}><div style={{fontSize:26,fontWeight:800,color:s.color,letterSpacing:"-.02em",lineHeight:1}}>{s.count}</div></div>
          <div style={{fontSize:11,color:DARK,fontWeight:600,textAlign:"center",lineHeight:1.3}}>{s.label}</div>
          <div style={{fontSize:10,color:"#9ab8a6",textAlign:"center",marginTop:3,fontStyle:"italic"}}>{s.sub}</div>
        </div>))}
      </div>
    </Card>

    {/* TWO-COLUMN BOTTOM ROW — Top performers + flow performance */}
    <div style={{display:"grid",gridTemplateColumns:"1.2fr 1fr",gap:16}}>
      <Card style={{padding:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14}}>
          <div>
            <div style={{fontWeight:700,color:DARK,fontSize:15}}>Top performers</div>
            <div style={{fontSize:12,color:MUTED,marginTop:2}}>Creators driving the most reach this cycle</div>
          </div>
          <button onClick={()=>onNavigate&&onNavigate('creators')} style={{border:"none",background:"none",color:G,fontSize:11,cursor:"pointer",fontWeight:700}}>View all →</button>
        </div>
        {topCreators.length===0?
          <div style={{padding:"24px 0",textAlign:"center",color:MUTED,fontSize:13}}>No content posted yet — top performers will surface here once creators post.</div>
          :<div style={{display:"flex",flexDirection:"column",gap:1}}>
          {topCreators.map((c,i)=>{
            const followers = typeof c.followers === 'number' ? c.followers : parseFollowers(c.followersF || c.followers || '0');
            const sc = STATUS_COLORS[c.status] || MUTED;
            return(<div key={c.id||c.handle||i} onClick={()=>onSelectCreator&&onSelectCreator(c)} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:i<topCreators.length-1?`1px solid #f0f4f2`:"none",cursor:"pointer"}}
              onMouseEnter={e=>e.currentTarget.style.background="#fafcfb"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{width:24,height:24,borderRadius:6,background:i<3?G+"20":"#f0f4f2",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:i<3?G:MUTED,flexShrink:0}}>{i+1}</div>
              <Avatar name={c.name||c.handle||"?"} color={c.color||"#60A5FA"} size={28}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:DARK,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name||c.handle}</div>
                <div style={{fontSize:11,color:MUTED,marginTop:1}}><HandleLink handle={c.handle} size={11} color={MUTED}/> · {formatReach(followers)} followers</div>
              </div>
              <span style={{background:sc+"18",color:sc,border:`1px solid ${sc}40`,borderRadius:5,padding:"2px 7px",fontSize:10,fontWeight:700,whiteSpace:"nowrap"}}>{c.status}</span>
            </div>);
          })}
        </div>}
      </Card>

      <Card style={{padding:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14}}>
          <div>
            <div style={{fontWeight:700,color:DARK,fontSize:15}}>Flow performance</div>
            <div style={{fontSize:12,color:MUTED,marginTop:2}}>Last {range} · reply rate by flow</div>
          </div>
          <button onClick={()=>onNavigate&&onNavigate('flows')} style={{border:"none",background:"none",color:G,fontSize:11,cursor:"pointer",fontWeight:700}}>Edit flows →</button>
        </div>
        {(!metrics||metrics.flowMetrics.length===0)?
          <div style={{padding:"24px 0",textAlign:"center",color:MUTED,fontSize:13}}>{!metrics?"Loading metrics…":"No flow activity in this period yet."}</div>
          :<div style={{display:"flex",flexDirection:"column",gap:10}}>
          {metrics.flowMetrics.slice(0,6).map(f=>{
            const sortedRates=metrics.flowMetrics.map(x=>x.replyRate).sort((a,b)=>b-a);
            const best=sortedRates[0];
            const isBest=f.replyRate===best&&best>0;
            return(<div key={f.id} style={{padding:"10px 0",borderBottom:`1px solid #f0f4f2`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:5}}>
                <div style={{fontSize:13,fontWeight:600,color:DARK,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:200}}>
                  {f.name}{isBest&&<span style={{fontSize:9,color:G,fontWeight:700,marginLeft:6}}>↑ TOP</span>}
                </div>
                <div style={{fontSize:13,fontWeight:700,color:DARK}}>{(f.replyRate*100).toFixed(1)}%</div>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                <div style={{flex:1,height:5,background:"#f0f0f0",borderRadius:3,overflow:"hidden"}}>
                  <div style={{width:`${best>0?Math.min(100,(f.replyRate/best)*100):0}%`,height:"100%",background:isBest?G:"#60A5FA",transition:"width .3s"}}/>
                </div>
                <div style={{fontSize:10,color:MUTED,fontWeight:500,whiteSpace:"nowrap"}}>{f.sent} sent · {f.replies} repl{f.replies===1?"y":"ies"}</div>
              </div>
            </div>);
          })}
        </div>}
      </Card>
    </div>
  </div>);
}

const STATUS_LIST=["To Outreach","Outreached","Negotiation","Paid Only","Awaiting Post","Posted","Inactive"];


// Generate discount code: BRENTON FORD → BRENTF15
const genDiscountCode=(name="")=>{
  const parts=name.trim().split(/\s+/).filter(Boolean);
  if(!parts.length)return"";
  const first=parts[0].toUpperCase().slice(0,5);
  const lastInit=parts.length>1?parts[parts.length-1][0].toUpperCase():"";
  return first+lastInit+"15";
};

// Parse follower string to number: "41.2K" → 41200, "1.2M" → 1200000
const parseFollowers=(val="")=>{
  if(typeof val==="number") return val;
  const s=String(val).trim().toUpperCase().replace(/,/g,"");
  if(s.endsWith("M")) return parseFloat(s)*1_000_000;
  if(s.endsWith("K")) return parseFloat(s)*1_000;
  return parseFloat(s)||0;
};

function Creators({onSelect,allCreators,setAllCreators,onStatusChange,activityLog=[],flows=[],brand=null,brandId=null,userId=null,campaigns=[],kanbanData={},persistKanbanItem=null,filterHint=null,clearFilterHint=null}){
  const[search,setSearch]=useState("");
  const[statusFilter,setStatusFilter]=useState("All");
  const[showAdd,setShowAdd]=useState(false);
  const[statusMenu,setStatusMenu]=useState(null);
  const[sortCol,setSortCol]=useState("name");
  const[sortDir,setSortDir]=useState("asc");
  const[selected,setSelected]=useState(new Set()); // bulk selection
  const[bulkStatus,setBulkStatus]=useState("");
  const[bulkBusy,setBulkBusy]=useState(false);
  const[bulkCampaignPicker,setBulkCampaignPicker]=useState(false);
  // Pagination — required at scale (2,000+ creators). Renders only the current
  // page; "select all current page" + "select all matching" handle big bulks.
  const[pageSize,setPageSize]=useState(50);
  const[pageIndex,setPageIndex]=useState(0);
  const[selectAllMatching,setSelectAllMatching]=useState(false); // overrides selected when true
  // Add-creator modal is the FIRST STEP — minimal fields (first/last name, IG handle, email).
  // The richer fields (engagement rate, market, discount code, order #, etc.) live on the
  // creator profile page for manual fill-in later.
  const emptyForm={firstName:"",lastName:"",handle:"",email:"",followers:"",status:"To Outreach"};
  const[form,setForm]=useState(emptyForm);
  const setF=(k,v)=>setForm(f=>({...f,[k]:v}));

  const handleSort=col=>{
    if(sortCol===col)setSortDir(d=>d==="asc"?"desc":"asc");
    else{setSortCol(col);setSortDir("asc");}
  };
  const Arrow=({col})=>sortCol!==col
    ?<span style={{color:"#d1d5db",marginLeft:3}}>↕</span>
    :<span style={{color:G,marginLeft:3}}>{sortDir==="asc"?"↑":"↓"}</span>;

  const[showFilters,setShowFilters]=useState(false);
  const[filterMarket,setFilterMarket]=useState("All");
  const[filterFollowers,setFilterFollowers]=useState("All");
  const[filterEng,setFilterEng]=useState("All");
  const[showArchived,setShowArchived]=useState(false);
  const[showNeedsReview,setShowNeedsReview]=useState(false);
  const[showAwaitingReply,setShowAwaitingReply]=useState(false);
  const needsReviewCount=allCreators.filter(c=>!c.archived&&c.review_reason).length;
  const awaitingReplyCount=allCreators.filter(c=>!c.archived&&{Overdue:1,"To Do":1}[getKanbanSubStatus(c,c.status,activityLog)]).length;
  useEffect(()=>{
    if(filterHint==="awaiting-reply"){setShowAwaitingReply(true);setShowNeedsReview(false);setShowArchived(false);setStatusFilter("All");if(clearFilterHint)clearFilterHint();}
  },[filterHint,clearFilterHint]);

  const FOLLOWER_RANGES={"All":null,"1K+":[1000],"5K+":[5000],"10K+":[10000],"25K+":[25000],"50K+":[50000],"100K+":[100000]};
  const ENG_RANGES={"All":null,"1%+":[1],"3%+":[3],"5%+":[5],"8%+":[8]};
  const getFollowers=c=>c.followers>0?c.followers:parseFollowers(c.followersF||"0");
  const getEng=c=>parseFloat(String(c.eng||c.engN||"0").replace("%",""))||0;

  const filtered=allCreators.filter(c=>{
    const isArchived=!!c.archived;
    if(showArchived) return isArchived;
    if(isArchived) return false;
    if(showNeedsReview&&!c.review_reason) return false;
    if(showAwaitingReply&&!{Overdue:1,"To Do":1}[getKanbanSubStatus(c,c.status,activityLog)]) return false;
    const name=(c.name||"").toLowerCase();
    const handle=(c.handle||"").toLowerCase();
    const matchSearch=search===""||name.includes(search.toLowerCase())||handle.includes(search.toLowerCase());
    const matchStatus=statusFilter==="All"||(statusFilter==="💰 Paid Leads"?!!c.paid_database_at:((c.status||"").trim()===statusFilter));
    const matchMarket=filterMarket==="All"||(c.market||"")===filterMarket;
    const fRange=FOLLOWER_RANGES[filterFollowers];
    const matchFollowers=!fRange||getFollowers(c)>=fRange[0];
    const eRange=ENG_RANGES[filterEng];
    const matchEng=!eRange||getEng(c)>=eRange[0];
    return matchSearch&&matchStatus&&matchMarket&&matchFollowers&&matchEng;
  }).sort((a,b)=>{
    const dir=sortDir==="asc"?1:-1;
    const getFollowersS=c=>c.followers>0?c.followers:parseFollowers(c.followersF||"0");
    const getEngS=c=>parseFloat(String(c.eng||c.engN||"0").replace("%",""))||0;
    if(sortCol==="name"){
      // Sort by first name then last name
      const aParts=(a.name||"").trim().split(/\s+/);
      const bParts=(b.name||"").trim().split(/\s+/);
      const firstComp=aParts[0].localeCompare(bParts[0]);
      if(firstComp!==0)return firstComp*dir;
      return((aParts[1]||"").localeCompare(bParts[1]||""))*dir;
    }
    if(sortCol==="followers")return(getFollowersS(a)-getFollowersS(b))*dir;
    if(sortCol==="eng")return(getEngS(a)-getEngS(b))*dir;
    if(sortCol==="score")return((a.score||0)-(b.score||0))*dir;
    if(sortCol==="status")return(a.status||"").localeCompare(b.status||"")*dir;
    return 0;
  });

  // Pagination — slice filtered to current page. Page index clamped so deletion
  // or filter changes can't strand us on a non-existent page.
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePageIndex = Math.min(pageIndex, totalPages - 1);
  const paged = filtered.slice(safePageIndex * pageSize, (safePageIndex + 1) * pageSize);
  // Reset to page 0 + clear select-all-matching when filters/search change
  // (otherwise you'd be stranded on page 47 of a 1-page result, or selectAllMatching
  // would silently apply to a different filter set than was selected against).
  useEffect(()=>{
    setPageIndex(0);
    setSelectAllMatching(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[search,statusFilter,filterMarket,filterFollowers,filterEng,showArchived,showNeedsReview,showAwaitingReply]);

  // Effective selection set — when selectAllMatching is true, treat every
  // creator in `filtered` (across all pages) as selected without actually
  // storing 2,000+ handles in the Set.
  const effectiveSelectedCount = selectAllMatching ? filtered.length : selected.size;
  const isEffectivelySelected = (handle)=>selectAllMatching || selected.has(handle);
  const getEffectiveSelectedHandles = ()=>selectAllMatching ? filtered.map(c=>c.handle) : [...selected];

  // Bulk action: change status. Single DB update + single batched activity_log
  // insert + single toast. PREVIOUSLY did per-creator onStatusChange callback
  // which triggered 5+ DB writes + 1 toast per creator → at 2,000 selected =
  // 10,000+ DB requests + 2,000 toast notifications stacking up screen-side.
  // Skip the per-row callback entirely in bulk path.
  const bulkChangeStatus = async()=>{
    if(!bulkStatus||!brandId)return;
    const handles = getEffectiveSelectedHandles();
    if(!handles.length)return;
    setBulkBusy(true);
    const{error}=await supabase.from('creators').update({status:bulkStatus}).eq('brand_id',brandId).in('handle',handles);
    if(error){setBulkBusy(false);toast(`Bulk status change failed: ${error.message}`,'error');return;}
    // Single batched activity_log insert — one row per creator so per-creator
    // history stays accurate, but one round-trip not 2,000.
    const activityRows = handles.map(h=>({
      brand_id:brandId, creator_handle:h, type:'status',
      text:`Status → ${bulkStatus} (bulk update)`,
      extra:{bulkChange:true,to:bulkStatus},
    }));
    // Chunk activity insert at 1,000 to avoid request size limits
    for(let i=0;i<activityRows.length;i+=1000){
      await supabase.from('activity_log').insert(activityRows.slice(i,i+1000));
    }
    // Single security_log entry for the whole bulk action (audit-friendly)
    logSecurityEvent(brandId,'creators.bulk_status_change','creator',null,{count:handles.length,to:bulkStatus});
    setAllCreators(cs=>cs.map(c=>handles.includes(c.handle)?{...c,status:bulkStatus}:c));
    setSelected(new Set());setSelectAllMatching(false);setBulkStatus("");
    setBulkBusy(false);
    toast(`✓ ${handles.length.toLocaleString()} creators → ${bulkStatus}`);
  };

  // Bulk action: archive (soft, reversible — uses existing archived flag)
  const bulkArchive = async()=>{
    const handles = getEffectiveSelectedHandles();
    if(!handles.length||!brandId)return;
    if(!(await confirmDialog({
      title:`Archive ${handles.length.toLocaleString()} creator${handles.length===1?"":"s"}?`,
      body:"They'll move to the Archived tab and can be restored anytime.",
      confirmLabel:"Archive",
    }))) return;
    setBulkBusy(true);
    const{error}=await supabase.from('creators').update({archived:true}).eq('brand_id',brandId).in('handle',handles);
    setBulkBusy(false);
    if(error){toast(`Archive failed: ${error.message}`,'error');return;}
    setAllCreators(cs=>cs.map(c=>handles.includes(c.handle)?{...c,archived:true}:c));
    setSelected(new Set());setSelectAllMatching(false);
    toast(`📦 ${handles.length.toLocaleString()} archived`);
  };

  // Bulk action: restore archived creators. The 30-day flow-cancel cool-down
  // in the email-worker (commit 67acca8) protects against an unintended auto-
  // re-engagement burst — if the creator's assignment was cancelled when they
  // were archived, restore alone won't fire emails until the cool-down lapses.
  // Per-row Restore button also wired below.
  const bulkRestore = async()=>{
    const handles = getEffectiveSelectedHandles();
    if(!handles.length||!brandId)return;
    if(!(await confirmDialog({
      title:`Restore ${handles.length.toLocaleString()} creator${handles.length===1?"":"s"}?`,
      body:"They'll move back to your active Creators list. The 30-day cool-down on cancelled flow assignments still applies — restored creators with a recent cancel won't auto-fire follow-ups until the cool-down lapses.",
      confirmLabel:"Restore",
    }))) return;
    setBulkBusy(true);
    const{error}=await supabase.from('creators').update({archived:false}).eq('brand_id',brandId).in('handle',handles);
    setBulkBusy(false);
    if(error){
      // 23505 = unique violation. With the new unique index on
      // (brand_id, lower(handle)) where archived=false, restoring a creator
      // whose handle matches an existing active row would conflict. Friendly
      // copy explains the fix.
      if (error.code === '23505' || /unique|duplicate/i.test(error.message||'')) {
        toast(`Can't restore — one of these handles is already active on this brand. Archive or rename the active one first.`, 'error');
      } else {
        toast(`Restore failed: ${error.message}`, 'error');
      }
      return;
    }
    setAllCreators(cs=>cs.map(c=>handles.includes(c.handle)?{...c,archived:false}:c));
    setSelected(new Set());setSelectAllMatching(false);
    toast(`✓ ${handles.length.toLocaleString()} restored`);
  };

  // Bulk action: hard delete via bulk_delete_creators RPC. Irreversible.
  // Double-confirmed because at 100+ scale a misclick is disaster.
  const bulkDelete = async()=>{
    const handles = getEffectiveSelectedHandles();
    if(!handles.length||!brandId)return;
    if(!(await confirmDialog({
      title:`Permanently delete ${handles.length.toLocaleString()} creator${handles.length===1?"":"s"}?`,
      body:<>This also wipes <strong>all their activity, flow assignments, and email history</strong>. This cannot be undone.</>,
      destructive:true, confirmLabel:"Delete permanently",
    }))) return;
    if(handles.length>=100 && !(await confirmDialog({
      title:"Double-check",
      body:`You're about to permanently destroy ${handles.length.toLocaleString()} creator records. Continue?`,
      destructive:true, confirmLabel:"Yes, destroy",
    }))) return;
    setBulkBusy(true);
    // RPC accepts text[] up to whatever Postgres can handle (very large).
    // Chunk at 1000 just to be safe with request size + give progress signal.
    const CHUNK = 1000;
    let totalDeleted = 0;
    for(let i=0;i<handles.length;i+=CHUNK){
      const slice = handles.slice(i,i+CHUNK);
      const{data,error}=await supabase.rpc('bulk_delete_creators',{p_brand_id:brandId,p_handles:slice});
      if(error){
        setBulkBusy(false);
        toast(`Deleted ${totalDeleted.toLocaleString()} before failure: ${error.message}`,'error');
        // Refresh from DB to converge state — partial delete means we don't know what's left.
        setAllCreators(cs=>cs.filter(c=>!handles.slice(0,i).includes(c.handle)));
        setSelected(new Set());setSelectAllMatching(false);
        return;
      }
      totalDeleted += (data||0);
    }
    setBulkBusy(false);
    setAllCreators(cs=>cs.filter(c=>!handles.includes(c.handle)));
    setSelected(new Set());setSelectAllMatching(false);
    toast(`🗑 ${totalDeleted.toLocaleString()} permanently deleted`);
  };

  // Bulk action: add selected creators to a campaign at "To Outreach" stage.
  // Upsert against (campaign_id, creator_handle) so re-adding existing
  // members silently no-ops instead of erroring. Once these creators are in
  // a campaign with linked flows, the worker picks them up automatically
  // (To Outreach status → linked outreach flow per migration 0049 wiring).
  const bulkAddToCampaign = async(campaignId)=>{
    const handles = getEffectiveSelectedHandles();
    if(!handles.length||!brandId||!campaignId)return;
    setBulkBusy(true);
    const rows = handles.map(h=>({brand_id:brandId, campaign_id:campaignId, creator_handle:h, stage:"To Outreach"}));
    const{error}=await supabase.from('kanban_items')
      .upsert(rows,{onConflict:'campaign_id,creator_handle'});
    if(error){setBulkBusy(false);toast(`Couldn't add to campaign: ${error.message}`,'error');return;}
    // Drop them into status="To Outreach" too so the worker (and the kanban view)
    // start the outreach sequence. Skip creators already past Outreached — don't
    // regress lifecycle just because someone re-adds them to a fresh campaign.
    const earlyStatuses = new Set(["",null,"To Outreach"]);
    const toReset = allCreators.filter(c=>handles.includes(c.handle) && earlyStatuses.has(c.status||null)).map(c=>c.handle);
    if(toReset.length>0){
      await supabase.from('creators').update({status:"To Outreach"}).eq('brand_id',brandId).in('handle',toReset);
      setAllCreators(cs=>cs.map(c=>toReset.includes(c.handle)?{...c,status:"To Outreach"}:c));
    }
    logSecurityEvent(brandId,'creators.bulk_add_to_campaign','campaign',campaignId,{count:handles.length});
    setSelected(new Set());setSelectAllMatching(false);setBulkCampaignPicker(false);
    setBulkBusy(false);
    const camp = campaigns.find(c=>c.id===campaignId);
    toast(`✓ ${handles.length.toLocaleString()} added to "${camp?.name||"campaign"}"`);
    // Surface the silent-skip case upfront: a creator with no email is "on flow"
    // but the worker can never send to them. Tell the operator now, not never.
    const noEmail = allCreators.filter(c=>handles.includes(c.handle) && !((c.email||"").trim())).map(c=>c.handle);
    if(noEmail.length){
      const shown = noEmail.slice(0,3).map(h=>'@'+h).join(', ');
      toast(`⚠ ${noEmail.length} have no email and won't be contacted until you add one: ${shown}${noEmail.length>3?` +${noEmail.length-3} more`:''}`,'error');
    }
  };

  const[showCSV,setShowCSV]=useState(false);
  const[csvText,setCSVText]=useState("");
  const[csvHeaders,setCSVHeaders]=useState([]);          // raw header strings, in source order
  const[csvRows,setCSVRows]=useState([]);                // 2D array of cell strings (post-header)
  const[csvMappings,setCSVMappings]=useState({});        // { targetField: sourceColumnIndex | null }
  const[csvStep,setCSVStep]=useState("upload");          // 'upload' | 'map' | 'preview'
  const[csvDefaultStatus,setCSVDefaultStatus]=useState("To Outreach");
  const[csvDefaultPlatform,setCSVDefaultPlatform]=useState("Instagram");
  const[csvPreview,setCSVPreview]=useState([]);          // mapped rows ready for import
  const[csvImporting,setCSVImporting]=useState(false);
  const[csvProgress,setCSVProgress]=useState(null);      // {current, total} during batched import

  // Target schema the operator can map source CSV columns to. Order = display
  // order in the mapper UI. `required` blocks the Continue button if unset.
  const CSV_TARGETS = [
    {key:"name", label:"Name", hint:"Display name (if separate first/last, map first here + last below)"},
    {key:"lastname", label:"Last name (optional)", hint:"Appended to Name on import. Skip if Name is already full."},
    {key:"handle", label:"IG / TikTok handle", required:true, hint:"Without @. Required to send email."},
    {key:"email", label:"Email"},
    {key:"followers", label:"Followers"},
    {key:"market", label:"Country / Market", hint:"AU, US, UK — anything text. Stored on the creator row."},
    {key:"niche", label:"Niche / category"},
    {key:"notes", label:"Notes / bio"},
    {key:"personalized_sentence", label:"Personalized line", hint:"IH 'Personalized Sentence' — leads the first outreach email."},
  ];

  // Header→target alias dictionary. Used for auto-suggesting the mapping.
  // Operator can always override any auto-pick from the dropdown.
  const CSV_ALIASES = {
    name: ["name","fullname","full_name","creatorname","creator","displayname","display_name","socialfullname","socialmediafullname","igfullname","firstname","first_name","first","givenname"],
    lastname: ["lastname","last_name","surname","familyname","family_name","last"],
    handle: ["handle","username","instagram","instagramhandle","instagram_handle","ighandle","ig_handle","ig","igusername","tiktokhandle","tiktok_handle","tiktok","tiktokusername","tt","tthandle"],
    email: ["email","emailaddress","email_address","creatoremail","contactemail","contact_email","mail"],
    followers: ["followers","followerscount","followers_count","followercount","follower_count","followercnt","followernumber","follower_number","audience","audiencesize","audience_size"],
    market: ["market","country","region","geo","location","countrycode","country_code","geo_location"],
    niche: ["niche","category","topic","vertical","industry","dashboard","dashboardtype","funnelstep","tag","tags"],
    notes: ["bio","about","description","notes","note","comment","comments","summary"],
    personalized_sentence: ["personalizedsentence","personalized_sentence","personalisedsentence","personalisation","personalization","personalizedline","personalisedline","icebreaker","opener","openingline","openinghook","firstline","introline","customline"],
  };
  const norm = (s)=>(s||"").toLowerCase().replace(/[^a-z0-9]/g,"");
  const autoSuggestMappings = (headers)=>{
    const map = {};
    const normalized = headers.map(norm);
    for(const target of CSV_TARGETS){
      const aliases = CSV_ALIASES[target.key] || [];
      // First exact-match pass, then includes-match for compound headers like "creator_email_address".
      let idx = -1;
      for(const a of aliases){
        idx = normalized.indexOf(norm(a));
        if(idx>=0) break;
      }
      if(idx<0){
        for(const a of aliases){
          idx = normalized.findIndex(h=>h.includes(norm(a)));
          if(idx>=0) break;
        }
      }
      map[target.key] = idx>=0 ? idx : null;
    }
    return map;
  };

  const handleAdd=async()=>{
    const fullName=`${(form.firstName||"").trim()} ${(form.lastName||"").trim()}`.trim();
    if(!fullName&&!form.handle.trim())return;
    const name=fullName||form.handle;
    const handle=(form.handle||name).replace("@","").toLowerCase().trim();
    const dupe=allCreators.find(c=>!c.archived&&((c.handle||"").toLowerCase()===handle||(c.name||"").toLowerCase()===name.toLowerCase()));
    if(dupe){toast(`⚠️ ${name} already in pipeline (${dupe.status})`,"error");return;}
    if(!brandId||!userId){toast("Brand not loaded yet","error");return;}
    const row={
      brand_id:brandId, user_id:userId,
      name, handle,
      email:(form.email||"").trim().toLowerCase()||null,
      followers:form.followers||null,
      platform:"Instagram",
      status:form.status||"To Outreach",
    };
    const {data, error} = await supabase.from('creators').insert(row).select().single();
    if(error){toast(`Add failed: ${error.message}`,"error");return;}
    setAllCreators(cs=>[...cs, data]);
    setShowAdd(false);setForm(emptyForm);
    toast(`${name} added to pipeline`);
  };

  // Proper CSV row splitter — handles quoted fields containing commas, escaped quotes.
  // Influencer Hero exports often have names with commas (e.g. "Smith, John") so naive split breaks.
  // Full RFC-4180-style CSV parse: quote-aware for BOTH commas and newlines, so
  // a quoted field containing line breaks (e.g. a multi-line Instagram bio in an
  // Influencer Hero export) stays ONE field instead of exploding into phantom
  // rows. Doubled quotes ("") inside a quoted field decode to a literal quote.
  // (Was: naive text.split("\n") per physical line — broke every multi-line bio.)
  const parseCSVText=(text)=>{
    const t=String(text||"").replace(/\r\n/g,"\n").replace(/\r/g,"\n");
    const rows=[]; let row=[]; let cur=""; let inQ=false;
    for(let i=0;i<t.length;i++){
      const ch=t[i];
      if(inQ){
        if(ch==='"' && t[i+1]==='"'){cur+='"';i++;}
        else if(ch==='"') inQ=false;
        else cur+=ch;
      }else{
        if(ch==='"' && cur==='') inQ=true;
        else if(ch===','){row.push(cur);cur="";}
        else if(ch==='\n'){row.push(cur);cur="";rows.push(row);row=[];}
        else cur+=ch;
      }
    }
    row.push(cur); rows.push(row);
    return rows.map(r=>r.map(s=>s.trim())).filter(r=>r.some(c=>c!==""));
  };

  // Step 1: parse the pasted/uploaded CSV into headers + raw rows. No mapping
  // happens here — we just expose the column structure to the operator.
  const parseCSV=()=>{
    const all=parseCSVText(csvText);
    if(all.length<2){toast("CSV needs a header row + at least one data row","error");return;}
    const headers=all[0];
    const rows=all.slice(1);
    setCSVHeaders(headers);
    setCSVRows(rows);
    setCSVMappings(autoSuggestMappings(headers));
    setCSVStep("map");
  };

  // Step 2: apply mappings + dedupe. Builds the preview list the operator
  // confirms before the actual DB insert in importCSV.
  const buildCSVPreview=()=>{
    const get=(row,key)=>{
      const idx = csvMappings[key];
      if(idx==null||idx<0) return "";
      return (row[idx]||"").trim();
    };
    // Archived creators don't count as dupes — the DB unique index is scoped to
    // archived=false, and archive+re-add is the supported re-import path.
    const existingHandles=new Set(allCreators.filter(c=>!c.archived).map(c=>(c.handle||"").toLowerCase()));
    // Shared emails (one manager inbox repping several creators) are legal but
    // worth flagging: every creator sharing an address gets their own outreach
    // email to the SAME inbox, and replies can only attribute to one of them.
    const existingEmails=new Set(allCreators.filter(c=>!c.archived&&c.email).map(c=>String(c.email).toLowerCase()));
    const seenInBatch=new Set();
    const emailsInBatch=new Set();
    // Same email shape check the agency-prospect form + suppress list use.
    const isValidEmail = (e)=>/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e||"").trim());
    const previewRows = csvRows.map(row=>{
      const first = get(row,"name");
      const last = get(row,"lastname");
      const name = last ? `${first}${first?" ":""}${last}`.trim() : first;
      const handleRaw = get(row,"handle").replace(/^@/,"").toLowerCase().trim();
      const handle = handleRaw;
      const followers = get(row,"followers");
      const emailRaw = get(row,"email");
      // Normalise on the way in — strips trailing whitespace and standardises
      // case, so "John@x.com" and "  john@x.com  " end up as one entry, not two.
      const email = emailRaw ? emailRaw.trim().toLowerCase() : "";
      const market = get(row,"market");
      const niche = get(row,"niche");
      const notes = get(row,"notes");
      const personalized_sentence = get(row,"personalized_sentence");
      const dupe = handle ? (existingHandles.has(handle)||seenInBatch.has(handle)) : false;
      // invalidEmail flag is separate from dupe so the preview can show both
      // categories distinctly. Empty email is allowed; only a non-empty bad
      // value flags the row.
      const invalidEmail = email !== "" && !isValidEmail(email);
      const emailDupe = !!email && !invalidEmail && !dupe && (existingEmails.has(email)||emailsInBatch.has(email));
      if(handle&&!dupe) seenInBatch.add(handle);
      if(email&&!invalidEmail&&!dupe) emailsInBatch.add(email);
      return {name:name||handle,handle,followers,email,market,niche,notes,personalized_sentence,dupe,invalidEmail,emailDupe};
    }).filter(r=>r.name||r.handle);
    setCSVPreview(previewRows);
    setCSVStep("preview");
  };

  const importCSV=async()=>{
    if(!brandId||!userId){toast("Brand not loaded yet","error");return;}
    setCSVImporting(true);
    // Skip rows that are: dupes, missing handle, OR have an invalid non-empty email.
    // Rows with invalid emails are kept VISIBLE in the preview (so operator sees
    // them) but excluded from the actual insert to keep DB clean.
    const toImport=csvPreview.filter(r=>!r.dupe&&!r.invalidEmail&&r.handle);
    if(toImport.length===0){toast("Nothing to import","error");setCSVImporting(false);return;}
    const rows=toImport.map(r=>({
      brand_id:brandId,
      user_id:userId,
      name:r.name||r.handle,
      handle:r.handle,
      email:r.email||null,
      followers:r.followers||null,
      platform:csvDefaultPlatform||"Instagram",
      status:csvDefaultStatus||"To Outreach",
      // niche column still doubles as "market bucket" today — operator-provided
      // niche wins, market is fallback (covers CSVs that only have country/market).
      niche:r.niche||r.market||null,
      notes:r.notes||null,
      personalized_sentence:r.personalized_sentence||null,
    }));
    // Batched inserts — single .insert() of 2000+ rows can hit Supabase request
    // size limits (~1MB) AND wedges the network call for 30+ seconds if it
    // succeeds, which feels broken to the operator. Chunks of 500 land each in
    // ~1-2 sec, let us show progress, and recover gracefully on partial failure.
    const BATCH = 500;
    const inserted = [];
    let failedAt = -1;
    let failedErr = null;
    for (let i = 0; i < rows.length; i += BATCH) {
      const chunk = rows.slice(i, i + BATCH);
      setCSVProgress({current: i, total: rows.length});
      const {data, error} = await supabase.from('creators').insert(chunk).select('*');
      if (error) { failedAt = i; failedErr = error; break; }
      if (data) inserted.push(...data);
    }
    setCSVProgress(null);
    setCSVImporting(false);
    // Mirror the hydration the page-load effect does — derive `market` from
    // `niche` so MARKET column renders immediately for newly-imported rows
    // instead of waiting until the next page refresh.
    const hydratedInserts = inserted.map(c => ({ ...c, market: c.market || c.niche || null }));
    if (failedErr) {
      // 23505 = unique_violation. With the new unique index on
      // (brand_id, lower(handle)) where archived=false, a CSV row whose handle
      // matches an existing active creator on THIS brand causes the batch to
      // abort. The client-side dedupe in buildCSVPreview catches local-state
      // matches, but a creator added in another tab between preview-time and
      // import-time slips through — surface the situation clearly.
      const friendly = (failedErr.code === '23505' || /unique|duplicate/i.test(failedErr.message||''))
        ? 'one of these handles already exists on this brand — refresh and re-run the preview to spot it'
        : failedErr.message;
      toast(`Imported ${inserted.length} before batch failure at row ${failedAt}: ${friendly}`, 'error');
      // Still set what we got — operator can retry remaining rows
      if (hydratedInserts.length > 0) setAllCreators(cs => [...cs, ...hydratedInserts]);
      return;
    }
    setAllCreators(cs => [...cs, ...hydratedInserts]);
    const skipped = csvPreview.filter(r => r.dupe).length;
    toast(`✓ ${inserted.length} imported${skipped > 0 ? ` · ${skipped} dupes skipped` : ""}`);
    setShowCSV(false);
    setCSVText("");setCSVHeaders([]);setCSVRows([]);setCSVMappings({});setCSVPreview([]);
    setCSVStep("upload");
  };
  const closeCSV = ()=>{
    setShowCSV(false);
    setCSVText("");setCSVHeaders([]);setCSVRows([]);setCSVMappings({});setCSVPreview([]);
    setCSVStep("upload");
  };

  const handleCSVFile=(file)=>{
    if(!file)return;
    const reader=new FileReader();
    reader.onload=(e)=>setCSVText(e.target.result);
    reader.readAsText(file);
  };

  const SORTABLE=[
    {key:"name",label:"Creator"},
    {key:"followers",label:"Followers"},
    {key:"eng",label:"Eng. rate"},
    {key:null,label:"Market"},
    {key:"status",label:"Status"},
    {key:null,label:"Order"},
    {key:null,label:"Discount code"},
  ];

  return(<div style={{display:"flex",flexDirection:"column",gap:16}} onClick={()=>statusMenu&&setStatusMenu(null)}>
    {showAdd&&(()=>{const canAdd=(form.firstName||"").trim()||(form.lastName||"").trim()||(form.handle||"").trim();return(<Modal title="Add Creator" subtitle="First step — just the essentials. Add engagement, market, discount code, etc. from the creator profile later." onClose={()=>setShowAdd(false)} width={480}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
        <div><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>First Name *</div><input value={form.firstName} onChange={e=>setF("firstName",e.target.value)} placeholder="e.g. Alice" style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/></div>
        <div><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Last Name</div><input value={form.lastName} onChange={e=>setF("lastName",e.target.value)} placeholder="e.g. Baddeley" style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/></div>
        <div><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>IG Handle *</div><input value={form.handle} onChange={e=>setF("handle",e.target.value)} placeholder="@handle" style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/></div>
        <div><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Email</div><input value={form.email} onChange={e=>setF("email",e.target.value)} placeholder="creator@email.com" type="email" style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/></div>
        <div><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Followers</div><input value={form.followers} onChange={e=>setF("followers",e.target.value)} placeholder="e.g. 18.9K" style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/></div>
        <div><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Initial Status</div><select value={form.status} onChange={e=>setF("status",e.target.value)} style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",background:"#fff",boxSizing:"border-box"}}>{STATUS_LIST.map(s=><option key={s}>{s}</option>)}</select></div>
      </div>
      <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:4}}>
        <button onClick={()=>setShowAdd(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer",fontWeight:500}}>Cancel</button>
        <button onClick={handleAdd} disabled={!canAdd} style={{background:canAdd?DARK:"#f0f0f0",color:canAdd?G:"#9ab8a6",border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:canAdd?"pointer":"not-allowed"}}>Add to Pipeline</button>
      </div>
    </Modal>);})()}

    <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
      <div style={{flex:1,position:"relative",minWidth:180}}><div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#9ab8a6"}}><Ico d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" size={15}/></div><input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search creators..." style={{width:"100%",padding:"10px 14px 10px 36px",border:"none",borderRadius:10,fontSize:13,outline:"none",background:"#fff",boxShadow:CARD,color:DARK}}/></div>
      {!showArchived&&<div style={{display:"flex",gap:4,background:"#fff",borderRadius:10,padding:3,boxShadow:CARD,flexWrap:"wrap"}}>
        {["All",...STATUS_LIST,"💰 Paid Leads"].map(s=><button key={s} onClick={()=>setStatusFilter(s)} style={{padding:"6px 11px",borderRadius:7,border:"none",background:statusFilter===s?DARK:"transparent",color:statusFilter===s?G:"#9ab8a6",fontSize:11,cursor:"pointer",fontWeight:statusFilter===s?700:400,whiteSpace:"nowrap"}}>{s}</button>)}
      </div>}
      <button onClick={()=>{setShowAwaitingReply(v=>!v);setShowNeedsReview(false);setShowArchived(false);setStatusFilter("All");}} style={{background:showAwaitingReply?"#3B82F6":awaitingReplyCount>0?"#EFF6FF":"#fff",color:showAwaitingReply?"#fff":awaitingReplyCount>0?"#1D4ED8":MUTED,border:`1px solid ${showAwaitingReply?"#3B82F6":awaitingReplyCount>0?"#BFDBFE":BORDER}`,borderRadius:10,padding:"9px 14px",fontSize:12,cursor:"pointer",fontWeight:showAwaitingReply||awaitingReplyCount>0?700:400,boxShadow:CARD,whiteSpace:"nowrap"}}>
        📩 Awaiting reply ({awaitingReplyCount})
      </button>
      <button onClick={()=>{setShowNeedsReview(v=>!v);setShowAwaitingReply(false);setShowArchived(false);setStatusFilter("All");}} style={{background:showNeedsReview?"#EF4444":needsReviewCount>0?"#FEF2F2":"#fff",color:showNeedsReview?"#fff":needsReviewCount>0?"#DC2626":MUTED,border:`1px solid ${showNeedsReview?"#EF4444":needsReviewCount>0?"#FECACA":BORDER}`,borderRadius:10,padding:"9px 14px",fontSize:12,cursor:"pointer",fontWeight:showNeedsReview||needsReviewCount>0?700:400,boxShadow:CARD,whiteSpace:"nowrap"}}>
        ⚠ Needs review ({needsReviewCount})
      </button>
      <button onClick={()=>{setShowArchived(a=>!a);setStatusFilter("All");}} style={{background:showArchived?"#F59E0B":"#fff",color:showArchived?"#fff":MUTED,border:`1px solid ${showArchived?"#F59E0B":BORDER}`,borderRadius:10,padding:"9px 14px",fontSize:12,cursor:"pointer",fontWeight:showArchived?700:400,boxShadow:CARD,whiteSpace:"nowrap"}}>
        📦 {showArchived?"Active":"Archived"} ({allCreators.filter(c=>showArchived?!c.archived:c.archived).length})
      </button>
      <button onClick={()=>{
        const headers=["Name","Handle","Followers","Engagement","Market","Status","Order","Discount Code","Email"];
        const rows=filtered.map(c=>[c.name,`@${c.handle}`,c.followersF||c.followers||"",c.eng||"",c.market||"",c.status||"",c.order||"",c.discountCode||genDiscountCode(c.name),c.email||""]);
        const csv=[headers,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
        const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);a.download=`creators-${new Date().toISOString().slice(0,10)}.csv`;a.click();
        toast("Exported to CSV");
      }} style={{background:"#fff",color:DARK,border:`1px solid ${BORDER}`,borderRadius:10,padding:"9px 14px",fontSize:12,cursor:"pointer",fontWeight:600,boxShadow:CARD,whiteSpace:"nowrap"}}>⬇ Export CSV</button>
      <button onClick={()=>setShowFilters(f=>!f)} style={{background:showFilters||filterMarket!=="All"||filterFollowers!=="All"||filterEng!=="All"?DARK:"#fff",color:showFilters||filterMarket!=="All"||filterFollowers!=="All"||filterEng!=="All"?G:MUTED,border:`1px solid ${BORDER}`,borderRadius:10,padding:"9px 14px",fontSize:12,cursor:"pointer",fontWeight:600,boxShadow:CARD,whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:5}}>
        ⚙ Filters{(filterMarket!=="All"||filterFollowers!=="All"||filterEng!=="All")&&<span style={{background:G,color:DARK,borderRadius:"50%",width:16,height:16,fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800}}>{[filterMarket!=="All",filterFollowers!=="All",filterEng!=="All"].filter(Boolean).length}</span>}
      </button>
      <button onClick={()=>setShowAdd(true)} style={{background:G,color:DARK,border:"none",borderRadius:10,padding:"10px 18px",fontWeight:700,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6,flexShrink:0}}><span style={{fontSize:18,lineHeight:1}}>+</span> Add creator</button>
      <button onClick={()=>setShowCSV(true)} style={{background:"#fff",color:DARK,border:`1px solid ${BORDER}`,borderRadius:10,padding:"10px 14px",fontWeight:600,fontSize:12,cursor:"pointer",flexShrink:0,boxShadow:CARD}}>⬆ Import CSV</button>
    </div>

    {showCSV&&(<Modal title="Import Creators from CSV" subtitle={
      csvStep==="upload"?"Paste any CSV — we'll let you map columns to creator fields next."
      :csvStep==="map"?"Match your CSV columns to creator fields."
      :(()=>{
        const ready=csvPreview.filter(r=>!r.dupe&&!r.invalidEmail).length;
        const dupes=csvPreview.filter(r=>r.dupe).length;
        const bad=csvPreview.filter(r=>!r.dupe&&r.invalidEmail).length;
        const sharedEmails=csvPreview.filter(r=>r.emailDupe).length;
        const parts=[`${ready} ready`];
        if(dupes>0) parts.push(`${dupes} duplicate${dupes===1?"":"s"}`);
        if(bad>0) parts.push(`${bad} bad email${bad===1?"":"s"}`);
        if(sharedEmails>0) parts.push(`${sharedEmails} shared email${sharedEmails===1?"":"s"}`);
        return `Final check — ${parts.join(", ")}`;
      })()
    } onClose={closeCSV} width={780}>
      {/* Step header — three pills showing progress through the import flow. */}
      <div style={{display:"flex",gap:8,marginBottom:18}}>
        {[
          {id:"upload",label:"1 · Upload"},
          {id:"map",label:"2 · Map columns"},
          {id:"preview",label:"3 · Confirm + import"},
        ].map(s=>(
          <div key={s.id} style={{flex:1,padding:"7px 10px",borderRadius:8,background:csvStep===s.id?DARK:"#f8faf9",color:csvStep===s.id?G:MUTED,fontSize:11,fontWeight:700,textAlign:"center",letterSpacing:".03em"}}>{s.label}</div>
        ))}
      </div>

      {csvStep==="upload"&&(<div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{background:"#f8faf9",border:`1px solid ${BORDER}`,borderRadius:10,padding:14,fontSize:12,color:MUTED,lineHeight:1.6}}>
          <div style={{fontWeight:700,color:DARK,marginBottom:6}}>Works with any CSV.</div>
          Whatever columns your source has — Influencer Hero, Modash, GRIN, Hypeauditor, a custom Sheet — you'll match them to our creator fields on the next step. Duplicates by handle auto-skip.
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <label style={{background:"#fff",border:`1px solid ${BORDER}`,borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:600,cursor:"pointer",color:DARK,display:"inline-flex",alignItems:"center",gap:6}}>
            📁 Upload CSV file
            <input type="file" accept=".csv,text/csv" style={{display:"none"}} onChange={e=>handleCSVFile(e.target.files[0])}/>
          </label>
          <span style={{fontSize:11,color:MUTED}}>or paste below ↓</span>
        </div>
        <textarea value={csvText} onChange={e=>setCSVText(e.target.value)} rows={8} placeholder={"Full Name,Instagram Handle,Follower Count,Email,Country\nAlice Baddeley,alice_be_active,18900,alice@example.com,Australia\nJames Park,jamespark_tri,41200,,Australia"} style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:12,fontFamily:"DM Mono, monospace",outline:"none",resize:"vertical",boxSizing:"border-box",lineHeight:1.6}}/>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={closeCSV} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={parseCSV} disabled={!csvText.trim()} style={{background:csvText.trim()?DARK:"#f0f0f0",color:csvText.trim()?G:"#9ab8a6",border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:csvText.trim()?"pointer":"not-allowed"}}>Continue →</button>
        </div>
      </div>)}

      {csvStep==="map"&&(()=>{
        const missingRequired = CSV_TARGETS.filter(t=>t.required && (csvMappings[t.key]==null));
        const usedIndexes = new Set(Object.values(csvMappings).filter(v=>v!=null&&v>=0));
        return(<div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{fontSize:12,color:MUTED,lineHeight:1.55,background:"#f8faf9",border:`1px solid ${BORDER}`,borderRadius:9,padding:"10px 12px"}}>
            We've guessed the mapping based on your column names — adjust any row by picking a different source column or skipping it. <strong>Detected {csvHeaders.length} columns, {csvRows.length.toLocaleString()} data rows.</strong>
          </div>

          {/* Mapping rows */}
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {CSV_TARGETS.map(target=>{
              const currentIdx = csvMappings[target.key];
              const sample = currentIdx!=null && currentIdx>=0 ? (csvRows[0]?.[currentIdx]||"") : "";
              return(<div key={target.key} style={{display:"grid",gridTemplateColumns:"180px 1fr 1fr",gap:10,alignItems:"center",padding:"8px 0"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:DARK}}>{target.label}{target.required&&<span style={{color:"#DC2626"}}> *</span>}</div>
                  {target.hint&&<div style={{fontSize:10,color:MUTED,marginTop:2,lineHeight:1.4}}>{target.hint}</div>}
                </div>
                <select value={currentIdx==null?"":currentIdx} onChange={e=>{
                  const v = e.target.value;
                  setCSVMappings(prev=>({...prev,[target.key]: v===""?null:parseInt(v,10)}));
                }} style={{padding:"8px 10px",border:`1.5px solid ${currentIdx==null && target.required?"#FCA5A5":BORDER}`,borderRadius:7,fontSize:12,outline:"none",background:"#fff"}}>
                  <option value="">— Skip / not in CSV —</option>
                  {csvHeaders.map((h,i)=>(
                    <option key={i} value={i} disabled={usedIndexes.has(i)&&i!==currentIdx}>
                      {h||`(column ${i+1})`}{usedIndexes.has(i)&&i!==currentIdx?" · already used":""}
                    </option>
                  ))}
                </select>
                <div style={{fontSize:11,color:MUTED,fontFamily:"DM Mono, monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {currentIdx!=null && currentIdx>=0 ? `e.g. "${sample||"(empty)"}"` : ""}
                </div>
              </div>);
            })}
          </div>

          {/* Defaults */}
          <div style={{borderTop:`1px solid ${BORDER}`,paddingTop:12,display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:6}}>Default status for new creators</div>
              <select value={csvDefaultStatus} onChange={e=>setCSVDefaultStatus(e.target.value)} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,outline:"none",background:"#fff"}}>
                {STATUS_LIST.map(s=><option key={s}>{s}</option>)}
              </select>
              <div style={{fontSize:10,color:MUTED,marginTop:4,lineHeight:1.4}}>Imported rows land here. "To Outreach" triggers the linked outreach flow when worker fires.</div>
            </div>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:6}}>Platform</div>
              <select value={csvDefaultPlatform} onChange={e=>setCSVDefaultPlatform(e.target.value)} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,outline:"none",background:"#fff"}}>
                <option>Instagram</option><option>TikTok</option><option>YouTube</option>
              </select>
            </div>
          </div>

          {missingRequired.length>0&&<div style={{padding:"9px 12px",background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,fontSize:11,color:"#991B1B"}}>⚠ Map the required field{missingRequired.length===1?"":"s"}: {missingRequired.map(t=>t.label).join(", ")}</div>}

          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button onClick={()=>setCSVStep("upload")} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 14px",fontSize:13,cursor:"pointer"}}>← Back</button>
            <button onClick={buildCSVPreview} disabled={missingRequired.length>0} style={{background:missingRequired.length===0?DARK:"#f0f0f0",color:missingRequired.length===0?G:"#9ab8a6",border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:missingRequired.length===0?"pointer":"not-allowed"}}>Preview →</button>
          </div>
        </div>);
      })()}

      {csvStep==="preview"&&(()=>{
        const readyCount = csvPreview.filter(r=>!r.dupe&&!r.invalidEmail).length;
        const dupeCount = csvPreview.filter(r=>r.dupe).length;
        const invalidCount = csvPreview.filter(r=>!r.dupe&&r.invalidEmail).length;
        const sharedEmailCount = csvPreview.filter(r=>r.emailDupe).length;
        return(<div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{display:"flex",gap:10,padding:"10px 14px",background:"#f8fdf8",borderRadius:10,border:`1px solid ${G}40`,fontSize:13,flexWrap:"wrap"}}>
          <span style={{color:G,fontWeight:700}}>✓ {readyCount} ready</span>
          {dupeCount>0&&<span style={{color:"#F59E0B",fontWeight:700}}>· {dupeCount} duplicate{dupeCount===1?"":"s"} (skipped)</span>}
          {invalidCount>0&&<span style={{color:"#DC2626",fontWeight:700}}>· {invalidCount} bad email{invalidCount===1?"":"s"} (skipped — fix the CSV)</span>}
          {sharedEmailCount>0&&<span style={{color:"#B45309",fontWeight:700}} title="Several creators share one inbox (e.g. a talent manager). They'll import, but each gets their own outreach email to the same address — consider keeping just one, or outreach will look like spam to that inbox.">· {sharedEmailCount} share an email with another creator (will import)</span>}
          <span style={{color:MUTED,marginLeft:"auto"}}>Status: <strong style={{color:DARK}}>{csvDefaultStatus}</strong> · Platform: <strong style={{color:DARK}}>{csvDefaultPlatform}</strong></span>
        </div>
        <div style={{maxHeight:340,overflowY:"auto",border:`1px solid ${BORDER}`,borderRadius:10}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:"#f8faf9",position:"sticky",top:0}}>{["Name","Handle","Email","Followers","Market","Niche",""].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:MUTED,textTransform:"uppercase",letterSpacing:".07em"}}>{h}</th>)}</tr></thead>
            <tbody>{csvPreview.map((r,i)=>{
              const rowBg = r.dupe ? "#FFFBEB" : r.invalidEmail ? "#FEF2F2" : r.emailDupe ? "#FFF7ED" : "transparent";
              return(<tr key={i} style={{borderTop:`1px solid ${BORDER}`,background:rowBg}}>
                <td style={{padding:"8px 12px",fontSize:12,fontWeight:600,color:DARK}}>{r.name}</td>
                <td style={{padding:"8px 12px",fontSize:12,color:MUTED}}>{r.handle?`@${r.handle}`:"—"}</td>
                <td style={{padding:"8px 12px",fontSize:12,color:r.invalidEmail?"#DC2626":MUTED,fontWeight:r.invalidEmail?600:400}}>{r.email||"—"}</td>
                <td style={{padding:"8px 12px",fontSize:12,color:MUTED}}>{r.followers||"—"}</td>
                <td style={{padding:"8px 12px",fontSize:12,color:MUTED}}>{r.market||"—"}</td>
                <td style={{padding:"8px 12px",fontSize:12,color:MUTED}}>{r.niche||"—"}</td>
                <td style={{padding:"8px 12px",fontSize:11}}>{
                  r.dupe?<span style={{color:"#92400E",fontWeight:700,background:"#FEF3C7",borderRadius:20,padding:"2px 8px"}}>Duplicate</span>
                  :r.invalidEmail?<span style={{color:"#991B1B",fontWeight:700,background:"#FEE2E2",borderRadius:20,padding:"2px 8px"}}>Bad email</span>
                  :r.emailDupe?<span style={{color:"#B45309",fontWeight:700,background:"#FFEDD5",borderRadius:20,padding:"2px 8px"}} title="Same email as another creator — one inbox will receive outreach for each of them">Shared email</span>
                  :<span style={{color:G,fontWeight:700,background:"#F0FDF4",borderRadius:20,padding:"2px 8px"}}>Ready</span>
                }</td>
              </tr>);
            })}</tbody>
          </table>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={()=>setCSVStep("map")} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 14px",fontSize:13,cursor:"pointer"}}>← Back to mapping</button>
          <button onClick={importCSV} disabled={readyCount===0||csvImporting} style={{background:readyCount>0?DARK:"#f0f0f0",color:readyCount>0?G:"#9ab8a6",border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:csvImporting?"wait":"pointer"}}>
            {csvImporting && csvProgress
              ? `Importing ${csvProgress.current.toLocaleString()} / ${csvProgress.total.toLocaleString()}…`
              : csvImporting
                ? "Importing…"
                : `Import ${readyCount.toLocaleString()} creators`}
          </button>
        </div>
      </div>);
      })()}
    </Modal>)}

    {showFilters&&(
      <div style={{background:"#fff",border:`1px solid ${BORDER}`,borderRadius:12,padding:"16px 20px",display:"flex",gap:20,flexWrap:"wrap",alignItems:"flex-end",boxShadow:CARD}}>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:MUTED,textTransform:"uppercase",letterSpacing:".07em",marginBottom:6}}>Market</div>
          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {["All","AU","US","UK","EU"].map(m=>(
              <button key={m} onClick={()=>setFilterMarket(m)} style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${filterMarket===m?G:BORDER}`,background:filterMarket===m?"#f0fdf4":"#fff",color:filterMarket===m?G:DARK,fontSize:12,fontWeight:filterMarket===m?700:400,cursor:"pointer"}}>{m}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:MUTED,textTransform:"uppercase",letterSpacing:".07em",marginBottom:6}}>Min. Followers</div>
          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {Object.keys(FOLLOWER_RANGES).map(r=>(
              <button key={r} onClick={()=>setFilterFollowers(r)} style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${filterFollowers===r?G:BORDER}`,background:filterFollowers===r?"#f0fdf4":"#fff",color:filterFollowers===r?G:DARK,fontSize:12,fontWeight:filterFollowers===r?700:400,cursor:"pointer"}}>{r}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:MUTED,textTransform:"uppercase",letterSpacing:".07em",marginBottom:6}}>Min. Engagement</div>
          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {Object.keys(ENG_RANGES).map(r=>(
              <button key={r} onClick={()=>setFilterEng(r)} style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${filterEng===r?G:BORDER}`,background:filterEng===r?"#f0fdf4":"#fff",color:filterEng===r?G:DARK,fontSize:12,fontWeight:filterEng===r?700:400,cursor:"pointer"}}>{r}</button>
            ))}
          </div>
        </div>
        {(filterMarket!=="All"||filterFollowers!=="All"||filterEng!=="All")&&(
          <button onClick={()=>{setFilterMarket("All");setFilterFollowers("All");setFilterEng("All");}} style={{border:`1px solid #FECACA`,background:"#FEF2F2",color:"#DC2626",borderRadius:8,padding:"5px 14px",fontSize:12,cursor:"pointer",fontWeight:600,marginTop:18}}>✕ Clear all</button>
        )}
      </div>
    )}

    {allCreators.length===0&&<Card style={{padding:56,textAlign:"center"}}>
      <div style={{fontSize:48,marginBottom:16}}>👤</div>
      <div style={{fontWeight:700,color:DARK,fontSize:18,marginBottom:8}}>No creators yet</div>
      <div style={{fontSize:13,color:MUTED,marginBottom:24,lineHeight:1.6,maxWidth:320,margin:"0 auto 24px"}}>Add your first creator manually or import from a campaign.</div>
      <button onClick={()=>setShowAdd(true)} style={{background:DARK,color:G,border:"none",borderRadius:10,padding:"11px 24px",fontWeight:700,fontSize:13,cursor:"pointer"}}>+ Add first creator</button>
    </Card>}

    {allCreators.length>0&&filtered.length===0&&<Card style={{padding:"32px 20px",textAlign:"center",color:MUTED,fontSize:13}}>
      No creators match this filter. <button onClick={()=>{setStatusFilter("All");setSearch("");setFilterMarket("All");setFilterFollowers("All");setFilterEng("All");setShowArchived(false);setShowNeedsReview(false);}} style={{background:"none",border:"none",color:G,fontWeight:700,cursor:"pointer",fontSize:13}}>Clear all filters</button>
    </Card>}


    {(selected.size>0||selectAllMatching)&&(
      <div style={{background:DARK,borderRadius:10,padding:"12px 18px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <span style={{color:G,fontWeight:700,fontSize:13}}>
          {effectiveSelectedCount.toLocaleString()} selected{selectAllMatching?` (all matching ${filtered.length.toLocaleString()})`:""}
        </span>
        {/* "Select all N matching" prompt — appears when the entire current page is selected
            but there are more rows in `filtered` not yet selected (Notion/Linear pattern). */}
        {!selectAllMatching && paged.length>0 && paged.every(c=>selected.has(c.handle)) && filtered.length>paged.length && (
          <button onClick={()=>{setSelectAllMatching(true);setSelected(new Set());}} style={{background:"rgba(255,255,255,0.12)",border:"none",borderRadius:6,padding:"4px 10px",color:G,fontSize:11,fontWeight:700,cursor:"pointer"}}>
            Select all {filtered.length.toLocaleString()} matching
          </button>
        )}
        <div style={{flex:1}}/>
        <select value={bulkStatus} onChange={e=>setBulkStatus(e.target.value)} disabled={bulkBusy} style={{padding:"7px 12px",borderRadius:8,border:"none",fontSize:13,background:"rgba(255,255,255,0.12)",color:"#fff",outline:"none",cursor:"pointer"}}>
          <option value="">Change status to...</option>
          {STATUS_LIST.map(s=><option key={s} value={s} style={{color:DARK,background:"#fff"}}>{s}</option>)}
        </select>
        <button onClick={bulkChangeStatus} disabled={!bulkStatus||bulkBusy} style={{background:bulkStatus&&!bulkBusy?G:"rgba(255,255,255,0.15)",color:bulkStatus&&!bulkBusy?DARK:"rgba(255,255,255,0.5)",border:"none",borderRadius:8,padding:"7px 16px",fontWeight:700,fontSize:12,cursor:bulkStatus&&!bulkBusy?"pointer":"default"}}>{bulkBusy?"Applying…":"Apply status"}</button>
        <button onClick={()=>setBulkCampaignPicker(true)} disabled={bulkBusy||campaigns.length===0} style={{background:"rgba(16,185,129,0.2)",border:"1px solid rgba(16,185,129,0.55)",borderRadius:8,padding:"7px 14px",color:"#a7f3d0",fontSize:12,fontWeight:700,cursor:bulkBusy||campaigns.length===0?"not-allowed":"pointer",opacity:campaigns.length===0?0.5:1}} title={campaigns.length===0?"Create a campaign first":""}>＋ Add to Campaign</button>
        {showArchived
          ? <button onClick={bulkRestore} disabled={bulkBusy} style={{background:"rgba(16,185,129,0.22)",border:"1px solid rgba(16,185,129,0.55)",borderRadius:8,padding:"7px 14px",color:"#a7f3d0",fontSize:12,fontWeight:700,cursor:bulkBusy?"wait":"pointer"}}>↩ Restore</button>
          : <button onClick={bulkArchive} disabled={bulkBusy} style={{background:"rgba(245,158,11,0.18)",border:"1px solid rgba(245,158,11,0.5)",borderRadius:8,padding:"7px 14px",color:"#fcd34d",fontSize:12,fontWeight:700,cursor:bulkBusy?"wait":"pointer"}}>📦 Archive</button>
        }
        <button onClick={bulkDelete} disabled={bulkBusy} style={{background:"rgba(239,68,68,0.18)",border:"1px solid rgba(239,68,68,0.5)",borderRadius:8,padding:"7px 14px",color:"#fca5a5",fontSize:12,fontWeight:700,cursor:bulkBusy?"wait":"pointer"}}>{bulkBusy?"…":"🗑 Delete"}</button>
        <button onClick={()=>{setSelected(new Set());setSelectAllMatching(false);}} disabled={bulkBusy} style={{background:"none",border:"1px solid rgba(255,255,255,0.2)",borderRadius:8,padding:"7px 12px",color:"rgba(255,255,255,0.6)",fontSize:12,cursor:"pointer"}}>✕ Clear</button>
      </div>
    )}

    {bulkCampaignPicker&&(<Modal title="Add selected creators to campaign" subtitle={`${effectiveSelectedCount.toLocaleString()} creator${effectiveSelectedCount===1?"":"s"} → "To Outreach" column`} onClose={()=>setBulkCampaignPicker(false)} width={460}>
      <div style={{fontSize:12,color:MUTED,marginBottom:14,lineHeight:1.55}}>If the campaign has a linked outreach flow, the worker will start the sequence automatically.</div>
      <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:360,overflowY:"auto"}}>
        {campaigns.length===0
          ?<div style={{padding:"22px 0",textAlign:"center",color:MUTED,fontSize:13}}>No campaigns yet. Create one in the Campaigns section.</div>
          :campaigns.map(c=>{
            const memberCount=(kanbanData[c.id]||[]).length;
            return(<button key={c.id} onClick={()=>bulkAddToCampaign(c.id)} disabled={bulkBusy} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 14px",border:`1px solid ${BORDER}`,borderRadius:8,background:"#fff",cursor:bulkBusy?"wait":"pointer",textAlign:"left"}}
              onMouseEnter={e=>{if(!bulkBusy)e.currentTarget.style.borderColor=G;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=BORDER;}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:DARK}}>{c.name}</div>
                <div style={{fontSize:11,color:MUTED,marginTop:2}}>{c.status} · {memberCount} creator{memberCount===1?"":"s"}</div>
              </div>
              <div style={{fontSize:11,fontWeight:700,color:G}}>Add ›</div>
            </button>);
          })
        }
      </div>
      <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:16}}>
        <button onClick={()=>setBulkCampaignPicker(false)} disabled={bulkBusy} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 18px",fontSize:13,cursor:bulkBusy?"wait":"pointer"}}>Cancel</button>
      </div>
    </Modal>)}

    {filtered.length>0&&<Card>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:"1px solid #f0f7f3"}}>
          <th style={{padding:"12px 16px",width:40}}>
            {/* Header checkbox now selects the CURRENT PAGE (not all-filtered) — at 2k+ scale
                "select all" via checkbox would silently grab everything across pages, which is
                rarely what the operator wants. The "Select all N matching" prompt in the bulk
                bar appears when the page is full-selected, for the genuinely-everything case. */}
            <input type="checkbox" checked={paged.length>0&&(selectAllMatching||paged.every(c=>selected.has(c.handle)))} onChange={e=>{
              if(e.target.checked){
                if(selectAllMatching){return;} // already maxed out
                setSelected(prev=>{const s=new Set(prev);paged.forEach(c=>s.add(c.handle));return s;});
              } else {
                setSelectAllMatching(false);
                setSelected(prev=>{const s=new Set(prev);paged.forEach(c=>s.delete(c.handle));return s;});
              }
            }} style={{accentColor:G,width:15,height:15,cursor:"pointer"}}/>
          </th>
          {SORTABLE.map(col=>(
            <th key={col.label} onClick={col.key?()=>handleSort(col.key):undefined}
              style={{padding:"12px 16px",textAlign:"left",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",whiteSpace:"nowrap",cursor:col.key?"pointer":"default",userSelect:"none",color:sortCol===col.key?"#374151":"#9ab8a6",background:sortCol===col.key?"#fafcfb":"transparent"}}
              onMouseEnter={e=>{if(col.key)e.currentTarget.style.background="#f5f7f5";}}
              onMouseLeave={e=>{e.currentTarget.style.background=sortCol===col.key?"#fafcfb":"transparent";}}>
              {col.label}{col.key&&<Arrow col={col.key}/>}
            </th>
          ))}
        </tr></thead>
        <tbody>{paged.map((c,i)=>{
          const rowSelected = isEffectivelySelected(c.handle);
          return(
          <tr key={c.id||c.handle||i} style={{borderBottom:"1px solid #f8faf9",cursor:"pointer",background:rowSelected?"#f0fdf4":""}} onClick={()=>onSelect(c)} onMouseEnter={e=>{if(!rowSelected)e.currentTarget.style.background="#fafcfb";}} onMouseLeave={e=>e.currentTarget.style.background=rowSelected?"#f0fdf4":""}>
            <td style={{padding:"14px 16px"}} onClick={e=>e.stopPropagation()}><input type="checkbox" checked={rowSelected} onChange={e=>{
              // If select-all-matching is ON and operator unchecks a row, switch to explicit-set mode
              // and exclude this handle. Cleaner than trying to maintain a "selected except these" set.
              if(selectAllMatching){
                setSelectAllMatching(false);
                setSelected(new Set(filtered.map(x=>x.handle).filter(h=>h!==c.handle)));
                return;
              }
              const s=new Set(selected);e.target.checked?s.add(c.handle):s.delete(c.handle);setSelected(s);
            }} style={{accentColor:G,width:15,height:15,cursor:"pointer"}}/></td>
            <td style={{padding:"14px 16px"}}><div style={{display:"flex",alignItems:"center",gap:10}}><Avatar name={c.name} color={c.color} size={34}/><div><div style={{fontWeight:600,color:DARK,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>{c.name}{c.review_reason&&<span title={c.review_reason} style={{background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",borderRadius:5,padding:"1px 7px",fontSize:10,fontWeight:700}}>⚠ {c.review_reason==="paid_only_inquiry"?"Paid only":c.review_reason==="unclear_reply"?"Unclear reply":c.review_reason==="unsubscribed_via_email_link"?"Unsubscribed":"Needs review"}</span>}{c.paid_database_at&&<span title="In per-brand Paid Leads list" style={{background:"#FEF3C7",color:"#92400E",border:"1px solid #FDE68A",borderRadius:5,padding:"1px 7px",fontSize:10,fontWeight:700}}>💰 Paid Leads</span>}{isFollowUpDue(c.handle,activityLog,flows)&&<span style={{background:"#FEF3C7",color:"#92400E",border:"1px solid #FDE68A",borderRadius:5,padding:"1px 7px",fontSize:10,fontWeight:700}}>Follow-up due</span>}{(()=>{const s=getKanbanSubStatus(c,c.status,activityLog);if(s==="Overdue")return<span title="They replied >3 days ago — still waiting on your reply" style={{background:"#FEF2F2",color:"#991B1B",border:"1px solid #FECACA",borderRadius:5,padding:"1px 7px",fontSize:10,fontWeight:700}}>🚨 Reply overdue</span>;if(s==="To Do")return<span title="They replied — needs reply from you" style={{background:"#EFF6FF",color:"#1D4ED8",border:"1px solid #BFDBFE",borderRadius:5,padding:"1px 7px",fontSize:10,fontWeight:700}}>📩 Awaiting reply</span>;return null;})()}</div><div style={{fontSize:11}}><HandleLink handle={c.handle} size={11}/></div></div></div></td>
            <td style={{padding:"14px 16px",fontWeight:600,color:DARK}}>{c.followersF||c.followers||"—"}</td>
            <td style={{padding:"14px 16px"}}><span style={{color:c.engN>=5?"#10B981":c.engN>=3?"#F59E0B":"#EF4444",fontWeight:600}}>{c.eng||"—"}</span></td>
            <td style={{padding:"14px 16px"}}><Badge label={c.market} color={c.market==="US"?"#EFF6FF":"#F0FDF4"} text={c.market==="US"?"#1D4ED8":"#166634"}/></td>
            <td style={{padding:"14px 16px",position:"relative"}} onClick={e=>e.stopPropagation()}>
              <span onClick={e=>{e.stopPropagation();setStatusMenu(statusMenu===c.handle?null:c.handle);}}
                style={{background:(STATUS_COLORS[c.status]||"#9ab8a6")+"18",color:STATUS_COLORS[c.status]||"#9ab8a6",border:`1px solid ${(STATUS_COLORS[c.status]||"#9ab8a6")}30`,borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:600,cursor:"pointer",userSelect:"none",display:"inline-flex",alignItems:"center",gap:4}}>
                {c.status}<span style={{fontSize:9,opacity:.7}}>▾</span>
              </span>
              {statusMenu===c.handle&&(
                <div style={{position:"absolute",top:"100%",left:0,background:"#fff",border:`1px solid ${BORDER}`,borderRadius:12,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",zIndex:200,overflow:"hidden",minWidth:170}}>
                  {STATUS_LIST.map(s=>{
                    const col=STATUS_COLORS[s]||"#9ab8a6";
                    const isCurrent=c.status===s;
                    return(<div key={s}
                      onClick={e=>{e.stopPropagation();setAllCreators(cs=>cs.map(cr=>cr.handle===c.handle?{...cr,status:s}:cr));if(onStatusChange)onStatusChange(c,s);setStatusMenu(null);toast(`${c.name} → ${s}`);}}
                      style={{padding:"9px 14px",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:8,background:isCurrent?"#f8faf9":"transparent",fontWeight:isCurrent?700:400}}
                      onMouseEnter={e=>e.currentTarget.style.background="#f5f7f5"}
                      onMouseLeave={e=>e.currentTarget.style.background=isCurrent?"#f8faf9":"transparent"}>
                      <span style={{width:7,height:7,borderRadius:"50%",background:col,flexShrink:0}}/>
                      <span style={{color:isCurrent?col:DARK,flex:1}}>{s}</span>
                      {isCurrent&&<span style={{color:col,fontSize:11}}>✓</span>}
                    </div>);
                  })}
                </div>
              )}
            </td>
            <td style={{padding:"14px 16px",fontSize:12}} onClick={e=>e.stopPropagation()}>
              {c.order
                ?<a href={`https://admin.shopify.com/store/${(brand?.shopifyUrl||"effortlessswimming").replace(".myshopify.com","").replace("https://","")}/orders?query=${encodeURIComponent(c.order)}`} target="_blank" rel="noreferrer" style={{fontFamily:"DM Mono, monospace",color:"#3B82F6",fontWeight:600,textDecoration:"none"}} onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"} onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>{c.order} ↗</a>
                :<span style={{color:"#d1d5db"}}>—</span>}
            </td>
            <td style={{padding:"14px 16px"}}>{(c.discountCode||genDiscountCode(c.name))?<span style={{fontFamily:"DM Mono, monospace",fontSize:11,fontWeight:600,color:DARK,background:"#f8faf9",border:`1px solid ${BORDER}`,borderRadius:5,padding:"2px 7px",letterSpacing:".05em"}}>{c.discountCode||genDiscountCode(c.name)}</span>:<span style={{color:"#d1d5db"}}>—</span>}</td>
          </tr>);
        })}</tbody>
      </table>

      {/* Pagination bar — page size selector + prev/next + page indicator.
          Total page count + current page calculated from `filtered` (post-filter,
          post-sort). Hidden when results fit on one page (≤ pageSize). */}
      {filtered.length>pageSize && (
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderTop:`1px solid ${BORDER}`,background:"#fafcfb",flexWrap:"wrap",gap:12}}>
          <div style={{display:"flex",alignItems:"center",gap:10,fontSize:12,color:MUTED}}>
            <span>Rows per page:</span>
            {[25,50,75,100].map(n=>(
              <button key={n} onClick={()=>{setPageSize(n);setPageIndex(0);}} style={{background:pageSize===n?DARK:"transparent",color:pageSize===n?G:MUTED,border:pageSize===n?"none":`1px solid ${BORDER}`,borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:pageSize===n?700:500,cursor:"pointer"}}>{n}</button>
            ))}
          </div>
          <div style={{fontSize:12,color:MUTED}}>
            Showing <strong style={{color:DARK}}>{(safePageIndex*pageSize+1).toLocaleString()}-{Math.min((safePageIndex+1)*pageSize, filtered.length).toLocaleString()}</strong> of <strong style={{color:DARK}}>{filtered.length.toLocaleString()}</strong>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <button onClick={()=>setPageIndex(0)} disabled={safePageIndex===0} style={{background:"transparent",border:`1px solid ${BORDER}`,borderRadius:6,padding:"4px 8px",fontSize:11,cursor:safePageIndex===0?"not-allowed":"pointer",color:safePageIndex===0?"#d1d5db":DARK,opacity:safePageIndex===0?0.5:1}}>« First</button>
            <button onClick={()=>setPageIndex(p=>Math.max(0,p-1))} disabled={safePageIndex===0} style={{background:"transparent",border:`1px solid ${BORDER}`,borderRadius:6,padding:"4px 10px",fontSize:11,cursor:safePageIndex===0?"not-allowed":"pointer",color:safePageIndex===0?"#d1d5db":DARK,opacity:safePageIndex===0?0.5:1}}>‹ Prev</button>
            <span style={{padding:"4px 12px",fontSize:12,color:DARK,fontWeight:600,minWidth:80,textAlign:"center"}}>Page {safePageIndex+1} / {totalPages}</span>
            <button onClick={()=>setPageIndex(p=>Math.min(totalPages-1,p+1))} disabled={safePageIndex>=totalPages-1} style={{background:"transparent",border:`1px solid ${BORDER}`,borderRadius:6,padding:"4px 10px",fontSize:11,cursor:safePageIndex>=totalPages-1?"not-allowed":"pointer",color:safePageIndex>=totalPages-1?"#d1d5db":DARK,opacity:safePageIndex>=totalPages-1?0.5:1}}>Next ›</button>
            <button onClick={()=>setPageIndex(totalPages-1)} disabled={safePageIndex>=totalPages-1} style={{background:"transparent",border:`1px solid ${BORDER}`,borderRadius:6,padding:"4px 8px",fontSize:11,cursor:safePageIndex>=totalPages-1?"not-allowed":"pointer",color:safePageIndex>=totalPages-1?"#d1d5db":DARK,opacity:safePageIndex>=totalPages-1?0.5:1}}>Last »</button>
          </div>
        </div>
      )}
    </Card>}
    <div style={{fontSize:12,color:"#9ab8a6",textAlign:"right"}}>{allCreators.length.toLocaleString()} total · {filtered.length.toLocaleString()} shown</div>
  </div>);
}

// Images a creator attached to a reply (archived by the reply webhook into the
// private inbound-attachments bucket). Signs each path on mount; the signed URL
// is what the <img> loads and what opens full-size on click. Non-image
// attachments are listed as chips so the operator knows they exist.
function InboundAttachments({images=[],attachments=[],gmailMessageId=null,compact=false}){
  const [urls,setUrls]=useState({});
  const paths=(images||[]).map(i=>i.path).filter(Boolean);
  useEffect(()=>{
    if(paths.length===0) return;
    let cancelled=false;
    (async()=>{
      const{data,error}=await supabase.storage.from('inbound-attachments').createSignedUrls(paths,3600);
      if(cancelled||error||!data) return;
      const map={};
      for(const d of data){ if(d?.path&&d?.signedUrl) map[d.path]=d.signedUrl; }
      setUrls(map);
    })();
    return()=>{cancelled=true;};
  // eslint-disable-next-line
  },[paths.join('|')]);
  const imagePaths=new Set(paths);
  const others=(attachments||[]).filter(a=>!/^image\//i.test(a.mimeType||"")||!(images||[]).some(i=>i.filename===a.filename));
  if(paths.length===0&&others.length===0) return null;
  const fmt=n=>!n?"":n<1024*1024?`${Math.round(n/1024)} KB`:`${(n/1024/1024).toFixed(1)} MB`;
  return(<div style={{marginTop:compact?6:10,display:"flex",flexWrap:"wrap",gap:8,alignItems:"flex-start"}}>
    {(images||[]).map((img,i)=>{
      const u=urls[img.path];
      return u
        ?<a key={img.path||i} href={u} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} title={`${img.filename} · ${fmt(img.size)} — open full size`} style={{display:"block",lineHeight:0}}>
            <img src={u} alt={img.filename} style={{maxHeight:compact?90:180,maxWidth:compact?140:260,borderRadius:8,border:`1px solid ${BORDER}`,objectFit:"cover",background:"#f3f4f6"}}/>
          </a>
        :<div key={img.path||i} style={{height:compact?90:180,width:compact?140:200,borderRadius:8,border:`1px solid ${BORDER}`,background:"#f3f4f6",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:MUTED}}>🖼 {img.filename}</div>;
    })}
    {others.map((a,i)=>(<a key={`${a.filename}-${i}`} href={gmailMessageId?`https://mail.google.com/mail/u/0/#all/${gmailMessageId}`:undefined} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} title="Open in Gmail" style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:11,color:DARK,background:"#f8faf9",border:`1px solid ${BORDER}`,borderRadius:20,padding:"4px 10px",textDecoration:"none"}}>📎 {a.filename||"attachment"}{a.size?<span style={{color:MUTED}}>· {fmt(a.size)}</span>:null}</a>))}
    {imagePaths.size===0&&(attachments||[]).some(a=>/^image\//i.test(a.mimeType||""))&&<span style={{fontSize:11,color:MUTED,fontStyle:"italic"}}>image attached — archive pending</span>}
  </div>);
}

function SendEmailModal({creator,flows=[],activityLog=[],onClose,onSent,preloadStep=null,brand=null,brandId=null}){
  // Canonical interpolation — single source of truth shared with the worker +
  // send-creator-email (see buildEmailVars/interpolateTemplate at module scope).
  const replaceVars=s=>interpolateTemplate(s, buildEmailVars(creator, brand));
  const[subject,setSubject]=useState(replaceVars(preloadStep?.subject||""));
  const[body,setBody]=useState(replaceVars(preloadStep?.body||""));
  const[selectedFlow,setSelectedFlow]=useState("");
  const[aiPersonalising,setAiPersonalising]=useState(false);
  // Operator-saved reusable templates for THIS brand (managed in the Flows tab).
  // Loaded fresh on open — sits alongside "Load from flow" + the reply chips and
  // works in BOTH new-email and reply contexts.
  const[savedTemplates,setSavedTemplates]=useState([]);
  const activeBrandId = brand?.id || brandId;
  useEffect(()=>{
    if(!activeBrandId){setSavedTemplates([]);return;}
    let cancelled=false;
    (async()=>{
      const{data}=await supabase.from('brand_email_templates').select().eq('brand_id',activeBrandId).order('name');
      if(!cancelled) setSavedTemplates(data||[]);
    })();
    return()=>{cancelled=true;};
  },[activeBrandId]);

  // AI reply suggestions — auto-fetched when there's an unanswered inbound to respond to.
  // The auto-reply system handles most cases; AMs land in this modal mainly for paid-only
  // negotiations, custom Qs, VIP touches. So suggestions are tuned for those moments.
  const sortedActivity = useMemo(()=>[...activityLog].sort((a,b)=>new Date(b.ts)-new Date(a.ts)),[activityLog]);
  const lastInbound = sortedActivity.find(e=>e.type==="email_received");
  const lastSent = sortedActivity.find(e=>e.type==="email_sent");
  const isReplyContext = lastInbound && (!lastSent || new Date(lastInbound.ts)>new Date(lastSent.ts));
  // Replying: the subject is theirs with "Re:" in front — fill it in so nobody has
  // to type one, and threading (In-Reply-To + Gmail threadId, sent below) keeps
  // the message in the creator's existing conversation.
  const replySubjectFor=(inb)=>inb?.subject ? (/^re:/i.test(inb.subject)?inb.subject:`Re: ${inb.subject}`) : "";
  useEffect(()=>{
    if(isReplyContext && !subject.trim() && lastInbound?.subject) setSubject(replySubjectFor(lastInbound));
  // eslint-disable-next-line
  },[isReplyContext]);
  const [suggestions,setSuggestions] = useState(null);
  const [suggestionsLoading,setSuggestionsLoading] = useState(false);
  const [suggestionsError,setSuggestionsError] = useState(null);

  const fetchSuggestions = async()=>{
    if(!lastInbound)return;
    setSuggestionsLoading(true); setSuggestionsError(null);
    try{
      // Recent conversation context — last 6 exchanges, oldest first for chronological prompt.
      const transcript = sortedActivity
        .filter(e=>e.type==="email_sent"||e.type==="email_received")
        .slice(0,6).reverse()
        .map(e=>{
          const dir = e.type==="email_sent" ? "BRAND → CREATOR" : "CREATOR → BRAND";
          const snippet = (e.bodySnippet || e.subject || e.text || "").slice(0,300);
          return `[${dir}] ${snippet}`;
        }).join("\n\n");
      const inboundBody = lastInbound.bodySnippet || lastInbound.text || lastInbound.subject || "";
      const inboundClass = lastInbound.classification || "neutral";
      const brandCtx = `Brand: ${brand?.name||"the brand"}. ${brand?.commissionRate?`Commission: ${brand.commissionRate}. `:""}${brand?.discountPct?`Audience discount: ${brand.discountPct}. `:""}`;
      const creatorCtx = `Creator: ${creator.name} (@${creator.handle}). Status: ${creator.status||"unknown"}. ${creator.review_reason==="paid_only_inquiry"?"Already flagged: paid-only inquiry.":""}`;

      const prompt = `You're suggesting reply options for an influencer agency account manager. They've landed here because the auto-reply system flagged this inbound as needing a human response.

CONTEXT
${brandCtx}
${creatorCtx}
Reply classification: ${inboundClass}

CONVERSATION HISTORY (oldest to newest):
${transcript}

LATEST INBOUND (from creator):
"""
${inboundBody}
"""

TASK
Generate 2–3 ready-to-send reply options the AM could use. Each must:
- Be 2–4 sentences max, no placeholders the AM has to fill in (use real values from context)
- Sound like a real person, brand voice (friendly, professional, not pushy)
- Cover DIFFERENT angles/strategies from each other (not just rewordings)
- For paid-only inquiries specifically: include options like "decline gracefully + keep door open for future budget", "ask their rate to consider", "counter-offer gifted + commission hybrid"
- For custom questions: answer directly, use only facts you can see in context
- For VIP / friendly tone: match the warmth the creator brought

Output strict JSON only: {"suggestions":[{"label":"short headline","tone":"friendly|professional|firm","body":"the full reply text"}]}`;

      const data = await callClaude({
        model: "claude-sonnet-4-6",
        max_tokens: 900,
        messages: [{role:"user", content:prompt}],
      });
      const raw = (data?.content?.[0]?.text || "").replace(/^```(?:json)?\s*|```\s*$/g,"").trim();
      const parsed = JSON.parse(raw);
      // Only keep entries with a non-empty body so applySuggestion never
      // sets body to undefined (would flip the textarea to uncontrolled mode).
      const clean = (parsed?.suggestions||[]).filter(s=>s && typeof s.body==="string" && s.body.trim());
      if(!clean.length) throw new Error("no usable suggestions returned");
      setSuggestions(clean);
    }catch(e){
      console.error("suggest-reply",e);
      setSuggestionsError(e.message||"Couldn't generate suggestions");
    }finally{ setSuggestionsLoading(false); }
  };
  // Auto-fire once when modal opens in reply context.
  useEffect(()=>{
    if(isReplyContext && !suggestions && !suggestionsLoading) fetchSuggestions();
  // eslint-disable-next-line
  },[isReplyContext]);
  const applySuggestion = (s)=>{
    setBody(s.body);
    if(!subject.trim() && lastInbound?.subject){
      setSubject(lastInbound.subject.startsWith("Re:") ? lastInbound.subject : `Re: ${lastInbound.subject}`);
    }
    toast("Draft loaded — edit before sending");
  };

  const aiPersonalise=async()=>{
    setAiPersonalising(true);
    try{
      const hasPlaceholder=body.includes("[ADD 1 PERSONALISED LINE HERE");
      const draft=body.trim();

      // Targeted placeholder fill — preserves the surrounding email.
      if(hasPlaceholder){
        const operatorNotes=(creator.notes||"").trim();
        const hasRealContent=operatorNotes.length>10;
        const data=await callClaude({
          model:"claude-sonnet-4-6",
          max_tokens:120,
          messages:[{role:"user",content:`Write ONE warm opening sentence for an influencer outreach email.

What you know about this creator:
- Name: ${creator.name}
- Handle: @${creator.handle}
- Niche (broad): ${creator.pocket||"swimming/triathlon"}
${hasRealContent?`- Operator notes: ${operatorNotes}`:`- No personal notes from operator`}

CRITICAL RULES (read carefully):
- You do NOT have access to their actual posts, videos, or content. Do NOT pretend you watched a specific reel, saw their latest race, noticed their open-water form, etc. Fabricating observations is the #1 thing to avoid.
${hasRealContent?`- The operator's notes above are the ONLY personal info you can reference. Use them lightly if relevant.`:`- Since you have no personal info, write a warm sentence that fits any ${creator.pocket||"swimmer/triathlete"} — e.g. acknowledge their niche, the season, a shared community ethos, or curiosity about their journey. Do NOT invent specifics.`}
- ONE sentence only, no greeting (greeting handled separately)
- Plain language, conversational, no emojis or exclamation marks
- Do not mention follower count or any metric
- Do not say "love your content" or "huge fan" — generic flattery is worse than nothing

Reply with ONLY the sentence.`}]
        });
        const sentence=(data.content?.[0]?.text||"").trim();
        if(sentence) setBody(prev=>prev.replace(/\[ADD 1 PERSONALISED LINE HERE[^\]]*\]/,sentence));
        return;
      }

      // Whole-email compose mode. Either drafts from scratch (empty body) or expands
      // a rough intent the user typed. Either way, feed Claude the full conversation
      // history so the reply is contextually appropriate, not a fresh outreach.

      // Build a chronological transcript of past exchanges with this creator.
      const exchanges=[...activityLog]
        .filter(e=>e.type==="email_sent"||e.type==="email_received")
        .sort((a,b)=>new Date(a.ts)-new Date(b.ts))
        .slice(-8); // last 8 events is plenty of context, keeps prompt small

      const transcript=exchanges.map(e=>{
        const when=new Date(e.ts).toISOString().slice(0,16).replace("T"," ");
        if(e.type==="email_sent"){
          // Reconstruct body from the matching flow step if we have a flowId.
          let bodyText="";
          if(e.flowId){
            const f=flows.find(x=>x.id===e.flowId);
            const step=f?.steps?.[(e.step!=null?e.step:0)];
            if(step?.body) bodyText=step.body;
          }
          return `[${when}] US -> THEM\nSubject: ${e.subject||"(no subject)"}\n${bodyText?bodyText.slice(0,800):"(content unavailable)"}`;
        }
        // email_received
        const cls=e.classification?` [classified: ${e.classification}]`:"";
        const snippet=e.bodySnippet||e.subject||"(no body)";
        return `[${when}] THEM -> US${cls}\n${snippet.slice(0,800)}`;
      }).join("\n\n---\n\n");

      const statusContext=creator.status?` Current creator lifecycle status: "${creator.status}".`:"";
      const reviewContext=creator.review_reason==="paid_only_inquiry"
        ? ` IMPORTANT: This creator replied saying they only do PAID collaborations. The operator's brand currently runs gifted-only collabs (no payment). Reply should politely acknowledge their position, decline paid terms, and either keep the door open for future paid budgets OR re-pitch the gifted offer with extra value — but NEVER agree to paid.`
        : "";

      const system=`You write influencer outreach emails on behalf of ${brand?.name||"the brand"}${brand?.domain?` (${brand.domain})`:""}.

OUTPUT FORMAT: Plain text email body only — no subject line, no quotes around it, no commentary, no markdown headings.

VOICE: Sound like a real human writing to a peer. Direct, warm, concise. Not corporate. Not over-friendly. Not begging.

PLACEHOLDERS: Use {{firstname}} and {{discount_code}} where appropriate — the system fills them at send time.

ANTI-FABRICATION (most important): You do NOT have access to the creator's actual posts, videos, training schedule, race results, location, or content history. Only use facts that appear in the data provided below (notes, prior messages in this thread, declared product preference, shipping address fields they sent us). Never invent: "saw your last race", "your open-water reel was incredible", "loved your post about X". Generic warmth beats fake-personal every time.

CONTEXT AWARENESS: If there's prior conversation, continue the thread naturally — do NOT restart with a cold intro. If they asked questions, answer them. If they shared info, acknowledge it. If they declined, don't push.${statusContext}${reviewContext}`;

      const intentBlock=draft.length>0
        ? `Operator's intent for this next message:\n"""${draft}"""\n\nWrite the email body that captures this intent in the conversation's context.`
        : exchanges.length>0
          ? `Read the conversation and write the next natural message in the thread. If the creator just replied, this is your reply to them — address what they actually said.`
          : `No prior conversation, no operator intent. Write a short cold-outreach email pitching a gifted collaboration. Don't fabricate familiarity with their content — keep it honest and direct.`;

      const userPrompt=`CREATOR DATA (only verifiable facts):
- Name: ${creator.name}
- Handle: @${creator.handle}
- Broad niche: ${creator.pocket||"swimming/triathlon"}
- Operator notes: ${creator.notes||"(none)"}

${exchanges.length>0?`CONVERSATION HISTORY (most recent 8 events):\n\n${transcript}\n\n---\n\n`:`(No prior emails exchanged with this creator yet.)\n\n`}${intentBlock}`;

      const data=await callClaude({
        model:"claude-sonnet-4-6",
        max_tokens:700,
        system,
        messages:[{role:"user",content:userPrompt}]
      });
      const newBody=(data.content?.[0]?.text||"").trim();
      if(!newBody){toast("AI returned empty response","error");return;}
      setBody(newBody);

      // If no subject set, suggest one based on the new body.
      if(!subject.trim()){
        const sd=await callClaude({
          model:"claude-haiku-4-5",
          max_tokens:40,
          messages:[{role:"user",content:`Suggest a short, natural-sounding email subject line (max 8 words, no quotes, no emoji). Email body:\n\n${newBody.slice(0,600)}\n\nReply with ONLY the subject line.`}]
        });
        const newSubject=(sd.content?.[0]?.text||"").trim().replace(/^["']|["']$/g,"");
        if(newSubject) setSubject(newSubject);
      }
      toast("AI draft ready","info");
    }catch(e){console.error(e);toast("AI write failed — see console","error");}
    setAiPersonalising(false);
  };
  const[sending,setSending]=useState(false);

  const loadFlow=(flowId)=>{
    if(!flowId) return;
    // Flow IDs are UUIDs (strings) — compare as strings, NOT parseInt
    const f=flows.find(f=>String(f.id)===String(flowId));
    if(!f||!f.steps||!f.steps[0])return;
    const replace=s=>interpolateTemplate(s, buildEmailVars(creator, brand));
    setSubject(replace(f.steps[0].subject||""));
    setBody(replace(f.steps[0].body||""));
  };

  const handleSend=async(opts={})=>{
    if(!creator.email){
      alert("No email address on file for this creator. Add it in their profile Details.");
      return;
    }
    if(!brand?.id && !brandId){
      toast("No brand context — refresh and try again", 'error');
      return;
    }
    // Subject is optional in the UI: fall back to "Re: <their subject>" in reply
    // context, else a plain brand greeting — the server rejects an empty subject.
    const effSubject = subject.trim() || (isReplyContext ? replySubjectFor(lastInbound) : "") || `Hello from ${brand?.name||"us"}`;
    setSending(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-creator-email`,{
        method:'POST',
        headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},
        body: JSON.stringify({
          brand_id: brand?.id || brandId,
          creator_handle: creator.handle,
          to: creator.email,
          subject: effSubject,
          body,
          flow_id: selectedFlow || null,
          confirm_no_order: opts.confirmNoOrder || false,
          // Thread the reply under the creator's message so they get a reply,
          // not a brand-new email. Anchors come from the inbound we're answering
          // (null for older inbounds captured before threading was stored).
          in_reply_to: isReplyContext ? (lastInbound?.rfcMessageId || null) : null,
          gmail_thread_id: isReplyContext ? (lastInbound?.gmailThreadId || null) : null,
        }),
      });
      const result=await resp.json();
      if(!resp.ok){
        if(result.error==='no_order_for_fulfillment'){
          setSending(false);
          if(await confirmDialog({title:"No order on file",body:result.message,destructive:true,confirmLabel:"Send anyway"})){
            return handleSend({confirmNoOrder:true});
          }
          return;
        }
        else if(result.error==='no_gmail_connection') toast("Connect a Gmail inbox to this brand first (Settings → Email)",'error');
        else if(result.error==='not_authorized') toast("Not authorised to send for this brand",'error');
        else if(result.error==='recipient_suppressed') toast(result.message||"This recipient is on the Suppress List. Remove them from Agency → Suppress list first.",'error');
        else if(result.error==='gmail_send_failed') toast(`Gmail send failed: ${result.message?.slice(0,100)||result.status}`,'error');
        else toast(`Send failed: ${result.error||resp.status}`,'error');
        setSending(false);
        return;
      }
      onSent({type:"email_sent",subject:effSubject,to:creator.email,body,flowId:selectedFlow||null,queueId:result.queue_id||null,fromInbox:result.from||null});
      toast(`Email sent to ${creator.name}`);
      setSending(false);
      onClose();
    }catch(e){
      console.error(e);
      toast(`Send failed: ${e.message||'network error'}`,'error');
      setSending(false);
    }
  };

  return(<Modal title={isReplyContext?"↩ Reply":"✉️ Send Email"} subtitle={`To: ${creator.email||"No email on file"}`} onClose={onClose} width={620}>
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {/* Inbound message preview — when replying, render the message we're
          replying TO at the top so the operator never loses the context they
          clicked into. Previously the modal hid the inbound body once "Reply"
          was clicked; now it's pinned. */}
      {isReplyContext&&lastInbound&&(()=>{
        const inboundBody = lastInbound.bodySnippet || lastInbound.text || "";
        return(<div style={{border:`1px solid #FDE68A`,background:"#FFFBEB",borderRadius:10,padding:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:"#92400E",textTransform:"uppercase",letterSpacing:".06em"}}>Replying to</div>
            <div style={{fontSize:11,color:"#92400E"}}>{new Date(lastInbound.ts).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})}</div>
          </div>
          {lastInbound.subject&&<div style={{fontSize:13,fontWeight:700,color:DARK,marginBottom:6}}>{lastInbound.subject}</div>}
          {inboundBody
            ?<div style={{fontSize:13,color:"#374151",lineHeight:1.6,whiteSpace:"pre-wrap",maxHeight:220,overflowY:"auto",background:"#fff",border:"1px solid #FEF3C7",borderRadius:7,padding:"10px 12px"}}>{inboundBody}</div>
            :<div style={{fontSize:12,color:MUTED,fontStyle:"italic"}}>(No message body captured)</div>
          }
          <InboundAttachments images={lastInbound.images||[]} attachments={lastInbound.attachments||[]} gmailMessageId={lastInbound.gmailMessageId||null} compact/>
        </div>);
      })()}
      {/* AI reply suggestions — only when this opens in reply-context (inbound message
          needs response). Most common: paid-only, custom Qs, VIP touch — the auto-reply
          system handles the simpler positive/negative/missing-fields cases. */}
      {isReplyContext&&(<div style={{border:`1px solid #DDD6FE`,background:"linear-gradient(180deg,#FAF5FF 0%,#fff 100%)",borderRadius:10,padding:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <span style={{fontSize:14}}>✨</span>
            <div style={{fontSize:12,fontWeight:700,color:"#5B21B6"}}>AI reply suggestions</div>
            {lastInbound?.classification&&<span style={{background:"#fff",color:"#5B21B6",border:"1px solid #DDD6FE",borderRadius:5,padding:"1px 7px",fontSize:10,fontWeight:600}}>classified: {lastInbound.classification}</span>}
          </div>
          <button onClick={()=>{setSuggestions(null);fetchSuggestions();}} disabled={suggestionsLoading} style={{border:"1px solid #DDD6FE",background:"#fff",color:"#5B21B6",borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:600,cursor:suggestionsLoading?"wait":"pointer"}}>↻ Refresh</button>
        </div>
        {suggestionsLoading&&<div style={{padding:"14px 0",textAlign:"center",fontSize:12,color:"#6B7280"}}>⏳ Reading conversation history…</div>}
        {suggestionsError&&<div style={{padding:"10px",fontSize:11,color:"#991B1B",background:"#FEF2F2",borderRadius:6}}>Couldn't generate: {suggestionsError}</div>}
        {suggestions&&!suggestionsLoading&&(<div style={{display:"flex",flexDirection:"column",gap:8}}>
          {suggestions.map((s,i)=>{
            const toneColors = {friendly:"#16A34A", professional:"#3B82F6", firm:"#DC2626"};
            const toneCol = toneColors[s.tone] || "#6B7280";
            return(<div key={i} style={{background:"#fff",border:`1px solid ${BORDER}`,borderRadius:8,padding:"10px 12px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:6}}>
                <div style={{display:"flex",alignItems:"center",gap:7,minWidth:0,flex:1}}>
                  <span style={{background:toneCol+"18",color:toneCol,border:`1px solid ${toneCol}30`,borderRadius:4,padding:"1px 7px",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".04em",whiteSpace:"nowrap"}}>{s.tone||"option"}</span>
                  <div style={{fontSize:12,fontWeight:600,color:DARK,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.label}</div>
                </div>
                <button onClick={()=>applySuggestion(s)} style={{border:"none",background:DARK,color:G,borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0}}>Use this</button>
              </div>
              <div style={{fontSize:12,color:"#374151",lineHeight:1.55,whiteSpace:"pre-wrap"}}>{s.body}</div>
            </div>);
          })}
          <div style={{fontSize:10,color:MUTED,textAlign:"center",fontStyle:"italic",marginTop:2}}>Click "Use this" to load a draft into the editor — edit before sending.</div>
        </div>)}
      </div>)}

      {flows.length>0&&(()=>{
        const picked = flows.find(f=>String(f.id)===String(selectedFlow));
        const hasPdf = !!picked?.attach_pdf_drive_file_id;
        return(<div>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:6}}>Load from flow</div>
          <select value={selectedFlow} onChange={e=>{setSelectedFlow(e.target.value);loadFlow(e.target.value);}} style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",background:"#fff"}}>
            <option value="">— Select a flow template —</option>
            {flows.map(f=><option key={f.id} value={f.id}>{f.name}{f.attach_pdf_drive_file_id?" 📎":""}</option>)}
          </select>
          {hasPdf&&<div style={{fontSize:11,color:G,fontWeight:600,marginTop:6,display:"flex",alignItems:"center",gap:6,background:"#F0FDF4",border:`1px solid ${G}30`,borderRadius:6,padding:"6px 10px"}}>📎 This flow has a PDF attached — it will be sent with this email.</div>}
        </div>);
      })()}
      {/* Saved templates — brand's reusable {name,subject,body} snippets (managed in
          Flows tab). Renders in BOTH new-email and reply contexts. Same "Replace
          draft?" guard as the reply chips. Prefills subject + body, interpolated. */}
      {savedTemplates.length>0&&(<div>
        <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:6}}>Saved templates</div>
        <select value="" onChange={async e=>{
          const t=savedTemplates.find(x=>String(x.id)===String(e.target.value));
          if(!t) return;
          if(body.trim() && !(await confirmDialog({title:"Replace draft?",body:"This will overwrite your current draft with the saved template.",confirmLabel:"Replace"}))) return;
          setSubject(replaceVars(t.subject||""));
          setBody(replaceVars(t.body||""));
        }} style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",background:"#fff"}}>
          <option value="">— Insert a saved template —</option>
          {savedTemplates.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>)}
      <div>
        <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:6}}>Subject</div>
        <input value={subject} onChange={e=>setSubject(e.target.value)} placeholder={isReplyContext?"Re: … (filled in automatically — stays in their thread)":"Email subject..."} style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
      </div>
      {isReplyContext&&(()=>{
        // Operator-friendly quick-reply starters. The most common AM turns —
        // acknowledge address, ask for rate, etc. Each chip prefills the body
        // (keeping any existing draft as a "discard?" beat handled by browser
        // confirm only if the body has content).
        const firstName = creator.name.split(" ")[0];
        const senderName = brand?.senderName||brand?.name||"";
        const brandName = brand?.name||"";
        const replyTemplates = [
          {label:"Thanks – sending product", body:`Hi ${firstName},\n\nThanks so much! I'll get the product out to you in the next day or two and send tracking once it ships.\n\nCheers,\n${senderName}`},
          {label:"Need address", body:`Hi ${firstName},\n\nGreat to hear! Could you send through your full name + shipping address so I can get the product to you?\n\nThanks,\n${senderName}`},
          {label:"What's your rate?", body:`Hi ${firstName},\n\nThanks for getting back to me. Could you share what your usual rate is for a paid collab? Happy to talk numbers and find the right fit.\n\n${senderName}`},
          {label:"Decline (polite)", body:`Hi ${firstName},\n\nTotally understand — no worries at all. We'll keep your details on file and happy to revisit down the line if anything changes.\n\nThanks for considering it!\n${senderName}`},
          {label:"Gifted-only (decline paid)", body:`Hi ${firstName},\n\nAppreciate you sharing your rates. We're running this collab as gifted-only for now — no budget for paid placements on this one. If that doesn't work, all good, and we'd love to revisit when we have paid budgets opening up.\n\nThanks either way,\n${senderName}`},
          {label:"Brief follow-up", body:`Hi ${firstName},\n\nJust checking in on the ${brandName} brief — anything you need from us to help with the post? Happy to clarify or send extra photos.\n\nThanks,\n${senderName}`},
        ];
        return(<div style={{borderTop:`1px solid ${BORDER}`,paddingTop:10}}>
          <div style={{fontSize:11,fontWeight:700,color:MUTED,textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>Reply templates</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {replyTemplates.map((t,i)=>(<button key={i} onClick={async()=>{
              if(body.trim() && !(await confirmDialog({title:"Replace draft?",body:"This will overwrite your current draft with the template body.",confirmLabel:"Replace"}))) return;
              setBody(t.body);
              if(!subject.trim() && lastInbound?.subject){
                setSubject(lastInbound.subject.startsWith("Re:")?lastInbound.subject:`Re: ${lastInbound.subject}`);
              }
            }} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:20,padding:"5px 12px",fontSize:11,cursor:"pointer",color:DARK,fontWeight:600}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=G;e.currentTarget.style.background="#F0FDF4";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=BORDER;e.currentTarget.style.background="#fff";}}>
              {t.label}
            </button>))}
          </div>
        </div>);
      })()}
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <div style={{fontSize:12,fontWeight:600,color:DARK}}>Body</div>
          <button onClick={aiPersonalise} disabled={aiPersonalising} title={body.includes("[ADD 1 PERSONALISED LINE HERE")?"Fill the [PERSONALISED LINE] placeholder":body.trim().length>0?"Rewrite into a complete email":"Generate a fresh outreach email"} style={{background:aiPersonalising?"#f0f0f0":DARK,color:aiPersonalising?"#9ab8a6":G,border:"none",borderRadius:7,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:aiPersonalising?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:5}}>
            {aiPersonalising?<>⏳ Generating…</>:body.includes("[ADD 1 PERSONALISED LINE HERE")?<>✨ Fill in</>:body.trim().length>0?<>✨ AI rewrite</>:<>✨ AI write</>}
          </button>
        </div>
        <textarea value={body} onChange={e=>setBody(e.target.value)} rows={8} placeholder="Email body..." style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit",boxSizing:"border-box",lineHeight:1.6}}/>
      </div>
      <div style={{fontSize:11,color:"#9ab8a6",background:"#f8faf9",padding:"8px 12px",borderRadius:7}}>
        💡 Variables: <code>{"{{firstname}}"}</code>, <code>{"{{discount_code}}"}</code>, <code>{"{{name}}"}</code> — pre-filled automatically
      </div>
      <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:4}}>
        <button onClick={onClose} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button>
        <button onClick={handleSend} disabled={!body||sending} style={{background:body?DARK:"#f0f0f0",color:body?G:"#9ab8a6",border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:body?"pointer":"not-allowed"}}>
          {sending?"Sending...":"✉️ Send Email"}
        </button>
      </div>
    </div>
  </Modal>);
}

function SendProductModal({creator,onClose,onSent,brand=null}){
  const[creatorMsg,setCreatorMsg]=useState("");
  const[aiLoading,setAiLoading]=useState(false);
  const[productSearch,setProductSearch]=useState("");
  const[cart,setCart]=useState([]); // [{product, variantId, variantTitle, qty}]
  const[activeProduct,setActiveProduct]=useState(null); // product being configured
  const[activeVariant,setActiveVariant]=useState("");
  const[activeQty,setActiveQty]=useState(1);
  // Load the brand's real Shopify catalog (active products + variants + price).
  const[products,setProducts]=useState([]);
  const[loadingProducts,setLoadingProducts]=useState(true);
  useEffect(()=>{(async()=>{
    try{
      const{products:prods}=await fetchShopifyCatalog("products");
      setProducts((prods||[]).filter(p=>p.status==="active"&&p.title)
        .map(p=>({...p, img:p.image, inventory:p.total_inventory}))
        .sort((a,b)=>a.title.localeCompare(b.title)));
    }catch(e){ console.warn("catalog load failed",e); }
    finally{ setLoadingProducts(false); }
  })();},[]);

  const addr=[creator.addressStreet,creator.addressCity,creator.addressState,creator.addressPostcode,creator.addressCountry||creator.market].filter(Boolean).join(", ");
  const filteredProducts=products.filter(p=>p.title.toLowerCase().includes(productSearch.toLowerCase()));

  const selectProduct=(p)=>{
    setActiveProduct(p);
    setActiveVariant(p.variantId||(p.variants?.[0]?.id)||"");
    setActiveQty(1);
  };

  const addToCart=()=>{
    if(!activeProduct)return;
    const variantTitle=activeProduct.variants?.find(v=>v.id===activeVariant)?.title||"Default";
    const item={product:activeProduct,variantId:activeVariant||activeProduct.variantId,variantTitle,qty:activeQty,key:`${activeProduct.sku}_${activeVariant}`};
    setCart(prev=>{
      const existing=prev.findIndex(i=>i.key===item.key);
      if(existing>=0){const updated=[...prev];updated[existing]={...updated[existing],qty:updated[existing].qty+activeQty};return updated;}
      return [...prev,item];
    });
    setActiveProduct(null);
    setProductSearch("");
  };

  const removeFromCart=(key)=>setCart(prev=>prev.filter(i=>i.key!==key));
  const updateCartQty=(key,qty)=>setCart(prev=>prev.map(i=>i.key===key?{...i,qty:Math.max(1,qty)}:i));

  const runAiMatch=async()=>{
    if(!creatorMsg.trim())return;
    setAiLoading(true);
    try{
      const productList=products.map(p=>p.title).join(", ");
      const data=await callClaude({
          model:"claude-sonnet-4-6",
          max_tokens:300,
          messages:[{role:"user",content:`A creator said: "${creatorMsg}". From this product list: ${productList}. List ALL products they mentioned, one per line, exact title only. If they mentioned variants (colors, sizes), note them in brackets. Reply with ONLY the product lines, nothing else.`}]
      });
      const lines=(data.content?.[0]?.text||"").split("\n").map(l=>l.trim()).filter(Boolean);
      lines.forEach(line=>{
        const cleanLine=line.replace(/\[.*\]/,"").trim();
        const found=products.find(p=>p.title.toLowerCase()===cleanLine.toLowerCase())||
                     products.find(p=>cleanLine.toLowerCase().includes(p.title.toLowerCase().split(" ").slice(0,3).join(" ").toLowerCase()))||
                     products.find(p=>p.title.toLowerCase().split(" ").some(w=>w.length>4&&cleanLine.toLowerCase().includes(w)));
        if(found){
          const item={product:found,variantId:found.variantId||(found.variants?.[0]?.id)||"",variantTitle:found.variants?.[0]?.title||"Default",qty:1,key:`${found.sku}_${found.variantId||found.variants?.[0]?.id||""}_auto`};
          setCart(prev=>prev.some(i=>i.product.sku===found.sku)?prev:[...prev,item]);
        }
      });
    }catch(e){/* silent */}
    setAiLoading(false);
  };

  const handleConfirm=()=>{
    if(!cart.length)return;
    const summary=cart.map(i=>`${i.qty}× ${i.product.title}${i.variantTitle!=="Default"?" ("+i.variantTitle+")":""}`).join(", ");
    onSent({type:"order_pending",product:summary,cart,address:addr,creatorMsg});
    // Auto-update creator status to Ordered
    if(onSent) onSent({type:"status_update",newStatus:"Negotiation"});
    const shopSlug=(brand?.shopifyUrl||"effortlessswimming").replace(".myshopify.com","").replace("https://","");window.open(`https://admin.shopify.com/store/${shopSlug}/draft_orders/new`,"_blank");
    onClose();
  };

  const total=cart.reduce((s,i)=>s+(i.product.price*i.qty),0);

  return(<Modal title="📦 Send Product" subtitle={`Order for ${creator.name}`} onClose={onClose} width={680}>
    <div style={{display:"grid",gridTemplateColumns:"1fr 260px",gap:16,minHeight:400}}>

      {/* Left — product picker */}
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {/* AI match */}
        <div style={{background:"#f8fdf8",border:`1px solid ${BORDER}`,borderRadius:10,padding:14}}>
          <div style={{fontSize:12,fontWeight:700,color:DARK,marginBottom:6}}>🤖 Paste creator's message</div>
          <textarea value={creatorMsg} onChange={e=>setCreatorMsg(e.target.value)} rows={2} placeholder={`"I'd love the Recon Red and a swim cap please"`} style={{width:"100%",padding:"8px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:12,outline:"none",resize:"none",fontFamily:"inherit",boxSizing:"border-box"}}/>
          <button onClick={runAiMatch} disabled={!creatorMsg.trim()||aiLoading} style={{marginTop:8,background:creatorMsg.trim()?DARK:"#f0f0f0",color:creatorMsg.trim()?G:"#9ab8a6",border:"none",borderRadius:7,padding:"7px 16px",fontWeight:700,fontSize:12,cursor:creatorMsg.trim()?"pointer":"not-allowed"}}>
            {aiLoading?"Matching...":"🤖 Auto-add matched products"}
          </button>
        </div>

        {/* Search */}
        <input value={productSearch} onChange={e=>setProductSearch(e.target.value)} placeholder="Search products..." style={{padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none"}}/>

        {/* Product list */}
        <div style={{flex:1,overflowY:"auto",maxHeight:260,border:`1px solid ${BORDER}`,borderRadius:10,background:"#fff"}}>
          {loadingProducts
            ? <div style={{padding:"20px 12px",fontSize:12,color:MUTED,textAlign:"center"}}>Loading catalog…</div>
            : products.length===0
              ? <div style={{padding:"20px 12px",fontSize:12,color:MUTED,textAlign:"center",lineHeight:1.5}}>No products loaded.<br/>Check Shopify is connected for this brand (Integrations).</div>
              : filteredProducts.length===0
                ? <div style={{padding:"20px 12px",fontSize:12,color:MUTED,textAlign:"center"}}>No products match “{productSearch}”.</div>
                : null}
          {filteredProducts.map((p,i)=>(
            <div key={i} onClick={()=>selectProduct(p)} style={{display:"flex",gap:10,padding:"10px 12px",cursor:"pointer",background:activeProduct?.sku===p.sku?"#f0fdf4":"transparent",borderBottom:i<filteredProducts.length-1?`1px solid ${BORDER}`:"none",alignItems:"center"}} onMouseEnter={e=>{if(activeProduct?.sku!==p.sku)e.currentTarget.style.background="#f8faf9";}} onMouseLeave={e=>{e.currentTarget.style.background=activeProduct?.sku===p.sku?"#f0fdf4":"transparent";}}>
              <img src={p.img} alt="" style={{width:36,height:36,objectFit:"cover",borderRadius:6,flexShrink:0}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:600,color:DARK}}>{p.title}</div>
                <div style={{fontSize:11,color:MUTED}}>AUD ${p.price} · {p.inventory>0?`${p.inventory} in stock`:<span style={{color:"#EF4444"}}>Out of stock</span>}</div>
              </div>
              {cart.some(i=>i.product.sku===p.sku)&&<span style={{color:G,fontSize:12,fontWeight:700}}>✓ Added</span>}
            </div>
          ))}
        </div>

        {/* Variant + qty picker */}
        {activeProduct&&(
          <div style={{background:"#f8fdf8",border:`1px solid ${G}40`,borderRadius:10,padding:14}}>
            <div style={{fontSize:12,fontWeight:700,color:DARK,marginBottom:8}}>{activeProduct.title}</div>
            {activeProduct.variants&&activeProduct.variants.length>0&&(
              <div style={{marginBottom:10}}>
                <div style={{fontSize:11,color:MUTED,marginBottom:6}}>Variant</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {activeProduct.variants.map(v=><button key={v.id} onClick={()=>setActiveVariant(v.id)} style={{padding:"4px 12px",borderRadius:7,border:`1px solid ${activeVariant===v.id?G:BORDER}`,background:activeVariant===v.id?"#f0fdf4":"#fff",color:activeVariant===v.id?G:DARK,fontSize:11,fontWeight:activeVariant===v.id?700:400,cursor:"pointer"}}>{v.title}</button>)}
                </div>
              </div>
            )}
            <div style={{display:"flex",gap:10,alignItems:"flex-end"}}>
              <div><div style={{fontSize:11,color:MUTED,marginBottom:4}}>Qty</div><input type="number" value={activeQty} min={1} onChange={e=>setActiveQty(parseInt(e.target.value)||1)} style={{width:60,padding:"6px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,outline:"none"}}/></div>
              <button onClick={addToCart} style={{background:DARK,color:G,border:"none",borderRadius:8,padding:"8px 18px",fontWeight:700,fontSize:12,cursor:"pointer",marginBottom:0}}>+ Add to order</button>
              <button onClick={()=>setActiveProduct(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:MUTED,padding:"8px"}}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Right — cart */}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        <div style={{fontWeight:700,fontSize:13,color:DARK}}>Order ({cart.length} item{cart.length!==1?"s":""})</div>

        {cart.length===0
          ?<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",border:`2px dashed ${BORDER}`,borderRadius:10,color:MUTED,fontSize:12,textAlign:"center",padding:20,minHeight:200}}>No products added yet.<br/>Search or use AI match.</div>
          :<div style={{flex:1,display:"flex",flexDirection:"column",gap:8}}>
            {cart.map(item=>(
              <div key={item.key} style={{background:"#fff",border:`1px solid ${BORDER}`,borderRadius:10,padding:10}}>
                <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                  <img src={item.product.img} alt="" style={{width:40,height:40,objectFit:"cover",borderRadius:7,flexShrink:0}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:11,fontWeight:700,color:DARK,lineHeight:1.3}}>{item.product.title}</div>
                    {item.variantTitle!=="Default"&&<div style={{fontSize:10,color:MUTED,marginTop:2}}>{item.variantTitle}</div>}
                    <div style={{fontSize:10,color:G,marginTop:2}}>AUD ${(item.product.price*item.qty).toFixed(2)}</div>
                  </div>
                  <button onClick={()=>removeFromCart(item.key)} style={{border:"none",background:"none",cursor:"pointer",color:"#EF4444",fontSize:14,padding:2,flexShrink:0}}>✕</button>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6,marginTop:8}}>
                  <button onClick={()=>updateCartQty(item.key,item.qty-1)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:5,width:22,height:22,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",color:DARK}}>−</button>
                  <span style={{fontSize:12,fontWeight:700,color:DARK,minWidth:16,textAlign:"center"}}>{item.qty}</span>
                  <button onClick={()=>updateCartQty(item.key,item.qty+1)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:5,width:22,height:22,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",color:DARK}}>+</button>
                </div>
              </div>
            ))}
            <div style={{borderTop:`1px solid ${BORDER}`,paddingTop:8,marginTop:4}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,fontWeight:700,color:DARK}}><span>Total</span><span>AUD ${total.toFixed(2)}</span></div>
            </div>
          </div>
        }

        {/* Shipping */}
        <div style={{background:"#f8faf9",borderRadius:8,padding:"10px 12px",fontSize:11,color:addr?DARK:MUTED}}>
          <div style={{fontWeight:600,marginBottom:4}}>📦 Ship to:</div>
          <div style={{lineHeight:1.5}}>{addr||"⚠️ No address on file"}</div>
        </div>

        <button onClick={handleConfirm} disabled={!cart.length} style={{background:cart.length?DARK:"#f0f0f0",color:cart.length?G:"#9ab8a6",border:"none",borderRadius:9,padding:"10px 0",fontWeight:700,fontSize:13,cursor:cart.length?"pointer":"not-allowed",width:"100%"}}>
          Open Shopify to create order →
        </button>
        <div style={{fontSize:11,color:MUTED,textAlign:"center",marginTop:6}}>Opens a blank Shopify draft order — enter the items above manually (auto-fill isn't wired yet).</div>
      </div>
    </div>
  </Modal>);
}


// Renders a plain-text email body with Gmail-style quote handling:
// detects where the quoted reply-chain begins (lines like "On ... wrote:",
// "pet, 22. svi 2026. u 15:32 X napisao je:", or any consecutive run of
// >-prefixed lines), splits the body into "new reply" + "quoted history",
// renders the quote section behind a "Show quoted text" toggle.
function EmailBodyText({body}){
  const [showQuoted, setShowQuoted] = useState(false);
  if(!body) return null;

  // Normalize newlines + strip standalone Outlook "________" separators that
  // some clients insert above their quoted reply chain.
  const text = body.replace(/\r\n/g, "\n").replace(/^_{5,}\s*$/gm, "").trim();
  const lines = text.split("\n");

  // Find where the quoted section begins. Try in priority order:
  //   1. "On ... wrote:" / "On ... at HH:MM ... wrote:" (English Gmail)
  //   2. ", wrote:" / "wrote:" line in any language ("napisao je:", "schrieb:", "a écrit:")
  //   3. First line that starts with ">" (after non-quoted content above it)
  let quoteStart = -1;
  for(let i=0;i<lines.length;i++){
    const ln = lines[i].trim();
    if(/^(On\b.*wrote:)$/i.test(ln)) { quoteStart = i; break; }
    if(/(napisao je|napisala je|wrote|schrieb|a écrit|escribió|ha scritto|kirjoitti):\s*$/i.test(ln)) { quoteStart = i; break; }
    // Localized Gmail header sometimes is "pet, 22. svi 2026. u 15:32 X napisao je:" on one line
    if(/napisao je|napisala je|wrote:|schrieb:|a écrit:/i.test(ln) && ln.length < 200) { quoteStart = i; break; }
  }
  // Fall back to first >-prefixed line
  if(quoteStart === -1){
    for(let i=0;i<lines.length;i++){
      if(/^>+\s?/.test(lines[i])){ quoteStart = i; break; }
    }
  }

  const main = (quoteStart === -1 ? lines : lines.slice(0, quoteStart)).join("\n").trim();
  const quoted = quoteStart === -1 ? "" : lines.slice(quoteStart).join("\n").trim();

  // Paragraph-split by blank lines for readable spacing.
  const renderParagraphs = (t) => t.split(/\n{2,}/).map((p,i)=>(
    <div key={i} style={{marginBottom:i<t.split(/\n{2,}/).length-1?10:0,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{p.replace(/\n/g,"\n")}</div>
  ));

  return(<div style={{fontSize:13,color:"#1f2937",lineHeight:1.65}}>
    {main && renderParagraphs(main)}
    {quoted && (<div style={{marginTop:main?14:0}}>
      <button onClick={()=>setShowQuoted(s=>!s)} style={{background:"none",border:"none",padding:0,fontSize:11,color:MUTED,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
        <span style={{display:"inline-block",width:18,height:1,background:MUTED}}/>
        {showQuoted?"Hide quoted text":"Show quoted text"} ▾
      </button>
      {showQuoted && (<div style={{borderLeft:`3px solid #e5e7eb`,paddingLeft:12,marginTop:8,fontSize:12,color:"#6B7280",lineHeight:1.6,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{quoted.replace(/^>+\s?/gm,"")}</div>)}
    </div>)}
  </div>);
}

function CreatorProfile({creator,onBack,onRemove,onDelete,onStatusChange,onUpdate,kanbanData={},agreements=[],activityLog=[],flows=[],onLogActivity,brand=null,campaigns=[],brandId=null,allCreators=[]}){
  const c=creator;
  const[showRemoveConfirm,setShowRemoveConfirm]=useState(false);
  const[deleteArmed,setDeleteArmed]=useState(false); // two-step delete inside the remove modal — no nested dialog
  const[deleting,setDeleting]=useState(false);
  const[currentStatus,setCurrentStatus]=useState(c.status);
  // Keep currentStatus in sync when creator prop updates externally
  useEffect(()=>{setCurrentStatus(c.status);},[c.status]);

  // ---------- Flow assignment (auto, based on creator status) ----------
  // System chooses the flow from c.status via pickFlowForStatus. When status changes
  // phase (e.g. Outreached → Ordered) the old assignment is completed and a new one
  // is created. No manual picker — the lifecycle drives it.
  const[assignment,setAssignment]=useState(null);
  const[assignmentLoaded,setAssignmentLoaded]=useState(false);

  // Load active assignment. Reloads when status changes so we see App-level reconcile writes.
  useEffect(()=>{
    if(!brandId||!c.handle){setAssignmentLoaded(true);return;}
    let cancelled=false;
    (async()=>{
      setAssignmentLoaded(false);
      const{data,error}=await supabase.from('flow_assignments')
        .select('*').eq('brand_id',brandId).eq('creator_handle',c.handle).eq('status','active').maybeSingle();
      if(cancelled)return;
      if(error){console.error('assignment load',error);setAssignmentLoaded(true);return;}
      setAssignment(data||null);
      setAssignmentLoaded(true);
    })();
    return()=>{cancelled=true;};
  },[brandId,c.handle,currentStatus]);

  // Lazy-create on profile open for legacy creators (added before this feature, never had a
  // status change since). The unique partial index on (brand_id, creator_handle) WHERE
  // status='active' prevents duplicates if App-level reconcile already wrote a row.
  useEffect(()=>{
    if(!brandId||!c.handle||!assignmentLoaded||assignment)return;
    const desired=pickFlowForStatus(currentStatus,flows,c.handle);
    if(!desired)return;
    let cancelled=false;
    (async()=>{
      // Opening a profile must never re-enroll someone the pipeline deliberately
      // stopped. Two cases the bare active-check above misses: a smart-PAUSED
      // assignment (positive reply, auto-resumes on its own — inserting a second
      // active row restarts the cold sequence in parallel), and a CANCELLED one
      // within the worker's 30d cool-down (negative reply / bounce / unsubscribe —
      // this exact path re-sent a cold step-0 to a creator 11h after they declined,
      // 9/8 ES launch). Deliberate re-engagement stays possible via an explicit
      // status change (handleStatusChange → reconcileFlowAssignment).
      const since=new Date(Date.now()-30*86400000).toISOString();
      const[{data:live},{data:recentCancel}]=await Promise.all([
        supabase.from('flow_assignments').select('id').eq('brand_id',brandId).eq('creator_handle',c.handle).in('status',['active','paused']).limit(1),
        supabase.from('flow_assignments').select('id').eq('brand_id',brandId).eq('creator_handle',c.handle).eq('status','cancelled').gte('cancelled_at',since).limit(1),
      ]);
      if(cancelled)return;
      if((live||[]).length>0||(recentCancel||[]).length>0)return;
      const{data,error}=await supabase.from('flow_assignments')
        .insert({brand_id:brandId,creator_handle:c.handle,flow_id:desired.id,status:'active'}).select().single();
      if(cancelled)return;
      if(error){
        // Likely raced with App-level reconcile — re-read to converge.
        const{data:re}=await supabase.from('flow_assignments').select('*').eq('brand_id',brandId).eq('creator_handle',c.handle).eq('status','active').maybeSingle();
        if(!cancelled)setAssignment(re||null);
        return;
      }
      setAssignment(data);
    })();
    return()=>{cancelled=true;};
  },[brandId,c.handle,currentStatus,assignmentLoaded,assignment,flows]);

  const[editField,setEditField]=useState(null);
  const[editVal,setEditVal]=useState("");
  useEffect(()=>{setEditField(null);setEditVal("");},[c.handle]);
  const[showEmailModal,setShowEmailModal]=useState(false);
  const[emailPreloadStep,setEmailPreloadStep]=useState(null);
  const[showProductModal,setShowProductModal]=useState(false);
  const[showUsageRights,setShowUsageRights]=useState(false);
  const[showBRoll,setShowBRoll]=useState(false);
  const[urPlatforms,setUrPlatforms]=useState(["Instagram","Facebook"]);
  const[urDuration,setUrDuration]=useState("12 months");
  const[urType,setUrType]=useState("Repurpose");
  const[urLink,setUrLink]=useState("");
  // Expanded conversation entries — click an email card to show the full body
  // (Influencer Hero pattern). Persists per profile-mount.
  const[expandedEntries,setExpandedEntries]=useState(new Set());
  // Cache of full email bodies fetched from email_queue. Activity log only
  // stores a 280-char snippet for received emails; sent emails have the full
  // body in email_queue (lookup by queueId). On expand we lazy-fetch.
  const[fullBodies,setFullBodies]=useState({});  // { queueId: bodyString }
  const fetchFullBody = async(queueId)=>{
    if(!queueId||fullBodies[queueId])return;
    setFullBodies(prev=>({...prev,[queueId]:"__loading__"}));
    const{data}=await supabase.from('email_queue').select('body').eq('id',queueId).maybeSingle();
    setFullBodies(prev=>({...prev,[queueId]:data?.body||""}));
  };
  const toggleEntryExpansion = (id, queueId)=>{
    setExpandedEntries(prev=>{
      const next = new Set(prev);
      if(next.has(id)) next.delete(id);
      else {
        next.add(id);
        if(queueId) fetchFullBody(queueId);
      }
      return next;
    });
  };

  const STATUSES_CP=["To Outreach","Outreached","Negotiation","Paid Only","Awaiting Post","Posted","Inactive"];
  const SC2={"To Outreach":"#A78BFA","Outreached":"#6B7280","Negotiation":"#3B82F6","Paid Only":"#EF4444","Awaiting Post":"#F59E0B","Posted":"#10B981","Inactive":"#9CA3AF"};

  // (Conversation state machine removed — the kanban label + status pill +
  // conversation thread + conditional "Needs review" banner cover the same
  // ground without duplicating it across three places.)

  const followUpStatus=useMemo(()=>getFollowUpStatus(c.handle,activityLog,flows),[c.handle,activityLog,flows]);

  // Campaigns this creator is in. Campaign IDs are UUIDs (strings) — the
  // previous parseInt() comparison silently returned NaN against real UUIDs
  // and made the lookup always fail, so the profile said "Not in any campaign"
  // even for creators who actually were in one.
  const creatorCampaigns=Object.entries(kanbanData).map(([id,creators])=>{
    const found=creators.find(cr=>cr.name===c.name||cr.handle===c.handle);
    if(!found)return null;
    const camp=campaigns.find(ca=>String(ca.id)===String(id))||CAMPAIGNS.find(ca=>String(ca.id)===String(id));
    return camp?{...camp,stage:found.stage}:null;
  }).filter(Boolean);

  const creatorAgreements=agreements.filter(a=>a.creator?.name===c.name||a.creator?.handle===c.handle);

  const handleStatusChange=(s)=>{setCurrentStatus(s);if(onStatusChange)onStatusChange(s);};

  // Load the most recent operator-triggered status change for this creator
  // so we can offer a one-step Undo. Only operator_actions writes here; the
  // worker / webhook auto-changes go to activity_log instead, so this Undo
  // is specifically for "I just changed it and want to take it back" cases.
  const [lastStatusChange, setLastStatusChange] = useState(null);
  useEffect(()=>{
    if(!brandId||!c.handle) return;
    let cancelled=false;
    (async()=>{
      const {data} = await supabase.from('operator_actions')
        .select('id, details, created_at')
        .eq('brand_id', brandId).eq('action','creator.status_change')
        .eq('target_id', c.handle)
        .order('created_at',{ascending:false}).limit(1).maybeSingle();
      if(cancelled) return;
      setLastStatusChange(data || null);
    })();
    return()=>{cancelled=true;};
  },[brandId, c.handle, currentStatus]);

  const undoLastStatus = async()=>{
    if(!lastStatusChange) return;
    const prev = lastStatusChange?.details?.from;
    if(!prev || !STATUS_LIST.includes(prev)) {
      toast("Previous status is invalid or no longer in the catalog", "error");
      return;
    }
    handleStatusChange(prev);
    toast(`↶ Reverted to ${prev}`, 'info');
  };
  const startEdit=(field,val)=>{setEditField(field);setEditVal(val||"");};
  const saveEdit=(field)=>{if(onUpdate)onUpdate({[field]:editVal});setEditField(null);toast("Saved");};

  // Phase 3: toggle creator membership in the per-brand Paid Leads list.
  // RPC handles DB update + ownership check; second call pushes the row to the
  // Drive sheet (or deletes it). Frontend uses fire-and-forget for the sheet
  // push — DB write is the source of truth, sheet is a mirror.
  const [paidDbBusy, setPaidDbBusy] = useState(false);
  const inPaidDb = !!c.paid_database_at;
  const togglePaidDb = async ()=>{
    if(!brandId||!c.id) return;
    if(inPaidDb && !(await confirmDialog({
      title:`Remove ${c.name} from Paid Leads?`,
      body:"The sheet row will be deleted. The creator stays in your normal pipeline; only the paid-lead tracking is removed.",
      destructive:true, confirmLabel:"Remove",
    }))) return;
    setPaidDbBusy(true);
    const{error}=await supabase.rpc('toggle_paid_database',{p_brand_id:brandId,p_creator_id:c.id,p_enabled:!inPaidDb});
    if(error){setPaidDbBusy(false);toast(`Paid Leads update failed: ${error.message}`,'error');return;}
    // Reflect locally so the badge flips immediately.
    if(onUpdate) onUpdate({paid_database_at:inPaidDb?null:new Date().toISOString(), paid_database_status:inPaidDb?null:"Open to paid"});
    // Sheet sync — fire-and-forget. If it fails the operator can retry; DB is canonical.
    try{
      const t = await window.Clerk?.session?.getToken();
      const igUrl = `https://www.instagram.com/${(c.handle||"").replace(/^@/,"")}/`;
      const path = inPaidDb ? '/paid-delete' : '/paid-upsert';
      const body = inPaidDb
        ? {brand_id:brandId, ig_link:igUrl}
        : {brand_id:brandId, brand_name:brand?.name||"", creator:{
            name:c.name, instagram:igUrl, followers:c.followers, email:c.email,
            country:c.market||c.country||"", status:"Open to paid",
          }};
      await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-drive${path}`,{
        method:'POST',headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},body:JSON.stringify(body),
      });
    }catch(e){console.error('paid sheet sync',e);}
    setPaidDbBusy(false);
    toast(inPaidDb?'✓ Removed from Paid Leads':'✓ Added to Paid Leads — sheet updating');
  };

  const handleSent=(entry)=>{
    if(entry.type==="status_update"){
      handleStatusChange(entry.newStatus);
      return;
    }
    if(onLogActivity){
      // send-creator-email already wrote the authoritative activity_log row
      // (with queueId so the body is fetchable). Optimistic-only add here so the
      // card shows instantly without inserting a duplicate row.
      if(entry.type==="email_sent") onLogActivity("email_sent",`Email sent: "${entry.subject}"`,{subject:entry.subject,to:entry.to,flowId:entry.flowId||null,queueId:entry.queueId||null,fromInbox:entry.fromInbox||null,body:entry.body||null,manual:true,_optimisticOnly:true});
      if(entry.type==="order_pending"){
        const summary=entry.cart?entry.cart.map(i=>`${i.qty}× ${i.product.title}${i.variantTitle!=="Default"?" ("+i.variantTitle+")":""}`).join(", "):entry.product;
        onLogActivity("order_pending",`Order pending: ${summary}`,{product:summary,qty:entry.cart?.reduce((s,i)=>s+i.qty,0)||1});
      }
    }
    toast(entry.type==="email_sent"?"Email opened in Gmail":"Order logged — opening Shopify");
  };

  // Manual log-reply for cases where the creator replied outside the system
  // (DM, Slack, SMS, called the operator, etc.). Captures subject + body so
  // the conversation thread renders a real card — not just an opaque "Creator
  // replied" stub the operator can't open.
  const[logReplyOpen,setLogReplyOpen]=useState(false);
  const[logReplySubject,setLogReplySubject]=useState("");
  const[logReplyBody,setLogReplyBody]=useState("");
  const[logReplyClass,setLogReplyClass]=useState("");
  const openLogReply=()=>{setLogReplySubject("");setLogReplyBody("");setLogReplyClass("");setLogReplyOpen(true);};
  const submitLogReply=()=>{
    if(onLogActivity){
      const subject = logReplySubject.trim();
      const body = logReplyBody.trim();
      const extra = {
        subject,
        bodySnippet: body.slice(0, 280),
        body,                                // full body (no truncation for manual)
        manual: true,
      };
      if(logReplyClass) extra.classification = logReplyClass;
      onLogActivity("email_received", `Creator replied${subject?`: "${subject}"`:""}${logReplyClass?` [${logReplyClass}]`:""}`, extra);
    }
    setLogReplyOpen(false);
    toast("Reply logged");
  };

  // EditableField — inline-edit row.
  // When `options` is provided, renders a <select> instead of a text input.
  // Used for Country (curated country list) so users don't free-type "AU" vs "Australia".
  const EditableField=({label,field,value,placeholder,options=null})=>(
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:`1px solid ${BORDER}`,minHeight:38}}>
      <div style={{fontSize:12,color:MUTED,fontWeight:500,minWidth:120}}>{label}</div>
      {editField===field
        ?<div style={{display:"flex",gap:6,flex:1,justifyContent:"flex-end"}}>
            {options
              ? <select autoFocus value={editVal} onChange={e=>setEditVal(e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter")saveEdit(field);if(e.key==="Escape")setEditField(null);}}
                  style={{padding:"4px 10px",border:`1px solid ${G}`,borderRadius:7,fontSize:12,outline:"none",minWidth:180,background:"#fff"}}>
                  <option value="">— Select —</option>
                  {options.map(o => typeof o === "string"
                    ? <option key={o} value={o}>{o}</option>
                    : <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              : <input autoFocus value={editVal} onChange={e=>setEditVal(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveEdit(field);if(e.key==="Escape")setEditField(null);}} placeholder={placeholder} style={{padding:"4px 10px",border:`1px solid ${G}`,borderRadius:7,fontSize:12,outline:"none",width:160}}/>}
            <button onClick={()=>saveEdit(field)} style={{background:DARK,color:G,border:"none",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Save</button>
            <button onClick={()=>setEditField(null)} style={{background:"#f8faf9",border:`1px solid ${BORDER}`,borderRadius:7,padding:"4px 8px",fontSize:11,cursor:"pointer"}}>✕</button>
          </div>
        :<div style={{display:"flex",gap:8,alignItems:"center",overflow:"hidden",maxWidth:"60%"}}>
            <span style={{fontSize:12,fontWeight:600,color:value?DARK:MUTED,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{value||<span style={{color:"#c0d4c8",fontWeight:400}}>Not set</span>}</span>
            <button onClick={()=>startEdit(field,value)} style={{border:"none",background:"none",cursor:"pointer",fontSize:10,color:MUTED,padding:"2px 5px",borderRadius:4,opacity:.5,flexShrink:0}} onMouseEnter={e=>{e.currentTarget.style.opacity=1;e.currentTarget.style.background="#f8faf9";}} onMouseLeave={e=>{e.currentTarget.style.opacity=.5;e.currentTarget.style.background="none";}}>Edit</button>
          </div>
      }
    </div>
  );

  // Common shipping countries (full names, not codes). Add more as needed —
  // these cover ~95% of kolabs's creator base.
  const COUNTRIES = ["Australia","New Zealand","United States","United Kingdom","Canada","Ireland","Singapore","Hong Kong","Germany","France","Netherlands","Spain","Italy","Sweden","Denmark","Norway","Finland","South Africa","UAE"];

  // Activity log entry icons/colors
  const entryStyle={
    status:{icon:"●",color:G},email_sent:{icon:"✉",color:"#3B82F6"},email_received:{icon:"✉",color:"#F59E0B"},
    order_pending:{icon:"📦",color:"#8B5CF6"},note:{icon:"✏",color:"#06B6D4"},edit:{icon:"📝",color:"#9ab8a6"},archive:{icon:"📦",color:"#F59E0B"},
    content_posted:{icon:"📷",color:"#EC4899"},mention_unmatched:{icon:"👀",color:"#9CA3AF"},first_post_alert:{icon:"🎯",color:"#10B981"},
  };

  return(<div style={{display:"flex",flexDirection:"column",gap:0,height:"100%"}}>
    {showEmailModal&&<SendEmailModal creator={c} flows={flows} activityLog={activityLog} onClose={()=>{setShowEmailModal(false);setEmailPreloadStep(null);}} onSent={handleSent} preloadStep={emailPreloadStep} brand={brand} brandId={brandId}/>}
    {logReplyOpen&&(<Modal title="📝 Log a manual reply" subtitle="Captures what the creator said outside the system (DM, call, SMS, etc.)" onClose={()=>setLogReplyOpen(false)} width={520}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Subject <span style={{color:MUTED,fontWeight:400}}>(optional)</span></div>
          <input value={logReplySubject} onChange={e=>setLogReplySubject(e.target.value)} placeholder="e.g. Re: Collaboration opportunity" style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
        </div>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>What did they say? *</div>
          <textarea autoFocus value={logReplyBody} onChange={e=>setLogReplyBody(e.target.value)} rows={6} placeholder={"Paste the message they sent, or write what they said on the call.\n\ne.g. \"Sounds great, here's my address: …\""} style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit",boxSizing:"border-box",lineHeight:1.55}}/>
        </div>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Classification <span style={{color:MUTED,fontWeight:400}}>(optional)</span></div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {[
              {v:"",label:"None"},
              {v:"positive",label:"Positive",color:"#16A34A"},
              {v:"negative",label:"Negative",color:"#DC2626"},
              {v:"paid_only",label:"Paid only",color:"#92400E"},
              {v:"neutral",label:"Neutral",color:"#6B7280"},
            ].map(opt=>(
              <button key={opt.v} onClick={()=>setLogReplyClass(opt.v)} style={{border:`1.5px solid ${logReplyClass===opt.v?(opt.color||G):BORDER}`,background:logReplyClass===opt.v?(opt.color||G)+"18":"#fff",color:logReplyClass===opt.v?(opt.color||DARK):MUTED,borderRadius:7,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:"pointer"}}>{opt.label}</button>
            ))}
          </div>
          <div style={{fontSize:10,color:MUTED,marginTop:6,lineHeight:1.5}}>Helps the kanban label the card correctly. Skip if unsure.</div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={()=>setLogReplyOpen(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={submitLogReply} disabled={!logReplyBody.trim()} style={{background:logReplyBody.trim()?DARK:"#f0f0f0",color:logReplyBody.trim()?G:"#9ab8a6",border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:logReplyBody.trim()?"pointer":"not-allowed"}}>Log reply</button>
        </div>
      </div>
    </Modal>)}
    {showProductModal&&<SendProductModal creator={c} onClose={()=>setShowProductModal(false)} onSent={handleSent} brand={brand}/>}

    {showUsageRights&&(<Modal title="🎬 Request Usage Rights" subtitle={`For content posted by ${c.name}`} onClose={()=>setShowUsageRights(false)} width={520}>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:8}}>Content link</div>
          <input value={urLink} onChange={e=>setUrLink(e.target.value)} placeholder="https://instagram.com/p/..." style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          <div style={{fontSize:11,color:MUTED,marginTop:4}}>Paste the link to their specific post you want rights to</div>
        </div>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:8}}>Type</div>
          <div style={{display:"flex",gap:6}}>
            {["Repurpose","Whitelist","Both"].map(t=><button key={t} onClick={()=>setUrType(t)} style={{flex:1,padding:"8px",border:`1px solid ${urType===t?G:BORDER}`,borderRadius:8,background:urType===t?"#f0fdf4":"#fff",color:urType===t?G:DARK,fontSize:12,fontWeight:urType===t?700:400,cursor:"pointer"}}>{t}</button>)}
          </div>
          <div style={{fontSize:11,color:MUTED,marginTop:6}}><strong>Repurpose</strong> — use in your own ads/content · <strong>Whitelist</strong> — boost their post from their account</div>
        </div>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:8}}>Platforms</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {["Instagram","Facebook","TikTok","YouTube","Email","Website"].map(p=>{const on=urPlatforms.includes(p);return(<button key={p} onClick={()=>setUrPlatforms(prev=>on?prev.filter(x=>x!==p):[...prev,p])} style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${on?G:BORDER}`,background:on?"#f0fdf4":"#fff",color:on?G:DARK,fontSize:12,fontWeight:on?700:400,cursor:"pointer"}}>{p}</button>);})}
          </div>
        </div>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:8}}>Duration</div>
          <div style={{display:"flex",gap:6}}>
            {["6 months","12 months","2 years","Perpetual"].map(d=><button key={d} onClick={()=>setUrDuration(d)} style={{flex:1,padding:"7px 6px",border:`1px solid ${urDuration===d?G:BORDER}`,borderRadius:8,background:urDuration===d?"#f0fdf4":"#fff",color:urDuration===d?G:DARK,fontSize:11,fontWeight:urDuration===d?700:400,cursor:"pointer"}}>{d}</button>)}
          </div>
        </div>
        <div style={{background:"#f8faf9",borderRadius:10,padding:"12px 14px",fontSize:12,color:MUTED,lineHeight:1.6}}>
          📧 Will send an email to {c.name} asking for {urType.toLowerCase()} rights on {urPlatforms.join(", ")} for {urDuration}.
          An agreement is created and logged to their profile.
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={()=>setShowUsageRights(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={async()=>{
            if(!c.email){toast("No email on file — add it in Creator details first","error");return;}
            if(!brandId){toast("Brand not loaded yet","error");return;}
            const subject=`Usage Rights Request — ${brand?.name||"Brand"}`;
            const body=`Hi ${c.name.split(" ")[0]},\n\nThank you for your recent post! We loved it.\n\nWe'd love to request usage rights to use your content in our marketing:\n\n• Type: ${urType}\n• Platforms: ${urPlatforms.join(", ")}\n• Duration: ${urDuration}${urLink?`\n• Content: ${urLink}`:""}\n\nIf you agree, please reply confirming and we'll send over a formal agreement.\n\nBest,\n${brand?.senderName||""}`;
            try{
              const token=await window.Clerk?.session?.getToken();
              const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-creator-email`,{
                method:'POST',headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},
                body:JSON.stringify({brand_id:brandId,creator_handle:c.handle,to:c.email,subject,body}),
              });
              const result=await resp.json();
              if(!resp.ok){toast(`Send failed: ${result.message||result.error||resp.status}`,'error');return;}
              if(onLogActivity) onLogActivity("status",`Usage rights requested (${urType} · ${urPlatforms.join(", ")} · ${urDuration})`,{usageRights:{status:"requested",type:urType,platforms:urPlatforms,duration:urDuration,link:urLink}});
              if(onUpdate) onUpdate({usageRights:{status:"requested",type:urType,platforms:urPlatforms,duration:urDuration,link:urLink,requested:new Date().toLocaleDateString("en-AU")}});
              toast("✓ Usage rights request emailed via brand Gmail");
              setShowUsageRights(false);
            }catch(e){toast(`Send failed: ${e.message||'network error'}`,'error');}
          }} style={{background:DARK,color:G,border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>🎬 Send Request</button>
        </div>
      </div>
    </Modal>)}

    {showBRoll&&(<Modal title="🎥 Request B-Roll" subtitle={`Raw footage from ${c.name}`} onClose={()=>setShowBRoll(false)} width={480}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{background:"#f8faf9",borderRadius:10,padding:"12px 14px",fontSize:12,color:MUTED,lineHeight:1.6}}>
          Sends a pre-written email asking {c.name} to share their raw footage. You can then edit, repurpose, and use it in your own content.
        </div>
        <div style={{background:"#fff",border:`1px solid ${BORDER}`,borderRadius:10,padding:14}}>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:8}}>Preview email</div>
          <div style={{fontSize:12,color:MUTED,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{`Hi ${c.name.split(" ")[0]},\n\nWe absolutely loved your recent content!\n\nWould you be able to share the raw footage with us? We'd love to use it for some of our own marketing.\n\nYou can share it via:\n• Google Drive${brand?.fromEmail?` (share to ${brand.fromEmail})`:""}\n• Dropbox\n• WeTransfer\n\nThank you so much!\n\n${brand?.senderName||""}\n${brand?.name||""}`}</div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={()=>setShowBRoll(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={async()=>{
            if(!c.email){toast("No email on file — add it in Creator details first","error");return;}
            if(!brandId){toast("Brand not loaded yet","error");return;}
            const subject=`B-Roll Footage Request — ${brand?.name||"Brand"}`;
            const body=`Hi ${c.name.split(" ")[0]},\n\nWe absolutely loved your recent content!\n\nWould you be able to share the raw footage with us?\n\nYou can share it via Google Drive (${brand?.fromEmail||""}), Dropbox, or WeTransfer.\n\nThank you!\n\n${brand?.senderName||""}\n${brand?.name||""}`;
            try{
              const token=await window.Clerk?.session?.getToken();
              const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-creator-email`,{
                method:'POST',headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},
                body:JSON.stringify({brand_id:brandId,creator_handle:c.handle,to:c.email,subject,body}),
              });
              const result=await resp.json();
              if(!resp.ok){toast(`Send failed: ${result.message||result.error||resp.status}`,'error');return;}
              if(onLogActivity) onLogActivity("status","B-roll footage requested",{broll:{status:"requested",requested:new Date().toLocaleDateString("en-AU")}});
              if(onUpdate) onUpdate({broll:{status:"requested",requested:new Date().toLocaleDateString("en-AU")}});
              toast("✓ B-roll request emailed via brand Gmail");
              setShowBRoll(false);
            }catch(e){toast(`Send failed: ${e.message||'network error'}`,'error');}
          }} style={{background:DARK,color:G,border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>🎥 Send Request</button>
        </div>
      </div>
    </Modal>)}
    {showRemoveConfirm&&(<Modal title="Remove creator" subtitle={c.name} onClose={()=>{if(!deleting){setShowRemoveConfirm(false);setDeleteArmed(false);}}} width={440}>
      <div style={{fontSize:13,color:MUTED,marginBottom:20,lineHeight:1.7}}><strong style={{color:DARK}}>Archive</strong> hides them from the main list (find them under "Archived") — reversible. <strong style={{color:"#DC2626"}}>Delete permanently</strong> removes the creator and all their history — emails, flow, activity, board card — and can't be undone.</div>
      {deleteArmed&&<div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:9,padding:"10px 13px",marginBottom:14,fontSize:12.5,color:"#991B1B",fontWeight:600}}>This permanently deletes {c.name} and everything tied to them. Click "Delete forever" to confirm.</div>}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
        <button disabled={deleting} onClick={async()=>{
          if(!onDelete||deleting)return;
          if(!deleteArmed){setDeleteArmed(true);return;}
          setDeleting(true);
          try{ await onDelete(); } finally { setDeleting(false); setDeleteArmed(false); setShowRemoveConfirm(false); }
        }} style={{background:deleteArmed?"#DC2626":"#fff",color:deleteArmed?"#fff":"#DC2626",border:"1px solid #FECACA",borderRadius:9,padding:"9px 16px",fontWeight:700,fontSize:13,cursor:deleting?"wait":"pointer",opacity:deleting?0.7:1}}>{deleting?"Deleting…":deleteArmed?"⚠️ Delete forever":"🗑 Delete permanently"}</button>
        <div style={{display:"flex",gap:8}}>
          <button disabled={deleting} onClick={()=>{setShowRemoveConfirm(false);setDeleteArmed(false);}} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button>
          {!deleteArmed&&<button disabled={deleting} onClick={()=>{setShowRemoveConfirm(false);if(onRemove)onRemove();}} style={{background:"#FFFBEB",color:"#92400E",border:"1px solid #FDE68A",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>📦 Archive</button>}
        </div>
      </div>
    </Modal>)}

    {/* Header */}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
      <button onClick={onBack} style={{display:"flex",alignItems:"center",gap:7,border:"none",background:"none",cursor:"pointer",color:MUTED,fontSize:13,fontWeight:500,padding:0}} onMouseEnter={e=>e.currentTarget.style.color=DARK} onMouseLeave={e=>e.currentTarget.style.color=MUTED}><Ico d="M10 19l-7-7m0 0l7-7m-7 7h18" size={15}/> Back</button>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <button onClick={()=>setShowEmailModal(true)} style={{background:DARK,color:G,border:"none",borderRadius:9,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>✉️ Send Email</button>
        <button onClick={()=>setShowProductModal(true)} style={{background:"#8B5CF6",color:"#fff",border:"none",borderRadius:9,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>📦 Send Product</button>
        {(currentStatus==="Posted"||currentStatus==="Awaiting Post")&&<>
          <button onClick={()=>setShowUsageRights(true)} style={{background:c.usageRights?.status==="requested"?"#FFF7ED":"#F5F3FF",color:c.usageRights?.status==="requested"?"#92400E":"#7C3AED",border:`1px solid ${c.usageRights?.status==="requested"?"#FDE68A":"#DDD6FE"}`,borderRadius:9,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            {c.usageRights?.status==="requested"?"🎬 Rights pending":c.usageRights?.status==="granted"?"✅ Rights granted":"🎬 Usage Rights"}
          </button>
          <button onClick={()=>setShowBRoll(true)} style={{background:c.broll?.status==="requested"?"#FFF7ED":"#F0FDF4",color:c.broll?.status==="requested"?"#92400E":G,border:`1px solid ${c.broll?.status==="requested"?"#FDE68A":"#BBF7D0"}`,borderRadius:9,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            {c.broll?.status==="requested"?"🎥 B-Roll pending":c.broll?.status==="received"?"✅ B-Roll received":"🎥 B-Roll"}
          </button>
        </>}
        <button onClick={()=>setShowRemoveConfirm(true)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:8,padding:"7px 12px",fontSize:12,cursor:"pointer",color:MUTED}} onMouseEnter={e=>{e.currentTarget.style.borderColor="#F59E0B";e.currentTarget.style.color="#92400E";}} onMouseLeave={e=>{e.currentTarget.style.borderColor=BORDER;e.currentTarget.style.color=MUTED;}}>Remove</button>
      </div>
    </div>

    {/* Two-column layout */}
    <div style={{display:"grid",gridTemplateColumns:"340px 1fr",gap:16,alignItems:"start"}}>

      {/* LEFT — Details */}
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {/* Creator card */}
        <Card style={{padding:20}}>
          <div style={{display:"flex",gap:14,alignItems:"center",marginBottom:16}}>
            <Avatar name={c.name} color={c.color} size={56}/>
            <div>
              <div style={{fontWeight:800,fontSize:17,color:DARK,letterSpacing:"-.02em"}}>{c.name}</div>
              <div style={{fontSize:12,marginTop:3}}><HandleLink handle={c.handle} size={12} color={MUTED}/></div>
              <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap",alignItems:"center"}}>
                {c.market&&<Badge label={c.market} color={c.market==="US"?"#EFF6FF":"#F0FDF4"} text={c.market==="US"?"#1D4ED8":"#166534"}/>}
                {c.pocket&&c.pocket!=="—"&&<Badge label={c.pocket} color="#F0FDF4" text="#166534"/>}
                {/* Paid Leads chip — only renders when creator is in the per-brand Paid Leads list. */}
                {inPaidDb && <span title={`In Paid Leads since ${new Date(c.paid_database_at).toLocaleDateString()}`} style={{background:"#FEF3C7",color:"#92400E",border:"1px solid #FDE68A",borderRadius:5,padding:"2px 7px",fontSize:10,fontWeight:700,letterSpacing:".03em"}}>💰 Paid Leads{c.paid_database_status?` · ${c.paid_database_status}`:""}</span>}
              </div>
              {/* Paid Leads toggle — small button below the badges, only shows when relevant. */}
              <div style={{marginTop:8}}>
                <button onClick={togglePaidDb} disabled={paidDbBusy} title={inPaidDb?"Remove from per-brand Paid Leads list sheet":"Add to per-brand Paid Leads list sheet (auto-fires on paid_only replies; manual for everyone else)"} style={{border:`1px solid ${inPaidDb?"#FECACA":"#FDE68A"}`,background:inPaidDb?"#FEF2F2":"#FFFBEB",color:inPaidDb?"#991B1B":"#92400E",borderRadius:6,padding:"3px 9px",fontSize:10,fontWeight:600,cursor:paidDbBusy?"wait":"pointer",letterSpacing:".02em"}}>
                  {paidDbBusy?"…":inPaidDb?"− Remove from Paid Leads":"💰 Add to Paid Leads"}
                </button>
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {STATUSES_CP.map(s=><button key={s} onClick={()=>handleStatusChange(s)} style={{padding:"3px 10px",borderRadius:20,fontSize:10,fontWeight:600,border:`1px solid ${currentStatus===s?(SC2[s]||"#9ab8a6")+"60":BORDER}`,background:currentStatus===s?(SC2[s]||"#9ab8a6")+"18":"#fff",color:currentStatus===s?(SC2[s]||"#9ab8a6"):MUTED,cursor:"pointer"}}>{s}</button>)}
          </div>
          {/* One-step Undo for the last operator-triggered status change.
              Reads operator_actions, not activity_log — so auto-changes from
              the worker/webhook (Outreached, Awaiting Post, etc.) aren't
              treated as revertible. Only manual operator changes qualify. */}
          {lastStatusChange && lastStatusChange.details?.from && lastStatusChange.details?.to === currentStatus && (
            <div style={{marginTop:8,padding:"7px 10px",background:PAGE_BG,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
              <div style={{fontSize:11,color:MUTED,lineHeight:1.4}}>
                Last change: <strong style={{color:DARK}}>{lastStatusChange.details.from}</strong> → <strong style={{color:DARK}}>{lastStatusChange.details.to}</strong>
                <span style={{marginLeft:6,opacity:.7}}>{new Date(lastStatusChange.created_at).toLocaleString("en-AU",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}</span>
              </div>
              <button onClick={undoLastStatus} title={`Revert to ${lastStatusChange.details.from}`} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:600,color:DARK,cursor:"pointer",whiteSpace:"nowrap"}}>↶ Undo</button>
            </div>
          )}
        </Card>

        {/* Details */}
        <Card style={{padding:18}}>
          <div style={{fontWeight:700,fontSize:13,color:DARK,marginBottom:12}}>Details</div>
          <EditableField label="Full name" field="name" value={c.name} placeholder="Full name"/>
          {/* Handle is intentionally read-only — it's the foreign key used
              across activity_log, email_queue, kanban_items, flow_assignments.
              Renaming it orphans all that history. If a typo needs fixing,
              archive + re-add. Tooltip explains. */}
          <div title="Handle is the identifier across activity, email queue, campaigns and flows. To change it, archive this creator and add a fresh one." style={{display:"flex",alignItems:"center",gap:10,padding:"6px 0 10px",borderBottom:`1px solid ${BORDER}`,marginBottom:10,cursor:"help"}}>
            <span style={{fontSize:11,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",minWidth:96}}>Handle</span>
            <span style={{flex:1,fontSize:13,color:DARK,fontFamily:"Menlo,Monaco,monospace"}}>@{c.handle||"—"}</span>
            <span style={{fontSize:10,color:MUTED,background:PAGE_BG,padding:"2px 7px",borderRadius:4}}>locked</span>
          </div>
          <EditableField label="Email" field="email" value={c.email} placeholder="email@example.com"/>
          <EditableField label="Followers" field="followersF" value={c.followersF} placeholder="18.9K"/>
          <EditableField label="Eng. rate" field="eng" value={c.eng} placeholder="4.2%"/>
          <EditableField label="Order #" field="order" value={c.order} placeholder="#20341"/>
          {/* Discount code with collision detection across this brand's
              active creators. Auto-derived codes (FIRST5+LASTINIT+15) collide
              for similar names (Alice Smith + Alice Sutton both = ALICES15).
              Operator now sees a ⚠ pill listing the other creator(s) using
              the same code so they can manually set a unique one. The custom
              code persists via the new discount_code column. */}
          {(()=>{
            const codeNow = c.discountCode || genDiscountCode(c.name);
            const conflicts = (allCreators || []).filter(o =>
              !o.archived && o.handle !== c.handle &&
              (o.discountCode || genDiscountCode(o.name)) === codeNow
            );
            return(<>
              <EditableField label="Discount code" field="discountCode" value={codeNow} placeholder="BRENTF15"/>
              {conflicts.length > 0 && <div style={{marginTop:-6,marginBottom:10,padding:"6px 10px",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:6,fontSize:11,color:"#92400E",lineHeight:1.5}}>
                ⚠ <strong>{codeNow}</strong> is also used by {conflicts.slice(0,3).map(o=>`@${o.handle}`).join(", ")}{conflicts.length>3?` and ${conflicts.length-3} more`:""}. Edit the code above to a unique value before sending.
              </div>}
            </>);
          })()}
        </Card>

        {/* Shipping */}
        <Card style={{padding:18}}>
          <div style={{fontWeight:700,fontSize:13,color:DARK,marginBottom:12}}>📦 Shipping</div>
          <EditableField label="Street" field="addressStreet" value={c.addressStreet} placeholder="123 Ocean Drive"/>
          <EditableField label="City" field="addressCity" value={c.addressCity} placeholder="Sydney"/>
          <EditableField label="State" field="addressState" value={c.addressState} placeholder="NSW"/>
          <EditableField label="Postcode" field="addressPostcode" value={c.addressPostcode} placeholder="2000"/>
          <EditableField label="Country" field="addressCountry" value={c.addressCountry||c.market} placeholder="Australia" options={COUNTRIES}/>
        </Card>

        {/* Social links */}
        <Card style={{padding:18}}>
          <div style={{fontWeight:700,fontSize:13,color:DARK,marginBottom:12}}>🔗 Social</div>
          {[{label:"Instagram",field:"instagram",icon:"📸",prefix:"https://instagram.com/"},{label:"TikTok",field:"tiktok",icon:"🎵",prefix:"https://tiktok.com/@"},{label:"YouTube",field:"youtube",icon:"▶️",prefix:"https://youtube.com/@"}].map(({label,field,icon,prefix})=>(
            <div key={field} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${BORDER}`}}>
              <div style={{fontSize:12,color:MUTED,fontWeight:500,minWidth:80}}>{icon} {label}</div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                {c[field]?<a href={prefix+c[field].replace(/^@/,"")} target="_blank" rel="noreferrer" style={{fontSize:12,fontWeight:600,color:G,textDecoration:"none"}}>@{c[field].replace(/^@/,"")}</a>:<span style={{fontSize:12,color:"#c0d4c8"}}>Not set</span>}
                {editField===field
                  ?<div style={{display:"flex",gap:5}}><input autoFocus value={editVal} onChange={e=>setEditVal(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveEdit(field);if(e.key==="Escape")setEditField(null);}} placeholder="@handle" style={{padding:"3px 8px",border:`1px solid ${G}`,borderRadius:6,fontSize:11,outline:"none",width:110}}/><button onClick={()=>saveEdit(field)} style={{background:DARK,color:G,border:"none",borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700,cursor:"pointer"}}>Save</button><button onClick={()=>setEditField(null)} style={{background:"#f8faf9",border:`1px solid ${BORDER}`,borderRadius:6,padding:"3px 6px",fontSize:10,cursor:"pointer"}}>✕</button></div>
                  :<button onClick={()=>startEdit(field,c[field])} style={{border:"none",background:"none",cursor:"pointer",fontSize:10,color:MUTED,opacity:.5}} onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=.5}>Edit</button>
                }
              </div>
            </div>
          ))}
        </Card>

        {/* Usage rights + B-Roll status */}
        {(c.usageRights||c.broll)&&<Card style={{padding:18}}>
          <div style={{fontWeight:700,fontSize:13,color:DARK,marginBottom:12}}>🎬 Content Rights</div>
          {c.usageRights&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:c.broll?`1px solid ${BORDER}`:"none"}}>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:DARK}}>Usage Rights</div>
              <div style={{fontSize:11,color:MUTED,marginTop:2}}>{c.usageRights.type} · {c.usageRights.platforms?.join(", ")} · {c.usageRights.duration}</div>
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <Badge label={c.usageRights.status==="granted"?"Granted ✓":"Requested"} color={c.usageRights.status==="granted"?"#F0FDF4":"#FFFBEB"} text={c.usageRights.status==="granted"?"#166534":"#92400E"}/>
              {c.usageRights.status==="requested"&&<button onClick={()=>{if(onUpdate)onUpdate({usageRights:{...c.usageRights,status:"granted"}});toast("Usage rights marked as granted");}} style={{border:`1px solid ${G}`,background:"#f0fdf4",color:G,borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700,cursor:"pointer"}}>Mark granted</button>}
            </div>
          </div>}
          {c.broll&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0"}}>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:DARK}}>B-Roll</div>
              <div style={{fontSize:11,color:MUTED,marginTop:2}}>Requested {c.broll.requested}</div>
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <Badge label={c.broll.status==="received"?"Received ✓":"Requested"} color={c.broll.status==="received"?"#F0FDF4":"#FFFBEB"} text={c.broll.status==="received"?"#166534":"#92400E"}/>
              {c.broll.status==="requested"&&<button onClick={()=>{if(onUpdate)onUpdate({broll:{...c.broll,status:"received",received:new Date().toLocaleDateString("en-AU")}});toast("B-Roll marked as received");}} style={{border:`1px solid ${G}`,background:"#f0fdf4",color:G,borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700,cursor:"pointer"}}>Mark received</button>}
            </div>
          </div>}
        </Card>}
        {/* Shopify order — only renders when an order was auto-created for this creator. */}
        {c.shopify_order_id&&(()=>{
          const shortId = String(c.shopify_order_id).slice(-7);
          const fulfilled = c.fulfilled_at ? new Date(c.fulfilled_at) : null;
          const trackingLink = c.tracking_url || (c.tracking_number && c.tracking_company
            ? `https://www.google.com/search?q=${encodeURIComponent(c.tracking_company+' '+c.tracking_number)}`
            : null);
          return (<Card style={{padding:18,border:`1px solid ${fulfilled?G+"50":BORDER}`,background:fulfilled?G+"06":"#fff"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <div style={{fontWeight:700,fontSize:13,color:DARK}}>📦 Shopify order</div>
              {c.shopify_order_url&&<a href={c.shopify_order_url} target="_blank" rel="noreferrer" style={{fontSize:11,color:G,fontWeight:700,textDecoration:"none"}}>Open in Shopify ↗</a>}
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:`1px solid ${BORDER}`}}>
              <span style={{fontSize:11,color:MUTED}}>Order</span>
              <span style={{fontSize:12,fontWeight:600,color:DARK,fontFamily:"DM Mono, monospace"}}>…{shortId}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:`1px solid ${BORDER}`}}>
              <span style={{fontSize:11,color:MUTED}}>Status</span>
              {fulfilled
                ?<span style={{background:G+"18",color:"#166534",border:`1px solid ${G}30`,borderRadius:5,padding:"2px 8px",fontSize:10,fontWeight:700}}>SHIPPED</span>
                :<span style={{background:"#FFFBEB",color:"#92400E",border:"1px solid #FDE68A",borderRadius:5,padding:"2px 8px",fontSize:10,fontWeight:700}}>AWAITING FULFILLMENT</span>}
            </div>
            {fulfilled&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:c.tracking_number?`1px solid ${BORDER}`:"none"}}>
              <span style={{fontSize:11,color:MUTED}}>Fulfilled</span>
              <span style={{fontSize:12,color:DARK}}>{fulfilled.toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"})}</span>
            </div>}
            {c.tracking_number&&<div style={{padding:"9px 0"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{fontSize:11,color:MUTED}}>Tracking</span>
                <span style={{fontSize:11,color:MUTED}}>{c.tracking_company||"Carrier"}</span>
              </div>
              {trackingLink
                ?<a href={trackingLink} target="_blank" rel="noreferrer" style={{display:"block",fontSize:12,fontWeight:600,color:G,fontFamily:"DM Mono, monospace",textDecoration:"none",wordBreak:"break-all"}}>{c.tracking_number} ↗</a>
                :<div style={{fontSize:12,fontWeight:600,color:DARK,fontFamily:"DM Mono, monospace",wordBreak:"break-all"}}>{c.tracking_number}</div>}
            </div>}
            {!fulfilled&&<div style={{fontSize:11,color:MUTED,marginTop:8,lineHeight:1.5,fontStyle:"italic"}}>Tracking number will appear here once the warehouse ships.</div>}
          </Card>);
        })()}

        <Card style={{padding:18}}>
          <div style={{fontWeight:700,fontSize:13,color:DARK,marginBottom:12}}>📣 Campaigns</div>
          {creatorCampaigns.length===0?<div style={{fontSize:12,color:MUTED}}>Not in any campaign yet. <span style={{color:"#c0d4c8"}}>Add them via the Campaigns tab.</span></div>:creatorCampaigns.map((camp,i)=>{
            const sc={"To Outreach":"#A78BFA","Outreached":"#6B7280","Negotiation":"#3B82F6","Paid Only":"#EF4444","Awaiting Post":"#F59E0B","Posted":"#10B981"}[camp.stage]||"#9ab8a6";
            return(<div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:i<creatorCampaigns.length-1?`1px solid ${BORDER}`:"none"}}>
              <div style={{fontSize:12,fontWeight:600,color:DARK}}>{camp.name}</div>
              <span style={{background:sc+"18",color:sc,border:`1px solid ${sc}30`,borderRadius:20,padding:"2px 8px",fontSize:10,fontWeight:600}}>{camp.stage}</span>
            </div>);
          })}
        </Card>

        <CreatorPostsCard creatorId={c.id} creatorHandle={c.handle} brandId={brandId} brandIgUsername={brand?.instagram}/>
      </div>

      {/* RIGHT — Conversation + Activity */}
      <div style={{display:"flex",flexDirection:"column",gap:12}}>

        {/* Needs-review banner — set by reply classifier when human attention is required. */}
        {c.review_reason&&(
          <div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:20}}>⚠</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:700,color:"#991B1B",fontSize:13}}>{
                c.review_reason==="paid_only_inquiry"?"Replied: paid collabs only"
                :c.review_reason==="unclear_reply"?"Replied — needs human read"
                :c.review_reason==="unsubscribed_via_email_link"?"Unsubscribed via email link"
                :"Needs your review"
              }</div>
              <div style={{fontSize:12,color:"#B91C1C",marginTop:2,lineHeight:1.5}}>{
                c.review_reason==="paid_only_inquiry"?"Creator declined the gifted collab but is open to paid work. Account manager should decide whether to engage on paid terms."
                :c.review_reason==="unclear_reply"?"AI couldn't read the reply (sarcasm, confusion, soft-defer, etc.) — flow paused, no auto-reply sent. You decide the next move."
                :c.review_reason==="unsubscribed_via_email_link"?"Creator clicked the unsubscribe footer on a previous email. Flow cancelled. They're on the agency-wide Suppress List."
                :`Flag: ${c.review_reason}`
              }</div>
            </div>
            <button onClick={async()=>{
              if(onUpdate) onUpdate({review_reason:null});
              await supabase.from('creators').update({review_reason:null}).eq('handle',c.handle);
              toast('Review flag cleared');
            }} style={{border:"1px solid #FECACA",background:"#fff",color:"#DC2626",borderRadius:7,padding:"6px 11px",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>Mark resolved</button>
          </div>
        )}

        {/* Flow-assignment status box removed entirely (operator request) — the
            "On flow / Lifecycle complete / No flow matches" banner added noise on
            every creator regardless of stage. Assignment still drives sending behind
            the scenes; it's just no longer surfaced on the profile. */}

        {/* Follow-up scheduler banner */}
        {followUpStatus?.status==="due"&&(
          <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:18}}>⏰</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,color:"#92400E",fontSize:13}}>Follow-up Due — {followUpStatus.daysOverdue} day{followUpStatus.daysOverdue!==1?"s":""} overdue</div>
              <div style={{fontSize:12,color:"#B45309",marginTop:2}}>Step {followUpStatus.sentCount+1}: "{followUpStatus.nextStep.subject}"</div>
            </div>
            <button onClick={()=>{setEmailPreloadStep(followUpStatus.nextStep);setShowEmailModal(true);}} style={{background:"#92400E",color:"#fff",border:"none",borderRadius:8,padding:"7px 14px",fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>Send Now</button>
          </div>
        )}
        {followUpStatus?.status==="upcoming"&&(
          <div style={{background:"#F8FAFF",border:"1px solid #BFDBFE",borderRadius:12,padding:"12px 18px",display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:16}}>📅</span>
            <div style={{fontSize:12,color:"#1D4ED8"}}><strong>Follow-up in {followUpStatus.daysUntil} day{followUpStatus.daysUntil!==1?"s":""}:</strong> "{followUpStatus.nextStep.subject}"</div>
          </div>
        )}

        {/* State banners removed — they duplicated the status pill at the top of
            the profile + the conversation thread already shows the latest activity.
            The single "needs human" banner now lives inside the conversation card
            and only fires when the AI didn't auto-handle the inbound. */}


        {/* Notes */}
        <Card style={{padding:18}}>
          <div style={{fontWeight:700,fontSize:13,color:DARK,marginBottom:10}}>📝 Notes</div>
          {editField==="notes"
            ?<div><textarea autoFocus value={editVal} onChange={e=>setEditVal(e.target.value)} rows={4} style={{width:"100%",padding:"9px 12px",border:`1px solid ${G}`,borderRadius:8,fontSize:12,outline:"none",resize:"vertical",fontFamily:"inherit",boxSizing:"border-box",lineHeight:1.6}}/><div style={{display:"flex",gap:8,marginTop:8,justifyContent:"flex-end"}}><button onClick={()=>setEditField(null)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:7,padding:"6px 12px",fontSize:12,cursor:"pointer"}}>Cancel</button><button onClick={()=>saveEdit("notes")} style={{background:DARK,color:G,border:"none",borderRadius:7,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Save</button></div></div>
            :<div><div style={{fontSize:12,color:c.notes?DARK:MUTED,lineHeight:1.7,whiteSpace:"pre-wrap",minHeight:32}}>{c.notes||"No notes yet."}</div><button onClick={()=>startEdit("notes",c.notes)} style={{marginTop:8,border:`1px solid ${BORDER}`,background:"#f8faf9",borderRadius:6,padding:"5px 12px",fontSize:11,cursor:"pointer",color:DARK}}>{c.notes?"Edit notes":"+ Add note"}</button></div>
          }
        </Card>

        {/* Conversation thread — Influencer Hero style. Action Required callout at the top
            for any unread/needs-response inbound, then chronological cards (newest first)
            with per-card actions. Sent emails are blue (we wrote), received are amber (they wrote). */}
        {(()=>{
          // Find the most recent received email that needs a reply (i.e. no email_sent after it).
          const sorted=[...activityLog].sort((a,b)=>new Date(b.ts)-new Date(a.ts));
          const lastReceived=sorted.find(e=>e.type==="email_received");
          const lastSent=sorted.find(e=>e.type==="email_sent");
          const unanswered = lastReceived && (!lastSent || new Date(lastReceived.ts)>new Date(lastSent.ts));
          // The AI auto-handles positive + negative classifications (and queues a
          // paid_only holding reply). Banner only fires when the AI explicitly did
          // NOT take action — neutral, missing classification (old or unprocessed
          // replies), or the AI handed off (paid_only still needs operator follow-up
          // on the negotiation itself, even though the holding reply went out).
          const cls = lastReceived?.classification;
          const aiHandled = cls === "positive" || cls === "negative";
          const isPaidNegotiation = cls === "paid_only";
          const needsReply = unanswered && !aiHandled;
          const bannerKind = !needsReply ? null
            : isPaidNegotiation ? "paid"
            : (!cls || cls === "neutral") ? "review"
            : "review";
          return(<>
            {bannerKind && (<Card style={{padding:0,background: bannerKind==="paid"?"#FEF2F2":"#FFFBEB",border:`1.5px solid ${bannerKind==="paid"?"#FCA5A5":"#F59E0B40"}`,overflow:"hidden",marginBottom:12}}>
              <div style={{padding:"10px 16px",background: bannerKind==="paid"?"#FEE2E2":"#FEF3C7",borderBottom:`1px solid ${bannerKind==="paid"?"#FECACA":"#FDE68A"}`,display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:16,color: bannerKind==="paid"?"#DC2626":"#F59E0B"}}>{bannerKind==="paid"?"💰":"⚠"}</span>
                <span style={{fontWeight:700,color: bannerKind==="paid"?"#991B1B":"#92400E",fontSize:13}}>{bannerKind==="paid"?"Paid negotiation — operator decision":"Needs your review"}</span>
              </div>
              <div style={{padding:"14px 16px",display:"flex",alignItems:"flex-start",gap:12}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,color:MUTED,marginBottom:3,fontWeight:600}}>
                    {bannerKind==="paid"
                      ? "AI sent a polite holding reply. You decide whether to engage paid terms."
                      : "AI didn't auto-handle this one — looks ambiguous. Take a look."}
                  </div>
                  <div style={{fontSize:13,fontWeight:700,color:DARK,marginBottom:4,overflow:"hidden",textOverflow:"ellipsis"}}>{lastReceived.subject||"(no subject)"}</div>
                  <div style={{fontSize:11,color:MUTED}}>{c.email||"—"} → {new Date(lastReceived.ts).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})}</div>
                </div>
                <button onClick={()=>setShowEmailModal(true)} style={{background:DARK,color:G,border:"none",borderRadius:7,padding:"7px 14px",fontWeight:700,fontSize:12,cursor:"pointer",flexShrink:0}}>Reply</button>
              </div>
            </Card>)}

            <Card style={{padding:18}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontWeight:700,fontSize:13,color:DARK}}>💬 Conversation</div>
                <button onClick={()=>setShowEmailModal(true)} style={{background:DARK,color:G,border:"none",borderRadius:7,padding:"6px 12px",fontWeight:700,fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>✉️ Send Email</button>
              </div>
              {(()=>{
                // Conversation is communication + order events only. Status changes,
                // archive flips, profile edits live in Activity (own card below) so
                // they don't drown out the actual messages between brand + creator.
                const conversationTypes = new Set(["email_sent","email_received","order_pending","shipped","delivered","bounce","note"]);
                const filtered = activityLog.filter(e=>conversationTypes.has(e.type));
                return filtered.length===0
                ?<div style={{padding:"24px 0",textAlign:"center",color:MUTED,fontSize:12,fontStyle:"italic"}}>No conversation yet. Send an email or wait for a reply.</div>
                :<div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {filtered.slice(0,30).map((entry,i)=>{
                    const es=entryStyle[entry.type]||{icon:"•",color:"#9ab8a6"};
                    const isEmailSent=entry.type==="email_sent";
                    const isEmailReceived=entry.type==="email_received";
                    const isEmail=isEmailSent||isEmailReceived;
                    const isBounce=entry.type==="bounce";
                    const d=new Date(entry.ts);
                    const tsMs=d.getTime();const now2=Date.now();const diff=now2-tsMs;
                    const timeStr=diff<60000?"just now":diff<3600000?`${Math.floor(diff/60000)}m ago`:diff<86400000?`${Math.floor(diff/3600000)}h ago`:diff<604800000?`${Math.floor(diff/86400000)}d ago`:d.toLocaleDateString("en-AU",{day:"numeric",month:"short"});
                    // Email cards — click-to-expand Influencer-Hero style.
                    // Card chrome (header w/ subject + timestamps + Reply button) is
                    // always visible. Body collapses to a 2-line snippet by default,
                    // expands to full text when the card is clicked. HTML bodies
                    // render as HTML (sanitized via DOM-builder, not innerHTML).
                    if(isEmail){
                      const senderColor=isEmailSent?"#3B82F6":"#F59E0B";
                      const entryId = entry.id || `${entry.ts}-${i}`;
                      const isExpanded = expandedEntries.has(entryId);
                      const queueId = entry.queueId || null;
                      const fetched = queueId ? fullBodies[queueId] : null;
                      const isLoadingFull = fetched === "__loading__";
                      const fullBody = (fetched && fetched !== "__loading__")
                        ? fetched
                        : (entry.body || entry.bodySnippet || "");
                      const hasBody = !!fullBody.trim();
                      // Strip HTML for the collapsed preview line. Real HTML body
                      // renders inside the expanded section.
                      const bodyAsText = fullBody.replace(/<br\s*\/?>/gi,"\n").replace(/<\/p\s*>/gi,"\n\n").replace(/<[^>]+>/g,"").replace(/&nbsp;/g," ").replace(/&amp;/g,"&").replace(/&lt;/g,"<").replace(/&gt;/g,">").replace(/&quot;/g,'"').replace(/&#39;/g,"'").trim();
                      const looksHtml = /<\/?[a-z][^>]*>/i.test(fullBody);
                      const fromInbox = entry.fromInbox || (isEmailSent ? (brand?.fromEmail||"") : "");
                      const toEmail = c.email || "—";
                      return(<div key={entryId} style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                        <div style={{width:28,height:28,borderRadius:"50%",background:senderColor+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0,color:senderColor,marginTop:6}}>
                          {isEmailSent?"➤":"✉"}
                        </div>
                        <div style={{flex:1,border:`1px solid ${BORDER}`,borderRadius:10,background:"#fff",overflow:"hidden",minWidth:0,boxShadow:isExpanded?"0 2px 8px rgba(0,0,0,0.05)":"none"}}>
                          {/* Header — always visible + always clickable. Expansion shows
                              full body when we have one, or a "(no body captured)" note +
                              Gmail deep link when we don't. Stays consistent so the operator
                              never has a card that visibly refuses to open. */}
                          <div onClick={()=>toggleEntryExpansion(entryId,queueId)} style={{padding:"11px 14px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,cursor:"pointer",userSelect:"none"}}>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:11,color:MUTED,fontWeight:600,marginBottom:3,display:"flex",alignItems:"center",gap:6}}>
                                {isEmailSent?"Email sent":"Email received"}
                                {entry.classification&&<span style={{background:entry.classification==="positive"?"#F0FDF4":entry.classification==="negative"?"#FEF2F2":entry.classification==="paid_only"?"#FFFBEB":"#F3F4F6",color:entry.classification==="positive"?"#166534":entry.classification==="negative"?"#991B1B":entry.classification==="paid_only"?"#92400E":"#374151",borderRadius:4,padding:"1px 7px",fontSize:10,fontWeight:700,textTransform:"capitalize"}}>{entry.classification.replace("_"," ")}</span>}
                                {entry.manual&&<span style={{background:"#F3F4F6",color:"#374151",borderRadius:4,padding:"1px 7px",fontSize:10,fontWeight:700}}>Manual log</span>}
                                <span style={{marginLeft:"auto",fontSize:10,color:MUTED,fontWeight:500}}>{isExpanded?"▼ collapse":"▶ click to expand"}</span>
                              </div>
                              <div style={{fontSize:14,fontWeight:700,color:DARK,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:1}}>{entry.subject||entry.text||"(no subject)"}</div>
                              <div style={{fontSize:11,color:MUTED,marginTop:5,lineHeight:1.5,display:"flex",alignItems:"center",flexWrap:"wrap",columnGap:6}}>
                                {isEmailSent
                                  ?<><span style={{fontFamily:"DM Mono, monospace",color:"#4b5563"}}>{fromInbox||"you"}</span><span style={{color:"#c0d4c8"}}>→</span><span style={{fontFamily:"DM Mono, monospace",color:"#4b5563"}}>{toEmail}</span></>
                                  :<><span style={{fontFamily:"DM Mono, monospace",color:"#4b5563"}}>{toEmail}</span><span style={{color:"#c0d4c8"}}>→</span><span style={{fontFamily:"DM Mono, monospace",color:"#4b5563"}}>{fromInbox||"your inbox"}</span></>}
                                <span style={{color:"#d1d5db",margin:"0 2px"}}>•</span>
                                <span>{d.toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})}</span>
                                <span style={{color:"#9ca3af",fontWeight:500}}>({timeStr})</span>
                              </div>
                              {!isExpanded&&hasBody&&<div style={{fontSize:12,color:"#6B7280",marginTop:10,lineHeight:1.55,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden",paddingTop:8,borderTop:`1px dashed ${BORDER}`}}>{bodyAsText.slice(0,260)}</div>}
                              {!isExpanded&&isEmailReceived&&(entry.images?.length>0||entry.attachments?.length>0)&&<div style={{fontSize:11,color:"#92400E",marginTop:6,fontWeight:600}}>{entry.images?.length>0?`🖼 ${entry.images.length} image${entry.images.length===1?"":"s"}`:`📎 ${entry.attachments.length} attachment${entry.attachments.length===1?"":"s"}`} — expand to view</div>}
                            </div>
                            <button onClick={e=>{e.stopPropagation();setShowEmailModal(true);}} style={{flexShrink:0,border:isEmailReceived?"none":`1px solid ${BORDER}`,background:isEmailReceived?DARK:"#fff",color:isEmailReceived?G:DARK,borderRadius:6,padding:"5px 11px",fontSize:11,cursor:"pointer",fontWeight:700,whiteSpace:"nowrap"}}>
                              {isEmailReceived?"Reply":"↩ Follow-up"}
                            </button>
                          </div>
                          {/* Expanded full body — renders HTML safely via dangerouslySetInnerHTML on
                              an already-sanitized string OR plain text fallback. Worker stores HTML
                              bodies for sent emails; received bodies are usually text snippets. */}
                          {isExpanded&&(<div style={{borderTop:`1px solid ${BORDER}`,padding:"16px 18px",background:"#fafcfb"}}>
                            {isLoadingFull
                              ?<div style={{fontSize:12,color:MUTED,fontStyle:"italic",textAlign:"center",padding:"8px 0"}}>⏳ Loading full message…</div>
                              :hasBody
                                ?(looksHtml
                                    ?<div style={{fontSize:13,color:"#1f2937",lineHeight:1.65,wordBreak:"break-word"}} dangerouslySetInnerHTML={{__html: fullBody}}/>
                                    :<EmailBodyText body={fullBody}/>)
                                :(isEmailReceived&&(entry.images?.length>0||entry.attachments?.length>0))
                                ?<div style={{fontSize:12,color:MUTED,fontStyle:"italic"}}>No text — the creator only sent attachments.</div>
                                :<div style={{padding:"4px 0"}}>
                                  <div style={{fontSize:12,color:MUTED,fontStyle:"italic",marginBottom:8}}>{entry.manual?"Manual log — operator didn't paste a body when logging this reply.":"No message body captured for this entry — could be from before classifier started running, or the webhook didn't run."}</div>
                                  {entry.gmailMessageId&&<a href={`https://mail.google.com/mail/u/0/#all/${entry.gmailMessageId}`} target="_blank" rel="noreferrer" style={{display:"inline-block",fontSize:12,fontWeight:700,color:G,textDecoration:"none",background:"#fff",border:`1px solid ${G}40`,borderRadius:6,padding:"5px 12px"}}>Open thread in Gmail ↗</a>}
                                </div>}
                            {isEmailReceived&&<InboundAttachments images={entry.images||[]} attachments={entry.attachments||[]} gmailMessageId={entry.gmailMessageId||null}/>}
                          </div>)}
                        </div>
                      </div>);
                    }
                    // Fulfillment events — render as proper event cards (matches the
                    // email card shape so the thread reads as one timeline).
                    if(entry.type==="order_pending"||entry.type==="shipped"||entry.type==="delivered"){
                      const isShipped = entry.type==="shipped";
                      const isDelivered = entry.type==="delivered";
                      const cfg = isShipped
                        ? {icon:"🚚", color:"#F59E0B", bg:"#FFFBEB", label:"Order shipped"}
                        : isDelivered
                          ? {icon:"✅", color:"#10B981", bg:"#F0FDF4", label:"Delivered"}
                          : {icon:"📦", color:"#8B5CF6", bg:"#F5F3FF", label:"Order placed"};
                      const orderId = entry.shopify_order_id || entry.order_id;
                      const orderUrl = entry.shopify_order_url || (orderId && brand?.shopifyUrl
                        ? `https://${String(brand.shopifyUrl).replace(/^https?:\/\//,"").replace(/\/$/,"")}/admin/orders/${orderId}` : null);
                      const trackingUrl = entry.tracking_url;
                      const trackingNum = entry.tracking_number;
                      const trackingCo = entry.tracking_company;
                      const product = entry.product;
                      return(<div key={entry.id||i} style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                        <div style={{width:28,height:28,borderRadius:"50%",background:cfg.color+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0,marginTop:6}}>{cfg.icon}</div>
                        <div style={{flex:1,border:`1px solid ${cfg.color}30`,borderRadius:10,background:cfg.bg,overflow:"hidden",minWidth:0}}>
                          <div style={{padding:"11px 14px"}}>
                            <div style={{fontSize:11,color:cfg.color,fontWeight:700,marginBottom:3,textTransform:"uppercase",letterSpacing:".05em"}}>{cfg.label}</div>
                            <div style={{fontSize:13,fontWeight:700,color:DARK}}>{product || entry.text || cfg.label}</div>
                            {(orderId||trackingNum)&&<div style={{fontSize:11,color:MUTED,marginTop:5,display:"flex",gap:10,flexWrap:"wrap"}}>
                              {orderId&&<span>Order <strong style={{color:DARK,fontFamily:"DM Mono, monospace"}}>#{orderId}</strong></span>}
                              {trackingNum&&<span>{trackingCo||"Tracking"} <strong style={{color:DARK,fontFamily:"DM Mono, monospace"}}>{trackingNum}</strong></span>}
                            </div>}
                            <div style={{fontSize:11,color:MUTED,marginTop:5}}>{d.toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})} · {timeStr}</div>
                            {(orderUrl||trackingUrl)&&<div style={{display:"flex",gap:7,marginTop:8}}>
                              {orderUrl&&<a href={orderUrl} target="_blank" rel="noreferrer" style={{fontSize:11,fontWeight:700,color:cfg.color,textDecoration:"none",background:"#fff",border:`1px solid ${cfg.color}40`,borderRadius:6,padding:"4px 10px"}}>Open in Shopify ↗</a>}
                              {trackingUrl&&<a href={trackingUrl} target="_blank" rel="noreferrer" style={{fontSize:11,fontWeight:700,color:cfg.color,textDecoration:"none",background:"#fff",border:`1px solid ${cfg.color}40`,borderRadius:6,padding:"4px 10px"}}>Track shipment ↗</a>}
                            </div>}
                          </div>
                        </div>
                      </div>);
                    }
                    // Bounces, notes, anything else that slipped through the filter — compact row.
                    return(<div key={entry.id||i} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"6px 0"}}>
                      <div style={{width:28,height:28,borderRadius:"50%",background:(isBounce?"#EF4444":es.color)+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0,color:isBounce?"#EF4444":es.color,marginTop:1,fontWeight:700}}>{isBounce?"⚠":es.icon}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,color:isBounce?"#991B1B":DARK,fontWeight:500}}>{entry.text}</div>
                        <div style={{fontSize:11,color:MUTED,marginTop:2}}>{timeStr}</div>
                      </div>
                    </div>);
                  })}
                </div>;
              })()}
              <button onClick={openLogReply} style={{marginTop:12,border:`1px dashed ${BORDER}`,background:"transparent",borderRadius:7,padding:"7px 14px",fontSize:11,cursor:"pointer",color:MUTED,width:"100%",textAlign:"center",boxSizing:"border-box"}} onMouseEnter={e=>e.currentTarget.style.borderColor=G} onMouseLeave={e=>e.currentTarget.style.borderColor=BORDER}>
                + Log a manual reply (they replied outside the system)
              </button>
            </Card>
          </>);
        })()}

        {/* Agreements */}
        {creatorAgreements.length>0&&<Card style={{padding:18}}>
          <div style={{fontWeight:700,fontSize:13,color:DARK,marginBottom:12}}>📄 Agreements</div>
          {creatorAgreements.map((ag,i)=>{
            const agColor=ag.status==="Signed"?"#10B981":ag.status==="Sent"?"#3B82F6":"#9ab8a6";
            return(<div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:i<creatorAgreements.length-1?`1px solid ${BORDER}`:"none"}}>
              <div><div style={{fontSize:12,fontWeight:600,color:DARK}}>{ag.collabType} · {ag.brand}</div><div style={{fontSize:10,color:MUTED,marginTop:2}}>Created {ag.created}</div></div>
              <span style={{background:agColor+"18",color:agColor,border:`1px solid ${agColor}30`,borderRadius:20,padding:"2px 8px",fontSize:10,fontWeight:600}}>{ag.status}</span>
            </div>);
          })}
        </Card>}

      </div>
    </div>
  </div>);
}


// ─── ORDER READINESS ─────────────────────────────────────────────────────────
// Checks a creator record for enough data to create a product order
function getOrderReadiness(creator){
  if(!creator) return null;
  const DONE_STAGES=["Posted"];
  if(DONE_STAGES.includes(creator.stage||creator.status)) return null;

  // Address check — need at least city + country
  const hasAddress=!!(
    (creator.addressStreet||creator.addressCity)&&
    (creator.addressCountry||creator.addressPostcode||creator.market)
  );

  // Product hint — from notes, activityLog last email_received, or pocket
  const notes=(creator.notes||"").toLowerCase();
  const PRODUCT_KEYWORDS=["recon","strike","viper","optical","swim cap","paddle","snorkel","wetsuit","backpack","towel","fin","bottle"];
  const hasProductHint=PRODUCT_KEYWORDS.some(k=>notes.includes(k))||
    (creator.pocket&&creator.pocket!=="—"&&creator.pocket!=="");

  if(hasAddress&&hasProductHint) return "ready";
  if(hasAddress&&!hasProductHint) return "no_product";
  if(!hasAddress&&hasProductHint) return "no_address";
  return null;
}
// ─────────────────────────────────────────────────────────────────────────────

function Campaigns({onSelectCreator,kanbanData,setKanbanData,allCreators,setAllCreators,campaigns,setCampaigns,activeBrand,brandId=null,userId=null,onStatusChange=null,persistKanbanItem,removeKanbanItem,flows=[],activityLog=[],selectedId=null,onSelectCampaign=()=>{}}){
  // ALL hooks at the top — React rules
  // Selected campaign is derived from App-held selectedId so it survives this
  // component unmounting (creator profile view) — see App's campaignSelectedId.
  const selected = selectedId==null ? null : (campaigns.find(c=>c.id===selectedId) || null);
  const setSelected = (c)=>onSelectCampaign(c ? c.id : null);
  const[filter,setFilter]=useState("All");
  const[dragItem,setDragItem]=useState(null);
  const[showAddCreator,setShowAddCreator]=useState(false);
  const[newCreator,setNewCreator]=useState({name:"",handle:"",followers:"",email:"",market:"AU",notes:""});
  const[showCampaignCreate,setShowCampaignCreate]=useState(false);
  const[editTarget,setEditTarget]=useState(null); // campaign being edited (full edit, not just rename)
  // (renameTarget state removed — full edit modal replaces the rename-only shortcut.)
  const[deleteTarget,setDeleteTarget]=useState(null); // campaign being deleted
  // campaign_flows linkage state. Loaded lazily for the edit/create modal so
  // the campaign list itself doesn't pay an extra query per row.
  const[campaignFlows,setCampaignFlows]=useState({}); // { [campaignId]: [flowId, ...] }
  const emptyC={name:"",markets:["AU"],status:"Active",brand:activeBrand||"—",rate:"50/day",platform:"Instagram",minFollowers:"5000",maxFollowers:"50000",minEng:"3",postType:"Reel",payment:"Gifted",sparkAds:false,brief:"",hashtag:"",discountCode:"",linkedFlowIds:[]};
  const[campForm,setCampForm]=useState(emptyC);
  // Pending auto-replies per creator handle → { status, scheduled_for }. Lets the
  // board tell apart a reply the AI is already handling (auto-sending / awaiting
  // your approval) from one that genuinely needs a human. step_index=-1 is the
  // auto-reply sentinel; 'pending' auto-sends on schedule, 'review_pending' waits
  // for the operator to approve (calibration mode).
  const[pendingReplies,setPendingReplies]=useState({});
  const setCF=(k,v)=>setCampForm(f=>({...f,[k]:v}));
  const toggleLinkedFlow=(fid)=>setCampForm(f=>({...f,linkedFlowIds:f.linkedFlowIds.includes(fid)?f.linkedFlowIds.filter(x=>x!==fid):[...f.linkedFlowIds,fid]}));

  // Load campaign_flows for all campaigns once on mount. Cheap query; lets the
  // table show "Linked: 2 flows" indicators and the edit modal prefill instantly.
  useEffect(()=>{
    if(!brandId) return;
    let cancelled=false;
    (async()=>{
      const{data,error}=await supabase.from('campaign_flows')
        .select('campaign_id, flow_id').eq('brand_id',brandId);
      if(cancelled||error) return;
      const map={};
      for(const row of data||[]){
        if(!map[row.campaign_id]) map[row.campaign_id]=[];
        map[row.campaign_id].push(row.flow_id);
      }
      setCampaignFlows(map);
    })();
    return()=>{cancelled=true;};
  },[brandId]);

  // Load pending auto-replies so the board can show "Auto-replying" / "Review"
  // instead of a generic "To Do". Re-runs when activityLog changes (a fresh reply
  // queues an auto-reply; a sent one drops out) so the labels stay live.
  useEffect(()=>{
    if(!brandId) return;
    let cancelled=false;
    (async()=>{
      // -1 = auto-reply, -3 = "went quiet" nudge — both are AI drafts the operator
      // may need to approve.
      const{data,error}=await supabase.from('email_queue')
        .select('id,creator_handle,status,scheduled_for,step_index')
        .eq('brand_id',brandId).in('step_index',[-1,-3]).in('status',['pending','review_pending']);
      if(cancelled||error) return;
      const map={};
      for(const r of data||[]){
        const k=r.creator_handle;
        // review_pending (needs approval) always wins the surface; otherwise keep
        // the soonest-scheduled pending row.
        if(!map[k] || r.status==='review_pending' ||
           (map[k].status!=='review_pending' && new Date(r.scheduled_for)<new Date(map[k].scheduled_for))){
          map[k]={id:r.id,status:r.status,scheduled_for:r.scheduled_for};
        }
      }
      setPendingReplies(map);
    })();
    return()=>{cancelled=true;};
  },[brandId,activityLog]);

  // Board-only sub-status: starts from the pure activity-based status, then — only
  // when the ball is in our court (To Do / Overdue) — upgrades to reflect that the
  // AI already has a reply queued. Kept local so the Dashboard / Creators views
  // (which also call getKanbanSubStatus) are unaffected.
  const boardSub=(creator,stageId)=>{
    const base=getKanbanSubStatus(creator,stageId,activityLog);
    if(base==="To Do"||base==="Overdue"){
      const pr=pendingReplies[creator?.handle];
      if(pr?.status==="review_pending") return "Review";
      if(pr?.status==="pending") return "AI replying";
    }
    return base;
  };
  // Muted one-liner under the pill telling the operator exactly what to do (or that
  // nothing's needed). Falls back to the outreach-stage detail for passive states.
  // Approve / discard an AI draft straight from the card. The "approve to send"
  // line used to point at a button that only existed on the Email Queue page.
  const approvePendingReply=async(handle)=>{
    const pr=pendingReplies[handle]; if(!pr?.id) return;
    const{error}=await supabase.from('email_queue').update({status:'pending'}).eq('id',pr.id).eq('status','review_pending');
    if(error){toast(`Couldn't approve: ${error.message}`,'error');return;}
    setPendingReplies(m=>({...m,[handle]:{...pr,status:'pending'}}));
    toast('✓ Approved — sends on the next worker tick (within 5 min)');
  };
  const discardPendingReply=async(handle)=>{
    const pr=pendingReplies[handle]; if(!pr?.id) return;
    if(!(await confirmDialog({title:"Discard AI draft?",body:"The draft is dropped and nothing is sent. You can still reply yourself from the creator profile.",confirmLabel:"Discard",destructive:true}))) return;
    const{error}=await supabase.from('email_queue').update({status:'cancelled'}).eq('id',pr.id).eq('status','review_pending');
    if(error){toast(`Couldn't discard: ${error.message}`,'error');return;}
    setPendingReplies(m=>{const n={...m};delete n[handle];return n;});
    toast('Draft discarded');
  };
  const boardActionDetail=(creator,stageId,sub)=>{
    if(sub==="Review") return "👀 AI draft ready — approve below, or edit it on the Email Queue page";
    if(sub==="AI replying"){
      const pr=pendingReplies[creator?.handle];
      const when=pr?.scheduled_for?new Date(pr.scheduled_for):null;
      const t=when?when.toLocaleTimeString("en-AU",{hour:"numeric",minute:"2-digit"}):null;
      return t?`🤖 Auto-reply sends ~${t}`:"🤖 Auto-reply queued";
    }
    if(sub==="To Do"||sub==="Overdue") return "✋ Your reply needed";
    return getOutreachStageDetail(creator,stageId,activityLog);
  };

  // Persist campaign_flows for a single campaign: full diff against the current
  // set, then INSERT new + DELETE removed. Keeps the join table in sync with the
  // operator's edit-modal selection without leaving orphans.
  const saveCampaignFlows = async(campaignId, nextFlowIds) => {
    if(!brandId||!campaignId) return;
    const current = campaignFlows[campaignId] || [];
    const toAdd = nextFlowIds.filter(f=>!current.includes(f));
    const toRemove = current.filter(f=>!nextFlowIds.includes(f));
    if(toAdd.length>0){
      const rows = toAdd.map(fid=>({brand_id:brandId, campaign_id:campaignId, flow_id:fid}));
      const{error}=await supabase.from('campaign_flows').insert(rows);
      if(error){toast(`Couldn't link flow: ${error.message}`,'error');return false;}
    }
    if(toRemove.length>0){
      const{error}=await supabase.from('campaign_flows').delete()
        .eq('campaign_id',campaignId).in('flow_id',toRemove);
      if(error){toast(`Couldn't unlink flow: ${error.message}`,'error');return false;}
    }
    setCampaignFlows(prev=>({...prev,[campaignId]:nextFlowIds}));
    return true;
  };

  // (doRename helper removed — replaced by the full Edit modal which includes
  // the name field. handleSaveEdit handles persistence + audit logging.)
  const doDelete = async(c) => {
    const creatorCount=(kanbanData[c.id]||[]).length;
    if(creatorCount>0){
      const{error:ke}=await supabase.from('kanban_items').delete().eq('campaign_id',c.id);
      if(ke){toast(`Couldn't delete kanban: ${ke.message}`,'error');return;}
    }
    const{error}=await supabase.from('campaigns').delete().eq('id',c.id);
    if(error){toast(`Delete failed: ${error.message}`,'error');return;}
    setCampaigns(cs=>cs.filter(x=>x.id!==c.id));
    setKanbanData(prev=>{const next={...prev};delete next[c.id];return next;});
    logSecurityEvent(brandId,'campaign.delete','campaign',c.id,{name:c.name,kanban_items_removed:creatorCount});
    toast(`"${c.name}" deleted`,'error');
    setDeleteTarget(null);
  };

  // Build the DB column patch from the form. Migration 0049 added every UI field
  // (markets/platform/payment/etc.) as a real column on campaigns so the data
  // doesn't drop on refresh.
  const formToColumns=(f)=>({
    name:f.name,
    status:f.status,
    markets:Array.isArray(f.markets)?f.markets:(f.market?[f.market]:[]),
    platform:f.platform||null,
    payment:f.payment||null,
    post_type:f.postType||null,
    min_followers:f.minFollowers||null,
    max_followers:f.maxFollowers||null,
    min_engagement:f.minEng||null,
    rate:f.rate||null,
    spark_ads:!!f.sparkAds,
    brief:f.brief||null,
    hashtag:f.hashtag||null,
    discount_code:f.discountCode||null,
  });

  const handleCreateCampaign=async()=>{
    if(!campForm.name.trim())return;
    if(!brandId){toast("Brand not loaded yet","error");return;}
    if(!userId){toast("User not loaded yet","error");return;}
    const now=new Date();
    const dateStr=`${String(now.getDate()).padStart(2,"0")}-${String(now.getMonth()+1).padStart(2,"0")}-${now.getFullYear()}`;
    const{data,error}=await supabase.from('campaigns').insert({
      brand_id:brandId,user_id:userId,
      ...formToColumns(campForm),
    }).select().single();
    if(error){toast(`Couldn't create campaign: ${error.message}`,"error");return;}
    if(campForm.linkedFlowIds?.length){
      await saveCampaignFlows(data.id, campForm.linkedFlowIds);
    }
    const enriched={...data, market:(data.markets&&data.markets[0])||campForm.market, brand:campForm.brand, rate:data.rate||campForm.rate, platform:data.platform||campForm.platform, payment:data.payment||campForm.payment, postType:data.post_type||campForm.postType, reached:`0/${campForm.maxFollowers||"?"}`, date:dateStr, sparkAds:data.spark_ads, brief:data.brief, hashtag:data.hashtag, discountCode:data.discount_code};
    setCampaigns(cs=>[enriched,...cs]);
    setShowCampaignCreate(false);setCampForm(emptyC);
    toast(`Campaign "${campForm.name}" created`);
  };

  const openEdit=(c)=>{
    setEditTarget(c);
    setCampForm({
      name:c.name||"",
      markets:Array.isArray(c.markets)?c.markets:(c.market?[c.market]:[]),
      status:c.status||"Active",
      brand:c.brand||activeBrand||"—",
      rate:c.rate||"50/day",
      platform:c.platform||"Instagram",
      minFollowers:c.min_followers||c.minFollowers||"",
      maxFollowers:c.max_followers||c.maxFollowers||"",
      minEng:c.min_engagement||c.minEng||"",
      postType:c.post_type||c.postType||"Reel",
      payment:c.payment||"Gifted",
      sparkAds:c.spark_ads||c.sparkAds||false,
      brief:c.brief||"",
      hashtag:c.hashtag||"",
      discountCode:c.discount_code||c.discountCode||"",
      linkedFlowIds:campaignFlows[c.id]||[],
    });
  };
  const handleSaveEdit=async()=>{
    if(!editTarget||!campForm.name.trim())return;
    const patch=formToColumns(campForm);
    const{data,error}=await supabase.from('campaigns').update(patch).eq('id',editTarget.id).select().single();
    if(error){toast(`Save failed: ${error.message}`,'error');return;}
    await saveCampaignFlows(editTarget.id, campForm.linkedFlowIds||[]);
    // Audit log — captures name changes (the old doRename helper logged
    // these separately; preserve the trail since rename is now part of edit).
    const changes = {};
    if(editTarget.name !== campForm.name) changes.name = {from: editTarget.name, to: campForm.name};
    if(editTarget.status !== campForm.status) changes.status = {from: editTarget.status, to: campForm.status};
    if(Object.keys(changes).length > 0){
      logSecurityEvent(brandId,'campaign.edit','campaign',editTarget.id,changes);
    }
    setCampaigns(cs=>cs.map(x=>x.id===editTarget.id?{...x,...data, market:(data.markets&&data.markets[0])||x.market, postType:data.post_type, sparkAds:data.spark_ads, discountCode:data.discount_code}:x));
    setEditTarget(null);setCampForm(emptyC);
    toast(`"${campForm.name}" updated`);
  };

  if(selected){
    const creators=[...(kanbanData[selected.id]||[])].sort((a,b)=>parseFloat(b.followers)-parseFloat(a.followers));
    const byStage=stage=>creators.filter(c=>c.stage===stage);
    const handleDragStart=(creator,fromStage)=>setDragItem({creator,fromStage});
    const handleDrop=toStage=>{
      if(!dragItem||dragItem.fromStage===toStage){setDragItem(null);return;}
      setKanbanData(prev=>({...prev,[selected.id]:prev[selected.id].map(c=>c.name===dragItem.creator.name?{...c,stage:toStage}:c)}));
      const newStatus=STAGE_TO_STATUS[toStage];
      // Persist stage change to DB.
      if(persistKanbanItem&&dragItem.creator.handle){
        persistKanbanItem(selected.id,dragItem.creator.handle,{stage:toStage});
      }
      // Persist via parent's handler — does targeted DB update + activity log + flow reconciliation.
      if(newStatus&&onStatusChange){
        const cr=allCreators.find(c=>c.name===dragItem.creator.name);
        if(cr) onStatusChange(cr,newStatus);
      }
      toast(`${dragItem.creator.name} → ${toStage}`);
      setDragItem(null);
    };
    const COLORS=["#60A5FA","#34D399","#FBBF24","#F87171","#818CF8","#E879F9","#F59E0B","#06B6D4"];
    // Same regex used elsewhere in the app (suppress list, agency prospects).
    const isValidEmail = (e)=>/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e||"").trim());
    const addCreator=async()=>{
      if(!newCreator.name.trim()&&!newCreator.handle.trim())return;
      // Validate + normalise email up-front so we never persist garbage like
      // 'asdfsadf' or 'John@Example.com'. Empty is allowed (creator-without-email
      // is a real state); a non-empty bad value is rejected.
      const rawEmail = (newCreator.email||"").trim();
      if (rawEmail && !isValidEmail(rawEmail)) {
        toast(`"${rawEmail}" doesn't look like an email — fix or leave blank`, 'error');
        return;
      }
      const normalisedEmail = rawEmail ? rawEmail.toLowerCase() : null;
      const name=newCreator.name||newCreator.handle;
      const handle=newCreator.handle.replace("@","").toLowerCase().trim()||name.toLowerCase().replace(/\s/g,"_");
      const exists=allCreators.some(c=>(c.handle||'').toLowerCase()===handle);
      // Add to kanban at "To Outreach" — worker auto-flips to Outreached the
      // moment the linked outreach flow's first email fires (see migration 0049
      // + email-worker post-send hook). Operator no longer has to drag.
      const kanbanItem={name,handle,followers:newCreator.followers||"?",stage:"To Outreach",color:COLORS[Math.floor(Math.random()*COLORS.length)],email:normalisedEmail||"",market:newCreator.market,notes:newCreator.notes};
      setKanbanData(prev=>({...prev,[selected.id]:[...(prev[selected.id]||[]),kanbanItem]}));
      if(!exists && brandId && userId){
        const {data,error}=await supabase.from('creators').insert({
          brand_id:brandId, user_id:userId,
          name, handle,
          email:normalisedEmail,
          followers:newCreator.followers||null,
          platform:"Instagram",
          status:"To Outreach", // worker advances to Outreached on first send
          niche:newCreator.market||null, notes:newCreator.notes||null,
        }).select().single();
        if(error){
          // 23505 = unique_violation. With the new partial-unique-index on
          // (brand_id, lower(handle)) we now catch races where two tabs
          // simultaneously add the same handle. Friendly message instead of
          // raw "duplicate key value violates unique constraint".
          if (error.code === '23505' || /unique|duplicate/i.test(error.message||'')) {
            toast(`@${handle} already exists on this brand`, 'error');
          } else {
            toast(`Add failed: ${error.message}`, 'error');
          }
          return;
        }
        if(setAllCreators) setAllCreators(cs=>[...cs,data]);
      } else if(exists && brandId){
        // Existing creator joining a fresh campaign — only reset to "To Outreach"
        // if they were sitting at no-status / a pre-outreach state. Don't regress
        // someone who already shipped or is paid.
        const existing = allCreators.find(c=>(c.handle||'').toLowerCase()===handle);
        if(existing && /^HARD/i.test(existing.bounce_reason||"")){
          // Dead address. Resetting them to "To Outreach" re-enrolled the cold
          // sequence and produced a fresh bounce every time (3× on 23/9).
          toast(`${name}: their email hard-bounced earlier — nothing will be sent to it. Fix the address on their profile first.`,'error');
        } else if(existing && (!existing.status || existing.status==="Inactive")){
          await supabase.from('creators').update({status:"To Outreach"}).eq('brand_id',brandId).eq('handle',existing.handle);
          if(setAllCreators) setAllCreators(cs=>cs.map(c=>c.handle===existing.handle?{...c,status:"To Outreach"}:c));
        }
      }
      if(persistKanbanItem) persistKanbanItem(selected.id,handle,{stage:"To Outreach"});
      setNewCreator({name:"",handle:"",followers:"",email:"",market:"AU",notes:""});
      setShowAddCreator(false);
      toast(`${name} added — outreach starts as soon as the worker picks it up`);
    };
    return(<div style={{display:"flex",flexDirection:"column",gap:16}}>
      {showAddCreator&&(<Modal title="Add creator" subtitle="Add manually" onClose={()=>setShowAddCreator(false)} width={420}>
        {[{label:"Full name",key:"name",placeholder:"e.g. Alice Baddeley"},{label:"Instagram handle",key:"handle",placeholder:"@handle"},{label:"Followers",key:"followers",placeholder:"e.g. 18.9K"},{label:"Email",key:"email",placeholder:"email@example.com"}].map(f=>(<div key={f.key} style={{marginBottom:14}}><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>{f.label}</div><input value={newCreator[f.key]} onChange={e=>setNewCreator(p=>({...p,[f.key]:e.target.value}))} placeholder={f.placeholder} style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/></div>))}
        <div style={{marginBottom:14}}><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Market</div><div style={{display:"flex",gap:8}}>{["AU","US","EU","UK"].map(m=>(<button key={m} onClick={()=>setNewCreator(p=>({...p,market:m}))} style={{flex:1,padding:"8px",border:`1.5px solid ${newCreator.market===m?G:BORDER}`,borderRadius:8,background:newCreator.market===m?G+"15":"#fff",color:newCreator.market===m?DARK:"#9ab8a6",fontWeight:newCreator.market===m?700:400,fontSize:12,cursor:"pointer"}}>{m}</button>))}</div></div>
        <div style={{marginBottom:20}}><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Notes</div><textarea value={newCreator.notes} onChange={e=>setNewCreator(p=>({...p,notes:e.target.value}))} rows={2} style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",resize:"none",fontFamily:"inherit",boxSizing:"border-box"}}/></div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}><button onClick={()=>setShowAddCreator(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button><button onClick={addCreator} style={{background:DARK,color:G,border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Add creator</button></div>
      </Modal>)}
      <div style={{display:"flex",alignItems:"center",gap:14}}>
        <button onClick={()=>setSelected(null)} style={{display:"flex",alignItems:"center",gap:6,border:"none",background:"none",cursor:"pointer",color:"#9ab8a6",fontSize:13,fontWeight:500,padding:0}} onMouseEnter={e=>e.currentTarget.style.color=DARK} onMouseLeave={e=>e.currentTarget.style.color="#9ab8a6"}><Ico d="M10 19l-7-7m0 0l7-7m-7 7h18" size={15}/> Back</button>
        <div style={{fontWeight:700,color:DARK,fontSize:15}}>{selected.name}</div>
        <Badge label={selected.status} color={selected.status==="Active"?"#ECFDF5":"#F9FAFB"} text={selected.status==="Active"?"#166534":"#6B7280"}/>
        <div style={{fontSize:12,color:"#9ab8a6",marginLeft:"auto"}}>Drag cards between columns to update status</div>
      </div>
      <StatusLegend/>
      <div style={{display:"grid",gridTemplateColumns:`repeat(${STAGES.length},1fr)`,gap:10,overflowX:"auto",paddingBottom:4}}>
        {STAGES.map(stage=>{
          // Sort within the column so the cards needing operator action bubble to
          // the top: Overdue / To Do (you must reply) → Review (approve AI draft) →
          // AI replying (FYI, no action) → Follow-up → Waiting → No Task. Stable
          // sort keeps existing order inside each tier. Columns don't have a manual
          // intra-column order to preserve (drag only moves between stages).
          const SUB_ORDER={"Overdue":0,"To Do":1,"Review":2,"AI replying":3,"Follow-up":4,"Waiting":5,"No Task":6};
          const subFor=card=>{const f=allCreators.find(cr=>cr.handle===card.handle||cr.name===card.name)||card;return SUB_ORDER[boardSub(f,stage.id)]??7;};
          const sc=[...byStage(stage.id)].sort((a,b)=>subFor(a)-subFor(b));return(<div key={stage.id} onDragOver={e=>e.preventDefault()} onDrop={()=>handleDrop(stage.id)} style={{background:dragItem&&dragItem.fromStage!==stage.id?stage.color+"08":"#F8FAF9",borderRadius:14,padding:"14px 12px",minHeight:300,border:`2px dashed ${dragItem&&dragItem.fromStage!==stage.id?stage.color+"40":"transparent"}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,paddingBottom:12,borderBottom:`2px solid ${stage.color}30`}}><div style={{fontWeight:700,fontSize:12,color:stage.color,textTransform:"uppercase",letterSpacing:".07em"}}>{stage.id}</div><div style={{background:stage.color+"20",color:stage.color,borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700}}>{sc.length}</div></div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {sc.map((c,i)=>{const fullForSub=allCreators.find(cr=>cr.handle===c.handle||cr.name===c.name)||c;const sub=boardSub(fullForSub,stage.id);const subColor={"Overdue":"#EF4444","No Task":"#9CA3AF","To Do":"#F59E0B","Review":"#8B5CF6","AI replying":"#3B82F6","Follow-up":"#F97316","Waiting":"#10B981"}[sub];const subBg={"Overdue":"#FEF2F2","No Task":"#F9FAFB","To Do":"#FFFBEB","Review":"#F5F3FF","AI replying":"#EFF6FF","Follow-up":"#FFF7ED","Waiting":"#ECFDF5"}[sub];
            return(<div key={i} draggable onDragStart={()=>handleDragStart(c,stage.id)} onClick={()=>{
              const full=allCreators.find(cr=>cr.handle===c.handle)||allCreators.find(cr=>cr.name===c.name);
              if(full){onSelectCreator(full);}
              else{onSelectCreator({name:c.name,handle:c.handle,followers:0,followersF:c.followers||"—",eng:"—",engN:0,status:stage.id,score:null,market:c.market||"—",pocket:"—",order:null,location:c.market||"—",avgViews:"—",avgLikes:"—",credibility:"—",color:c.color,email:c.email||null,notes:c.notes||null});}
            }} style={{background:"#fff",borderRadius:11,padding:"12px 14px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",cursor:"grab",userSelect:"none",border:`1px solid ${sub==="Overdue"?"#FECACA":"#f0f7f3"}`}} onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.1)";e.currentTarget.style.transform="translateY(-1px)";}} onMouseLeave={e=>{e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,0.06)";e.currentTarget.style.transform="";}}>
              <div style={{display:"flex",gap:10,alignItems:"center"}}>
                <Avatar name={c.name} color={c.color} size={30}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:12,color:DARK,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</div>
                  <div style={{fontSize:10,marginTop:1}}><HandleLink handle={c.handle} size={10}/></div>
                </div>
              </div>
              <div style={{marginTop:8,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:4}}>
                <div style={{fontSize:11,color:"#6b9e80",fontWeight:500}}>{c.followers} followers</div>
                {sub&&<span style={{fontSize:10,fontWeight:700,color:subColor,background:subBg,borderRadius:20,padding:"2px 8px",border:`1px solid ${subColor}30`}}>{sub}</span>}
              </div>
              {(()=>{const detail=boardActionDetail(fullForSub,stage.id,sub);if(!detail)return null;const actionable=sub==="Review"||sub==="To Do"||sub==="Overdue";return<div style={{marginTop:5,fontSize:10,color:actionable?subColor:"#9ab8a6",fontWeight:actionable?700:500}}>{detail}</div>;})()}
              {sub==="Review"&&pendingReplies[fullForSub?.handle||c.handle]?.id&&<div onClick={e=>e.stopPropagation()} onMouseDown={e=>e.stopPropagation()} draggable={false} style={{marginTop:7,display:"flex",gap:6}}>
                <button onClick={()=>approvePendingReply(fullForSub?.handle||c.handle)} style={{border:"none",background:"#8B5CF6",color:"#fff",borderRadius:6,padding:"4px 11px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Approve &amp; send</button>
                <button onClick={()=>discardPendingReply(fullForSub?.handle||c.handle)} style={{border:"1px solid #DDD6FE",background:"#fff",color:"#5B21B6",borderRadius:6,padding:"4px 9px",fontSize:11,fontWeight:600,cursor:"pointer"}}>Discard</button>
              </div>}
              {(()=>{
                const fullC=allCreators.find(cr=>cr.handle===c.handle||cr.name===c.name);
                const readiness=getOrderReadiness({...(fullC||c),stage:stage.id});
                if(!readiness) return null;
                const cfg={
                  ready:{label:"📦 Ready to order",bg:"#F0FDF4",color:"#166534",border:"#BBF7D0"},
                  no_address:{label:"⚠️ Need address",bg:"#FFFBEB",color:"#92400E",border:"#FDE68A"},
                  no_product:{label:"💬 Product unconfirmed",bg:"#F8FAFF",color:"#3B82F6",border:"#BFDBFE"},
                }[readiness];
                return(
                  <div onClick={e=>e.stopPropagation()} style={{marginTop:8,display:"inline-flex",alignItems:"center",gap:4,background:cfg.bg,border:`1px solid ${cfg.border}`,borderRadius:20,padding:"2px 9px",fontSize:10,fontWeight:700,color:cfg.color,cursor:"default"}}>
                    {cfg.label}
                  </div>
                );
              })()}
            </div>);})}
            {sc.length===0&&<div onClick={()=>stage.id==="To Outreach"?setShowAddCreator(true):null} style={{border:"2px dashed #e8f0eb",borderRadius:10,padding:"20px",textAlign:"center",color:"#c0d8c8",fontSize:12,cursor:stage.id==="To Outreach"?"pointer":"default"}}>{stage.id==="To Outreach"?"+ Add creator":"Drop here"}</div>}
            {stage.id==="To Outreach"&&sc.length>0&&<button onClick={()=>setShowAddCreator(true)} style={{background:"none",border:"1.5px dashed #c0d4c8",borderRadius:10,padding:"10px",fontSize:12,color:"#6b9e80",cursor:"pointer",fontWeight:600,width:"100%",marginTop:4}} onMouseEnter={e=>{e.currentTarget.style.borderColor=G;e.currentTarget.style.color=G;}} onMouseLeave={e=>{e.currentTarget.style.borderColor="#c0d4c8";e.currentTarget.style.color="#6b9e80";}}>+ Add creator</button>}
          </div>
        </div>);})}
      </div>
    </div>);
  }

  
  const filtered=campaigns.filter(c=>filter==="All"||c.status===filter);
  return(<div style={{display:"flex",flexDirection:"column",gap:16}}>
    {deleteTarget && <ConfirmModal title="Delete campaign" destructive confirmLabel="Delete permanently"
      body={<>Delete <strong>"{deleteTarget.name}"</strong>?{(kanbanData[deleteTarget.id]||[]).length>0 && <> The {(kanbanData[deleteTarget.id]||[]).length} creator{(kanbanData[deleteTarget.id]||[]).length===1?"":"s"} in this campaign will be unlinked (they stay in your Creators list). </>} This can't be undone.</>}
      onCancel={()=>setDeleteTarget(null)} onConfirm={()=>doDelete(deleteTarget)}/>}
    {(showCampaignCreate||editTarget)&&(()=>{
      const isEdit=!!editTarget;
      const close=()=>{if(isEdit) setEditTarget(null); else setShowCampaignCreate(false); setCampForm(emptyC);};
      const submit=isEdit?handleSaveEdit:handleCreateCampaign;
      // Group flows by type so the operator can see the available pool per type
      // (Outreach / Fulfillment / Post-content). Active-only — disabled flows
      // can't be linked.
      const flowsByType=(flows||[]).filter(f=>f.is_active!==false).reduce((acc,f)=>{const t=f.type||"Outreach";(acc[t]=acc[t]||[]).push(f);return acc;},{});
      return(<Modal title={isEdit?`Edit "${editTarget.name}"`:"New Campaign"} subtitle={isEdit?"Update campaign details and linked flows.":"Set up a new influencer outreach campaign."} onClose={close} width={620}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
        <div style={{gridColumn:"1/-1"}}>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Campaign Name *</div>
          <input value={campForm.name} onChange={e=>setCF("name",e.target.value)} placeholder="e.g. AU | IG Seeding | 1-5k | Swimming" style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
        </div>
        <div style={{gridColumn:"1/-1"}}>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Target markets <span style={{color:MUTED,fontWeight:400}}>(pick one or more)</span></div>
          <CountryMultiSelect value={Array.isArray(campForm.markets)?campForm.markets:(campForm.market?[campForm.market]:[])} onChange={v=>setCF("markets",v)}/>
        </div>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Status</div>
          <select value={campForm.status} onChange={e=>setCF("status",e.target.value)} style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",background:"#fff",boxSizing:"border-box"}}>
            <option>Paused</option>
            <option>Active</option>
          </select>
        </div>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Platform</div>
          <select value={campForm.platform} onChange={e=>setCF("platform",e.target.value)} style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",background:"#fff",boxSizing:"border-box"}}>
            <option>Instagram</option><option>TikTok</option><option>YouTube</option>
          </select>
        </div>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Outreach rate (new creators/day)</div>
          <div style={{fontSize:11,color:MUTED,marginBottom:6,lineHeight:1.5}}>Counts first emails to new creators only. Follow-ups to people already contacted aren't budgeted here — the inbox's daily cap (Settings → Email) is their only limit.</div>
          <input type="number" min={1} max={500} value={parseInt(campForm.rate)||50} onChange={e=>setCF("rate",`${Math.max(1,Math.min(500,parseInt(e.target.value)||50))}/day`)} placeholder="50" style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
        </div>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Payment</div>
          <select value={campForm.payment} onChange={e=>setCF("payment",e.target.value)} style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",background:"#fff",boxSizing:"border-box"}}>
            <option>Gifted</option><option>Paid</option><option>Gifted + Paid</option>
          </select>
        </div>
        <div style={{gridColumn:"1/-1"}}>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Hashtag</div>
          <input value={campForm.hashtag} onChange={e=>setCF("hashtag",e.target.value)} placeholder="#yourcampaign" style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
        </div>
        <div style={{gridColumn:"1/-1"}}>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Brief / notes</div>
          <textarea value={campForm.brief} onChange={e=>setCF("brief",e.target.value)} rows={3} placeholder="Internal notes — angle, talking points, targets, anything the team should know." style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"inherit",resize:"vertical"}}/>
        </div>
        <div style={{gridColumn:"1/-1",borderTop:`1px solid ${BORDER}`,paddingTop:14}}>
          <div style={{fontSize:12,fontWeight:700,color:DARK,marginBottom:8}}>Linked email flows</div>
          <div style={{fontSize:11,color:MUTED,marginBottom:10,lineHeight:1.5}}>Pin specific flows to this campaign. When a creator in this campaign hits a status that needs a flow (Outreached, Ordered, etc.), only flows linked here are eligible. Multiple flows of the same type A/B split across creators. Nothing linked = brand-wide default.</div>
          {(flows||[]).length===0
            ?<div style={{padding:"14px 12px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,fontSize:12,color:"#92400E"}}>No flows yet. Create flows in the Flows section, then come back to link them here.</div>
            :<div style={{display:"flex",flexDirection:"column",gap:10,maxHeight:240,overflowY:"auto"}}>
              {["Outreach","Fulfillment","Post-content"].map(type=>{
                const list=flowsByType[type]||[];
                if(list.length===0) return null;
                return(<div key={type}>
                  <div style={{fontSize:10,fontWeight:700,color:MUTED,textTransform:"uppercase",letterSpacing:".06em",marginBottom:5}}>{type}</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {list.map(f=>{
                      const on=campForm.linkedFlowIds.includes(f.id);
                      return(<button key={f.id} onClick={()=>toggleLinkedFlow(f.id)} style={{border:`1.5px solid ${on?G:BORDER}`,background:on?"#F0FDF4":"#fff",color:on?DARK:MUTED,borderRadius:8,padding:"6px 11px",fontSize:12,cursor:"pointer",fontWeight:on?700:500,display:"inline-flex",alignItems:"center",gap:6}}>
                        <span style={{width:14,height:14,borderRadius:4,border:`1.5px solid ${on?G:BORDER}`,background:on?G:"#fff",display:"inline-flex",alignItems:"center",justifyContent:"center",color:DARK,fontSize:10,fontWeight:900}}>{on?"✓":""}</span>
                        {f.name}
                      </button>);
                    })}
                  </div>
                </div>);
              })}
            </div>
          }
        </div>
      </div>
      <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
        <button onClick={close} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer",fontWeight:500}}>Cancel</button>
        <button onClick={submit} style={{background:campForm.name?DARK:"#f0f0f0",color:campForm.name?G:"#9ab8a6",border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:campForm.name?"pointer":"not-allowed"}}>{isEdit?"Save changes":"Create Campaign"}</button>
      </div>
    </Modal>);})()}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <div style={{display:"flex",gap:4,background:"#fff",borderRadius:10,padding:4,boxShadow:CARD}}>{["All","Active","Paused"].map(t=>(<button key={t} onClick={()=>setFilter(t)} style={{background:filter===t?DARK:"transparent",color:filter===t?G:"#9ab8a6",border:"none",borderRadius:7,padding:"7px 16px",fontSize:12,cursor:"pointer",fontWeight:filter===t?700:400}}>{t}</button>))}</div>
      <button onClick={()=>setShowCampaignCreate(true)} style={{background:G,color:DARK,border:"none",borderRadius:10,padding:"10px 18px",fontWeight:700,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}><span style={{fontSize:18,lineHeight:1}}>+</span> Create campaign</button>
    </div>
    {campaigns.length===0
      ?<Card style={{padding:56,textAlign:"center"}}>
          <div style={{fontSize:48,marginBottom:16}}>📣</div>
          <div style={{fontWeight:700,color:DARK,fontSize:18,marginBottom:8}}>No campaigns yet</div>
          <div style={{fontSize:13,color:MUTED,marginBottom:24,lineHeight:1.6,maxWidth:320,margin:"0 auto 24px"}}>Create your first outreach campaign to start managing creators at scale.</div>
          <button onClick={()=>setShowCampaignCreate(true)} style={{background:DARK,color:G,border:"none",borderRadius:10,padding:"11px 24px",fontWeight:700,fontSize:13,cursor:"pointer"}}>+ Create first campaign</button>
        </Card>
      :<Card>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{borderBottom:"1px solid #f0f7f3"}}>{["Campaign name","Brand","Status","Queue","Rate","Created",""].map(h=>(<th key={h} style={{padding:"12px 16px",textAlign:"left",fontSize:10,color:"#9ab8a6",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em"}}>{h}</th>))}</tr></thead>
          <tbody>{filtered.length===0
            ?<tr><td colSpan={7} style={{padding:"32px 16px",textAlign:"center",color:MUTED,fontSize:13}}>
                No campaigns match this filter. <button onClick={()=>setFilter("All")} style={{background:"none",border:"none",color:G,fontWeight:700,cursor:"pointer",fontSize:13}}>Show all</button>
              </td></tr>
            :filtered.map((c,i)=>{
              // Queue: count creators in "To Outreach" stage for this campaign
              const stageCreators=kanbanData[c.id]||[];
              const inQueue=stageCreators.filter(cr=>cr.stage==="To Outreach").length;
              const outreached=stageCreators.filter(cr=>cr.stage!=="To Outreach").length;
              const total=stageCreators.length;
              const rateNum=parseInt(c.rate)||50;
              const daysLeft=inQueue>0?Math.ceil(inQueue/rateNum):0;
            return(
            <tr key={i} style={{borderBottom:"1px solid #f8faf9",cursor:"pointer"}} onClick={()=>setSelected(c)} onMouseEnter={e=>e.currentTarget.style.background="#fafcfb"} onMouseLeave={e=>e.currentTarget.style.background=""}>
              <td style={{padding:"14px 16px"}}><div style={{display:"flex",alignItems:"center",gap:10}}><div style={{width:8,height:8,borderRadius:"50%",background:c.status==="Active"?G:"#9ca3af",boxShadow:c.status==="Active"?`0 0 0 3px ${G}30`:"none",flexShrink:0}}/><span style={{fontWeight:600,color:DARK,fontSize:13}}>{c.name}</span></div></td>
              <td style={{padding:"14px 16px",color:"#9ab8a6",fontSize:12}}>{c.brand}</td>
              <td style={{padding:"14px 16px"}} onClick={async e=>{
                e.stopPropagation();
                const ns=c.status==="Active"?"Paused":"Active";
                // Optimistic UI then persist. Status was previously in-memory only —
                // refresh would revert. Now writes to DB.
                setCampaigns(cs=>cs.map(x=>x.id===c.id?{...x,status:ns}:x));
                const{error}=await supabase.from('campaigns').update({status:ns}).eq('id',c.id);
                if(error){toast(`Couldn't save status: ${error.message}`,'error');setCampaigns(cs=>cs.map(x=>x.id===c.id?{...x,status:c.status}:x));return;}
                toast(`${c.name} → ${ns}`);
              }}>
                <span style={{background:c.status==="Active"?"#ECFDF5":"#F9FAFB",color:c.status==="Active"?"#166534":"#6B7280",border:`1px solid ${c.status==="Active"?"#BBF7D0":"#E5E7EB"}`,borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:600,cursor:"pointer",userSelect:"none",display:"inline-flex",alignItems:"center",gap:5}}>
                  {c.status}<span style={{fontSize:9,opacity:.6}}>▾</span>
                </span>
              </td>
              <td style={{padding:"14px 16px"}}>
                {total>0
                  ?<div>
                    <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:4}}>
                      <div style={{flex:1,maxWidth:80,background:"#f0f0f0",borderRadius:4,height:4,overflow:"hidden"}}><div style={{width:`${total>0?Math.round(outreached/total*100):0}%`,height:"100%",background:G}}/></div>
                      <span style={{fontSize:11,color:DARK,fontWeight:600}}>{outreached}/{total}</span>
                    </div>
                    {inQueue>0&&c.status==="Active"&&<div style={{fontSize:10,color:MUTED}}>{inQueue} in queue · ~{daysLeft}d left</div>}
                    {inQueue===0&&total>0&&<div style={{fontSize:10,color:G,fontWeight:600}}>All outreached ✓</div>}
                  </div>
                  :<span style={{fontSize:12,color:MUTED}}>No creators added</span>
                }
              </td>
              <td style={{padding:"14px 16px",fontFamily:"DM Mono, monospace",fontSize:12,fontWeight:600,color:DARK}}>{c.rate}</td>
              <td style={{padding:"14px 16px",fontSize:12,color:"#9ab8a6"}}>{c.date}</td>
              <td style={{padding:"14px 16px"}} onClick={e=>e.stopPropagation()}>
                <div style={{display:"flex",gap:4}}>
                  {/* EDIT — full edit modal (markets, brief, hashtag, flow links, etc.).
                      The previous rename-only ✏️ button was dropped — the edit modal
                      includes the name field on the first row, so there's no shortcut
                      needed and two pencil icons next to each other was confusing. */}
                  <button onClick={()=>openEdit(c)}
                    style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:7,padding:"5px 9px",fontSize:12,cursor:"pointer",color:MUTED}} title="Edit campaign"
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=G;e.currentTarget.style.color=DARK;}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=BORDER;e.currentTarget.style.color=MUTED;}}>✎ Edit</button>

                  {/* DUPLICATE — was in-memory only, now creates a proper DB row */}
                  <button onClick={async()=>{
                    if(!brandId||!userId){toast("Brand not loaded","error");return;}
                    const{data,error}=await supabase.from('campaigns').insert({
                      brand_id:brandId,user_id:userId,
                      name:`Copy of ${c.name}`,status:"Paused",
                    }).select().single();
                    if(error){toast(`Duplicate failed: ${error.message}`,'error');return;}
                    // Preserve UI-only fields (rate, brand, market, etc.) on the duplicate.
                    const now=new Date();
                    const d=`${String(now.getDate()).padStart(2,"0")}-${String(now.getMonth()+1).padStart(2,"0")}-${now.getFullYear()}`;
                    // Copy the source campaign's linked email flows onto the duplicate.
                    const sourceFlowIds=campaignFlows[c.id]||c.linkedFlowIds||[];
                    if(sourceFlowIds.length) await saveCampaignFlows(data.id, sourceFlowIds);
                    const dup={...data,market:c.market,brand:c.brand,rate:c.rate,platform:c.platform,payment:c.payment,postType:c.postType,reached:"0/0",date:d,sparkAds:c.sparkAds,brief:c.brief,hashtag:c.hashtag,discountCode:c.discountCode,linkedFlowIds:sourceFlowIds};
                    setCampaigns(cs=>[dup,...cs]);
                    logSecurityEvent(brandId,'campaign.duplicate','campaign',data.id,{source:c.id,name:dup.name});
                    toast(`"${c.name}" duplicated (paused) — review + activate when ready`);
                  }} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:7,padding:"5px 9px",fontSize:12,cursor:"pointer",color:MUTED}} title="Duplicate"
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=G;e.currentTarget.style.color=DARK;}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=BORDER;e.currentTarget.style.color=MUTED;}}>⧉</button>

                  {/* DELETE — styled confirm modal (replaces window.confirm) */}
                  <button onClick={()=>setDeleteTarget(c)}
                    style={{border:`1px solid #FECACA`,background:"#fff",borderRadius:7,padding:"5px 9px",fontSize:12,cursor:"pointer",color:"#DC2626"}} title="Delete campaign"
                    onMouseEnter={e=>{e.currentTarget.style.background="#FEF2F2";}}
                    onMouseLeave={e=>{e.currentTarget.style.background="#fff";}}>🗑</button>
                </div>
              </td>
            </tr>);
          })}</tbody>
        </table>
      </Card>
    }
  </div>);
}

// Recommended minimum sample size per variant before declaring an A/B winner.
// Below this, observed reply rates are mostly noise (small-N variance dominates real effect).
// Industry rule-of-thumb for proportion tests with effect sizes typical of email A/B is
// 200–500 per variant; we default to 300. Can be made a brand setting later if needed.
const MIN_AB_SAMPLE_PER_VARIANT = 300;

// Two-proportion z-test. Returns {z, confidence} where confidence is null if not
// statistically meaningful, "95%" if |z| > 1.96, "99%" if |z| > 2.576.
// Pooled variance — standard test for comparing reply-rate proportions across two variants.
function abZScore(aSent, aReplies, bSent, bReplies) {
  if (aSent < 30 || bSent < 30) return { z: 0, confidence: null }; // CLT shaky below n=30
  const p1 = aReplies / aSent, p2 = bReplies / bSent;
  const pPool = (aReplies + bReplies) / (aSent + bSent);
  const denom = Math.sqrt(pPool * (1 - pPool) * (1/aSent + 1/bSent));
  if (denom === 0) return { z: 0, confidence: null };
  const z = (p2 - p1) / denom;
  let confidence = null;
  if (Math.abs(z) >= 2.576) confidence = "99%";
  else if (Math.abs(z) >= 1.96) confidence = "95%";
  return { z, confidence };
}

// Aggregate A/B verdict — what to show the operator in the UI + how confident to be in a winner.
function evaluateAB(stats) {
  const { A, B } = stats;
  const sampleMet = A.sent >= MIN_AB_SAMPLE_PER_VARIANT && B.sent >= MIN_AB_SAMPLE_PER_VARIANT;
  const { z, confidence } = abZScore(A.sent, A.replies, B.sent, B.replies);
  const winner = A.rate === B.rate ? null : (A.rate > B.rate ? 'A' : 'B');
  return {
    sampleMet,
    moreNeeded: { A: Math.max(0, MIN_AB_SAMPLE_PER_VARIANT - A.sent), B: Math.max(0, MIN_AB_SAMPLE_PER_VARIANT - B.sent) },
    winner,
    z, confidence,
    significant: !!confidence,
  };
}

function Flows({flows,setFlows,saveState='idle',savedAt=null,brandId=null}){
  const[editing,setEditing]=useState(null);
  const[activeStep,setActiveStep]=useState(0);
  // Editor sidebar mode — Steps (default editing view) vs Settings (name, type,
  // active, reply-pause, PDF). Splitting these prevents the old all-in-one
  // sidebar from drowning the operator in chrome before they edit a single step.
  const[editorSidebar,setEditorSidebar]=useState('steps'); // 'steps' | 'settings'
  // Per-step A/B metrics, keyed by stepId. Lazy-loaded when editing an A/B step.
  const[variantStats,setVariantStats]=useState({}); // { [stepId]: {A:{sent,replies,rate}, B:{...}} }
  const[loadingVariantStats,setLoadingVariantStats]=useState(false);
  // Re-render the "Saved · 5s ago" label every 15s so it stays current without manual refresh.
  const[,setNow]=useState(Date.now());
  useEffect(()=>{const t=setInterval(()=>setNow(Date.now()),15000);return()=>clearInterval(t);},[]);
  const renderSaveStatus=()=>{
    if(saveState==='pending'||saveState==='saving') return <span style={{color:MUTED,fontSize:12,display:"flex",alignItems:"center",gap:6}}><span style={{width:6,height:6,borderRadius:"50%",background:"#F59E0B",display:"inline-block"}}/>Saving…</span>;
    if(saveState==='error') return <span style={{color:"#DC2626",fontSize:12,display:"flex",alignItems:"center",gap:6}}>⚠ Save failed — check connection</span>;
    if(savedAt){
      const sec=Math.floor((Date.now()-savedAt)/1000);
      const label=sec<5?"just now":sec<60?`${sec}s ago`:sec<3600?`${Math.floor(sec/60)}m ago`:new Date(savedAt).toLocaleTimeString();
      return <span style={{color:"#166534",fontSize:12,display:"flex",alignItems:"center",gap:6}}><span style={{width:6,height:6,borderRadius:"50%",background:G,display:"inline-block"}}/>Saved · {label}</span>;
    }
    return <span style={{color:MUTED,fontSize:12}}>Auto-saves as you type</span>;
  };
  const[showCreate,setShowCreate]=useState(false);
  const[newName,setNewName]=useState("");
  const[newType,setNewType]=useState("Outreach");
  const[deleteTarget,setDeleteTarget]=useState(null);
  const[menuOpen,setMenuOpen]=useState(null);
  const[createStep,setCreateStep]=useState("pick");
  const[selectedTpl,setSelectedTpl]=useState(null);
  const[tplFilter,setTplFilter]=useState("All");
  // PDF attach picker: holds the flow_id we're attaching a PDF to + loaded file list.
  const[pdfAttachTarget,setPdfAttachTarget]=useState(null);
  const[pdfFiles,setPdfFiles]=useState(null);          // null = not loaded, [] = empty
  const[pdfFilesLoading,setPdfFilesLoading]=useState(false);
  const[pdfFilesError,setPdfFilesError]=useState(null);
  const[pdfBrandRootUrl,setPdfBrandRootUrl]=useState(null);
  const[pdfPickerSearch,setPdfPickerSearch]=useState("");
  const bodyRef=useRef(null);
  const execFmt=(cmd,val)=>{document.execCommand(cmd,false,val);bodyRef.current?.focus();};

  // ---------- Saved email templates (brand_email_templates) ----------
  // Operator-authored reusable {name,subject,body} emails, surfaced as a picker
  // in the Send Email modal. Managed here in the Flows tab, next to flow templates.
  const[emailTpls,setEmailTpls]=useState([]);
  const[emailTplsLoaded,setEmailTplsLoaded]=useState(false);
  const[tplEditor,setTplEditor]=useState(null); // null | {id?, name, subject, body}
  const[tplSaving,setTplSaving]=useState(false);
  useEffect(()=>{
    if(!brandId){setEmailTpls([]);setEmailTplsLoaded(true);return;}
    let cancelled=false;
    (async()=>{
      const{data}=await supabase.from('brand_email_templates').select().eq('brand_id',brandId).order('name');
      if(!cancelled){setEmailTpls(data||[]);setEmailTplsLoaded(true);}
    })();
    return()=>{cancelled=true;};
  },[brandId]);
  const saveEmailTpl=async()=>{
    if(!tplEditor?.name?.trim()){toast("Template needs a name","error");return;}
    if(!brandId){toast("No brand context — refresh and try again","error");return;}
    setTplSaving(true);
    const payload={brand_id:brandId,name:tplEditor.name.trim(),subject:tplEditor.subject||"",body:tplEditor.body||""};
    if(tplEditor.id){
      const{data,error}=await supabase.from('brand_email_templates').update(payload).eq('id',tplEditor.id).select().single();
      setTplSaving(false);
      if(error){toast(error.message,'error');return;}
      setEmailTpls(prev=>prev.map(t=>t.id===tplEditor.id?data:t).sort((a,b)=>a.name.localeCompare(b.name)));
      toast(`Template "${payload.name}" saved`);
    }else{
      const{data,error}=await supabase.from('brand_email_templates').insert({...payload,created_by_clerk_user_id:window.Clerk?.user?.id||null}).select().single();
      setTplSaving(false);
      if(error){toast(error.message,'error');return;}
      setEmailTpls(prev=>[...prev,data].sort((a,b)=>a.name.localeCompare(b.name)));
      toast(`Template "${payload.name}" created`);
    }
    setTplEditor(null);
  };
  const deleteEmailTpl=async(t)=>{
    if(!(await confirmDialog({title:`Delete template "${t.name}"?`,body:"It's removed from the Send Email picker. Flows are unaffected.",destructive:true,confirmLabel:"Delete"})))return;
    const{error}=await supabase.from('brand_email_templates').delete().eq('id',t.id);
    if(error){toast(error.message,'error');return;}
    setEmailTpls(prev=>prev.filter(x=>x.id!==t.id));
    toast(`Template "${t.name}" deleted`);
  };

  // Fetch PDFs in the brand's Drive folders. Called when the picker opens.
  const loadBrandPdfs = async()=>{
    if(!brandId) return;
    setPdfFilesLoading(true);setPdfFilesError(null);
    try{
      const t = await window.Clerk?.session?.getToken();
      const resp = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-drive/list-brand-pdfs`,{
        method:'POST',
        headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},
        body:JSON.stringify({brand_id:brandId}),
      });
      const data = await resp.json();
      if(!resp.ok){
        if(data.error==='drive_not_provisioned') setPdfFilesError("Drive isn't set up for this brand yet. Go to Settings → Integrations → Connect Google Drive.");
        else if(data.error==='agency_drive_not_connected') setPdfFilesError("Google Drive isn't connected. Settings → Integrations → Connect Google Drive.");
        else setPdfFilesError(data.message || data.error || `Couldn't load files (${resp.status})`);
        setPdfFilesLoading(false);
        return;
      }
      setPdfFiles(data.files || []);
      setPdfBrandRootUrl(data.brand_root_url || null);
    }catch(e){
      setPdfFilesError(`Network error: ${e.message||e}`);
    }
    setPdfFilesLoading(false);
  };
  const openPdfPicker = (flowId)=>{
    setPdfAttachTarget(flowId);
    setPdfPickerSearch("");
    if(pdfFiles===null) loadBrandPdfs(); // first open — fetch
  };
  const closePdfPicker = ()=>{setPdfAttachTarget(null);setPdfPickerSearch("");};
  const pickPdf = (file)=>{
    setFlows(prev=>prev.map(f=>f.id===pdfAttachTarget?{...f,attach_pdf_drive_file_id:file.id, attach_pdf_filename:file.name}:f));
    closePdfPicker();
    toast(`📎 "${file.name}" attached — first email of this flow will include it`);
  };
  const formatBytes = (n)=>{
    if(!n) return "—";
    if(n<1024) return `${n} B`;
    if(n<1024*1024) return `${(n/1024).toFixed(0)} KB`;
    return `${(n/1024/1024).toFixed(1)} MB`;
  };

  // Wrap {{placeholders}} in a visible chrome span (orange, bold) inside the editor
  // ONLY. We use a data-attribute so we can confidently strip it back to {{name}}
  // before saving — never persist editor chrome into the DB body, otherwise the
  // worker's interpolate() won't find the placeholder keys.
  // Chrome is colour + tint only — NOT bold. A bold chrome hid real <strong>
  // formatting around placeholders (ChatGPT pastes arrive as
  // <strong>{{firstname}}</strong>), so bold greetings/sign-offs shipped for weeks
  // with nobody able to see it in the editor.
  const wrapPlaceholders=(html)=>html
    .replace(/{{(\w+(?:\|[^}]+)?)}}/g,'<span data-ph="1" style="color:#B45309;background:#FFF4E0;border-radius:3px;padding:0 2px">{{$1}}</span>');
  const stripPlaceholderChrome=(html)=>{
    if(!html) return "";
    // Strip the chrome spans back to bare {{name}}: our data-ph spans, plus any
    // leaked copy of the old orange chrome (a template copied out of a rendered
    // editor carried <span style="color: rgb(245, 158, 11); font-weight: 600;">
    // into the DB and every email).
    return html
      .replace(/<span\s+data-ph="1"[^>]*>(\{\{[^}]+\}\})<\/span>/gi,'$1')
      .replace(/<span\s+[^>]*(?:#F59E0B|245,\s*158,\s*11)[^>]*>\s*(\{\{[^}]+\}\})\s*<\/span>/gi,'$1');
  };
  // Heuristic: is the stored body already HTML (rich) or legacy plain text?
  // Legacy templates + older flows are plain text — promote to <p>-wrapped HTML
  // on first edit so the contentEditable surface treats lines correctly.
  const ensureBodyHtml=(body)=>{
    if(!body) return "";
    if(/<\/?[a-z][^>]*>/i.test(body)) return body; // already HTML
    // Plain text → wrap paragraphs (blank-line separated) in <p>, single newlines as <br>.
    const escaped=body.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    return escaped.split(/\n{2,}/).map(p=>`<p style="margin:0 0 12px 0">${p.replace(/\n/g,"<br>")}</p>`).join("");
  };

  const[activeVariant,setActiveVariant]=useState("A");

  useEffect(()=>{
    if(bodyRef.current&&editing){
      const flow=flows.find(f=>f.id===editing)||{steps:[]};
      const step=flow.steps[activeStep];
      const raw=step?.abTest&&activeVariant==="B"?(step?.bodyB||""):(step?.body||"");
      if(raw){
        bodyRef.current.innerHTML=wrapPlaceholders(ensureBodyHtml(raw));
      } else {
        bodyRef.current.innerHTML='<span style="color:#c0d4c8;font-style:italic">Start writing your email...</span>';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[activeStep,editing,activeVariant]);

  // Pull A/B metrics for the currently-edited step from email_queue + activity_log.
  // For each sent variant, counts unique creators who later received any reply
  // (heuristic: reply ts > sent ts of that creator's variant assignment).
  useEffect(()=>{
    if(!editing||!brandId)return;
    const flow=flows.find(f=>f.id===editing);
    if(!flow)return;
    const step=flow.steps[activeStep];
    if(!step?.abTest||!step?.id)return;
    if(variantStats[step.id])return; // already loaded
    let cancelled=false;
    (async()=>{
      setLoadingVariantStats(true);
      try{
        const{data:sent}=await supabase.from('email_queue')
          .select('variant, creator_handle, sent_at')
          .eq('brand_id',brandId)
          .eq('flow_id',flow.id)
          .eq('step_index',activeStep)
          .eq('status','sent')
          .not('variant','is',null);
        if(cancelled)return;
        const sentList=sent||[];
        const handles=[...new Set(sentList.map(r=>r.creator_handle))];
        let replies=[];
        if(handles.length>0){
          const{data:rs}=await supabase.from('activity_log')
            .select('creator_handle, ts')
            .eq('brand_id',brandId)
            .in('creator_handle',handles)
            .eq('type','email_received');
          if(cancelled)return;
          replies=rs||[];
        }
        const firstReplyByHandle=new Map();
        for(const r of replies){
          const t=new Date(r.ts).getTime();
          const cur=firstReplyByHandle.get(r.creator_handle);
          if(cur==null||t<cur) firstReplyByHandle.set(r.creator_handle,t);
        }
        const stats={A:{sent:0,replies:0,rate:0},B:{sent:0,replies:0,rate:0}};
        const byHandleVariant=new Map(); // dedupe — count each handle's earliest sent_at per variant
        for(const r of sentList){
          if(r.variant!=='A'&&r.variant!=='B')continue;
          const key=`${r.variant}::${r.creator_handle}`;
          const sentMs=r.sent_at?new Date(r.sent_at).getTime():Date.now();
          const cur=byHandleVariant.get(key);
          if(cur==null||sentMs<cur) byHandleVariant.set(key,sentMs);
        }
        for(const[key,sentMs] of byHandleVariant.entries()){
          const[variant,handle]=key.split('::');
          stats[variant].sent++;
          const firstReply=firstReplyByHandle.get(handle);
          if(firstReply!=null&&firstReply>sentMs) stats[variant].replies++;
        }
        for(const v of['A','B']) stats[v].rate=stats[v].sent>0?stats[v].replies/stats[v].sent:0;
        if(!cancelled) setVariantStats(prev=>({...prev,[step.id]:stats}));
      }catch(e){console.error('variant stats',e);}
      finally{if(!cancelled)setLoadingVariantStats(false);}
    })();
    return()=>{cancelled=true;};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[editing,activeStep,brandId]);

  // A/B helpers
  const toggleAB=(stepIdx)=>setFlows(prev=>prev.map(f=>f.id===editing?{...f,steps:f.steps.map((s,si)=>si===stepIdx?{...s,abTest:!s.abTest,subjectB:s.subjectB||s.subject,bodyB:s.bodyB||s.body,split:s.split||50}:s)}:f));
  const setSplit=(stepIdx,val)=>setFlows(prev=>prev.map(f=>f.id===editing?{...f,steps:f.steps.map((s,si)=>si===stepIdx?{...s,split:val}:s)}:f));
  const declareWinner=async(stepIdx,winner)=>{
    const flow=flows.find(f=>f.id===editing);
    const step=flow?.steps?.[stepIdx];
    const stats=step?.id?variantStats[step.id]:null;
    // Build a smart confirm message based on data quality.
    let title=`Lock in Variant ${winner}`;
    let body=`This replaces Step ${stepIdx+1}'s subject + body with Variant ${winner} and removes the A/B test — all future sends use Variant ${winner} only.`;
    let destructive=false;
    if(stats){
      const ev=evaluateAB(stats);
      if(!ev.sampleMet){
        const needs=[];
        if(ev.moreNeeded.A>0) needs.push(`A needs ${ev.moreNeeded.A} more`);
        if(ev.moreNeeded.B>0) needs.push(`B needs ${ev.moreNeeded.B} more`);
        title=`⚠ Early winner — Variant ${winner}`;
        body=`You've only sent ${stats.A.sent} (A) / ${stats.B.sent} (B). Recommended: at least ${MIN_AB_SAMPLE_PER_VARIANT} per variant before declaring (${needs.join(", ")}).\n\nAt this sample size, the reply rates you're seeing are mostly noise. Variant ${winner} might just be lucky. Lock in anyway?`;
        destructive=true;
      } else if(!ev.significant){
        title=`⚠ Not statistically significant`;
        body=`Sample sizes are large enough (A: ${stats.A.sent}, B: ${stats.B.sent}) but the difference between ${(stats.A.rate*100).toFixed(1)}% and ${(stats.B.rate*100).toFixed(1)}% is within margin of error (95% confidence not reached).\n\nVariant ${winner} might not actually be better — pick a winner only if you have a non-data reason. Lock in Variant ${winner} anyway?`;
        destructive=true;
      } else if(ev.winner!==winner){
        title=`⚠ Picking the loser`;
        body=`The data says Variant ${ev.winner} is winning at ${ev.confidence} confidence (${(stats[ev.winner].rate*100).toFixed(1)}% vs ${(stats[winner].rate*100).toFixed(1)}%), but you're choosing Variant ${winner}. Lock in anyway?`;
        destructive=true;
      } else {
        title=`Lock in winner — Variant ${winner}`;
        body=`Variant ${winner} is the data-backed winner (${ev.confidence} confidence, ${(stats[winner].rate*100).toFixed(1)}% reply rate vs ${(stats[winner==='A'?'B':'A'].rate*100).toFixed(1)}%).`;
      }
    } else {
      title=`⚠ No data yet`;
      body=`No sends have been recorded for this A/B test. You're picking based on gut feel only. Lock in Variant ${winner}?`;
      destructive=true;
    }
    if(!(await confirmDialog({title,body,destructive,confirmLabel:`Lock in ${winner}`})))return;
    setFlows(prev=>prev.map(f=>f.id===editing?{...f,steps:f.steps.map((s,si)=>si===stepIdx?{...s,abTest:false,subject:winner==="A"?s.subject:s.subjectB,body:winner==="A"?s.body:s.bodyB}:s)}:f));
  };

  if(editing){
    const flow=flows.find(f=>f.id===editing);
    // Defensive: if the editing id doesn't match any flow (e.g. flow was just deleted
    // or state hasn't propagated yet), bail back to the list instead of crashing.
    if(!flow){ return(<div style={{padding:24,textAlign:"center",color:MUTED}}><div>This flow no longer exists.</div><button onClick={()=>setEditing(null)} style={{marginTop:12,background:DARK,color:G,border:"none",borderRadius:8,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>Back to flows</button></div>); }
    const step=flow.steps[activeStep];
    return(<div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 106px)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <button onClick={()=>{setEditing(null);setActiveStep(0);}} style={{display:"flex",alignItems:"center",gap:6,border:"none",background:"none",cursor:"pointer",color:"#9ab8a6",fontSize:13,fontWeight:500,padding:0}}><Ico d="M10 19l-7-7m0 0l7-7m-7 7h18" size={15}/> Back</button>
          <div style={{fontWeight:700,color:DARK,fontSize:16}}>Editing: {flow.name}</div>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          {renderSaveStatus()}
          <button onClick={async()=>{
            if(!(await confirmDialog({title:`Delete flow "${flow.name}"?`,body:"Creators currently assigned will lose their flow.",destructive:true,confirmLabel:"Delete"})))return;
            logSecurityEvent(brandId,'flow.delete','flow',editing,{name:flow.name,type:flow.type,steps:flow.steps?.length||0});
            setFlows(prev=>prev.filter(f=>f.id!==editing));
            setEditing(null);setActiveStep(0);
            toast(`Flow "${flow.name}" deleted`);
          }} style={{border:"1px solid #FECACA",background:"#fff",color:"#DC2626",borderRadius:9,padding:"8px 14px",fontSize:12,cursor:"pointer",fontWeight:600}}>Delete flow</button>
        </div>
      </div>
      {/* Editor tab bar — Steps vs Settings. Splits the historically dense
          sidebar (name + type + active + reply-pause + PDF + step list, all
          stacked) into two focused screens so the operator sees only what
          they need for the current task. */}
      <div style={{display:"flex",gap:4,borderBottom:`1px solid ${BORDER}`,marginBottom:14}}>
        {[
          {key:'steps',label:'Steps',hint:'Edit the email sequence'},
          {key:'settings',label:'Settings',hint:'Type, reply pause, PDF attachment'},
        ].map(t=>(
          <button key={t.key} onClick={()=>setEditorSidebar(t.key)} title={t.hint}
            style={{
              background:"transparent",border:"none",
              padding:"9px 16px",fontSize:13,fontWeight:editorSidebar===t.key?700:500,
              color:editorSidebar===t.key?DARK:MUTED,cursor:"pointer",
              borderBottom:editorSidebar===t.key?`2px solid ${G}`:"2px solid transparent",
              marginBottom:-1,
            }}>{t.label}</button>
        ))}
      </div>
      <div style={{display:editorSidebar==='steps'?"flex":"none",gap:16,flex:1,overflow:"hidden"}}>
        <div style={{width:290,flexShrink:0,display:"flex",flexDirection:"column",gap:10,overflowY:"auto"}}>
          {/* Sidebar in Steps mode: just the step list + add step button.
              Name, type, active, reply-pause, PDF attach all live under the
              Settings tab now — keeps this column focused on the actual
              sequence the operator is editing. */}
          {/* (settings panel moved to Settings tab below) */}
          <div style={{fontSize:10,color:"#9ab8a6",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",padding:"0 4px"}}>Steps</div>
          {flow.steps.map((s,i)=>(<div key={s.id} onClick={()=>{setActiveStep(i);setActiveVariant("A");}} style={{background:activeStep===i?"#fff":"#f8faf9",borderRadius:12,padding:14,cursor:"pointer",border:`1.5px solid ${activeStep===i?G:"transparent"}`,boxShadow:activeStep===i?CARD:"none",position:"relative"}}>
            <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:6}}>
              <div style={{width:26,height:26,borderRadius:7,background:activeStep===i?G+"20":"#e8f0eb",display:"flex",alignItems:"center",justifyContent:"center"}}><Ico d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" size={13} color={activeStep===i?G:"#9ab8a6"}/></div>
              <div style={{fontWeight:600,fontSize:13,color:DARK,flex:1}}>Step {i+1}</div>
              {s.abTest&&<span title="This step is running a subject/body variant test (A vs B)" style={{background:"#8B5CF6",color:"#fff",borderRadius:5,padding:"1px 6px",fontSize:9,fontWeight:800}}>VARIANT</span>}
              <button onClick={async e=>{
                e.stopPropagation();
                if(flow.steps.length<=1){toast("Flow must have at least one step","error");return;}
                if(!(await confirmDialog({title:`Delete step ${i+1}?`,body:"The step's email won't fire for anyone in this flow.",destructive:true,confirmLabel:"Delete step"})))return;
                setFlows(prev=>prev.map(f=>f.id===editing?{...f,steps:f.steps.filter((_,si)=>si!==i)}:f));
                if(activeStep>=i&&activeStep>0) setActiveStep(activeStep-1);
              }} title="Remove this step"
                style={{border:"none",background:"none",color:"#d1d5db",fontSize:18,cursor:"pointer",padding:"0 4px",lineHeight:1}}
                onMouseEnter={e=>e.currentTarget.style.color="#DC2626"}
                onMouseLeave={e=>e.currentTarget.style.color="#d1d5db"}>×</button>
            </div>
            <div style={{fontSize:11,color:"#9ab8a6",marginBottom:8,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.subject||"No subject"}</div>
            {i<flow.steps.length-1&&(<div style={{display:"flex",alignItems:"center",gap:8,fontSize:11,color:"#9ab8a6"}}><span>Send in</span><input value={s.days||0} onChange={e=>setFlows(prev=>prev.map(f=>f.id===editing?{...f,steps:f.steps.map((st,si)=>si===i?{...st,days:parseInt(e.target.value)||0}:st)}:f))} onClick={e=>e.stopPropagation()} style={{width:40,padding:"3px 6px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,textAlign:"center",outline:"none",background:"#fff"}}/><span>days</span></div>)}
          </div>))}
          <button onClick={()=>setFlows(prev=>prev.map(f=>f.id===editing?{...f,steps:[...f.steps,{id:Date.now(),subject:"",body:"",days:7}]}:f))} style={{background:"#f8faf9",border:"1.5px dashed #c0d4c8",borderRadius:12,padding:"11px",fontSize:13,color:"#4a7a5e",cursor:"pointer",fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",gap:6}} onMouseEnter={e=>{e.currentTarget.style.background=G+"15";e.currentTarget.style.borderColor=G;}} onMouseLeave={e=>{e.currentTarget.style.background="#f8faf9";e.currentTarget.style.borderColor="#c0d4c8";}}>+ Add email step</button>
        </div>
        <div style={{flex:1,background:"#fff",borderRadius:14,display:"flex",flexDirection:"column",overflowY:"auto",boxShadow:CARD}}>
          {/* A/B test header — Aftersell-inspired side-by-side variant cards.
              Both variants visible simultaneously; click "Edit Variant X" to
              switch the editor below (activeVariant state). Replaces the old
              tabbed toggle UI where operator only saw one variant at a time. */}
          {step?.abTest&&(()=>{
            // Strip HTML + truncate for the preview text in each card. Body is
            // stored as HTML (since the rich editor commit), so naive substring
            // would show <p>, <span>, etc.
            const previewBody = (html)=>{
              if(!html) return "(empty)";
              const text = String(html).replace(/<[^>]+>/g," ").replace(/&nbsp;/g," ").replace(/\s+/g," ").trim();
              return text.length>120 ? text.slice(0,120)+"…" : (text||"(empty)");
            };
            const stats = step?.id ? variantStats[step.id] : null;
            const splitA = step.split || 50;
            const splitB = 100 - splitA;
            return(<div style={{padding:"12px 18px 14px",borderBottom:"1px solid #f0f7f3",background:"#fafcfb"}}>
              {/* Header row — test title + split control + winner picks */}
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#7C3AED",textTransform:"uppercase",letterSpacing:".06em",display:"flex",alignItems:"center",gap:6}}>
                  <span>⚗️ Step {activeStep+1} · A/B Variant Test</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:MUTED,marginLeft:6}}>
                  <span>Split:</span>
                  <input type="number" value={splitA} min={10} max={90} onChange={e=>setSplit(activeStep,parseInt(e.target.value)||50)} style={{width:42,padding:"3px 6px",border:`1px solid ${BORDER}`,borderRadius:5,fontSize:11,textAlign:"center",outline:"none"}}/>
                  <span>% to A</span>
                </div>
                <div style={{marginLeft:"auto",display:"flex",gap:6}}>
                  <button onClick={()=>declareWinner(activeStep,"A")} title="End the test and keep Variant A as the only version" style={{background:"#F0FDF4",color:G,border:`1px solid ${G}30`,borderRadius:6,padding:"4px 10px",fontSize:10,fontWeight:700,cursor:"pointer"}}>🏆 Pick A</button>
                  <button onClick={()=>declareWinner(activeStep,"B")} title="End the test and keep Variant B as the only version" style={{background:"#F5F3FF",color:"#8B5CF6",border:"1px solid #DDD6FE",borderRadius:6,padding:"4px 10px",fontSize:10,fontWeight:700,cursor:"pointer"}}>🏆 Pick B</button>
                  <button onClick={()=>toggleAB(activeStep)} title="Remove the variant test — keeps Variant A's content, discards B" style={{background:"#fff",border:`1px solid ${BORDER}`,borderRadius:6,padding:"4px 10px",fontSize:10,color:MUTED,cursor:"pointer"}}>✕ Remove A/B</button>
                </div>
              </div>

              {/* Side-by-side variant cards */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {[
                  {v:"A",color:G,bgTint:"#F0FDF4",borderTint:G+"40",subject:step.subject||"",body:step.body||"",pct:splitA},
                  {v:"B",color:"#8B5CF6",bgTint:"#F5F3FF",borderTint:"#DDD6FE",subject:step.subjectB||"",body:step.bodyB||"",pct:splitB},
                ].map(card=>{
                  const isActive = activeVariant===card.v;
                  const vStats = stats?.[card.v];
                  const ev = stats ? evaluateAB(stats) : null;
                  const isWinner = ev?.significant && ev?.winner===card.v;
                  const isLeading = ev && !ev.significant && ev?.winner===card.v;
                  return(<div key={card.v} style={{background:"#fff",border:`2px solid ${isActive?card.color:BORDER}`,borderRadius:10,padding:"12px 14px",cursor:isActive?"default":"pointer",transition:"border-color .15s",boxShadow:isActive?"0 0 0 3px "+card.color+"18":"none"}}
                    onClick={()=>!isActive&&setActiveVariant(card.v)}
                    onMouseEnter={e=>{if(!isActive)e.currentTarget.style.borderColor=card.borderTint;}}
                    onMouseLeave={e=>{if(!isActive)e.currentTarget.style.borderColor=BORDER;}}>
                    {/* Card header — variant label + % of recipients + winner badge */}
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                      <span style={{background:card.color,color:"#fff",borderRadius:5,padding:"2px 8px",fontSize:10,fontWeight:800,letterSpacing:".05em"}}>VARIANT {card.v}</span>
                      <span style={{fontSize:11,color:MUTED,fontWeight:600}}>{card.pct}% of recipients</span>
                      {isWinner&&<span style={{background:card.color,color:"#fff",fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:4,marginLeft:"auto"}}>🏆 WINNER · {ev.confidence}</span>}
                      {isLeading&&<span style={{color:card.color,fontSize:9,fontWeight:700,marginLeft:"auto",opacity:0.8}}>leading</span>}
                    </div>
                    {/* Subject line preview */}
                    <div style={{marginBottom:8}}>
                      <div style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:3}}>Subject</div>
                      <div style={{fontSize:13,color:DARK,fontWeight:600,lineHeight:1.4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{card.subject||<span style={{color:"#c0d4c8",fontWeight:400,fontStyle:"italic"}}>(empty)</span>}</div>
                    </div>
                    {/* Body preview — stripped HTML, truncated to 120 chars */}
                    <div style={{marginBottom:10}}>
                      <div style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:3}}>Preview</div>
                      <div style={{fontSize:11.5,color:"#4b5563",lineHeight:1.5,maxHeight:50,overflow:"hidden"}}>{previewBody(card.body)}</div>
                    </div>
                    {/* Metrics row + progress to sample size */}
                    <div style={{borderTop:`1px solid ${BORDER}`,paddingTop:9}}>
                      {vStats ? (<>
                        <div style={{fontSize:11,color:DARK,marginBottom:5,display:"flex",gap:10}}>
                          <span><strong>{vStats.sent}</strong> sent</span>
                          <span><strong>{vStats.replies}</strong> repl{vStats.replies===1?"y":"ies"}</span>
                          <span style={{marginLeft:"auto"}}>
                            <strong style={{color:isWinner?card.color:DARK,fontSize:13}}>{(vStats.rate*100).toFixed(1)}%</strong>
                            <span style={{color:MUTED,fontSize:10,marginLeft:3}}>reply rate</span>
                          </span>
                        </div>
                        <div style={{height:4,background:"#f0f0f0",borderRadius:2,overflow:"hidden"}}>
                          <div style={{height:"100%",background:vStats.sent>=MIN_AB_SAMPLE_PER_VARIANT?G:"#F59E0B",width:`${Math.min(100,Math.round((vStats.sent/MIN_AB_SAMPLE_PER_VARIANT)*100))}%`,transition:"width .3s"}}/>
                        </div>
                        <div style={{fontSize:10,color:MUTED,marginTop:4}}>{vStats.sent>=MIN_AB_SAMPLE_PER_VARIANT?"Sample size ✓":`${MIN_AB_SAMPLE_PER_VARIANT-vStats.sent} more recommended (target ${MIN_AB_SAMPLE_PER_VARIANT})`}</div>
                      </>) : (
                        <div style={{fontSize:11,color:MUTED,fontStyle:"italic"}}>No sends yet — metrics appear once the worker fires this step.</div>
                      )}
                    </div>
                    {/* Edit button — clicking the card itself also switches variants, but
                        this gives an explicit affordance. */}
                    <button onClick={e=>{e.stopPropagation();setActiveVariant(card.v);}} style={{marginTop:10,width:"100%",background:isActive?card.color:"#fff",color:isActive?"#fff":card.color,border:`1px solid ${card.color}`,borderRadius:7,padding:"6px 10px",fontSize:11,fontWeight:700,cursor:isActive?"default":"pointer"}}>
                      {isActive?"✏️ Editing this variant ↓":`✏️ Edit Variant ${card.v}`}
                    </button>
                  </div>);
                })}
              </div>

              {/* Overall guidance line — explicit recommendation for the operator. */}
              {stats && (stats.A.sent+stats.B.sent)>0 && (()=>{
                const ev=evaluateAB(stats);
                return(<div style={{marginTop:10,padding:"7px 12px",borderRadius:7,background:ev.sampleMet?(ev.significant?"#F0FDF4":"#FFFBEB"):"#FFFBEB",fontSize:11,color:ev.sampleMet?(ev.significant?"#166534":"#92400E"):"#92400E",fontWeight:600}}>
                  {!ev.sampleMet&&`⏳ Keep running — recommend ${MIN_AB_SAMPLE_PER_VARIANT} sends per variant before declaring (A: ${ev.moreNeeded.A} to go, B: ${ev.moreNeeded.B} to go)`}
                  {ev.sampleMet&&!ev.significant&&`⚖️ Sample large enough but difference not statistically significant (95% confidence not reached) — keep running or pick by gut`}
                  {ev.sampleMet&&ev.significant&&`✓ Variant ${ev.winner} wins at ${ev.confidence} confidence — safe to declare`}
                </div>);
              })()}
              {!stats && loadingVariantStats && (
                <div style={{marginTop:10,fontSize:11,color:MUTED,fontStyle:"italic"}}>Loading variant metrics…</div>
              )}
            </div>);
          })()}
          {/* Header row when NOT in A/B mode — kept simple, just shows step number + add-variant button */}
          <div style={{padding:"10px 18px",borderBottom:"1px solid #f0f7f3",display:"flex",alignItems:"center",gap:10}}>
            {step?.abTest&&<>
              <div style={{fontSize:11,color:MUTED,fontWeight:500}}>Editing: <strong style={{color:activeVariant==="A"?G:"#8B5CF6"}}>Variant {activeVariant}</strong> of Step {activeStep+1}</div>
            </>}
            {!step?.abTest&&<>
              <div style={{fontSize:11,color:MUTED,fontWeight:500}}>Step {activeStep+1}</div>
              {/* Distinguished from the SIDEBAR "Flow split" card — that splits NEW
                  creators across multiple FLOWS of the same type. This one tests
                  two different subject/body variants within THIS step. Both can be
                  active at once; they're orthogonal. */}
              <button onClick={()=>{
                if(flow.is_active===false){toast("Flow is paused — activate it first to run variant tests","error");return;}
                toggleAB(activeStep);setActiveVariant("A");
              }} title={flow.is_active===false?"Flow is paused — activate to run variant tests":"Test two different subject lines / body copy on this step. Different from the Flow split on the left (which routes across multiple flows)."} style={{marginLeft:"auto",background:flow.is_active===false?"#f3f4f6":"#F5F3FF",color:flow.is_active===false?MUTED:"#8B5CF6",border:`1px solid ${flow.is_active===false?BORDER:"#DDD6FE"}`,borderRadius:7,padding:"4px 12px",fontSize:11,fontWeight:700,cursor:flow.is_active===false?"not-allowed":"pointer",opacity:flow.is_active===false?0.6:1}}>⚗️ Add subject/body variant</button>
            </>}
          </div>
          {/* (Old A/B metrics bar removed — per-variant metrics now live inside each
              card in the new side-by-side layout above.) */}
          {/* Subject */}
          <div style={{padding:"13px 18px",borderBottom:"1px solid #f0f7f3",display:"flex",alignItems:"center",gap:12}}>
            <div style={{fontSize:11,color:"#9ab8a6",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",flexShrink:0}}>{step?.abTest?`Subject ${activeVariant}`:"Subject"}</div>
            <input
              value={step?.abTest&&activeVariant==="B"?(step.subjectB||""):(step?.subject||"")}
              onChange={e=>{
                if(step?.abTest&&activeVariant==="B") setFlows(prev=>prev.map(f=>f.id===editing?{...f,steps:f.steps.map((s,si)=>si===activeStep?{...s,subjectB:e.target.value}:s)}:f));
                else setFlows(prev=>prev.map(f=>f.id===editing?{...f,steps:f.steps.map((s,si)=>si===activeStep?{...s,subject:e.target.value}:s)}:f));
              }}
              style={{flex:1,border:"none",fontSize:14,fontWeight:600,color:DARK,outline:"none"}}
              placeholder={step?.abTest&&activeVariant==="B"?"Variant B subject line...":"Enter subject line..."}
            />
          </div>
          {/* Unknown-variable warning. Scans subject + body for {{...}} placeholders
              that aren't in the supported set, so a typo like {{firstName}} (capital N)
              gets flagged instead of going out literally in the email. The supported
              set mirrors replaceVars() in SendEmailModal — keep them in sync. */}
          {(()=>{
            // Single source of truth — same list the interpolator resolves.
            const KNOWN_VARS = new Set(EMAIL_TEMPLATE_VARS);
            const subjectVal = step?.abTest && activeVariant === "B" ? (step.subjectB || "") : (step?.subject || "");
            const bodyVal = step?.abTest && activeVariant === "B" ? (step.bodyB || "") : (step?.body || "");
            const matches = [...(subjectVal + " " + bodyVal).matchAll(/{{\s*([a-zA-Z0-9_]+)(\|[^}]+)?\s*}}/g)];
            const unknown = [...new Set(matches.map(m => m[1].toLowerCase()).filter(v => !KNOWN_VARS.has(v)))];
            if (unknown.length === 0) return null;
            return(<div style={{padding:"7px 18px",background:"#FEF3C7",borderBottom:"1px solid #FDE68A",fontSize:11,color:"#92400E",lineHeight:1.4}}>
              ⚠ Unknown variable{unknown.length===1?"":"s"}: {unknown.map(v=>`{{${v}}}`).join(", ")} — will be left blank when the email is sent. Fix the spelling (case-sensitive: {`{{firstname}}`}, not {`{{firstName}}`}) or remove the placeholder.
            </div>);
          })()}
          {/* Toolbar — Google-Docs-style formatting. Built on contentEditable execCommand.
              Saved into body as HTML (multipart/alternative in the worker → recipient sees
              actual formatting, not <b>raw markup</b>). Two rows so we don't crowd. */}
          <div style={{padding:"7px 14px 0 14px",borderBottom:"none",display:"flex",gap:2,alignItems:"center",flexWrap:"wrap"}}>
            {/* Paragraph style: paragraph / H1 / H2 / H3 / blockquote */}
            <select defaultValue="" onMouseDown={e=>e.stopPropagation()} onChange={e=>{const v=e.target.value;if(!v)return;if(v==="blockquote"){execFmt("formatBlock","BLOCKQUOTE");}else{execFmt("formatBlock",v);}e.target.value="";}}
              title="Paragraph style"
              style={{padding:"4px 8px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,color:"#374151",background:"#fff",outline:"none",cursor:"pointer"}}>
              <option value="">Style</option>
              <option value="P">Paragraph</option>
              <option value="H1">Heading 1</option>
              <option value="H2">Heading 2</option>
              <option value="H3">Heading 3</option>
              <option value="blockquote">Quote block</option>
            </select>
            {/* Font family */}
            <select defaultValue="" onChange={e=>{const v=e.target.value;if(!v)return;execFmt("fontName",v);e.target.value="";}}
              title="Font family"
              style={{padding:"4px 8px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,color:"#374151",background:"#fff",outline:"none",cursor:"pointer",marginLeft:4}}>
              <option value="">Font</option>
              <option value="Arial, sans-serif" style={{fontFamily:"Arial,sans-serif"}}>Sans (default)</option>
              <option value="Georgia, serif" style={{fontFamily:"Georgia,serif"}}>Georgia</option>
              <option value="'Times New Roman', serif" style={{fontFamily:"'Times New Roman',serif"}}>Times</option>
              <option value="'Helvetica Neue', Helvetica, sans-serif" style={{fontFamily:"Helvetica,sans-serif"}}>Helvetica</option>
              <option value="Verdana, sans-serif" style={{fontFamily:"Verdana,sans-serif"}}>Verdana</option>
              <option value="'Courier New', monospace" style={{fontFamily:"'Courier New',monospace"}}>Courier</option>
            </select>
            {/* Font size — execCommand fontSize takes 1-7 in HTML legacy units. */}
            <select defaultValue="" onChange={e=>{const v=e.target.value;if(!v)return;execFmt("fontSize",v);e.target.value="";}}
              title="Font size"
              style={{padding:"4px 8px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,color:"#374151",background:"#fff",outline:"none",cursor:"pointer",marginLeft:4}}>
              <option value="">Size</option>
              <option value="2">Small (10px)</option>
              <option value="3">Normal (13px)</option>
              <option value="4">Large (16px)</option>
              <option value="5">XL (18px)</option>
              <option value="6">2XL (24px)</option>
              <option value="7">3XL (32px)</option>
            </select>
            <div style={{flex:1}}/>
            {/* Clear formatting on the far right of row 1 */}
            <button title="Clear formatting" onMouseDown={e=>{e.preventDefault();execFmt("removeFormat");execFmt("formatBlock","P");}}
              style={{border:"none",background:"none",padding:"5px 9px",borderRadius:6,cursor:"pointer",fontSize:13,color:"#374151"}}
              onMouseEnter={e=>e.currentTarget.style.background="#f8faf9"} onMouseLeave={e=>e.currentTarget.style.background="none"}>✕ Clear</button>
          </div>
          <div style={{padding:"5px 14px 8px 14px",borderBottom:"1px solid #f0f7f3",display:"flex",gap:2,alignItems:"center",flexWrap:"wrap"}}>
            {/* B / I / U / S */}
            {[{label:"B",cmd:"bold",title:"Bold (Ctrl+B)",style:{fontWeight:700}},{label:"I",cmd:"italic",title:"Italic (Ctrl+I)",style:{fontStyle:"italic"}},{label:"U",cmd:"underline",title:"Underline",style:{textDecoration:"underline"}},{label:"S",cmd:"strikeThrough",title:"Strikethrough",style:{textDecoration:"line-through"}}].map(t=>(<button key={t.cmd} title={t.title} onMouseDown={e=>{e.preventDefault();execFmt(t.cmd);}} style={{border:"none",background:"none",padding:"5px 9px",borderRadius:6,cursor:"pointer",fontSize:13,color:"#374151",...t.style}} onMouseEnter={e=>e.currentTarget.style.background="#f8faf9"} onMouseLeave={e=>e.currentTarget.style.background="none"}>{t.label}</button>))}
            <div style={{width:1,height:18,background:"#e4ede7",margin:"0 6px"}}/>
            {/* Alignment */}
            {[{cmd:"justifyLeft",label:"⇤",title:"Align left"},{cmd:"justifyCenter",label:"≡",title:"Center"},{cmd:"justifyRight",label:"⇥",title:"Align right"}].map(t=>(<button key={t.cmd} title={t.title} onMouseDown={e=>{e.preventDefault();execFmt(t.cmd);}} style={{border:"none",background:"none",padding:"5px 9px",borderRadius:6,cursor:"pointer",fontSize:14,color:"#374151"}} onMouseEnter={e=>e.currentTarget.style.background="#f8faf9"} onMouseLeave={e=>e.currentTarget.style.background="none"}>{t.label}</button>))}
            <div style={{width:1,height:18,background:"#e4ede7",margin:"0 6px"}}/>
            {/* Lists + indent */}
            {[{label:"•",cmd:"insertUnorderedList",title:"Bullet list"},{label:"1.",cmd:"insertOrderedList",title:"Numbered list"},{label:"⇥",cmd:"indent",title:"Indent"},{label:"⇤",cmd:"outdent",title:"Outdent"}].map(t=>(<button key={t.cmd} title={t.title} onMouseDown={e=>{e.preventDefault();execFmt(t.cmd);}} style={{border:"none",background:"none",padding:"5px 9px",borderRadius:6,cursor:"pointer",fontSize:13,color:"#374151"}} onMouseEnter={e=>e.currentTarget.style.background="#f8faf9"} onMouseLeave={e=>e.currentTarget.style.background="none"}>{t.label}</button>))}
            <div style={{width:1,height:18,background:"#e4ede7",margin:"0 6px"}}/>
            {/* Text color — expanded palette + custom picker */}
            <span style={{fontSize:11,color:MUTED,fontWeight:700,letterSpacing:".03em",marginRight:3}}>A</span>
            {["#1a1a1a","#6B7280","#DC2626","#EA580C","#F59E0B","#10B981","#0EA5E9","#3B82F6","#8B5CF6","#EC4899"].map(c=>(
              <button key={`fg-${c}`} title={`Text color ${c}`} onMouseDown={e=>{e.preventDefault();execFmt("foreColor",c);}}
                style={{border:`1px solid ${BORDER}`,background:c,width:16,height:16,borderRadius:3,cursor:"pointer",padding:0,margin:"0 1px"}}/>
            ))}
            <input type="color" title="Custom text color" onChange={e=>{execFmt("foreColor",e.target.value);}}
              style={{width:20,height:20,padding:0,border:`1px solid ${BORDER}`,borderRadius:4,cursor:"pointer",margin:"0 2px",background:"#fff"}}/>
            <div style={{width:1,height:18,background:"#e4ede7",margin:"0 6px"}}/>
            {/* Highlight — expanded swatches + clear */}
            <span style={{fontSize:11,marginRight:3}}>🖍</span>
            {["transparent","#FEF3C7","#FED7AA","#FECACA","#D1FAE5","#A7F3D0","#BFDBFE","#DBEAFE","#DDD6FE","#FCE7F3"].map(c=>(
              <button key={`bg-${c}`} title={c==="transparent"?"Clear highlight":`Highlight ${c}`} onMouseDown={e=>{e.preventDefault();execFmt("hiliteColor",c);}}
                style={{border:`1px solid ${BORDER}`,background:c==="transparent"?"#fff":c,width:16,height:16,borderRadius:3,cursor:"pointer",padding:0,margin:"0 1px",position:"relative"}}>
                {c==="transparent"&&<span style={{position:"absolute",top:0,left:4,color:"#DC2626",fontSize:11,lineHeight:1}}>∅</span>}
              </button>
            ))}
            <div style={{width:1,height:18,background:"#e4ede7",margin:"0 6px"}}/>
            {/* Link + horizontal rule */}
            {/* Insert link — capture the contentEditable selection on mousedown
                BEFORE the modal opens (focus shifts to the modal input, so the
                selection would otherwise be lost). On submit, restore the saved
                Range and run execCommand against it. */}
            <button title="Insert link" onMouseDown={async e=>{
              e.preventDefault();
              const sel = window.getSelection();
              const savedRange = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
              const u = await promptDialog({title:"Insert link", label:"URL", placeholder:"https://example.com", confirmLabel:"Insert"});
              if(!u) return;
              if(savedRange){
                bodyRef.current?.focus();
                const s = window.getSelection();
                s.removeAllRanges();
                s.addRange(savedRange);
              }
              const url=/^https?:\/\//i.test(u)?u:`https://${u}`;
              execFmt("createLink",url);
            }}
              style={{border:"none",background:"none",padding:"5px 9px",borderRadius:6,cursor:"pointer",fontSize:13,color:"#374151"}}
              onMouseEnter={e=>e.currentTarget.style.background="#f8faf9"} onMouseLeave={e=>e.currentTarget.style.background="none"}>🔗</button>
            <button title="Remove link" onMouseDown={e=>{e.preventDefault();execFmt("unlink");}}
              style={{border:"none",background:"none",padding:"5px 9px",borderRadius:6,cursor:"pointer",fontSize:13,color:"#374151"}}
              onMouseEnter={e=>e.currentTarget.style.background="#f8faf9"} onMouseLeave={e=>e.currentTarget.style.background="none"}>⛔</button>
            <button title="Horizontal divider" onMouseDown={e=>{e.preventDefault();execFmt("insertHorizontalRule");}}
              style={{border:"none",background:"none",padding:"5px 9px",borderRadius:6,cursor:"pointer",fontSize:13,color:"#374151"}}
              onMouseEnter={e=>e.currentTarget.style.background="#f8faf9"} onMouseLeave={e=>e.currentTarget.style.background="none"}>―</button>
          </div>
          {/* Body — key changes when switching variant to force re-render */}
          <div key={`body-${activeStep}-${activeVariant}`} ref={bodyRef} contentEditable suppressContentEditableWarning
            onInput={e=>{
              // Save innerHTML so formatting (bold, color, links, etc.) survives. Strip
              // the placeholder-highlight chrome spans so {{name}} stays template-friendly
              // for the worker's interpolate() call. Previously this saved innerText,
              // which silently nuked every formatting tool in the toolbar.
              const html=stripPlaceholderChrome(e.currentTarget?.innerHTML||"");
              if(step?.abTest&&activeVariant==="B") setFlows(prev=>prev.map(f=>f.id===editing?{...f,steps:f.steps.map((s,si)=>si===activeStep?{...s,bodyB:html}:s)}:f));
              else setFlows(prev=>prev.map(f=>f.id===editing?{...f,steps:f.steps.map((s,si)=>si===activeStep?{...s,body:html}:s)}:f));
            }}
            style={{minHeight:340,padding:"20px 22px",outline:"none",fontSize:13,color:"#374151",lineHeight:1.8,background:step?.abTest&&activeVariant==="B"?"#FDFAFF":"#fff"}}
          />
        </div>
      </div>
      {/* Settings tab — full-width centered panel. Holds everything that used
          to crowd the Steps sidebar: flow name, type (now with one-line hint
          per option), active toggle, reply pause window, PDF attachment.
          Flow-level A/B split UI was removed entirely; if operators want
          A/B testing, the step-level variant flow handles it. */}
      <div style={{display:editorSidebar==='settings'?"block":"none",flex:1,overflowY:"auto",paddingTop:4}}>
        <div style={{maxWidth:640,margin:"0 auto",display:"flex",flexDirection:"column",gap:18}}>
          {/* Name */}
          <Card style={{padding:18}}>
            <label style={{fontSize:10,color:"#9ab8a6",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",display:"block",marginBottom:6}}>Flow name</label>
            <input value={flow.name} onChange={e=>setFlows(prev=>prev.map(f=>f.id===editing?{...f,name:e.target.value}:f))}
              style={{width:"100%",padding:"10px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:14,outline:"none",boxSizing:"border-box"}}/>
          </Card>

          {/* Type + active toggle */}
          <Card style={{padding:18}}>
            <label style={{fontSize:10,color:"#9ab8a6",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",display:"block",marginBottom:8}}>Type</label>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[
                {key:"Outreach", hint:"Cold email to new creators in your pipeline."},
                {key:"Fulfillment", hint:"After agreement signed — order confirmation, shipping, content deadlines."},
                {key:"Post-content", hint:"After they post — thank-you, repost permission, next-collab pitch."},
              ].map(t=>{
                const selected = flow.type===t.key;
                return(<label key={t.key} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",border:`1.5px solid ${selected?G:BORDER}`,borderRadius:9,cursor:"pointer",background:selected?G+"08":"#fff"}}>
                  <input type="radio" name="flow-type" checked={selected}
                    onChange={()=>setFlows(prev=>prev.map(f=>f.id===editing?{...f,type:t.key}:f))}
                    style={{marginTop:2,accentColor:G,cursor:"pointer"}}/>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:13,color:DARK}}>{t.key}</div>
                    <div style={{fontSize:11,color:MUTED,marginTop:2,lineHeight:1.5}}>{t.hint}</div>
                  </div>
                </label>);
              })}
            </div>
            <div style={{borderTop:`1px solid ${BORDER}`,marginTop:14,paddingTop:14}}>
              <label style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:13,color:DARK,cursor:"pointer"}}>
                <div>
                  <div style={{fontWeight:600}}>{flow.is_active!==false?"Active":"Paused"}</div>
                  <div style={{fontSize:11,color:MUTED,marginTop:2,lineHeight:1.4}}>
                    {flow.is_active!==false
                      ? `New ${(flow.type||'matching').toLowerCase()} creators get assigned to this flow automatically.`
                      : `Creators already on this flow keep their assignment; new creators skip it.`}
                  </div>
                </div>
                <input type="checkbox" checked={flow.is_active!==false}
                  onChange={e=>setFlows(prev=>prev.map(f=>f.id===editing?{...f,is_active:e.target.checked}:f))}
                  style={{accentColor:G,width:18,height:18,cursor:"pointer",flexShrink:0,marginLeft:14}}/>
              </label>
            </div>
          </Card>

          {/* Reply pause */}
          {(()=>{
            const days = (typeof flow.smart_pause_days === 'number' && flow.smart_pause_days >= 1 && flow.smart_pause_days <= 30) ? flow.smart_pause_days : 5;
            return(<Card style={{padding:18}}>
              <label style={{fontSize:10,color:"#9ab8a6",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",display:"block",marginBottom:6}}>Reply pause</label>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:14}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,color:DARK,fontWeight:600,marginBottom:2}}>Wait after a positive / neutral reply</div>
                  <div style={{fontSize:11,color:MUTED,lineHeight:1.5}}>If a creator replies, we pause this many days before the next follow-up fires. Lets the human conversation breathe before automation jumps back in.</div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                  <input type="number" min={1} max={30} value={days}
                    onChange={e=>{
                      const n=parseInt(e.target.value,10);
                      if(isNaN(n))return;
                      const clamped=Math.max(1,Math.min(30,n));
                      setFlows(prev=>prev.map(f=>f.id===editing?{...f,smart_pause_days:clamped}:f));
                    }}
                    style={{width:62,padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:14,outline:"none",textAlign:"center",fontWeight:600}}/>
                  <span style={{fontSize:13,color:MUTED,fontWeight:600}}>days</span>
                </div>
              </div>
            </Card>);
          })()}

          {/* PDF attachment */}
          <Card style={{padding:18}}>
            <label style={{fontSize:10,color:"#9ab8a6",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",display:"block",marginBottom:8}}>📎 PDF attachment</label>
            {flow.attach_pdf_drive_file_id
              ?<div style={{background:"#F0FDF4",border:`1.5px solid ${G}40`,borderRadius:9,padding:"12px 14px",display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:34,height:34,borderRadius:7,background:"#FEE2E2",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0,color:"#DC2626"}}>📕</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:DARK,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{flow.attach_pdf_filename||"PDF attached"}</div>
                  <div style={{fontSize:11,color:G,fontWeight:600,marginTop:2}}>Attaches on step 1 of this {String(flow.type||'').toLowerCase()} flow</div>
                </div>
                <button onClick={()=>openPdfPicker(flow.id)} title="Replace PDF" style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:6,padding:"5px 11px",fontSize:12,cursor:"pointer",color:DARK,fontWeight:600,flexShrink:0}}>Change</button>
                <button onClick={()=>setFlows(prev=>prev.map(f=>f.id===editing?{...f,attach_pdf_drive_file_id:null,attach_pdf_filename:null}:f))} title="Remove PDF" style={{border:"none",background:"none",color:"#9ca3af",fontSize:18,cursor:"pointer",padding:"0 6px",lineHeight:1,flexShrink:0}}
                  onMouseEnter={e=>e.currentTarget.style.color="#DC2626"}
                  onMouseLeave={e=>e.currentTarget.style.color="#9ca3af"}>×</button>
              </div>
              :<button onClick={()=>openPdfPicker(flow.id)} style={{width:"100%",border:`1.5px dashed #c0d4c8`,background:"#f8faf9",borderRadius:9,padding:"16px 14px",cursor:"pointer",color:"#4a7a5e",fontWeight:600,fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=G;e.currentTarget.style.color=G;e.currentTarget.style.background=G+"08";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="#c0d4c8";e.currentTarget.style.color="#4a7a5e";e.currentTarget.style.background="#f8faf9";}}>
                <span style={{fontSize:18}}>＋</span> Attach PDF file
              </button>
            }
            <div style={{fontSize:11,color:MUTED,marginTop:10,lineHeight:1.55}}>
              Optional. Attaches once on the first email of this flow. Common uses: brand brief, product info, content guidelines. <strong style={{color:"#92400E"}}>Max 25 MB</strong> — Gmail rejects larger files.
            </div>
          </Card>
        </div>
      </div>
    </div>);
  }

  return(<div style={{display:"flex",flexDirection:"column",gap:16}}>
    {showCreate&&(<Modal title="Create new flow" subtitle="Start from scratch or choose a template." onClose={()=>setShowCreate(false)} width={580}>
      {createStep==="pick"&&(<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <button onClick={()=>setCreateStep("scratch")} style={{background:"#f8faf9",border:`1.5px solid ${BORDER}`,borderRadius:12,padding:"16px",textAlign:"left",cursor:"pointer"}}><div style={{fontSize:22,marginBottom:6}}>✏️</div><div style={{fontWeight:700,color:DARK,fontSize:13,marginBottom:4}}>Start from scratch</div><div style={{fontSize:11,color:"#9ab8a6"}}>Write your own subject lines and body copy</div></button>
        <button onClick={()=>setCreateStep("templates")} style={{background:G+"10",border:`1.5px solid ${G}50`,borderRadius:12,padding:"16px",textAlign:"left",cursor:"pointer"}}><div style={{fontSize:22,marginBottom:6}}>📋</div><div style={{fontWeight:700,color:DARK,fontSize:13,marginBottom:4}}>Choose a template</div><div style={{fontSize:11,color:"#9ab8a6"}}>Pre-written flows — customize with your brand</div></button>
      </div>)}
      {createStep==="scratch"&&(<>
        <div style={{marginBottom:14}}><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Flow name</div><input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="e.g. Outreach — Triathlon Creators" style={{width:"100%",padding:"11px 14px",border:`1px solid ${BORDER}`,borderRadius:10,fontSize:14,outline:"none",boxSizing:"border-box"}}/></div>
        <div style={{marginBottom:24}}><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Flow type</div><div style={{display:"flex",gap:8}}>{["Outreach","Fulfillment","Post-content"].map(t=>(<button key={t} onClick={()=>setNewType(t)} style={{flex:1,padding:"10px",border:`1.5px solid ${newType===t?G:BORDER}`,borderRadius:10,background:newType===t?G+"15":"#fff",color:newType===t?DARK:"#9ab8a6",fontWeight:newType===t?700:400,fontSize:12,cursor:"pointer"}}>{t}</button>))}</div></div>
        <div style={{display:"flex",justifyContent:"space-between",gap:8}}>
          <button onClick={()=>setCreateStep("pick")} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 16px",fontSize:13,cursor:"pointer"}}>← Back</button>
          <div style={{display:"flex",gap:8}}><button onClick={()=>setShowCreate(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button><button onClick={()=>{
            if(!newName.trim())return;
            // Seed a from-scratch flow with 3 steps (initial + 2 follow-ups) so the editor
            // opens with structure instead of one empty box. User edits content.
            const nf={id:crypto.randomUUID(),name:newName,type:newType,steps:[
              {id:1,subject:"Subject line — edit me",body:"Hi {{firstname}},\n\nWrite your opening message here.\n\nThanks,\n{{sender_name}}",days:0},
              {id:2,subject:"RE: Subject line — edit me",body:"Hi {{firstname}},\n\nFollow-up here.\n\n{{sender_name}}",days:3},
              {id:3,subject:"RE: Subject line — edit me",body:"Hi {{firstname}},\n\nLast follow-up.\n\n{{sender_name}}",days:7},
            ],used:0,reply:"—",is_active:true,split_weight:100,smart_pause_days:5};
            setFlows([...flows,nf]);setShowCreate(false);setNewName("");setCreateStep("pick");setEditing(nf.id);setActiveStep(0);toast(`Flow "${newName}" created`);
          }} style={{background:DARK,color:G,border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Create Flow</button></div>
        </div>
      </>)}
      {createStep==="templates"&&(<>
        <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>{["All","Outreach","Fulfillment","Post-content"].map(f=>(<button key={f} onClick={()=>setTplFilter(f)} style={{padding:"4px 12px",borderRadius:20,border:`1px solid ${tplFilter===f?DARK:BORDER}`,background:tplFilter===f?DARK:"#fff",color:tplFilter===f?G:"#9ab8a6",fontSize:11,fontWeight:tplFilter===f?700:400,cursor:"pointer"}}>{f}</button>))}</div>
        <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:340,overflowY:"auto",marginBottom:16}}>
          {TEMPLATE_LIBRARY.filter(t=>tplFilter==="All"||t.category===tplFilter).map(tpl=>(<div key={tpl.id} onClick={()=>setSelectedTpl(tpl)} style={{background:selectedTpl?.id===tpl.id?tpl.color+"12":"#f8faf9",border:`1.5px solid ${selectedTpl?.id===tpl.id?tpl.color:BORDER}`,borderRadius:10,padding:"12px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:tpl.color,flexShrink:0}}/>
            <div style={{flex:1}}><div style={{fontWeight:600,fontSize:13,color:DARK}}>{tpl.name}</div><div style={{fontSize:11,color:"#9ab8a6",marginTop:2}}>{tpl.description}</div></div>
            <div style={{display:"flex",gap:6,flexShrink:0}}><Badge label={tpl.category} color={tpl.category==="Fulfillment"?"#F0FDF4":tpl.category==="Outreach"?"#EFF6FF":"#FFFBEB"} text={tpl.category==="Fulfillment"?"#166534":tpl.category==="Outreach"?"#1D4ED8":"#92400E"}/><Badge label={`${tpl.steps} email${tpl.steps>1?"s":""}`} color="#F8FAF9" text="#6B7280"/></div>
            {selectedTpl?.id===tpl.id&&<div style={{color:tpl.color,fontSize:16}}>✓</div>}
          </div>))}
        </div>
        <div style={{display:"flex",justifyContent:"space-between",gap:8}}>
          <button onClick={()=>setCreateStep("pick")} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 16px",fontSize:13,cursor:"pointer"}}>← Back</button>
          <div style={{display:"flex",gap:8}}><button onClick={()=>setShowCreate(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button><button disabled={!selectedTpl} onClick={()=>{if(!selectedTpl)return;const nf={id:crypto.randomUUID(),name:selectedTpl.name,type:selectedTpl.category,steps:selectedTpl.steps_data.map((s,i)=>({id:i+1,subject:s.subject,body:s.body,days:s.days})),used:0,reply:"—"};setFlows([...flows,nf]);setShowCreate(false);setSelectedTpl(null);setCreateStep("pick");setEditing(nf.id);setActiveStep(0);toast(`Flow "${selectedTpl.name}" created`);}} style={{background:selectedTpl?DARK:"#f0f0f0",color:selectedTpl?G:"#9ab8a6",border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:selectedTpl?"pointer":"not-allowed"}}>Use this template</button></div>
        </div>
      </>)}
    </Modal>)}
    {deleteTarget&&(<Modal title="Delete Flow" subtitle="This action cannot be undone." onClose={()=>setDeleteTarget(null)} width={400}><div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:8}}><button onClick={()=>setDeleteTarget(null)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button><button onClick={()=>{const target=flows.find(f=>f.id===deleteTarget);const name=target?.name;logSecurityEvent(brandId,'flow.delete','flow',deleteTarget,{name,type:target?.type});setFlows(flows.filter(f=>f.id!==deleteTarget));setDeleteTarget(null);toast(`Flow "${name}" deleted`,"error");}} style={{background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Delete</button></div></Modal>)}
    {pdfAttachTarget&&(()=>{
      const targetFlow = flows.find(f=>f.id===pdfAttachTarget);
      const q = pdfPickerSearch.trim().toLowerCase();
      const filtered = (pdfFiles||[]).filter(f=>!q||f.name.toLowerCase().includes(q));
      return(<Modal title="📎 Attach PDF from Drive" subtitle={targetFlow?`To "${targetFlow.name}" — attaches once on step 1`:""} onClose={closePdfPicker} width={620}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {/* Search + refresh row */}
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input
              autoFocus
              value={pdfPickerSearch}
              onChange={e=>setPdfPickerSearch(e.target.value)}
              placeholder="🔍 Search files…"
              style={{flex:1,padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}
            />
            <button onClick={loadBrandPdfs} disabled={pdfFilesLoading} title="Refresh list" style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:8,padding:"9px 12px",fontSize:13,cursor:pdfFilesLoading?"wait":"pointer",color:DARK,fontWeight:600}}>↻</button>
            {pdfBrandRootUrl&&<a href={pdfBrandRootUrl} target="_blank" rel="noreferrer" style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:8,padding:"9px 12px",fontSize:11,color:DARK,fontWeight:600,textDecoration:"none",whiteSpace:"nowrap"}}>Open Drive ↗</a>}
          </div>
          {/* Results */}
          <div style={{minHeight:260,maxHeight:420,overflowY:"auto",border:`1px solid ${BORDER}`,borderRadius:9,padding:6}}>
            {pdfFilesLoading
              ?<div style={{padding:"60px 0",textAlign:"center",color:MUTED,fontSize:13}}>⏳ Loading files from Drive…</div>
              :pdfFilesError
                ?<div style={{padding:"24px 16px",textAlign:"center",color:"#991B1B",background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,fontSize:13,lineHeight:1.5,margin:6}}>{pdfFilesError}<div style={{marginTop:10}}><button onClick={loadBrandPdfs} style={{background:DARK,color:G,border:"none",borderRadius:7,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>↻ Retry</button></div></div>
                :(pdfFiles||[]).length===0
                  ?<div style={{padding:"50px 24px",textAlign:"center",color:MUTED,fontSize:13,lineHeight:1.6}}>
                    <div style={{fontSize:36,marginBottom:8}}>📂</div>
                    <div style={{fontWeight:700,color:DARK,marginBottom:6}}>No PDFs in this brand's Drive yet</div>
                    <div>Drop a PDF into the brand's Drive folder, then refresh.</div>
                    {pdfBrandRootUrl&&<div style={{marginTop:12}}><a href={pdfBrandRootUrl} target="_blank" rel="noreferrer" style={{color:G,fontWeight:600,fontSize:12}}>Open brand folder in Drive →</a></div>}
                  </div>
                  :filtered.length===0
                    ?<div style={{padding:"50px 24px",textAlign:"center",color:MUTED,fontSize:13}}>No files match "<strong style={{color:DARK}}>{pdfPickerSearch}</strong>"</div>
                    :<div style={{display:"flex",flexDirection:"column",gap:4}}>
                      {filtered.map(file=>{
                        const oversized = file.size && file.size > 25*1024*1024;
                        return(<button key={file.id} onClick={()=>!oversized&&pickPdf(file)} disabled={oversized} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",border:`1.5px solid transparent`,borderRadius:8,background:oversized?"#FEF2F2":"transparent",cursor:oversized?"not-allowed":"pointer",textAlign:"left",width:"100%",opacity:oversized?0.7:1}}
                          onMouseEnter={e=>{if(!oversized){e.currentTarget.style.background="#F0FDF4";e.currentTarget.style.borderColor=G;}}}
                          onMouseLeave={e=>{e.currentTarget.style.background=oversized?"#FEF2F2":"transparent";e.currentTarget.style.borderColor="transparent";}}>
                          <div style={{width:36,height:36,borderRadius:7,background:oversized?"#FEE2E2":"#FEE2E2",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0,color:"#DC2626"}}>📕</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:600,color:DARK,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{file.name}</div>
                            <div style={{fontSize:11,color:MUTED,marginTop:2,display:"flex",gap:8,flexWrap:"wrap"}}>
                              <span>{formatBytes(file.size)}</span>
                              {file.folder&&<span>· {file.folder}</span>}
                              {file.modifiedTime&&<span>· Updated {new Date(file.modifiedTime).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"})}</span>}
                            </div>
                            {oversized&&<div style={{fontSize:11,color:"#DC2626",fontWeight:600,marginTop:3}}>⚠ Over 25 MB — Gmail will reject</div>}
                          </div>
                          {!oversized&&<div style={{fontSize:11,fontWeight:700,color:G,flexShrink:0}}>Attach ›</div>}
                        </button>);
                      })}
                    </div>
            }
          </div>
          <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"9px 12px",fontSize:11,color:"#92400E",lineHeight:1.55}}>
            <strong>⚠ Max 25 MB</strong> — Gmail rejects bigger attachments. Files over the limit are greyed out above.
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
            <button onClick={closePdfPicker} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      </Modal>);
    })()}
    <div style={{display:"flex",justifyContent:"flex-end"}}><button onClick={()=>{setCreateStep("pick");setSelectedTpl(null);setShowCreate(true);}} style={{background:G,color:DARK,border:"none",borderRadius:10,padding:"10px 18px",fontWeight:700,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}><span style={{fontSize:18,lineHeight:1}}>+</span> Create New Flow</button></div>
    {flows.length===0
      ?<Card style={{padding:56,textAlign:"center"}}>
          <div style={{fontSize:48,marginBottom:16}}>✉️</div>
          <div style={{fontWeight:700,color:DARK,fontSize:18,marginBottom:8}}>No flows yet</div>
          <div style={{fontSize:13,color:MUTED,marginBottom:24,lineHeight:1.6,maxWidth:320,margin:"0 auto 24px"}}>Create your first outreach or fulfillment flow to automate creator communication.</div>
          <button onClick={()=>{setCreateStep("pick");setSelectedTpl(null);setShowCreate(true);}} style={{background:DARK,color:G,border:"none",borderRadius:10,padding:"11px 24px",fontWeight:700,fontSize:13,cursor:"pointer"}}>+ Create first flow</button>
        </Card>
      :<Card>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:"1px solid #f0f7f3"}}>{["Flow name","Type","Steps","Duration",""].map(h=>(<th key={h} style={{padding:"12px 16px",textAlign:"left",fontSize:10,color:"#9ab8a6",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em"}}>{h}</th>))}</tr></thead>
        <tbody>{flows.map((f,i)=>(<tr key={i} style={{borderBottom:"1px solid #f8faf9",cursor:"pointer"}} onClick={()=>{setEditing(f.id);setActiveStep(0);}} onMouseEnter={e=>e.currentTarget.style.background="#fafcfb"} onMouseLeave={e=>{e.currentTarget.style.background="";}}>
          <td style={{padding:"14px 16px",fontWeight:600,color:DARK}}>{f.name}</td>
          <td style={{padding:"14px 16px"}}><Badge label={f.type} color={f.type==="Fulfillment"?"#F0FDF4":f.type==="Outreach"?"#EFF6FF":"#FFFBEB"} text={f.type==="Fulfillment"?"#166534":f.type==="Outreach"?"#1D4ED8":"#92400E"}/></td>
          <td style={{padding:"14px 16px",color:DARK,fontWeight:500}}>{f.steps.length} emails</td>
          <td style={{padding:"14px 16px",color:"#6b9e80"}}>{f.steps.length>1?`${f.steps[f.steps.length-1].days} days`:"Instant"}</td>
          <td style={{padding:"14px 16px",position:"relative"}} onClick={e=>e.stopPropagation()}>
            <button onClick={()=>setMenuOpen(menuOpen===f.id?null:f.id)} style={{border:"none",background:"none",cursor:"pointer",fontSize:18,color:"#9ab8a6",padding:"4px 8px",borderRadius:6}}>⋯</button>
            {menuOpen===f.id&&(<div style={{position:"absolute",right:40,top:"50%",transform:"translateY(-50%)",background:"#fff",border:`1px solid ${BORDER}`,borderRadius:12,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",zIndex:100,minWidth:170,overflow:"hidden"}}>
              <button onClick={()=>{
                // Send preview — open mailto with first step pre-filled
                const step=f.steps?.[0];
                if(step){const sub=encodeURIComponent(step.subject||"");const bod=encodeURIComponent((step.body||"").replace(/{{[^}]+}}/g,"[variable]"));window.open(`mailto:?subject=${sub}&body=${bod}`,"_blank");}
                setMenuOpen(null);toast("Preview opened in your email client");
              }} style={{display:"flex",alignItems:"center",gap:9,padding:"11px 16px",width:"100%",border:"none",background:"none",cursor:"pointer",fontSize:13,color:DARK,textAlign:"left",fontWeight:500}} onMouseEnter={e=>e.currentTarget.style.background="#f8faf9"} onMouseLeave={e=>e.currentTarget.style.background="none"}>👁 See a preview</button>
              <button onClick={()=>{
                // Duplicate flow
                const dup={...f,id:crypto.randomUUID(),name:`${f.name} (copy)`,used:0,reply:"—"};
                setFlows(prev=>[...prev,dup]);
                setMenuOpen(null);toast(`"${f.name}" duplicated`);
              }} style={{display:"flex",alignItems:"center",gap:9,padding:"11px 16px",width:"100%",border:"none",background:"none",cursor:"pointer",fontSize:13,color:DARK,textAlign:"left",fontWeight:500}} onMouseEnter={e=>e.currentTarget.style.background="#f8faf9"} onMouseLeave={e=>e.currentTarget.style.background="none"}>⧉ Duplicate</button>
              <div style={{borderTop:"1px solid #FEE2E2"}}><button onClick={()=>{setDeleteTarget(f.id);setMenuOpen(null);}} style={{display:"flex",alignItems:"center",gap:9,padding:"11px 16px",width:"100%",border:"none",background:"none",cursor:"pointer",fontSize:13,color:"#DC2626",textAlign:"left",fontWeight:600}} onMouseEnter={e=>e.currentTarget.style.background="#FEF2F2"} onMouseLeave={e=>e.currentTarget.style.background="none"}>🗑 Delete</button></div>
            </div>)}
          </td>
        </tr>))}</tbody>
      </table>
    </Card>}
    {/* ---------- Saved email templates library ---------- */}
    <Card style={{padding:0,overflow:"hidden"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,padding:"16px 18px",borderBottom:"1px solid #f0f7f3"}}>
        <div style={{minWidth:0}}>
          <div style={{fontWeight:700,color:DARK,fontSize:15}}>Saved email templates</div>
          <div style={{fontSize:12,color:MUTED,marginTop:2}}>Reusable one-off emails — pick them straight from the Send Email window. Supports {"{{placeholder}}"} variables.</div>
        </div>
        <button onClick={()=>setTplEditor({name:"",subject:"",body:""})} style={{background:G,color:DARK,border:"none",borderRadius:9,padding:"9px 16px",fontWeight:700,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6,flexShrink:0}}><span style={{fontSize:16,lineHeight:1}}>+</span> New template</button>
      </div>
      {!emailTplsLoaded
        ?<div style={{padding:"28px",textAlign:"center",color:MUTED,fontSize:13}}>Loading…</div>
        :emailTpls.length===0
          ?<div style={{padding:"32px 24px",textAlign:"center",color:MUTED,fontSize:13,lineHeight:1.6}}>No saved templates yet. Create one to reuse it whenever you email a creator.</div>
          :<div>{emailTpls.map((t,i)=>(<div key={t.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 18px",borderBottom:i<emailTpls.length-1?"1px solid #f8faf9":"none"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:600,fontSize:13,color:DARK}}>{t.name}</div>
              <div style={{fontSize:11,color:"#9ab8a6",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.subject||"(no subject)"}</div>
            </div>
            <button onClick={()=>setTplEditor({id:t.id,name:t.name,subject:t.subject||"",body:t.body||""})} style={{border:`1px solid ${BORDER}`,background:"#fff",color:DARK,borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>Edit</button>
            <button onClick={()=>deleteEmailTpl(t)} style={{border:"1px solid #FECACA",background:"#fff",color:"#DC2626",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>Delete</button>
          </div>))}</div>
      }
    </Card>
    {tplEditor&&(<Modal title={tplEditor.id?"Edit template":"New email template"} subtitle="Reusable subject + body. Use {{firstname}}, {{brand_name}} etc. — filled in per creator when you send." onClose={()=>setTplEditor(null)} width={620}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:6}}>Template name</div><input value={tplEditor.name} onChange={e=>setTplEditor(t=>({...t,name:e.target.value}))} placeholder="e.g. Paid rate ask" style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/></div>
        <div><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:6}}>Subject</div><input value={tplEditor.subject} onChange={e=>setTplEditor(t=>({...t,subject:e.target.value}))} placeholder="Email subject…" style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/></div>
        <div><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:6}}>Body</div><textarea value={tplEditor.body} onChange={e=>setTplEditor(t=>({...t,body:e.target.value}))} rows={9} placeholder={"Hi {{firstname}},\n\n…\n\nThanks,\n{{sender_name}}"} style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit",boxSizing:"border-box",lineHeight:1.6}}/></div>
        <div style={{fontSize:11,color:"#9ab8a6",background:"#f8faf9",padding:"8px 12px",borderRadius:7}}>💡 Variables: <code>{"{{firstname}}"}</code>, <code>{"{{brand_name}}"}</code>, <code>{"{{discount_code}}"}</code>, <code>{"{{sender_name}}"}</code> — filled per creator on send.</div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={()=>setTplEditor(null)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={saveEmailTpl} disabled={!tplEditor.name.trim()||tplSaving} style={{background:tplEditor.name.trim()&&!tplSaving?DARK:"#f0f0f0",color:tplEditor.name.trim()&&!tplSaving?G:"#9ab8a6",border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:tplEditor.name.trim()&&!tplSaving?"pointer":"not-allowed"}}>{tplSaving?"Saving…":tplEditor.id?"Save changes":"Create template"}</button>
        </div>
      </div>
    </Modal>)}
  </div>);
}

function EmailQueue({brandId,flows=[]}){
  const[rows,setRows]=useState([]);
  const[loaded,setLoaded]=useState(false);
  const[filter,setFilter]=useState("all"); // all | pending | sent | failed | skipped
  const[refreshTick,setRefreshTick]=useState(0);
  // Editor modal state for pending auto-replies. Opening writes the row's
  // body into editBody; Save persists to email_queue.body, leaving the
  // original_draft column untouched so the AI's first take is preserved as
  // the learning baseline.
  const[editingRow,setEditingRow]=useState(null);
  const[editBody,setEditBody]=useState("");
  const[editBusy,setEditBusy]=useState(false);

  useEffect(()=>{
    if(!brandId){setLoaded(true);return;}
    let cancelled=false;
    (async()=>{
      setLoaded(false);
      const{data,error}=await supabase.from('email_queue')
        .select('*').eq('brand_id',brandId)
        .order('scheduled_for',{ascending:false}).limit(500);
      if(cancelled)return;
      if(error){console.error('queue load',error);setLoaded(true);return;}
      setRows(data||[]);
      setLoaded(true);
    })();
    return()=>{cancelled=true;};
  },[brandId,refreshTick]);

  const counts=useMemo(()=>{
    const c={pending:0,sent:0,failed:0,skipped:0,sending:0,cancelled:0,review_pending:0};
    rows.forEach(r=>{if(c[r.status]!==undefined)c[r.status]++;});
    return c;
  },[rows]);

  const visible=filter==="all"?rows:rows.filter(r=>r.status===filter);

  // Approve a review_pending auto-reply → flip to 'pending' so worker fires it
  // on the next tick. Operator can also Edit body before approving via the
  // existing Edit affordance (which leaves original_draft untouched).
  const approveRow=async(id)=>{
    const{error}=await supabase.from('email_queue').update({status:'pending'}).eq('id',id).eq('status','review_pending');
    if(error){toast(`Couldn't approve: ${error.message}`,'error');return;}
    setRows(prev=>prev.map(r=>r.id===id?{...r,status:'pending'}:r));
    toast('✓ Approved — will send on next worker tick');
  };

  const cancelRow=async(id)=>{
    const{error}=await supabase.from('email_queue').update({status:'cancelled'}).eq('id',id);
    if(error){toast(`Couldn't cancel: ${error.message}`,'error');return;}
    setRows(prev=>prev.map(r=>r.id===id?{...r,status:'cancelled'}:r));
    toast('Cancelled','info');
  };

  const openEditor=(row)=>{
    setEditingRow(row);
    setEditBody(row.body || "");
  };
  // saveEdit updates body in place. If the row is review_pending AND the
  // operator chose Save & Approve, also flip status to 'pending' in the
  // same DB write so the worker picks it up on next tick. One-step UX
  // for the "edit + ship it" flow in calibration mode.
  const saveEdit=async(alsoApprove=false)=>{
    if(!editingRow||editBusy) return;
    const trimmed=editBody.trim();
    if(!trimmed){toast("Body can't be empty",'error');return;}
    const targetStatus = alsoApprove && editingRow.status==='review_pending' ? 'pending' : editingRow.status;
    setEditBusy(true);
    const patch = alsoApprove && editingRow.status==='review_pending'
      ? { body: trimmed, status: 'pending' }
      : { body: trimmed };
    const{error}=await supabase.from('email_queue')
      .update(patch).eq('id',editingRow.id).in('status',['pending','review_pending']);
    setEditBusy(false);
    if(error){toast(`Couldn't save: ${error.message}`,'error');return;}
    setRows(prev=>prev.map(r=>r.id===editingRow.id?{...r,body:trimmed,status:targetStatus}:r));
    const edited = editingRow.original_draft && editingRow.original_draft.trim() !== trimmed;
    if(alsoApprove) toast('✓ Saved & approved — sending on next tick');
    else toast(edited?'✓ Edit saved — the AI will learn from this next time':'✓ Saved');
    setEditingRow(null);
    setEditBody("");
  };

  const flowName=(id)=>flows.find(f=>f.id===id)?.name||"—";

  const statusColor={
    pending:"#3B82F6",sending:"#F59E0B",sent:"#10B981",failed:"#EF4444",
    cancelled:"#9CA3AF",skipped:"#9CA3AF",review_pending:"#8B5CF6",
  };

  return(<div style={{display:"flex",flexDirection:"column",gap:16}}>
    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
      {[
        {key:"all",label:`All (${rows.length})`},
        // Awaiting review tab only appears if there's something in it — keeps
        // the UI quiet for brands that haven't enabled review mode.
        ...(counts.review_pending>0?[{key:"review_pending",label:`Awaiting review (${counts.review_pending})`,color:"#8B5CF6"}]:[]),
        {key:"pending",label:`Pending (${counts.pending})`,color:"#3B82F6"},
        {key:"sent",label:`Sent (${counts.sent})`,color:"#10B981"},
        {key:"failed",label:`Failed (${counts.failed})`,color:"#EF4444"},
        {key:"skipped",label:`Skipped (${counts.skipped})`,color:"#9CA3AF"},
      ].map(t=>(<button key={t.key} onClick={()=>setFilter(t.key)} style={{padding:"6px 14px",borderRadius:20,border:`1px solid ${filter===t.key?DARK:BORDER}`,background:filter===t.key?DARK:"#fff",color:filter===t.key?G:DARK,fontSize:12,fontWeight:600,cursor:"pointer"}}>{t.label}</button>))}
      <button onClick={()=>setRefreshTick(t=>t+1)} style={{marginLeft:"auto",padding:"6px 14px",borderRadius:8,border:`1px solid ${BORDER}`,background:"#fff",color:DARK,fontSize:12,fontWeight:600,cursor:"pointer"}}>↻ Refresh</button>
    </div>

    {!loaded
      ?<Card style={{padding:40,textAlign:"center",color:MUTED}}>Loading…</Card>
      :visible.length===0
        ?<Card style={{padding:40,textAlign:"center"}}>
            <div style={{fontSize:36,marginBottom:10}}>📭</div>
            <div style={{fontWeight:700,color:DARK,fontSize:15,marginBottom:6}}>No emails in this view</div>
            <div style={{fontSize:13,color:MUTED}}>{filter==="all"?"Once creators are on a flow, the worker queues their emails here.":`No ${filter} emails right now.`}</div>
          </Card>
        :<Card>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{borderBottom:`1px solid ${BORDER}`}}>
                {["Scheduled","Status","Creator","Subject","Step","Variant","Attempts",""].map(h=>(<th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".07em"}}>{h}</th>))}
              </tr></thead>
              <tbody>{visible.map(r=>{
                const when=new Date(r.scheduled_for);
                const isFuture=when.getTime()>Date.now();
                return(<tr key={r.id} style={{borderBottom:`1px solid #f5f6f7`}}>
                  <td style={{padding:"12px 14px",fontSize:12,color:DARK,whiteSpace:"nowrap"}}>{when.toLocaleString("en-AU",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"})}<div style={{fontSize:10,color:MUTED}}>{r.sent_at?`sent ${new Date(r.sent_at).toLocaleString("en-AU",{hour:"2-digit",minute:"2-digit"})}`:isFuture?"in future":"due now"}</div></td>
                  <td style={{padding:"12px 14px",whiteSpace:"nowrap"}}><span style={{background:statusColor[r.status]+"18",color:statusColor[r.status],fontWeight:700,fontSize:11,padding:"2px 9px",borderRadius:6,border:`1px solid ${statusColor[r.status]}40`}}>{r.status}</span>{r.test_send&&<span style={{marginLeft:6,background:"#FFFBEB",color:"#92400E",fontWeight:800,fontSize:10,padding:"2px 7px",borderRadius:5,border:"1px solid #FDE68A",letterSpacing:".05em"}}>TEST</span>}{r.original_draft&&(()=>{
                    // Auto-reply provenance badge:
                    //   • "AI · edited"   — operator changed the draft before send (sent or pending)
                    //   • "AI · pending"  — draft generated, awaiting review/send
                    //   • "AI · sent as-is" — went out exactly as Sonnet drafted it
                    // Hovering shows the original draft so the operator can compare.
                    const edited = r.body && r.original_draft.trim() !== r.body.trim();
                    const label = r.status === 'pending' ? (edited ? 'AI · edited' : 'AI · drafted') : (edited ? 'AI · edited' : 'AI · as-is');
                    return(<span title={`AI draft was:\n\n${r.original_draft}`} style={{marginLeft:6,background:edited?"#EEF2FF":"#F3F4F6",color:edited?"#3730A3":"#374151",fontWeight:700,fontSize:10,padding:"2px 7px",borderRadius:5,border:`1px solid ${edited?"#C7D2FE":"#E5E7EB"}`,letterSpacing:".04em",cursor:"help"}}>{label}</span>);
                  })()}</td>
                  <td style={{padding:"12px 14px",fontSize:12,color:DARK,fontWeight:600}}>@{r.creator_handle}</td>
                  <td style={{padding:"12px 14px",fontSize:12,color:DARK,maxWidth:320,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.subject}<div style={{fontSize:10,color:MUTED,marginTop:2}}>flow: {flowName(r.flow_id)}</div></td>
                  <td style={{padding:"12px 14px",fontSize:12,color:DARK}}>{r.step_index+1}</td>
                  <td style={{padding:"12px 14px",fontSize:12,color:DARK}}>{r.variant||"—"}</td>
                  <td style={{padding:"12px 14px",fontSize:12,color:DARK}}>{r.attempts}{r.last_error&&<div title={r.last_error} style={{fontSize:10,color:"#EF4444",marginTop:2,maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",cursor:"help"}}>{r.last_error}</div>}</td>
                  <td style={{padding:"12px 14px",whiteSpace:"nowrap"}}>{(r.status==="pending"||r.status==="review_pending")&&<>
                    {r.status==="review_pending"&&<button onClick={()=>approveRow(r.id)} title="Approve as-is — will send on next worker tick"
                      style={{border:"none",background:"#8B5CF6",color:"#fff",borderRadius:6,padding:"3px 11px",fontSize:11,cursor:"pointer",fontWeight:700,marginRight:6}}>Approve</button>}
                    <button onClick={()=>openEditor(r)} style={{border:`1px solid ${BORDER}`,background:"#fff",color:DARK,borderRadius:6,padding:"3px 9px",fontSize:11,cursor:"pointer",fontWeight:600,marginRight:6}}>{r.original_draft?"Edit reply":"Edit"}</button>
                    <button onClick={()=>cancelRow(r.id)} style={{border:`1px solid ${BORDER}`,background:"#fff",color:MUTED,borderRadius:6,padding:"3px 9px",fontSize:11,cursor:"pointer"}}>{r.status==="review_pending"?"Reject":"Cancel"}</button>
                  </>}</td>
                </tr>);
              })}</tbody>
            </table>
          </Card>
    }
    {editingRow && (()=>{
      // Edit-before-send modal for queue rows. Auto-reply rows get the
      // "what the AI drafted" diff context; flow rows just get a plain
      // edit affordance. Saving leaves original_draft untouched so the
      // learning baseline is preserved.
      const isAutoReply = !!editingRow.original_draft;
      const aiDraft = editingRow.original_draft || "";
      const diverged = isAutoReply && aiDraft.trim() !== editBody.trim();
      return(<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",backdropFilter:"blur(3px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
        <div style={{background:"#fff",borderRadius:14,padding:28,width:680,maxWidth:"94vw",maxHeight:"92vh",overflowY:"auto",boxShadow:"0 0 0 1px rgba(0,0,0,0.08),0 24px 80px rgba(0,0,0,0.18)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
            <div>
              <div style={{fontWeight:700,fontSize:17,color:DARK}}>{isAutoReply?"Edit auto-reply":"Edit queued email"}</div>
              <div style={{fontSize:12,color:MUTED,marginTop:3}}>To @{editingRow.creator_handle} · {editingRow.to_email}</div>
            </div>
            <button onClick={()=>{setEditingRow(null);setEditBody("");}} disabled={editBusy} style={{border:"none",background:PAGE_BG,borderRadius:6,width:28,height:28,cursor:"pointer",color:MUTED,fontSize:16}}>×</button>
          </div>
          <div style={{fontSize:11,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:5}}>Subject</div>
          <div style={{fontSize:13,color:DARK,padding:"7px 10px",background:PAGE_BG,borderRadius:7,marginBottom:14}}>{editingRow.subject}</div>
          {isAutoReply && <div style={{marginBottom:12,padding:"10px 12px",background:"#F8FAFC",border:`1px solid ${BORDER}`,borderRadius:8}}>
            <div style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:5,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span>AI's draft</span>
              {diverged?<span style={{background:"#EEF2FF",color:"#3730A3",padding:"2px 7px",borderRadius:5,fontSize:10}}>Your edits will feed the next reply</span>:<span style={{color:MUTED,fontSize:10}}>(unchanged so far)</span>}
            </div>
            <div style={{fontSize:12,color:"#4b5563",lineHeight:1.5,whiteSpace:"pre-wrap",maxHeight:120,overflowY:"auto"}}>{aiDraft}</div>
          </div>}
          <div style={{fontSize:11,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:5}}>{isAutoReply?"Your version":"Body"}</div>
          <textarea value={editBody} onChange={e=>setEditBody(e.target.value)} disabled={editBusy}
            style={{width:"100%",minHeight:200,padding:"11px 13px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,fontFamily:"inherit",outline:"none",lineHeight:1.5,resize:"vertical"}}/>
          <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:18}}>
            <button onClick={()=>{setEditingRow(null);setEditBody("");}} disabled={editBusy} style={{background:"transparent",border:`1px solid ${BORDER}`,color:MUTED,borderRadius:8,padding:"9px 16px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
            <button onClick={()=>saveEdit(false)} disabled={editBusy||!editBody.trim()} style={{background:editBody.trim()&&!editBusy?"#fff":"#f0f0f0",border:`1px solid ${BORDER}`,color:editBody.trim()&&!editBusy?DARK:"#9ab8a6",borderRadius:8,padding:"9px 18px",fontSize:13,fontWeight:600,cursor:editBody.trim()&&!editBusy?"pointer":"not-allowed"}}>{editingRow.status==='review_pending'?"Save only":"Save"}</button>
            {editingRow.status==='review_pending' && <button onClick={()=>saveEdit(true)} disabled={editBusy||!editBody.trim()} style={{background:editBody.trim()&&!editBusy?"#8B5CF6":"#f0f0f0",color:editBody.trim()&&!editBusy?"#fff":"#9ab8a6",border:"none",borderRadius:8,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:editBody.trim()&&!editBusy?"pointer":"not-allowed"}}>{editBusy?"Saving…":"Save & approve"}</button>}
          </div>
        </div>
      </div>);
    })()}
  </div>);
}

function ContentAnalysis(){return(<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"60vh",flexDirection:"column",gap:16}}><div style={{width:64,height:64,borderRadius:16,background:"#f0f4f2",display:"flex",alignItems:"center",justifyContent:"center"}}><svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="#9ab8a6" strokeWidth={1.5} strokeLinecap="round"><path d="M15 10l4.553-2.069A1 1 0 0121 8.869v6.262a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg></div><div style={{fontWeight:700,fontSize:18,color:DARK}}>Not available yet</div><div style={{fontSize:13,color:MUTED,textAlign:"center",maxWidth:320,lineHeight:1.6}}>Content analysis runs via the Claude API on your video files. Connect Google Drive and the analysis pipeline to enable this feature.</div></div>);}

// Per-creator Posts card on CreatorProfile. Shows detected content for
// just this one creator + a Posts-over-time mini chart + the Track UGC
// toggle. Mirrors what Influencer Hero shows on its "Posts" tab.
function CreatorPostsCard({creatorId, creatorHandle, brandId, brandIgUsername}){
  const [posts, setPosts] = useState([]);
  const [trackUgc, setTrackUgc] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    (async () => {
      // Posts: prefer creator_id match; fall back to handle for unmatched/legacy rows.
      let q = supabase.from('creator_posts').select('*').eq('brand_id', brandId);
      if (creatorId) q = q.eq('creator_id', creatorId);
      else if (creatorHandle) q = q.eq('creator_handle', String(creatorHandle).replace(/^@/,''));
      else return;
      const { data, error } = await q.order('posted_at', { ascending: false, nullsFirst: false }).limit(60);
      if (cancelled) return;
      if (error) { console.error('creator posts load', error); setLoaded(true); return; }
      setPosts(data || []);
      setLoaded(true);
    })();
    if (creatorId) {
      (async () => {
        const { data } = await supabase.from('creators').select('track_ugc').eq('id', creatorId).maybeSingle();
        if (!cancelled && data) setTrackUgc(data.track_ugc !== false);
      })();
    }
    return () => { cancelled = true; };
  }, [brandId, creatorId, creatorHandle]);

  const toggleTrack = async () => {
    if (!creatorId) return;
    const next = !trackUgc;
    setTrackUgc(next);
    const { error } = await supabase.from('creators').update({ track_ugc: next }).eq('id', creatorId);
    if (error) { toast(`Couldn't save: ${error.message}`, 'error'); setTrackUgc(!next); }
    else toast(next ? 'Tracking new content for this creator' : 'Paused content tracking');
  };

  // Posts-over-time mini chart: count per day across the last 30 days.
  // Compact bars rendered as flex divs (avoids a charting dep for this small viz).
  const days = 30;
  const buckets = Array.from({length: days}, (_, i) => {
    const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() - (days - 1 - i));
    return { date: d, count: 0 };
  });
  for (const p of posts) {
    if (!p.posted_at) continue;
    const t = new Date(p.posted_at); t.setHours(0,0,0,0);
    const idx = Math.floor((t.getTime() - buckets[0].date.getTime()) / (24 * 3600 * 1000));
    if (idx >= 0 && idx < days) buckets[idx].count++;
  }
  const maxCount = Math.max(1, ...buckets.map(b => b.count));

  return (
    <Card style={{padding:18}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
        <div style={{fontWeight:700,fontSize:13,color:DARK,display:"flex",alignItems:"center",gap:8}}>
          📷 Posts {posts.length>0 && <span style={{fontSize:11,fontWeight:600,color:MUTED,background:"#f0f4f2",padding:"1px 7px",borderRadius:10}}>{posts.length}</span>}
        </div>
        {creatorId && (
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:MUTED,cursor:"pointer"}}>
            <input type="checkbox" checked={trackUgc} onChange={toggleTrack} style={{cursor:"pointer"}}/>
            Track UGC
          </label>
        )}
      </div>

      {!loaded ? (
        <div style={{fontSize:12,color:MUTED,padding:"8px 0"}}>Loading…</div>
      ) : posts.length === 0 ? (
        <div style={{fontSize:12,color:MUTED,padding:"8px 0",lineHeight:1.6}}>
          No content detected yet. When this creator tags {brandIgUsername?`@${String(brandIgUsername).replace(/^@/,'')}`:'the brand'} in a post or reel, it'll show up here within 15 minutes.
        </div>
      ) : (
        <>
          {/* Posts over time — last 30 days, mini bar chart */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10,color:MUTED,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>Posts over time · last 30 days</div>
            <div style={{display:"flex",alignItems:"flex-end",gap:2,height:48,padding:"4px 0"}}>
              {buckets.map((b, i) => (
                <div key={i} title={`${b.count} on ${b.date.toLocaleDateString()}`}
                  style={{flex:1,height:`${(b.count/maxCount)*100}%`,minHeight:b.count>0?4:2,background:b.count>0?G:"#e8eceb",borderRadius:2,transition:"opacity .15s"}}/>
              ))}
            </div>
          </div>

          {/* Compact card grid for this creator's posts */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(110px, 1fr))",gap:8}}>
            {posts.slice(0, 12).map(p => {
              const isVideo = p.media_type === 'VIDEO' || p.media_product_type === 'REELS' || p.media_product_type === 'STORY';
              const productType = (p.media_product_type || '').toUpperCase();
              const badgeLabel = productType === 'STORY' ? 'Story' : productType === 'REELS' ? 'Reel' : productType === 'AD' ? 'Ad' : (p.media_type === 'CAROUSEL_ALBUM' ? 'Carousel' : 'Post');
              return (
                <a key={p.id} href={p.permalink || '#'} target="_blank" rel="noopener noreferrer"
                  style={{position:"relative",aspectRatio:"1/1",borderRadius:8,overflow:"hidden",background:"#f3f4f6",textDecoration:"none",display:"block"}}>
                  {p.thumbnail_url ? (
                    <img src={p.thumbnail_url} alt={p.caption||''}
                      style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}
                      onError={(e)=>{e.target.style.display='none';}}/>
                  ) : (
                    <div style={{height:"100%",display:"flex",alignItems:"center",justifyContent:"center",color:MUTED,fontSize:10,padding:6,textAlign:"center"}}>{badgeLabel}</div>
                  )}
                  <div style={{position:"absolute",top:4,left:4,background:"rgba(0,0,0,0.55)",color:"#fff",fontSize:9,fontWeight:700,padding:"1px 5px",borderRadius:4,textTransform:"uppercase",letterSpacing:0.4}}>{badgeLabel}</div>
                  {isVideo && <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:24,height:24,borderRadius:"50%",background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <svg width={10} height={10} viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
                  </div>}
                </a>
              );
            })}
          </div>
          {posts.length > 12 && (
            <div style={{fontSize:11,color:MUTED,marginTop:10,textAlign:"center"}}>+ {posts.length - 12} more — see full wall in Receipts</div>
          )}
        </>
      )}
    </Card>
  );
}

// Receipts — cross-creator wall of detected content for this brand.
// Source: creator_posts table, populated by instagram-mentions-webhook +
// instagram-tags-poll (auto) + manual Add (operator). Distinct from
// "Content Analysis" which is the Claude-vision creative QA tool.
function Receipts({brandId, brand, activeBrand, onSelectCreator, allCreators}){
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState("all"); // all|7d|30d|90d
  const [mediaType, setMediaType] = useState("all"); // all|IMAGE|VIDEO|CAROUSEL_ALBUM
  const [mediaProductType, setMediaProductType] = useState("all"); // all|FEED|REELS|STORY|AD
  const [selectedCreatorIds, setSelectedCreatorIds] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  // Segment switch: 'all' shows everything; 'matched' = posts from creators
  // we have in our pipeline (creator_id is set); 'organic' = posts where
  // someone tagged the brand but they're not in our creator roster.
  const [segment, setSegment] = useState("all");
  // Per-card carousel state: maps post.id → current slide index. Default 0.
  const [carouselIdx, setCarouselIdx] = useState({});
  // Inline video refs for single-play behavior — when one starts, pause all
  // others. Keyed by post.id since only one slide is visible per card at a time.
  const videoRefs = useRef(new Map());
  const handleVideoPlay = (id) => {
    videoRefs.current.forEach((el, k) => {
      if (k !== id && el && !el.paused) el.pause();
    });
  };
  // Post detail modal — opens when operator clicks the image/expand on a card.
  // Shows full caption + full-size media + carousel nav + actions in one view
  // instead of bouncing to instagram.com. Keeps focus on the wall.
  const [detailPost, setDetailPost] = useState(null);
  const [detailIdx, setDetailIdx]   = useState(0); // current slide in modal
  // Re-archive state for the "Try to recover" button on stranded videos.
  const [reArchiving, setReArchiving] = useState(false);
  const tryRecoverArchive = async (post) => {
    setReArchiving(true);
    try {
      const tok = await window.Clerk?.session?.getToken();
      const resp = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/creator-post-rearchive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok}`, 'content-type': 'application/json' },
        body: JSON.stringify({ post_id: post.id }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.ok) {
        toast('✓ Video recovered — refreshing');
        // Refresh the post row so detailPost picks up the new drive_file_id.
        const { data: fresh } = await supabase.from('creator_posts').select('*').eq('id', post.id).maybeSingle();
        if (fresh) {
          setDetailPost(fresh);
          setPosts(prev => prev.map(x => x.id === fresh.id ? fresh : x));
        }
      } else if (resp.status === 410) {
        toast(data.message || 'Video file is permanently unrecoverable — IG already paginated past it.', 'error');
      } else {
        toast(`Recover failed: ${data.reason || data.error || resp.status}`, 'error');
      }
    } catch (e) {
      toast(`Recover error: ${e.message || e}`, 'error');
    } finally {
      setReArchiving(false);
    }
  };
  // Custom date range — only used when dateRange === 'custom'.
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo,   setCustomDateTo]   = useState("");
  // Sort field + direction. posted_at-desc is default; likes/comments/
  // engagement also offered. views are almost always null for tagged-in
  // posts (Meta only exposes view counts for media the token's account
  // OWNS) — we offer it but flag it in the dropdown so operators know.
  const [sortKey, setSortKey] = useState("posted_at");  // posted_at|likes|comments|engagement
  const [sortDir, setSortDir] = useState("desc");       // desc|asc
  // If a prior session left sortKey on 'views' (option no longer offered —
  // Meta doesn't expose view counts for tagged posts), flip to engagement.
  useEffect(() => { if (sortKey === 'views') setSortKey('engagement'); }, [sortKey]);
  // Pagination: pull a page at a time instead of one big query.
  const PAGE_SIZE = 100;
  const [loadLimit, setLoadLimit] = useState(PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(null);     // null = unknown until first load completes
  // Manual add modal state.
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualFile, setManualFile] = useState(null);
  const [manualCreatorId, setManualCreatorId] = useState(null);
  const [manualCreatorHandle, setManualCreatorHandle] = useState("");
  const [manualFormat, setManualFormat] = useState("STORY");
  const [manualPermalink, setManualPermalink] = useState("");
  const [manualCaption, setManualCaption] = useState("");
  const [manualUploading, setManualUploading] = useState(false);
  // Usage rights modal state (per-post). Opened from card 3-dot menu.
  const [urPost, setUrPost] = useState(null); // creator_posts row being acted on
  const [urType, setUrType] = useState("Repurpose");
  const [urPlatforms, setUrPlatforms] = useState(["Instagram", "Facebook"]);
  const [urDuration, setUrDuration] = useState("12 months");
  const [urCompensation, setUrCompensation] = useState("Gifted");
  const [urPaidAmount, setUrPaidAmount] = useState("");
  const [urSending, setUrSending] = useState(false);

  const openUR = (post) => {
    setOpenMenuId(null);
    setUrPost(post);
    setUrType("Repurpose"); setUrPlatforms(["Instagram", "Facebook"]); setUrDuration("12 months");
    setUrCompensation("Gifted"); setUrPaidAmount("");
  };

  const submitUR = async () => {
    if (!urPost) return;
    const creator = (allCreators || []).find(c => c.id === urPost.creator_id);
    if (!creator?.email) { toast("No email on file for that creator — add it in their profile first", "error"); return; }
    setUrSending(true);
    try {
      const compensationLine = urCompensation === "Paid" && urPaidAmount.trim()
        ? `${urPaidAmount.trim()} (paid)`
        : urCompensation;
      const firstName = (creator.name || creator.handle || "there").split(" ")[0];
      const subject = `Usage Rights Request — ${brand?.name || "Brand"}`;
      const body = `Hi ${firstName},\n\nThank you for your recent post${urPost.permalink ? `: ${urPost.permalink}` : ""} — we loved it.\n\nWe'd love to request usage rights to use your content in our marketing:\n\n• Type: ${urType}\n• Platforms: ${urPlatforms.join(", ")}\n• Duration: ${urDuration}\n• Compensation: ${compensationLine}\n\nIf you agree, please reply confirming and we'll send over a formal agreement.\n\nBest,\n${brand?.senderName || ""}`;

      const token = await window.Clerk?.session?.getToken();
      const resp = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-creator-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify({ brand_id: brandId, creator_handle: creator.handle, to: creator.email, subject, body }),
      });
      const result = await resp.json();
      if (!resp.ok) { toast(`Send failed: ${result.message || result.error || resp.status}`, "error"); setUrSending(false); return; }

      const terms = {
        type: urType, platforms: urPlatforms, duration: urDuration,
        compensation: urCompensation, paid_amount: urCompensation === "Paid" ? urPaidAmount.trim() : null,
      };
      await supabase.from("creator_posts").update({
        usage_rights_status: "requested",
        usage_rights_requested_at: new Date().toISOString(),
        usage_rights_terms: terms,
        usage_rights_email_id: result?.email_id || null,
      }).eq("id", urPost.id);

      await supabase.from("activity_log").insert({
        brand_id: brandId,
        creator_handle: creator.handle,
        type: "status",
        text: `Usage rights requested for post (${urType} · ${urPlatforms.join(", ")} · ${urDuration})`,
        extra: { usage_rights: { status: "requested", ...terms }, creator_post_id: urPost.id, permalink: urPost.permalink || null },
      });

      toast(`✓ Request emailed to ${creator.email}`);
      setUrPost(null);
      load();
    } catch (e) {
      console.error(e);
      toast(`Send failed: ${e.message || "network error"}`, "error");
    } finally {
      setUrSending(false);
    }
  };

  // Assign action for organic posts (creator_id null). Operator picks a
  // creator from the brand's list, we set creator_id + creator_handle.
  // The handle-change DB trigger re-links other organic posts on same
  // handle. Drive folder move is best-effort and only fires if we know
  // the creator's folder ID.
  const [assignPost, setAssignPost] = useState(null);
  const [assignCreatorId, setAssignCreatorId] = useState("");

  const submitAssign = async () => {
    if (!assignPost || !assignCreatorId) return;
    const creator = (allCreators || []).find(c => c.id === assignCreatorId);
    if (!creator) { toast("Creator not found", "error"); return; }

    const { error } = await supabase.from("creator_posts").update({
      creator_id: creator.id,
      creator_handle: String(creator.handle || "").replace(/^@/, ""),
    }).eq("id", assignPost.id);
    if (error) { toast(`Assign failed: ${error.message}`, "error"); return; }

    await supabase.from("activity_log").insert({
      brand_id: brandId,
      creator_handle: String(creator.handle || "").replace(/^@/, ""),
      type: "status",
      text: `Post manually assigned to @${creator.handle}`,
      extra: { creator_post_id: assignPost.id, permalink: assignPost.permalink || null, was_organic: true },
    });

    toast(`✓ Assigned to @${creator.handle}`);
    setAssignPost(null);
    setAssignCreatorId("");
    load();
  };

  const flipURStatus = async (post, newStatus) => {
    setOpenMenuId(null);
    const ts = new Date().toISOString();
    const patch = { usage_rights_status: newStatus };
    if (newStatus === "granted") patch.usage_rights_granted_at = ts;
    if (newStatus === "denied") patch.usage_rights_denied_at = ts;
    await supabase.from("creator_posts").update(patch).eq("id", post.id);
    const creator = (allCreators || []).find(c => c.id === post.creator_id);
    if (creator) {
      await supabase.from("activity_log").insert({
        brand_id: brandId,
        creator_handle: creator.handle,
        type: "status",
        text: `Usage rights ${newStatus} for post`,
        extra: { creator_post_id: post.id, permalink: post.permalink || null, new_status: newStatus },
      });
    }
    toast(`Usage rights marked ${newStatus}`);
    load();
  };

  const submitManualAdd = async () => {
    if (!manualFile) { toast("Pick a file first", "error"); return; }
    if (!manualCreatorHandle.trim() && !manualCreatorId) { toast("Pick a creator or type a handle", "error"); return; }
    setManualUploading(true);
    try {
      // Read file as base64.
      const fileBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          // Strip "data:mime;base64," prefix.
          const idx = String(result).indexOf("base64,");
          resolve(idx >= 0 ? String(result).slice(idx + 7) : String(result));
        };
        reader.onerror = reject;
        reader.readAsDataURL(manualFile);
      });

      const token = await window.Clerk?.session?.getToken();
      const r = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/creator-content-manual-add`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify({
          brand_id: brandId,
          creator_id: manualCreatorId || null,
          creator_handle: manualCreatorHandle.trim().replace(/^@/, ""),
          media_product_type: manualFormat,
          permalink: manualPermalink.trim() || null,
          caption: manualCaption.trim() || null,
          file_base64: fileBase64,
          file_mime: manualFile.type || "image/jpeg",
          file_name: manualFile.name,
        }),
      });
      const body = await r.json();
      if (!r.ok || !body.ok) { toast(`Upload failed: ${body.error || r.status}`, "error"); setManualUploading(false); return; }
      toast(`✓ Added · ${body.drive_file_id ? "saved to Drive" : "in wall only"}`);
      setShowManualModal(false);
      setManualFile(null); setManualCreatorHandle(""); setManualCreatorId(null);
      setManualPermalink(""); setManualCaption(""); setManualFormat("STORY");
      load();
    } catch (e) {
      console.error(e);
      toast("Upload failed — see console", "error");
    } finally {
      setManualUploading(false);
    }
  };

  // Push date + sort to the Supabase query so pagination works correctly
  // across the whole archive (not just the loaded page). Search + segment
  // + media-type filters stay client-side on the currently-loaded subset.
  const buildLoadQuery = (limit) => {
    let q = supabase.from('creator_posts')
      .select('*', { count: 'exact' })
      .eq('brand_id', brandId);
    // Date range
    if (dateRange === '7d')  q = q.gte('posted_at', new Date(Date.now() - 7  * 86400e3).toISOString());
    if (dateRange === '30d') q = q.gte('posted_at', new Date(Date.now() - 30 * 86400e3).toISOString());
    if (dateRange === '90d') q = q.gte('posted_at', new Date(Date.now() - 90 * 86400e3).toISOString());
    if (dateRange === 'custom') {
      if (customDateFrom) q = q.gte('posted_at', new Date(customDateFrom + 'T00:00:00Z').toISOString());
      if (customDateTo)   q = q.lte('posted_at', new Date(customDateTo   + 'T23:59:59Z').toISOString());
    }
    // Sort. Every branch ends with a posted_at desc tiebreaker so rows that
    // tie on the primary key (e.g. all share NULL likes/comments) come back
    // in a stable, sensible order — newest first — instead of whatever order
    // Postgres feels like returning that day.
    const asc = sortDir === 'asc';
    if (sortKey === 'likes')        q = q.order('likes_count',    { ascending: asc, nullsFirst: false });
    else if (sortKey === 'comments') q = q.order('comments_count', { ascending: asc, nullsFirst: false });
    else if (sortKey === 'engagement') {
      // Postgres doesn't let us order by a JS-style expression via PostgREST
      // easily. Approximate by ordering by likes first then comments — good
      // enough; engagement is dominated by likes for most accounts.
      q = q.order('likes_count', { ascending: asc, nullsFirst: false });
      q = q.order('comments_count', { ascending: asc, nullsFirst: false });
    }
    else q = q.order('posted_at', { ascending: asc, nullsFirst: false });
    if (sortKey !== 'posted_at') {
      q = q.order('posted_at', { ascending: false, nullsFirst: false });
    }
    return q.limit(limit);
  };

  const load = async () => {
    if (!brandId) return;
    setLoading(true);
    const { data, error, count } = await buildLoadQuery(loadLimit);
    if (error) console.error('receipts load', error);
    setPosts(data || []);
    setTotalRows(typeof count === 'number' ? count : null);
    setLoading(false);
  };
  // Re-query whenever brand/date/sort changes. Other filters stay client-side
  // so they don't trigger a full re-fetch on every keystroke.
  useEffect(() => {
    setLoadLimit(PAGE_SIZE);
    load();
    /* eslint-disable-next-line */
  }, [brandId, dateRange, customDateFrom, customDateTo, sortKey, sortDir]);
  // When operator clicks Load more we just re-query with a bigger limit;
  // Supabase returns rows already sorted by our ORDER BY so the new chunk
  // is appended naturally.
  useEffect(() => {
    if (loadLimit !== PAGE_SIZE) load();
    /* eslint-disable-next-line */
  }, [loadLimit]);

  // Counts per segment (computed against everything BUT the segment filter
  // itself, so the pill counts stay stable as the operator picks one).
  const baseFiltered = posts.filter(p => {
    if (!showHidden && p.hidden_at) return false;
    if (showHidden && !p.hidden_at) return false;
    // date filter applied server-side via buildLoadQuery — no client cutoff needed
    if (mediaType !== "all" && p.media_type !== mediaType) return false;
    if (mediaProductType !== "all" && p.media_product_type !== mediaProductType) return false;
    if (selectedCreatorIds.length && !selectedCreatorIds.includes(p.creator_id)) return false;
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      const haystack = `${p.creator_handle || ''} ${p.caption || ''}`.toLowerCase();
      if (!haystack.includes(s)) return false;
    }
    return true;
  });
  const segCounts = {
    all: baseFiltered.length,
    matched: baseFiltered.filter(p => p.creator_id).length,
    organic: baseFiltered.filter(p => !p.creator_id).length,
  };
  const filtered = baseFiltered.filter(p => {
    if (segment === "matched") return !!p.creator_id;
    if (segment === "organic") return !p.creator_id;
    return true;
  });

  const hide = async (post) => {
    setOpenMenuId(null);
    await supabase.from('creator_posts').update({
      hidden_at: new Date().toISOString(),
    }).eq('id', post.id);
    load();
  };
  const unhide = async (post) => {
    setOpenMenuId(null);
    await supabase.from('creator_posts').update({
      hidden_at: null, hidden_by_clerk_user_id: null,
    }).eq('id', post.id);
    load();
  };
  const jumpToCreator = (post) => {
    setOpenMenuId(null);
    const creator = (allCreators || []).find(c => c.id === post.creator_id || (c.handle || '').replace(/^@/,'').toLowerCase() === (post.creator_handle || '').replace(/^@/,'').toLowerCase());
    if (creator && onSelectCreator) onSelectCreator(creator);
    else toast(`No creator profile for @${post.creator_handle}`, 'info');
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / (24 * 3600 * 1000));
    if (days < 1) return "today";
    if (days < 2) return "yesterday";
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.floor(days/30)} months ago`;
    return `${Math.floor(days/365)} years ago`;
  };

  const mediaProductBadge = (p) => {
    const mpt = (p.media_product_type || '').toUpperCase();
    if (mpt === 'STORY') return { label: 'Story', color: '#EC4899' };
    if (mpt === 'REELS') return { label: 'Reel', color: '#8B5CF6' };
    if (mpt === 'AD') return { label: 'Ad', color: '#F59E0B' };
    if ((p.media_type || '').toUpperCase() === 'CAROUSEL_ALBUM') return { label: 'Carousel', color: '#3B82F6' };
    return { label: 'Post', color: '#10B981' };
  };

  return (
    <div style={{padding:24, maxWidth:1400, margin:"0 auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18}}>
        <div>
          <h1 style={{margin:0,fontSize:24,fontWeight:800,color:DARK}}>Content Wall</h1>
          <div style={{fontSize:13,color:MUTED,marginTop:4}}>Every post + reel where {activeBrand || 'this brand'} was tagged. Auto-detected within 15 min · {filtered.length} {filtered.length===1?'item':'items'}{showHidden?' (hidden)':''}</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search caption or handle…"
            style={{padding:"8px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",width:260,boxSizing:"border-box"}}/>
          <button onClick={()=>setShowFilters(v=>!v)}
            style={{border:`1px solid ${showFilters?G:BORDER}`,background:showFilters?G+'15':"#fff",color:showFilters?G:DARK,borderRadius:8,padding:"8px 14px",fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
            Filters
          </button>
          <button onClick={()=>setShowManualModal(true)}
            style={{border:"none",background:DARK,color:"#fff",borderRadius:8,padding:"8px 14px",fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
            + Add manually
          </button>
        </div>
      </div>

      {showManualModal && (
        <Modal title="Add post to Content Wall" subtitle="Story screenshots, manually-found posts, anything not auto-detected" onClose={()=>!manualUploading&&setShowManualModal(false)} width={520}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>File <span style={{color:MUTED,fontWeight:400}}>(image or video, max 50MB)</span></div>
              <input type="file" accept="image/*,video/*"
                onChange={e=>setManualFile(e.target.files?.[0]||null)}
                style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
              {manualFile && (
                <div style={{marginTop:8,padding:8,background:"#f8faf9",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:11,color:MUTED}}>
                  {manualFile.name} · {(manualFile.size/1024/1024).toFixed(2)}MB · {manualFile.type}
                </div>
              )}
            </div>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Creator handle</div>
              <input list="manual-creator-list" value={manualCreatorHandle}
                onChange={e=>{
                  const v = e.target.value;
                  setManualCreatorHandle(v);
                  const match = (allCreators||[]).find(c => (c.handle||"").replace(/^@/,"").toLowerCase() === v.replace(/^@/,"").toLowerCase());
                  setManualCreatorId(match?.id || null);
                }}
                placeholder="@handle" autoFocus
                style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
              <datalist id="manual-creator-list">
                {(allCreators||[]).slice(0,200).map(c => <option key={c.id} value={(c.handle||"").replace(/^@/,"")}>{c.name||""}</option>)}
              </datalist>
              <div style={{fontSize:10,color:MUTED,marginTop:5,lineHeight:1.5}}>
                {manualCreatorId ? "✓ Matched to creator in this brand" : "Not in creators list — will save to Organic Mentions"}
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Format</div>
                <select value={manualFormat} onChange={e=>setManualFormat(e.target.value)}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box"}}>
                  <option value="STORY">Story</option>
                  <option value="REELS">Reel</option>
                  <option value="FEED">Feed post</option>
                  <option value="AD">Ad</option>
                </select>
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>IG URL <span style={{color:MUTED,fontWeight:400}}>(optional)</span></div>
                <input value={manualPermalink} onChange={e=>setManualPermalink(e.target.value)}
                  placeholder="https://instagram.com/..."
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
              </div>
            </div>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Caption <span style={{color:MUTED,fontWeight:400}}>(optional)</span></div>
              <textarea value={manualCaption} onChange={e=>setManualCaption(e.target.value)} rows={3}
                placeholder="What did they write?"
                style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <button disabled={manualUploading} onClick={()=>setShowManualModal(false)}
                style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 18px",fontSize:13,cursor:manualUploading?"not-allowed":"pointer"}}>Cancel</button>
              <button disabled={manualUploading||!manualFile||(!manualCreatorHandle.trim())} onClick={submitManualAdd}
                style={{background:manualUploading||!manualFile||!manualCreatorHandle.trim()?"#f0f0f0":DARK,color:manualUploading||!manualFile||!manualCreatorHandle.trim()?"#9ab8a6":G,border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:manualUploading?"wait":(!manualFile||!manualCreatorHandle.trim())?"not-allowed":"pointer"}}>{manualUploading?"Uploading…":"Add to wall"}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Segment pills: All / Our creators (matched) / Organic (not in roster).
          Counts reflect the current search + filter state but exclude the
          segment filter itself so the numbers stay meaningful as a switch. */}
      <div style={{display:"flex",gap:6,marginBottom:14,padding:4,background:"#f3f4f6",borderRadius:10,width:"fit-content"}}>
        {[
          {key:"all",     label:"All",          count:segCounts.all},
          {key:"matched", label:"Our creators", count:segCounts.matched},
          {key:"organic", label:"Organic",      count:segCounts.organic},
        ].map(opt => {
          const active = segment === opt.key;
          return (
            <button key={opt.key} onClick={()=>setSegment(opt.key)}
              style={{
                padding:"7px 14px",border:"none",borderRadius:7,cursor:"pointer",
                background:active?"#fff":"transparent",
                color:active?DARK:MUTED,
                fontWeight:active?700:500,fontSize:12.5,
                boxShadow:active?"0 1px 3px rgba(0,0,0,0.08)":"none",
                display:"flex",alignItems:"center",gap:7,
                transition:"all .12s",
              }}>
              {opt.label}
              <span style={{
                background:active?G+"22":"transparent",color:active?G:MUTED,
                padding:"1px 7px",borderRadius:10,fontSize:11,fontWeight:700,
              }}>{opt.count}</span>
            </button>
          );
        })}
      </div>

      {showFilters && (
        <Card style={{padding:16,marginBottom:16,display:"flex",gap:18,flexWrap:"wrap",alignItems:"center"}}>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <div style={{fontSize:11,fontWeight:600,color:MUTED}}>Posted</div>
            <select value={dateRange} onChange={e=>setDateRange(e.target.value)} style={{padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,minWidth:140}}>
              <option value="all">All time</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="custom">Custom range…</option>
            </select>
          </div>
          {dateRange === 'custom' && (<>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              <div style={{fontSize:11,fontWeight:600,color:MUTED}}>From</div>
              <input type="date" value={customDateFrom} onChange={e=>setCustomDateFrom(e.target.value)}
                style={{padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,minWidth:140}}/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              <div style={{fontSize:11,fontWeight:600,color:MUTED}}>To</div>
              <input type="date" value={customDateTo} onChange={e=>setCustomDateTo(e.target.value)}
                style={{padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,minWidth:140}}/>
            </div>
          </>)}
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <div style={{fontSize:11,fontWeight:600,color:MUTED}}>Sort by</div>
            <div style={{display:"flex",gap:4}}>
              <select value={sortKey === 'views' ? 'engagement' : sortKey} onChange={e=>setSortKey(e.target.value)}
                style={{padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,minWidth:140}}>
                <option value="posted_at">Date posted</option>
                <option value="likes">Likes</option>
                <option value="comments">Comments</option>
                <option value="engagement">Engagement (likes + comments)</option>
              </select>
              <button onClick={()=>setSortDir(d=>d==='desc'?'asc':'desc')}
                title={sortDir==='desc'?'High to low':'Low to high'}
                style={{padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,background:"#fff",cursor:"pointer",minWidth:36}}>{sortDir==='desc'?'↓':'↑'}</button>
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <div style={{fontSize:11,fontWeight:600,color:MUTED}}>Format</div>
            <select value={mediaProductType} onChange={e=>setMediaProductType(e.target.value)} style={{padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,minWidth:140}}>
              <option value="all">All formats</option><option value="FEED">Feed posts</option><option value="REELS">Reels</option><option value="STORY">Stories</option><option value="AD">Ads</option>
            </select>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <div style={{fontSize:11,fontWeight:600,color:MUTED}}>Media type</div>
            <select value={mediaType} onChange={e=>setMediaType(e.target.value)} style={{padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,minWidth:140}}>
              <option value="all">All media</option><option value="IMAGE">Image</option><option value="VIDEO">Video</option><option value="CAROUSEL_ALBUM">Carousel</option>
            </select>
          </div>
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:DARK,cursor:"pointer",marginLeft:"auto"}}>
            <input type="checkbox" checked={showHidden} onChange={e=>setShowHidden(e.target.checked)}/>
            Show hidden posts
          </label>
          <button onClick={()=>{setDateRange("all");setMediaType("all");setMediaProductType("all");setSelectedCreatorIds([]);setShowHidden(false);setSearch("");}}
            style={{border:`1px solid ${BORDER}`,background:"#fff",color:MUTED,borderRadius:7,padding:"7px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>Reset</button>
        </Card>
      )}

      {loading ? (
        <div style={{padding:40,textAlign:"center",color:MUTED,fontSize:13}}>Loading…</div>
      ) : filtered.length === 0 ? (
        <Card style={{padding:48,textAlign:"center"}}>
          <div style={{fontSize:42,marginBottom:8}}>📥</div>
          <div style={{fontWeight:700,fontSize:16,color:DARK,marginBottom:5}}>Nothing tagged yet</div>
          <div style={{fontSize:13,color:MUTED,maxWidth:380,margin:"0 auto",lineHeight:1.6}}>When creators tag {brand?.instagram?`@${String(brand.instagram).replace(/^@/,'')}`:'this brand'} in a feed post, reel, or comment, you'll see them here. Detection runs every 15 minutes.</div>
        </Card>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))",gap:14}}>
          {filtered.map(p => {
            const badge = mediaProductBadge(p);
            // Carousel pager state. carousel_children is a JSONB array of
            // {url, thumbnail_url, media_type} stored at insert time. When
            // present, render left/right buttons + slide indicator so operator
            // can flip through and see exactly which slide tagged the brand.
            const slides = Array.isArray(p.carousel_children) ? p.carousel_children : null;
            const slideCount = slides?.length ?? 0;
            // Default to the slide that actually has the brand tagged (FB Login
            // flow exposes children{tagged_users} so we can resolve this). Falls
            // back to slide 0 for IG Login or when tag info isn't available.
            const defaultIdx = (typeof p.carousel_tagged_slide_index === 'number' && p.carousel_tagged_slide_index >= 0 && p.carousel_tagged_slide_index < slideCount) ? p.carousel_tagged_slide_index : 0;
            const curIdx = (carouselIdx[p.id] ?? defaultIdx) % Math.max(1, slideCount);
            const curSlide = slides && slideCount > 0 ? slides[curIdx] : null;
            // Prefer current slide's url, fall back to the post's thumbnail.
            const imgSrc = curSlide?.url || curSlide?.thumbnail_url || p.thumbnail_url;
            const isCurSlideVideo = curSlide
              ? String(curSlide.media_type || '').toUpperCase() === 'VIDEO'
              : (p.media_type === 'VIDEO' || p.media_product_type === 'REELS' || p.media_product_type === 'STORY');
            const advance = (dir) => (e) => {
              e.preventDefault();
              e.stopPropagation();
              setCarouselIdx(prev => ({...prev, [p.id]: (curIdx + dir + slideCount) % slideCount}));
            };
            return (
              <Card key={p.id} style={{padding:0,overflow:"hidden",display:"flex",flexDirection:"column",position:"relative"}}>
                <div style={{padding:"12px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1px solid ${BORDER}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                    <div style={{width:32,height:32,borderRadius:"50%",background:"#e8f0eb",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:G,flexShrink:0}}>{(p.creator_handle||'?').charAt(0).toUpperCase()}</div>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{fontWeight:700,fontSize:13,color:DARK,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>@{p.creator_handle}</div>
                      <div style={{fontSize:11,color:MUTED,display:"flex",alignItems:"center",gap:5}}>
                        <span style={{background:badge.color+'20',color:badge.color,padding:"1px 6px",borderRadius:5,fontWeight:600,fontSize:10}}>{badge.label}</span>
                        {fmtDate(p.posted_at)}
                      </div>
                    </div>
                  </div>
                  <button onClick={(e)=>{e.stopPropagation();setOpenMenuId(openMenuId===p.id?null:p.id);}} style={{border:"none",background:"transparent",cursor:"pointer",color:MUTED,padding:4,borderRadius:6,fontSize:18,lineHeight:1}}>⋮</button>
                  {openMenuId === p.id && (
                    <div style={{position:"absolute",top:42,right:8,background:"#fff",border:`1px solid ${BORDER}`,borderRadius:8,boxShadow:"0 8px 20px rgba(0,0,0,0.08)",padding:6,zIndex:10,minWidth:200}}>
                      <button onClick={()=>jumpToCreator(p)} style={{display:"block",width:"100%",textAlign:"left",border:"none",background:"transparent",padding:"7px 10px",fontSize:12,color:DARK,cursor:"pointer",borderRadius:5}}>Open creator profile</button>
                      {p.permalink && <a href={p.permalink} target="_blank" rel="noopener noreferrer" onClick={()=>setOpenMenuId(null)} style={{display:"block",padding:"7px 10px",fontSize:12,color:DARK,textDecoration:"none",borderRadius:5}}>Open on Instagram ↗</a>}
                      {/* Organic posts get an Assign action. Matched posts skip this. */}
                      {!p.creator_id && (
                        <button onClick={()=>{setOpenMenuId(null);setAssignPost(p);setAssignCreatorId("");}} style={{display:"block",width:"100%",textAlign:"left",border:"none",background:"transparent",padding:"7px 10px",fontSize:12,color:DARK,cursor:"pointer",borderRadius:5}}>Assign to creator…</button>
                      )}
                      {/* Usage rights actions — only for matched creators (we have an email to send to). */}
                      {p.creator_id && !p.usage_rights_status && (
                        <button onClick={()=>openUR(p)} style={{display:"block",width:"100%",textAlign:"left",border:"none",background:"transparent",padding:"7px 10px",fontSize:12,color:G,cursor:"pointer",borderRadius:5,fontWeight:600}}>🎬 Request usage rights</button>
                      )}
                      {p.creator_id && p.usage_rights_status === "requested" && (<>
                        <button onClick={()=>flipURStatus(p, "granted")} style={{display:"block",width:"100%",textAlign:"left",border:"none",background:"transparent",padding:"7px 10px",fontSize:12,color:G,cursor:"pointer",borderRadius:5}}>Mark UR granted</button>
                        <button onClick={()=>flipURStatus(p, "denied")} style={{display:"block",width:"100%",textAlign:"left",border:"none",background:"transparent",padding:"7px 10px",fontSize:12,color:"#EF4444",cursor:"pointer",borderRadius:5}}>Mark UR denied</button>
                      </>)}
                      {p.hidden_at
                        ? <button onClick={()=>unhide(p)} style={{display:"block",width:"100%",textAlign:"left",border:"none",background:"transparent",padding:"7px 10px",fontSize:12,color:DARK,cursor:"pointer",borderRadius:5}}>Unhide</button>
                        : <button onClick={()=>hide(p)} style={{display:"block",width:"100%",textAlign:"left",border:"none",background:"transparent",padding:"7px 10px",fontSize:12,color:"#EF4444",cursor:"pointer",borderRadius:5}}>Hide from wall</button>
                      }
                    </div>
                  )}
                </div>
                {/* Media slot. Video cards (.mp4 sources) keep inline playback
                    so the operator can preview without opening the modal; an
                    "expand" button in the corner opens the detail modal for
                    full caption + actions. Image/story cards open the detail
                    modal on click (replacing the old "jump to IG permalink"
                    behavior — modal has its own Open-on-IG button). */}
                {(() => {
                  const openDetail = () => { setDetailIdx(curIdx); setDetailPost(p); };
                  // Same video-source priority chain as the detail modal: prefer
                  // a direct .mp4 in imgSrc; fall back to streaming through the
                  // creator-post-video edge function when we have a drive_file_id.
                  const inlineMp4 = isCurSlideVideo && typeof imgSrc === 'string' && /\.mp4($|\?)/i.test(imgSrc) ? imgSrc : null;
                  const driveStreamUrl = isCurSlideVideo && p.drive_file_id
                    ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/creator-post-video?post_id=${encodeURIComponent(p.id)}`
                    : null;
                  const cardVideoSrc = inlineMp4 || driveStreamUrl;
                  const isPlayableVideo = !!cardVideoSrc;
                  if (isPlayableVideo) {
                    // Poster: prefer slide thumbnail, then post thumbnail .jpg.
                    // Avoid using the video file as its own poster.
                    const poster = (curSlide && curSlide.thumbnail_url && curSlide.thumbnail_url !== cardVideoSrc)
                      ? curSlide.thumbnail_url
                      : (typeof p.thumbnail_url === 'string' && !/\.mp4($|\?)/i.test(p.thumbnail_url) ? p.thumbnail_url : undefined);
                    return (
                      <div style={{height:300,background:"#000",position:"relative"}}>
                        <video
                          ref={el => {
                            if (el) videoRefs.current.set(p.id, el);
                            else videoRefs.current.delete(p.id);
                          }}
                          src={cardVideoSrc}
                          poster={poster}
                          controls
                          playsInline
                          preload="metadata"
                          onPlay={() => handleVideoPlay(p.id)}
                          style={{width:"100%",height:"100%",objectFit:"cover",display:"block",background:"#000"}}
                        />
                        <button onClick={openDetail} aria-label="Open detail"
                          title="Open details"
                          style={{position:"absolute",top:8,right:8,width:30,height:30,borderRadius:7,border:"none",background:"rgba(0,0,0,0.55)",color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2}}>
                          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                        </button>
                        {slideCount > 1 && (<>
                          <button onClick={advance(-1)} aria-label="Previous slide"
                            style={{position:"absolute",top:"50%",left:8,transform:"translateY(-50%)",width:30,height:30,borderRadius:"50%",border:"none",background:"rgba(0,0,0,0.55)",color:"#fff",fontSize:16,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1,zIndex:2}}>‹</button>
                          <button onClick={advance(1)} aria-label="Next slide"
                            style={{position:"absolute",top:"50%",right:8,transform:"translateY(-50%)",width:30,height:30,borderRadius:"50%",border:"none",background:"rgba(0,0,0,0.55)",color:"#fff",fontSize:16,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1,zIndex:2}}>›</button>
                          <div style={{position:"absolute",top:8,left:"50%",transform:"translateX(-50%)",background:"rgba(0,0,0,0.6)",color:"#fff",fontSize:10,fontWeight:600,padding:"3px 8px",borderRadius:10,letterSpacing:0.4,display:"flex",alignItems:"center",gap:5,zIndex:2}}>
                            {curIdx + 1} / {slideCount}
                            {typeof p.carousel_tagged_slide_index === 'number' && p.carousel_tagged_slide_index === curIdx && (
                              <span style={{background:G,color:"#000",padding:"0 5px",borderRadius:8,fontWeight:700,fontSize:9,textTransform:"uppercase",letterSpacing:0.5}}>Tagged</span>
                            )}
                          </div>
                        </>)}
                      </div>
                    );
                  }
                  return (
                <div onClick={openDetail}
                  style={{display:"block",height:300,background:"#f3f4f6",position:"relative",cursor:"pointer"}}>
                  {imgSrc ? (
                    <img src={imgSrc} alt={p.caption||p.creator_handle}
                      style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}
                      onError={(e)=>{e.target.style.display='none';}}/>
                  ) : (
                    <div style={{height:"100%",display:"flex",alignItems:"center",justifyContent:"center",color:MUTED,fontSize:13,padding:20,textAlign:"center"}}>
                      {p.caption ? p.caption.slice(0, 120) + (p.caption.length>120?'…':'') : (p.media_product_type === 'STORY' ? 'Story (video unavailable)' : 'No preview available')}
                    </div>
                  )}
                  {isCurSlideVideo && (
                    <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:48,height:48,borderRadius:"50%",background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <svg width={20} height={20} viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                  )}
                  {slideCount > 1 && (<>
                    <button onClick={advance(-1)} aria-label="Previous slide"
                      style={{position:"absolute",top:"50%",left:8,transform:"translateY(-50%)",width:30,height:30,borderRadius:"50%",border:"none",background:"rgba(0,0,0,0.55)",color:"#fff",fontSize:16,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>‹</button>
                    <button onClick={advance(1)} aria-label="Next slide"
                      style={{position:"absolute",top:"50%",right:8,transform:"translateY(-50%)",width:30,height:30,borderRadius:"50%",border:"none",background:"rgba(0,0,0,0.55)",color:"#fff",fontSize:16,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>›</button>
                    <div style={{position:"absolute",bottom:8,left:"50%",transform:"translateX(-50%)",background:"rgba(0,0,0,0.6)",color:"#fff",fontSize:10,fontWeight:600,padding:"3px 8px",borderRadius:10,letterSpacing:0.4,display:"flex",alignItems:"center",gap:5}}>
                      {curIdx + 1} / {slideCount}
                      {typeof p.carousel_tagged_slide_index === 'number' && p.carousel_tagged_slide_index === curIdx && (
                        <span style={{background:G,color:"#000",padding:"0 5px",borderRadius:8,fontWeight:700,fontSize:9,textTransform:"uppercase",letterSpacing:0.5}}>Tagged</span>
                      )}
                    </div>
                  </>)}
                </div>
                  );
                })()}
                {(p.likes_count != null || p.comments_count != null || p.usage_rights_status) && (
                  <div style={{padding:"8px 14px",fontSize:11,color:MUTED,display:"flex",gap:12,borderTop:`1px solid ${BORDER}`,alignItems:"center"}}>
                    {p.likes_count != null && <span>♥ {p.likes_count.toLocaleString()}</span>}
                    {p.comments_count != null && <span>💬 {p.comments_count.toLocaleString()}</span>}
                    {p.views_count != null && <span>▶ {p.views_count.toLocaleString()}</span>}
                    {p.usage_rights_status && (
                      <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:5,background:
                        p.usage_rights_status === "granted" ? G+"20" :
                        p.usage_rights_status === "denied" ? "#EF444420" :
                        "#F59E0B20",color:
                        p.usage_rights_status === "granted" ? "#166534" :
                        p.usage_rights_status === "denied" ? "#991B1B" :
                        "#92400E",border:`1px solid ${
                          p.usage_rights_status === "granted" ? G+"50" :
                          p.usage_rights_status === "denied" ? "#EF444450" :
                          "#F59E0B50"}`}}>UR: {p.usage_rights_status}</span>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {!loading && filtered.length > 0 && totalRows !== null && posts.length < totalRows && (
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,marginTop:18}}>
          <div style={{fontSize:12,color:MUTED}}>Showing {posts.length} of {totalRows} posts</div>
          <button onClick={()=>setLoadLimit(n => n + PAGE_SIZE)}
            style={{padding:"9px 18px",border:`1px solid ${BORDER}`,borderRadius:8,background:"#fff",fontSize:13,fontWeight:600,color:DARK,cursor:"pointer"}}>
            Load {Math.min(PAGE_SIZE, totalRows - posts.length)} more
          </button>
        </div>
      )}

      {detailPost && (() => {
        const p = detailPost;
        const badge = mediaProductBadge(p);
        const slides = Array.isArray(p.carousel_children) ? p.carousel_children : null;
        const slideCount = slides?.length ?? 0;
        const idx = slideCount > 0 ? (detailIdx % slideCount + slideCount) % slideCount : 0;
        const slide = slides && slideCount > 0 ? slides[idx] : null;
        const src = slide?.url || slide?.thumbnail_url || p.thumbnail_url;
        const isVideoSlide = slide
          ? String(slide.media_type || '').toUpperCase() === 'VIDEO'
          : (p.media_type === 'VIDEO' || p.media_product_type === 'REELS' || p.media_product_type === 'STORY');
        // Video source priority:
        //   1. src is already a playable .mp4 (Storage-backed stories) — use directly
        //   2. We have a drive_file_id — stream through creator-post-video edge function
        //   3. Neither — fall back to image render
        const inlineMp4 = isVideoSlide && typeof src === 'string' && /\.mp4($|\?)/i.test(src) ? src : null;
        const driveStreamUrl = isVideoSlide && p.drive_file_id
          ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/creator-post-video?post_id=${encodeURIComponent(p.id)}`
          : null;
        const videoSrc = inlineMp4 || driveStreamUrl;
        const isPlayableVideo = !!videoSrc;
        // Poster: prefer the slide-level thumbnail (carousel inner), then post
        // thumbnail (always a .jpg for Reels), but never use the video file as
        // its own poster.
        const poster = (slide && slide.thumbnail_url && slide.thumbnail_url !== videoSrc)
          ? slide.thumbnail_url
          : (typeof p.thumbnail_url === 'string' && !/\.mp4($|\?)/i.test(p.thumbnail_url) ? p.thumbnail_url : undefined);
        return (
          <Modal title={`@${p.creator_handle}`}
            subtitle={`${badge.label} · ${fmtDate(p.posted_at)}${slideCount>1?` · ${idx+1} / ${slideCount}`:''}`}
            onClose={() => setDetailPost(null)} width={760}>
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {/* Media */}
              <div style={{background:"#000",borderRadius:10,overflow:"hidden",position:"relative",aspectRatio:"4/5",maxHeight:520}}>
                {isPlayableVideo ? (
                  <video src={videoSrc} poster={poster} controls playsInline preload="metadata"
                    style={{width:"100%",height:"100%",objectFit:"contain",display:"block",background:"#000"}}/>
                ) : src ? (
                  <>
                    <img src={src} alt={p.caption || p.creator_handle}
                      style={{width:"100%",height:"100%",objectFit:"contain",display:"block",background:"#000",opacity:isVideoSlide?0.55:1}}
                      onError={(e)=>{e.target.style.display='none';}}/>
                    {/* Video-shaped post but no playable source — happens when
                        tags-poll inserted the row but the Drive upload failed
                        on first poll, and /me/tags has since paginated past
                        the media (Meta's 24h signed-URL window closed). The
                        original file is unrecoverable from our side; surface
                        that clearly + funnel the operator straight to IG. */}
                    {isVideoSlide && !isPlayableVideo && (
                      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:24,textAlign:"center",pointerEvents:"none"}}>
                        <div style={{background:"rgba(0,0,0,0.78)",color:"#fff",padding:"14px 18px",borderRadius:10,maxWidth:420,pointerEvents:"auto"}}>
                          <div style={{fontSize:13,fontWeight:700,marginBottom:6}}>⚠ Video file not archived</div>
                          <div style={{fontSize:11,opacity:0.85,lineHeight:1.5,marginBottom:10}}>
                            tags-poll inserted the row but the Drive upload failed on the first attempt. Try to recover — if Meta still surfaces this Reel via <code style={{fontFamily:"monospace",fontSize:10,padding:"1px 4px",background:"rgba(255,255,255,0.12)",borderRadius:3}}>/me/tags</code> we can grab a fresh signed URL.
                          </div>
                          <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
                            <button onClick={() => tryRecoverArchive(p)} disabled={reArchiving}
                              style={{background:G,color:DARK,padding:"7px 14px",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:reArchiving?"wait":"pointer",opacity:reArchiving?0.6:1}}>
                              {reArchiving ? 'Recovering…' : '↻ Try to recover'}
                            </button>
                            {p.permalink && (
                              <a href={p.permalink} target="_blank" rel="noopener noreferrer"
                                style={{display:"inline-block",background:"rgba(255,255,255,0.15)",color:"#fff",padding:"7px 14px",borderRadius:7,fontSize:12,fontWeight:700,textDecoration:"none"}}>Watch on Instagram ↗</a>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",color:"#9ca3af",fontSize:13,padding:24,textAlign:"center"}}>
                    {p.media_product_type === 'STORY' ? 'Story media no longer available (lookaside URL expired)' : 'No preview available'}
                  </div>
                )}
                {slideCount > 1 && (<>
                  <button onClick={()=>setDetailIdx(i => (i - 1 + slideCount) % slideCount)} aria-label="Previous slide"
                    style={{position:"absolute",top:"50%",left:12,transform:"translateY(-50%)",width:36,height:36,borderRadius:"50%",border:"none",background:"rgba(0,0,0,0.55)",color:"#fff",fontSize:18,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>‹</button>
                  <button onClick={()=>setDetailIdx(i => (i + 1) % slideCount)} aria-label="Next slide"
                    style={{position:"absolute",top:"50%",right:12,transform:"translateY(-50%)",width:36,height:36,borderRadius:"50%",border:"none",background:"rgba(0,0,0,0.55)",color:"#fff",fontSize:18,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>›</button>
                  <div style={{position:"absolute",bottom:12,left:"50%",transform:"translateX(-50%)",background:"rgba(0,0,0,0.6)",color:"#fff",fontSize:11,fontWeight:600,padding:"4px 10px",borderRadius:12,display:"flex",alignItems:"center",gap:6}}>
                    {idx + 1} / {slideCount}
                    {typeof p.carousel_tagged_slide_index === 'number' && p.carousel_tagged_slide_index === idx && (
                      <span style={{background:G,color:"#000",padding:"0 6px",borderRadius:8,fontWeight:700,fontSize:9,textTransform:"uppercase",letterSpacing:0.5}}>Tagged</span>
                    )}
                  </div>
                </>)}
              </div>
              {/* Engagement row */}
              {(p.likes_count != null || p.comments_count != null || p.views_count != null) && (
                <div style={{display:"flex",gap:18,fontSize:13,color:DARK,fontWeight:600}}>
                  {p.likes_count != null && <span>♥ {p.likes_count.toLocaleString()} likes</span>}
                  {p.comments_count != null && <span>💬 {p.comments_count.toLocaleString()} comments</span>}
                  {p.views_count != null && <span>▶ {p.views_count.toLocaleString()} views</span>}
                </div>
              )}
              {/* Caption */}
              {p.caption && (
                <div style={{fontSize:13,color:DARK,lineHeight:1.65,whiteSpace:"pre-wrap",maxHeight:200,overflowY:"auto",padding:"12px 14px",background:"#f8faf9",borderRadius:9,border:`1px solid ${BORDER}`}}>
                  {p.caption}
                </div>
              )}
              {/* Meta row */}
              <div style={{display:"flex",gap:8,fontSize:11,color:MUTED,flexWrap:"wrap"}}>
                {p.creator_id ? <span style={{background:G+"20",color:"#166534",padding:"3px 9px",borderRadius:10,fontWeight:600}}>From your creator @{p.creator_handle}</span>
                              : <span style={{background:"#FEF3C7",color:"#92400E",padding:"3px 9px",borderRadius:10,fontWeight:600}}>Organic — @{p.creator_handle} isn't on your roster</span>}
                {p.usage_rights_status && (
                  <span style={{padding:"3px 9px",borderRadius:10,fontWeight:600,
                    background: p.usage_rights_status === "granted" ? G+"20" : p.usage_rights_status === "denied" ? "#FEE2E2" : "#FEF3C7",
                    color: p.usage_rights_status === "granted" ? "#166534" : p.usage_rights_status === "denied" ? "#991B1B" : "#92400E",
                  }}>UR: {p.usage_rights_status}</span>
                )}
              </div>
              {/* Action row */}
              <div style={{display:"flex",gap:8,flexWrap:"wrap",paddingTop:6,borderTop:`1px solid ${BORDER}`,marginTop:2}}>
                {/* Download — available regardless of UR status. Storage-backed
                    media downloads directly (public bucket); Drive-archived
                    opens the Drive viewer (operator downloads from there
                    since Drive blocks direct anonymous downloads on shared
                    files). Fresh lookaside URLs fall through to a same-window
                    download attempt — works for media within IG's ~24h
                    signed-URL window. */}
                {(() => {
                  const storageSrc = (typeof src === 'string' && src.includes('/storage/v1/object/public/')) ? src
                    : (typeof p.thumbnail_url === 'string' && p.thumbnail_url.includes('/storage/v1/object/public/')) ? p.thumbnail_url
                    : null;
                  const driveId = p.drive_file_id;
                  const fallbackSrc = typeof src === 'string' && /^https?:\/\//i.test(src) ? src : null;
                  const ext = (isPlayableVideo ? 'mp4' : (isVideoSlide ? 'mp4' : 'jpg'));
                  const dateStr = p.posted_at ? new Date(p.posted_at).toISOString().slice(0,10) : 'undated';
                  const fname = `@${p.creator_handle}_${(p.media_product_type||'post').toLowerCase()}_${dateStr}.${ext}`;
                  if (storageSrc) {
                    return (<a href={storageSrc} download={fname}
                      style={{padding:"9px 14px",border:"none",borderRadius:8,background:DARK,color:G,fontSize:13,textDecoration:"none",fontWeight:700,display:"flex",alignItems:"center",gap:6}}>⬇ Download</a>);
                  }
                  if (driveId) {
                    return (<a href={`https://drive.google.com/file/d/${driveId}/view`} target="_blank" rel="noopener noreferrer"
                      title="Opens this post in Drive — download from the Drive viewer"
                      style={{padding:"9px 14px",border:"none",borderRadius:8,background:DARK,color:G,fontSize:13,textDecoration:"none",fontWeight:700,display:"flex",alignItems:"center",gap:6}}>⬇ Download (via Drive ↗)</a>);
                  }
                  if (fallbackSrc) {
                    return (<a href={fallbackSrc} download={fname} target="_blank" rel="noopener noreferrer"
                      title="Direct download from Instagram's signed URL. Works only while the URL is fresh (~24h)."
                      style={{padding:"9px 14px",border:"none",borderRadius:8,background:DARK,color:G,fontSize:13,textDecoration:"none",fontWeight:700,display:"flex",alignItems:"center",gap:6}}>⬇ Download</a>);
                  }
                  return (<button disabled title="Media not archived yet — check back after the next 15-min poll, or open in Instagram and save from there"
                    style={{padding:"9px 14px",border:`1px solid ${BORDER}`,borderRadius:8,background:"#f3f4f6",fontSize:13,color:MUTED,cursor:"not-allowed",fontWeight:600}}>⬇ Download unavailable</button>);
                })()}
                {p.permalink && (
                  <a href={p.permalink} target="_blank" rel="noopener noreferrer"
                    style={{padding:"9px 14px",border:`1px solid ${BORDER}`,borderRadius:8,background:"#fff",fontSize:13,color:DARK,textDecoration:"none",fontWeight:600,display:"flex",alignItems:"center",gap:6}}>Open on Instagram ↗</a>
                )}
                <button onClick={() => { setDetailPost(null); jumpToCreator(p); }}
                  style={{padding:"9px 14px",border:`1px solid ${BORDER}`,borderRadius:8,background:"#fff",fontSize:13,color:DARK,cursor:"pointer",fontWeight:600}}>Open creator profile</button>
                {!p.creator_id && (
                  <button onClick={() => { setDetailPost(null); setAssignPost(p); setAssignCreatorId(""); }}
                    style={{padding:"9px 14px",border:`1px solid ${BORDER}`,borderRadius:8,background:"#fff",fontSize:13,color:DARK,cursor:"pointer",fontWeight:600}}>Assign to creator…</button>
                )}
                {p.creator_id && !p.usage_rights_status && (
                  <button onClick={() => { setDetailPost(null); openUR(p); }}
                    style={{padding:"9px 14px",border:"none",borderRadius:8,background:G,fontSize:13,color:DARK,cursor:"pointer",fontWeight:700}}>🎬 Request usage rights</button>
                )}
                {p.creator_id && p.usage_rights_status === "requested" && (<>
                  <button onClick={() => { flipURStatus(p, "granted"); setDetailPost(null); }}
                    style={{padding:"9px 14px",border:`1px solid ${G}50`,borderRadius:8,background:G+"15",fontSize:13,color:"#166534",cursor:"pointer",fontWeight:600}}>Mark UR granted</button>
                  <button onClick={() => { flipURStatus(p, "denied"); setDetailPost(null); }}
                    style={{padding:"9px 14px",border:"1px solid #FECACA",borderRadius:8,background:"#FEF2F2",fontSize:13,color:"#991B1B",cursor:"pointer",fontWeight:600}}>Mark UR denied</button>
                </>)}
                <div style={{marginLeft:"auto"}}>
                  {p.hidden_at
                    ? <button onClick={() => { unhide(p); setDetailPost(null); }} style={{padding:"9px 14px",border:`1px solid ${BORDER}`,borderRadius:8,background:"#fff",fontSize:13,color:DARK,cursor:"pointer",fontWeight:600}}>Unhide</button>
                    : <button onClick={() => { hide(p); setDetailPost(null); }} style={{padding:"9px 14px",border:"1px solid #FECACA",borderRadius:8,background:"#FEF2F2",fontSize:13,color:"#991B1B",cursor:"pointer",fontWeight:600}}>Hide from wall</button>}
                </div>
              </div>
            </div>
          </Modal>
        );
      })()}

      {assignPost && (
        <Modal title="Assign post to creator" subtitle={`Currently shown under Organic Mentions for @${assignPost.creator_handle}`} onClose={()=>setAssignPost(null)} width={460}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div style={{fontSize:13,color:MUTED,lineHeight:1.55}}>
              Pick which creator in your roster posted this. Once assigned, future posts from @{assignPost.creator_handle} will auto-link to them (via handle match).
            </div>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Creator</div>
              <select value={assignCreatorId} onChange={e=>setAssignCreatorId(e.target.value)} autoFocus
                style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box"}}>
                <option value="">— pick a creator —</option>
                {(allCreators||[]).slice().sort((a,b)=>(a.name||"").localeCompare(b.name||"")).map(c=>(
                  <option key={c.id} value={c.id}>{(c.handle||"").replace(/^@/,"")} · {c.name||""}</option>
                ))}
              </select>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <button onClick={()=>setAssignPost(null)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 18px",fontSize:13,cursor:"pointer"}}>Cancel</button>
              <button disabled={!assignCreatorId} onClick={submitAssign}
                style={{background:!assignCreatorId?"#f0f0f0":DARK,color:!assignCreatorId?"#9ab8a6":G,border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:!assignCreatorId?"not-allowed":"pointer"}}>Assign</button>
            </div>
          </div>
        </Modal>
      )}

      {urPost && (
        <Modal title="🎬 Request Usage Rights" subtitle={`For ${urPost.creator_handle}'s ${(urPost.media_product_type||"post").toLowerCase()}`} onClose={()=>!urSending&&setUrPost(null)} width={520}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:8}}>Type</div>
              <div style={{display:"flex",gap:6}}>
                {["Repurpose","Whitelist","Both"].map(t =>
                  <button key={t} onClick={()=>setUrType(t)} style={{flex:1,padding:"8px",border:`1px solid ${urType===t?G:BORDER}`,borderRadius:8,background:urType===t?"#f0fdf4":"#fff",color:urType===t?G:DARK,fontSize:12,fontWeight:urType===t?700:400,cursor:"pointer"}}>{t}</button>
                )}
              </div>
            </div>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:8}}>Platforms</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {["Instagram","Facebook","TikTok","YouTube","Email","Website"].map(p => {
                  const on = urPlatforms.includes(p);
                  return <button key={p} onClick={()=>setUrPlatforms(prev=>on?prev.filter(x=>x!==p):[...prev,p])}
                    style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${on?G:BORDER}`,background:on?"#f0fdf4":"#fff",color:on?G:DARK,fontSize:12,fontWeight:on?700:400,cursor:"pointer"}}>{p}</button>;
                })}
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Duration</div>
                <select value={urDuration} onChange={e=>setUrDuration(e.target.value)}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box"}}>
                  <option>3 months</option>
                  <option>6 months</option>
                  <option>12 months</option>
                  <option>Perpetual</option>
                </select>
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Compensation</div>
                <select value={urCompensation} onChange={e=>setUrCompensation(e.target.value)}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box"}}>
                  <option>Gifted</option>
                  <option>Paid</option>
                  <option>Extra product</option>
                </select>
              </div>
            </div>
            {urCompensation === "Paid" && (
              <div>
                <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Amount</div>
                <input value={urPaidAmount} onChange={e=>setUrPaidAmount(e.target.value)} placeholder="e.g. $200 USD"
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
              </div>
            )}
            <div style={{background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8,padding:11,fontSize:11,color:"#1E40AF",lineHeight:1.5}}>
              📧 Will email {(allCreators||[]).find(c=>c.id===urPost.creator_id)?.email || "creator"} asking for {urType.toLowerCase()} rights on {urPlatforms.join(", ")} for {urDuration}.
              {urPost.permalink && <><br/>Post: <a href={urPost.permalink} target="_blank" rel="noopener noreferrer" style={{color:"#1E40AF"}}>{urPost.permalink}</a></>}
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <button disabled={urSending} onClick={()=>setUrPost(null)}
                style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:urSending?"not-allowed":"pointer"}}>Cancel</button>
              <button disabled={urSending||urPlatforms.length===0} onClick={submitUR}
                style={{background:urSending||urPlatforms.length===0?"#f0f0f0":DARK,color:urSending||urPlatforms.length===0?"#9ab8a6":G,border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:urSending?"wait":urPlatforms.length===0?"not-allowed":"pointer"}}>{urSending?"Sending…":"🎬 Send Request"}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Signing state machine panel for an open agreement. Three states:
//   • Draft (not yet signed by brand): show "Sign as brand" form
//   • Brand signed, not yet sent: show "Send to creator" button + creator email confirm
//   • Sent, awaiting creator: show "Awaiting creator signature" + copy link + resend
//   • Fully signed: show both signatures + audit trail
function AgreementSigningPanel({ agreement, onUpdate }) {
  const { user } = useUser();
  const defaultBrandName = user?.fullName
    || [user?.firstName, user?.lastName].filter(Boolean).join(" ")
    || user?.primaryEmailAddress?.emailAddress || "";
  const [brandSignerName, setBrandSignerName] = useState(defaultBrandName);
  const [brandSigMode, setBrandSigMode] = useState("type"); // "type" | "draw"
  const [brandSigImage, setBrandSigImage] = useState(null); // base64 data URL when drawn
  const [signing, setSigning] = useState(false);
  const [sending, setSending] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const brandSigned = !!agreement.brandSignedAt;
  const sentToCreator = !!agreement.sentAt;
  const creatorSigned = !!agreement.creatorSignedAt;
  const pdfUploaded = !!agreement.signedPdfDriveId;
  const signingUrl = agreement.signingToken
    ? `${window.location.origin}/sign/${agreement.signingToken}`
    : null;

  // Brand signs
  const signAsBrand = async () => {
    if (!brandSignerName.trim() || brandSignerName.trim().length < 2) {
      toast("Enter your full name to sign","error"); return;
    }
    setSigning(true);
    const nowIso = new Date().toISOString();
    const brandEmail = user?.primaryEmailAddress?.emailAddress || null;
    const updatePayload = {
      brand_signer_name: brandSignerName.trim(),
      brand_signer_email: brandEmail,
      brand_signed_at: nowIso,
      brand_signer_image: brandSigMode === "draw" ? brandSigImage : null,
    };
    const { error } = await supabase.from('agreements').update(updatePayload).eq('id', agreement.id);
    setSigning(false);
    if (error) { toast(`Couldn't sign: ${error.message}`,'error'); return; }
    onUpdate({ ...agreement, brandSignerName: brandSignerName.trim(), brandSignerEmail: brandEmail, brandSignedAt: nowIso, brandSignerImage: updatePayload.brand_signer_image });
    toast(`✓ Signed as ${brandSignerName.trim()}`);
  };

  // Generate + download signed PDF (client-side, no Drive upload)
  const downloadPdf = () => {
    try {
      const base64 = generateSignedPdf({
        agreement,
        brandName: agreement.brand,
        brandLogoDataUrl: null, // brand logo not currently available in this scope; future: pass through
        brandSignerName: agreement.brandSignerName,
        brandSignedAt: agreement.brandSignedAt,
        brandSignerImage: agreement.brandSignerImage,
        creatorSignerName: agreement.creatorSignerName,
        creatorSignedAt: agreement.creatorSignedAt,
        creatorSignerImage: agreement.creatorSignerImage,
        creatorSignerEmail: agreement.creatorSignerEmail,
        creatorSignerIp: agreement.creatorSignerIp,
        sentAt: agreement.sentAt,
        agreementRef: agreement.agreementRef || agreement.id,
        content: agreement.content,
        collabType: agreement.collabType || "Gifted",
      });
      // Trigger browser download
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${base64}`;
      link.download = `Agreement — ${agreement.creator?.name || agreement.creator?.handle || 'creator'} × ${agreement.brand} — ${new Date(agreement.signedAt || Date.now()).toISOString().slice(0,10)}.pdf`;
      link.click();
      toast("✓ Signed PDF downloaded");
    } catch (e) {
      console.error("PDF generation failed", e);
      toast(`PDF generation failed: ${e.message}`, 'error');
    }
  };

  // Generate signed PDF + upload to Drive
  const saveToDrive = async () => {
    setPdfBusy(true);
    try {
      const base64 = generateSignedPdf({
        agreement,
        brandName: agreement.brand,
        brandLogoDataUrl: null,
        brandSignerName: agreement.brandSignerName,
        brandSignedAt: agreement.brandSignedAt,
        brandSignerImage: agreement.brandSignerImage,
        creatorSignerName: agreement.creatorSignerName,
        creatorSignedAt: agreement.creatorSignedAt,
        creatorSignerImage: agreement.creatorSignerImage,
        creatorSignerEmail: agreement.creatorSignerEmail,
        creatorSignerIp: agreement.creatorSignerIp,
        sentAt: agreement.sentAt,
        agreementRef: agreement.agreementRef || agreement.id,
        content: agreement.content,
        collabType: agreement.collabType || "Gifted",
      });
      const t = await window.Clerk?.session?.getToken();
      const r = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-drive/upload-signed-agreement`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${t}`, 'content-type': 'application/json' },
        body: JSON.stringify({ agreement_id: agreement.id, pdf_base64: base64 }),
      });
      const body = await r.json();
      if (!r.ok) {
        toast(`Drive upload failed: ${body.message || body.error || r.status}`, 'error');
        setPdfBusy(false); return;
      }
      onUpdate({ ...agreement, signedPdfDriveId: body.file_id, signedPdfDriveUrl: body.web_view_link });
      toast(`✓ Saved to Drive — opening file`);
      // Auto-open the Drive file in a new tab so operator sees the result
      if (body.web_view_link) window.open(body.web_view_link, '_blank');
    } catch (e) {
      console.error("Drive upload failed", e);
      toast(`Upload failed: ${e.message}`, 'error');
    }
    setPdfBusy(false);
  };

  // Send to creator (calls Edge Function)
  const sendToCreator = async () => {
    if (!agreement.creator?.email) {
      toast(`No email on file for ${agreement.creator?.name||"this creator"}. Add one on their profile first.`,'error');
      return;
    }
    setSending(true);
    try {
      const t = await window.Clerk?.session?.getToken();
      const r = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-agreement-email`,{
        method:'POST',
        headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},
        body:JSON.stringify({ agreement_id: agreement.id, app_url: window.location.origin }),
      });
      const body = await r.json();
      if (!r.ok) {
        toast(`Send failed: ${body.message||body.error||r.status}`,'error');
        setSending(false); return;
      }
      onUpdate({ ...agreement, signingToken: body.signing_url.split('/sign/')[1], agreementRef: body.agreement_ref, sentAt: new Date().toISOString(), status:'Sent' });
      toast(`✓ Sent to ${body.sent_to}`);
    } catch (e) {
      toast(`Send failed: ${e.message}`,'error');
    }
    setSending(false);
  };

  const copyLink = () => {
    if (!signingUrl) return;
    navigator.clipboard?.writeText(signingUrl);
    toast("Signing link copied");
  };

  const fmtFull = (iso) => iso ? new Date(iso).toLocaleString("en-US",{dateStyle:"long",timeStyle:"short"}) : "—";

  // ─── Card layouts per state ──────────────────────────────────────────
  return (<Card style={{padding:24}}>
    <div style={{fontSize:11,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:14}}>Signatures</div>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16}}>
      {/* Brand signature column */}
      <div style={{background:brandSigned?"#F0FDF4":"#fafafa",border:`1px solid ${brandSigned?G+"40":BORDER}`,borderRadius:10,padding:"14px 16px"}}>
        <div style={{fontSize:10,fontWeight:700,color:brandSigned?"#166534":MUTED,textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>
          {brandSigned?"✓ Signed by brand":"Brand signature"}
        </div>
        {brandSigned ? (<>
          {/* If drawn signature exists, render as image; else typed in handwriting font */}
          {agreement.brandSignerImage
            ? <img src={agreement.brandSignerImage} alt="Brand signature" style={{maxWidth:"100%",height:48,objectFit:"contain",marginBottom:4,background:"#fff",border:`1px solid ${BORDER}`,borderRadius:6}}/>
            : <div style={{fontSize:22,color:DARK,fontFamily:"'Caveat',cursive,sans-serif",fontStyle:"italic",fontWeight:700,marginBottom:4,lineHeight:1.2}}>{agreement.brandSignerName}</div>
          }
          <div style={{fontSize:11,color:MUTED,marginTop:6}}><strong style={{color:DARK,fontWeight:600}}>{agreement.brandSignerName}</strong></div>
          <div style={{fontSize:11,color:MUTED}}>{fmtFull(agreement.brandSignedAt)}</div>
          {agreement.brandSignerEmail&&<div style={{fontSize:11,color:MUTED}}>{agreement.brandSignerEmail}</div>}
        </>) : (<>
          <div style={{fontSize:11,color:MUTED,marginBottom:8,lineHeight:1.4}}>Sign first, then send to the creator.</div>
          <input value={brandSignerName} onChange={e=>setBrandSignerName(e.target.value)} placeholder="Your full name" style={{width:"100%",padding:"8px 11px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box",marginBottom:8}}/>
          {/* Type / Draw mode toggle */}
          <div style={{display:"flex",gap:4,background:"#fff",borderRadius:7,padding:2,marginBottom:8,border:`1px solid ${BORDER}`}}>
            {[
              {k:"type",label:"⌨ Type"},
              {k:"draw",label:"✍ Draw"},
            ].map(m=>(<button key={m.k} onClick={()=>setBrandSigMode(m.k)} style={{flex:1,padding:"5px 8px",border:"none",background:brandSigMode===m.k?DARK:"transparent",color:brandSigMode===m.k?G:MUTED,borderRadius:5,fontSize:11,fontWeight:600,cursor:"pointer"}}>{m.label}</button>))}
          </div>
          {brandSigMode==="type"
            ? <div style={{padding:"10px 12px",background:"#fff",border:`1px dashed ${BORDER}`,borderRadius:7,fontFamily:"'Caveat',cursive,sans-serif",fontStyle:"italic",fontWeight:700,fontSize:22,color:DARK,lineHeight:1.2,minHeight:46,display:"flex",alignItems:"center",marginBottom:8}}>{brandSignerName||<span style={{color:"#c0d4c8",fontSize:13,fontFamily:"inherit"}}>(type your name above to preview)</span>}</div>
            : <div style={{marginBottom:8}}><DrawSignature value={brandSigImage} typedFallback={brandSignerName} onChange={setBrandSigImage} height={80}/></div>
          }
          <button onClick={signAsBrand} disabled={signing||!brandSignerName.trim()||(brandSigMode==="draw"&&!brandSigImage)} style={{width:"100%",background:signing||!brandSignerName.trim()||(brandSigMode==="draw"&&!brandSigImage)?"#e5e7eb":DARK,color:signing||!brandSignerName.trim()||(brandSigMode==="draw"&&!brandSigImage)?MUTED:G,border:"none",borderRadius:7,padding:"8px 12px",fontWeight:700,fontSize:12,cursor:signing||!brandSignerName.trim()||(brandSigMode==="draw"&&!brandSigImage)?"not-allowed":"pointer"}}>{signing?"Signing…":"✍️ Sign as brand"}</button>
        </>)}
      </div>

      {/* Creator signature column */}
      <div style={{background:creatorSigned?"#F0FDF4":"#fafafa",border:`1px solid ${creatorSigned?G+"40":BORDER}`,borderRadius:10,padding:"14px 16px"}}>
        <div style={{fontSize:10,fontWeight:700,color:creatorSigned?"#166534":MUTED,textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>
          {creatorSigned?"✓ Signed by creator":sentToCreator?"Awaiting creator":"Creator signature"}
        </div>
        {creatorSigned ? (<>
          {agreement.creatorSignerImage
            ? <img src={agreement.creatorSignerImage} alt="Creator signature" style={{maxWidth:"100%",height:48,objectFit:"contain",marginBottom:4,background:"#fff",border:`1px solid ${BORDER}`,borderRadius:6}}/>
            : <div style={{fontSize:22,color:DARK,fontFamily:"'Caveat',cursive,sans-serif",fontStyle:"italic",fontWeight:700,marginBottom:4,lineHeight:1.2}}>{agreement.creatorSignerName}</div>
          }
          <div style={{fontSize:11,color:MUTED,marginTop:6}}><strong style={{color:DARK,fontWeight:600}}>{agreement.creatorSignerName}</strong></div>
          <div style={{fontSize:11,color:MUTED}}>{fmtFull(agreement.creatorSignedAt)}</div>
          {agreement.creatorSignerEmail&&<div style={{fontSize:11,color:MUTED}}>{agreement.creatorSignerEmail}</div>}
          {agreement.creatorSignerIp&&<div style={{fontSize:10,color:MUTED,fontFamily:"DM Mono, monospace",marginTop:2}}>IP: {agreement.creatorSignerIp}</div>}
        </>) : sentToCreator ? (<>
          <div style={{fontSize:12,color:DARK,marginBottom:2}}>Sent {fmtFull(agreement.sentAt)}</div>
          <div style={{fontSize:11,color:MUTED,marginBottom:4}}>to {agreement.creator?.email}</div>
          {agreement.creatorViewedAt
            ? <div style={{fontSize:11,color:"#1D4ED8",marginBottom:8}}>👁 Viewed {fmtFull(agreement.creatorViewedAt)} ({agreement.creatorViewCount||1}×)</div>
            : <div style={{fontSize:11,color:"#92400E",marginBottom:8}}>Not yet opened</div>
          }
        </>) : (<>
          <div style={{fontSize:11,color:MUTED,lineHeight:1.4}}>{brandSigned?"Click Send below to email the signing link to the creator.":"Sign first as the brand, then send."}</div>
        </>)}
      </div>
    </div>

    {/* Action bar */}
    {brandSigned && !sentToCreator && (
      <button onClick={sendToCreator} disabled={sending||!agreement.creator?.email} style={{width:"100%",background:sending?"#e5e7eb":G,color:sending?MUTED:DARK,border:"none",borderRadius:9,padding:"11px 16px",fontWeight:700,fontSize:14,cursor:sending||!agreement.creator?.email?"not-allowed":"pointer"}}>
        {sending?"Sending…":!agreement.creator?.email?"⚠ No email on file for creator":`📧 Send to ${agreement.creator.email}`}
      </button>
    )}

    {sentToCreator && !creatorSigned && signingUrl && (
      <div style={{display:"flex",gap:8,alignItems:"center",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:9,padding:"10px 14px"}}>
        <span style={{fontSize:11,color:"#6B7280",fontWeight:600,flexShrink:0}}>Signing link:</span>
        <code style={{fontSize:11,color:"#1D4ED8",fontFamily:"DM Mono, monospace",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{signingUrl}</code>
        <button onClick={copyLink} style={{border:"none",background:"#fff",color:"#1D4ED8",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Copy</button>
        <button onClick={sendToCreator} disabled={sending} style={{border:"none",background:"#fff",color:"#1D4ED8",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:sending?"wait":"pointer"}}>Resend</button>
      </div>
    )}

    {creatorSigned && (<>
      <div style={{background:"#F0FDF4",border:`1px solid ${G}40`,borderRadius:9,padding:"12px 16px",textAlign:"center",marginBottom:10}}>
        <div style={{fontSize:14,fontWeight:700,color:"#166534",marginBottom:3}}>🎉 Agreement fully executed</div>
        <div style={{fontSize:11,color:MUTED}}>Both parties signed. Reference: <span style={{fontFamily:"DM Mono, monospace",color:DARK}}>{agreement.agreementRef||"—"}</span></div>
      </div>
      {/* PDF actions — download for local copy, save-to-Drive for cloud archive */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <button onClick={downloadPdf} style={{flex:1,minWidth:180,background:"#fff",color:DARK,border:`1px solid ${BORDER}`,borderRadius:8,padding:"10px 14px",fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          ⬇ Download signed PDF
        </button>
        {pdfUploaded
          ? <a href={agreement.signedPdfDriveUrl} target="_blank" rel="noreferrer" style={{flex:1,minWidth:180,background:"#F0FDF4",color:"#166534",border:`1px solid ${G}40`,borderRadius:8,padding:"10px 14px",fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,textDecoration:"none"}}>
              ✓ Open in Drive
            </a>
          : <button onClick={saveToDrive} disabled={pdfBusy} style={{flex:1,minWidth:180,background:pdfBusy?"#e5e7eb":G,color:pdfBusy?MUTED:DARK,border:"none",borderRadius:8,padding:"10px 14px",fontSize:13,fontWeight:700,cursor:pdfBusy?"wait":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              {pdfBusy ? "Saving…" : "📁 Save signed PDF to Drive"}
            </button>
        }
      </div>
    </>)}
  </Card>);
}

function Agreements({brandData,activeBrand,agreements,setAgreements,allCreators=[],persistAgreement,removeAgreement,brandId=null}){
  const brand=brandData?.[activeBrand];
  const[showNew,setShowNew]=useState(false);
  const[step,setStep]=useState("creator");
  const[selectedCreator,setSelectedCreator]=useState(null);
  const[creatorSearch,setCreatorSearch]=useState("");
  const[viewingAgreement,setViewingAgreement]=useState(null);
  // Agreement-builder form. All commercial fields default empty — every
  // value comes from operator input, never invented. Legal boilerplate
  // opt-ins (disclosure, warranties, IP licence, termination, indemnification)
  // default ON because they're industry-standard protections, not commercial
  // policy. See src/agreementTemplate.js for the clause text + rendering.
  const[form,setForm]=useState({
    ...DEFAULT_AGREEMENT_FORM,
    // Currency cascades from brand settings — changes to brand.currency in Settings
    // propagate to NEW agreement drafts here. Existing agreements keep their
    // stored currency for historical fidelity (don't retroactively rewrite contracts).
    currency: brand?.currency || DEFAULT_AGREEMENT_FORM.currency,
    brandLegalName: brand?.name || activeBrand || "",
    brandAbn: brand?.abn || "",
  });
  // If operator switches brand or changes brand currency mid-session, reflect that
  // in the open draft form (only when nothing has been typed yet — otherwise we'd
  // surprise them by overwriting their input).
  useEffect(()=>{
    const brandCurrency = brand?.currency;
    if(!brandCurrency) return;
    setForm(f => f.paymentAmount || f.currency !== DEFAULT_AGREEMENT_FORM.currency ? f : {...f, currency: brandCurrency});
  // eslint-disable-next-line
  },[brand?.currency, activeBrand]);
  // Advanced sections start collapsed — operators see core (type, deliverables,
  // compensation, jurisdiction) first; advanced clauses behind a toggle.
  const[showAdvanced,setShowAdvanced]=useState(false);
  const filteredCreators=allCreators.filter(c=>!c.archived&&(c.name.toLowerCase().includes(creatorSearch.toLowerCase())||c.handle.includes(creatorSearch)));
  const fillTemplate=()=>buildAgreementText({form, brand, creator: selectedCreator});
  const createAgreement=async()=>{
    const content=fillTemplate();
    const ag={
      id:null,
      creator:selectedCreator,
      brand:activeBrand,
      collabType:form.collabType,
      status:"Draft",
      created:new Date().toLocaleDateString("en-AU"),
      deliverables:form.deliverables,
      content,
      driveUrl:null, driveId:null,
      compensation: form.collabType==="Paid" && form.paymentAmount
        ? `${form.currency} ${form.paymentAmount}${form.gstApplicable?" + GST":""}`
        : (form.collabType==="Gifted" ? "Gifted product" : ""),
      commissionRate: form.commissionRate || "",
      audienceDiscount: form.audienceDiscount || "",
      contentGuidelines: form.contentGuidelines || "",
      postingDeadline: form.postingDeadline || "",
      usageRights: form.usageRights || "",
      usageDuration: form.usageDuration || "",
      jurisdiction: form.jurisdiction || "",
    };
    // Persist to Supabase first so we have a real uuid. If the write fails, do NOT
    // fabricate a Date.now() id and show success — that agreement (a legal doc)
    // would vanish on reload. Surface the failure and bail.
    const persistedId = persistAgreement ? await persistAgreement(ag) : null;
    if(!persistedId){ toast("Couldn't save the agreement — check your connection and try again",'error'); return; }
    ag.id = persistedId;
    setAgreements(prev=>[ag,...prev]);
    setShowNew(false);setStep("creator");setSelectedCreator(null);
    toast(`Agreement created for ${selectedCreator.name}`);

    // Save to Google Drive as Google Doc
    // (Removed a dead Drive-save attempt here that POSTed to a non-existent MCP
    // endpoint — https://drivemcp.googleapis.com/mcp/v1 — inside an empty catch.
    // It never succeeded, so it only burned a Sonnet call on every agreement
    // create. Signed-PDF-to-Drive is handled by the google-agency-drive edge fn.)
  };
  if(viewingAgreement)return(<div style={{display:"flex",flexDirection:"column",gap:16}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <button onClick={()=>setViewingAgreement(null)} style={{display:"flex",alignItems:"center",gap:6,border:"none",background:"none",cursor:"pointer",color:"#9ab8a6",fontSize:13,fontWeight:500,padding:0}}><Ico d="M10 19l-7-7m0 0l7-7m-7 7h18" size={15}/> Back to agreements</button>
      <div style={{display:"flex",gap:8}}>
        <Badge label={viewingAgreement.status} color={viewingAgreement.status==="Signed"?"#ECFDF5":viewingAgreement.status==="Sent"?"#EFF6FF":"#F9FAFB"} text={viewingAgreement.status==="Signed"?"#166534":viewingAgreement.status==="Sent"?"#1D4ED8":"#6B7280"}/>
        <button onClick={()=>{
          const ag=viewingAgreement;
          // Print the ACTUAL agreement content (operator-authored), never a
          // reconstructed table with invented defaults. No fake legal terms.
          const esc=s=>String(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
          const html=`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Partnership Agreement — ${esc(ag.brand)}</title><style>body{font-family:Georgia,serif;max-width:680px;margin:60px auto;padding:0 40px;color:#1a1a1a;line-height:1.7}h1{font-size:22px;margin-bottom:4px}h2{font-size:15px;color:#666;font-weight:400;margin-top:0}hr{border:none;border-top:1px solid #e0e0e0;margin:28px 0}pre{font-family:Georgia,serif;font-size:14px;white-space:pre-wrap;word-break:break-word;line-height:1.7;margin:0}.sig{margin-top:60px;display:flex;gap:80px}.sig-block{flex:1;border-top:1px solid #333;padding-top:10px;font-size:13px;color:#666}@media print{body{margin:20px}}</style></head><body>
<h1>Partnership Agreement</h1><h2>${esc(ag.brand)} × ${esc(ag.creator?.name||"Creator")}</h2>
<hr>
<pre>${esc(ag.content)}</pre>
<div class="sig">
<div class="sig-block">Brand Representative<br><strong>${esc(ag.brand)}</strong><br>Date: ___________</div>
<div class="sig-block">Creator<br><strong>${esc(ag.creator?.name||"")}</strong><br>Date: ___________</div>
</div>
</body></html>`;
          const w=window.open("","_blank");
          w.document.write(html);w.document.close();
          setTimeout(()=>w.print(),400);
        }} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer",color:DARK,fontWeight:600}}>⬇ Download PDF</button>
        {viewingAgreement.driveUrl&&<a href={viewingAgreement.driveUrl} target="_blank" rel="noreferrer" style={{border:`1px solid #BBF7D0`,background:"#F0FDF4",borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer",color:"#166534",fontWeight:600,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:5}}>📁 Open in Drive</a>}
      </div>
    </div>

    {/* Agreement content */}
    <Card style={{padding:32}}><pre style={{fontFamily:"'DM Mono',monospace",fontSize:12,color:DARK,lineHeight:1.9,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{viewingAgreement.content}</pre></Card>

    {/* Signing state machine — replaces old fake "Generate signing link" + "Mark as signed" buttons.
        Now properly DB-backed via migration 0036 + send-agreement-email Edge Function +
        public /sign/:token route + submit_creator_signature RPC. */}
    <AgreementSigningPanel
      agreement={viewingAgreement}
      onUpdate={(updated)=>{
        setViewingAgreement(updated);
        setAgreements(prev=>prev.map(a=>a.id===updated.id?updated:a));
      }}
    />
  </div>);
  return(<div style={{display:"flex",flexDirection:"column",gap:16}}>
    {showNew&&(<Modal title={step==="creator"?"Choose creator":step==="terms"?"Agreement terms":"Preview"} onClose={()=>{setShowNew(false);setStep("creator");setSelectedCreator(null);}} width={step==="preview"?720:step==="terms"?640:520}>
      {step==="creator"&&(<><input value={creatorSearch} onChange={e=>setCreatorSearch(e.target.value)} placeholder="Search creators..." style={{width:"100%",padding:"10px 12px",border:`1px solid ${BORDER}`,borderRadius:9,fontSize:13,outline:"none",marginBottom:14,boxSizing:"border-box"}}/>
        <div style={{maxHeight:320,overflowY:"auto",display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>{filteredCreators.map((c,i)=>(<div key={i} onClick={()=>setSelectedCreator(c)} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:10,border:`1.5px solid ${selectedCreator?.handle===c.handle?G:BORDER}`,background:selectedCreator?.handle===c.handle?G+"10":"#fff",cursor:"pointer"}}><Avatar name={c.name} color={c.color} size={32}/><div style={{flex:1}}><div style={{fontWeight:600,fontSize:13,color:DARK}}>{c.name}</div><div style={{fontSize:11,color:"#9ab8a6"}}>@{c.handle} · {c.followersF}</div></div>{selectedCreator?.handle===c.handle&&<div style={{color:G,fontWeight:700}}>✓</div>}</div>))}</div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}><button onClick={()=>setShowNew(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button><button disabled={!selectedCreator} onClick={()=>setStep("terms")} style={{background:selectedCreator?DARK:"#f0f0f0",color:selectedCreator?G:"#9ab8a6",border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:selectedCreator?"pointer":"not-allowed"}}>Next →</button></div></>)}
      {step==="terms"&&(()=>{
        const setF=(k,v)=>setForm(p=>({...p,[k]:v}));
        const isPaid=form.collabType==="Paid"||form.collabType==="Long-term content creator";
        const isAU=form.jurisdiction==="Australia";
        const sectionTitle=(title,subtitle)=><div style={{marginTop:18,marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${BORDER}`}}><div style={{fontSize:11,fontWeight:700,color:DARK,letterSpacing:".06em",textTransform:"uppercase"}}>{title}</div>{subtitle&&<div style={{fontSize:11,color:MUTED,marginTop:3}}>{subtitle}</div>}</div>;
        const lbl=(t,opt)=><div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>{t} {opt&&<span style={{color:MUTED,fontWeight:400}}>({opt})</span>}</div>;
        const inp={width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"};
        const sel={...inp,padding:"8px 10px",background:"#fff"};
        const ta={...inp,resize:"vertical",fontFamily:"inherit"};
        return(<div style={{maxHeight:"65vh",overflowY:"auto",paddingRight:6,marginRight:-6}}>
          <div style={{fontSize:11,color:MUTED,marginBottom:6,lineHeight:1.5,background:"#f8faf9",border:`1px solid ${BORDER}`,borderRadius:8,padding:"10px 12px"}}>Only fields you fill in appear in the rendered agreement. Standard legal clauses (disclosure, warranties, IP licence, indemnification, termination, governing law) are auto-included — untick at the bottom if you don't want them.</div>

          {sectionTitle("Collaboration")}
          <div style={{marginBottom:10}}>
            {lbl("Type")}
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {["Gifted","Paid","Long-term content creator","Other"].map(t=><button key={t} onClick={()=>setForm(p=>({...p,collabType:t,collabTypeOther:t==="Other"?(p.collabTypeOther||""):""}))} style={{padding:"8px 12px",border:`1.5px solid ${form.collabType===t?G:BORDER}`,borderRadius:8,background:form.collabType===t?G+"15":"#fff",color:form.collabType===t?DARK:"#9ab8a6",fontWeight:form.collabType===t?700:400,fontSize:12,cursor:"pointer"}}>{t}</button>)}
            </div>
            {form.collabType==="Other"&&<input value={form.collabTypeOther||""} onChange={e=>setF("collabTypeOther",e.target.value)} placeholder="Describe the collaboration type…" style={{...inp,marginTop:6,padding:"7px 10px",fontSize:12}}/>}
          </div>
          <div>
            {lbl("Intro note","optional — warm opening paragraph; leaves a short default if blank")}
            <textarea value={form.introNote} onChange={e=>setF("introNote",e.target.value)} rows={2} placeholder={`e.g. "We're delighted to officially welcome you to the ${brand?.name||"brand"} family!"`} style={ta}/>
          </div>

          {sectionTitle("Scope & timeline")}
          <div style={{marginBottom:10}}>
            {lbl("Deliverables","one per line")}
            <textarea value={form.deliverables} onChange={e=>setF("deliverables",e.target.value)} rows={3} placeholder={"e.g.\n1× Instagram Reel\n1× Story\nCollab post invite"} style={ta}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:10}}>
            <div>{lbl("Brief / draft due","optional")}<input type="date" value={form.briefDeliveryDate} onChange={e=>setF("briefDeliveryDate",e.target.value)} style={sel}/></div>
            <div>{lbl("Posting deadline","optional")}<input type="date" value={form.postingDeadline} onChange={e=>setF("postingDeadline",e.target.value)} style={sel}/></div>
          </div>
          <div>
            {lbl("Content guidelines","one per line, optional")}
            <textarea value={form.contentGuidelines} onChange={e=>setF("contentGuidelines",e.target.value)} rows={3} placeholder={brand?.instagram?`e.g.\nMust tag ${brand.instagram}\nUse hashtag #${(brand?.name||"brand").toLowerCase().replace(/\\s+/g,"")}`:"e.g.\nMust tag @brandhandle\nUse #brandname hashtag"} style={ta}/>
          </div>

          {isPaid&&<>
            {sectionTitle("Compensation")}
            <div style={{display:"grid",gridTemplateColumns:"110px 1fr 1fr",gap:12,marginBottom:10}}>
              <div>{lbl("Currency")}<select value={form.currency} onChange={e=>setF("currency",e.target.value)} style={sel}>{CURRENCIES.map(c=><option key={c.code} value={c.code}>{c.code}</option>)}</select></div>
              <div>{lbl("Amount")}<input value={form.paymentAmount} onChange={e=>setF("paymentAmount",e.target.value)} placeholder="e.g. 500" style={inp}/></div>
              <div>{lbl("Payment terms","optional")}<select value={form.paymentTerms} onChange={e=>setF("paymentTerms",e.target.value)} style={sel}><option value="">—</option><option>On invoice</option><option>NET 7</option><option>NET 14</option><option>NET 30</option><option>50% upfront, 50% on delivery</option><option>End of month</option></select></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:10}}>
              <div>{lbl("Payment method","optional")}<select value={form.paymentMethod} onChange={e=>setF("paymentMethod",e.target.value)} style={sel}><option value="">—</option><option>Bank transfer</option><option>PayPal</option><option>Stripe</option><option>Wise</option><option>On creator's invoice</option></select></div>
              {isAU&&form.currency==="AUD"&&<div style={{paddingTop:24}}><label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:DARK,cursor:"pointer"}}><input type="checkbox" checked={form.gstApplicable} onChange={e=>setF("gstApplicable",e.target.checked)}/>GST applicable (AU + creator GST-registered)</label></div>}
            </div>
          </>}
          {form.collabType!=="Long-term content creator"&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginTop:10,marginBottom:10}}>
            <div>{lbl("Affiliate commission","optional")}<input value={form.commissionRate} onChange={e=>setF("commissionRate",e.target.value)} placeholder="e.g. 10%" style={inp}/></div>
            <div>{lbl("Audience discount code","optional")}<input value={form.audienceDiscount} onChange={e=>setF("audienceDiscount",e.target.value)} placeholder="e.g. ALICE15" style={inp}/></div>
          </div>}

          {sectionTitle("Usage rights")}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:10}}>
            <div>{lbl("Rights granted","optional")}<select value={form.usageRights} onChange={e=>setF("usageRights",e.target.value)} style={sel}><option value="">— not specified —</option><option>Organic only</option><option>Organic + Paid</option><option>Paid only</option><option>Full transfer</option></select></div>
            <div>{lbl("Duration","optional")}<input value={form.usageDuration} onChange={e=>setF("usageDuration",e.target.value)} placeholder="e.g. 6 months, 12 months, perpetual" disabled={!form.usageRights||form.usageRights==="Full transfer"} style={{...inp,padding:"9px 12px",background:!form.usageRights||form.usageRights==="Full transfer"?"#f8faf9":"#fff",color:!form.usageRights||form.usageRights==="Full transfer"?MUTED:DARK}}/></div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:10}}>
            <div>{lbl("Territory","optional")}<select value={form.usageTerritory} onChange={e=>setF("usageTerritory",e.target.value)} style={sel} disabled={!form.usageRights||form.usageRights==="Full transfer"}><option value="">—</option><option>Worldwide</option><option>Australia</option><option>Australia + New Zealand</option><option>North America</option><option>Europe</option><option>UK</option></select></div>
            <div style={{paddingTop:24}}>
              <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:DARK,cursor:"pointer"}}><input type="checkbox" checked={form.whitelisting} onChange={e=>setF("whitelisting",e.target.checked)}/>Paid amplification / whitelisting (Spark Ads)</label>
              {form.whitelisting&&<input value={form.whitelistingDuration} onChange={e=>setF("whitelistingDuration",e.target.value)} placeholder="duration e.g. 60 days" style={{...inp,marginTop:6,padding:"7px 10px",fontSize:12}}/>}
            </div>
          </div>

          <div style={{marginTop:18,marginBottom:8}}>
            <button onClick={()=>setShowAdvanced(v=>!v)} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:8,padding:"7px 12px",fontSize:11,cursor:"pointer",color:DARK,fontWeight:600}}>{showAdvanced?"− Hide advanced clauses":"+ Show advanced clauses (exclusivity, approval, IP, legal opt-ins)"}</button>
          </div>

          {showAdvanced&&<>
            {sectionTitle("Exclusivity & approval","Optional. Don't include unless specifically negotiated.")}
            <div style={{display:"grid",gridTemplateColumns:"1.5fr 1fr",gap:12,marginBottom:10}}>
              <div>{lbl("Exclusivity categories","optional")}<input value={form.exclusivityCategories} onChange={e=>setF("exclusivityCategories",e.target.value)} placeholder="e.g. activewear, swimwear" style={inp}/></div>
              <div>{lbl("Exclusivity window","optional")}<input value={form.exclusivityWindow} onChange={e=>setF("exclusivityWindow",e.target.value)} placeholder="e.g. 30 days" style={inp}/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:10}}>
              <div>{lbl("Revision rounds","optional")}<input value={form.approvalRounds} onChange={e=>setF("approvalRounds",e.target.value)} placeholder="e.g. 2" style={inp}/></div>
              <div>{lbl("Kill fee","optional")}<input value={form.killFee} onChange={e=>setF("killFee",e.target.value)} placeholder="e.g. 50%" style={inp}/></div>
            </div>

            {sectionTitle("Parties","Pulled from brand + creator settings; edit if their legal name differs")}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:10}}>
              <div>{lbl("Brand legal name")}<input value={form.brandLegalName} onChange={e=>setF("brandLegalName",e.target.value)} placeholder={brand?.name} style={inp}/></div>
              <div>{lbl("Brand ABN","AU only, optional")}<input value={form.brandAbn} onChange={e=>setF("brandAbn",e.target.value)} placeholder="e.g. 12 345 678 901" style={inp}/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:10}}>
              <div>{lbl("Creator legal name")}<input value={form.creatorLegalName} onChange={e=>setF("creatorLegalName",e.target.value)} placeholder={selectedCreator?.name} style={inp}/></div>
              <div>{lbl("Creator ABN","optional, AU creators")}<input value={form.creatorAbn} onChange={e=>setF("creatorAbn",e.target.value)} placeholder="e.g. 98 765 432 109" style={inp}/></div>
            </div>

            {sectionTitle("Legal clauses","Industry-standard protections — leave on unless you have a specific reason")}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:10}}>
              {[
                ["includeDisclosure","Disclosure (ACCC/AANA-compliant)"],
                ["includeWarranties","Creator warranties (originality, IP)"],
                ["includeIndemnification","Indemnification (with liability cap)"],
                ["includeTermination","Termination clause"],
                ["includeConfidentiality","Confidentiality / NDA"],
                ["ipTransferToBrand","Full IP transfer to brand (heavy — discuss first)"],
              ].map(([k,label])=><label key={k} style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:DARK,cursor:"pointer",padding:"4px 0"}}><input type="checkbox" checked={!!form[k]} onChange={e=>setF(k,e.target.checked)}/>{label}</label>)}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1.5fr 1fr",gap:12,marginBottom:10}}>
              <div>{lbl("Disclosure tag","shown on the agreement")}<input value={form.disclosureTag} onChange={e=>setF("disclosureTag",e.target.value)} placeholder='e.g. "#ad — Paid Partnership"' disabled={!form.includeDisclosure} style={{...inp,background:form.includeDisclosure?"#fff":"#f8faf9",color:form.includeDisclosure?DARK:MUTED}}/></div>
              <div>{lbl("Termination notice","days")}<input value={form.noticePeriodDays} onChange={e=>setF("noticePeriodDays",e.target.value)} placeholder="e.g. 7" disabled={!form.includeTermination} style={{...inp,background:form.includeTermination?"#fff":"#f8faf9",color:form.includeTermination?DARK:MUTED}}/></div>
            </div>
          </>}

          {sectionTitle("Governing law")}
          <div style={{display:"grid",gridTemplateColumns:isAU?"1fr 200px":"1fr",gap:12,marginBottom:20}}>
            <div>{lbl("Country")}<CountrySelect value={form.jurisdiction} onChange={v=>setForm(p=>({...p,jurisdiction:v}))} placeholder="Governing law country…"/></div>
            {isAU&&<div>{lbl("State")}<select value={form.jurisdictionState} onChange={e=>setF("jurisdictionState",e.target.value)} style={sel}><option value="">—</option>{["NSW","VIC","QLD","WA","SA","TAS","ACT","NT"].map(s=><option key={s}>{s}</option>)}</select></div>}
          </div>

          <div style={{display:"flex",justifyContent:"space-between",gap:8,paddingTop:8,borderTop:`1px solid ${BORDER}`}}>
            <button onClick={()=>setStep("creator")} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 16px",fontSize:13,cursor:"pointer"}}>← Back</button>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setShowNew(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button>
              <button onClick={()=>setStep("preview")} style={{background:DARK,color:G,border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Preview →</button>
            </div>
          </div>
        </div>);
      })()}
      {step==="preview"&&(<><div style={{maxHeight:400,overflowY:"auto",background:"#f8faf9",borderRadius:10,padding:20,marginBottom:16}}><pre style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:DARK,lineHeight:1.8,whiteSpace:"pre-wrap",wordBreak:"break-word",margin:0}}>{fillTemplate()}</pre></div><div style={{display:"flex",justifyContent:"space-between",gap:8}}><button onClick={()=>setStep("terms")} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 16px",fontSize:13,cursor:"pointer"}}>← Edit</button><div style={{display:"flex",gap:8}}><button onClick={()=>setShowNew(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button><button onClick={createAgreement} style={{background:DARK,color:G,border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Create agreement</button></div></div></>)}
    </Modal>)}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}><div style={{fontSize:13,color:"#9ab8a6"}}>{agreements.length} agreement{agreements.length!==1?"s":""}</div><button onClick={()=>{setShowNew(true);setStep("creator");}} style={{background:G,color:DARK,border:"none",borderRadius:10,padding:"10px 18px",fontWeight:700,fontSize:13,cursor:"pointer"}}>+ New agreement</button></div>
    {agreements.length===0?(<Card style={{padding:48,textAlign:"center"}}><div style={{fontSize:48,marginBottom:16,opacity:.4}}>📄</div><div style={{fontWeight:700,color:DARK,fontSize:17,marginBottom:6}}>No agreements yet</div><div style={{fontSize:13,color:"#9ab8a6",marginBottom:24,maxWidth:380,margin:"0 auto 24px",lineHeight:1.6}}>Create your first agreement.</div><button onClick={()=>{setShowNew(true);setStep("creator");}} style={{background:DARK,color:G,border:"none",borderRadius:10,padding:"10px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>+ Create agreement</button></Card>):
    (<Card><table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr style={{borderBottom:"1px solid #f0f7f3"}}>{["Creator","Brand","Type","Created","Status","Usage rights",""].map(h=>(<th key={h} style={{padding:"12px 16px",textAlign:"left",fontSize:10,color:"#9ab8a6",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em"}}>{h}</th>))}</tr></thead><tbody>{agreements.map((ag,i)=>{
      // Usage-rights expiry badge — computed from operator-typed duration
      // text + creator signed_at. Colour-coded: red=expired, amber=≤14d out,
      // grey=ok, blue=perpetual, no badge=unsigned/no-duration.
      const exp = computeAgreementExpiry(ag.usageDuration, ag.creatorSignedAt);
      const expColors = {
        expired: {bg:"#FEE2E2",fg:"#991B1B"},
        warn:    {bg:"#FEF3C7",fg:"#92400E"},
        ok:      {bg:"#F3F4F6",fg:"#374151"},
        perpetual:{bg:"#EFF6FF",fg:"#1D4ED8"},
      };
      const ec = expColors[exp.urgency];
      return(<tr key={ag.id||i} style={{borderBottom:"1px solid #f8faf9",cursor:"pointer"}} onClick={()=>setViewingAgreement(ag)} onMouseEnter={e=>e.currentTarget.style.background="#fafcfb"} onMouseLeave={e=>e.currentTarget.style.background=""}><td style={{padding:"14px 16px"}}><div style={{display:"flex",alignItems:"center",gap:10}}><Avatar name={ag.creator.name} color={ag.creator.color} size={30}/><div><div style={{fontWeight:600,color:DARK,fontSize:13}}>{ag.creator.name}</div><div style={{fontSize:11,color:"#9ab8a6"}}>@{ag.creator.handle}</div></div></div></td><td style={{padding:"14px 16px",fontSize:12,color:"#9ab8a6"}}>{ag.brand}</td><td style={{padding:"14px 16px"}}><Badge label={ag.collabType} color={ag.collabType==="Paid"?"#FFFBEB":"#F0FDF4"} text={ag.collabType==="Paid"?"#92400E":"#166634"}/></td><td style={{padding:"14px 16px",fontSize:12,color:"#9ab8a6"}}>{ag.created}</td><td style={{padding:"14px 16px"}}><Badge label={ag.status} color={ag.status==="Signed"?"#ECFDF5":ag.status==="Sent"?"#EFF6FF":"#F9FAFB"} text={ag.status==="Signed"?"#166534":ag.status==="Sent"?"#1D4ED8":"#6B7280"}/></td>
      <td style={{padding:"14px 16px"}}>{ec && exp.label
        ? <span title={ag.usageDuration ? `Duration: ${ag.usageDuration}` : ""} style={{background:ec.bg,color:ec.fg,padding:"3px 8px",borderRadius:5,fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{exp.label}</span>
        : <span style={{color:MUTED,fontSize:11}}>{ag.usageDuration || "—"}</span>}</td>
      <td style={{padding:"14px 16px"}} onClick={e=>e.stopPropagation()}>
      {/* Delete button — mirrors the Campaigns pattern. Confirms, hits the existing
          removeAgreement RPC, removes from local state, logs to security_log. */}
      <button onClick={async()=>{
        if(!(await confirmDialog({
          title:`Delete agreement for ${ag.creator?.name||"this creator"}?`,
          body:"This can't be undone. The Google Drive doc (if any) is NOT touched — only the record in the app.",
          destructive:true, confirmLabel:"Delete agreement",
        })))return;
        const deleted = removeAgreement ? await removeAgreement(ag.id) : false;
        if(!deleted){ toast("Couldn't delete the agreement — try again",'error'); return; }
        setAgreements(prev=>prev.filter(a=>a.id!==ag.id));
        logSecurityEvent(brandId,'agreement.delete','agreement',ag.id,{creator_name:ag.creator?.name||null,creator_handle:ag.creator?.handle||null,status:ag.status,collab_type:ag.collabType});
        toast(`Agreement for ${ag.creator?.name||"creator"} deleted`);
      }} style={{border:"1px solid #FECACA",background:"#fff",color:"#DC2626",borderRadius:7,padding:"5px 9px",fontSize:12,cursor:"pointer"}} title="Delete agreement"
        onMouseEnter={e=>{e.currentTarget.style.background="#FEF2F2";}}
        onMouseLeave={e=>{e.currentTarget.style.background="#fff";}}>🗑</button>
    </td></tr>);
    })}</tbody></table></Card>)}
  </div>);
}

// Integration tiles list. Meta / Motion / Modash / Klaviyo stripped per
// Kristijan's feedback — they were placeholders not wired to anything real.
// Add them back when there's a concrete backend integration to support.
const INTG_LIST=[
  {name:"Shopify",desc:"Orders, fulfillment, tracking",color:"#96BF48",bg:"#F9FFF0",svg:<svg viewBox="0 0 48 48" width={22} height={22}><rect width="48" height="48" rx="10" fill="#96BF48"/><path fill="#1A1A1A" d="M33 20h-4c-.4-3.4-3.1-6-6.5-6S16.4 16.6 16 20h-4l-2 18h25L33 20zm-10.5-4c2 0 3.7 1.8 4 4h-8c.3-2.2 2-4 4-4z"/><path fill="none" stroke="#1A1A1A" strokeWidth="2.5" strokeLinecap="round" d="M19.5 20v-3.5a3 3 0 016 0V20"/><path fill="#96BF48" d="M26.5 28.5c0-.7-.6-1.3-1.8-1.3H23c-.3 0-.5-.2-.5-.5s.2-.4.5-.4h3.3V25H23c-1.2 0-2 .8-2 1.9 0 .9.6 1.5 1.7 1.8l.9.3c.5.1.7.3.7.6 0 .3-.2.6-.7.6H21v1.8h2.7c1.6 0 2.8-.9 2.8-2.1v-.4z"/></svg>},
  {name:"Google Sheets",desc:"Creator database, tracking",color:"#34A853",bg:"#F0FDF4",svg:<svg viewBox="0 0 24 32" width={17} height={22}><path fill="#34A853" d="M14 0H2C.9 0 0 .9 0 2v28c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V8l-10-8z"/><path fill="#1E8E3E" d="M14 0v8h8"/><path fill="#fff" fillOpacity=".3" d="M14 0v8h8"/><rect fill="white" x="3" y="13" width="18" height="14" rx="1"/><path fill="#34A853" d="M3 13h18v4H3zm0 6h18v1H3zm0 4h18v3H3zm6-10v14H8V13zm6 0v14h-1V13z"/></svg>},
  {name:"Google Drive",desc:"Content storage, analysis",color:"#4285F4",bg:"#EFF6FF",svg:<svg viewBox="0 0 87.3 78" width={22} height={22}><path fill="#0066da" d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"/><path fill="#00ac47" d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5z"/><path fill="#ea4335" d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.5l5.85 11.5z"/><path fill="#00832d" d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"/><path fill="#2684fc" d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"/><path fill="#ffba00" d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"/></svg>},
  {name:"Gmail",desc:"Send outreach + receive replies",color:"#EA4335",bg:"#FEF2F2",svg:<svg viewBox="0 0 48 48" width={22} height={22}><path fill="#4caf50" d="M45 16.2l-5 2.75-5 4.75L35 40h7c1.657 0 3-1.343 3-3V16.2z"/><path fill="#1e88e5" d="M3 16.2l3.614 1.71L13 23.7V40H6c-1.657 0-3-1.343-3-3V16.2z"/><polygon fill="#e53935" points="35,11.2 24,19.45 13,11.2 12,17 13,23.7 24,31.45 35,23.7 36,17"/><path fill="#c62828" d="M3 12.298V16.2l10 7.5V11.2L9.876 8.859C9.132 8.301 8.228 8 7.298 8 4.924 8 3 9.924 3 12.298z"/><path fill="#fbc02d" d="M45 12.298V16.2l-10 7.5V11.2l3.124-2.341C38.868 8.301 39.772 8 40.702 8 43.076 8 45 9.924 45 12.298z"/></svg>},
  {name:"Instagram",desc:"Auto-detect tagged posts + stories",color:"#E1306C",bg:"#FFF1F4",svg:<svg viewBox="0 0 48 48" width={22} height={22}><defs><linearGradient id="ig-grad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#F58529"/><stop offset="50%" stopColor="#DD2A7B"/><stop offset="100%" stopColor="#8134AF"/></linearGradient></defs><rect width="48" height="48" rx="12" fill="url(#ig-grad)"/><rect x="13" y="13" width="22" height="22" rx="6" fill="none" stroke="#fff" strokeWidth="2.5"/><circle cx="24" cy="24" r="5" fill="none" stroke="#fff" strokeWidth="2.5"/><circle cx="31.5" cy="16.5" r="1.5" fill="#fff"/></svg>},
  {name:"Social Snowball",desc:"Affiliate codes + revenue",color:"#5B5BD6",bg:"#EEF2FF",svg:<svg viewBox="0 0 24 24" width={22} height={22}><circle cx="12" cy="12" r="10" fill="none" stroke="#5B5BD6" strokeWidth="2"/><path fill="#5B5BD6" d="M12 6l1.5 3 3.5.5-2.5 2.5.5 3.5L12 14l-3 1.5.5-3.5L7 9.5l3.5-.5z"/></svg>},
  {name:"Slack",desc:"Team notifications",color:"#E01E5A",bg:"#FFF5F8",svg:<svg viewBox="0 0 122.8 122.8" width={22} height={22}><path fill="#E01E5A" d="M25.8 77.9c0 7.1-5.8 12.9-12.9 12.9S0 85 0 77.9s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.9z"/><path fill="#36C5F0" d="M44.8 25.8C44.8 18.7 50.6 12.9 57.7 12.9S70.6 18.7 70.6 25.8 64.8 38.7 57.7 38.7H44.8V25.8zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.6C5.5 58.1-.3 52.3-.3 45.2s5.8-12.9 12.9-12.9H44.8z"/><path fill="#2EB67D" d="M96.9 44.8c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H84.1V44.8h12.8zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.6C64.6 5.5 70.4-.3 77.5-.3s12.9 5.8 12.9 12.9v32.2z"/><path fill="#ECB22E" d="M77.9 97c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9 5.8-12.9 12.9-12.9h12.9V97zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.9z"/></svg>},
];

function Integrations({brand,activeBrand,setBrandData,brandId}){
  // (connecting state removed with the dead toggle modal — see Integrations cleanup.)
  const[gmailInboxes,setGmailInboxes]=useState([]); // array of { id, email_address, daily_cap, expires_at }
  const[gmailBusy,setGmailBusy]=useState(false);

  // Agency Google (Drive+Sheets) is ONE agency-wide connection — only the agency
  // owner can connect/disconnect it (the disconnect edge fn rejects non-owners
  // with 403). Members see the status read-only so the button doesn't 403 on them.
  const[isAgencyOwner,setIsAgencyOwner]=useState(false);
  useEffect(()=>{(async()=>{const{data}=await supabase.rpc('user_owns_any_brand');setIsAgencyOwner(!!data);})();},[]);

  // Shopify connection state. One row per brand. Loaded from RLS-protected status view.
  const[shopifyStatus,setShopifyStatus]=useState(null); // { shop_domain, shop_name, currency } or null
  const[shopifyBusy,setShopifyBusy]=useState(false);
  const[showShopifyConnect,setShowShopifyConnect]=useState(false);
  const[shopifyForm,setShopifyForm]=useState({shop_domain:"",access_token:"",api_secret:""});
  // 'oauth' = redirect install (only works for stores in our app's Shopify org);
  // 'token' = paste a custom-app Admin API token (works on any store, no org lock).
  const[shopifyMode,setShopifyMode]=useState('oauth');
  // Public-app agency flow: stores that completed OAuth via the generic install
  // link but aren't linked to a brand yet. Owner-only; claimed onto a brand here.
  const[pendingInstalls,setPendingInstalls]=useState([]);
  const[pendingBusy,setPendingBusy]=useState(false);
  const[installLinkShop,setInstallLinkShop]=useState("");
  const[copiedLink,setCopiedLink]=useState(false);

  // Instagram Graph API per-brand connection. Set by the OAuth callback
  // edge function after operator finishes the Connect Instagram flow.
  // Used to receive mention/comment webhooks and (later) fetch insights.
  const[igStatus,setIgStatus]=useState(null); // {instagram_username, instagram_business_account_id, instagram_connected_at, instagram_webhook_subscribed_at, instagram_token_expires_at} | null
  const[igBusy,setIgBusy]=useState(false);
  const loadIgStatus=async()=>{
    if(!brandId)return;
    const{data}=await supabase.from('brands').select('instagram_username,instagram_business_account_id,instagram_connected_at,instagram_webhook_subscribed_at,instagram_token_expires_at').eq('id',brandId).maybeSingle();
    setIgStatus(data?.instagram_business_account_id?data:null);
  };
  useEffect(()=>{loadIgStatus();/* eslint-disable-line react-hooks/exhaustive-deps */},[brandId]);

  const startInstagramConnect=async()=>{
    if(!brandId){toast('Brand not loaded yet','error');return;}
    setIgBusy(true);
    try{
      const t=await window.Clerk?.session?.getToken();
      const returnTo=window.location.origin+window.location.pathname;
      // Instagram-direct "Business Login for Instagram" flow (instagram-oauth-initiate):
      // no Facebook screen, IG-only consent. This is the proven-live path — Effortless
      // connected via it (IGAA token, mentions+comments webhook subscribed, /me/tags poll
      // working). The Facebook-login flow (instagram-oauth-fb-initiate) is kept deployed
      // but unused: it requires the parent Meta app's App Domains to be configured and a
      // linked FB Page, which we deliberately avoid.
      const url=`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/instagram-oauth-initiate?brand_id=${encodeURIComponent(brandId)}&return_to=${encodeURIComponent(returnTo)}`;
      const r=await fetch(url,{headers:{Authorization:`Bearer ${t}`}});
      const body=await r.json();
      if(!r.ok||!body.auth_url){toast(`Connect failed: ${body.error||r.status}`,'error');setIgBusy(false);return;}
      window.location.href=body.auth_url;
    }catch(e){console.error(e);toast('Connect failed — see console','error');setIgBusy(false);}
  };
  const disconnectInstagram=async()=>{
    if(!brandId)return;
    if(!(await confirmDialog({title:'Disconnect Instagram?',body:'Tagged-post webhooks will stop arriving. You can reconnect later.',destructive:true,confirmLabel:'Disconnect'})))return;
    setIgBusy(true);
    try{
      // Edge function unsubscribes the Page from webhooks Meta-side first, then
      // clears our columns — so tagged-post webhooks actually stop arriving.
      const t=await window.Clerk?.session?.getToken();
      const r=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/instagram-disconnect`,{
        method:'POST',headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},body:JSON.stringify({brand_id:brandId}),
      });
      const body=await r.json();
      if(!r.ok||!body.ok){toast(`Disconnect failed: ${body.error||r.status}`,'error');return;}
      setIgStatus(null);
      toast(body.unsubscribed?'Instagram disconnected · webhooks unsubscribed':'Instagram disconnected');
    }catch(e){console.error(e);toast('Disconnect failed','error');}
    finally{setIgBusy(false);}
  };

  // Handle return from Meta OAuth: ?ig_connect=success|partial|error
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const result=params.get('ig_connect');
    if(!result)return;
    const igUsername=params.get('ig_username');
    if(result==='success'){
      toast(`✓ Instagram connected${igUsername?` as @${igUsername}`:''}`);
      loadIgStatus();
    }else if(result==='partial'){
      toast(`Connected${igUsername?` as @${igUsername}`:''} but webhook subscribe failed — see console`,'error');
      console.warn('IG connect partial:',params.get('warning'),params.get('detail'));
      loadIgStatus();
    }else if(result==='error'){
      toast(`Instagram connect failed: ${params.get('reason')||'unknown'}`,'error');
      console.warn('IG connect error:',params.get('reason'),params.get('detail'));
    }
    // Strip query params so a refresh doesn't re-toast.
    const clean=window.location.pathname+window.location.hash;
    window.history.replaceState({},'',clean);
    // eslint-disable-next-line
  },[]);

  // Agency Drive + Sheets — both are the SAME underlying OAuth connection
  // (drive.file + spreadsheets scopes). Singleton — shared across all brands.
  const[agencyDriveStatus,setAgencyDriveStatus]=useState(null); // {email,...} or null
  const[agencyDriveBusy,setAgencyDriveBusy]=useState(false);
  useEffect(()=>{
    (async()=>{
      const{data}=await supabase.rpc('agency_drive_status').select('*').maybeSingle();
      setAgencyDriveStatus(data||null);
    })();
  },[]);
  const startAgencyDriveConnect=async()=>{
    setAgencyDriveBusy(true);
    try{
      const t=await window.Clerk?.session?.getToken();
      const r=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-oauth/init`,{
        method:'POST',headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},body:'{}',
      });
      const body=await r.json();
      if(!r.ok||!body.url){toast(`Connect failed: ${body.error||r.status}`,'error');setAgencyDriveBusy(false);return;}
      window.location.href=body.url;
    }catch(e){console.error(e);toast('Connect failed','error');setAgencyDriveBusy(false);}
  };
  const disconnectAgencyDrive=async()=>{
    if(!(await confirmDialog({title:'Disconnect agency Google?',body:'Both Drive and Sheets access removed in one go.',destructive:true,confirmLabel:'Disconnect'})))return;
    setAgencyDriveBusy(true);
    try{
      const t=await window.Clerk?.session?.getToken();
      const r=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-oauth/disconnect`,{
        method:'POST',headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},body:'{}',
      });
      if(!r.ok){toast(`Disconnect failed: ${r.status}`,'error');return;}
      setAgencyDriveStatus(null);
      toast('Agency Google disconnected');
    }catch(e){console.error(e);toast('Disconnect failed','error');}
    finally{setAgencyDriveBusy(false);}
  };

  const loadShopifyStatus=async()=>{
    if(!brandId)return;
    const{data,error}=await supabase.rpc('shopify_connection_status').select('*').eq('brand_id',brandId).maybeSingle();
    if(error){console.error('shopify status',error);return;}
    setShopifyStatus(data||null);
  };
  useEffect(()=>{loadShopifyStatus();/* eslint-disable-line react-hooks/exhaustive-deps */},[brandId]);

  const submitShopifyConnect=async()=>{
    if(!brandId)return;
    const shop=shopifyForm.shop_domain.trim();
    if(!shop){toast('Shop domain required','error');return;}
    setShopifyBusy(true);
    try{
      const t=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/shopify-oauth/init`,{
        method:'POST',
        headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},
        body:JSON.stringify({shop_domain:shop,brand_id:brandId}),
      });
      const body=await resp.json();
      if(!resp.ok||!body.url){toast(`Connect failed: ${body.error||body.detail||resp.status}`,'error');setShopifyBusy(false);return;}
      // Redirect to Shopify's install page — user authorizes there and Shopify redirects back to our callback.
      window.location.href=body.url;
    }catch(e){console.error(e);toast('Connect failed — see console','error');setShopifyBusy(false);}
  };

  // Paste a custom-app Admin API token (shpat_…) — used for stores outside our
  // app's Shopify org, where the OAuth install is blocked. Validates + stores
  // server-side, then registers the ship/cancel webhooks just like OAuth does.
  const submitShopifyConnectToken=async()=>{
    if(!brandId)return;
    const shop=shopifyForm.shop_domain.trim();
    const token=shopifyForm.access_token.trim();
    if(!shop){toast('Shop domain required','error');return;}
    if(!token){toast('Admin API access token required','error');return;}
    setShopifyBusy(true);
    try{
      const t=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/shopify-connect/connect`,{
        method:'POST',
        headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},
        body:JSON.stringify({shop_domain:shop,access_token:token,api_secret:shopifyForm.api_secret.trim()||undefined,brand_id:brandId}),
      });
      const body=await resp.json();
      if(!resp.ok||!body.ok){toast(`Connect failed: ${body.detail||body.error||resp.status}`,'error');setShopifyBusy(false);return;}
      toast(`Connected to ${body.shop_name||body.shop_domain}`,'success');
      setShopifyForm({shop_domain:"",access_token:"",api_secret:""});
      setShowShopifyConnect(false);
      setShopifyBusy(false);
      loadShopifyStatus();
    }catch(e){console.error(e);toast('Connect failed — see console','error');setShopifyBusy(false);}
  };

  const disconnectShopify=async()=>{
    if(!brandId)return;
    if(!(await confirmDialog({title:'Disconnect Shopify?',body:'Orders will no longer auto-create on positive replies.',destructive:true,confirmLabel:'Disconnect'})))return;
    setShopifyBusy(true);
    try{
      const t=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/shopify-connect/disconnect`,{
        method:'POST',
        headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},
        body:JSON.stringify({brand_id:brandId}),
      });
      const body=await resp.json();
      if(!resp.ok||!body.ok){toast(`Disconnect failed: ${body.error||resp.status}`,'error');return;}
      setShopifyStatus(null);
      toast('Shopify disconnected');
    }catch(e){console.error(e);toast('Disconnect failed','error');}
    finally{setShopifyBusy(false);}
  };

  // --- Public-app pending installs (agency owner only) ---------------------
  const loadPendingInstalls=async()=>{
    try{
      const t=await window.Clerk?.session?.getToken();
      const r=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/shopify-oauth/pending`,{headers:{Authorization:`Bearer ${t}`}});
      if(!r.ok){setPendingInstalls([]);return;} // non-owners get 403 — show nothing
      const body=await r.json();
      setPendingInstalls(Array.isArray(body.installs)?body.installs:[]);
    }catch(e){console.error('pending installs load failed',e);}
  };
  useEffect(()=>{if(isAgencyOwner)loadPendingInstalls();/* eslint-disable-line react-hooks/exhaustive-deps */},[isAgencyOwner]);

  // Normalize a typed shop the same way the edge function does, for the link preview.
  const normalizeShopClient=(input)=>{
    let s=(input||"").trim().toLowerCase().replace(/^https?:\/\//,"").replace(/\/.*$/,"");
    if(s&&!s.includes("."))s=`${s}.myshopify.com`;
    return s;
  };
  const installLink=installLinkShop.trim()
    ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/shopify-oauth/install?shop=${encodeURIComponent(normalizeShopClient(installLinkShop))}`
    : "";

  const claimInstall=async(install)=>{
    if(!brandId){toast('Pick a brand first','error');return;}
    if(!(await confirmDialog({title:`Link ${install.shop_name||install.shop_domain} to ${activeBrand}?`,body:`This store will be connected to ${activeBrand} and start syncing orders + tracking. You can disconnect later.`,confirmLabel:'Link store'})))return;
    setPendingBusy(true);
    try{
      const t=await window.Clerk?.session?.getToken();
      const r=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/shopify-oauth/claim`,{
        method:'POST',headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},
        body:JSON.stringify({install_id:install.id,brand_id:brandId}),
      });
      const body=await r.json();
      if(!r.ok||!body.ok){toast(`Link failed: ${body.error||r.status}`,'error');setPendingBusy(false);return;}
      toast(`✓ ${body.shop_name||body.shop_domain} linked to ${activeBrand}`,'success');
      await loadPendingInstalls();
      loadShopifyStatus();
    }catch(e){console.error(e);toast('Link failed — see console','error');}
    finally{setPendingBusy(false);}
  };

  // Load all connected Gmail inboxes for this brand.
  useEffect(()=>{
    if(!brandId)return;
    let cancelled=false;
    (async()=>{
      const{data,error}=await supabase.rpc('gmail_connection_status').select('*').eq('brand_id',brandId).order('connected_at');
      if(cancelled)return;
      if(error){console.error('gmail status',error);return;}
      setGmailInboxes(data||[]);
    })();
    return()=>{cancelled=true;};
  },[brandId]);
  const gmailStatus=gmailInboxes[0]||null; // first inbox = primary for tile display

  const startGmailConnect=async()=>{
    if(!brandId){toast('Brand not loaded yet','error');return;}
    setGmailBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/gmail-oauth/init`,{
        method:'POST',
        headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},
        body:JSON.stringify({brand_id:brandId}),
      });
      const body=await resp.json();
      if(!resp.ok||!body.url){toast(`Connect failed: ${body.error||resp.status}`,'error');setGmailBusy(false);return;}
      window.location.href=body.url;
    }catch(e){
      console.error(e);
      toast('Connect failed — see console','error');
      setGmailBusy(false);
    }
  };

  const disconnectGmail=async()=>{
    if(!brandId)return;
    if(!(await confirmDialog({title:'Disconnect all Gmail inboxes?',body:'Every connected inbox for this brand will be disconnected. You can reconnect any of them later.',destructive:true,confirmLabel:'Disconnect all'})))return;
    setGmailBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/gmail-oauth/disconnect`,{
        method:'POST',
        headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},
        body:JSON.stringify({brand_id:brandId}),
      });
      const body=await resp.json();
      if(!resp.ok||!body.ok){toast(`Disconnect failed: ${body.error||resp.status}`,'error');setGmailBusy(false);return;}
      setGmailInboxes([]);
      toast('All Gmail inboxes disconnected');
    }catch(e){
      console.error(e);
      toast('Disconnect failed — see console','error');
    }finally{
      setGmailBusy(false);
    }
  };

  if(!brand)return null;
  // intgs / toggle / connecting-modal removed — they backed a "fake connect"
  // localStorage flow for non-real integrations. Real integrations (Gmail,
  // Shopify, Google Drive) have explicit OAuth state checks. Non-real ones
  // show a disabled "Coming soon" button — no toggle path to flip.

  return(<div style={{display:"flex",flexDirection:"column",gap:16}}>
    {showShopifyConnect&&(
      <Modal title="Connect Shopify" subtitle={`Install on ${activeBrand}'s store`} onClose={()=>setShopifyBusy?null:setShowShopifyConnect(false)} width={500}>
        <div style={{display:"flex",gap:6,marginBottom:16,background:"#F1F5F3",borderRadius:9,padding:4}}>
          {[['oauth','Install (OAuth)'],['token','Paste token']].map(([m,label])=>(
            <button key={m} disabled={shopifyBusy} onClick={()=>setShopifyMode(m)} style={{flex:1,padding:"7px 10px",borderRadius:7,border:"none",fontSize:12,fontWeight:700,cursor:"pointer",background:shopifyMode===m?"#fff":"transparent",color:shopifyMode===m?DARK:MUTED,boxShadow:shopifyMode===m?"0 1px 2px rgba(0,0,0,.06)":"none"}}>{label}</button>
          ))}
        </div>
        {shopifyMode==='oauth'?(
          <div style={{background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:10,padding:14,marginBottom:18,fontSize:12,color:"#1E40AF",lineHeight:1.6}}>
            You'll be redirected to your Shopify admin to authorize the <strong>Influencer OS</strong> app. After you click <strong>Install app</strong> in Shopify, you'll come back here connected. <strong>Only works for stores in our app's Shopify organization</strong> — if you hit a "same organization" error, use <strong>Paste token</strong> instead.
          </div>
        ):(
          <div style={{background:"#FEFCE8",border:"1px solid #FDE68A",borderRadius:10,padding:14,marginBottom:18,fontSize:12,color:"#92400E",lineHeight:1.6}}>
            For stores outside our org. In the store's Shopify admin: <strong>Settings → Apps and sales channels → Develop apps → Create an app</strong>. Enable Admin API scopes <code>read_products, read_inventory, write_draft_orders, write_orders, read_orders, read_customers, write_customers</code>, install it, then reveal and paste the <strong>Admin API access token</strong> (<code>shpat_…</code>) below.
          </div>
        )}
        <div style={{marginBottom:shopifyMode==='token'?14:18}}>
          <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Shop domain</div>
          <input autoFocus value={shopifyForm.shop_domain} onChange={e=>setShopifyForm(p=>({...p,shop_domain:e.target.value}))} placeholder="your-store.myshopify.com" style={{width:"100%",padding:"10px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"DM Mono, monospace"}}/>
          <div style={{fontSize:11,color:MUTED,marginTop:5}}>Just the part before <code>.myshopify.com</code> works too.</div>
        </div>
        {shopifyMode==='token'&&(
          <div style={{marginBottom:18}}>
            <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Admin API access token</div>
            <input type="password" value={shopifyForm.access_token} onChange={e=>setShopifyForm(p=>({...p,access_token:e.target.value}))} placeholder="shpat_…" style={{width:"100%",padding:"10px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"DM Mono, monospace"}}/>
            <div style={{fontSize:11,color:MUTED,marginTop:5}}>Shown only once in Shopify after you install the custom app.</div>
            <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5,marginTop:14}}>API secret key</div>
            <input type="password" value={shopifyForm.api_secret} onChange={e=>setShopifyForm(p=>({...p,api_secret:e.target.value}))} placeholder="shpss_…" style={{width:"100%",padding:"10px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"DM Mono, monospace"}}/>
            <div style={{fontSize:11,color:MUTED,marginTop:5}}>Required for order & tracking sync. Find it in your Shopify admin under Apps → Develop apps → [your app] → API credentials → "API secret key".</div>
          </div>
        )}
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button disabled={shopifyBusy} onClick={()=>setShowShopifyConnect(false)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 18px",fontSize:13,cursor:"pointer"}}>Cancel</button>
          {shopifyMode==='oauth'?(
            <button disabled={shopifyBusy||!shopifyForm.shop_domain} onClick={submitShopifyConnect} style={{background:shopifyBusy||!shopifyForm.shop_domain?"#f0f0f0":DARK,color:shopifyBusy||!shopifyForm.shop_domain?"#9ab8a6":G,border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:shopifyBusy?"wait":"pointer"}}>{shopifyBusy?"Opening Shopify…":"Continue to Shopify →"}</button>
          ):(
            <button disabled={shopifyBusy||!shopifyForm.shop_domain||!shopifyForm.access_token} onClick={submitShopifyConnectToken} style={{background:shopifyBusy||!shopifyForm.shop_domain||!shopifyForm.access_token?"#f0f0f0":DARK,color:shopifyBusy||!shopifyForm.shop_domain||!shopifyForm.access_token?"#9ab8a6":G,border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:shopifyBusy?"wait":"pointer"}}>{shopifyBusy?"Connecting…":"Connect"}</button>
          )}
        </div>
      </Modal>
    )}
    <div style={{fontSize:13,color:"#9ab8a6"}}>Connected to <strong style={{color:DARK}}>{activeBrand}</strong> — each brand has its own credentials.</div>
    {isAgencyOwner&&(
      <Card style={{padding:18,border:`1px solid ${BORDER}`}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
          <span style={{fontWeight:700,color:DARK,fontSize:14}}>🛍️ Shopify install links</span>
          {pendingInstalls.length>0&&<span style={{background:"#FEF3C7",color:"#92400E",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>{pendingInstalls.length} awaiting link</span>}
        </div>
        <div style={{fontSize:12,color:MUTED,lineHeight:1.5,marginBottom:14}}>Send a client their personal install link. They authorize once in their own Shopify admin — no login to this tool needed — then the store appears below to link to a brand.</div>
        <div style={{display:"flex",gap:8,alignItems:"flex-end",marginBottom:6}}>
          <div style={{flex:1}}>
            <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:5}}>Client store domain</div>
            <input value={installLinkShop} onChange={e=>{setInstallLinkShop(e.target.value);setCopiedLink(false);}} placeholder="client-store.myshopify.com" style={{width:"100%",padding:"10px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"DM Mono, monospace"}}/>
          </div>
          <button disabled={!installLink} onClick={()=>{navigator.clipboard?.writeText(installLink);setCopiedLink(true);toast('Install link copied','success');}} style={{background:!installLink?"#f0f0f0":DARK,color:!installLink?"#9ab8a6":G,border:"none",borderRadius:9,padding:"10px 18px",fontWeight:700,fontSize:13,cursor:!installLink?"not-allowed":"pointer",whiteSpace:"nowrap"}}>{copiedLink?"✓ Copied":"Copy link"}</button>
        </div>
        {installLink&&<div style={{fontSize:11,color:MUTED,fontFamily:"DM Mono, monospace",wordBreak:"break-all",background:"#F8FAF9",border:`1px solid ${BORDER}`,borderRadius:7,padding:"8px 10px"}}>{installLink}</div>}

        <div style={{height:1,background:BORDER,margin:"16px 0"}}/>
        <div style={{fontSize:12,fontWeight:700,color:DARK,marginBottom:10}}>Installed stores awaiting a brand</div>
        {pendingInstalls.length===0
          ?<div style={{fontSize:12,color:MUTED}}>No unlinked installs. When a client finishes their install link, their store shows here to link to <strong>{activeBrand}</strong>.</div>
          :<div style={{display:"flex",flexDirection:"column",gap:8}}>
            {pendingInstalls.map(inst=>(
              <div key={inst.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,border:`1px solid ${BORDER}`,borderRadius:9,padding:"10px 12px",background:"#fff"}}>
                <div style={{minWidth:0}}>
                  <div style={{fontWeight:700,color:DARK,fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{inst.shop_name||inst.shop_domain}</div>
                  <div style={{fontSize:11,color:MUTED,fontFamily:"DM Mono, monospace"}}>{inst.shop_domain}{inst.currency?` · ${inst.currency}`:""}</div>
                </div>
                <button disabled={pendingBusy} onClick={()=>claimInstall(inst)} style={{background:pendingBusy?"#f0f0f0":DARK,color:pendingBusy?"#9ab8a6":G,border:"none",borderRadius:8,padding:"7px 14px",fontWeight:700,fontSize:12,cursor:pendingBusy?"wait":"pointer",whiteSpace:"nowrap",flexShrink:0}}>Link to {activeBrand} →</button>
              </div>
            ))}
          </div>}
      </Card>
    )}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
      {INTG_LIST.map((intg,i)=>{
        const isGmail=intg.name==="Gmail";
        const isShopify=intg.name==="Shopify";
        const isAgencyGoogle=intg.name==="Google Drive"||intg.name==="Google Sheets";
        const isInstagram=intg.name==="Instagram";
        const realConnection=isGmail||isShopify||isAgencyGoogle||isInstagram;
        // A dead Instagram token is NOT connected: Effortless showed "✓ Connected"
        // for 3 weeks while every tags poll failed with "Session has expired".
        const igExpired=isInstagram&&!!igStatus?.instagram_business_account_id&&!!igStatus?.instagram_token_expires_at&&new Date(igStatus.instagram_token_expires_at)<new Date();
        const connected=(isGmail&&gmailInboxes.length>0)||(isShopify&&!!shopifyStatus)||(isAgencyGoogle&&!!agencyDriveStatus?.email)||(isInstagram&&!!igStatus?.instagram_business_account_id&&!igExpired);
        // Show which account this is connected to. Per Kristijan's Loom feedback —
        // useful to glance at and confirm "yes, this is the right inbox/shop."
        const connectedAs = isGmail
            ? (gmailStatus?.email_address ? `${gmailStatus.email_address}${gmailInboxes.length>1?` (+${gmailInboxes.length-1} more)`:""}` : "")
          : isShopify
            ? (shopifyStatus?.shop_name || shopifyStatus?.shop_domain || "")
          : isAgencyGoogle
            ? (agencyDriveStatus?.email || "")
          : isInstagram
            ? (igStatus?.instagram_username ? `@${igStatus.instagram_username}` : "")
          : "";
        const synced=""; // Connected-as line replaces the synced timestamp
        const CONN_GREEN=G, CONN_RED="#EF4444";
        const busy=(isGmail&&gmailBusy)||(isShopify&&shopifyBusy)||(isAgencyGoogle&&agencyDriveBusy)||(isInstagram&&igBusy);
        const onConnectClick=isGmail?startGmailConnect:isShopify?(()=>setShowShopifyConnect(true)):isAgencyGoogle?startAgencyDriveConnect:isInstagram?startInstagramConnect:()=>toast(`${intg.name} integration — coming soon`,"info");
        const onDisconnectClick=isGmail?disconnectGmail:isShopify?disconnectShopify:isAgencyGoogle?disconnectAgencyDrive:isInstagram?disconnectInstagram:null;
        const borderColor=connected?CONN_GREEN:realConnection?BORDER:"#E5E7EB";
        const bgColor=connected?`${CONN_GREEN}08`:realConnection?"#fff":"#FAFAFB";
        return(<Card key={i} style={{padding:20,border:`${connected?1.5:1}px solid ${borderColor}`,background:bgColor,opacity:realConnection?1:0.85}}>
        <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
          <div style={{width:46,height:46,borderRadius:12,background:connected?intg.bg:"#f8faf9",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,border:`1px solid ${connected?intg.color+"30":BORDER}`,filter:realConnection?"none":"grayscale(40%)"}}>{intg.svg}</div>
          <div style={{flex:1}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div><div style={{fontWeight:700,color:DARK,fontSize:14,marginBottom:3}}>{intg.name}</div><div style={{fontSize:12,color:"#9ab8a6"}}>{intg.desc}</div></div>
              {isAgencyGoogle&&!isAgencyOwner
                ?<span title="The agency Google connection is shared across every brand — only the agency owner can connect or disconnect it." style={{background:"#f8faf9",color:"#9ab8a6",border:`1px solid ${BORDER}`,borderRadius:8,padding:"6px 14px",fontSize:11,fontWeight:700,flexShrink:0,whiteSpace:"nowrap"}}>Owner-managed</span>
                :realConnection
                ?<button disabled={busy} onClick={connected?onDisconnectClick:onConnectClick} style={{background:connected?"#fff":CONN_RED,color:connected?CONN_RED:"#fff",border:connected?`1px solid ${CONN_RED}30`:"none",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:busy?"wait":"pointer",flexShrink:0,opacity:busy?0.6:1}}>{busy?(connected?"Disconnecting…":(isGmail||isAgencyGoogle?"Opening Google…":"Connecting…")):connected?"Disconnect":"Connect"}</button>
                :<button disabled style={{background:"#f3f4f6",color:"#9ca3af",border:"1px solid #e5e7eb",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"not-allowed",flexShrink:0}}>Coming soon</button>
              }
            </div>
            {connected
              ?<div style={{marginTop:10,display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                  <span style={{display:"inline-flex",alignItems:"center",gap:5,background:CONN_GREEN+"18",color:"#166534",fontWeight:700,fontSize:11,padding:"3px 9px",borderRadius:6,border:`1px solid ${CONN_GREEN}40`}}>✓ Connected</span>
                  {connectedAs && <span title={connectedAs} style={{fontSize:11,color:DARK,fontWeight:500,fontFamily:"DM Mono, monospace",maxWidth:240,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>· {connectedAs}</span>}
                  {synced&&<Badge label={synced} color="#F8FAF9" text="#6B7280"/>}
                </div>
              :igExpired
                ?<div style={{marginTop:8,fontSize:12,color:CONN_RED,fontWeight:600,display:"inline-flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>⚠ Token expired {new Date(igStatus.instagram_token_expires_at).toLocaleDateString("en-AU",{day:"numeric",month:"short"})}{igStatus?.instagram_username?` · @${igStatus.instagram_username}`:""} — tagged posts aren't being pulled. Reconnect.</div>
              :realConnection
                ?<div style={{marginTop:8,fontSize:12,color:CONN_RED,fontWeight:600,display:"inline-flex",alignItems:"center",gap:5}}>⚠ Not connected</div>
                :<div style={{marginTop:8,fontSize:12,color:"#F59E0B",fontWeight:600,display:"inline-flex",alignItems:"center",gap:5}}>⚠ Not connected · coming soon</div>}
          </div>
        </div>
      </Card>);})}
    </div>
  </div>);
}

// Team membership management for the current active brand. Owner-only.
// Lists current members + pending invites + new-invite form.
function TeamMembersCard({brandId,brandName}){
  const [members,setMembers] = useState([]);
  const [loaded,setLoaded] = useState(false);
  const [isOwner,setIsOwner] = useState(false);
  const [newInvite,setNewInvite] = useState({email:"",role:"account_manager"});
  const [inviting,setInviting] = useState(false);
  const [copiedId,setCopiedId] = useState(null);
  const { user } = useUser();
  const myClerkId = user?.id;

  const load = async()=>{
    if(!brandId)return;
    const{data}=await supabase.from('brand_members')
      .select('*').eq('brand_id',brandId).is('removed_at',null)
      .order('invited_at',{ascending:true});
    setMembers(data||[]);
    setIsOwner((data||[]).some(m=>m.clerk_user_id===myClerkId&&m.role==='owner'&&m.accepted_at));
    setLoaded(true);
  };
  useEffect(()=>{load();/* eslint-disable-line react-hooks/exhaustive-deps */},[brandId,myClerkId]);

  const invite = async()=>{
    const email=newInvite.email.trim().toLowerCase();
    if(!email||!email.includes('@')){toast('Enter a valid email','error');return;}
    if(members.some(m=>(m.email||'').toLowerCase()===email)){toast('Already invited / member','error');return;}
    setInviting(true);
    const token=`${Math.random().toString(36).slice(2,10)}${Date.now().toString(36)}`;
    const{data:inserted,error}=await supabase.from('brand_members').insert({
      brand_id:brandId, email, role:newInvite.role,
      invite_token:token, invited_by_clerk_user_id:myClerkId,
    }).select().single();
    if(error){setInviting(false);toast(`Invite failed: ${error.message}`,'error');return;}
    logSecurityEvent(brandId,'member.invite','brand_member',inserted.id,{email,role:newInvite.role});
    // Fire the real email via the brand's primary Gmail. Best-effort — failure
    // doesn't undo the invite, operator can still copy the URL manually.
    try{
      const t=await window.Clerk?.session?.getToken();
      const r=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-invite-email`,{
        method:'POST',headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},
        body:JSON.stringify({member_id:inserted.id, app_url:window.location.origin}),
      });
      const body=await r.json();
      if(!r.ok){
        toast(`Invite saved, email send failed: ${body.message||body.error||r.status}. Copy the link from the pending row to share manually.`,'error');
      }else{
        toast(`✓ Invite email sent to ${email}`);
      }
    }catch(e){
      toast(`Invite saved, email send failed: ${e.message}. Copy the link to share manually.`,'error');
    }
    setInviting(false);
    setNewInvite({email:"",role:"account_manager"});
    load();
  };
  const resendInvite = async(member)=>{
    try{
      const t=await window.Clerk?.session?.getToken();
      const r=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-invite-email`,{
        method:'POST',headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},
        body:JSON.stringify({member_id:member.id, app_url:window.location.origin}),
      });
      const body=await r.json();
      if(!r.ok){toast(`Resend failed: ${body.message||body.error||r.status}`,'error');}
      else{toast(`✓ Email resent to ${member.email}`);load();}
    }catch(e){toast(`Resend failed: ${e.message}`,'error');}
  };
  const remove = async(member)=>{
    if(member.role==='owner'&&members.filter(m=>m.role==='owner'&&m.accepted_at).length<=1){
      toast("Can't remove the last owner",'error');return;
    }
    if(!(await confirmDialog({title:`Remove ${member.email||member.clerk_user_id}?`,body:"They'll lose access to this brand immediately. You can re-invite later.",destructive:true,confirmLabel:"Remove"})))return;
    const{error}=await supabase.from('brand_members').update({removed_at:new Date().toISOString()}).eq('id',member.id);
    if(error){toast(`Remove failed: ${error.message}`,'error');return;}
    logSecurityEvent(brandId,'member.remove','brand_member',member.id,{email:member.email||null,role:member.role});
    toast('Member removed');
    load();
  };
  const changeRole = async(member,newRole)=>{
    if(member.role==='owner'&&newRole!=='owner'&&members.filter(m=>m.role==='owner'&&m.accepted_at).length<=1){
      toast("Can't demote the last owner",'error');return;
    }
    const{error}=await supabase.from('brand_members').update({role:newRole}).eq('id',member.id);
    if(error){toast(`Update failed: ${error.message}`,'error');return;}
    logSecurityEvent(brandId,'member.role_change','brand_member',member.id,{email:member.email||null,from:member.role,to:newRole});
    toast(`Role updated to ${newRole}`);
    load();
  };
  const inviteUrl = (token)=>{
    const base=typeof window!=='undefined'?`${window.location.origin}`:'https://app.kolabs.agency';
    return `${base}/?invite=${token}`;
  };
  const copyInviteUrl = async(member)=>{
    if(!member.invite_token)return;
    try{
      await navigator.clipboard.writeText(inviteUrl(member.invite_token));
      setCopiedId(member.id);setTimeout(()=>setCopiedId(null),2000);
      toast('Invite link copied');
    }catch{toast('Copy failed','error');}
  };

  if(!loaded)return(<Card style={{padding:24}}><div style={{fontSize:12,color:MUTED}}>Loading team…</div></Card>);

  const accepted=members.filter(m=>m.accepted_at);
  const pending=members.filter(m=>!m.accepted_at);

  return(<div style={{display:"flex",flexDirection:"column",gap:12}}>
    <Card style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:12}}>
        <div>
          <div style={{fontWeight:700,color:DARK,fontSize:15}}>Team members</div>
          <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:540,lineHeight:1.55}}>Operators who can access <strong>{brandName}</strong>. Owners can invite + manage roles + see team activity. Account managers (members) work in the brand but don't see team-level reports.</div>
        </div>
        {!isOwner&&<span style={{background:"#FEF3C7",color:"#92400E",border:"1px solid #FDE68A",borderRadius:6,padding:"4px 9px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>View-only</span>}
      </div>
      {accepted.length===0?
        <div style={{padding:"18px 0",textAlign:"center",color:MUTED,fontSize:13}}>No active members.</div>
        :<div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:isOwner?16:0}}>
          {accepted.map(m=>{
            const isMe=m.clerk_user_id===myClerkId;
            const initials=(m.email||m.clerk_user_id||'?').charAt(0).toUpperCase();
            return(<div key={m.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",border:`1px solid ${BORDER}`,borderRadius:10,background:isMe?G+"06":"#fff"}}>
              <div style={{width:32,height:32,borderRadius:"50%",background:G+"18",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,color:G,fontSize:13,flexShrink:0}}>{initials}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:DARK}}>{m.email||<span style={{fontFamily:"DM Mono, monospace",fontSize:11}}>{m.clerk_user_id?.slice(0,16)}…</span>}{isMe&&<span style={{color:MUTED,fontWeight:400,marginLeft:6}}>(you)</span>}</div>
                <div style={{fontSize:11,color:MUTED,marginTop:2}}>Joined {new Date(m.accepted_at).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"})}</div>
              </div>
              {isOwner&&!isMe?
                <select value={m.role} onChange={e=>changeRole(m,e.target.value)} style={{padding:"5px 9px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:11,fontWeight:600,background:"#fff",color:DARK}}>
                  {['owner','account_manager','view_only'].map(r=><option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
                </select>
                :<span style={{background:m.role==='owner'?G+"18":"#F3F4F6",color:m.role==='owner'?"#166534":MUTED,border:`1px solid ${m.role==='owner'?G+"30":BORDER}`,borderRadius:5,padding:"3px 9px",fontSize:11,fontWeight:700,textTransform:"capitalize"}}>{(m.role||'').replace(/_/g,' ')}</span>
              }
              {isOwner&&!isMe&&<button onClick={()=>remove(m)} title="Remove" style={{border:"none",background:"none",color:"#d1d5db",fontSize:18,cursor:"pointer",padding:"2px 6px"}} onMouseEnter={e=>e.currentTarget.style.color="#DC2626"} onMouseLeave={e=>e.currentTarget.style.color="#d1d5db"}>×</button>}
            </div>);
          })}
        </div>
      }

      {isOwner&&pending.length>0&&(<div style={{marginBottom:16}}>
        <div style={{fontSize:11,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:8}}>Pending invites</div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {pending.map(m=>{
            const sent=!!m.invite_sent_at;
            const sendErr=m.invite_send_error;
            return(<div key={m.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",border:`1px dashed ${sendErr?"#FECACA":"#FDE68A"}`,borderRadius:10,background:sendErr?"#FEF2F2":"#FFFBEB"}}>
              <div style={{width:32,height:32,borderRadius:"50%",background:sendErr?"#FEE2E2":"#FEF3C7",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{sendErr?"⚠":"📨"}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:DARK}}>{m.email}</div>
                <div style={{fontSize:11,color:sendErr?"#991B1B":"#92400E",marginTop:2}}>
                  Invited {new Date(m.invited_at).toLocaleDateString("en-AU",{day:"numeric",month:"short"})} · role: {m.role}
                  {sent&&!sendErr&&<span style={{color:"#166534",marginLeft:8,fontWeight:700}}>· ✓ Email sent {new Date(m.invite_sent_at).toLocaleDateString("en-AU",{day:"numeric",month:"short"})}</span>}
                  {sendErr&&<span style={{color:"#DC2626",marginLeft:8,fontWeight:700}}>· ✗ Send failed</span>}
                </div>
                {sendErr&&<div style={{fontSize:10,color:"#991B1B",marginTop:3,fontFamily:"DM Mono, monospace",wordBreak:"break-word"}}>{sendErr}</div>}
              </div>
              <button onClick={()=>resendInvite(m)} title={sent?"Resend email":"Send email"} style={{border:"1px solid #FDE68A",background:"#fff",color:"#92400E",borderRadius:7,padding:"6px 12px",fontWeight:700,fontSize:11,cursor:"pointer"}}>{sent?"Resend":"Send email"}</button>
              <button onClick={()=>copyInviteUrl(m)} style={{border:`1px solid ${BORDER}`,background:"#fff",color:DARK,borderRadius:7,padding:"6px 12px",fontWeight:600,fontSize:11,cursor:"pointer"}}>{copiedId===m.id?"✓ Copied":"Copy link"}</button>
              <button onClick={()=>remove(m)} title="Cancel invite" style={{border:"none",background:"none",color:"#d1d5db",fontSize:18,cursor:"pointer",padding:"2px 6px"}}>×</button>
            </div>);
          })}
        </div>
      </div>)}

      {isOwner&&(<div style={{paddingTop:16,borderTop:`1px solid ${BORDER}`,marginTop:4}}>
        <div style={{fontSize:11,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:8}}>Invite a team member</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <input value={newInvite.email} onChange={e=>setNewInvite(p=>({...p,email:e.target.value}))} onKeyDown={e=>{if(e.key==='Enter')invite();}} placeholder="email@company.com" style={{flex:1,minWidth:200,padding:"8px 11px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none"}}/>
          <select value={newInvite.role} onChange={e=>setNewInvite(p=>({...p,role:e.target.value}))} style={{padding:"8px 11px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,background:"#fff",color:DARK}}>
            {['account_manager','view_only','owner'].map(r=><option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
          </select>
          <button onClick={invite} disabled={inviting} style={{background:DARK,color:G,border:"none",borderRadius:8,padding:"8px 16px",fontWeight:700,fontSize:13,cursor:inviting?"wait":"pointer"}}>{inviting?"Inviting…":"Send invite"}</button>
        </div>
        <div style={{fontSize:11,color:MUTED,marginTop:6,lineHeight:1.5}}>They get an email invite. You can also manage everyone across all brands in <strong style={{color:DARK}}>Agency workspace → Team</strong>.</div>
      </div>)}
    </Card>
  </div>);
}

// Operator activity tracking — sessions + actions surfaced per Clerk user.
// Today: shows the current operator their own data (RLS-scoped). When Katarina
// joins (separate Clerk account) her data accumulates here too — and once the
// brand_members table goes in, owner will see ALL team members' rollups.
function TeamActivityCard({userProfile={}, currentUserId=null}={}){
  const { user } = useUser();
  const myUserId = currentUserId || user?.id;
  const [sessions,setSessions] = useState([]);
  const [actions,setActions] = useState([]);
  const [emailByUserId,setEmailByUserId] = useState({}); // clerk_user_id → email
  const [loaded,setLoaded] = useState(false);
  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      // Pull last 30 days of sessions + actions for the current user (RLS handles the scope).
      const since=new Date(Date.now()-30*86400000).toISOString();
      const [s,a]=await Promise.all([
        supabase.from('operator_sessions').select('*').gte('last_active_at',since).order('started_at',{ascending:false}),
        supabase.from('operator_actions').select('*').gte('created_at',since).order('created_at',{ascending:false}).limit(200),
      ]);
      if(cancelled)return;
      setSessions(s.data||[]); setActions(a.data||[]);

      // Email lookup — collect every unique clerk_user_id we have activity for
      // and pull the email from brand_members. Replaces "user_3E5...pdMR" style
      // truncated IDs with actual emails in the UI (per Kristijan 2026-05-25).
      // RLS lets us see brand_members for any brand we're a member of, so as
      // an owner we can see emails for our agency's teammates.
      const uniqueIds = [...new Set([
        ...(s.data||[]).map((r)=>r.clerk_user_id).filter(Boolean),
        ...(a.data||[]).map((r)=>r.clerk_user_id).filter(Boolean),
      ])];
      if (uniqueIds.length > 0) {
        const { data: members } = await supabase.from('brand_members')
          .select('clerk_user_id, email')
          .in('clerk_user_id', uniqueIds)
          .not('email','is',null);
        if (!cancelled) {
          const map = {};
          for (const m of (members||[])) if (m.email) map[m.clerk_user_id] = m.email;
          setEmailByUserId(map);
        }
      }
      setLoaded(true);
    })();
    return()=>{cancelled=true;};
  },[]);

  // Roll sessions up into per-user-per-day buckets.
  //   totalMs  = last_active_at - started_at (heartbeated session liveness)
  //   activeMs = active_seconds * 1000 (only counts when input within 5min)
  // We keep both so owner can spot idle-heavy sessions: total >> active means
  // tab was open but operator wasn't touching the page.
  const dailyByUser = useMemo(()=>{
    const map=new Map(); // key: userId|YYYY-MM-DD -> {totalMs, activeMs, count}
    for(const s of sessions){
      const start=new Date(s.started_at).getTime();
      const end=new Date(s.last_active_at).getTime();
      const totalMs=Math.max(0,end-start);
      const activeMs=Math.max(0,(s.active_seconds||0)*1000);
      const date=new Date(s.started_at).toISOString().slice(0,10);
      const key=`${s.clerk_user_id}|${date}`;
      const cur=map.get(key)||{userId:s.clerk_user_id,date,totalMs:0,activeMs:0,count:0};
      cur.totalMs+=totalMs; cur.activeMs+=activeMs; cur.count++;
      map.set(key,cur);
    }
    return [...map.values()].sort((a,b)=>b.date.localeCompare(a.date)||b.activeMs-a.activeMs);
  },[sessions]);

  // Last-30-day totals per user. Same two-clock model as daily. Window
  // matches the data we already fetch from the DB so total + active are
  // both honest 30-day numbers (used to be 7 days, which made "total"
  // way smaller than operators' actual cumulative time — confusing).
  const thirtyDayByUser = useMemo(()=>{
    const cutoff=Date.now()-30*86400000;
    const totals=new Map();
    for(const s of sessions){
      const start=new Date(s.started_at).getTime();
      const end=new Date(s.last_active_at).getTime();
      if(end<cutoff)continue;
      // Note: clipping at cutoff for totalMs but not for activeMs — active_seconds
      // is a session-lifetime counter, can't be sliced. Sessions that started before
      // the 30d window contribute their full active_seconds; rare in practice.
      const totalMs=Math.max(0,end-Math.max(start,cutoff));
      const activeMs=Math.max(0,(s.active_seconds||0)*1000);
      const cur=totals.get(s.clerk_user_id)||{userId:s.clerk_user_id,totalMs:0,activeMs:0,sessions:0};
      cur.totalMs+=totalMs; cur.activeMs+=activeMs; cur.sessions++;
      totals.set(s.clerk_user_id,cur);
    }
    // Add action counts.
    for(const a of actions){
      if(new Date(a.created_at).getTime()<cutoff)continue;
      const cur=totals.get(a.clerk_user_id)||{userId:a.clerk_user_id,totalMs:0,activeMs:0,sessions:0};
      cur.actions=(cur.actions||0)+1;
      totals.set(a.clerk_user_id,cur);
    }
    return [...totals.values()].sort((a,b)=>b.activeMs-a.activeMs);
  },[sessions,actions]);

  const fmtDuration=ms=>{
    if(!ms)return "0m";
    const mins=Math.round(ms/60000);
    if(mins<60)return `${mins}m`;
    const h=Math.floor(mins/60), m=mins%60;
    return m>0?`${h}h ${m}m`:`${h}h`;
  };
  const shortUserId=id=>id?(id.length>16?`${id.slice(0,8)}…${id.slice(-4)}`:id):'(unknown)';
  // Display name precedence: email (if we know it) > shortened user id.
  // Self-row gets the Clerk-known first/last name for a more personal touch.
  const operatorLabel=(uid)=>emailByUserId[uid]||shortUserId(uid);

  if(!loaded)return(<Card style={{padding:24}}><div style={{fontSize:12,color:MUTED}}>Loading activity…</div></Card>);

  // % active of total — surface engagement quality at a glance.
  const pctActive = (activeMs,totalMs)=>{
    if(!totalMs)return null;
    return Math.min(100,Math.round((activeMs/totalMs)*100));
  };
  // Color-code engagement: green ≥70%, amber 40-70%, red <40%
  const engagementColor = pct => pct==null?MUTED:(pct>=70?"#16a34a":pct>=40?"#d97706":"#dc2626");

  return(<div style={{display:"flex",flexDirection:"column",gap:12}}>
    <Card style={{padding:24}}>
      <div style={{marginBottom:14}}>
        <div style={{fontWeight:700,color:DARK,fontSize:15}}>Team activity</div>
        <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:580,lineHeight:1.55}}>Active vs total time-in-app + audit trail per operator.</div>
      </div>

      {/* Legend — defines every term so the numbers below mean something */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:8,marginBottom:18,padding:14,background:"#fafcfb",border:`1px dashed ${BORDER}`,borderRadius:10}}>
        <div>
          <div style={{fontSize:10,fontWeight:800,color:G,letterSpacing:".05em",marginBottom:3}}>ACTIVE</div>
          <div style={{fontSize:11,color:DARK,lineHeight:1.4}}>Real work time. Counted when the operator moved mouse / typed / scrolled within the last 5 minutes.</div>
        </div>
        <div>
          <div style={{fontSize:10,fontWeight:800,color:MUTED,letterSpacing:".05em",marginBottom:3}}>TOTAL</div>
          <div style={{fontSize:11,color:DARK,lineHeight:1.4}}>Tab open & visible. Includes idle time where they walked away with the tab open.</div>
        </div>
        <div>
          <div style={{fontSize:10,fontWeight:800,color:"#16a34a",letterSpacing:".05em",marginBottom:3}}>ENGAGED %</div>
          <div style={{fontSize:11,color:DARK,lineHeight:1.4}}>Active ÷ Total. Green ≥70%, amber 40-70%, red &lt;40%. Low % = lots of dead tabs.</div>
        </div>
        <div>
          <div style={{fontSize:10,fontWeight:800,color:"#1D4ED8",letterSpacing:".05em",marginBottom:3}}>SESSION</div>
          <div style={{fontSize:11,color:DARK,lineHeight:1.4}}>One row per app-open until tab closed. A user can have multiple per day if they reload.</div>
        </div>
        <div>
          <div style={{fontSize:10,fontWeight:800,color:"#7c3aed",letterSpacing:".05em",marginBottom:3}}>ACTIONS / HR</div>
          <div style={{fontSize:11,color:DARK,lineHeight:1.4}}>Tracked things they did per active hour: status changes, flow edits, sends. Higher = more output per hour.</div>
        </div>
      </div>
      {sessions.length===0?
        <div style={{padding:"22px 0",textAlign:"center",color:MUTED,fontSize:13}}>No sessions logged yet — they start tracking as soon as someone opens the app.</div>
        :<>
        <div style={{fontSize:11,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:8}}>Last 30 days</div>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:18}}>
          {thirtyDayByUser.map(u=>{
            const pct=pctActive(u.activeMs,u.totalMs);
            const actionsPerHr = u.activeMs>0 ? ((u.actions||0)/(u.activeMs/3600000)).toFixed(1) : null;
            const isMe = u.userId === myUserId;
            const displayName = isMe && (userProfile?.firstName || userProfile?.lastName)
              ? `${userProfile.firstName||""} ${userProfile.lastName||""}`.trim()
              : operatorLabel(u.userId);
            return(<div key={u.userId} style={{display:"flex",alignItems:"center",gap:14,padding:"12px 14px",border:`1px solid ${BORDER}`,borderRadius:10,background:isMe?"#fafcfb":"#fff"}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:G+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:G,flexShrink:0}}>{(displayName||"?").charAt(0).toUpperCase()}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <div style={{fontSize:13,fontWeight:700,color:DARK,fontFamily: isMe||emailByUserId[u.userId]?"inherit":"DM Mono, monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{displayName}</div>
                  {isMe && <span style={{background:G+"18",color:G,padding:"1px 6px",borderRadius:4,fontSize:9,fontWeight:700,letterSpacing:".05em"}}>YOU</span>}
                </div>
                <div style={{fontSize:11,color:MUTED,marginTop:2}}>
                  {u.sessions} session{u.sessions!==1?"s":""} · {u.actions||0} action{(u.actions||0)!==1?"s":""}
                  {actionsPerHr!==null && <> · <span style={{color:DARK,fontWeight:600}}>{actionsPerHr}/hr</span></>}
                </div>
              </div>
              <div style={{display:"flex",gap:18,alignItems:"baseline",flexShrink:0}}>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:18,fontWeight:800,color:DARK,letterSpacing:"-.02em"}}>{fmtDuration(u.activeMs)}</div>
                  <div style={{fontSize:10,color:MUTED,marginTop:1}}>active</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:14,fontWeight:600,color:MUTED}}>{fmtDuration(u.totalMs)}</div>
                  <div style={{fontSize:10,color:MUTED,marginTop:1}}>total</div>
                </div>
                {pct!==null && <div style={{textAlign:"right",minWidth:42}}>
                  <div style={{fontSize:14,fontWeight:700,color:engagementColor(pct)}}>{pct}%</div>
                  <div style={{fontSize:10,color:MUTED,marginTop:1}}>engaged</div>
                </div>}
              </div>
            </div>);
          })}
        </div>

        <div style={{fontSize:11,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:8}}>Daily breakdown (last 30 days)</div>
        <div style={{maxHeight:280,overflowY:"auto",border:`1px solid ${BORDER}`,borderRadius:8}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:"#f8faf9"}}>
              {["Date","Operator","Active","Total","Engaged","Sessions"].map(h=><th key={h} style={{padding:"7px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:MUTED,textTransform:"uppercase",letterSpacing:".05em"}}>{h}</th>)}
            </tr></thead>
            <tbody>{dailyByUser.map((d,i)=>{
              const pct=pctActive(d.activeMs,d.totalMs);
              return(<tr key={i} style={{borderTop:`1px solid ${BORDER}`}}>
                <td style={{padding:"7px 12px",fontSize:12,color:DARK}}>{new Date(d.date+"T00:00:00").toLocaleDateString("en-AU",{day:"numeric",month:"short",weekday:"short"})}</td>
                <td style={{padding:"7px 12px",fontSize:12,color:DARK,fontFamily:emailByUserId[d.userId]?"inherit":"DM Mono, monospace"}}>{operatorLabel(d.userId)}</td>
                <td style={{padding:"7px 12px",fontSize:12,fontWeight:700,color:DARK}}>{fmtDuration(d.activeMs)}</td>
                <td style={{padding:"7px 12px",fontSize:12,color:MUTED}}>{fmtDuration(d.totalMs)}</td>
                <td style={{padding:"7px 12px",fontSize:12,fontWeight:700,color:engagementColor(pct)}}>{pct==null?"—":`${pct}%`}</td>
                <td style={{padding:"7px 12px",fontSize:12,color:MUTED}}>{d.count}</td>
              </tr>);
            })}</tbody>
          </table>
        </div>
        </>
      }
    </Card>

    <Card style={{padding:24}}>
      <div style={{marginBottom:14}}>
        <div style={{fontWeight:700,color:DARK,fontSize:15}}>Recent actions</div>
        <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:540,lineHeight:1.55}}>High-signal events: status changes, brand creation, flow edits. Not exhaustive — built to spot patterns + recover from mistakes, not log every keystroke.</div>
      </div>
      {actions.length===0?
        <div style={{padding:"22px 0",textAlign:"center",color:MUTED,fontSize:13}}>No actions logged yet.</div>
        :<div style={{maxHeight:360,overflowY:"auto"}}>
          {actions.slice(0,50).map(a=>(<div key={a.id} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 0",borderBottom:`1px solid #f0f4f2`}}>
            <span style={{background:G+"18",color:G,padding:"2px 8px",borderRadius:5,fontSize:10,fontWeight:700,fontFamily:"DM Mono, monospace",flexShrink:0}}>{a.action}</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,color:DARK}}>
                {a.target_type&&<span style={{color:MUTED}}>{a.target_type}: </span>}
                {a.target_id&&<span style={{fontWeight:600}}>{a.target_id}</span>}
                {a.details&&Object.keys(a.details).length>0&&<span style={{color:MUTED,fontSize:11}}> · {Object.entries(a.details).slice(0,3).map(([k,v])=>`${k}=${v}`).join(", ")}</span>}
              </div>
              <div style={{fontSize:11,color:MUTED,marginTop:2}}>
                <span style={{fontFamily:emailByUserId[a.clerk_user_id]?"inherit":"DM Mono, monospace"}}>{operatorLabel(a.clerk_user_id)}</span> · {new Date(a.created_at).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})}
              </div>
            </div>
          </div>))}
        </div>
      }
    </Card>
  </div>);
}

// ----------------------------------------------------------------------
// AgencyView — owner-only top-level "master dashboard" for cross-brand work.
// Sits at /agency (active==="agency") in the nav, separate from per-brand
// Settings. Foundation that's meant to grow over time: brands overview,
// team matrix, activity, Drive, and (queued) recent-changes feed + brand
// agreements.
//
// Each section is a self-contained card so we can iterate independently.
// ----------------------------------------------------------------------
// AgencyPage — shared wrapper that gives every agency sidebar item a
// consistent header (big title + subtitle). The sidebar handles navigation;
// the page body is just the relevant card(s).
function AgencyPage({title, subtitle, children}){
  return(<div style={{display:"flex",flexDirection:"column",gap:14,maxWidth:1100}}>
    <div style={{marginBottom:4}}>
      <div style={{fontSize:22,fontWeight:800,color:DARK,letterSpacing:"-.02em"}}>{title}</div>
      {subtitle && <div style={{fontSize:13,color:MUTED,marginTop:4,maxWidth:680,lineHeight:1.55}}>{subtitle}</div>}
    </div>
    {children}
  </div>);
}

// Agency Dashboard — overview of cross-brand operations.
// 4 KPI cards (sent this week, replies pending, sequences active, brands count)
// + recent sends feed + recent replies feed + Gmail watch status.
function AgencyDashboardPage({brandsList=[], userId}){
  const [stats, setStats] = useState({sentThisWeek:0, repliesPending:0, sequencesActive:0});
  const [recentSends, setRecentSends] = useState([]);
  const [recentReplies, setRecentReplies] = useState([]);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      const weekAgo = new Date(Date.now()-7*86400000).toISOString();

      // Gmail status (for the watch indicator on the dashboard)
      try{
        const token=await window.Clerk?.session?.getToken();
        const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-gmail-oauth/status`,{
          method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'}, body:'{}',
        });
        if(!cancelled) setGmailStatus((await resp.json())||{connected:false});
      }catch{}

      const [cSent, cReplies, cActive] = await Promise.all([
        supabase.from('agency_outreach_sends').select('id',{count:'exact',head:true}).eq('status','sent').gte('sent_at',weekAgo),
        supabase.from('agency_outreach_messages').select('id',{count:'exact',head:true}).eq('direction','inbound').is('read_by_owner_at',null),
        supabase.from('agency_prospects').select('id',{count:'exact',head:true}).not('sequence_activated_at','is',null).eq('sequence_paused',false),
      ]);

      const [{data: sends}, {data: replies}] = await Promise.all([
        supabase.from('agency_outreach_sends').select('id, step_index, sent_at, rendered_subject, prospect_id').eq('status','sent').order('sent_at',{ascending:false}).limit(10),
        supabase.from('agency_outreach_messages').select('id, from_email, subject, body_text, sent_at, reply_hint, read_by_owner_at, prospect_id').eq('direction','inbound').order('sent_at',{ascending:false}).limit(8),
      ]);

      const ids = [...new Set([...(sends||[]).map(s=>s.prospect_id), ...(replies||[]).map(r=>r.prospect_id)].filter(Boolean))];
      const{data: prospects} = ids.length
        ? await supabase.from('agency_prospects').select('id, brand_name, contact_first_name, contact_last_name').in('id',ids)
        : {data:[]};
      const pMap = Object.fromEntries((prospects||[]).map(p=>[p.id,p]));

      if(cancelled) return;
      setStats({
        sentThisWeek: cSent.count||0,
        repliesPending: cReplies.count||0,
        sequencesActive: cActive.count||0,
      });
      setRecentSends((sends||[]).map(s=>({...s, prospect:pMap[s.prospect_id]})));
      setRecentReplies((replies||[]).map(r=>({...r, prospect:pMap[r.prospect_id]})));
      setLoaded(true);
    })();
    return()=>{cancelled=true;};
  },[]);

  const kpi = (label, value, color, hint)=>(
    <div style={{padding:"16px 18px",border:`1px solid ${BORDER}`,borderRadius:10,background:"#fff"}}>
      <div style={{fontSize:10,fontWeight:800,letterSpacing:".06em",color:color||MUTED,textTransform:"uppercase",marginBottom:6}}>{label}</div>
      <div style={{fontSize:28,fontWeight:800,color:DARK,letterSpacing:"-.02em",lineHeight:1}}>{value}</div>
      {hint && <div style={{fontSize:10,color:MUTED,marginTop:5}}>{hint}</div>}
    </div>
  );

  const stepLabel = (i)=> i===0?"initial":`follow-up ${i}`;

  return(<AgencyPage title="Agency dashboard" subtitle={`Cross-brand owner view. ${brandsList.length} brand${brandsList.length===1?"":"s"} operated · agency Gmail ${gmailStatus?.connected?"connected":"disconnected"}${gmailStatus?.watch_active?" · real-time on":""}.`}>
    {/* KPI grid */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:10}}>
      {kpi("Sent this week", stats.sentThisWeek, "#1D4ED8", "cold + follow-ups")}
      {kpi("Replies pending", stats.repliesPending, stats.repliesPending>0?"#DC2626":"#16a34a", stats.repliesPending===0?"all caught up":"unread in inbox")}
      {kpi("Sequences active", stats.sequencesActive, G, "prospects still receiving")}
      {kpi("Brands", brandsList.length, DARK, "in your portfolio")}
    </div>

    {/* Recent sends */}
    <Card style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14}}>
        <div>
          <div style={{fontWeight:700,color:DARK,fontSize:15}}>Recent sends</div>
          <div style={{fontSize:12,color:MUTED,marginTop:3}}>Last 10 outbound emails fired by the worker.</div>
        </div>
      </div>
      {!loaded ? <div style={{fontSize:12,color:MUTED,padding:"10px 0"}}>Loading…</div>
        : recentSends.length===0
          ? <div style={{padding:"22px 0",textAlign:"center",color:MUTED,fontSize:13}}>No sends yet. Activate a prospect + run the worker from the Outreach page.</div>
          : <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {recentSends.map(s=>(<div key={s.id} style={{display:"flex",alignItems:"center",gap:12,padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,color:DARK,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.prospect?.brand_name||"(unknown)"} · <span style={{color:MUTED,fontWeight:500}}>{s.rendered_subject||"(no subject)"}</span></div>
                  <div style={{fontSize:11,color:MUTED,marginTop:2}}>{stepLabel(s.step_index)}</div>
                </div>
                <div style={{fontSize:11,color:MUTED,flexShrink:0,whiteSpace:"nowrap"}}>{new Date(s.sent_at).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})}</div>
              </div>))}
            </div>}
    </Card>

    {/* Recent replies */}
    <Card style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14}}>
        <div>
          <div style={{fontWeight:700,color:DARK,fontSize:15}}>Recent replies</div>
          <div style={{fontSize:12,color:MUTED,marginTop:3}}>Inbound responses from prospects. AI hint shown when classified.</div>
        </div>
      </div>
      {!loaded ? <div style={{fontSize:12,color:MUTED,padding:"10px 0"}}>Loading…</div>
        : recentReplies.length===0
          ? <div style={{padding:"22px 0",textAlign:"center",color:MUTED,fontSize:13}}>No replies yet. Replies stream in once prospects respond + the inbox is synced (or real-time is on).</div>
          : <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {recentReplies.map(r=>{
                const unread = !r.read_by_owner_at;
                const preview = (r.body_text||r.subject||"(no body)").replace(/\s+/g," ").slice(0,140);
                return(<div key={r.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",border:`1px solid ${unread?G+"60":BORDER}`,background:unread?G+"06":"#fff",borderRadius:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      <div style={{fontWeight:unread?700:600,color:DARK,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.prospect?.brand_name||"(unknown)"}</div>
                      {r.reply_hint && <span style={{background:"#EFF6FF",color:"#1D4ED8",padding:"1px 6px",borderRadius:4,fontSize:9,fontWeight:700}}>{r.reply_hint.replace(/_/g," ")}</span>}
                      {unread && <span style={{background:G,color:"#000",padding:"1px 6px",borderRadius:10,fontSize:9,fontWeight:800}}>NEW</span>}
                    </div>
                    <div style={{fontSize:11,color:MUTED,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.from_email} · {preview}{preview.length>=140?"…":""}</div>
                  </div>
                  <div style={{fontSize:11,color:MUTED,flexShrink:0,whiteSpace:"nowrap"}}>{new Date(r.sent_at).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})}</div>
                </div>);
              })}
            </div>}
    </Card>
  </AgencyPage>);
}

// Cold outreach page inline content — pipeline + compose + inbox + reports.
// Header is provided by AgencyPage wrapper in the routing dispatcher.
function AgencyOutreachInline({userProfile={}}){
  const [tab, setTab] = useState("pipeline");
  const [gmailStatus, setGmailStatus] = useState(null); // null=loading, {connected, email?, watch_active?, ...}
  const [gmailBusy, setGmailBusy] = useState(false);
  const [watchBusy, setWatchBusy] = useState(false);
  const [calendlyStatus, setCalendlyStatus] = useState(null); // null=loading
  const [calendlyBusy, setCalendlyBusy] = useState(false);
  const [calendlyShowConnect, setCalendlyShowConnect] = useState(false);
  const [calendlyPat, setCalendlyPat] = useState("");

  const loadCalendlyStatus = async()=>{
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-calendly-oauth/status`,{
        method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'}, body:'{}',
      });
      const body=await resp.json();
      setCalendlyStatus(body||{connected:false});
    }catch{ setCalendlyStatus({connected:false}); }
  };
  useEffect(()=>{ loadCalendlyStatus(); },[]);

  const saveCalendlyToken = async()=>{
    if(!calendlyPat.trim()||calendlyBusy) return;
    setCalendlyBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-calendly-oauth/save-token`,{
        method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},
        body: JSON.stringify({pat: calendlyPat.trim()}),
      });
      const body=await resp.json();
      if(!resp.ok){
        if(body.error==='invalid_token') toast("That PAT didn't authenticate with Calendly",'error');
        else if(body.error==='webhook_secret_not_configured') toast("Set AGENCY_CALENDLY_WEBHOOK_SECRET env var in Supabase first",'error');
        else toast(`Calendly connect failed: ${body.error||resp.status}`,'error');
        return;
      }
      if(body.connected_without_webhook){
        toast(`Connected as ${body.email||""} — webhook subscribe failed (${body.error}). Click "Retry webhook" to try again.`,'warning');
      } else {
        toast(`Calendly connected: ${body.email||"OK"}`);
      }
      setCalendlyPat("");
      setCalendlyShowConnect(false);
      await loadCalendlyStatus();
    }catch(e){
      console.error(e);
      toast('Calendly connect failed','error');
    }finally{
      setCalendlyBusy(false);
    }
  };
  const disconnectCalendly = async()=>{
    if(!(await confirmDialog({title:'Disconnect Calendly?',body:'Booking webhooks will stop — new bookings won\'t auto-flip prospect status.',destructive:true,confirmLabel:'Disconnect'}))) return;
    setCalendlyBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-calendly-oauth/disconnect`,{
        method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'}, body:'{}',
      });
      if(!resp.ok){ toast(`Disconnect failed: ${resp.status}`,'error'); return; }
      toast("Calendly disconnected");
      setCalendlyStatus({connected:false});
    }finally{ setCalendlyBusy(false); }
  };

  const loadGmailStatus = async()=>{
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-gmail-oauth/status`,{
        method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'}, body:'{}',
      });
      const body=await resp.json();
      setGmailStatus(body||{connected:false});
    }catch{ setGmailStatus({connected:false}); }
  };
  useEffect(()=>{ loadGmailStatus(); },[]);

  const startWatch = async()=>{
    if(watchBusy) return;
    setWatchBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-gmail-oauth/start-watch`,{
        method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'}, body:'{}',
      });
      const body=await resp.json();
      if(!resp.ok){
        if(body.error==='pubsub_topic_not_configured') toast("Set AGENCY_GMAIL_PUBSUB_TOPIC env var in Supabase first",'error');
        else if(body.error==='watch_failed') toast(`Watch failed: ${body.detail?.slice(0,80)||resp.status}`,'error');
        else toast(`Watch error: ${body.error||resp.status}`,'error');
        return;
      }
      toast("Real-time enabled — replies will land instantly");
      await loadGmailStatus();
    }catch(e){
      console.error(e);
      toast(`Watch failed: ${e.message||e}`,'error');
    }finally{
      setWatchBusy(false);
    }
  };
  const stopWatch = async()=>{
    if(!(await confirmDialog({title:'Stop real-time updates?',body:"You'll need to use 'Sync inbox' manually to pull replies.",destructive:true,confirmLabel:'Stop'}))) return;
    setWatchBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-gmail-oauth/stop-watch`,{
        method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'}, body:'{}',
      });
      if(!resp.ok){ toast(`Stop watch failed: ${resp.status}`,'error'); return; }
      toast("Real-time disabled");
      await loadGmailStatus();
    }finally{ setWatchBusy(false); }
  };

  const connectGmail = async()=>{
    setGmailBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-gmail-oauth/init`,{
        method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'}, body:'{}',
      });
      const body=await resp.json();
      if(!resp.ok||!body.url){ toast(`Connect failed: ${body.error||resp.status}`,'error'); setGmailBusy(false); return; }
      window.location.href=body.url;
    }catch(e){ console.error(e); toast('Connect failed','error'); setGmailBusy(false); }
  };
  const disconnectGmail = async()=>{
    if(!(await confirmDialog({title:'Disconnect agency Gmail?',body:'Pending sends will stop until reconnected.',destructive:true,confirmLabel:'Disconnect'}))) return;
    setGmailBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-gmail-oauth/disconnect`,{
        method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'}, body:'{}',
      });
      if(!resp.ok){ toast(`Disconnect failed: ${resp.status}`,'error'); return; }
      setGmailStatus({connected:false});
      toast('Agency Gmail disconnected');
    }catch(e){ console.error(e); toast('Disconnect failed','error'); }
    finally{ setGmailBusy(false); }
  };

  const gmailConnected = !!gmailStatus?.connected;
  const tabs = [
    {id:"pipeline", label:"Pipeline", description:"Prospects + stages"},
    {id:"compose",  label:"Compose",  description:"Email + PDF + Calendly"},
    {id:"inbox",    label:"Inbox",    description:"Reply threads"},
    {id:"reports",  label:"Reports",  description:"Funnel metrics"},
  ];

  return(<div style={{display:"flex",flexDirection:"column",gap:14}}>
    {/* Gmail + Calendly status pills (header title is provided by AgencyPage) */}
    <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:4}}>
      <AgencyGmailConnectBadge status={gmailStatus} busy={gmailBusy} watchBusy={watchBusy} onConnect={connectGmail} onDisconnect={disconnectGmail} onStartWatch={startWatch} onStopWatch={stopWatch}/>
      <CalendlyConnectBadge status={calendlyStatus} busy={calendlyBusy} onShowConnect={()=>setCalendlyShowConnect(true)} onDisconnect={disconnectCalendly}/>
    </div>

    {calendlyShowConnect && !calendlyStatus?.connected && <div style={{padding:14,background:"#fafcfb",border:`1px solid ${BORDER}`,borderRadius:10}}>
      <div style={{fontWeight:700,color:DARK,fontSize:13,marginBottom:6}}>Connect Calendly</div>
      <div style={{fontSize:11,color:MUTED,lineHeight:1.5,marginBottom:10}}>
        Generate a Personal Access Token at <a href="https://calendly.com/integrations/api_webhooks" target="_blank" rel="noreferrer" style={{color:G,textDecoration:"none",fontWeight:600}}>calendly.com/integrations/api_webhooks ↗</a> → Personal access tokens → Create. Paste it below. We'll auto-subscribe to booking webhooks so meetings flip prospects to "Meeting booked" the moment they book. {`{{calendly_url}}`} in your sequence will substitute your scheduling URL.
      </div>
      <div style={{display:"flex",gap:8}}>
        <input type="password" value={calendlyPat} onChange={e=>setCalendlyPat(e.target.value)} placeholder="eyJraWQiOi..." disabled={calendlyBusy}
          style={{flex:1,padding:"8px 12px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",fontFamily:"DM Mono, monospace"}}/>
        <button onClick={saveCalendlyToken} disabled={!calendlyPat.trim()||calendlyBusy}
          style={{background:calendlyPat.trim()&&!calendlyBusy?DARK:"#f0f0f0",color:calendlyPat.trim()&&!calendlyBusy?G:"#9ab8a6",border:"none",borderRadius:7,padding:"8px 16px",fontSize:13,fontWeight:700,cursor:calendlyPat.trim()&&!calendlyBusy?"pointer":"not-allowed",whiteSpace:"nowrap"}}>
          {calendlyBusy?"Connecting…":"Save & subscribe"}
        </button>
        <button onClick={()=>{setCalendlyShowConnect(false);setCalendlyPat("");}} style={{background:"transparent",border:`1px solid ${BORDER}`,color:MUTED,borderRadius:7,padding:"8px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
      </div>
    </div>}

    {/* Sub-nav */}
    <div style={{display:"flex",gap:2,background:"#fff",borderRadius:10,padding:4,boxShadow:CARD,alignSelf:"flex-start"}}>
      {tabs.map(t=>(<button key={t.id} onClick={()=>setTab(t.id)} title={t.description}
        style={{background:tab===t.id?DARK:"transparent",color:tab===t.id?G:"#9ab8a6",border:"none",borderRadius:7,padding:"7px 16px",fontSize:12,cursor:"pointer",fontWeight:tab===t.id?700:400}}>
        {t.label}
      </button>))}
    </div>

    {tab==="pipeline" && <OutreachPipelineCard/>}
    {tab==="compose"  && <OutreachComposeCard userProfile={userProfile} gmailConnected={gmailConnected}/>}
    {tab==="inbox"    && <OutreachInboxCard gmailConnected={gmailConnected}/>}
    {tab==="reports"  && <OutreachReportsCard/>}
  </div>);
}

// Calendly connection status pill. Different from Gmail because Calendly
// auth is just paste-a-PAT (no OAuth round-trip).
//   status = null (loading) | {connected, webhook_subscribed, calendly_user_email, last_error, ...}
function CalendlyConnectBadge({status, busy, onShowConnect, onDisconnect}){
  if(status===null){
    return(<div style={{padding:"8px 14px",border:`1px solid ${BORDER}`,borderRadius:10,fontSize:12,color:MUTED}}>Checking Calendly…</div>);
  }
  const connected = !!status?.connected;
  const webhookOk = !!status?.webhook_subscribed;
  const flavour = connected ? (webhookOk ? "good" : "warn") : "off";
  const colors = flavour==="good"?{bd:G+"40",bg:G+"08",fg:G,dot:G}
    : flavour==="warn"?{bd:"#FDE68A",bg:"#FFFBEB",fg:"#92400E",dot:"#F59E0B"}
    : {bd:BORDER,bg:"#fff",fg:MUTED,dot:"#d1d5db"};
  return(<div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",border:`1px solid ${colors.bd}`,background:colors.bg,borderRadius:10,fontSize:12,fontWeight:600,color:colors.fg}}>
    <div style={{width:8,height:8,borderRadius:"50%",background:colors.dot}}/>
    {!connected ? "Calendly not connected"
      : webhookOk ? <>Calendly · {status.calendly_user_email||"connected"}</>
      : "Calendly · webhook missing"}
    {!connected
      ? <button disabled={busy} onClick={onShowConnect} style={{marginLeft:6,background:DARK,color:G,border:"none",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:busy?"wait":"pointer"}}>Connect →</button>
      : <button disabled={busy} onClick={onDisconnect} style={{marginLeft:6,background:"transparent",border:"1px solid #FECACA",color:"#DC2626",borderRadius:6,padding:"3px 9px",fontSize:11,fontWeight:600,cursor:busy?"wait":"pointer"}}>Disconnect</button>}
  </div>);
}

// Agency Gmail connection status pill + Gmail watch (real-time) toggle.
//   status = null (loading) | {connected, watch_active, watch_expires_at, email, ...}
function AgencyGmailConnectBadge({status, busy, watchBusy, onConnect, onDisconnect, onStartWatch, onStopWatch}){
  if(status===null){
    return(<div style={{padding:"8px 14px",border:`1px solid ${BORDER}`,borderRadius:10,fontSize:12,color:MUTED}}>Checking Gmail…</div>);
  }
  const connected = !!status?.connected;
  const watchActive = !!status?.watch_active;
  return(<div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
    <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",border:`1px solid ${connected?G+"40":"#FDE68A"}`,background:connected?G+"08":"#FFFBEB",borderRadius:10,fontSize:12,fontWeight:600,color:connected?G:"#92400E"}}>
      <div style={{width:8,height:8,borderRadius:"50%",background:connected?G:"#F59E0B"}}/>
      {connected ? <>{status.email || "Agency Gmail connected"}</> : "Agency Gmail not connected"}
      {!connected
        ? <button disabled={busy} onClick={onConnect} style={{marginLeft:6,background:DARK,color:G,border:"none",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:busy?"wait":"pointer",opacity:busy?0.6:1}}>{busy?"Opening…":"Connect →"}</button>
        : <button disabled={busy} onClick={onDisconnect} style={{marginLeft:6,background:"transparent",border:"1px solid #FECACA",color:"#DC2626",borderRadius:6,padding:"3px 9px",fontSize:11,fontWeight:600,cursor:busy?"wait":"pointer"}}>Disconnect</button>}
    </div>
    {connected && <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",border:`1px solid ${watchActive?G+"40":BORDER}`,background:watchActive?G+"08":"#fff",borderRadius:10,fontSize:12,fontWeight:600,color:watchActive?G:MUTED}}>
      <div style={{width:8,height:8,borderRadius:"50%",background:watchActive?G:"#d1d5db"}}/>
      {watchActive ? "Real-time on" : "Polling only"}
      {!watchActive
        ? <button disabled={watchBusy} onClick={onStartWatch} title="Subscribes the inbox to Gmail push notifications via Pub/Sub. Replies land instantly." style={{marginLeft:6,background:DARK,color:G,border:"none",borderRadius:6,padding:"3px 9px",fontSize:11,fontWeight:700,cursor:watchBusy?"wait":"pointer",opacity:watchBusy?0.6:1}}>{watchBusy?"…":"Enable real-time"}</button>
        : <button disabled={watchBusy} onClick={onStopWatch} title="Stops Gmail push. Manual sync still works." style={{marginLeft:6,background:"transparent",border:`1px solid ${BORDER}`,color:MUTED,borderRadius:6,padding:"3px 9px",fontSize:11,fontWeight:600,cursor:watchBusy?"wait":"pointer"}}>{watchBusy?"…":"Stop"}</button>}
    </div>}
  </div>);
}

// Pipeline tab — prospect list grouped by stage. Loads agency_prospects via RLS.
function OutreachPipelineCard(){
  const { user } = useUser();
  const userId = user?.id;
  const [prospects, setProspects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [activatingId, setActivatingId] = useState(null);
  const [workerBusy, setWorkerBusy] = useState(false);

  const STAGES = [
    {id:"researched", label:"Researched", color:"#9ab8a6"},
    {id:"contacted",  label:"Contacted",  color:"#1D4ED8"},
    {id:"replied",    label:"Replied",    color:"#92400E"},
    {id:"meeting_booked", label:"Meeting booked", color:"#166534"},
    {id:"proposal_sent",  label:"Proposal sent",  color:"#7c3aed"},
    {id:"won",  label:"Won",  color:"#16a34a"},
    {id:"lost", label:"Lost", color:"#9ca3af"},
    {id:"do_not_contact", label:"DNC", color:"#DC2626"},
  ];

  const load = async()=>{
    const{data,error}=await supabase.from('agency_prospects').select('*').order('created_at',{ascending:false});
    if(error){ console.error(error); setLoaded(true); return; }
    setProspects(data||[]);
    setLoaded(true);
  };
  useEffect(()=>{ load(); },[]);

  const countsByStatus = useMemo(()=>{
    const m={}; for(const s of STAGES) m[s.id]=0;
    for(const p of prospects) m[p.status]=(m[p.status]||0)+1;
    return m;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[prospects]);

  const visibleProspects = useMemo(()=>{
    if(filterStatus==="all") return prospects;
    return prospects.filter(p=>p.status===filterStatus);
  },[prospects, filterStatus]);

  const stageLabel = (id)=>STAGES.find(s=>s.id===id)?.label || id;
  const stageColor = (id)=>STAGES.find(s=>s.id===id)?.color || MUTED;

  const activateProspect = async(p)=>{
    if(activatingId) return;
    setActivatingId(p.id);
    try{
      const{data,error}=await supabase.rpc('activate_outreach_sequence',{p_prospect_id:p.id});
      if(error) throw error;
      const stepCount = Array.isArray(data) ? data.length : 0;
      toast(`Queued ${stepCount} step${stepCount===1?"":"s"} for ${p.brand_name}`);
      await load();
    }catch(e){
      console.error(e);
      const msg = e?.message || String(e);
      let friendly = msg;
      if(/no_active_template/.test(msg)) friendly = "Save + activate a sequence in Compose first";
      else if(/sequence_already_active/.test(msg)) friendly = "Prospect already has queued sends";
      else if(/prospect_sequence_paused/.test(msg)) friendly = "Prospect is paused (reply received or bounced)";
      else if(/prospect_not_eligible/.test(msg)) friendly = "Prospect must be in Researched or Contacted";
      toast(`Activation failed: ${friendly}`,'error');
    }finally{
      setActivatingId(null);
    }
  };

  const runWorker = async()=>{
    if(workerBusy) return;
    setWorkerBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-outreach-send-worker`,{
        method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'}, body:'{}',
      });
      const body=await resp.json();
      if(!resp.ok){
        toast(`Worker error: ${body.error||resp.status}`,'error');
        return;
      }
      const sent = body.sent||0, skipped = body.skipped||0, failed = body.failed||0;
      if(body.summary==='no_due_sends'){
        toast("No due sends right now");
      } else {
        toast(`Worker: ${sent} sent · ${skipped} skipped · ${failed} failed`);
      }
      await load();
    }catch(e){
      console.error(e);
      toast(`Worker failed: ${e.message||e}`,'error');
    }finally{
      setWorkerBusy(false);
    }
  };

  const dueQueued = useMemo(()=> prospects.reduce((acc,p)=>acc+(p.sequence_activated_at&&!p.sequence_paused?1:0),0), [prospects]);

  return(<div style={{display:"flex",flexDirection:"column",gap:12}}>
    <Card style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontWeight:700,color:DARK,fontSize:15}}>Pipeline</div>
          <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:540,lineHeight:1.55}}>Every prospect brand you're reaching out to. When replies arrive or Calendly bookings land, you get a suggested next stage — nothing moves until you confirm. You stay in the loop on every prospect.</div>
        </div>
        <div style={{display:"flex",gap:8,flexShrink:0}}>
          <button onClick={runWorker} disabled={workerBusy}
            title={dueQueued>0?"Process any due sends now":"No active prospects yet"}
            style={{background:"#fff",border:`1px solid ${BORDER}`,color:DARK,borderRadius:8,padding:"8px 14px",fontWeight:600,fontSize:12,cursor:workerBusy?"wait":"pointer",whiteSpace:"nowrap"}}>
            {workerBusy?"Running…":"Run worker now"}
          </button>
          <button onClick={()=>setAdding(a=>!a)} style={{background:DARK,color:G,border:"none",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>{adding ? "Cancel" : "+ Add prospect"}</button>
        </div>
      </div>

      {adding && <AddProspectForm userId={userId} onAdded={(p)=>{setProspects(prev=>[p,...prev]);setAdding(false);}}/>}

      {/* Stages summary row — clickable filters */}
      <div style={{display:"grid",gridTemplateColumns:`repeat(${STAGES.length+1},1fr)`,gap:8,marginBottom:18,marginTop:adding?18:0}}>
        <button onClick={()=>setFilterStatus("all")}
          style={{padding:"10px 8px",border:`1px solid ${filterStatus==="all"?DARK:BORDER}`,borderRadius:8,background:filterStatus==="all"?"#fafcfb":"#fff",textAlign:"center",cursor:"pointer"}}>
          <div style={{fontSize:9,fontWeight:800,letterSpacing:".06em",color:DARK,textTransform:"uppercase",marginBottom:4}}>All</div>
          <div style={{fontSize:18,fontWeight:800,color:DARK,letterSpacing:"-.02em"}}>{prospects.length}</div>
        </button>
        {STAGES.map(s=>(<button key={s.id} onClick={()=>setFilterStatus(s.id)}
          style={{padding:"10px 6px",border:`1px solid ${filterStatus===s.id?s.color:BORDER}`,borderRadius:8,background:filterStatus===s.id?s.color+"10":"#fff",textAlign:"center",cursor:"pointer"}}>
          <div style={{fontSize:9,fontWeight:800,letterSpacing:".06em",color:s.color,textTransform:"uppercase",marginBottom:4}}>{s.label}</div>
          <div style={{fontSize:18,fontWeight:800,color:DARK,letterSpacing:"-.02em"}}>{countsByStatus[s.id]||0}</div>
        </button>))}
      </div>

      {!loaded ? <div style={{fontSize:12,color:MUTED,padding:"20px 0",textAlign:"center"}}>Loading prospects…</div>
        : visibleProspects.length===0 ?
          <div style={{padding:"40px 20px",textAlign:"center",background:"#fafcfb",border:`1px dashed ${BORDER}`,borderRadius:10}}>
            <div style={{fontSize:14,fontWeight:600,color:DARK,marginBottom:6}}>{filterStatus==="all"?"No prospects yet":`No prospects in ${stageLabel(filterStatus)}`}</div>
            <div style={{fontSize:12,color:MUTED,maxWidth:480,margin:"0 auto",lineHeight:1.55}}>{filterStatus==="all"?'Click "+ Add prospect" to add your first one. CSV import + Motion surfacing come later.':"Try selecting a different stage or click All."}</div>
          </div>
        :
        <div style={{border:`1px solid ${BORDER}`,borderRadius:10,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:"#f8faf9"}}>
              {["Brand / Contact","Status","Source","Last contacted","Sequence",""].map(h=><th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:MUTED,textTransform:"uppercase",letterSpacing:".05em"}}>{h}</th>)}
            </tr></thead>
            <tbody>{visibleProspects.map(p=>(<tr key={p.id} style={{borderTop:`1px solid ${BORDER}`}}>
              <td style={{padding:"10px 12px"}}>
                <div style={{fontWeight:600,color:DARK,fontSize:13}}>{p.brand_name}</div>
                <div style={{fontSize:11,color:MUTED,marginTop:2}}>
                  {(p.contact_first_name||p.contact_last_name) && <>{[p.contact_first_name,p.contact_last_name].filter(Boolean).join(" ")} · </>}
                  <a href={`mailto:${p.contact_email}`} style={{color:MUTED,textDecoration:"none"}} onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"} onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>{p.contact_email}</a>
                  {p.brand_handle && <> · @{p.brand_handle}</>}
                </div>
              </td>
              <td style={{padding:"10px 12px"}}>
                <span style={{background:stageColor(p.status)+"18",color:stageColor(p.status),padding:"2px 7px",borderRadius:4,fontSize:9,fontWeight:800,letterSpacing:".05em"}}>{stageLabel(p.status)}</span>
              </td>
              <td style={{padding:"10px 12px",fontSize:11,color:MUTED}}>{p.source||"—"}</td>
              <td style={{padding:"10px 12px",fontSize:11,color:MUTED}}>{p.last_contacted_at ? new Date(p.last_contacted_at).toLocaleDateString("en-AU",{day:"numeric",month:"short"}) : "—"}</td>
              <td style={{padding:"10px 12px"}}>
                {p.sequence_paused
                  ? <span style={{background:"#FEF3C7",color:"#92400E",padding:"2px 7px",borderRadius:4,fontSize:9,fontWeight:700}}>PAUSED</span>
                  : p.sequence_activated_at
                    ? <span style={{background:G+"18",color:G,padding:"2px 7px",borderRadius:4,fontSize:9,fontWeight:700}}>ACTIVE</span>
                    : <span style={{color:MUTED,fontSize:11}}>not queued</span>}
              </td>
              <td style={{padding:"10px 12px",textAlign:"right"}}>
                {p.sequence_activated_at || p.sequence_paused
                  ? <span style={{fontSize:11,color:MUTED}}>—</span>
                  : <button onClick={()=>activateProspect(p)} disabled={activatingId===p.id||(p.status!=="researched"&&p.status!=="contacted")}
                      title={p.status!=="researched"&&p.status!=="contacted"?"Only researched/contacted prospects can be activated":"Queue all sequence steps for this prospect"}
                      style={{background:G+"18",border:`1px solid ${G}40`,color:G,borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:600,cursor:(activatingId===p.id||(p.status!=="researched"&&p.status!=="contacted"))?"not-allowed":"pointer",opacity:activatingId===p.id?0.6:1}}>
                      {activatingId===p.id?"Activating…":"Activate"}
                    </button>}
              </td>
            </tr>))}</tbody>
          </table>
        </div>}
    </Card>
  </div>);
}

// Inline form to add a single prospect. Bare minimum required: brand_name +
// contact_email. Everything else optional.
function AddProspectForm({userId, onAdded}){
  const [f, setF] = useState({
    brand_name:"", contact_first_name:"", contact_last_name:"", contact_email:"",
    brand_handle:"", brand_website:"", industry:"", source:"manual",
    personalisation_line:"", timezone:"Australia/Sydney", notes:"",
  });
  const [busy, setBusy] = useState(false);
  const update = (k,v)=>setF(p=>({...p,[k]:v}));

  const validEmail = (e)=>/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const canSubmit = f.brand_name.trim() && validEmail(f.contact_email.trim());

  const submit = async()=>{
    if(!canSubmit||busy) return;
    setBusy(true);
    try{
      const payload = {
        owner_clerk_user_id: userId,
        brand_name: f.brand_name.trim(),
        contact_first_name: f.contact_first_name.trim() || null,
        contact_last_name: f.contact_last_name.trim() || null,
        contact_email: f.contact_email.trim().toLowerCase(),
        brand_handle: f.brand_handle.trim().replace(/^@/,'') || null,
        brand_website: f.brand_website.trim() || null,
        industry: f.industry.trim() || null,
        source: f.source || "manual",
        personalisation_line: f.personalisation_line.trim() || null,
        timezone: f.timezone || "UTC",
        notes: f.notes.trim() || null,
      };
      const{data,error}=await supabase.from('agency_prospects').insert(payload).select('*').single();
      if(error) throw error;
      toast(`Added ${payload.brand_name}`);
      onAdded(data);
    }catch(e){
      console.error(e);
      const msg = e?.message?.includes("agency_prospects_unique_email")
        ? `${f.contact_email} is already a prospect`
        : (e.message||String(e));
      toast(`Add failed: ${msg}`,'error');
    }finally{
      setBusy(false);
    }
  };

  const cell = (label, key, type="text", placeholder="")=> (
    <div>
      <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>{label}</label>
      <input type={type} value={f[key]} onChange={e=>update(key,e.target.value)} placeholder={placeholder} disabled={busy}
        style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
    </div>
  );

  return(<div style={{padding:18,background:"#fafcfb",border:`1px solid ${BORDER}`,borderRadius:10}}>
    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:10}}>
      {cell("Brand name *","brand_name","text","Acme Goggles Co")}
      {cell("Contact email *","contact_email","email","sarah@acmegoggles.com")}
      <div>
        <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Source</label>
        <select value={f.source} onChange={e=>update('source',e.target.value)} disabled={busy}
          style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,background:"#fff",outline:"none",boxSizing:"border-box"}}>
          {["manual","motion","ig_dm","csv_import","modash","referral","event","other"].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {cell("First name","contact_first_name","text","Sarah")}
      {cell("Last name","contact_last_name","text","Cooper")}
      {cell("Brand IG handle","brand_handle","text","acmegoggles")}
      {cell("Website","brand_website","url","https://acmegoggles.com")}
      {cell("Industry","industry","text","Swimming gear")}
      <div>
        <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Timezone</label>
        <select value={f.timezone} onChange={e=>update('timezone',e.target.value)} disabled={busy}
          style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,background:"#fff",outline:"none",boxSizing:"border-box"}}>
          {["Australia/Sydney","Australia/Melbourne","Europe/Ljubljana","Europe/London","America/New_York","America/Los_Angeles","UTC"].map(t=><option key={t} value={t}>{t}</option>)}
        </select>
      </div>
    </div>
    <div style={{marginBottom:10}}>
      <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Personalisation line</label>
      <input type="text" value={f.personalisation_line} onChange={e=>update('personalisation_line',e.target.value)} disabled={busy}
        placeholder="just hit 100K followers / launched a new line / has a January sale running"
        style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
      <div style={{fontSize:10,color:MUTED,marginTop:4}}>Used as {`{{personalisation_line}}`} in the email body. Leave blank for AI to fill at send time.</div>
    </div>
    <div style={{marginBottom:10}}>
      <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Notes (private to you)</label>
      <textarea value={f.notes} onChange={e=>update('notes',e.target.value)} disabled={busy} rows={2}
        style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box",resize:"vertical",fontFamily:"inherit"}}/>
    </div>
    <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
      <button onClick={submit} disabled={!canSubmit||busy}
        style={{background:canSubmit&&!busy?DARK:"#f0f0f0",color:canSubmit&&!busy?G:"#9ab8a6",border:"none",borderRadius:8,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:canSubmit&&!busy?"pointer":"not-allowed"}}>
        {busy?"Adding…":"Add prospect"}
      </button>
    </div>
  </div>);
}

// Compose tab — sequence builder. Initial cold email + up to 7 follow-ups.
// Each step has its own subject/body/day-offset. System fires the whole
// sequence per prospect automatically, but stops on reply.
// Frontend-only state for now; persistence lands in Phase 2 with the
// agency_outreach_templates migration.
function OutreachComposeCard({userProfile={}, gmailConnected}){
  const { user } = useUser();
  const userId = user?.id;
  // Sequence: array of {label, subject, body, dayOffset, attachPdf?}. Step 0 is initial.
  const [sequence, setSequence] = useState([
    {
      label: "Initial",
      subject: "Quick idea for {{brand_name}}",
      body: `Hi {{first_name}},\n\nI run kolabs — an influencer marketing agency that helped {{case_study_brand}} scale from {{case_study_metric_before}} to {{case_study_metric_after}} in 90 days.\n\nNoticed {{brand_name}} {{personalisation_line}} — wondered if you'd be open to a 20-min call to share how we'd think about it for you?\n\nCase study attached. Calendar link below.\n\n— Kristijan\n{{calendly_url}}`,
      dayOffset: 0,
    },
    { label: "Follow-up 1", subject: "", body: "", dayOffset: 3 },
    { label: "Follow-up 2", subject: "", body: "", dayOffset: 7 },
    { label: "Follow-up 3", subject: "", body: "", dayOffset: 14 },
  ]);
  const [activeStep, setActiveStep] = useState(0);
  const [attached, setAttached] = useState(false);
  // Persistence state
  const [templateId, setTemplateId] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Load existing template on mount
  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      const{data}=await supabase.from('agency_outreach_templates')
        .select('*').order('updated_at',{ascending:false}).limit(1).maybeSingle();
      if(cancelled){return;}
      if(data){
        setTemplateId(data.id);
        setIsActive(!!data.is_active);
        setLastSavedAt(data.updated_at);
        const steps = Array.isArray(data.steps) ? data.steps : [];
        if(steps.length>0){
          setSequence(steps.map((s,i)=>({
            label: s.label || (i===0?"Initial":`Follow-up ${i}`),
            subject: s.subject||"",
            body: s.body||"",
            dayOffset: typeof s.day_offset==='number' ? s.day_offset : (i===0?0:i*3),
          })));
          const initial = steps[0];
          if(initial?.attach_pdf) setAttached(true);
        }
      }
      setLoaded(true);
    })();
    return()=>{cancelled=true;};
  },[]);

  const updateStep = (i, patch) => {
    setDirty(true);
    setSequence(s => s.map((step, idx) => idx===i ? {...step, ...patch} : step));
  };
  const addStep = () => {
    if(sequence.length>=8) return; // 8 max (initial + 7 follow-ups)
    const lastOffset = sequence[sequence.length-1].dayOffset;
    setDirty(true);
    setSequence(s => [...s, { label: `Follow-up ${s.length}`, subject:"", body:"", dayOffset: lastOffset + 7 }]);
    setActiveStep(sequence.length);
  };
  const removeStep = (i) => {
    if(i===0) return; // can't delete the initial
    setDirty(true);
    setSequence(s => s.filter((_,idx)=>idx!==i).map((step,idx)=>idx===0?step:{...step,label:`Follow-up ${idx}`}));
    if(activeStep>=sequence.length-1) setActiveStep(Math.max(0,sequence.length-2));
  };

  const insertMerge = (token)=>{
    updateStep(activeStep, { body: sequence[activeStep].body + `{{${token}}}` });
  };

  const toggleAttached = () => { setAttached(a=>!a); setDirty(true); };

  const buildStepsJson = () => sequence.map((s,i)=>({
    label: s.label,
    subject: s.subject,
    body: s.body,
    day_offset: i===0 ? 0 : s.dayOffset,
    attach_pdf: i===0 ? attached : false,
  }));

  // PDF Drive file ID — loaded from template, edited in the attach section.
  const [pdfDriveFileId, setPdfDriveFileId] = useState("");
  useEffect(()=>{
    // when templateId loads, also pull pdf_drive_file_id
    if(!templateId) return;
    let cancelled=false;
    (async()=>{
      const{data}=await supabase.from('agency_outreach_templates').select('pdf_drive_file_id').eq('id',templateId).maybeSingle();
      if(!cancelled && data) setPdfDriveFileId(data.pdf_drive_file_id||"");
    })();
    return()=>{cancelled=true;};
  },[templateId]);

  const saveSequence = async () => {
    if(!userId || busy) return;
    setBusy(true);
    try{
      const steps = buildStepsJson();
      const payload = {
        owner_clerk_user_id: userId,
        name: "Default outreach sequence",
        steps,
        pdf_drive_file_id: pdfDriveFileId.trim() || null,
        updated_at: new Date().toISOString(),
      };
      if(templateId){
        const{error}=await supabase.from('agency_outreach_templates').update(payload).eq('id',templateId);
        if(error) throw error;
      } else {
        const{data,error}=await supabase.from('agency_outreach_templates').insert(payload).select('id, is_active, updated_at').single();
        if(error) throw error;
        setTemplateId(data.id);
        setIsActive(!!data.is_active);
      }
      setLastSavedAt(new Date().toISOString());
      setDirty(false);
      toast("Sequence saved");
    }catch(e){
      console.error(e);
      toast(`Save failed: ${e.message||e}`,'error');
    }finally{
      setBusy(false);
    }
  };

  // Activate flips is_active=true on this template + sets others=false to
  // satisfy the partial unique index agency_outreach_templates_one_active.
  // Deactivate is the same with the booleans flipped.
  const toggleActivate = async () => {
    if(!userId || !templateId || busy) return;
    setBusy(true);
    try{
      if(isActive){
        const{error}=await supabase.from('agency_outreach_templates').update({is_active:false}).eq('id',templateId);
        if(error) throw error;
        setIsActive(false);
        toast("Sequence deactivated");
      }else{
        // Deactivate any other active templates first to avoid the unique-index conflict.
        await supabase.from('agency_outreach_templates').update({is_active:false}).neq('id',templateId).eq('is_active',true);
        const{error}=await supabase.from('agency_outreach_templates').update({is_active:true}).eq('id',templateId);
        if(error) throw error;
        setIsActive(true);
        toast("Sequence activated — fires on next eligible prospect");
      }
    }catch(e){
      console.error(e);
      toast(`Activation failed: ${e.message||e}`,'error');
    }finally{
      setBusy(false);
    }
  };

  const canActivate = !!templateId && !dirty && sequence[0]?.subject.trim() && sequence[0]?.body.trim() && gmailConnected;
  const mergeFields = ["first_name","last_name","brand_name","brand_handle","personalisation_line","case_study_brand","case_study_metric_before","case_study_metric_after","calendly_url"];

  const step = sequence[activeStep];

  return(<Card style={{padding:24}}>
    <div style={{marginBottom:14}}>
      <div style={{fontWeight:700,color:DARK,fontSize:15}}>Sequence builder</div>
      <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:620,lineHeight:1.55}}>Initial cold email + up to 7 follow-ups. The system fires the whole sequence per prospect automatically on the cadence you set — and <b style={{color:DARK}}>stops the moment they reply</b>, so you only ever read fresh threads in the inbox. You provide the email content; backend wiring fires the schedule.</div>
    </div>

    {/* Sequence step tabs */}
    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:14,flexWrap:"wrap",padding:"10px 12px",background:"#fafcfb",border:`1px solid ${BORDER}`,borderRadius:10}}>
      {sequence.map((s,i)=>{
        const filled = s.subject.trim().length>0 && s.body.trim().length>0;
        const isActive = i===activeStep;
        return(<button key={i} onClick={()=>setActiveStep(i)}
          style={{background:isActive?DARK:(filled?G+"18":"#fff"),color:isActive?G:(filled?G:MUTED),border:`1px solid ${isActive?DARK:(filled?G+"40":BORDER)}`,borderRadius:7,padding:"6px 11px",fontSize:11,fontWeight:isActive?700:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
          <span>{s.label}</span>
          <span style={{fontSize:9,opacity:0.7,fontWeight:600}}>{i===0?"day 0":`+${s.dayOffset}d`}</span>
          {!filled && <span style={{width:5,height:5,borderRadius:"50%",background:"#FDE68A"}}/>}
        </button>);
      })}
      {sequence.length<8 && <button onClick={addStep}
        style={{background:"transparent",border:`1px dashed ${BORDER}`,color:MUTED,borderRadius:7,padding:"6px 11px",fontSize:11,fontWeight:600,cursor:"pointer"}}
        onMouseEnter={e=>{e.currentTarget.style.color=DARK;e.currentTarget.style.borderColor=DARK;}}
        onMouseLeave={e=>{e.currentTarget.style.color=MUTED;e.currentTarget.style.borderColor=BORDER;}}>+ Add follow-up</button>}
      <div style={{flex:1}}/>
      <div style={{fontSize:10,color:MUTED}}>{sequence.filter(s=>s.subject.trim()&&s.body.trim()).length}/{sequence.length} drafted</div>
    </div>

    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {/* Day offset (not on initial) */}
      {activeStep>0 && <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:G+"08",border:`1px solid ${G}40`,borderRadius:10}}>
        <span style={{fontSize:12,color:DARK,fontWeight:600}}>Send</span>
        <input type="number" min={1} max={60} value={step.dayOffset}
          onChange={e=>updateStep(activeStep,{dayOffset: Math.max(1, Math.min(60, parseInt(e.target.value)||1))})}
          style={{width:60,padding:"5px 9px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:13,outline:"none",textAlign:"center"}}/>
        <span style={{fontSize:12,color:DARK,fontWeight:600}}>days after</span>
        <span style={{fontSize:12,color:MUTED}}>{activeStep===1?"initial email":sequence[activeStep-1].label}</span>
        {activeStep>0 && <><div style={{flex:1}}/>
          <button onClick={()=>removeStep(activeStep)} style={{background:"transparent",border:"1px solid #FECACA",color:"#DC2626",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>Remove step</button>
        </>}
      </div>}

      <div>
        <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:5}}>Subject — {step.label}</label>
        <input type="text" value={step.subject} onChange={e=>updateStep(activeStep,{subject:e.target.value})}
          placeholder={activeStep>0?"Re: previous subject — or new angle":"Cold email subject"}
          style={{width:"100%",padding:"9px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
      </div>

      <div>
        <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:5}}>Body — {step.label}</label>
        <textarea value={step.body} onChange={e=>updateStep(activeStep,{body:e.target.value})}
          placeholder={activeStep>0?"Follow-up content goes here…":"Cold email body"}
          rows={11}
          style={{width:"100%",padding:"10px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box",resize:"vertical",fontFamily:"inherit",lineHeight:1.5}}/>
      </div>

      <div>
        <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:5}}>Insert merge field</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {mergeFields.map(m=>(<button key={m} onClick={()=>insertMerge(m)}
            style={{background:"#f8faf9",border:`1px solid ${BORDER}`,borderRadius:6,padding:"4px 9px",fontSize:11,fontFamily:"DM Mono, monospace",color:DARK,cursor:"pointer"}}
            onMouseEnter={e=>{e.currentTarget.style.background=G+"18";e.currentTarget.style.borderColor=G+"40";}}
            onMouseLeave={e=>{e.currentTarget.style.background="#f8faf9";e.currentTarget.style.borderColor=BORDER;}}>
            {`{{${m}}}`}
          </button>))}
        </div>
      </div>

      {/* PDF attachment — only on initial (follow-ups don't repeat the PDF) */}
      {activeStep===0 && <div style={{padding:"12px 14px",background:"#fafcfb",border:`1px solid ${BORDER}`,borderRadius:10}}>
        <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:attached?10:0}}>
          <button onClick={toggleAttached}
            style={{background:attached?G+"18":"#fff",border:`1px solid ${attached?G+"40":BORDER}`,color:attached?G:DARK,borderRadius:7,padding:"7px 12px",fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
            📎 {attached ? "Attached" : "Attach case study PDF"}
          </button>
          {attached && <button onClick={toggleAttached} style={{background:"transparent",border:"none",color:MUTED,fontSize:11,cursor:"pointer"}}>Remove</button>}
          <div style={{flex:1}}/>
          <div style={{fontSize:11,color:MUTED}}>On the initial email only</div>
        </div>
        {attached && <div>
          <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Drive file ID</label>
          <input type="text" value={pdfDriveFileId} onChange={e=>{setPdfDriveFileId(e.target.value);setDirty(true);}}
            placeholder="1Ab2C3dEf4GhIj5kLmnOpQrStUvWxYz_AbCdEf"
            style={{width:"100%",padding:"8px 10px",border:`1px solid ${pdfDriveFileId.trim()?BORDER:"#FDE68A"}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"DM Mono, monospace"}}/>
          <div style={{fontSize:10,color:MUTED,marginTop:4,lineHeight:1.5}}>
            Open your case-study PDF in Drive → click Share → "Copy link" → it looks like <code style={{background:"#fff",padding:"1px 4px",borderRadius:3}}>drive.google.com/file/d/<b>FILE_ID</b>/view</code>. Paste the FILE_ID part above. Worker downloads on send.
          </div>
        </div>}
      </div>}

      <div style={{display:"flex",gap:10,alignItems:"center",justifyContent:"space-between",paddingTop:14,borderTop:`1px solid ${BORDER}`,flexWrap:"wrap"}}>
        <div style={{display:"flex",flexDirection:"column",gap:3,maxWidth:420}}>
          <div style={{fontSize:11,color:MUTED,lineHeight:1.5}}>{isActive
            ? <>Active sequence — currently firing for any prospects you queue. {!gmailConnected && <span style={{color:"#DC2626"}}>Gmail disconnected, sends paused.</span>}</>
            : "Save first, then activate when you're ready to start firing this sequence."
          }</div>
          {loaded && lastSavedAt && <div style={{fontSize:10,color:MUTED,fontFamily:"DM Mono, monospace"}}>{dirty ? "● unsaved changes" : `saved · ${new Date(lastSavedAt).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})}`}</div>}
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={saveSequence} disabled={busy||!dirty}
            style={{background:"#fff",border:`1px solid ${dirty?DARK:BORDER}`,color:dirty?DARK:MUTED,borderRadius:8,padding:"9px 14px",fontSize:12,fontWeight:600,cursor:(busy||!dirty)?"not-allowed":"pointer",opacity:(busy||!dirty)?0.6:1}}>
            {busy?"Saving…":"Save sequence"}
          </button>
          <button onClick={toggleActivate} disabled={busy||(!isActive&&!canActivate)}
            title={isActive?"Pause this sequence":(!gmailConnected?"Connect agency Gmail first":(!templateId?"Save the sequence first":(dirty?"Save changes first":"Start firing this sequence for queued prospects")))}
            style={{background:isActive?"#DC2626":(canActivate?DARK:"#f0f0f0"),color:isActive?"#fff":(canActivate?G:"#9ab8a6"),border:"none",borderRadius:8,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:(busy||(!isActive&&!canActivate))?"not-allowed":"pointer",opacity:busy?0.6:1}}>
            {busy?"…":(isActive?"Deactivate":"Activate")}
          </button>
        </div>
      </div>
    </div>
  </Card>);
}

// Inbox tab — real prospect-reply threads pulled by agency-outreach-sync-inbox.
// V1: thread list + expand-to-read. Reply composing happens in the operator's
// own Gmail (we don't send replies from the app per the human-in-loop rule).
function OutreachInboxCard({gmailConnected}){
  const [threads, setThreads] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [, setLastSyncAt] = useState(null);
  const [expandedThreadId, setExpandedThreadId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | unread

  const load = async()=>{
    // Pull all messages for the owner grouped client-side into threads.
    // Limit 500 to keep payload reasonable; older threads visible via "Load more" later.
    const{data:msgs}=await supabase.from('agency_outreach_messages')
      .select('id, gmail_thread_id, prospect_id, direction, from_email, subject, body_text, sent_at, received_at, read_by_owner_at, reply_hint')
      .order('sent_at',{ascending:false}).limit(500);
    if(!msgs){ setLoaded(true); return; }

    // Join in prospect basics
    const prospectIds = [...new Set(msgs.map(m=>m.prospect_id).filter(Boolean))];
    const{data:prospects}=prospectIds.length
      ? await supabase.from('agency_prospects').select('id, brand_name, contact_first_name, contact_last_name, contact_email, status, sequence_paused').in('id',prospectIds)
      : {data:[]};
    const pById = Object.fromEntries((prospects||[]).map(p=>[p.id,p]));

    // Group into threads
    const tmap = new Map();
    for(const m of msgs){
      if(!m.gmail_thread_id) continue;
      const cur = tmap.get(m.gmail_thread_id) || {
        threadId: m.gmail_thread_id,
        prospect: pById[m.prospect_id] || null,
        messages: [],
        latestAt: 0,
        unreadCount: 0,
        hasInbound: false,
      };
      cur.messages.push(m);
      const t = new Date(m.sent_at||0).getTime();
      if(t>cur.latestAt) cur.latestAt = t;
      if(m.direction==='inbound'){
        cur.hasInbound = true;
        if(!m.read_by_owner_at) cur.unreadCount += 1;
      }
      tmap.set(m.gmail_thread_id, cur);
    }
    // Sort threads by latest activity desc
    const arr = [...tmap.values()].sort((a,b)=>b.latestAt-a.latestAt);
    setThreads(arr);
    setLoaded(true);
  };
  useEffect(()=>{ load(); /* eslint-disable-line react-hooks/exhaustive-deps */ },[]);

  const sync = async()=>{
    if(syncing) return;
    setSyncing(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-outreach-sync-inbox`,{
        method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'}, body:'{}',
      });
      const body=await resp.json();
      if(!resp.ok){ toast(`Sync error: ${body.error||resp.status}`,'error'); return; }
      if(body.summary==='no_threads_to_sync'){
        toast("No threads to sync — send a prospect first");
      } else {
        const newIn = body.new_inbound||0;
        toast(newIn>0 ? `${newIn} new repl${newIn===1?'y':'ies'} pulled in` : "Inbox up to date");
      }
      setLastSyncAt(new Date().toISOString());
      await load();
    }catch(e){
      console.error(e);
      toast(`Sync failed: ${e.message||e}`,'error');
    }finally{
      setSyncing(false);
    }
  };

  const toggleExpand = (threadId)=>{
    setExpandedThreadId(prev => prev===threadId ? null : threadId);
  };

  const markRead = async(messageId)=>{
    const now = new Date().toISOString();
    await supabase.from('agency_outreach_messages').update({read_by_owner_at:now}).eq('id',messageId);
    setThreads(prev => prev.map(t => ({
      ...t,
      messages: t.messages.map(m => m.id===messageId ? {...m,read_by_owner_at:now} : m),
      unreadCount: t.messages.filter(m => m.id!==messageId && m.direction==='inbound' && !m.read_by_owner_at).length,
    })));
  };

  const markAllReadInThread = async(thread)=>{
    const unreadIds = thread.messages.filter(m=>m.direction==='inbound' && !m.read_by_owner_at).map(m=>m.id);
    if(unreadIds.length===0) return;
    const now = new Date().toISOString();
    await supabase.from('agency_outreach_messages').update({read_by_owner_at:now}).in('id',unreadIds);
    setThreads(prev => prev.map(t => t.threadId!==thread.threadId ? t : {
      ...t,
      messages: t.messages.map(m => unreadIds.includes(m.id) ? {...m,read_by_owner_at:now} : m),
      unreadCount: 0,
    }));
  };

  const visibleThreads = filter==="unread" ? threads.filter(t=>t.unreadCount>0) : threads;

  return(<Card style={{padding:24}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:12,flexWrap:"wrap"}}>
      <div>
        <div style={{fontWeight:700,color:DARK,fontSize:15}}>Inbox</div>
        <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:580,lineHeight:1.55}}>Replies from prospects land here. The sequence auto-pauses on the first reply (DB trigger) — you read + respond from your own Gmail, the app just surfaces what's pending.</div>
      </div>
      <div style={{display:"flex",gap:8,flexShrink:0,alignItems:"center"}}>
        <div style={{display:"flex",gap:2,background:"#f8faf9",borderRadius:7,padding:3}}>
          {[{id:"all",label:"All"},{id:"unread",label:"Unread"}].map(f=>(
            <button key={f.id} onClick={()=>setFilter(f.id)}
              style={{background:filter===f.id?"#fff":"transparent",color:filter===f.id?DARK:MUTED,border:"none",borderRadius:5,padding:"4px 10px",fontSize:11,fontWeight:filter===f.id?700:500,cursor:"pointer"}}>
              {f.label}{f.id==="unread"&&<span style={{marginLeft:4,color:G,fontWeight:800}}>{threads.reduce((acc,t)=>acc+t.unreadCount,0)||""}</span>}
            </button>
          ))}
        </div>
        <button onClick={sync} disabled={syncing||!gmailConnected}
          title={gmailConnected?"Pull new messages from Gmail":"Connect agency Gmail first"}
          style={{background:DARK,color:G,border:"none",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:(syncing||!gmailConnected)?"not-allowed":"pointer",whiteSpace:"nowrap",opacity:syncing?0.6:1}}>
          {syncing?"Syncing…":"Sync inbox"}
        </button>
      </div>
    </div>

    {!gmailConnected
      ? <div style={{padding:"40px 20px",textAlign:"center",background:"#fafcfb",border:`1px dashed ${BORDER}`,borderRadius:10}}>
          <div style={{fontSize:14,fontWeight:600,color:DARK,marginBottom:6}}>Connect agency Gmail to start</div>
          <div style={{fontSize:12,color:MUTED,maxWidth:480,margin:"0 auto",lineHeight:1.55}}>The connect button is in the header. Once linked, sync pulls every prospect-thread you've sent into.</div>
        </div>
      : !loaded
        ? <div style={{padding:"30px 0",textAlign:"center",color:MUTED,fontSize:13}}>Loading threads…</div>
        : visibleThreads.length===0
          ? <div style={{padding:"40px 20px",textAlign:"center",background:"#fafcfb",border:`1px dashed ${BORDER}`,borderRadius:10}}>
              <div style={{fontSize:14,fontWeight:600,color:DARK,marginBottom:6}}>{filter==="unread"?"No unread threads":"No threads yet"}</div>
              <div style={{fontSize:12,color:MUTED,maxWidth:480,margin:"0 auto",lineHeight:1.55}}>{filter==="unread"?"All replies have been read.":'Send a prospect first (Pipeline → Activate → Run worker), then sync to pull replies.'}</div>
            </div>
          : <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {visibleThreads.map(t=>{
                const expanded = expandedThreadId===t.threadId;
                const lastInbound = [...t.messages].reverse().find(m=>m.direction==='inbound');
                const preview = lastInbound?.body_text || lastInbound?.subject || t.messages[t.messages.length-1]?.subject || "(no content)";
                return(<div key={t.threadId} style={{border:`1px solid ${t.unreadCount>0?G+"60":BORDER}`,borderRadius:10,background:t.unreadCount>0?G+"06":"#fff",overflow:"hidden"}}>
                  <button onClick={()=>toggleExpand(t.threadId)}
                    style={{width:"100%",border:"none",background:"transparent",cursor:"pointer",padding:"12px 14px",textAlign:"left",display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:34,height:34,borderRadius:8,background:G+"18",color:G,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0}}>
                      {(t.prospect?.brand_name||"?").charAt(0).toUpperCase()}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{fontWeight:t.unreadCount>0?700:600,color:DARK,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.prospect?.brand_name||"(unknown brand)"}</div>
                        {t.unreadCount>0 && <span style={{background:G,color:"#000",padding:"1px 7px",borderRadius:10,fontSize:9,fontWeight:800}}>{t.unreadCount}</span>}
                        {t.prospect?.sequence_paused && <span style={{background:"#FEF3C7",color:"#92400E",padding:"1px 6px",borderRadius:4,fontSize:9,fontWeight:700}}>PAUSED</span>}
                      </div>
                      <div style={{fontSize:11,color:MUTED,marginTop:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                        {t.prospect?.contact_email || "—"} · {preview.slice(0,120)}{preview.length>120?"…":""}
                      </div>
                    </div>
                    <div style={{flexShrink:0,textAlign:"right"}}>
                      <div style={{fontSize:10,color:MUTED}}>{new Date(t.latestAt).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})}</div>
                      <div style={{fontSize:10,color:MUTED,marginTop:2}}>{t.messages.length} message{t.messages.length===1?"":"s"}</div>
                    </div>
                    <span style={{flexShrink:0,fontSize:14,color:MUTED}}>{expanded?"▾":"▸"}</span>
                  </button>

                  {expanded && <div style={{padding:"4px 14px 14px",borderTop:`1px solid ${BORDER}`,background:"#fff"}}>
                    {t.unreadCount>0 && <div style={{display:"flex",justifyContent:"flex-end",padding:"8px 0"}}>
                      <button onClick={()=>markAllReadInThread(t)} style={{background:"transparent",border:`1px solid ${BORDER}`,color:MUTED,borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>Mark all read</button>
                    </div>}
                    {[...t.messages].sort((a,b)=>new Date(a.sent_at).getTime()-new Date(b.sent_at).getTime()).map(m=>{
                      const inbound = m.direction==='inbound';
                      const unread = inbound && !m.read_by_owner_at;
                      return(<div key={m.id} style={{margin:"10px 0",padding:"12px 14px",borderRadius:9,background:inbound?(unread?G+"10":"#fafcfb"):"#fff",border:`1px solid ${inbound&&unread?G+"40":BORDER}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:10,marginBottom:6,flexWrap:"wrap"}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{background:inbound?G+"18":"#F3F4F6",color:inbound?G:DARK,padding:"1px 7px",borderRadius:4,fontSize:9,fontWeight:800,letterSpacing:".05em"}}>{inbound?"REPLY":"SENT"}</span>
                            <span style={{fontSize:12,fontWeight:600,color:DARK}}>{m.from_email}</span>
                            {m.reply_hint && <span style={{background:"#EFF6FF",color:"#1D4ED8",padding:"1px 6px",borderRadius:4,fontSize:9,fontWeight:700}}>{m.reply_hint.replace(/_/g," ")}</span>}
                          </div>
                          <div style={{fontSize:10,color:MUTED}}>{new Date(m.sent_at).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})}</div>
                        </div>
                        {m.subject && <div style={{fontSize:12,fontWeight:600,color:DARK,marginBottom:6}}>{m.subject}</div>}
                        <div style={{fontSize:12.5,color:DARK,whiteSpace:"pre-wrap",lineHeight:1.55,maxHeight:300,overflowY:"auto"}}>{m.body_text || "(no plain-text body)"}</div>
                        {unread && <div style={{marginTop:8,display:"flex",justifyContent:"flex-end"}}>
                          <button onClick={()=>markRead(m.id)} style={{background:"transparent",border:`1px solid ${G}40`,color:G,borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700,cursor:"pointer"}}>Mark read</button>
                        </div>}
                      </div>);
                    })}
                    <div style={{marginTop:14,padding:"10px 12px",background:"#fafcfb",border:`1px dashed ${BORDER}`,borderRadius:8,fontSize:11,color:MUTED,lineHeight:1.5}}>
                      <b style={{color:DARK}}>Reply from your own Gmail.</b> Sequence is paused for this prospect — no more auto-sends will fire on this thread. Reply composer inside the app is not on the roadmap (keeps you natural in Gmail).
                    </div>
                  </div>}
                </div>);
              })}
            </div>}
  </Card>);
}

// Reports tab — real cumulative outreach funnel computed from prospect pipeline
// state + sends. ("Opened" is intentionally omitted — open tracking isn't wired,
// so showing it would be a fake metric.)
function OutreachReportsCard(){
  const [counts,setCounts]=useState(null);
  useEffect(()=>{(async()=>{
    const [{data:prospects},{count:emailsSent}] = await Promise.all([
      supabase.from('agency_prospects').select('status,last_contacted_at'),
      supabase.from('agency_outreach_sends').select('id',{count:'exact',head:true}).eq('status','sent'),
    ]);
    const p = prospects||[];
    const reachedReply = s => ['replied','meeting_booked','proposal_sent','won'].includes(s);
    const reachedMeeting = s => ['meeting_booked','proposal_sent','won'].includes(s);
    setCounts({
      contacted: p.filter(x=>x.last_contacted_at).length,
      replied: p.filter(x=>reachedReply(x.status)).length,
      meeting: p.filter(x=>reachedMeeting(x.status)).length,
      won: p.filter(x=>x.status==='won').length,
      emailsSent: emailsSent||0,
      total: p.length,
    });
  })();},[]);
  const FUNNEL = counts ? [
    {label:"Contacted", value:counts.contacted, color:"#1D4ED8"},
    {label:"Replied", value:counts.replied, color:"#92400E"},
    {label:"Meeting booked", value:counts.meeting, color:"#166534"},
    {label:"Won", value:counts.won, color:"#16a34a"},
  ] : [];
  const pct = (n,d) => d>0 ? Math.round((n/d)*100) : 0;
  return(<Card style={{padding:24}}>
    <div style={{marginBottom:14}}>
      <div style={{fontWeight:700,color:DARK,fontSize:15}}>Funnel</div>
      <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:540,lineHeight:1.55}}>Prospects that reached each stage{counts?` · ${counts.emailsSent} email${counts.emailsSent===1?"":"s"} sent · ${counts.total} prospect${counts.total===1?"":"s"} total`:""}.</div>
    </div>
    {!counts
      ? <div style={{padding:"30px",textAlign:"center",fontSize:13,color:MUTED}}>Loading…</div>
      : counts.total===0
        ? <div style={{padding:"30px 20px",textAlign:"center",background:"#fafcfb",border:`1px dashed ${BORDER}`,borderRadius:10,fontSize:13,color:MUTED}}>No prospects yet. Add prospects + activate a sequence to start the funnel.</div>
        : <>
          <div style={{display:"grid",gridTemplateColumns:`repeat(${FUNNEL.length},1fr)`,gap:10,marginBottom:10}}>
            {FUNNEL.map((s,i)=>(<div key={s.label} style={{padding:"14px 12px",border:`1px solid ${BORDER}`,borderRadius:10,background:"#fff",position:"relative"}}>
              <div style={{fontSize:10,fontWeight:800,letterSpacing:".06em",color:s.color,textTransform:"uppercase",marginBottom:5}}>{s.label}</div>
              <div style={{fontSize:22,fontWeight:800,color:DARK,letterSpacing:"-.02em"}}>{s.value}</div>
              {i>0 && <div style={{fontSize:10,color:MUTED,marginTop:2}}>{pct(s.value,FUNNEL[i-1].value)}% of {FUNNEL[i-1].label.toLowerCase()}</div>}
              {i<FUNNEL.length-1 && <div style={{position:"absolute",right:-8,top:"50%",transform:"translateY(-50%)",fontSize:14,color:"#d1d5db"}}>→</div>}
            </div>))}
          </div>
          <div style={{fontSize:11,color:MUTED,lineHeight:1.5}}>Open tracking isn't available (we don't embed tracking pixels), so there's no "opened" stage. Per-template breakdown arrives as volume grows.</div>
        </>}
  </Card>);
}

// Brands overview: cards for every brand he owns. Shows name + logo + send_mode,
// click to switch + jump to that brand's dashboard. Includes a "+ New brand"
// button at the end of the grid.
function BrandsOverviewCard({brandsList=[], brandData={}, onAddBrand, onSwitchBrand}){
  const [enriched, setEnriched] = useState({});
  const [adding, setAdding] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(()=>{
    if(!brandsList.length)return;
    let cancelled=false;
    (async()=>{
      const ids = brandsList.map(b=>b.id);
      // send_mode + member count + last_action per brand
      const [{data:brandsExtra}, {data:members}, {data:actions}] = await Promise.all([
        supabase.from('brands').select('id, send_mode, daily_send_cap, logo_url').in('id',ids),
        supabase.from('brand_members').select('brand_id, clerk_user_id, email, removed_at').in('brand_id',ids).is('removed_at',null),
        supabase.from('operator_actions').select('brand_id, created_at').in('brand_id',ids).order('created_at',{ascending:false}).limit(500),
      ]);
      if(cancelled)return;
      const out = {};
      for(const b of brandsExtra||[]) out[b.id] = {send_mode:b.send_mode, daily_send_cap:b.daily_send_cap, logo_url:b.logo_url, members:0, lastAction:null};
      for(const m of members||[]){
        if(!out[m.brand_id]) out[m.brand_id] = {members:0,lastAction:null};
        out[m.brand_id].members += 1;
      }
      for(const a of actions||[]){
        if(!out[a.brand_id]) continue;
        if(!out[a.brand_id].lastAction || a.created_at > out[a.brand_id].lastAction) out[a.brand_id].lastAction = a.created_at;
      }
      setEnriched(out);
    })();
    return()=>{cancelled=true;};
  },[brandsList]);

  const sendModeBadge = (mode)=>{
    const map = {
      live: {bg:"#DCFCE7",fg:"#166534",label:"LIVE"},
      test: {bg:"#FEF3C7",fg:"#92400E",label:"TEST"},
      paused: {bg:"#F3F4F6",fg:"#6B7280",label:"PAUSED"},
    };
    const s = map[mode] || map.paused;
    return <span style={{background:s.bg,color:s.fg,padding:"2px 7px",borderRadius:5,fontSize:9,fontWeight:800,letterSpacing:".06em"}}>{s.label}</span>;
  };

  const submitNew = async()=>{
    if(!newBrandName.trim()||busy)return;
    setBusy(true);
    try{
      const ok = await onAddBrand(newBrandName.trim());
      if(ok!==false){ setNewBrandName(""); setAdding(false); }
    }finally{ setBusy(false); }
  };

  // Emergency stop — flips EVERY brand to send_mode='paused' in one shot.
  // Triple-gate confirmation because this kills every outbound in flight
  // across every brand:
  //   1) confirmDialog summary ("Pause N brands?")
  //   2) confirmDialog reinforcement ("Are you sure?")
  //   3) promptDialog "type PAUSE" — literal text match required
  // Reactivation is per-brand (operator goes back to Settings → Email
  // and flips each one back to live). Intentional friction so one
  // distracted click can't quietly re-arm the worker fleet.
  const liveOrTestCount = brandsList.filter(b => {
    const m = (enriched[b.id]?.send_mode) || "paused";
    return m === "live" || m === "test";
  }).length;
  const pauseAllBrands = async () => {
    if (busy) return;
    if (liveOrTestCount === 0) { toast("All brands are already paused", "info"); return; }
    const ok1 = await confirmDialog({
      title: `Pause all ${liveOrTestCount} active brand${liveOrTestCount===1?"":"s"}?`,
      body: `Every brand currently in live or test mode flips to paused. The worker stops sending for all of them immediately. Reactivation is per-brand from Settings → Email — no bulk un-pause.`,
      destructive: true,
      confirmLabel: "Continue",
    });
    if (!ok1) return;
    const ok2 = await confirmDialog({
      title: "Last check — are you sure?",
      body: `This is the agency-wide kill switch. Every brand stops outbound email until you manually re-enable each one. Use this when something is going wrong and you need to stop the bleeding now.`,
      destructive: true,
      confirmLabel: "Yes, pause everything",
    });
    if (!ok2) return;
    const typed = await promptDialog({
      title: "Type PAUSE to confirm",
      subtitle: `Final confirmation — type the word PAUSE (uppercase) below.`,
      label: "Confirmation",
      placeholder: "PAUSE",
      confirmLabel: "Pause all brands",
    });
    if (typed !== "PAUSE") {
      if (typed !== null) toast(`Confirmation didn't match "PAUSE" — no changes`, "info");
      return;
    }
    const ids = brandsList.map(b => b.id);
    const { error } = await supabase.from("brands").update({ send_mode: "paused" }).in("id", ids);
    if (error) { toast(`Pause-all failed: ${error.message}`, "error"); return; }
    // Refresh enrichment so the UI reflects the new state immediately.
    setEnriched(prev => {
      const next = { ...prev };
      for (const id of ids) next[id] = { ...(next[id] || {}), send_mode: "paused" };
      return next;
    });
    toast(`⛔ All ${liveOrTestCount} active brand${liveOrTestCount===1?"":"s"} paused`, "success");
  };

  return(<Card style={{padding:24}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:12,flexWrap:"wrap"}}>
      <div>
        <div style={{fontWeight:700,color:DARK,fontSize:15}}>Brands</div>
        <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:540,lineHeight:1.55}}>Every brand you own. Click a card to switch to it. Numbers reflect current state — send mode, team size, last activity.</div>
      </div>
      <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
        {liveOrTestCount > 0 && (
          <button onClick={pauseAllBrands} disabled={busy} title="Agency-wide kill switch. Pauses every active brand. Triple confirmation required."
            style={{background:"#FEF2F2",color:"#991B1B",border:"1px solid #FECACA",borderRadius:8,padding:"8px 12px",fontWeight:700,fontSize:11,cursor:"pointer",whiteSpace:"nowrap",letterSpacing:".02em"}}>
            ⛔ Pause all ({liveOrTestCount})
          </button>
        )}
        {!adding && <button onClick={()=>setAdding(true)} style={{background:DARK,color:G,border:"none",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>+ New brand</button>}
      </div>
    </div>

    {adding && <div style={{display:"flex",gap:8,marginBottom:14,padding:14,background:"#f8faf9",border:`1px solid ${BORDER}`,borderRadius:10}}>
      <input value={newBrandName} onChange={e=>setNewBrandName(e.target.value)} placeholder="Brand name…" autoFocus disabled={busy}
        onKeyDown={e=>{if(e.key==='Enter')submitNew(); if(e.key==='Escape')setAdding(false);}}
        style={{flex:1,padding:"8px 12px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none"}}/>
      <button onClick={submitNew} disabled={!newBrandName.trim()||busy} style={{background:newBrandName.trim()&&!busy?DARK:"#f0f0f0",color:newBrandName.trim()&&!busy?G:"#9ab8a6",border:"none",borderRadius:7,padding:"8px 16px",fontSize:13,fontWeight:700,cursor:newBrandName.trim()&&!busy?"pointer":"not-allowed",whiteSpace:"nowrap"}}>{busy?"Creating…":"Create"}</button>
      <button onClick={()=>{setAdding(false); setNewBrandName("");}} disabled={busy} style={{background:"transparent",border:`1px solid ${BORDER}`,color:MUTED,borderRadius:7,padding:"8px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
    </div>}

    {brandsList.length===0
      ?<div style={{padding:"22px 0",textAlign:"center",color:MUTED,fontSize:13}}>No brands yet. Click "+ New brand" to create your first.</div>
      :<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:10}}>
        {brandsList.map(b=>{
          const e = enriched[b.id] || {};
          const logo = e.logo_url || brandData[b.name]?.logo;
          const last = e.lastAction ? new Date(e.lastAction) : null;
          const lastTxt = last ? (Date.now()-last.getTime() < 86400000
            ? `${Math.floor((Date.now()-last.getTime())/3600000)}h ago`
            : last.toLocaleDateString("en-AU",{day:"numeric",month:"short"}))
            : "—";
          return(<div key={b.id}
            onClick={()=>onSwitchBrand&&onSwitchBrand(b.id)}
            title={onSwitchBrand?`Open ${b.name} dashboard`:undefined}
            style={{padding:"14px 14px",border:`1px solid ${BORDER}`,borderRadius:10,background:"#fff",cursor:onSwitchBrand?"pointer":"default",transition:"border-color .12s, box-shadow .12s, transform .12s"}}
            onMouseEnter={e=>{if(onSwitchBrand){e.currentTarget.style.borderColor=G;e.currentTarget.style.boxShadow=`0 2px 8px ${G}22`;}}}
            onMouseLeave={e=>{if(onSwitchBrand){e.currentTarget.style.borderColor=BORDER;e.currentTarget.style.boxShadow="none";}}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              {logo
                ?<img src={logo} alt={b.name} style={{width:32,height:32,borderRadius:7,objectFit:"cover",border:`1px solid ${BORDER}`,flexShrink:0}}/>
                :<div style={{width:32,height:32,borderRadius:7,background:G+"18",color:G,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,flexShrink:0}}>{(b.name||"?").charAt(0).toUpperCase()}</div>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:700,color:DARK,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{b.name}</div>
                <div style={{marginTop:2}}>{sendModeBadge(e.send_mode)}</div>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:MUTED,marginTop:8,paddingTop:8,borderTop:`1px solid ${BORDER}`}}>
              <span>{e.members||0} member{(e.members||0)!==1?"s":""}</span>
              <span>last: {lastTxt}</span>
            </div>
          </div>);
        })}
      </div>}
  </Card>);
}

// Team matrix: rows = unique members, columns = brands he owns, cells = role.
// V1 read-only matrix + brand-click navigation.
// Team — per-person cards. Each card shows the person + their brand×role
// assignments as chips. "+ Assign brand" adds a new brand_members row for
// them; chip X removes one. Top of card invites a new person across N brands.
// Per Kristijan's Loom feedback: ditch the giant matrix when there are 4+
// brands, use a member-first list instead.
function TeamMatrixCard({brandsList=[], userId, userProfile={}, onSwitchBrand}){
  const [members, setMembers] = useState([]); // grouped: [{key, clerkUserId, email, isCurrent, isPending, assignments:[{id,brand_id,role,accepted}]}]
  const [loaded, setLoaded] = useState(false);
  const [assigningKey, setAssigningKey] = useState(null); // person key currently editing
  const [assignBrand, setAssignBrand] = useState("");
  const [assignRole, setAssignRole] = useState("account_manager");
  const [busy, setBusy] = useState(false);
  // New-person invite state
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("account_manager");
  const [inviteBrandIds, setInviteBrandIds] = useState([]); // selected brands for new invite

  const ROLES = ["owner","account_manager","view_only"];
  const ROLE_COLORS = {owner:{bg:"#DBEAFE",fg:"#1D4ED8"},account_manager:{bg:"#DCFCE7",fg:"#166534"},view_only:{bg:"#FEF3C7",fg:"#92400E"}};

  const load = async()=>{
    if(!brandsList.length){ setLoaded(true); return; }
    const ids = brandsList.map(b=>b.id);
    const{data}=await supabase.from('brand_members').select('*').in('brand_id',ids).is('removed_at',null);
    const map = new Map();
    for(const m of data||[]){
      const key = m.clerk_user_id || `email:${m.email}`;
      const cur = map.get(key) || {
        key, clerkUserId: m.clerk_user_id||null, email: m.email||null,
        isCurrent: m.clerk_user_id && m.clerk_user_id === userId,
        isPending: !m.accepted_at,
        assignments: [],
      };
      cur.assignments.push({id:m.id, brand_id:m.brand_id, role:m.role, accepted:!!m.accepted_at, invite_token:m.invite_token});
      if(m.accepted_at) cur.isPending = false;
      if(m.email && !cur.email) cur.email = m.email;
      map.set(key, cur);
    }
    setMembers([...map.values()].sort((a,b)=>{
      if(a.isCurrent && !b.isCurrent) return -1;
      if(b.isCurrent && !a.isCurrent) return 1;
      return b.assignments.length - a.assignments.length;
    }));
    setLoaded(true);
  };
  useEffect(()=>{ load(); /* eslint-disable-line react-hooks/exhaustive-deps */ },[brandsList, userId]);

  const brandName = (id) => brandsList.find(b=>b.id===id)?.name || "(?)";

  const displayName = (m) => {
    if(m.isCurrent){
      const full = `${(userProfile.firstName||"").trim()} ${(userProfile.lastName||"").trim()}`.trim();
      if(full) return full;
    }
    return m.email || (m.clerkUserId ? `${m.clerkUserId.slice(0,7)}…${m.clerkUserId.slice(-4)}` : "(unknown)");
  };

  const changeRole = async(assignmentId, newRole)=>{
    setBusy(true);
    try{
      const{error}=await supabase.from('brand_members').update({role:newRole}).eq('id',assignmentId);
      if(error) throw error;
      await load();
    }catch(e){ toast(`Role change failed: ${e?.message||e}`,'error'); }
    finally{ setBusy(false); }
  };

  const removeAssignment = async(member, assignment)=>{
    const bn = brandName(assignment.brand_id);
    // Hard guard: never let an operator remove themselves. Easy to do by accident
    // on the × button and locks them out of the brand instantly.
    if(member.isCurrent || member.clerkUserId === userId){
      toast("Can't remove yourself. Ask another owner to do it.", 'error');
      return;
    }
    if(!(await confirmDialog({title:`Remove from ${bn}?`,body:`${displayName(member)} will lose access to ${bn}. Other brand assignments stay.`,destructive:true,confirmLabel:"Remove"}))) return;
    setBusy(true);
    try{
      const{error}=await supabase.from('brand_members').update({removed_at:new Date().toISOString()}).eq('id',assignment.id);
      if(error) throw error;
      logSecurityEvent(assignment.brand_id,'member.remove','brand_member',assignment.id,{email:member.email||null,role:assignment.role,via:'team_matrix'});
      toast(`Removed from ${bn}`);
      await load();
    }catch(e){ toast(`Remove failed: ${e?.message||e}`,'error'); }
    finally{ setBusy(false); }
  };

  const beginAssign = (m)=>{
    setAssigningKey(m.key);
    setAssignBrand("");
    setAssignRole("account_manager");
  };
  const submitAssign = async(m)=>{
    if(!assignBrand || busy) return;
    setBusy(true);
    try{
      const payload = { brand_id: assignBrand, role: assignRole };
      if(m.clerkUserId){ payload.clerk_user_id = m.clerkUserId; payload.accepted_at = new Date().toISOString(); }
      else if(m.email){ payload.email = m.email; payload.invite_token = (typeof crypto!=='undefined'&&crypto.randomUUID)?crypto.randomUUID():String(Date.now()); }
      const{error}=await supabase.from('brand_members').insert(payload);
      if(error) throw error;
      toast(`Added to ${brandName(assignBrand)}`);
      setAssigningKey(null);
      await load();
    }catch(e){
      const msg = /duplicate|unique/i.test(e?.message||"") ? "Already a member of that brand" : (e?.message||String(e));
      toast(`Assign failed: ${msg}`,'error');
    }finally{ setBusy(false); }
  };

  const toggleInviteBrand = (id)=>{
    setInviteBrandIds(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  };
  const submitInvite = async()=>{
    if(!inviteEmail.trim() || inviteBrandIds.length===0 || busy) return;
    setBusy(true);
    try{
      const email = inviteEmail.trim().toLowerCase();
      const rows = inviteBrandIds.map(bid => ({
        brand_id: bid, email, role: inviteRole,
        invite_token: (typeof crypto!=='undefined'&&crypto.randomUUID)?crypto.randomUUID():String(Date.now())+Math.random(),
        invited_by_clerk_user_id: userId || null,
      }));
      const{data:inserted,error}=await supabase.from('brand_members').insert(rows).select();
      if(error) throw error;
      // Best-effort send one email per brand via that brand's Gmail. Each brand
      // needs Gmail connected for it to actually go out; failures surface so the
      // operator knows to copy the link manually.
      const token = await window.Clerk?.session?.getToken();
      const results = await Promise.all((inserted||[]).map(async row => {
        try{
          const r = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-invite-email`, {
            method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},
            body: JSON.stringify({member_id: row.id, app_url: window.location.origin}),
          });
          const b = await r.json().catch(()=>({}));
          if(r.ok) return {ok:true, brand_id:row.brand_id, clerkInvite:b.clerk_invitation};
          return {ok:false, brand_id:row.brand_id, msg:b.message||b.error||`http_${r.status}`};
        }catch(e){ return {ok:false, brand_id:row.brand_id, msg:e.message}; }
      }));
      const sent = results.filter(x=>x.ok).length;
      const failed = results.filter(x=>!x.ok);
      const clerkSkipped = results.some(x=>x.ok && x.clerkInvite && x.clerkInvite!=='created');
      if(failed.length===0){
        toast(`✓ Invite email sent to ${email} for ${sent} brand${sent===1?"":"s"}${clerkSkipped?' (warning: Clerk invitation failed — invitee may not get a sign-up form)':''}`);
      } else if(sent>0){
        toast(`Sent ${sent}/${results.length} emails. ${failed.length} failed — copy the invite link from those brand cards.`, 'error');
      } else {
        const first = failed[0]?.msg || 'unknown';
        toast(`Invites saved but no emails sent (${first}). Copy the invite link to share manually.`, 'error');
      }
      setInviting(false); setInviteEmail(""); setInviteRole("account_manager"); setInviteBrandIds([]);
      await load();
    }catch(e){
      toast(`Invite failed: ${e?.message||e}`,'error');
    }finally{ setBusy(false); }
  };

  const copyInviteLink = async(token)=>{
    if(!token) return;
    const url = `${window.location.origin}/?invite=${token}`;
    try{ await navigator.clipboard.writeText(url); toast("Invite link copied"); }
    catch{ toast("Copy failed",'error'); }
  };

  const resendInvite = async(member)=>{
    if(busy) return;
    setBusy(true);
    try{
      const token = await window.Clerk?.session?.getToken();
      // Send one email per pending brand assignment for this person.
      const pending = member.assignments.filter(a=>!a.accepted);
      const results = await Promise.all(pending.map(async a => {
        try{
          const r = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-invite-email`, {
            method:'POST', headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},
            body: JSON.stringify({member_id: a.id, app_url: window.location.origin}),
          });
          if(r.ok) return {ok:true};
          const b = await r.json().catch(()=>({}));
          return {ok:false, msg:b.message||b.error||`http_${r.status}`};
        }catch(e){ return {ok:false, msg:e.message}; }
      }));
      const sent = results.filter(x=>x.ok).length;
      const failed = results.filter(x=>!x.ok);
      if(failed.length===0) toast(`✓ Email sent to ${member.email} (${sent} brand${sent===1?"":"s"})`);
      else if(sent>0) toast(`Sent ${sent}/${results.length}. ${failed.length} failed — copy link manually.`,'error');
      else toast(`Send failed: ${failed[0]?.msg||'unknown'}. Copy invite link to share manually.`,'error');
    }catch(e){ toast(`Resend failed: ${e?.message||e}`,'error'); }
    finally{ setBusy(false); }
  };

  if(!loaded) return <Card style={{padding:24}}><div style={{fontSize:12,color:MUTED}}>Loading team…</div></Card>;

  return(<div style={{display:"flex",flexDirection:"column",gap:12}}>
    {/* Invite new person — top form */}
    <Card style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:inviting?14:0}}>
        <div>
          <div style={{fontWeight:700,color:DARK,fontSize:15}}>{inviting?"Invite new team member":"Team members"}</div>
          {!inviting && <div style={{fontSize:12,color:MUTED,marginTop:3}}>{members.length} {members.length===1?"person":"people"} across {brandsList.length} {brandsList.length===1?"brand":"brands"}</div>}
        </div>
        {!inviting && <button onClick={()=>setInviting(true)} style={{background:DARK,color:G,border:"none",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>+ Invite person</button>}
      </div>
      {inviting && <div>
        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:10,marginBottom:12}}>
          <div>
            <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Email</label>
            <input type="email" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="katarina@kolabs.agency" disabled={busy}
              style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Role</label>
            <select value={inviteRole} onChange={e=>setInviteRole(e.target.value)} disabled={busy}
              style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,background:"#fff",outline:"none",boxSizing:"border-box"}}>
              {ROLES.map(r=><option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
            </select>
          </div>
        </div>
        <div style={{marginBottom:12}}>
          <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:6}}>Assign to brands ({inviteBrandIds.length} selected)</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {brandsList.map(b=>{
              const on = inviteBrandIds.includes(b.id);
              return <button key={b.id} onClick={()=>toggleInviteBrand(b.id)} disabled={busy}
                style={{background:on?DARK:"#fff",color:on?G:DARK,border:`1px solid ${on?DARK:BORDER}`,borderRadius:7,padding:"5px 11px",fontSize:11,fontWeight:on?700:500,cursor:"pointer"}}>
                {on?"✓ ":""}{b.name}
              </button>;
            })}
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={()=>{setInviting(false);setInviteEmail("");setInviteBrandIds([]);}} disabled={busy} style={{background:"transparent",border:`1px solid ${BORDER}`,color:MUTED,borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button onClick={submitInvite} disabled={busy||!inviteEmail.trim()||inviteBrandIds.length===0}
            style={{background:(!busy&&inviteEmail.trim()&&inviteBrandIds.length>0)?DARK:"#f0f0f0",color:(!busy&&inviteEmail.trim()&&inviteBrandIds.length>0)?G:"#9ab8a6",border:"none",borderRadius:8,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:(!busy&&inviteEmail.trim()&&inviteBrandIds.length>0)?"pointer":"not-allowed"}}>
            {busy?"Inviting…":`Invite to ${inviteBrandIds.length||0} brand${inviteBrandIds.length===1?"":"s"}`}
          </button>
        </div>
      </div>}
    </Card>

    {/* Per-person cards */}
    {members.length===0 ? <Card style={{padding:24}}><div style={{padding:"22px 0",textAlign:"center",color:MUTED,fontSize:13}}>No team members yet — invite someone above.</div></Card>
      : members.map(m=>{
          const initial = (displayName(m)||"?").charAt(0).toUpperCase();
          const assignedBrandIds = new Set(m.assignments.map(a=>a.brand_id));
          const unassignedBrands = brandsList.filter(b=>!assignedBrandIds.has(b.id));
          return(<Card key={m.key} style={{padding:20,background:m.isCurrent?"#fafcfb":"#fff"}}>
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:14}}>
              <div style={{width:42,height:42,borderRadius:"50%",background:G+"18",color:G,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:15,flexShrink:0}}>{initial}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <div style={{fontWeight:700,color:DARK,fontSize:14}}>{displayName(m)}</div>
                  {m.isCurrent && <span style={{background:G+"18",color:G,padding:"1px 6px",borderRadius:4,fontSize:9,fontWeight:700,letterSpacing:".05em"}}>YOU</span>}
                  {m.isPending && <span style={{background:"#FEF3C7",color:"#92400E",padding:"1px 6px",borderRadius:4,fontSize:9,fontWeight:700,letterSpacing:".05em"}}>PENDING INVITE</span>}
                </div>
                {m.email && <div style={{fontSize:12,color:MUTED,marginTop:2}}>{m.email}</div>}
              </div>
              {m.isPending && m.assignments[0]?.invite_token && <div style={{display:"flex",gap:6,flexShrink:0}}>
                <button onClick={()=>resendInvite(m)} disabled={busy} style={{background:DARK,color:G,border:"none",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:busy?"wait":"pointer"}}>{busy?"Sending…":"Resend email"}</button>
                <button onClick={()=>copyInviteLink(m.assignments[0].invite_token)} style={{background:"transparent",border:`1px solid ${BORDER}`,color:DARK,borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer"}}>Copy link</button>
              </div>}
            </div>

            {/* Brand assignments */}
            <div style={{display:"flex",flexWrap:"wrap",gap:7,marginBottom:assigningKey===m.key||unassignedBrands.length>0?12:0}}>
              {m.assignments.map(a=>{
                const c = ROLE_COLORS[a.role] || ROLE_COLORS.account_manager;
                return(<div key={a.id} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 10px",background:c.bg,border:`1px solid ${c.fg}30`,borderRadius:8,fontSize:11}}>
                  <button onClick={()=>onSwitchBrand&&onSwitchBrand(a.brand_id)} title={`Open ${brandName(a.brand_id)}`}
                    style={{background:"transparent",border:"none",color:DARK,fontWeight:600,cursor:onSwitchBrand?"pointer":"default",padding:0,fontFamily:"inherit",fontSize:11}}>
                    {brandName(a.brand_id)}
                  </button>
                  <span style={{color:c.fg,opacity:0.5}}>·</span>
                  <select value={a.role} onChange={e=>changeRole(a.id, e.target.value)} disabled={busy||m.isCurrent}
                    style={{background:"transparent",border:"none",color:c.fg,fontWeight:700,fontSize:10,cursor:m.isCurrent?"not-allowed":"pointer",outline:"none",appearance:"none",paddingRight:8}}
                    title={m.isCurrent?"Can't change your own role":"Change role"}>
                    {ROLES.map(r=><option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
                  </select>
                  {!m.isCurrent && <button onClick={()=>removeAssignment(m, a)} disabled={busy} title="Remove from this brand"
                    style={{background:"transparent",border:"none",color:c.fg,fontSize:14,cursor:"pointer",padding:0,lineHeight:1,opacity:0.5}}
                    onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0.5}>×</button>}
                </div>);
              })}
              {assigningKey===m.key
                ? <div style={{display:"flex",gap:6,alignItems:"center",padding:"5px 10px",background:"#fff",border:`1px dashed ${G}`,borderRadius:8}}>
                    <select value={assignBrand} onChange={e=>setAssignBrand(e.target.value)} autoFocus disabled={busy}
                      style={{border:"none",fontSize:11,fontWeight:600,color:DARK,background:"transparent",outline:"none",cursor:"pointer"}}>
                      <option value="">— pick brand —</option>
                      {unassignedBrands.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                    <span style={{color:MUTED,opacity:0.5}}>·</span>
                    <select value={assignRole} onChange={e=>setAssignRole(e.target.value)} disabled={busy}
                      style={{border:"none",fontSize:10,fontWeight:700,color:DARK,background:"transparent",outline:"none",cursor:"pointer"}}>
                      {ROLES.map(r=><option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
                    </select>
                    <button onClick={()=>submitAssign(m)} disabled={!assignBrand||busy} style={{background:assignBrand&&!busy?DARK:"#f0f0f0",color:assignBrand&&!busy?G:"#9ab8a6",border:"none",borderRadius:5,padding:"3px 10px",fontSize:10,fontWeight:700,cursor:assignBrand&&!busy?"pointer":"not-allowed"}}>{busy?"…":"Add"}</button>
                    <button onClick={()=>setAssigningKey(null)} disabled={busy} style={{background:"transparent",border:"none",color:MUTED,fontSize:12,cursor:"pointer",padding:"0 4px"}}>✕</button>
                  </div>
                : unassignedBrands.length>0 && <button onClick={()=>beginAssign(m)} disabled={busy}
                    style={{display:"flex",alignItems:"center",gap:5,padding:"5px 11px",background:"transparent",border:`1px dashed ${BORDER}`,borderRadius:8,color:MUTED,fontSize:11,fontWeight:600,cursor:"pointer"}}
                    onMouseEnter={e=>{e.currentTarget.style.color=DARK;e.currentTarget.style.borderColor=DARK;}}
                    onMouseLeave={e=>{e.currentTarget.style.color=MUTED;e.currentTarget.style.borderColor=BORDER;}}>
                    + Assign brand
                  </button>}
            </div>
          </Card>);
        })}
  </div>);
}

// Extracted from the previous Settings → agency tab. Same logic, just relocated
// to the new owner-only top-level Agency view. Hits the same google-agency-oauth
// edge functions as before.
function AgencyDriveCard(){
  const [agencyDrive, setAgencyDrive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async()=>{
    const{data}=await supabase.rpc('agency_drive_status').select('*').maybeSingle();
    setAgencyDrive(data||{});
  };
  useEffect(()=>{ load(); },[]);

  const connect = async()=>{
    setBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-oauth/init`,{
        method:'POST',headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},body:'{}',
      });
      const body=await resp.json();
      if(!resp.ok||!body.url){ toast(`Connect failed: ${body.error||resp.status}`,'error'); setBusy(false); return; }
      window.location.href=body.url;
    }catch(e){ console.error(e); toast('Connect failed','error'); setBusy(false); }
  };
  const disconnect = async()=>{
    if(!(await confirmDialog({title:'Disconnect agency Google Drive?',body:'Folder structures stay in Drive but the app loses write access.',destructive:true,confirmLabel:'Disconnect'})))return;
    setBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-oauth/disconnect`,{
        method:'POST',headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},body:'{}',
      });
      if(!resp.ok){ toast(`Disconnect failed: ${resp.status}`,'error'); return; }
      setAgencyDrive({}); setTestResult(null);
      toast('Agency Drive disconnected');
    }catch(e){ console.error(e); toast('Disconnect failed','error'); }
    finally{ setBusy(false); }
  };
  const test = async()=>{
    setBusy(true); setTestResult(null);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-oauth/test`,{
        method:'POST',headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},body:'{}',
      });
      const body=await resp.json();
      if(!resp.ok||!body.ok){ toast(`Test failed: ${body.error||body.detail||resp.status}`,'error'); setTestResult({ok:false,error:body.error||body.detail||`HTTP ${resp.status}`}); return; }
      setTestResult({ok:true,folder:body.folder});
      toast(`✓ Test folder created: ${body.folder.name}`);
    }catch(e){ console.error(e); toast('Test failed','error'); }
    finally{ setBusy(false); }
  };

  return(<Card style={{padding:24}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6,gap:12}}>
      <div>
        <div style={{fontWeight:700,color:DARK,fontSize:15}}>Agency Google Drive</div>
        <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:540,lineHeight:1.55}}>One Google connection for the whole agency — kolabs owns the Drive that holds creator tracking sheets, agreement Docs, and content folders for every brand. New brands inherit the folder structure automatically.</div>
      </div>
    </div>
    {agencyDrive===null?<div style={{fontSize:12,color:MUTED,marginTop:10}}>Loading…</div>
      :agencyDrive.email?<div style={{marginTop:14,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",alignItems:"center",gap:14,padding:"13px 14px",background:G+"08",border:`1px solid ${G}40`,borderRadius:10}}>
          <div style={{width:30,height:30,borderRadius:8,background:G+"18",border:`1px solid ${G}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:14,color:G}}>✓</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:700,color:DARK}}>{agencyDrive.email}</div>
            <div style={{fontSize:11,color:MUTED,marginTop:2}}>
              Connected {agencyDrive.connected_at?new Date(agencyDrive.connected_at).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"}):"—"}
              {agencyDrive.agency_root_folder_id&&<> · root folder <a href={`https://drive.google.com/drive/folders/${agencyDrive.agency_root_folder_id}`} target="_blank" rel="noreferrer" style={{color:G,textDecoration:"none",fontWeight:600}}>open in Drive ↗</a></>}
            </div>
          </div>
          <div style={{display:"flex",gap:6,flexShrink:0}}>
            <button disabled={busy} onClick={test} style={{border:`1px solid ${BORDER}`,background:"#fff",color:DARK,borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:busy?"wait":"pointer"}}>Test access</button>
            <button disabled={busy} onClick={disconnect} style={{border:"1px solid #FECACA",background:"#fff",color:"#DC2626",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:busy?"wait":"pointer"}}>Disconnect</button>
          </div>
        </div>
        {testResult&&(testResult.ok
          ?<div style={{fontSize:12,padding:"10px 14px",background:"#F0FDF4",border:`1px solid ${G}40`,borderRadius:8,color:"#166534"}}>
            ✓ Test passed — created <strong>{testResult.folder?.name}</strong>
            {testResult.folder?.webViewLink&&<> · <a href={testResult.folder.webViewLink} target="_blank" rel="noreferrer" style={{color:G,fontWeight:700}}>open ↗</a></>}
          </div>
          :<div style={{fontSize:12,padding:"10px 14px",background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,color:"#DC2626"}}>
            ✗ Test failed: {testResult.error}
          </div>)}
      </div>
      :<div style={{marginTop:14}}>
        <div style={{padding:"14px 16px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,fontSize:13,color:"#92400E",lineHeight:1.55,marginBottom:14}}>
          ⚠ Not connected. Click <strong>Connect Drive</strong> below to sign in with the Google account that should own kolabs's Drive (your personal Gmail works for testing; switch to a kolabs.agency Workspace account when ready for production).
        </div>
        <button disabled={busy} onClick={connect} style={{background:DARK,color:G,border:"none",borderRadius:9,padding:"10px 18px",fontWeight:700,fontSize:13,cursor:busy?"wait":"pointer",opacity:busy?0.6:1}}>{busy?"Opening Google…":"Connect Drive"}</button>
      </div>}
  </Card>);
}

// Revenue tracker — manual MRR / ARR per brand. Brands can be linked to
// in-tool brands (brand_id) OR be external entries (brand_name only).
// Calculations live in the component: active = ended_at null or future,
// MRR = sum of active entries, ARR = MRR * 12.
function RevenueTrackerCard({userId, brandsList=[]}){
  const [entries, setEntries] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0,10);
  const blank = { brand_name:"", brand_id:null, monthly_amount:"", currency:"AUD", started_at: today, ended_at:"", notes:"" };
  const [form, setForm] = useState(blank);

  const load = async()=>{
    const{data}=await supabase.from('agency_revenue_entries').select('*').order('started_at',{ascending:false});
    setEntries(data||[]); setLoaded(true);
  };
  useEffect(()=>{ load(); },[]);

  const isActive = (e) => !e.ended_at || new Date(e.ended_at) >= new Date(today);

  // Group by currency for clean MRR rollup (don't sum AUD + USD blind)
  const byCurrency = useMemo(()=>{
    const m = {};
    for(const e of entries){
      if(!isActive(e)) continue;
      const c = e.currency || 'AUD';
      m[c] = (m[c]||0) + Number(e.monthly_amount||0);
    }
    return m;
  // eslint-disable-next-line
  },[entries, today]);

  const fmt = (n, c)=> new Intl.NumberFormat('en-AU',{style:'currency',currency:c||'AUD',maximumFractionDigits:0}).format(n||0);

  const canSubmit = form.brand_name.trim() && Number(form.monthly_amount) > 0 && form.started_at;

  const submit = async()=>{
    if(!canSubmit||busy) return;
    setBusy(true);
    try{
      const payload = {
        owner_clerk_user_id: userId,
        brand_id: form.brand_id || null,
        brand_name: form.brand_name.trim(),
        monthly_amount: Number(form.monthly_amount),
        currency: form.currency || 'AUD',
        started_at: form.started_at,
        ended_at: form.ended_at || null,
        notes: form.notes.trim() || null,
      };
      if(editingId){
        const{error}=await supabase.from('agency_revenue_entries').update(payload).eq('id',editingId);
        if(error) throw error;
        toast("Entry updated");
      } else {
        const{error}=await supabase.from('agency_revenue_entries').insert(payload);
        if(error) throw error;
        toast(`${payload.brand_name} added`);
      }
      setAdding(false); setEditingId(null); setForm(blank);
      await load();
    }catch(e){
      console.error(e);
      toast(`Save failed: ${e?.message||e}`,'error');
    }finally{
      setBusy(false);
    }
  };

  const beginEdit = (e) => {
    setEditingId(e.id);
    setForm({
      brand_id: e.brand_id || null,
      brand_name: e.brand_name || "",
      monthly_amount: String(e.monthly_amount||""),
      currency: e.currency || "AUD",
      started_at: e.started_at || today,
      ended_at: e.ended_at || "",
      notes: e.notes || "",
    });
    setAdding(true);
  };

  const endNow = async(e)=>{
    if(!(await confirmDialog({title:`End "${e.brand_name}"?`,body:`Marking as ended on ${today}. They'll drop out of active MRR but stay in history.`,confirmLabel:"End"}))) return;
    await supabase.from('agency_revenue_entries').update({ended_at: today}).eq('id', e.id);
    await load();
    toast(`${e.brand_name} ended`);
  };
  const removeEntry = async(e)=>{
    if(!(await confirmDialog({title:`Delete "${e.brand_name}" entry?`,body:"This is permanent. Use End instead if you want to preserve history.",destructive:true,confirmLabel:"Delete permanently"}))) return;
    await supabase.from('agency_revenue_entries').delete().eq('id', e.id);
    await load();
    toast(`${e.brand_name} deleted`);
  };

  const active = entries.filter(isActive);
  const past = entries.filter(e => !isActive(e));

  // Brand name suggestions from in-tool brands (clicking a suggestion fills brand_id too)
  const brandSuggestions = brandsList.filter(b => !active.some(e => e.brand_id === b.id));

  return(<div style={{display:"flex",flexDirection:"column",gap:12}}>
    {/* MRR / ARR rollup */}
    <Card style={{padding:24}}>
      <div style={{fontWeight:700,color:DARK,fontSize:15,marginBottom:14}}>Monthly recurring revenue</div>
      {Object.keys(byCurrency).length === 0
        ? <div style={{padding:"22px 0",textAlign:"center",color:MUTED,fontSize:13}}>No active brand entries yet. Add one below to see your MRR.</div>
        : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
            {Object.entries(byCurrency).map(([c,sum])=>(
              <div key={c} style={{padding:"14px 16px",border:`1px solid ${G}40`,borderRadius:10,background:G+"08"}}>
                <div style={{fontSize:10,fontWeight:800,color:G,letterSpacing:".06em",marginBottom:5}}>MRR · {c}</div>
                <div style={{fontSize:26,fontWeight:800,color:DARK,letterSpacing:"-.02em"}}>{fmt(sum,c)}</div>
                <div style={{fontSize:11,color:MUTED,marginTop:5}}>ARR: <strong style={{color:DARK}}>{fmt(sum*12,c)}</strong></div>
              </div>
            ))}
          </div>}
      <div style={{fontSize:11,color:MUTED,marginTop:12,lineHeight:1.5}}>Currencies rolled up separately — don't add AUD + USD blindly. End-dated entries drop out automatically.</div>
    </Card>

    {/* Add / Edit form */}
    <Card style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14}}>
        <div style={{fontWeight:700,color:DARK,fontSize:15}}>{adding ? (editingId ? "Edit entry" : "Add brand") : "Brands"}</div>
        {!adding && <button onClick={()=>{setAdding(true);setEditingId(null);setForm(blank);}} style={{background:DARK,color:G,border:"none",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>+ Add brand</button>}
      </div>

      {adding && <div style={{padding:18,background:"#fafcfb",border:`1px solid ${BORDER}`,borderRadius:10,marginBottom:14}}>
        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:10,marginBottom:10}}>
          <div>
            <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Brand name *</label>
            <input value={form.brand_name} onChange={e=>setForm(f=>({...f,brand_name:e.target.value,brand_id:null}))} placeholder="Effortless Swimming" disabled={busy}
              list="brand-suggestions"
              style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
            <datalist id="brand-suggestions">{brandSuggestions.map(b=><option key={b.id} value={b.name}/>)}</datalist>
            {brandSuggestions.length>0 && <div style={{fontSize:10,color:MUTED,marginTop:4}}>Type to pick from {brandSuggestions.length} in-tool brand{brandSuggestions.length===1?"":"s"}, or enter a new external brand</div>}
          </div>
          <div>
            <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Monthly $ *</label>
            <input type="number" min={0} step="0.01" value={form.monthly_amount} onChange={e=>setForm(f=>({...f,monthly_amount:e.target.value}))} placeholder="2000" disabled={busy}
              style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Currency</label>
            <select value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value}))} disabled={busy}
              style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,background:"#fff",outline:"none",boxSizing:"border-box"}}>
              {["AUD","USD","EUR","GBP","NZD","CAD"].map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 2fr",gap:10,marginBottom:10}}>
          <div>
            <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Started *</label>
            <input type="date" value={form.started_at} onChange={e=>setForm(f=>({...f,started_at:e.target.value}))} disabled={busy}
              style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Ended (optional)</label>
            <input type="date" value={form.ended_at} onChange={e=>setForm(f=>({...f,ended_at:e.target.value}))} disabled={busy}
              style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Notes</label>
            <input value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="external · retainer · scope etc" disabled={busy}
              style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={()=>{setAdding(false);setEditingId(null);setForm(blank);}} disabled={busy} style={{background:"transparent",border:`1px solid ${BORDER}`,color:MUTED,borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button onClick={submit} disabled={!canSubmit||busy} style={{background:canSubmit&&!busy?DARK:"#f0f0f0",color:canSubmit&&!busy?G:"#9ab8a6",border:"none",borderRadius:8,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:canSubmit&&!busy?"pointer":"not-allowed"}}>
            {busy?"Saving…":(editingId?"Update":"Add")}
          </button>
        </div>
      </div>}

      {!loaded ? <div style={{fontSize:12,color:MUTED,padding:"20px 0",textAlign:"center"}}>Loading…</div>
        : <>
          {active.length>0 && <div style={{marginBottom:past.length>0?20:0}}>
            <div style={{fontSize:10,fontWeight:800,color:G,letterSpacing:".06em",marginBottom:8,textTransform:"uppercase"}}>Active · {active.length}</div>
            <RevenueTable rows={active} onEdit={beginEdit} onEnd={endNow} onRemove={removeEntry} brandsList={brandsList} fmt={fmt}/>
          </div>}
          {past.length>0 && <div>
            <div style={{fontSize:10,fontWeight:800,color:MUTED,letterSpacing:".06em",marginBottom:8,textTransform:"uppercase"}}>Past · {past.length}</div>
            <RevenueTable rows={past} onEdit={beginEdit} onEnd={null} onRemove={removeEntry} brandsList={brandsList} fmt={fmt} past/>
          </div>}
          {entries.length===0 && !adding && <div style={{padding:"30px 0",textAlign:"center",color:MUTED,fontSize:13}}>No brands tracked yet. Click "+ Add brand" to log your first one.</div>}
        </>}
    </Card>
  </div>);
}

function RevenueTable({rows, onEdit, onEnd, onRemove, brandsList, fmt, past=false}){
  const linkedName = (e)=> e.brand_id ? brandsList.find(b=>b.id===e.brand_id)?.name : null;
  return(<div style={{border:`1px solid ${BORDER}`,borderRadius:10,overflow:"hidden"}}>
    <table style={{width:"100%",borderCollapse:"collapse"}}>
      <thead><tr style={{background:"#f8faf9"}}>
        {["Brand","Monthly","Started","Ended","Notes",""].map(h=><th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:MUTED,textTransform:"uppercase",letterSpacing:".05em"}}>{h}</th>)}
      </tr></thead>
      <tbody>{rows.map(e=>(<tr key={e.id} style={{borderTop:`1px solid ${BORDER}`,opacity:past?0.6:1}}>
        <td style={{padding:"10px 12px",fontSize:13,color:DARK,fontWeight:600}}>
          {e.brand_name}
          {!e.brand_id && <span title="External — not linked to an in-tool brand" style={{marginLeft:6,background:"#FEF3C7",color:"#92400E",padding:"1px 5px",borderRadius:3,fontSize:8,fontWeight:700,letterSpacing:".05em"}}>EXT</span>}
          {linkedName(e) && linkedName(e) !== e.brand_name && <span style={{marginLeft:6,fontSize:10,color:MUTED}}>(linked: {linkedName(e)})</span>}
        </td>
        <td style={{padding:"10px 12px",fontSize:13,color:DARK,fontWeight:700,whiteSpace:"nowrap"}}>{fmt(e.monthly_amount, e.currency)}</td>
        <td style={{padding:"10px 12px",fontSize:11,color:MUTED}}>{new Date(e.started_at).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"})}</td>
        <td style={{padding:"10px 12px",fontSize:11,color:MUTED}}>{e.ended_at ? new Date(e.ended_at).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"}) : <span style={{color:G,fontWeight:600}}>ongoing</span>}</td>
        <td style={{padding:"10px 12px",fontSize:11,color:MUTED,maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.notes||"—"}</td>
        <td style={{padding:"10px 12px",textAlign:"right",whiteSpace:"nowrap"}}>
          <button onClick={()=>onEdit(e)} title="Edit" style={{background:"transparent",border:`1px solid ${BORDER}`,color:DARK,borderRadius:6,padding:"3px 9px",fontSize:11,fontWeight:600,cursor:"pointer",marginRight:6}}>Edit</button>
          {onEnd && <button onClick={()=>onEnd(e)} title="Mark as ended today" style={{background:"transparent",border:"1px solid #FDE68A",color:"#92400E",borderRadius:6,padding:"3px 9px",fontSize:11,fontWeight:600,cursor:"pointer",marginRight:6}}>End</button>}
          <button onClick={()=>onRemove(e)} title="Delete permanently" style={{background:"transparent",border:"1px solid #FECACA",color:"#DC2626",borderRadius:6,padding:"3px 9px",fontSize:11,fontWeight:600,cursor:"pointer"}}>Delete</button>
        </td>
      </tr>))}</tbody>
    </table>
  </div>);
}

// Suppress list — emails we never contact again. Auto-populated by the worker
// on bounces; manually editable from this surface.
function SuppressListCard({userId}){
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newReason, setNewReason] = useState("manual");
  const [busy, setBusy] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async()=>{
    const{data}=await supabase.from('agency_outreach_suppress').select('*').order('created_at',{ascending:false});
    setRows(data||[]);
    setLoaded(true);
  };
  useEffect(()=>{ load(); },[]);

  const validEmail = (e)=>/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const canAdd = validEmail(newEmail.trim());

  const addEntry = async()=>{
    if(!canAdd||busy) return;
    setBusy(true);
    try{
      const{data,error}=await supabase.from('agency_outreach_suppress').insert({
        owner_clerk_user_id: userId,
        email: newEmail.trim().toLowerCase(),
        reason: newReason,
      }).select('*').single();
      if(error) throw error;
      setRows(prev=>[data, ...prev]);
      setNewEmail("");
      setNewReason("manual");
      toast(`${data.email} added to suppress list`);
    }catch(e){
      console.error(e);
      const msg = /duplicate|unique/i.test(e?.message||"") ? "Already on the list" : (e?.message||String(e));
      toast(`Add failed: ${msg}`,'error');
    }finally{
      setBusy(false);
    }
  };

  const removeEntry = async(row)=>{
    if(!(await confirmDialog({title:`Remove ${row.email}?`,body:"They'll be contactable again. The worker no longer blocks sends to this address.",confirmLabel:"Remove"}))) return;
    setRemovingId(row.id);
    try{
      const{error}=await supabase.from('agency_outreach_suppress').delete().eq('id',row.id);
      if(error) throw error;
      setRows(prev=>prev.filter(r=>r.id!==row.id));
      toast(`${row.email} removed`);
    }catch(e){
      console.error(e);
      toast(`Remove failed: ${e?.message||e}`,'error');
    }finally{
      setRemovingId(null);
    }
  };

  const filtered = filter.trim()
    ? rows.filter(r => (r.email||"").includes(filter.trim().toLowerCase()))
    : rows;

  const reasonBadge = (reason)=>{
    const map = {
      bounced: {bg:"#FEE2E2",fg:"#991B1B"},
      unsubscribed: {bg:"#FEF3C7",fg:"#92400E"},
      manual: {bg:"#F3F4F6",fg:"#374151"},
      replied_negative: {bg:"#FEF3C7",fg:"#92400E"},
    };
    const s = map[reason] || map.manual;
    return <span style={{background:s.bg,color:s.fg,padding:"1px 7px",borderRadius:4,fontSize:9,fontWeight:700,letterSpacing:".03em"}}>{(reason||"manual").replace(/_/g," ")}</span>;
  };

  return(<div style={{display:"flex",flexDirection:"column",gap:12}}>
    <Card style={{padding:24}}>
      <div style={{fontWeight:700,color:DARK,fontSize:15,marginBottom:14}}>Add to suppress</div>
      <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:240}}>
          <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Email</label>
          <input type="email" value={newEmail} onChange={e=>setNewEmail(e.target.value)}
            onKeyDown={e=>{if(e.key==='Enter'&&canAdd) addEntry();}}
            placeholder="someone@brand.com" disabled={busy}
            style={{width:"100%",padding:"8px 12px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
        </div>
        <div style={{flexShrink:0,width:170}}>
          <label style={{fontSize:10,color:MUTED,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",display:"block",marginBottom:4}}>Reason</label>
          <select value={newReason} onChange={e=>setNewReason(e.target.value)} disabled={busy}
            style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,background:"#fff",outline:"none",boxSizing:"border-box"}}>
            <option value="manual">manual</option>
            <option value="unsubscribed">unsubscribed</option>
            <option value="replied_negative">replied negative</option>
            <option value="bounced">bounced</option>
          </select>
        </div>
        <button onClick={addEntry} disabled={!canAdd||busy}
          style={{background:canAdd&&!busy?DARK:"#f0f0f0",color:canAdd&&!busy?G:"#9ab8a6",border:"none",borderRadius:8,padding:"9px 18px",fontSize:13,fontWeight:700,cursor:canAdd&&!busy?"pointer":"not-allowed",whiteSpace:"nowrap"}}>
          {busy?"Adding…":"Add"}
        </button>
      </div>
      <div style={{fontSize:11,color:MUTED,marginTop:10,lineHeight:1.5}}>Honored by BOTH the agency cold-outreach worker AND brand→creator flow sends. Bounced addresses auto-land here with reason="bounced". Use "unsubscribed" when someone explicitly opts out — although now that every outbound email includes a one-click unsubscribe footer + List-Unsubscribe header, most opt-outs land in this list automatically.</div>
    </Card>

    <Card style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14,gap:12,flexWrap:"wrap"}}>
        <div style={{fontWeight:700,color:DARK,fontSize:15}}>{rows.length} {rows.length===1?"entry":"entries"}</div>
        <input type="text" value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Search…"
          style={{padding:"6px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,outline:"none",width:220}}/>
      </div>
      {!loaded ? <div style={{fontSize:12,color:MUTED,padding:"10px 0"}}>Loading…</div>
        : rows.length===0 ? <div style={{padding:"22px 0",textAlign:"center",color:MUTED,fontSize:13}}>Suppress list is empty. Bounces will fill it automatically; add manually for explicit unsubscribes.</div>
        : filtered.length===0 ? <div style={{padding:"22px 0",textAlign:"center",color:MUTED,fontSize:13}}>No matches for "{filter}".</div>
        : <div style={{border:`1px solid ${BORDER}`,borderRadius:10,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:"#f8faf9"}}>
              {["Email","Reason","Added",""].map(h=><th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:MUTED,textTransform:"uppercase",letterSpacing:".05em"}}>{h}</th>)}
            </tr></thead>
            <tbody>{filtered.map(r=>(<tr key={r.id} style={{borderTop:`1px solid ${BORDER}`}}>
              <td style={{padding:"9px 12px",fontSize:13,color:DARK,fontFamily:"DM Mono, monospace"}}>{r.email}</td>
              <td style={{padding:"9px 12px"}}>{reasonBadge(r.reason)}</td>
              <td style={{padding:"9px 12px",fontSize:11,color:MUTED}}>{new Date(r.created_at).toLocaleString("en-AU",{day:"numeric",month:"short",year:"numeric"})}</td>
              <td style={{padding:"9px 12px",textAlign:"right"}}>
                <button onClick={()=>removeEntry(r)} disabled={removingId===r.id}
                  style={{background:"transparent",border:"1px solid #FECACA",color:"#DC2626",borderRadius:6,padding:"3px 9px",fontSize:11,fontWeight:600,cursor:removingId===r.id?"wait":"pointer"}}>
                  {removingId===r.id?"…":"Remove"}
                </button>
              </td>
            </tr>))}</tbody>
          </table>
        </div>}
    </Card>
  </div>);
}

// Coming-soon section card — for sections that are part of the agency dashboard
// vision but not yet built. Renders a card with title + subtitle so the
// dashboard layout shows what's planned without faking functionality.
function PlaceholderCard({title, subtitle}){
  return(<Card style={{padding:24,background:"#fafcfb",border:`1px dashed ${BORDER}`}}>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
      <div style={{fontWeight:700,color:MUTED,fontSize:14}}>{title}</div>
      <span style={{background:"#FEF3C7",color:"#92400E",padding:"2px 7px",borderRadius:4,fontSize:9,fontWeight:800,letterSpacing:".05em"}}>COMING SOON</span>
    </div>
    <div style={{fontSize:12.5,color:MUTED,lineHeight:1.55,maxWidth:580}}>{subtitle}</div>
  </Card>);
}

// Security audit log viewer. Owner-only. Shows the high-severity events
// captured via log_security_event() — brand delete, member role change,
// cap change, send mode change, inbox disconnect, flow delete. Last 200
// events for the current brand, filterable by action prefix.
function SecurityLogCard({brandId}){
  const [events,setEvents] = useState([]);
  const [loaded,setLoaded] = useState(false);
  const [filterAction,setFilterAction] = useState("all");
  useEffect(()=>{
    if(!brandId)return;
    let cancelled=false;
    (async()=>{
      const{data}=await supabase.from('security_log')
        .select('*').eq('brand_id',brandId).order('created_at',{ascending:false}).limit(200);
      if(cancelled)return;
      setEvents(data||[]); setLoaded(true);
    })();
    return()=>{cancelled=true;};
  },[brandId]);
  // Distinct action prefixes (e.g. "brand", "member", "gmail_inbox") for the filter chips.
  const actionGroups = useMemo(()=>{
    const set=new Set();
    for(const e of events){
      const prefix=(e.action||'').split('.')[0];
      if(prefix) set.add(prefix);
    }
    return ["all", ...Array.from(set).sort()];
  },[events]);
  const filtered = useMemo(()=>{
    if(filterAction==="all") return events;
    return events.filter(e=>(e.action||'').startsWith(filterAction+'.'));
  },[events,filterAction]);
  // Color coding by severity. brand.delete = red, member.* = amber, others = blue/grey.
  const actionStyle = (action)=>{
    if(!action) return {bg:"#f3f4f6",fg:"#4b5563"};
    if(action.startsWith('brand.delete')) return {bg:"#FEE2E2",fg:"#991B1B"};
    if(action.startsWith('brand.restore')) return {bg:"#DCFCE7",fg:"#166534"};
    if(action.startsWith('member.')) return {bg:"#FEF3C7",fg:"#92400E"};
    if(action.startsWith('flow.delete')) return {bg:"#FEE2E2",fg:"#991B1B"};
    if(action.startsWith('gmail_inbox.disconnect')) return {bg:"#FEF3C7",fg:"#92400E"};
    if(action.startsWith('brand.send_mode')) return {bg:"#DBEAFE",fg:"#1D4ED8"};
    return {bg:"#f3f4f6",fg:"#4b5563"};
  };
  const shortUserId = (u)=>u?`${u.slice(0,12)}…`:"system";
  return(<div style={{display:"flex",flexDirection:"column",gap:12}}>
    <Card style={{padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontWeight:700,color:DARK,fontSize:15}}>Security audit log</div>
          <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:540,lineHeight:1.55}}>
            Forensic trail of high-severity actions on this brand — brand delete, member role change, cap change, send-mode flip, inbox disconnect, flow delete. Last 200 events. Visible to brand owners only.
          </div>
        </div>
        {/* Filter chips by action prefix. */}
        {actionGroups.length>1&&(
          <div style={{display:"flex",gap:4,background:"#f8faf9",borderRadius:8,padding:3,flexWrap:"wrap"}}>
            {actionGroups.map(g=>(
              <button key={g} onClick={()=>setFilterAction(g)} style={{padding:"4px 10px",borderRadius:6,border:"none",background:filterAction===g?DARK:"transparent",color:filterAction===g?G:MUTED,fontSize:11,fontWeight:filterAction===g?700:500,cursor:"pointer",textTransform:"capitalize"}}>{g==="all"?"All":g.replace(/_/g," ")}</button>
            ))}
          </div>
        )}
      </div>
      {!loaded
        ?<div style={{fontSize:13,color:MUTED,padding:"20px 0"}}>Loading audit log…</div>
        :filtered.length===0
          ?<div style={{fontSize:13,color:MUTED,padding:"24px 0",textAlign:"center",background:"#fafcfb",borderRadius:10,border:`1px dashed ${BORDER}`}}>{events.length===0?"No security events yet for this brand. They'll appear here as your team operates the brand.":"No events match the current filter."}</div>
          :<div style={{display:"flex",flexDirection:"column",gap:1,background:BORDER,borderRadius:10,overflow:"hidden",border:`1px solid ${BORDER}`}}>
            {filtered.map((e,i)=>{
              const style=actionStyle(e.action);
              return(<div key={e.id} style={{display:"flex",alignItems:"flex-start",gap:12,padding:"11px 14px",background:"#fff"}}>
                <span style={{background:style.bg,color:style.fg,padding:"2px 8px",borderRadius:5,fontSize:10,fontWeight:700,fontFamily:"DM Mono, monospace",flexShrink:0,whiteSpace:"nowrap"}}>{e.action}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,color:DARK,lineHeight:1.5}}>
                    {e.target_type&&e.target_id&&<span style={{color:MUTED}}>{e.target_type} </span>}
                    {e.details&&Object.keys(e.details).length>0&&(
                      <span style={{fontSize:11,color:MUTED}}>
                        {Object.entries(e.details).slice(0,5).map(([k,v])=>`${k}=${typeof v==='object'?JSON.stringify(v):v}`).join(" · ")}
                      </span>
                    )}
                  </div>
                  <div style={{fontSize:11,color:MUTED,marginTop:3}}>
                    <span style={{fontFamily:"DM Mono, monospace"}}>{shortUserId(e.actor_clerk_user_id)}</span>
                    {" · "}{new Date(e.created_at).toLocaleString("en-AU",{day:"numeric",month:"short",year:"numeric",hour:"numeric",minute:"2-digit"})}
                  </div>
                </div>
              </div>);
            })}
          </div>
      }
    </Card>
  </div>);
}

// Pulls SPF / DMARC / DKIM records via Google DNS-over-HTTPS (CORS-friendly).
// For Workspace-hosted brand domains, all three must be set up before high-volume
// cold outreach — missing DMARC esp. is the most common deliverability hit.
// gmail.com / googlemail.com (personal accounts) → Google handles all 3, skip.
// Per-inbox health row for the Health tab. Self-contained — does its own
// DNS lookup for SPF / DKIM / DMARC so the Health card doesn't need to be
// passed pre-computed deliverability data. Mirrors DeliverabilityPill's
// classification logic but exposes the structured findings (not just a label)
// so we can list each issue separately.
function InboxHealthRow({inbox, usage}){
  const domain=(inbox.email_address||"").split("@")[1]||"";
  const [dns,setDns]=useState({loading:true});
  useEffect(()=>{
    let cancelled=false;
    if(!domain){setDns({loading:false});return;}
    if(/(^|\.)gmail\.com$/i.test(domain)||/(^|\.)googlemail\.com$/i.test(domain)){
      setDns({loading:false,skip:true});return;
    }
    (async()=>{
      const lookup=async(name)=>{
        try{
          const r=await fetch(`https://dns.google/resolve?name=${encodeURIComponent(name)}&type=TXT`);
          const j=await r.json();
          return (j.Answer||[]).map(a=>(a.data||"").replace(/^"|"$/g,'').replace(/" +"/g,''));
        }catch{return[];}
      };
      const [spfTxt,dmarcTxt,dkimGoogle,dkimSel1]=await Promise.all([
        lookup(domain), lookup(`_dmarc.${domain}`),
        lookup(`google._domainkey.${domain}`), lookup(`selector1._domainkey.${domain}`),
      ]);
      if(cancelled) return;
      const spf=spfTxt.find(t=>/^v=spf1/i.test(t))||null;
      const dmarc=dmarcTxt.find(t=>/^v=DMARC1/i.test(t))||null;
      const dkim=dkimGoogle.find(t=>/v=DKIM1/i.test(t))||dkimSel1.find(t=>/v=DKIM1/i.test(t))||null;
      const dmarcPolicy=dmarc?(dmarc.match(/p=([a-z]+)/i)||[])[1]:null;
      setDns({loading:false,spf,dmarc,dkim,dmarcPolicy});
    })();
    return ()=>{cancelled=true;};
  },[domain]);

  // Compute every signal we know about for this inbox.
  const watchExp=inbox.watch_expires_at?new Date(inbox.watch_expires_at):null;
  const hrs=watchExp?(watchExp-Date.now())/3.6e6:null;
  const watchStatus=!watchExp?"never_started":hrs<=0?"expired":hrs<24?"expiring":"active";
  const warmupStart=inbox.warmup_started_at?new Date(inbox.warmup_started_at):null;
  const warmupDay=warmupStart?Math.floor((Date.now()-warmupStart.getTime())/(24*60*60*1000)):null;
  const inWarmup=warmupDay!=null && warmupDay<14;
  const rawCap=inbox.daily_cap||100;
  const rampCap=warmupDay==null?null:warmupDay<3?25:warmupDay<6?50:warmupDay<10?100:warmupDay<14?150:null;
  const effCap=rampCap==null?rawCap:Math.min(rawCap,rampCap);
  const used=usage||0;
  const capPct=Math.round((used/effCap)*100);

  // Build the issues list. Severity rule: only "warn" / "crit" if the operator
  // genuinely needs to do something. "info" is for context only and shouldn't
  // turn the row orange — handled separately below.
  //
  // Plain-English over jargon. Almost nobody on the kolabs team is going to
  // know what "DMARC p=none" or "Gmail watch" means — explain the impact and
  // whether action is needed, not the underlying mechanism.
  const issues=[]; // crit + warn only — these flip the badge color
  const fyis=[];   // info-only context (warmup state, normal starting configs)

  if(watchStatus==="expired" && hrs!=null && hrs < -24) {
    // Only flag as critical if expiry is MORE than 24h past — within 24h, the
    // daily auto-renew cron may not have fired yet. Avoids noisy false alarms.
    issues.push({sev:"crit",msg:"Reply detection has been OFF for over a day. The daily auto-renew should have fixed this — disconnect this inbox in Settings → Email and reconnect to refresh it."});
  } else if(watchStatus==="never_started") {
    issues.push({sev:"crit",msg:"Reply detection is not registered for this inbox — incoming replies won't trigger automation. Disconnect this inbox in Settings → Email and reconnect to fix."});
  }
  // 'expiring' (under 24h to renewal) is normal — the daily cron handles it
  // automatically. Surface it as FYI only, no alarm.
  else if(watchStatus==="expiring") {
    fyis.push(`Reply tracking refreshes automatically every day. Next refresh in ${hrs?Math.round(hrs):'<24'}h — nothing to do.`);
  }

  if(!dns.loading && !dns.skip){
    if(!dns.spf) issues.push({sev:"crit",msg:`Your sending domain (${domain}) has no SPF record. Gmail will start filtering your outreach as spam past low volume. Ask whoever owns the domain DNS to add Google's SPF TXT record — same fix as connecting a Google Workspace.`});
    if(!dns.dkim) issues.push({sev:"warn",msg:`Your sending domain (${domain}) is missing the DKIM signing key. Without it, replies forwarded through other mail servers can fail authentication and bounce. Fix: copy the DKIM TXT record from Google Workspace admin and add it to DNS.`});
    if(!dns.dmarc) issues.push({sev:"warn",msg:`Your sending domain (${domain}) has no DMARC record. Without one, you have no protection against spoofing — anyone can send email pretending to be from your domain. Add a starter DMARC record (your IT can copy from Google's recommended one).`});
    else if(dns.dmarcPolicy==="none") {
      // This is the CORRECT starting config for a freshly-connected domain.
      // Do not flag as warning.
      fyis.push(`Email-spoofing protection on ${domain} is in "observe-only" mode — the recommended starting state. Once you've been sending cleanly for a few weeks you can tighten it (your IT will know what that means). No rush.`);
    }
  }
  if(inWarmup) fyis.push(`In Gmail-warmup mode — day ${warmupDay+1} of 14. Daily send cap is held at ${effCap} so the inbox builds reputation gradually. Full cap (${rawCap}) unlocks on day 14.`);
  if(capPct>=90) issues.push({sev:"warn",msg:`You've sent ${used} of ${effCap} allowed today (${capPct}%). When you hit 100% the worker stops sending from this inbox until tomorrow. Add another inbox or raise the cap if this is becoming routine.`});

  // Severity is driven by issues only. FYIs don't change the badge — they
  // render as a small muted block under the issue list so the operator sees
  // them but doesn't get scared by a yellow row when nothing is wrong.
  const topSev=issues.find(i=>i.sev==="crit")?"crit":issues.find(i=>i.sev==="warn")?"warn":"ok";
  const sevColors={ok:G,warn:"#F59E0B",crit:"#EF4444"};
  const sevBg={ok:G+"10",warn:"#FFFBEB",crit:"#FEF2F2"};
  const sevLabel={ok:"Looks good",warn:"Needs a look",crit:"Action needed"};

  return(<div style={{padding:"12px 14px",border:`1px solid ${sevColors[topSev]}50`,borderRadius:9,background:sevBg[topSev]}}>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:(issues.length||fyis.length)?8:0,flexWrap:"wrap"}}>
      <span style={{fontWeight:600,color:DARK,fontSize:13}}>{inbox.email_address}</span>
      {inbox.is_primary && <span style={{background:G+"18",color:"#166534",fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:5}}>PRIMARY</span>}
      <span style={{background:sevColors[topSev]+"22",color:sevColors[topSev],fontSize:10,fontWeight:700,padding:"1px 8px",borderRadius:5,marginLeft:"auto"}}>{topSev==="ok"?sevLabel[topSev]:`${sevLabel[topSev]} · ${issues.length}`}</span>
    </div>
    {issues.length>0 && (
      <ul style={{margin:0,padding:0,listStyle:"none",display:"flex",flexDirection:"column",gap:5}}>
        {issues.map((iss,i)=>(
          <li key={i} style={{fontSize:11,lineHeight:1.55,display:"flex",gap:6}}>
            <span style={{flexShrink:0,fontWeight:700,color:sevColors[iss.sev]}}>{iss.sev==="crit"?"✕":"⚠"}</span>
            <span style={{color:DARK}}>{iss.msg}</span>
          </li>
        ))}
      </ul>
    )}
    {fyis.length>0 && (
      <div style={{marginTop:issues.length?8:0,padding:"6px 10px",background:"rgba(0,0,0,0.03)",borderRadius:6,display:"flex",flexDirection:"column",gap:4}}>
        {fyis.map((f,i)=>(
          <div key={i} style={{fontSize:11,color:MUTED,lineHeight:1.5}}>{f}</div>
        ))}
      </div>
    )}
    {dns.loading && !issues.length && !fyis.length && <div style={{fontSize:11,color:MUTED,fontStyle:"italic"}}>Checking deliverability records…</div>}
  </div>);
}

function DeliverabilityPill({email}){
  const domain=(email||"").split("@")[1]||"";
  const [state,setState]=useState({loading:true});
  useEffect(()=>{
    let cancelled=false;
    if(!domain) return;
    if(/(^|\.)gmail\.com$/i.test(domain)||/(^|\.)googlemail\.com$/i.test(domain)){
      setState({loading:false,skip:true});return;
    }
    (async()=>{
      const lookup=async(name)=>{
        try{
          const r=await fetch(`https://dns.google/resolve?name=${encodeURIComponent(name)}&type=TXT`);
          const j=await r.json();
          return (j.Answer||[]).map(a=>(a.data||"").replace(/^"|"$/g,'').replace(/" +"/g,''));
        }catch{return[];}
      };
      const [spfTxt,dmarcTxt,dkimGoogle,dkimSel1]=await Promise.all([
        lookup(domain),
        lookup(`_dmarc.${domain}`),
        lookup(`google._domainkey.${domain}`),
        lookup(`selector1._domainkey.${domain}`),
      ]);
      if(cancelled) return;
      const spf=spfTxt.find(t=>/^v=spf1/i.test(t))||null;
      const dmarc=dmarcTxt.find(t=>/^v=DMARC1/i.test(t))||null;
      const dkim=dkimGoogle.find(t=>/v=DKIM1/i.test(t))||dkimSel1.find(t=>/v=DKIM1/i.test(t))||null;
      const dmarcPolicy=dmarc?(dmarc.match(/p=([a-z]+)/i)||[])[1]:null;
      setState({loading:false,spf,dmarc,dkim,dmarcPolicy});
    })();
    return ()=>{cancelled=true;};
  },[domain]);
  if(state.loading) return <span style={{background:"#F3F4F6",color:"#6B7280",fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:5,border:"1px solid #E5E7EB"}}>Deliverability …</span>;
  if(state.skip) return <span title="Personal Gmail account — Google manages SPF/DKIM/DMARC for gmail.com automatically." style={{background:"#F3F4F6",color:"#6B7280",fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:5,border:"1px solid #E5E7EB"}}>gmail.com</span>;
  const haveSpf=!!state.spf, haveDmarc=!!state.dmarc, haveDkim=!!state.dkim;
  const score=(haveSpf?1:0)+(haveDmarc?1:0)+(haveDkim?1:0);
  // Red if SPF missing (hard block on volume). Amber if DMARC missing or p=none. Green if all + p≥quarantine.
  let color="#10B981", bg="#ECFDF5", border="#BBF7D0", label="Deliverability ✓";
  if(!haveSpf){color="#EF4444";bg="#FEF2F2";border="#FECACA";label="SPF missing ✗";}
  else if(!haveDmarc||!haveDkim){color="#F59E0B";bg="#FFFBEB";border="#FDE68A";label=score===2?"Deliverability ⚠":"Records missing ⚠";}
  else if(state.dmarcPolicy==="none"){color="#F59E0B";bg="#FFFBEB";border="#FDE68A";label="DMARC monitor-only ⚠";}
  const tooltip=[
    `SPF: ${haveSpf?"✓":"✗ missing"}${haveSpf?` (${state.spf.slice(0,80)}${state.spf.length>80?"…":""})`:""}`,
    `DKIM: ${haveDkim?"✓":"✗ missing — add google._domainkey TXT record"}`,
    `DMARC: ${haveDmarc?`✓ p=${state.dmarcPolicy||"?"}`:"✗ missing"}${state.dmarcPolicy==="none"?" (monitor only — upgrade to quarantine/reject once clean)":""}`,
  ].join("\n");
  return <span title={tooltip} style={{background:bg,color,fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:5,border:`1px solid ${border}`,cursor:"help"}}>{label}</span>;
}

// Inline editor for brand.settings.daily_send_cap. Lives in Settings → Email tab.
// Default 300. Operator can raise once deliverability + reply rate prove out.
function BrandSendCapCard({brandId}){
  const [cap,setCap] = useState(null);
  const [draft,setDraft] = useState("");
  const [saving,setSaving] = useState(false);
  const [todaySent,setTodaySent] = useState(null);
  const [brandTz,setBrandTz] = useState(null);
  useEffect(()=>{
    if(!brandId)return;
    let cancelled=false;
    (async()=>{
      const{data}=await supabase.from('brands').select('settings').eq('id',brandId).maybeSingle();
      if(cancelled)return;
      const current=typeof data?.settings?.daily_send_cap==='number'?data.settings.daily_send_cap:300;
      setCap(current);setDraft(String(current));
      // Brand-configured TZ — used to format the pace projection hit time so
      // the UI matches the brand_notification message verbatim. Falls back
      // to operator's browser TZ if brand has none set.
      setBrandTz(data?.settings?.timezone || null);
      // Also pull today's sent count for context.
      const startOfDay=new Date();startOfDay.setUTCHours(0,0,0,0);
      const{count}=await supabase.from('email_queue')
        .select('*',{count:'exact',head:true})
        .eq('brand_id',brandId).eq('status','sent')
        .gte('sent_at',startOfDay.toISOString());
      if(!cancelled) setTodaySent(count||0);
    })();
    return()=>{cancelled=true;};
  },[brandId]);
  const save=async()=>{
    const n=parseInt(draft,10);
    if(!n||n<1||n>5000){toast('Cap must be 1–5000','error');return;}
    setSaving(true);
    // Fetch + merge into settings JSONB so we don't clobber other keys.
    const{data:row}=await supabase.from('brands').select('settings').eq('id',brandId).maybeSingle();
    const next={...(row?.settings||{}),daily_send_cap:n};
    const{error}=await supabase.from('brands').update({settings:next}).eq('id',brandId);
    setSaving(false);
    if(error){toast(`Save failed: ${error.message}`,'error');return;}
    logSecurityEvent(brandId,'brand.daily_cap_change','brand',brandId,{from:cap,to:n});
    setCap(n);toast(`✓ Daily cap: ${n}`);
  };
  const dirty=cap!==null&&parseInt(draft,10)!==cap;
  const usePct=cap&&todaySent!=null?Math.min(100,Math.round((todaySent/cap)*100)):0;
  const usageColor=usePct>=90?"#EF4444":usePct>=70?"#F59E0B":G;
  // Predictive pace projection. Mirrors the worker's cap_pace_warning logic:
  // only meaningful with ≥3h of signal and ≥5 sends. Tells the operator
  // BEFORE the 90% trigger fires whether today is on track or trending hot.
  const paceProjection = (()=>{
    if(!cap || todaySent==null) return null;
    const startOfDay=new Date(); startOfDay.setUTCHours(0,0,0,0);
    const hoursElapsed=(Date.now()-startOfDay.getTime())/3.6e6;
    if(todaySent<5 || hoursElapsed<3 || hoursElapsed>=20) return null;
    const projected=Math.round((todaySent/hoursElapsed)*24);
    const projOverCap=projected>cap;
    const hoursToCap=(cap/todaySent)*hoursElapsed;
    const hitMs=startOfDay.getTime()+hoursToCap*3.6e6;
    // Format in brand TZ (matches the worker's brand_notification message);
    // fall back to operator's browser TZ if brand TZ is unset or invalid.
    let hitLocal;
    try {
      hitLocal = brandTz
        ? new Date(hitMs).toLocaleTimeString("en-US",{timeZone:brandTz,hour:"numeric",minute:"2-digit"}) + ` (${brandTz})`
        : new Date(hitMs).toLocaleTimeString([],{hour:"numeric",minute:"2-digit"});
    } catch {
      hitLocal = new Date(hitMs).toLocaleTimeString([],{hour:"numeric",minute:"2-digit"});
    }
    return {projected, projOverCap, hitLocal, headroom: cap-todaySent};
  })();
  return(<Card style={{padding:24}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:12}}>
      <div>
        <div style={{fontWeight:700,color:DARK,fontSize:15}}>Daily send cap</div>
        <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:540,lineHeight:1.55}}>Total emails this brand can send across all inboxes per day. Worker auto-pauses when hit and signals the operator (Dashboard banner). Default <strong>300</strong> — raise once reply rate proves out, lower if deliverability suffers.</div>
      </div>
    </div>
    {cap===null?<div style={{fontSize:12,color:MUTED}}>Loading…</div>:
      <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <input type="number" min={1} max={5000} value={draft} onChange={e=>setDraft(e.target.value)}
            onKeyDown={e=>{if(e.key==='Enter')save();}}
            style={{width:80,padding:"7px 10px",border:`1px solid ${dirty?"#F59E0B":BORDER}`,borderRadius:7,fontSize:14,fontWeight:700,textAlign:"center",outline:"none",background:dirty?"#FFFBEB":"#fff"}}/>
          <span style={{fontSize:12,color:MUTED}}>emails / day</span>
        </div>
        {dirty&&<button onClick={save} disabled={saving} style={{background:DARK,color:G,border:"none",borderRadius:7,padding:"7px 14px",fontWeight:700,fontSize:12,cursor:saving?"wait":"pointer"}}>{saving?"Saving…":"Save"}</button>}
        {dirty&&<button onClick={()=>setDraft(String(cap))} style={{border:`1px solid ${BORDER}`,background:"#fff",color:MUTED,borderRadius:7,padding:"7px 12px",fontSize:12,cursor:"pointer"}}>Cancel</button>}
        {todaySent!=null&&(
          <div style={{flex:1,minWidth:200,display:"flex",alignItems:"center",gap:10}}>
            <div style={{flex:1,height:8,background:"#f0f0f0",borderRadius:4,overflow:"hidden"}}>
              <div style={{width:`${usePct}%`,height:"100%",background:usageColor,transition:"width .3s"}}/>
            </div>
            <span style={{fontSize:12,fontWeight:600,color:usageColor,whiteSpace:"nowrap"}}>{todaySent} / {cap} today ({usePct}%)</span>
          </div>
        )}
      </div>
    }
    {paceProjection && (
      <div style={{marginTop:12,padding:"8px 12px",borderRadius:8,background:paceProjection.projOverCap?"#FFFBEB":"#F0FDF4",border:`1px solid ${paceProjection.projOverCap?"#FDE68A":G+"30"}`,display:"flex",alignItems:"center",gap:8,fontSize:12}}>
        <span>{paceProjection.projOverCap?"⚠️":"📈"}</span>
        <div style={{color:paceProjection.projOverCap?"#92400E":"#166534",fontWeight:600,flex:1}}>
          {paceProjection.projOverCap
            ? <>On current pace, today projects to <strong>{paceProjection.projected}</strong> sends — cap of {cap} likely reached around <strong>{paceProjection.hitLocal}</strong>. {paceProjection.headroom} sends remaining before auto-pause.</>
            : <>On track — projecting <strong>{paceProjection.projected}</strong> sends today (well under {cap} cap).</>
          }
        </div>
      </div>
    )}
  </Card>);
}

function Settings({brand,activeBrand,setBrandData,brandData,setActiveBrand,brandId,reloadBrands}){
  const[tab,setTab]=useState("brand");
  const[editing,setEditing]=useState({});

  // Agency-owner check — gates the Activity tab visibility. Only operators
  // who are role='owner' on at least one brand see team-time-tracking data.
  // For now this means Kristijan only; expands automatically when other
  // owners exist.
  const [isAgencyOwner, setIsAgencyOwner] = useState(false);
  useEffect(()=>{
    (async()=>{
      const{data}=await supabase.rpc('user_owns_any_brand');
      setIsAgencyOwner(!!data);
    })();
  },[]);

  // ---------- Send-mode toggle ----------
  // Loaded from Supabase (brand.send_mode column). Three states: paused / test / live.
  // Worker honors this — paused brands skip entirely, test brands enqueue but don't call Gmail.
  const[sendMode,setSendMode]=useState(null);
  const[sendModeBusy,setSendModeBusy]=useState(false);
  // Owner of THIS brand? Send mode / delete / ownership-transfer are owner-only
  // (enforced by the brands_guard_sensitive DB trigger). Used to lock the toggle
  // for account_managers so they don't click into a hard error.
  const[isBrandOwner,setIsBrandOwner]=useState(false);
  useEffect(()=>{
    if(!brandId){setIsBrandOwner(false);return;}
    let cancelled=false;
    (async()=>{
      const{data}=await supabase.rpc('user_is_brand_owner',{p_brand_id:brandId});
      if(!cancelled)setIsBrandOwner(!!data);
    })();
    return()=>{cancelled=true;};
  },[brandId]);
  useEffect(()=>{
    if(!brandId)return;
    let cancelled=false;
    (async()=>{
      const{data}=await supabase.from('brands').select('send_mode').eq('id',brandId).maybeSingle();
      if(!cancelled)setSendMode(data?.send_mode||'paused');
    })();
    return()=>{cancelled=true;};
  },[brandId]);
  const changeSendMode=async(next)=>{
    if(!brandId||sendMode===next)return;
    if(next==='live' && !(await confirmDialog({
      title:"Switch to LIVE send mode?",
      body:<>Emails will actually send via Gmail from this point on. Make sure <strong>all your flows are reviewed</strong> first — there's no undo on emails already in flight.</>,
      confirmLabel:"Go live",
    }))) return;
    setSendModeBusy(true);
    const{error}=await supabase.from('brands').update({send_mode:next}).eq('id',brandId);
    setSendModeBusy(false);
    if(error){toast(`Couldn't change mode: ${error.message}`,'error');return;}
    setSendMode(next);
    toast(`Send mode: ${next}`,next==='live'?'success':'info');
  };

  // ---------- Webhook health (failure log) ----------
  // Polls every 60s for unacknowledged webhook failures across all brands the user owns
  // IG tags-poll health: last run timestamp + summary (errors / archived / scanned).
  // Worker writes brands.last_tags_poll_at + last_tags_poll_summary on each tick.
  const[igPollStatus,setIgPollStatus]=useState(null);
  useEffect(()=>{
    if(!brandId)return;
    let cancelled=false;
    const load=async()=>{
      const{data}=await supabase.from('brands')
        .select('last_tags_poll_at,last_tags_poll_summary,instagram_business_account_id,instagram_username')
        .eq('id',brandId).maybeSingle();
      if(!cancelled)setIgPollStatus(data||null);
    };
    load();
    const t=setInterval(load,30000);
    return()=>{cancelled=true;clearInterval(t);};
  },[brandId]);

  // (RLS scoped). Surfaces a red dot on the Health tab + lets operator review + ack.
  const[healthFailures,setHealthFailures]=useState([]);
  const healthAlertCount=healthFailures.filter(f=>!f.acknowledged_at).length;
  const loadHealthFailures=async()=>{
    const{data}=await supabase.from('webhook_failures')
      .select('id, brand_id, source, error_code, error_message, payload_summary, http_status, created_at, acknowledged_at')
      .order('created_at',{ascending:false})
      .limit(100);
    setHealthFailures(data||[]);
  };
  useEffect(()=>{
    loadHealthFailures();
    const t=setInterval(loadHealthFailures,60000);
    return()=>clearInterval(t);
  // eslint-disable-next-line
  },[]);
  const acknowledgeFailure=async(id)=>{
    const{error}=await supabase.from('webhook_failures').update({acknowledged_at:new Date().toISOString()}).eq('id',id);
    if(error){toast(`Ack failed: ${error.message}`,'error');return;}
    setHealthFailures(prev=>prev.map(f=>f.id===id?{...f,acknowledged_at:new Date().toISOString()}:f));
  };
  const acknowledgeAllFailures=async()=>{
    const ids=healthFailures.filter(f=>!f.acknowledged_at).map(f=>f.id);
    if(ids.length===0)return;
    const{error}=await supabase.from('webhook_failures').update({acknowledged_at:new Date().toISOString()}).in('id',ids);
    if(error){toast(`Ack failed: ${error.message}`,'error');return;}
    loadHealthFailures();
    toast(`✓ Acknowledged ${ids.length}`);
  };

  // ---------- Agency Drive (singleton Google connection across all brands) ----------
  const[agencyDrive,setAgencyDrive]=useState(null); // null = unknown / unloaded; row or {} = state
  const[agencyDriveBusy,setAgencyDriveBusy]=useState(false);
  const[agencyDriveTestResult,setAgencyDriveTestResult]=useState(null);
  const loadAgencyDrive=async()=>{
    const{data}=await supabase.rpc('agency_drive_status').select('*').maybeSingle();
    setAgencyDrive(data||{});
  };
  useEffect(()=>{loadAgencyDrive();/* eslint-disable-line react-hooks/exhaustive-deps */},[]);
  const connectAgencyDrive=async()=>{
    setAgencyDriveBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-oauth/init`,{
        method:'POST',headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},body:'{}',
      });
      const body=await resp.json();
      if(!resp.ok||!body.url){toast(`Connect failed: ${body.error||resp.status}`,'error');setAgencyDriveBusy(false);return;}
      window.location.href=body.url;
    }catch(e){console.error(e);toast('Connect failed','error');setAgencyDriveBusy(false);}
  };
  const disconnectAgencyDrive=async()=>{
    if(!(await confirmDialog({title:'Disconnect agency Google Drive?',body:'Folder structures stay in Drive but the app loses write access.',destructive:true,confirmLabel:'Disconnect'})))return;
    setAgencyDriveBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-oauth/disconnect`,{
        method:'POST',headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},body:'{}',
      });
      if(!resp.ok){toast(`Disconnect failed: ${resp.status}`,'error');return;}
      setAgencyDrive({});setAgencyDriveTestResult(null);
      toast('Agency Drive disconnected');
    }catch(e){console.error(e);toast('Disconnect failed','error');}
    finally{setAgencyDriveBusy(false);}
  };
  const testAgencyDrive=async()=>{
    setAgencyDriveBusy(true);setAgencyDriveTestResult(null);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-agency-oauth/test`,{
        method:'POST',headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},body:'{}',
      });
      const body=await resp.json();
      if(!resp.ok||!body.ok){toast(`Test failed: ${body.error||body.detail||resp.status}`,'error');setAgencyDriveTestResult({ok:false,error:body.error||body.detail||`HTTP ${resp.status}`});return;}
      setAgencyDriveTestResult({ok:true,folder:body.folder});
      toast(`✓ Test folder created: ${body.folder.name}`);
    }catch(e){console.error(e);toast('Test failed','error');}
    finally{setAgencyDriveBusy(false);}
  };

  // ---------- Inboxes (multi-Gmail per brand) ----------
  const[inboxes,setInboxes]=useState([]);
  const[inboxUsage,setInboxUsage]=useState({}); // { credId: sentToday }
  const[inboxBusy,setInboxBusy]=useState(false);

  const loadInboxes=async()=>{
    if(!brandId)return;
    const{data,error}=await supabase.rpc('gmail_connection_status').select('*').eq('brand_id',brandId).order('connected_at');
    if(error){console.error('inboxes load',error);return;}
    const list=data||[];
    setInboxes(list);
    // Pull today's usage for each inbox in parallel
    const startOfDay=new Date(); startOfDay.setUTCHours(0,0,0,0);
    const usage={};
    await Promise.all(list.map(async i=>{
      const{count}=await supabase.from('email_queue')
        .select('*',{count:'exact',head:true})
        .eq('gmail_credential_id',i.id).eq('status','sent').gte('sent_at',startOfDay.toISOString());
      usage[i.id]=count||0;
    }));
    setInboxUsage(usage);
  };
  useEffect(()=>{loadInboxes();/* eslint-disable-line react-hooks/exhaustive-deps */},[brandId]);

  // emailHint: re-auth an EXISTING inbox in place (Google preselects that account;
  // the oauth callback upserts on (brand_id, email_address) so cap/warmup/primary
  // survive). Null = fresh connect with the account picker.
  const startGmailOauth=async(emailHint=null)=>{
    if(!brandId)return;
    setInboxBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/gmail-oauth/init`,{
        method:'POST',
        headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},
        body:JSON.stringify(emailHint?{brand_id:brandId,email_hint:emailHint}:{brand_id:brandId}),
      });
      const body=await resp.json();
      if(!resp.ok||!body.url){toast(`Connect failed: ${body.error||resp.status}`,'error');setInboxBusy(false);return;}
      window.location.href=body.url;
    }catch(e){console.error(e);toast('Connect failed — see console','error');setInboxBusy(false);}
  };
  const addInbox=()=>startGmailOauth();
  const reconnectInbox=(inbox)=>startGmailOauth(inbox.email_address);

  const disconnectInbox=async(email)=>{
    if(!brandId)return;
    if(!(await confirmDialog({title:`Disconnect ${email}?`,body:'You can reconnect this inbox anytime.',destructive:true,confirmLabel:'Disconnect'})))return;
    setInboxBusy(true);
    try{
      const token=await window.Clerk?.session?.getToken();
      const resp=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/gmail-oauth/disconnect`,{
        method:'POST',
        headers:{Authorization:`Bearer ${token}`,'content-type':'application/json'},
        body:JSON.stringify({email_address:email,brand_id:brandId}),
      });
      const body=await resp.json();
      if(!resp.ok||!body.ok){toast(`Disconnect failed: ${body.error||resp.status}`,'error');setInboxBusy(false);return;}
      const removed=inboxes.find(i=>i.email_address===email);
      setInboxes(prev=>prev.filter(i=>i.email_address!==email));
      logSecurityEvent(brandId,'gmail_inbox.disconnect','gmail_inbox',removed?.id||null,{email});
      toast(`${email} disconnected`);
    }catch(e){console.error(e);toast('Disconnect failed','error');}
    finally{setInboxBusy(false);}
  };

  const setPrimaryInbox=async(inbox)=>{
    if(!brandId||inbox.is_primary)return;
    if(!(await confirmDialog({title:`Make ${inbox.email_address} the primary inbox?`,body:'New outreach will be sent from this inbox first. Other inboxes act as overflow when this one hits its daily cap.',confirmLabel:'Set as primary'})))return;
    setInboxBusy(true);
    const{error}=await supabase.rpc('set_primary_gmail_inbox',{p_brand_id:brandId,p_credential_id:inbox.id});
    setInboxBusy(false);
    if(error){toast(`Couldn't set primary: ${error.message}`,'error');return;}
    // Optimistic local update + reload to confirm
    setInboxes(prev=>prev.map(i=>({...i,is_primary:i.id===inbox.id})));
    logSecurityEvent(brandId,'gmail_inbox.set_primary','gmail_inbox',inbox.id,{email:inbox.email_address});
    toast(`✓ ${inbox.email_address} is now the primary inbox`);
    loadInboxes();
  };

  const saveInboxCap=async(inbox,newCap)=>{
    const cap=parseInt(newCap,10);
    if(!brandId||!cap||cap<1||cap>2000){toast('Daily cap must be 1–2000','error');return;}
    if(cap===inbox.daily_cap)return; // no change
    setInboxBusy(true);
    const{error}=await supabase.rpc('update_gmail_inbox_cap',{p_brand_id:brandId,p_credential_id:inbox.id,p_cap:cap});
    setInboxBusy(false);
    if(error){toast(`Couldn't save cap: ${error.message}`,'error');return;}
    setInboxes(prev=>prev.map(i=>i.id===inbox.id?{...i,daily_cap:cap}:i));
    logSecurityEvent(brandId,'gmail_inbox.cap_change','gmail_inbox',inbox.id,{email:inbox.email_address,from:inbox.daily_cap,to:cap});
    toast(`✓ Daily cap set to ${cap}`);
  };

  // Toggle warmup on/off for an inbox. Skipping warmup is irreversible-ish
  // (re-enabling resets the clock to today, not the original connection date),
  // so confirm before disabling. Mirrors the worker's warmupRampCap schedule.
  const toggleInboxWarmup=async(inbox)=>{
    const currentlyEnabled = !!inbox.warmup_started_at;
    if(currentlyEnabled){
      if(!(await confirmDialog({title:`Skip warmup for ${inbox.email_address}?`,body:`This inbox will jump straight to its full ${inbox.daily_cap||100}/day cap.\n\nOnly skip if this account is ALREADY sending cold outreach at volume from another tool (e.g. migrating from Influencer Hero / Mailshake / Lemlist).\n\nExisting Gmail accounts with normal use — even multi-year-old ones — should still warm up. Gmail tracks sending patterns, not just account age. Going from a few personal emails/day to 100+ cold outreaches/day is the spike that gets flagged.`,destructive:true,confirmLabel:'Skip warmup'}))) return;
    }
    setInboxBusy(true);
    const{error}=await supabase.rpc('set_gmail_inbox_warmup',{p_brand_id:brandId,p_credential_id:inbox.id,p_enabled:!currentlyEnabled});
    setInboxBusy(false);
    if(error){toast(`Couldn't update warmup: ${error.message}`,'error');return;}
    setInboxes(prev=>prev.map(i=>i.id===inbox.id?{...i,warmup_started_at:currentlyEnabled?null:new Date().toISOString()}:i));
    toast(currentlyEnabled?'✓ Warmup skipped — inbox at full cap':'✓ Warmup restarted (day 1)');
  };

  const logo=brand?.logo||null;
  // Logo persistence. Previously state-only (lost on refresh / didn't sync across
  // devices). Now resizes client-side (max 512px) + writes to brand.settings.logo
  // via persistBrandField, which hits both localStorage AND Supabase.
  const setLogo=async(dataUrl)=>{
    // Empty / null means "clear logo"
    if(!dataUrl){
      await persistBrandField('logo', null);
      return;
    }
    await persistBrandField('logo', dataUrl);
  };
  // Resize an uploaded image file to a sane dimension before storing as data URL.
  // Logos rarely need to be larger than 512px and the JSONB storage cost matters
  // when this row gets fetched on every settings load.
  const resizeLogoFile=(file,maxDim=512,quality=0.9)=>new Promise((resolve,reject)=>{
    const reader=new FileReader();
    reader.onload=(e)=>{
      const img=new Image();
      img.onload=()=>{
        let{width,height}=img;
        if(width>maxDim||height>maxDim){
          const ratio=maxDim/Math.max(width,height);
          width=Math.round(width*ratio); height=Math.round(height*ratio);
        }
        const canvas=document.createElement('canvas');
        canvas.width=width; canvas.height=height;
        const ctx=canvas.getContext('2d');
        ctx.drawImage(img,0,0,width,height);
        // Keep PNG when source is PNG (preserves transparency for logos with
        // transparent backgrounds). Otherwise JPEG for smaller file size.
        const isPng=file.type==='image/png';
        const out=canvas.toDataURL(isPng?'image/png':'image/jpeg',quality);
        resolve(out);
      };
      img.onerror=()=>reject(new Error('Could not load image'));
      img.src=e.target.result;
    };
    reader.onerror=()=>reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
  const handleLogoUpload=async(file)=>{
    if(!file)return;
    if(!file.type.startsWith('image/')){toast('Pick an image file','error');return;}
    if(file.size>10*1024*1024){toast('File too big (>10MB) — try a smaller image','error');return;}
    try{
      const dataUrl=await resizeLogoFile(file,512,0.9);
      await setLogo(dataUrl);
      toast('✓ Logo updated');
    }catch(e){
      toast(`Logo upload failed: ${e.message}`,'error');
    }
  };
  const[deleteStep,setDeleteStep]=useState(0);
  const[deleteInput,setDeleteInput]=useState("");
  const[emailDraft,setEmailDraft]=useState(null);
  const[emailSaved,setEmailSaved]=useState(false);
  const[editVals,setEditVals]=useState({});

  // Load this brand's fields from DB on brandId change, merge into the localStorage UI cache.
  // Source of truth: brands.settings JSONB + brands.name. UI keeps using brandData[activeBrand]
  // for instant reads, but DB drives the actual values across devices.
  useEffect(()=>{
    if(!brandId||!activeBrand)return;
    let cancelled=false;
    (async()=>{
      const{data}=await supabase.from('brands').select('name, settings').eq('id',brandId).maybeSingle();
      if(cancelled||!data)return;
      const s=data.settings||{};
      // Fields persisted to brands.settings (everything except things owned by their own column:
      // name → brands.name, send_mode → brands.send_mode, snowball_zap_url → brands.snowball_zap_url)
      setBrandData(prev=>({...prev,[activeBrand]:{
        ...(prev[activeBrand]||{}),
        name:data.name,
        domain:s.domain??prev[activeBrand]?.domain??"",
        market:s.market??prev[activeBrand]?.market??"",
        instagram:s.instagram??prev[activeBrand]?.instagram??"",
        youtube:s.youtube??prev[activeBrand]?.youtube??"",
        tiktok:s.tiktok??prev[activeBrand]?.tiktok??"",
        senderName:s.senderName??prev[activeBrand]?.senderName??"",
        fromEmail:s.fromEmail??prev[activeBrand]?.fromEmail??"",
        commissionRate:s.commissionRate??prev[activeBrand]?.commissionRate??"15%",
        discountPct:s.discountPct??prev[activeBrand]?.discountPct??"15%",
        codeFormat:s.codeFormat??prev[activeBrand]?.codeFormat??"FIRSTNAME + 15",
        signature:s.signature??prev[activeBrand]?.signature??"",
        timezone:s.timezone??prev[activeBrand]?.timezone??"",
        currency:s.currency??prev[activeBrand]?.currency??"",
        logo:s.logo??prev[activeBrand]?.logo??null,
        email:s.email_timing??prev[activeBrand]?.email??{orderConfirm:0,arriving:7,brief:12,fu1:17,fu2:22,fu3:27,oFu1:3,oFu2:8,oFu3:22},
      }}));
    })();
    return()=>{cancelled=true;};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[brandId]);

  // Save a single brand field. `name` writes to brands.name column (and refreshes the sidebar
  // switcher); everything else merges into brands.settings JSONB at the top level.
  // Falls back to local-only update if brandId isn't loaded yet, so optimistic edits never lose.
  const persistBrandField=async(key,val)=>{
    setBrandData(prev=>({...prev,[activeBrand]:{...prev[activeBrand],[key]:val}}));
    if(!brandId)return;
    if(key==='name'){
      const{error}=await supabase.from('brands').update({name:val}).eq('id',brandId);
      if(error){toast(`Save failed: ${error.message}`,'error');return;}
      if(reloadBrands)await reloadBrands();
      // Also rename the localStorage key so brandData[newName] takes over from brandData[oldName].
      if(val!==activeBrand){
        setBrandData(prev=>{const cur=prev[activeBrand];const{[activeBrand]:_,...rest}=prev;return{...rest,[val]:{...cur,name:val}};});
        setActiveBrand(val);
      }
      toast('✓ Brand name updated');
      return;
    }
    // Merge into settings JSONB. Fetch latest first to avoid clobbering other settings.
    const{data:row}=await supabase.from('brands').select('settings').eq('id',brandId).maybeSingle();
    const nextSettings={...(row?.settings||{}), [key]:val};
    const{error}=await supabase.from('brands').update({settings:nextSettings}).eq('id',brandId);
    if(error){toast(`Save failed: ${error.message}`,'error');return;}
    toast('✓ Saved');
  };

  const saveEmailChanges=async()=>{
    if(!emailDraft)return;
    setBrandData(prev=>({...prev,[activeBrand]:{...prev[activeBrand],email:emailDraft}}));
    if(brandId){
      const{data:row}=await supabase.from('brands').select('settings').eq('id',brandId).maybeSingle();
      const nextSettings={...(row?.settings||{}), email_timing:emailDraft};
      const{error}=await supabase.from('brands').update({settings:nextSettings}).eq('id',brandId);
      if(error){toast(`Save failed: ${error.message}`,'error');return;}
    }
    setEmailDraft(null);setEmailSaved(true);setTimeout(()=>setEmailSaved(false),3000);
    toast('✓ Email timing saved');
  };
  const update=persistBrandField;

  if(!brand)return null;
  const emailValues=emailDraft||brand.email;
  const hasEmailChanges=emailDraft!==null&&JSON.stringify(emailDraft)!==JSON.stringify(brand.email);
  const updateEmailDraft=(key,val)=>{setEmailDraft(prev=>({...(prev||brand.email),[key]:parseInt(val)||0}));setEmailSaved(false);};
  // Soft-delete brand — sets deleted_at + a recovery token (7-day window) instead of
  // immediate hard delete. Fires an email to the owner with a Restore link so accidental
  // deletes can be undone. Hard cascade-delete only happens via future cleanup cron.
  const deleteBrand=async()=>{
    if(!brandId){toast('No brand to delete','error');setDeleteStep(0);return;}
    const token=`${Math.random().toString(36).slice(2,10)}${Date.now().toString(36)}`;
    const{error}=await supabase.from('brands').update({
      deleted_at:new Date().toISOString(),
      deletion_token:token,
    }).eq('id',brandId);
    if(error){toast(`Delete failed: ${error.message}`,'error');setDeleteStep(0);return;}
    logSecurityEvent(brandId,'brand.delete','brand',brandId,{brand_name:activeBrand,recovery_window_days:7});
    // Send recovery-link email (best-effort). Uses the brand's primary Gmail to send to
    // the brand owner's email.
    try{
      const t=await window.Clerk?.session?.getToken();
      await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-brand-deletion-notice`,{
        method:'POST',headers:{Authorization:`Bearer ${t}`,'content-type':'application/json'},
        body:JSON.stringify({brand_id:brandId, deletion_token:token, app_url:window.location.origin}),
      });
    }catch(e){console.warn('deletion notice send failed (continuing)',e);}
    const{[activeBrand]:_,...rest}=brandData;
    setBrandData(rest);
    setDeleteStep(0);setDeleteInput("");
    toast(`Brand "${activeBrand}" scheduled for deletion · recovery email sent (7-day window)`,'error');
    setTimeout(()=>{ try { localStorage.removeItem('ios_activeBrandId'); } catch{} window.location.reload(); }, 800);
  };
  const FieldRow=({label,value,fieldKey,placeholder})=>{
    const isEditing=editing[fieldKey];
    return(<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 0",borderBottom:"1px solid #f8faf9"}}>
      <div style={{fontSize:13,color:"#9ab8a6",fontWeight:500,minWidth:160}}>{label}</div>
      {isEditing
        ?<div style={{display:"flex",gap:6,flex:1,justifyContent:"flex-end"}}>
            <input autoFocus value={editVals[fieldKey]??value??""} onChange={e=>setEditVals(p=>({...p,[fieldKey]:e.target.value}))} onKeyDown={e=>{if(e.key==="Enter"){update(fieldKey,editVals[fieldKey]??value);setEditing(p=>({...p,[fieldKey]:false}));}if(e.key==="Escape")setEditing(p=>({...p,[fieldKey]:false}));}} placeholder={placeholder} style={{padding:"5px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",width:220}}/>
            <button onClick={()=>{update(fieldKey,editVals[fieldKey]??value);setEditing(p=>({...p,[fieldKey]:false}));}} style={{background:DARK,color:G,border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Save</button>
            <button onClick={()=>setEditing(p=>({...p,[fieldKey]:false}))} style={{background:"#f8faf9",border:`1px solid ${BORDER}`,borderRadius:7,padding:"5px 10px",fontSize:12,cursor:"pointer"}}>✕</button>
          </div>
        :<div style={{display:"flex",gap:8,alignItems:"center"}}>
            <div style={{fontSize:13,fontWeight:700,color:DARK}}>{value||<span style={{color:"#c0d4c8"}}>Not set</span>}</div>
            <button onClick={()=>{setEditVals(p=>({...p,[fieldKey]:value}));setEditing(p=>({...p,[fieldKey]:true}));}} style={{border:"none",background:"none",cursor:"pointer",fontSize:11,color:"#9ab8a6",padding:"2px 6px",borderRadius:5}} onMouseEnter={e=>e.currentTarget.style.background="#f8faf9"} onMouseLeave={e=>e.currentTarget.style.background="none"}>Edit</button>
          </div>
      }
    </div>);
  };

  // Social link field — same edit/save UX as FieldRow but the displayed value
  // doubles as a clickable link to the right platform's profile page.
  const SocialLinkField=({label,value,fieldKey,platform,placeholder})=>{
    const isEditing=editing[fieldKey];
    const handle=(value||"").replace(/^@/,"").trim();
    const urlBase={instagram:"https://instagram.com/",youtube:"https://youtube.com/@",tiktok:"https://tiktok.com/@"}[platform];
    const url=handle?`${urlBase}${handle}`:null;
    return(<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 0",borderBottom:"1px solid #f8faf9"}}>
      <div style={{fontSize:13,color:"#9ab8a6",fontWeight:500,minWidth:160}}>{label}</div>
      {isEditing
        ?<div style={{display:"flex",gap:6,flex:1,justifyContent:"flex-end"}}>
            <input autoFocus value={editVals[fieldKey]??value??""} onChange={e=>setEditVals(p=>({...p,[fieldKey]:e.target.value}))} onKeyDown={e=>{if(e.key==="Enter"){update(fieldKey,editVals[fieldKey]??value);setEditing(p=>({...p,[fieldKey]:false}));}if(e.key==="Escape")setEditing(p=>({...p,[fieldKey]:false}));}} placeholder={placeholder} style={{padding:"5px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",width:220}}/>
            <button onClick={()=>{update(fieldKey,editVals[fieldKey]??value);setEditing(p=>({...p,[fieldKey]:false}));}} style={{background:DARK,color:G,border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Save</button>
            <button onClick={()=>setEditing(p=>({...p,[fieldKey]:false}))} style={{background:"#f8faf9",border:`1px solid ${BORDER}`,borderRadius:7,padding:"5px 10px",fontSize:12,cursor:"pointer"}}>✕</button>
          </div>
        :<div style={{display:"flex",gap:8,alignItems:"center"}}>
            {handle
              ?<a href={url} target="_blank" rel="noreferrer" style={{fontSize:13,fontWeight:700,color:G,textDecoration:"none"}}>@{handle} ↗</a>
              :<span style={{fontSize:13,color:"#c0d4c8"}}>Not set</span>
            }
            <button onClick={()=>{setEditVals(p=>({...p,[fieldKey]:value}));setEditing(p=>({...p,[fieldKey]:true}));}} style={{border:"none",background:"none",cursor:"pointer",fontSize:11,color:"#9ab8a6",padding:"2px 6px",borderRadius:5}} onMouseEnter={e=>e.currentTarget.style.background="#f8faf9"} onMouseLeave={e=>e.currentTarget.style.background="none"}>Edit</button>
          </div>
      }
    </div>);
  };

  // Visual styles for the send-mode toggle.
  const modeMeta={
    paused:{label:"Paused",color:"#9CA3AF",bg:"#F3F4F6",border:"#E5E7EB",desc:"Nothing fires. Worker skips this brand entirely."},
    test:  {label:"Test",  color:"#F59E0B",bg:"#FFFBEB",border:"#FDE68A",desc:"Worker queues + 'sends' but never calls Gmail. Rows show TEST badge."},
    live:  {label:"Live",  color:"#10B981",bg:"#ECFDF5",border:"#A7F3D0",desc:"Real emails go out via your connected Gmail."},
  };
  const currentModeMeta=modeMeta[sendMode]||modeMeta.paused;

  return(<div style={{display:"flex",flexDirection:"column",gap:20,maxWidth:660}}>
    {/* Send mode — top-of-page safety switch, prominent because it governs whether real emails go out. */}
    {sendMode!==null&&(
      <Card style={{padding:18,border:`1.5px solid ${currentModeMeta.border}`,background:currentModeMeta.bg}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
              <span style={{fontWeight:800,color:DARK,fontSize:14}}>Send mode</span>
              <span style={{background:currentModeMeta.color,color:"#fff",fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:4,letterSpacing:".05em"}}>{currentModeMeta.label.toUpperCase()}</span>
            </div>
            <div style={{fontSize:12,color:"#4B5563",lineHeight:1.5}}>{currentModeMeta.desc}</div>
          </div>
          <div style={{display:"flex",gap:0,border:`1px solid ${BORDER}`,borderRadius:9,overflow:"hidden",background:"#fff",flexShrink:0}}>
            {["paused","test","live"].map((m,i)=>(<button key={m} disabled={sendModeBusy} onClick={()=>changeSendMode(m)} style={{background:sendMode===m?modeMeta[m].color:"#fff",color:sendMode===m?"#fff":DARK,border:"none",borderLeft:i>0?`1px solid ${BORDER}`:"none",padding:"7px 14px",fontSize:12,fontWeight:700,cursor:sendModeBusy?"wait":"pointer",textTransform:"capitalize"}}>{m}</button>))}
          </div>
        </div>
      </Card>
    )}
    {deleteStep===1&&(<Modal title="Delete brand" subtitle={`Permanently delete "${activeBrand}"?`} onClose={()=>setDeleteStep(0)} width={420}><div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:10,padding:14,marginBottom:20,fontSize:13,color:"#991B1B",lineHeight:1.6}}>⚠ This cannot be undone.</div><div style={{display:"flex",justifyContent:"flex-end",gap:8}}><button onClick={()=>setDeleteStep(0)} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button><button onClick={()=>setDeleteStep(2)} style={{background:"#DC2626",color:"#fff",border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Yes, delete</button></div></Modal>)}
    {deleteStep===2&&(<Modal title="Confirm deletion" onClose={()=>setDeleteStep(0)} width={420}><div style={{marginBottom:8,fontSize:13,color:"#9ab8a6"}}>Type <strong style={{color:DARK}}>{activeBrand}</strong> to confirm:</div><input value={deleteInput} onChange={e=>setDeleteInput(e.target.value)} placeholder={activeBrand} style={{width:"100%",padding:"10px 12px",border:`1px solid ${deleteInput===activeBrand?"#DC2626":BORDER}`,borderRadius:8,fontSize:13,outline:"none",marginBottom:20,boxSizing:"border-box"}}/><div style={{display:"flex",justifyContent:"flex-end",gap:8}}><button onClick={()=>{setDeleteStep(0);setDeleteInput("");}} style={{border:`1px solid ${BORDER}`,background:"#fff",borderRadius:9,padding:"9px 20px",fontSize:13,cursor:"pointer"}}>Cancel</button><button onClick={deleteBrand} disabled={deleteInput!==activeBrand} style={{background:deleteInput===activeBrand?"#DC2626":"#f0f0f0",color:deleteInput===activeBrand?"#fff":"#9ab8a6",border:"none",borderRadius:9,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:deleteInput===activeBrand?"pointer":"not-allowed"}}>Delete forever</button></div></Modal>)}
    <div style={{display:"flex",gap:2,background:"#fff",borderRadius:10,padding:4,boxShadow:CARD,alignSelf:"flex-start"}}>{["general","brand","email","security","health","team"].filter(t=>t!=='security'||isAgencyOwner).map(t=>(<button key={t} onClick={()=>setTab(t)} style={{background:tab===t?DARK:"transparent",color:tab===t?G:"#9ab8a6",border:"none",borderRadius:7,padding:"7px 18px",fontSize:12,cursor:"pointer",fontWeight:tab===t?700:400,textTransform:"capitalize",position:"relative"}}>
      {t}
      {t==="health"&&healthAlertCount>0&&<span style={{position:"absolute",top:2,right:4,minWidth:14,height:14,borderRadius:7,background:"#EF4444",color:"#fff",fontSize:9,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 4px"}}>{healthAlertCount>99?"99+":healthAlertCount}</span>}
    </button>))}</div>
    {tab==="general"&&(<Card style={{padding:24}}><div style={{fontWeight:700,color:DARK,fontSize:15,marginBottom:18}}>General settings</div>
      <DropdownField label="Timezone" value={brand.timezone} fieldKey="timezone" options={TIMEZONE_OPTIONS} onSave={update}/>
      <DropdownField label="Currency" value={brand.currency} fieldKey="currency" options={CURRENCY_OPTIONS} onSave={update}/>
    </Card>)}
    {tab==="brand"&&(<><Card style={{padding:24}}>
      <div style={{fontWeight:700,color:DARK,fontSize:15,marginBottom:18}}>Brand identity</div>
      <div style={{display:"flex",gap:18,alignItems:"center",padding:"16px 0",borderBottom:"1px solid #f0f7f3",marginBottom:4}}>
        <div style={{width:108,height:108,borderRadius:18,background:logo?"transparent":"#f8faf9",border:`1px solid ${BORDER}`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}>
          {logo?<img src={logo} alt="logo" style={{width:"100%",height:"100%",objectFit:"contain"}}/>:<span style={{fontSize:42}}>🏊</span>}
        </div>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,color:DARK,fontSize:15,marginBottom:4}}>Brand logo</div>
          <div style={{fontSize:11,color:MUTED,marginBottom:10}}>Square or wide. Auto-resized to 512px on upload. Used on sign-in pages, agreement PDFs, and emails.</div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <label style={{background:DARK,color:G,border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer",fontWeight:700}}>
              {logo?"Replace logo":"Upload logo"}
              <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{handleLogoUpload(e.target.files[0]);e.target.value="";}}/>
            </label>
            {logo&&<button onClick={async()=>{if(await confirmDialog({title:"Remove brand logo?",body:"The logo will be cleared from sign-in pages, agreement PDFs, and emails until you upload a new one.",destructive:true,confirmLabel:"Remove"}))setLogo(null);}} style={{background:"#fff",color:"#DC2626",border:"1px solid #FECACA",borderRadius:8,padding:"7px 12px",fontSize:12,cursor:"pointer",fontWeight:600}}>Remove</button>}
          </div>
        </div>
      </div>
      <FieldRow label="Brand name" value={brand.name} fieldKey="name" placeholder="Brand name"/>
      <FieldRow label="Store domain" value={brand.domain} fieldKey="domain" placeholder="shop.example.com"/>
      <SocialLinkField label="Instagram" value={brand.instagram} fieldKey="instagram" platform="instagram" placeholder="@handle"/>
      <SocialLinkField label="YouTube" value={brand.youtube} fieldKey="youtube" platform="youtube" placeholder="@handle"/>
      <SocialLinkField label="TikTok" value={brand.tiktok} fieldKey="tiktok" platform="tiktok" placeholder="@handle"/>
    </Card>
    {isBrandOwner&&<Card style={{padding:24,border:"1px solid #FECACA"}}><div style={{fontWeight:700,color:"#DC2626",fontSize:14,marginBottom:6}}>Danger zone</div><div style={{fontSize:13,color:"#9ab8a6",marginBottom:16}}>Permanently delete this brand and all its data.</div><button onClick={()=>Object.keys(brandData).length>1?setDeleteStep(1):null} disabled={Object.keys(brandData).length<=1} style={{background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",borderRadius:8,padding:"9px 18px",fontWeight:700,fontSize:13,cursor:Object.keys(brandData).length>1?"pointer":"not-allowed",opacity:Object.keys(brandData).length<=1?0.5:1}}>Delete brand</button>{Object.keys(brandData).length<=1&&<div style={{fontSize:11,color:"#9ab8a6",marginTop:8}}>You must have at least one brand.</div>}</Card>}</>)}
    {tab==="email"&&(<div style={{display:"flex",flexDirection:"column",gap:12}}>
      {/* Brand-level daily send cap — total across all this brand's inboxes per day.
          When hit, worker pauses + writes a brand_notification surfaced on the Dashboard. */}
      <BrandSendCapCard brandId={brandId}/>

      {/* Sending inboxes — connected Gmail accounts that the worker rotates between. */}
      <Card style={{padding:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:12}}>
          <div>
            <div style={{fontWeight:700,color:DARK,fontSize:15}}>Sending inboxes</div>
            <div style={{fontSize:13,color:"#9ab8a6",marginTop:3,maxWidth:480,lineHeight:1.5}}>Gmail accounts used to send outreach. The worker rotates between them by remaining daily capacity. Add more to scale past ~100/day per account.</div>
          </div>
          <button onClick={addInbox} disabled={inboxBusy} style={{background:DARK,color:G,border:"none",borderRadius:9,padding:"9px 16px",fontWeight:700,fontSize:13,cursor:inboxBusy?"wait":"pointer",flexShrink:0,opacity:inboxBusy?0.6:1,whiteSpace:"nowrap"}}>{inboxBusy?"Opening Google…":"+ Add inbox"}</button>
        </div>

        {inboxes.length===0
          ?<div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,padding:"14px 16px",fontSize:13,color:"#92400E"}}>
              ⚠ No Gmail connected for this brand. Click <strong>Add inbox</strong> to connect one — required before any email can go out.
            </div>
          :<div style={{display:"flex",flexDirection:"column",gap:8}}>
              {/* Primary first, then by connection time. */}
              {[...inboxes].sort((a,b)=>(b.is_primary?1:0)-(a.is_primary?1:0)).map((inbox)=>{
                const used=inboxUsage[inbox.id]||0;
                const rawCap=inbox.daily_cap||100;
                // Warmup ramp (must match worker's warmupRampCap exactly):
                // day 0-2 → 25, 3-5 → 50, 6-9 → 100, 10-13 → 150, 14+ → full
                const warmupStart=inbox.warmup_started_at?new Date(inbox.warmup_started_at):null;
                const warmupDay=warmupStart?Math.floor((Date.now()-warmupStart.getTime())/(24*60*60*1000)):null;
                const rampCap=warmupDay==null?null:warmupDay<3?25:warmupDay<6?50:warmupDay<10?100:warmupDay<14?150:null;
                const cap=rampCap==null?rawCap:Math.min(rawCap,rampCap);
                const warmingUp=rampCap!=null && rampCap<rawCap;
                // Next ramp step (informational, only when warming)
                const nextBumpDay=warmupDay==null?null:warmupDay<3?3:warmupDay<6?6:warmupDay<10?10:warmupDay<14?14:null;
                const nextBumpCap=nextBumpDay==null?null:nextBumpDay===3?50:nextBumpDay===6?100:nextBumpDay===10?150:rawCap;
                const daysToBump=nextBumpDay==null?null:nextBumpDay-warmupDay;
                const pct=Math.min(100,Math.round((used/cap)*100));
                const barColor=pct>=100?"#EF4444":pct>=80?"#F59E0B":G;
                // Watch status — Gmail's users.watch() expires every 7 days; daily cron auto-renews.
                // If watch_expires_at is missing OR in the past → reply detection is broken until renewal cycles.
                const watchExp=inbox.watch_expires_at?new Date(inbox.watch_expires_at):null;
                const hrsUntilWatchExpiry=watchExp?(watchExp-Date.now())/3.6e6:null;
                const watchStatus=!watchExp?"never_started":hrsUntilWatchExpiry<=0?"expired":hrsUntilWatchExpiry<24?"expiring":"active";
                const watchColors={active:G,expiring:"#F59E0B",expired:"#EF4444",never_started:MUTED};
                const watchLabels={active:"Watch ✓",expiring:"Watch expiring",expired:"Watch expired",never_started:"Watch not started"};
                return(<div key={inbox.id} style={{display:"flex",alignItems:"center",gap:14,padding:"13px 14px",border:`1px solid ${inbox.is_primary?G+"60":BORDER}`,borderRadius:10,background:inbox.is_primary?G+"08":"#fafcfb"}}>
                  <div style={{width:30,height:30,borderRadius:8,background:inbox.is_primary?G+"18":"#f0f4f2",border:`1px solid ${inbox.is_primary?G+"40":BORDER}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{inbox.is_primary?<span style={{color:G,fontSize:16}}>★</span>:<span style={{color:MUTED,fontSize:12,fontWeight:700}}>↻</span>}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap"}}>
                      <span style={{fontWeight:600,color:DARK,fontSize:13}}>{inbox.email_address}</span>
                      {inbox.display_name&&<span style={{fontSize:11,color:MUTED}}>· sends as “{inbox.display_name}”</span>}
                      {inbox.is_primary
                        ?<span style={{background:G+"18",color:"#166534",fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:5,border:`1px solid ${G}40`}}>PRIMARY</span>
                        :<span style={{background:"#F3F4F6",color:"#6B7280",fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:5,border:`1px solid ${BORDER}`}}>OVERFLOW</span>
                      }
                      <span title={watchExp?`Renews automatically; expires ${watchExp.toLocaleString()}`:"Gmail watch not registered — reply detection won't fire"}
                        style={{background:watchColors[watchStatus]+"18",color:watchColors[watchStatus],fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:5,border:`1px solid ${watchColors[watchStatus]}40`}}>{watchLabels[watchStatus]}</span>
                      <DeliverabilityPill email={inbox.email_address}/>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{flex:1,maxWidth:240,height:5,background:"#f0f0f0",borderRadius:3,overflow:"hidden"}}><div style={{width:`${pct}%`,height:"100%",background:barColor,transition:"width .3s"}}/></div>
                      <span style={{fontSize:11,color:pct>=100?"#EF4444":MUTED,fontWeight:600,whiteSpace:"nowrap"}}>{used} / {warmingUp?<span title={`Warmup-limited (max ${rawCap})`} style={{color:"#7C3AED",fontWeight:700}}>{cap}</span>:<input type="number" min={1} max={2000} defaultValue={rawCap}
                        onBlur={e=>saveInboxCap(inbox,e.target.value)}
                        onKeyDown={e=>{if(e.key==='Enter')e.target.blur();}}
                        disabled={inboxBusy}
                        title="Daily send cap — click to edit, blur or Enter to save"
                        style={{width:50,padding:"1px 4px",border:`1px solid ${BORDER}`,borderRadius:4,fontSize:11,fontWeight:600,color:DARK,background:"#fff",textAlign:"center"}}/>} today</span>
                    </div>
                    {/* Warmup ramp banner — only renders while warming up. Shows current day +
                        effective cap + next bump. Operator can skip if they know the inbox
                        already has reputation (rare for fresh kolabs onboardings). */}
                    {warmingUp && (
                      <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6,padding:"5px 9px",background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:6}}>
                        <span style={{fontSize:11}}>🌱</span>
                        <div style={{fontSize:11,color:"#5B21B6",fontWeight:600,flex:1,minWidth:0}}>
                          Warmup day {warmupDay+1}/14 — capped at <strong>{cap}/day</strong>
                          {daysToBump!=null && <span style={{color:"#7C3AED",fontWeight:500}}> · jumps to {nextBumpCap}/day in {daysToBump===0?"<1":daysToBump} day{daysToBump===1?"":"s"}</span>}
                        </div>
                        <button onClick={()=>toggleInboxWarmup(inbox)} disabled={inboxBusy} title="Skip only if this inbox is already sending cold outreach at volume from another tool — existing accounts with normal email use still benefit from warmup" style={{border:"1px solid #DDD6FE",background:"#fff",color:"#5B21B6",borderRadius:5,padding:"2px 8px",fontSize:10,fontWeight:600,cursor:inboxBusy?"wait":"pointer"}}>Skip</button>
                      </div>
                    )}
                  </div>
                  <div style={{display:"flex",gap:6,flexShrink:0}}>
                    {!inbox.is_primary&&<button disabled={inboxBusy} onClick={()=>setPrimaryInbox(inbox)} style={{border:`1px solid ${G}60`,background:"#fff",color:G,borderRadius:7,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:inboxBusy?"wait":"pointer"}}>Set as primary</button>}
                    <button disabled={inboxBusy} onClick={()=>reconnectInbox(inbox)} title="Re-authorize this inbox with Google without disconnecting — keeps daily cap, warmup progress and primary status. Use when the token expires (weekly while the app is in Google test mode)." style={{border:`1px solid ${G}60`,background:"#fff",color:G,borderRadius:7,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:inboxBusy?"wait":"pointer"}}>Reconnect</button>
                    <button disabled={inboxBusy} onClick={()=>disconnectInbox(inbox.email_address)} style={{border:`1px solid #FECACA`,background:"#fff",color:"#DC2626",borderRadius:7,padding:"5px 11px",fontSize:11,fontWeight:600,cursor:inboxBusy?"wait":"pointer"}}>Disconnect</button>
                  </div>
                </div>);
              })}
              {inboxes.length>0&&<div style={{fontSize:11,color:MUTED,paddingTop:4}}>Worker sends from <strong style={{color:DARK}}>★ primary</strong> first; overflows to other inboxes when primary hits cap. Total daily capacity: <strong style={{color:DARK}}>{inboxes.reduce((s,i)=>s+(i.daily_cap||100),0)}</strong> across {inboxes.length} inbox{inboxes.length!==1?"es":""}.</div>}
            </div>
        }
      </Card>

      {/* AUTO-REPLY V2 — review mode + voice guide.
          Auto-replies are generated by Sonnet when a creator replies to outreach.
          Review mode gates them on operator approval before send (recommended
          for the first 50 creators of a brand going live). Voice guide is
          optional explicit voice direction injected into the AI prompt. */}
      <Card style={{padding:24}}>
        <div style={{fontWeight:700,color:DARK,fontSize:15,marginBottom:6}}>AI auto-reply</div>
        <div style={{fontSize:13,color:"#9ab8a6",marginBottom:18}}>How the AI handles creator replies on your behalf.</div>
        <label style={{display:"flex",alignItems:"flex-start",gap:12,padding:"12px 0",borderTop:`1px solid ${BORDER}`,cursor:"pointer"}}>
          <input type="checkbox" checked={brand?.auto_reply_review_mode===true}
            onChange={e=>persistBrandField('auto_reply_review_mode',e.target.checked)}
            style={{marginTop:3,accentColor:G,width:16,height:16,flexShrink:0,cursor:"pointer"}}/>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:600,color:DARK}}>Review before sending</div>
            <div style={{fontSize:11,color:MUTED,marginTop:3,lineHeight:1.5}}>When ON, AI drafts queue with status <strong style={{color:"#8B5CF6"}}>Awaiting review</strong>. The worker doesn't send them until you hit Approve on the Email Queue page. Recommended for your first 50 creators per brand so you can correct the voice — every edit you make trains the next reply.</div>
          </div>
        </label>
        {/* Fulfilment mode — how the gift reaches the creator. "tiktok_shop" =
            the account manager sends a Target Collaboration invite in TikTok
            Shop by hand; the AI then asks only for the TikTok handle + ONE
            product pick (never an address), and "Target invite sent" in the
            thread moves the creator to Awaiting Post. Added 2026-10-13. */}
        <FulfillmentModeEditor brand={brand} persistBrandField={persistBrandField}/>
        <div style={{padding:"14px 0 0",borderTop:`1px solid ${BORDER}`,marginTop:6}}>
          <div style={{fontSize:13,fontWeight:600,color:DARK,marginBottom:4}}>Voice guide <span style={{color:MUTED,fontWeight:400,fontSize:11,marginLeft:5}}>optional</span></div>
          <div style={{fontSize:11,color:MUTED,marginBottom:8,lineHeight:1.5}}>3-5 sentences telling the AI how to write for this brand. Lands in every auto-reply prompt. Useful before you have edited examples for it to learn from.</div>
          <textarea value={brand?.auto_reply_voice_guide||""}
            onChange={e=>persistBrandField('auto_reply_voice_guide',e.target.value)}
            placeholder={"Write like a friendly, no-fluff founder. Short sentences. No corporate-speak. Match the creator's energy. Sign off as just the brand name."}
            style={{width:"100%",minHeight:90,padding:"10px 12px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,fontFamily:"inherit",outline:"none",resize:"vertical",lineHeight:1.5}}/>
        </div>
      </Card>

      <Card style={{padding:24}}>
      <div style={{fontWeight:700,color:DARK,fontSize:15,marginBottom:6}}>Email sequence timing</div>
      <div style={{fontSize:13,color:"#9ab8a6",marginBottom:18}}>Edit the number of days between each email step.</div>
      {[
        // Reordered per user — outreach sequence first (matches real lifecycle), then fulfillment, then post-content.
        {label:"Outreach email",key:"oFu0",unit:"Instant",fixed:true},
        {label:"Outreach follow-up 1",key:"oFu1",unit:"days after outreach"},
        {label:"Outreach follow-up 2",key:"oFu2",unit:"days after follow-up 1"},
        {label:"Outreach follow-up 3 (final)",key:"oFu3",unit:"days after follow-up 2"},
        {label:"Order confirmation",key:"orderConfirm",unit:"Instant",fixed:true},
        {label:"Package arriving",key:"arriving",unit:"days after fulfilled"},
        {label:"Content brief",key:"brief",unit:"days after fulfilled"},
        {label:"Follow-up 1",key:"fu1",unit:"days after brief"},
        {label:"Follow-up 2",key:"fu2",unit:"days after follow-up 1"},
        {label:"Follow-up 3 (final)",key:"fu3",unit:"days after follow-up 2"},
      ].map(({label,key,unit,fixed})=>{const isDirty=emailDraft&&emailDraft[key]!==brand.email[key];return(<div key={key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",borderRadius:8,marginBottom:4,background:isDirty?"#FFFBEB":"transparent",border:`1px solid ${isDirty?"#FDE68A":"transparent"}`}}><div style={{display:"flex",alignItems:"center",gap:8}}>{isDirty&&<span style={{width:6,height:6,borderRadius:"50%",background:"#F59E0B",display:"inline-block"}}/>}<div style={{fontSize:13,color:isDirty?"#92400E":"#9ab8a6",fontWeight:isDirty?600:500}}>{label}</div></div><div style={{display:"flex",alignItems:"center",gap:8}}>{fixed?<span style={{fontSize:13,fontWeight:700,color:DARK}}>Instant</span>:<input type="number" value={emailValues[key]??""} min={1} max={90} onChange={e=>updateEmailDraft(key,e.target.value)} style={{width:52,padding:"5px 8px",border:`1.5px solid ${isDirty?"#F59E0B":BORDER}`,borderRadius:7,fontSize:13,textAlign:"center",outline:"none",background:isDirty?"#FFFBEB":"#fff",fontWeight:isDirty?700:400,color:DARK}}/>}{!fixed&&<span style={{fontSize:12,color:"#9ab8a6",minWidth:150}}>{unit}</span>}</div></div>);})}
    </Card>
    {emailSaved&&<div style={{background:"#ECFDF5",border:"1px solid #6EE7B7",borderRadius:10,padding:"12px 16px",display:"flex",alignItems:"center",gap:10}}><span style={{color:"#10B981",fontSize:16}}>✓</span><span style={{fontSize:13,color:"#166534",fontWeight:600}}>Email timing saved.</span></div>}
    {hasEmailChanges&&<div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}><div style={{display:"flex",alignItems:"center",gap:10}}><span style={{fontSize:16}}>⚠</span><div><div style={{fontSize:13,color:"#92400E",fontWeight:700}}>Unsaved changes</div><div style={{fontSize:11,color:"#A16207",marginTop:2}}>Changes won't apply until saved.</div></div></div><div style={{display:"flex",gap:8}}><button onClick={()=>setEmailDraft(null)} style={{border:"1px solid #FDE68A",background:"#fff",borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer",color:"#92400E",fontWeight:500}}>Discard</button><button onClick={saveEmailChanges} style={{background:"#F59E0B",color:"#fff",border:"none",borderRadius:8,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Save changes</button></div></div>}
    </div>)}
    {tab==="agency"&&!isAgencyOwner&&<Card style={{padding:24}}><div style={{fontSize:13,color:MUTED}}>The agency Google connection is shared across every brand and managed by the agency owner.</div></Card>}
    {tab==="agency"&&isAgencyOwner&&(<div style={{display:"flex",flexDirection:"column",gap:12}}>
      <Card style={{padding:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6,gap:12}}>
          <div>
            <div style={{fontWeight:700,color:DARK,fontSize:15}}>Agency Google Drive</div>
            <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:540,lineHeight:1.55}}>One Google connection for the whole agency — kolabs owns the Drive that holds creator tracking sheets, agreement Docs, and content folders for every brand. New brands inherit the folder structure automatically (no per-brand OAuth).</div>
          </div>
        </div>
        {agencyDrive===null?<div style={{fontSize:12,color:MUTED,marginTop:10}}>Loading…</div>:
         agencyDrive.email?<div style={{marginTop:14,display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"flex",alignItems:"center",gap:14,padding:"13px 14px",background:G+"08",border:`1px solid ${G}40`,borderRadius:10}}>
              <div style={{width:30,height:30,borderRadius:8,background:G+"18",border:`1px solid ${G}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:14,color:G}}>✓</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:DARK}}>{agencyDrive.email}</div>
                <div style={{fontSize:11,color:MUTED,marginTop:2}}>
                  Connected {agencyDrive.connected_at?new Date(agencyDrive.connected_at).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"}):"—"}
                  {agencyDrive.agency_root_folder_id&&<> · root folder <a href={`https://drive.google.com/drive/folders/${agencyDrive.agency_root_folder_id}`} target="_blank" rel="noreferrer" style={{color:G,textDecoration:"none",fontWeight:600}}>open in Drive ↗</a></>}
                </div>
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0}}>
                <button disabled={agencyDriveBusy} onClick={testAgencyDrive} style={{border:`1px solid ${BORDER}`,background:"#fff",color:DARK,borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:agencyDriveBusy?"wait":"pointer"}}>Test access</button>
                <button disabled={agencyDriveBusy} onClick={disconnectAgencyDrive} style={{border:"1px solid #FECACA",background:"#fff",color:"#DC2626",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:agencyDriveBusy?"wait":"pointer"}}>Disconnect</button>
              </div>
            </div>
            {agencyDriveTestResult&&(agencyDriveTestResult.ok
              ?<div style={{fontSize:12,padding:"10px 14px",background:"#F0FDF4",border:`1px solid ${G}40`,borderRadius:8,color:"#166534"}}>
                ✓ Test passed — created <strong>{agencyDriveTestResult.folder.name}</strong>
                {agencyDriveTestResult.folder.webViewLink&&<> · <a href={agencyDriveTestResult.folder.webViewLink} target="_blank" rel="noreferrer" style={{color:G,fontWeight:700}}>open ↗</a></>}
              </div>
              :<div style={{fontSize:12,padding:"10px 14px",background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,color:"#DC2626"}}>
                ✗ Test failed: {agencyDriveTestResult.error}
              </div>
            )}
          </div>:
          <div style={{marginTop:14}}>
            <div style={{padding:"14px 16px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,fontSize:13,color:"#92400E",lineHeight:1.55,marginBottom:14}}>
              ⚠ Not connected. Click <strong>Connect Drive</strong> below to sign in with the Google account that should own kolabs's Drive (your personal Gmail works for testing; switch to a kolabs.agency Workspace account when ready for production).
            </div>
            <button disabled={agencyDriveBusy} onClick={connectAgencyDrive} style={{background:DARK,color:G,border:"none",borderRadius:9,padding:"10px 18px",fontWeight:700,fontSize:13,cursor:agencyDriveBusy?"wait":"pointer",opacity:agencyDriveBusy?0.6:1}}>{agencyDriveBusy?"Opening Google…":"Connect Drive"}</button>
            <div style={{fontSize:11,color:MUTED,marginTop:10,lineHeight:1.6}}>Requests scopes: <code style={{background:"#f8faf9",padding:"1px 5px",borderRadius:4,fontFamily:"DM Mono, monospace",fontSize:10}}>drive.file</code> (create/manage app files only — can't see your other Drive content) + <code style={{background:"#f8faf9",padding:"1px 5px",borderRadius:4,fontFamily:"DM Mono, monospace",fontSize:10}}>spreadsheets</code> (read/write Sheets we create).</div>
          </div>}
      </Card>
    </div>)}

    {tab==="activity"&&isAgencyOwner&&<TeamActivityCard/>}
    {tab==="activity"&&!isAgencyOwner&&<Card style={{padding:24}}><div style={{fontSize:13,color:MUTED}}>Activity reports are visible to brand owners only.</div></Card>}
    {tab==="security"&&isAgencyOwner&&<SecurityLogCard brandId={brandId}/>}
    {tab==="security"&&!isAgencyOwner&&<Card style={{padding:24}}><div style={{fontSize:13,color:MUTED}}>The security audit log is visible to brand owners only.</div></Card>}

    {tab==="health"&&(<div style={{display:"flex",flexDirection:"column",gap:12}}>
      {/* Email infrastructure health — surfaces warnings that previously
          only appeared as small badges inside Settings → Email (Watch
          expiring, DMARC monitor-only, SPF missing, warmup in progress,
          cap pressure). Computed per inbox + aggregated at the top. */}
      <Card style={{padding:24}}>
        <div style={{fontWeight:700,color:DARK,fontSize:15,marginBottom:3}}>Email infrastructure</div>
        <div style={{fontSize:13,color:MUTED,marginBottom:14,lineHeight:1.55}}>Per-inbox checks for the things that silently break outbound: Gmail watch (reply detection), SPF / DKIM / DMARC at your sending domain, and warmup state.</div>
        {inboxes.length===0
          ?<div style={{padding:"14px 16px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,fontSize:13,color:"#92400E"}}>
              ⚠ No Gmail connected for this brand. Settings → Email → Add inbox.
            </div>
          :(()=>{
            const sorted=[...inboxes].sort((a,b)=>(b.is_primary?1:0)-(a.is_primary?1:0));
            return(<div style={{display:"flex",flexDirection:"column",gap:10}}>
              {sorted.map(inbox=>(
                <InboxHealthRow key={inbox.id} inbox={inbox} usage={inboxUsage[inbox.id]||0}/>
              ))}
            </div>);
          })()
        }
      </Card>

      {/* Instagram tags-poll health */}
      <Card style={{padding:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:12}}>
          <div>
            <div style={{fontWeight:700,color:DARK,fontSize:15}}>Instagram tag detection</div>
            <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:540,lineHeight:1.55}}>Runs every 15 min. Pulls posts tagging {igPollStatus?.instagram_username?`@${igPollStatus.instagram_username}`:'your brand'} from Meta and writes them to the Content Wall + Drive archive.</div>
          </div>
        </div>
        {!igPollStatus?.instagram_business_account_id ? (
          <div style={{padding:"14px 16px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,fontSize:13,color:"#92400E",lineHeight:1.55}}>
            Instagram not connected yet. Settings → Integrations → Connect Instagram.
          </div>
        ) : !igPollStatus?.last_tags_poll_at ? (
          <div style={{padding:"14px 16px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8,fontSize:13,color:"#1E40AF",lineHeight:1.55}}>
            Connected, awaiting first poll (runs every 15 min).
          </div>
        ) : (() => {
          const s = igPollStatus.last_tags_poll_summary || {};
          const lastAt = new Date(igPollStatus.last_tags_poll_at);
          const minsAgo = Math.floor((Date.now() - lastAt.getTime()) / 60000);
          const errs = Array.isArray(s.errors) ? s.errors : [];
          const driveOk = s.drive_connected !== false;
          // Staleness has to be part of the verdict, not just a caption. The
          // numbers below are whatever the last tick stored — if the worker
          // dies they sit there looking clean forever. 45 min = 3 missed
          // 15-min ticks, i.e. more than a one-off blip.
          const stale = minsAgo >= 45;
          const ago = minsAgo < 1 ? "just now" : minsAgo < 60 ? `${minsAgo} min ago` : minsAgo < 2880 ? `${Math.floor(minsAgo/60)}h ago` : `${Math.floor(minsAgo/1440)} days ago`;
          const state = stale ? "stale" : (errs.length === 0 && driveOk) ? "ok" : "bad";
          const tone = {
            ok:   {bg:G+"10",  border:G+"50",   fg:"#166534", label:"✓ Healthy"},
            stale:{bg:"#FFFBEB",border:"#FDE68A",fg:"#92400E", label:`⚠ Stale — no poll in ${ago.replace(" ago","")}`},
            bad:  {bg:"#FEF2F2",border:"#FECACA",fg:"#991B1B", label:"⚠ Issues detected"},
          }[state];
          return (<div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:tone.bg,border:`1px solid ${tone.border}`,borderRadius:8}}>
              <div style={{fontSize:13,color:tone.fg,fontWeight:600}}>{tone.label}</div>
              <div style={{fontSize:11,color:MUTED}}>Last poll: {ago}</div>
            </div>
            {stale && (
              <div style={{padding:"10px 14px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,fontSize:12,color:"#92400E",lineHeight:1.55}}>
                The poll should run every 15 min but hasn't reported in since {lastAt.toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})}. The counts below are from that run, not from now — treat them as history. Tagged posts may not be reaching the Content Wall or the Drive archive.
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"repeat(5, 1fr)",gap:8}}>
              {[
                {label:"Scanned",value:s.scanned ?? 0},
                {label:"New matched",value:s.new_matched ?? 0},
                {label:"New organic",value:s.new_unmatched ?? 0},
                {label:"Archived",value:s.archived ?? 0},
                {label:"Metrics refreshed",value:s.metrics_refreshed ?? 0},
              ].map(stat=>(
                <div key={stat.label} style={{padding:"10px 12px",background:"#fafcfb",border:`1px solid ${BORDER}`,borderRadius:8}}>
                  <div style={{fontSize:18,fontWeight:700,color:DARK}}>{stat.value}</div>
                  <div style={{fontSize:10,color:MUTED,fontWeight:600,textTransform:"uppercase",letterSpacing:0.4}}>{stat.label}</div>
                </div>
              ))}
            </div>
            {!driveOk && (
              <div style={{padding:"10px 14px",background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,fontSize:12,color:"#991B1B",lineHeight:1.55}}>
                ⚠ Agency Drive disconnected — posts detected but not archived. Settings → Integrations → reconnect.
              </div>
            )}
            {errs.length > 0 && (<details style={{padding:"10px 14px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8}}>
              <summary style={{fontSize:12,fontWeight:600,color:"#92400E",cursor:"pointer"}}>{errs.length} error{errs.length===1?"":"s"} from last poll</summary>
              <ul style={{margin:"8px 0 0 16px",padding:0,fontSize:11,color:"#92400E",lineHeight:1.6}}>
                {errs.map((e,i)=>(<li key={i} style={{fontFamily:"DM Mono, monospace",wordBreak:"break-word"}}>{e}</li>))}
              </ul>
            </details>)}
          </div>);
        })()}
      </Card>

      <Card style={{padding:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:12}}>
          <div>
            <div style={{fontWeight:700,color:DARK,fontSize:15}}>Integration health</div>
            <div style={{fontSize:13,color:MUTED,marginTop:3,maxWidth:540,lineHeight:1.55}}>Failures from inbound webhooks (Shopify fulfillment, Gmail reply notifications, Snowball). Surfaces silent integration breakage — token expiry, schema drift, downstream API errors — before it costs you a week of missed events.</div>
          </div>
          {healthAlertCount>0&&<button onClick={acknowledgeAllFailures} style={{background:DARK,color:G,border:"none",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer",flexShrink:0,whiteSpace:"nowrap"}}>Acknowledge all ({healthAlertCount})</button>}
        </div>
        {healthFailures.length===0?
          <div style={{padding:"22px 0",textAlign:"center",color:G,fontSize:13,fontWeight:600}}>✓ All clear — no webhook failures recorded</div>
          :<div style={{display:"flex",flexDirection:"column",gap:8}}>
          {healthFailures.map(f=>{
            const isUnack=!f.acknowledged_at;
            const sourceLabel={shopify_fulfillment:"Shopify · Fulfillment",gmail_reply:"Gmail · Reply",snowball_zap_in:"Snowball Zap",shopify_oauth:"Shopify · OAuth",google_agency_oauth:"Google · Agency OAuth"}[f.source]||f.source;
            return(<div key={f.id} style={{padding:"12px 14px",border:`1px solid ${isUnack?"#FECACA":BORDER}`,borderRadius:10,background:isUnack?"#FEF2F2":"#fafcfb"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                    <span style={{fontSize:11,fontWeight:700,color:isUnack?"#DC2626":MUTED,textTransform:"uppercase",letterSpacing:".05em"}}>{sourceLabel}</span>
                    <span style={{fontSize:10,fontWeight:600,color:isUnack?"#991B1B":MUTED,background:isUnack?"#FECACA":"#F3F4F6",padding:"1px 7px",borderRadius:4,fontFamily:"DM Mono, monospace"}}>{f.error_code||"unknown"}</span>
                    <span style={{fontSize:10,color:MUTED,fontFamily:"DM Mono, monospace"}}>HTTP {f.http_status||"—"}</span>
                  </div>
                  <div style={{fontSize:12,color:DARK,marginBottom:5,lineHeight:1.5,wordBreak:"break-word"}}>{f.error_message||"(no message)"}</div>
                  <div style={{fontSize:11,color:MUTED}}>{new Date(f.created_at).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"numeric",minute:"2-digit",second:"2-digit"})}</div>
                  {f.payload_summary&&Object.keys(f.payload_summary).length>0&&(
                    <details style={{marginTop:6}}>
                      <summary style={{fontSize:11,color:MUTED,cursor:"pointer"}}>Payload context</summary>
                      <pre style={{fontSize:10,color:MUTED,marginTop:4,padding:8,background:"#fff",border:`1px solid ${BORDER}`,borderRadius:6,overflow:"auto",fontFamily:"DM Mono, monospace"}}>{JSON.stringify(f.payload_summary,null,2)}</pre>
                    </details>
                  )}
                </div>
                {isUnack
                  ?<button onClick={()=>acknowledgeFailure(f.id)} style={{flexShrink:0,border:`1px solid ${BORDER}`,background:"#fff",color:DARK,borderRadius:7,padding:"5px 11px",fontSize:11,fontWeight:600,cursor:"pointer"}}>Acknowledge</button>
                  :<span style={{flexShrink:0,fontSize:10,color:MUTED,fontWeight:600,padding:"5px 8px"}}>✓ Acked</span>
                }
              </div>
            </div>);
          })}
        </div>}
      </Card>
    </div>)}

    {tab==="team"&&<TeamMembersCard brandId={brandId} brandName={activeBrand}/>}
  </div>);
}
